/**
 * Job Board Module - Read-only schedule view for drivers
 *
 * Unlike Dispatch (full CRUD), Job Board is view-only:
 * - See daily/weekly job schedule
 * - View job details (no editing)
 * - Access DVIR from here
 * - No Add Job, Mass Text, or edit capabilities
 */

import {
  db,
  // TASK-4879 — needed to prove the in-app driver is who they say they are when
  // posting a safety sign-in (see submitSafetyMeetingSignIn below). Same module
  // specifier as everything else here, so this is ONE instance, not a second
  // copy of firebase-config (Pitfall #1).
  auth,
  storage,
  functions,
  httpsCallable,
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  writeBatch,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  increment,
  arrayUnion,
  ref,
  uploadBytes,
  getDownloadURL
} from '../firebase-config.js?v=1868';

// TASK-1180 — serverTimestamp isn't re-exported by firebase-config.js; import
// it straight from the SAME 10.8.0 gstatic URL firebase-config uses (same
// module instance — the ap.js runTransaction precedent). This is NOT the ?v=
// cross-import pitfall (no buster on the gstatic URL, single instance).
import { runTransaction, serverTimestamp } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';
// TASK-4602 — the SAME specifier pretrip.js uses. A different ?v= would mint a
// second module instance (Pitfall #1) and, for an IndexedDB wrapper, two
// instances racing the same version upgrade.
import * as photoQueue from '../utils/photoQueue.js?v=1810';
// TASK-9402 — iPhone HEIC → JPEG for driver uploads (receipts, ticket photos,
// expenses). Same converter, cap and message as every other photo screen.
import { prepareUploadFile, prepareUploadFiles, uploadContentType, skippedPhotosMessage, photoErrorMessage } from '../utils/uploadPrep.js?v=1';
import { createPhotoPrepTracker, settlePhotoPrepBeforeSave } from '../utils/heicConvert.js?v=3';
// TASK-9740 — the ONE field list for the driver-signed Bill of Lading /
// Disposal Ticket, generated from functions/src/shared/jobDocumentSpec.js by
// scripts/build-shared.cjs. jobboard.js is its only importer.
import { jobDocumentKindLabel, jobDocumentFieldsFor, validateJobDocumentFields, jobDocumentDriverPo } from '../shared/job-document-spec.js?v=9995007';
// TASK-9694 — a driver's job-screen Doc rides in job.permits[] but is not a load permit.
import { isJobDocEntry } from '../shared/job-permit-entry.js?v=10282127';
// TASK-9868 — who uploaded a ticket photo + what it is (BOL / scale ticket /
// permit / other). Pure module, shared with billing.js (no state, so two
// instances are harmless).
import { normalizeTicketDocType, ticketDocTypeLabel, ticketDocTypeOptionsHtml, buildTicketDocUploader, fileAddedAuditPayload, fileUploadCustomMetadata } from '../shared/ticket-doc-meta.js?v=9958';
// TASK-9721 — optional Job on the PPE self-log, preselected from the open clock
// entry / the single assignment today. The pure decision module is LAZY-loaded
// when the self-log form opens (no cold-load cost); hr.js loads the same file at
// the SAME buster (no state, so two instances would be harmless anyway).
let _ppeJobMod = null;
function _ppeJobModLoad() {
  if (_ppeJobMod) return Promise.resolve(_ppeJobMod);
  return __ddChunkWrap(import('../shared/ppe-issue-job.js?v=9721')).then(m => (_ppeJobMod = m));
}

import {
  jobDayVisibleToCurrentDriver as _sharedJobDayVisible,
  isJobDayAssignedToDriver as _sharedJobDayAssignedToDriver,
  computeLoadInfo as _sharedComputeLoadInfo,
  buildDriverLoadCounterHTML as _sharedBuildDriverLoadCounterHTML,
  buildCalendarMonthGridHTML as _sharedBuildCalendarMonthGridHTML,
} from '../utils/driverScheduleHelpers.js?v=6';

// ─── TASK-4610 — jobs.startDate/endDate MUST be a Firestore Timestamp ────────
// Firestore range filters are TYPE-SCOPED. Verified live 2026-08-25 on a
// throwaway tenant: a job whose startDate is the STRING '2026-08-26' is NOT
// returned by `where('startDate','>=',<Timestamp>)` — the shape of the windowed
// job subscriptions below. The doc exists, nothing throws, and the job is
// invisible to the driver. (Real incident: Ethan could not see his 310 mini-ex
// haul for a day.) The RENDER path already tolerates both types, which is
// exactly why it stayed hidden — the doc never arrives to be rendered.
//
// DELIBERATELY DUPLICATED from dispatch.js rather than imported: cross-module
// imports carry a ?v= buster and give you a SECOND module instance (CLAUDE.md
// Pitfall #1), and routing through window.DispatchModule would make a
// silent-failure guard depend on another module's load order. jobboard.js runs
// for PIN drivers, who are precisely the people this protects. Keep the two
// copies in sync; both are exercised by tests/task4610-job-date-coercion.unit.cjs.
const _JOB_DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
function coerceJobTimestamp(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value?.toDate === 'function') return value;              // already a Timestamp
  let d = null;
  if (value instanceof Date) d = value;
  else if (typeof value === 'number' && Number.isFinite(value)) d = new Date(value);
  else if (typeof value === 'string') {
    const s = value.trim();
    // Date-only → NOON local. `new Date('2026-08-26')` is UTC midnight, which is
    // 2026-08-25 19:00 in CDT and renders on the WRONG DAY. Same convention as
    // jobDays.date — memory/lesson_jobday_date_anchors_to_noon_local.
    d = _JOB_DATE_ONLY_RE.test(s) ? new Date(s + 'T12:00:00') : new Date(s);
  }
  if (!(d instanceof Date) || !Number.isFinite(d.getTime())) return null;
  return Timestamp.fromDate(d);
}

const _warnedStringDateJobs = new Set(); // warn once per id — snapshots re-fire

// Display-only normalizer. Never writes Firestore: repairing a stored doc is
// Chris's call, not a side effect of a driver opening the board.
function normalizeJobDoc(id, data) {
  const s = data.startDate, e = data.endDate;
  const sBad = s !== null && s !== undefined && typeof s.toDate !== 'function';
  const eBad = e !== null && e !== undefined && typeof e.toDate !== 'function';
  if (!sBad && !eBad) return { id, ...data };
  const out = { id, ...data };
  if (sBad) out.startDate = coerceJobTimestamp(s);
  if (eBad) out.endDate = coerceJobTimestamp(e);
  out._dateTypeRepaired = true;
  if (!_warnedStringDateJobs.has(id)) {
    _warnedStringDateJobs.add(id);
    console.warn('[JobBoard] TASK-4610 — job', id, 'stores a NON-Timestamp date. Coerced for display only; the stored doc is still wrong and is invisible to the windowed board query. Run scripts sweep + tell Chris.', { startDate: s, endDate: e });
  }
  return out;
}

// The quarantine read. Strings sort AFTER timestamps in Firestore's value order
// and a range filter only matches its OWN type, so `startDate >= ''` returns
// EXACTLY the string-typed startDates — verified live: not numbers, not maps,
// not null, not a missing field. Zero documents in a healthy tenant, so this
// costs two listeners and one billed read each on connect, not a doubled board.
// It never gates the publish: if it errors, the board renders as it does today.
//
// BOTH fields are swept, not just startDate (COD Code Reviewer). A doc with a
// Timestamp startDate OLDER than the cutoff and a STRING endDate inside it is
// invisible to all three of Query 1 (start < cutoff), Query 2 (endDate is not
// a Timestamp) and a startDate-only quarantine (startDate is not a string).
// That mixed shape is exactly what a partial hand-repair produces.
//
// Deliberately UNBOUNDED (no upper bound on the range). A bound like
// `< cutoffISO` would only be correct if every bad value were ISO-formatted —
// 'Aug 26 2026' sorts nowhere near it and would be silently missed again,
// which is the exact failure mode being fixed. Unbounded costs one read per
// corrupt job and zero in a healthy tenant, so the price is paid only by a
// tenant that already has a problem. TASK_4610_DIRTY_TENANT_WARN makes that
// loud instead of quietly expensive.
//
// Calls back with a snapshot-shaped `{ docs }` union so the two call sites
// stay identical to a plain onSnapshot handler.
const TASK_4610_DIRTY_TENANT_WARN = 50;
function subscribeStringDatedJobs(tenantId, onSnap) {
  const byField = { startDate: [], endDate: [] };
  const emit = () => {
    const seen = new Map();
    for (const d of byField.startDate.concat(byField.endDate)) if (!seen.has(d.id)) seen.set(d.id, d);
    if (seen.size >= TASK_4610_DIRTY_TENANT_WARN) {
      console.warn(`[JobBoard] TASK-4610 — ${seen.size} jobs in this tenant store a NON-Timestamp date. The board is paying a read for each one on every load. Run the sweep and repair the data.`);
    }
    onSnap({ docs: Array.from(seen.values()) });
  };
  const one = (field) => {
    try {
      return onSnapshot(
        query(collection(db, 'tenants', tenantId, 'jobs'), where(field, '>=', ''), orderBy(field, 'asc')),
        (snap) => { byField[field] = snap.docs; emit(); },
        (error) => console.warn(`[JobBoard] TASK-4610 ${field} string-date sweep unavailable (board unaffected):`, error?.message || error)
      );
    } catch (e) {
      console.warn(`[JobBoard] TASK-4610 ${field} string-date sweep could not subscribe:`, e?.message || e);
      return () => {};
    }
  };
  const unsubStart = one('startDate');
  const unsubEnd = one('endDate');
  return () => { try { unsubStart(); } catch (_) {} try { unsubEnd(); } catch (_) {} };
}

// Apply the SAME window the bad type excluded the doc from — a rescued job must
// not surface anywhere a correctly-typed one wouldn't. Mirrors Query 1
// (startDate >= cutoff, no status filter) and Query 2 (endDate >= cutoff, minus
// 'finished'). A repair, not a widening.
function stringDatedJobsInWindow(snapshot, cutoffTs) {
  const cutMs = cutoffTs?.toMillis?.() ?? 0;
  const out = new Map();
  snapshot.docs.forEach(d => {
    const job = normalizeJobDoc(d.id, d.data());
    const sMs = job.startDate?.toMillis?.() ?? null;
    const eMs = job.endDate?.toMillis?.() ?? null;
    const inQ1 = sMs !== null && sMs >= cutMs;
    const inQ2 = eMs !== null && eMs >= cutMs && job.status !== 'finished';
    if (inQ1 || inQ2) out.set(d.id, job);
  });
  return out;
}

// ============================================
// TASK-1513 — Record Expense job-picker relevance window (days).
// The picker in openExpenseModal trims the OUTPUT of _expenseVisibleJobs to
// jobs whose [startDate, endDate || startDate] span OVERLAPS
// [today - backDays, today + fwdDays] (span-overlap, not startDate alone —
// a long-running active job that started 60 days ago and ends today must
// stay in the default list). Cancelled jobs are dropped. A "Show older jobs"
// text link below the select reveals the rest.
// PRESENTATION-ONLY: both submit paths re-derive the chosen job through
// _expenseVisibleJobs independently, so this window can neither block a
// legitimate save nor smuggle an out-of-scope job past submit.
// Two named constants so the widths are one-line tunable.
// ============================================
const EXPENSE_JOB_WINDOW_DRIVER = { backDays: 14, fwdDays: 7 };   // driver contexts (a driver personId resolves)
const EXPENSE_JOB_WINDOW_ADMIN  = { backDays: 30, fwdDays: 30 };  // admin/AP bookkeeper path (driverId null — monthly reconciliation)

// TASK-1513 — the one shared implementation of the window (reconciliation.js
// reuses it via window.JobBoardModule.filterExpenseJobWindow — Pitfall #1:
// never a cross-import with ?v=). Pure function over an ALREADY-SCOPED jobs
// array: callers must pass the post-restriction list (_expenseVisibleJobs
// output / tenant-scoped BI state), never a wider one — `forceIncludeId` is
// looked up ONLY inside the passed array, so an out-of-scope id forces
// nothing in. Dateless jobs stay visible (a job that can't be judged stale
// shouldn't hide); a malformed date never throws.
// ---------------------------------------------------------------------------
// TASK-4529 — fleet ref resolver. A jobDay/ticket ref may be a Firestore doc
// ID, a unit number, a nickname, or a legacy name — and on a live tenant one
// truck's DOC ID can equal another truck's UNIT number (TITS: doc id "15" =
// Dove, while Blue Jay's unit = "15"). A single-pass
// `find(t => t.id === ref || t.unit === ref)` returns whichever happens to
// sort first, so ID matches must be exhausted across the WHOLE list before
// any unit/nickname/name match is considered.
// KEEP IN SYNC — identical copies live in dispatch.js / jobboard.js /
// clock.js / billing.js / bi.js / jsa.js (cross-imports forbidden: Pitfall #1
// — ?v= cache busters make duplicate module instances). Twin registered in
// docs/audits/twin-audit-2026-08-19.md. A seventh SERVER copy lives in
// functions/src/services/ticketGateway.js. Byte-equality of all copies is
// asserted by tests/task4529-fleet-ref-resolver.unit.cjs.
function _resolveFleetRef(ref, list) {
  if (ref === null || ref === undefined || ref === '') return null;
  const s = String(ref);
  const arr = list || [];
  const byId = arr.find(x => x && x.id !== null && x.id !== undefined && String(x.id) === s);
  if (byId) return byId;
  const eq = (v) => v !== null && v !== undefined && v !== '' && String(v) === s;
  return arr.find(x => x && (eq(x.unit) || eq(x.nickname) || eq(x.name) || eq(x.partNumber))) || null;
}

function filterExpenseJobWindow(jobs, opts) {
  const backDays = Number(opts?.backDays ?? EXPENSE_JOB_WINDOW_DRIVER.backDays);
  const fwdDays = Number(opts?.fwdDays ?? EXPENSE_JOB_WINDOW_DRIVER.fwdDays);
  const forceIncludeId = opts?.forceIncludeId || null;
  const DAY_MS = 86400000;
  // TASK-4591 — optional anchor. Every caller before this one picked a job for
  // work happening NOW, so `today` was the right centre. The ticket-review
  // picker is not: a biller opening an 8/01 ticket in September wants the jobs
  // that were running on 8/01, not the ones running today. Callers that omit it
  // keep the exact previous behaviour.
  const now = Number(opts?.anchorMs) || Date.now();
  const winStart = now - backDays * DAY_MS;
  const winEnd = now + fwdDays * DAY_MS;
  const toMs = (v) => {
    try {
      const d = v?.toDate?.() || (v ? new Date(v) : null);
      const t = d ? d.getTime() : NaN;
      return isNaN(t) ? null : t;
    } catch (_) { return null; }
  };
  const kept = (Array.isArray(jobs) ? jobs : []).filter(j => {
    if (!j || j.status === 'cancelled') return false;
    const s = toMs(j.startDate) ?? toMs(j.createdAt);
    if (s == null) return true; // dateless — can't judge staleness, keep visible
    const e = toMs(j.endDate) ?? s;
    return Math.min(s, e) <= winEnd && Math.max(s, e) >= winStart;
  });
  // Forced-include of the modal's context/selected job. Looked up in the SAME
  // (post-restriction) array — see the function comment above; do NOT "fix"
  // this to search a wider list. Cancelled stays dropped even when forced
  // (COD Code Reviewer): a clocked-into job that got cancelled mid-shift falls
  // back to "— No job —", which is fine because Job is optional.
  if (forceIncludeId && !kept.some(j => j && j.id === forceIncludeId)) {
    const forced = (Array.isArray(jobs) ? jobs : [])
      .find(j => j && j.id === forceIncludeId && j.status !== 'cancelled');
    if (forced) kept.push(forced);
  }
  return kept;
}

// ============================================
// TASK-1160 — gateway idempotency keys (local copy — clock.js has the twin;
// cross-importing across ?v= busters would mint two module instances, CLAUDE.md
// pitfall #1). A client deadline-exceeded on mutateTicketAndClockEntry means WE
// gave up while the server may still complete; a retry then double-applies a
// money mutation (double-finalized switch segments = the TITS driver incident).
// Each LOGICAL op gets a stable key: a random session token per scope
// ('switchJob:<entryId>') persisted across retries, suffixed with a hash of the
// exact payload — identical retry replays server-side, changed payload executes
// fresh. Cleared only on confirmed success. Dedup key ONLY — never auth.
// ============================================
const _gwOpSessions = {};
function _gwOpKey(scope, payload) {
  if (!_gwOpSessions[scope]) {
    _gwOpSessions[scope] = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : (Date.now().toString(36) + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2));
  }
  let h = 5381;
  const s = JSON.stringify(payload || {});
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return (_gwOpSessions[scope] + '_' + h.toString(36)).replace(/[^A-Za-z0-9_-]/g, '').slice(0, 128);
}
function _gwOpDone(scope) { delete _gwOpSessions[scope]; }
function _gwIsDeadlineExceeded(err) {
  return /deadline-exceeded/i.test(String(err?.code || '')) ||
    /deadline exceeded/i.test(String(err?.message || ''));
}
// Client timeout for clock-money gateway calls: 120s (default 70s) — cold start
// + slow cellular sit on top of the server's 30s window. (TASK-1160)
const GW_CLIENT_TIMEOUT_MS = 120000;

// ============================================
// STATE
// ============================================
// TASK-345 Slice M kill-switch — module-scope so both subscribeToData and the
// TASK-569 calendar window-math helper read the same flag. Flip to false to
// revert the PIN-driver date-windowing (driver sub goes unbounded again).
const JB_FAST_PIN_DRIVER_FILTER = true;

const VIEW_MODE_LS_KEY = 'dd_jobboard_view_mode_v1';
function readSavedViewMode() {
  try {
    const v = localStorage.getItem(VIEW_MODE_LS_KEY);
    return v === 'calendar' ? 'calendar' : 'day';
  } catch (_) { return 'day'; }
}

const JobBoardState = {
  tenantId: null,
  currentDate: new Date(),
  viewMode: readSavedViewMode(), // 'day' | 'calendar'
  calendarMonth: new Date(),     // anchor date for the rendered month
  jobs: [],
  jobDays: [],
  // TASK-569 — on-demand month cache for the calendar. The live jobDays/jobs
  // subscriptions are date-WINDOWED (PIN driver = 7d back, admin = 90d back),
  // but the calendar can page to ANY month. When a rendered month falls OUTSIDE
  // the live window we fetch that month's jobDays (+ referenced parent jobs)
  // once via getDocs and stash them here, keyed 'YYYY-MM', so paging back to a
  // past month populates instead of rendering blank. Render-only — NEVER merged
  // into the live arrays. Fetched jobDays still pass jobDayVisibleToCurrentDriver
  // at render time, so a driver can't see others' jobDays in a past month.
  _calMonthCache: {},     // 'YYYY-MM' -> { jobDays: [], jobsById: {id: job} }
  _calMonthFetching: {},  // 'YYYY-MM' -> true while a fetch is in flight
  _calMonthError: {},     // 'YYYY-MM' -> true if the fetch failed (degrade to empty)
  people: [],
  trucks: [],
  trailers: [],
  customers: [],
  permits: [],           // 811 locate permits (load-once via getDocs) — for job.permitId render on driver side
  tickets: [],
  ticketLinesCache: {},  // ticketId -> lines[]
  projectsCache: {},     // projectId -> project data | null (null = confirmed not found)
  // customerId -> active-contract fieldTicketSettings | null (null = no active contract / not found).
  // Mirrors clock.js loadFieldTicketItems contract lookup. Used to gate the driver-ticket signature canvas.
  contractSigCache: {},
  // TASK-10190 — jobDayId -> read-time Field Ticket Settings (or null) for a day
  // whose stamp is missing/null. Session-only; never written back.
  jobDaySigReadTime: {},
  jobDaySigReadState: {}, // TASK-10190 rest — key -> 1 while the read-time call is out or after a retryable failure
  unsubscribers: [],
  dataLoaded: { jobs: false, jobDays: false },
  // TASK-424 #5 (C3): admin jobs/jobDays live window. The non-PIN admin path
  // here used to stream the ENTIRE jobs + jobDays collections (unbounded). Now
  // windowed the SAME way the PIN-driver path is (TASK-345 Slice M) with a
  // wider default lookback (90d vs the driver's 7d). `>=` lower bound, NO upper
  // bound → all upcoming/future jobs always streamed; only OLDER jobs excluded.
  // "Load older" widens this and resubscribes. <= 0 means "all" (no lower bound).
  adminWindowDaysBack: 90,
  // Signature canvas state
  sigCanvas: null,
  sigCtx: null,
  sigDrawing: false,
  sigLastX: 0,
  sigLastY: 0,
  sigTicketId: null,
  // TASK-437 — PPE sign-off modal state
  ppeSigCanvas: null,
  ppeSigCtx: null,
  ppeSigHasDrawn: false,
  ppeTypes: [],        // active safetyEquipmentTypes loaded for the driver session
  ppePending: [],      // admin-issued safetyEquipment docs for THIS driver w/ no acknowledgment
  ppeMode: 'list',     // 'list' | 'ack' | 'selflog'
  ppeAckTargetId: null, // doc id being acknowledged (ack flow only)
  ppeJobCtx: null,      // TASK-9721 — self-log job context (null = not loaded / failed)
  ppeJobGen: 0,         // TASK-9721 — bumps on every self-log open; a late lookup for an older form is dropped
  assets: [],
  // Cost allocations (driver-expense submission v1)
  costAllocations: [],
  // TASK-4585 — "no allocations" and "the snapshot has not landed" are the same
  // empty array, and the requirement waiver below turns on emptiness. Without
  // this flag a modal opened before the first snapshot would waive the
  // requirement on a tenant that actually has allocations.
  _costAllocationsLoaded: false,
  // Tenant driver-expense settings (mirrored when settings tenant doc is read)
  driverExpenseSettings: null,
  // TASK-361 — driver's recent JSAs moved to JSA module (jsa.js renderMyJsaSection).
  // TASK-794 — JSA-on-job lookup for the job detail modal: { jobDayId: {id} } and
  // { jobId: {id} } so the modal can surface Sign/Get-Signatures on the existing
  // report (most-recent non-dismissed wins; keyed both ways for multi-day jobs).
  jsaByJobDay: {},
  jsaByJob: {},
  // TASK-9371 — EVERY non-dismissed JSA per jobDay / per job (summaries,
  // createdAt desc) so the job modal lists them all instead of the first one.
  // `jsaJobFetch` holds a one-shot where('jobId','==',…) result per job taken
  // on modal open — the 30-day/500 subscription window above cannot promise
  // "every" on an old or busy job.
  jsaListByJobDay: {},
  jsaListByJob: {},
  jsaJobFetch: {},
  // TASK-9372 — equipment inspections on the job modal. `inspectionsByJob`
  // holds a one-shot read of pretrips per job, taken on modal open (undefined
  // = loading, [] = none). TASK-9707: that read is the open day's equipment
  // (truckId in jobDay trucks/trailers/assets, that day) PLUS any pretrip
  // stamped with the jobId — see _jbFetchJobInspections. `inspReqSeq` drops a
  // read that lands after a newer one started. `inspAssetNames` resolves
  // jobDay.assets[] ids to names with one getDoc per id — a PIN session never
  // subscribes the assets collection, so JobBoardState.assets is [] there.
  // `inspShowAll` = the per-job "Show all" toggle past the first 3 rows.
  inspectionsByJob: {},
  inspReqSeq: {},
  inspAssetNames: {},
  inspShowAll: {},
  // Clock state (Driver PIN clock access)
  clockEntries: [],        // Active clock entries for this tenant
  clockedInEntryId: null,  // Current driver's active entry ID
  // Build Ticket form state
  btEquipment: [],      // [{ assetId, assetName, assetType, fleetNumber, rate, rateUnit, startTime, endTime, notes, _dirty, _saved, _restored, _lineId, _followsClock }] — _followsClock (TASK-572): window tracks the clock window; cleared on manual time edit
  btDowntime: [],       // [{ startTime, endTime, notes, _lineId, _saved, _restored, _dirty, _followsClock }]
  btStandby: [],        // [{ startTime, endTime, notes, _lineId, _saved, _restored, _dirty, _followsClock }]
  btPhotos: [],         // [{ file, preview, name, _existing }]
  btTicketWorkTimes: null, // { workStartTime, workStopTime } from ticket doc
  _btTicketId: null,    // ticket ID for Save
  _btEntry: null,       // clock entry being clocked out
  _btEditedClockIn: null,
  _btEditedClockOut: null,
  // TASK-1180 — forgotten-clock-out OUT-ONLY correction (driver flow):
  _btDriverAdjustedClockOut: false, // true when the shrink came from jbApplyForgotClockOut
  _btDriverAdjustReason: null       // mandatory driver reason (FLSA contemporaneous justification)
};

// ============================================
// INITIALIZATION
// ============================================
export function initJobBoard(container, tenantId) {
  if (!tenantId) {
    container.innerHTML = `
      <div class="empty-state" style="padding:40px;text-align:center;">
        <span class="material-icons" style="font-size:64px;color:var(--text-muted);">error_outline</span>
        <div class="empty-state-text">Unable to load Job Board. Please try again.</div>
      </div>
    `;
    return () => {};
  }

  // HOTFIX 2026-05-28 — clear any existing subscriptions BEFORE subscribing
  // fresh. Prevents duplicate-subscription bug when initJobBoard is called
  // twice without intermediate cleanup (e.g. PIN session restore →
  // navigateToView('jobboard') → redirect logic triggers another
  // navigateToView('jobboard'); see app.js:6513 — driver-PIN-dispatch
  // redirect cascading). Each duplicate set adds 9 onSnapshot listeners,
  // so 2 init calls = 18 listeners = 14+ cascading renderJobBoard calls
  // observed in Chris's console logs. Cleanup here makes the second call
  // a no-op load (idempotent) instead of compounding.
  if (Array.isArray(JobBoardState.unsubscribers) && JobBoardState.unsubscribers.length > 0) {
    console.log('[JobBoard] Clearing', JobBoardState.unsubscribers.length, 'stale subscriptions before re-subscribing');
    JobBoardState.unsubscribers.forEach(unsub => { try { unsub(); } catch (_) {} });
    JobBoardState.unsubscribers = [];
  }

  // P0 1019 — JobBoardState is module-scoped and survives a logout / company
  // switch in this tab. A different tenant starts with EMPTY lists, so nothing
  // (the clock-in truck picker, "In a different truck?", the driver Vehicles
  // list via the fleet bridge) can show the previous company's roster while the
  // new snapshots load — or forever, if one of them is refused.
  if (JobBoardState.tenantId !== tenantId) {
    JobBoardState.jobs = [];
    JobBoardState.jobDays = [];
    JobBoardState.people = [];
    JobBoardState.trucks = [];
    JobBoardState.trailers = [];
    JobBoardState.assets = [];
    JobBoardState.customers = [];
    JobBoardState.permits = [];
    JobBoardState.tickets = [];
    JobBoardState.ticketLinesCache = {};
    if (window.AppState) {
      window.AppState.fleetTrucks = null;
      window.AppState.fleetTrailers = null;
      window.AppState.fleetPeople = null;
      window.AppState.fleetTenantId = null;
    }
  }
  JobBoardState.tenantId = tenantId;
  JobBoardState.currentDate = new Date();
  JobBoardState.dataLoaded = { jobs: false, jobDays: false };
  // Clear per-customer caches on (re)init so a reused session that switches
  // tenants never serves a stale customerId -> contract-settings mapping.
  JobBoardState.contractSigCache = {};
  JobBoardState.jobDaySigReadTime = {}; // TASK-10190 — per tenant
  JobBoardState.jobDaySigReadState = {};
  JobBoardState.projectsCache = {};
  // TASK-569 — drop the calendar's on-demand month cache so a reused session
  // that switches tenants never renders a prior tenant's past-month jobDays.
  JobBoardState._calMonthCache = {};
  JobBoardState._calMonthFetching = {};
  JobBoardState._calMonthError = {};

  // Render the job board UI
  container.innerHTML = getJobBoardHTML();

  // Event delegation for clock button — checks live state, not just data attributes
  const clockBtn = document.getElementById('btn-driver-clockin');
  if (clockBtn) {
    clockBtn.addEventListener('click', () => {
      // TASK-355 2026-05-22: resolve PIN -> SPA member personId so SPA members
      // (jobboard granted, no PIN) can clock in/out from this button too.
      const driverId = getActiveJobBoardPersonId();
      const myEntry = JobBoardState.clockEntries.find(e => e.personId === driverId);
      // Fallback: use data-attributes if subscription is stale but button shows Clock Out
      const btnAction = clockBtn.dataset.action;
      const btnEntryId = clockBtn.dataset.entryId;
      console.log('[JobBoard] Clock button clicked:', { driverId, myEntry: myEntry?.id, entriesCount: JobBoardState.clockEntries.length, btnAction, btnEntryId });
      if (myEntry) {
        clockOutStandalone(myEntry.id, myEntry.jobId || '', myEntry.jobDayId || '');
      } else if (btnAction === 'clock-out' && btnEntryId) {
        // Data-attribute fallback — button was rendered as Clock Out but subscription lost the entry
        console.warn('[JobBoard] Using data-attribute fallback for clock-out');
        clockOutStandalone(btnEntryId, clockBtn.dataset.jobId || '', clockBtn.dataset.jobDayId || '');
      } else {
        openStandaloneClockIn();
      }
    });
  }

  // Subscribe to real-time data
  subscribeToData(tenantId);

  // TASK-4666 — TASK-4594's stash was only ever offered for retry when the SAME
  // ticket's Build Ticket sheet was reopened — which, after a clock-out, nobody
  // does; the rows sat on the phone until the 14-day TTL deleted them. Board
  // entry is the one surface every driver returns to, so recover here:
  // auto-send when the network allows (strictly additive merge), banner when it
  // does not. Deferred so first paint wins; never a modal, never blocks.
  setTimeout(() => { try { _jbRecoverAbandonedCsd(); } catch (_) { /* best-effort */ } }, 1500);

  // Return cleanup function
  return () => {
    JobBoardState.unsubscribers.forEach(unsub => unsub());
    JobBoardState.unsubscribers = [];
  };
}

// ============================================
// HTML TEMPLATE
// ============================================
function getJobBoardHTML() {
  const today = new Date();
  const dateDisplay = today.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric'
  });

  // 2026-05-28 Option A — PIN drivers get the dispatch.js driver bottom-nav
  // (Inspections / Clock In / Exam Report / Actions) at the bottom of the
  // jobboard view, and the redundant top-bar colored action buttons are
  // hidden via .driver-mode-pin .jobboard-actions{display:none}. Per Chris:
  // "make sure everything is still available in bottom nav" — DVIR/JSA via
  // Inspections tab, Clock In + Build Ticket via the bottom Clock button
  // (becomes Build Ticket in ticket-mode), Time Off + Record Expense via the
  // Actions sheet tiles. All handlers stay on window.DispatchModule.
  const _isPinDriver = !!window.AppState?.driverModePin;
  // v1.15.923 — inject jobboard-owned clock handler so the bottom-nav Clock
  // button opens openStandaloneClockIn (reads JobBoardState.customers) rather
  // than dispatch's openDriverClockIn (reads empty DispatchState.customers).
  const _driverBottomNav = (_isPinDriver && window.DispatchModule?.getDriverBottomNavHTML)
    ? window.DispatchModule.getDriverBottomNavHTML({ clockOnClick: 'window.JobBoardModule.handleBottomNavClock()' })
    : '';
  const _driverActionsSheet = (_isPinDriver && window.DispatchModule?.getDriverActionsSheetHTML)
    ? window.DispatchModule.getDriverActionsSheetHTML()
    : '';

  // H3 — read-only access-link session: hide every write action (DVIR/JSA/expense/
  // clock) and show a banner pointing the driver to PIN login to take action.
  const _isReadOnlyLink = !!window.isReadOnlyLinkSession?.();

  // TASK-9374 — calendar mode is a one-row, viewport-locked layout keyed off
  // .jb-cal-mode / .jobboard-jobs--calendar (CSS). Set at first paint from the
  // saved mode; setViewMode + renderCalendarView keep them in sync after that.
  const _calMode = JobBoardState.viewMode === 'calendar';

  return `
    <div class="jobboard-view${_calMode ? ' jb-cal-mode' : ''}${_isReadOnlyLink ? ' jb-readonly' : ''}">
      <!-- TASK-9374: .jb-chrome is display:contents in DAY mode (zero layout
           effect — day mode is untouched) and ONE flex row in CALENDAR mode:
           ‹ Month Year › · Day/Calendar segmented control · customer-filter
           chip · language globe. .jobboard-header MUST keep that class — the
           driver i18n pill mounts into it (driver-i18n-es.js HEADER_SELECTORS)
           and early-returns once #driverLangToggle exists. -->
      <div class="jb-chrome">
      <!-- Header -->
      <div class="jobboard-header">
        <h1 class="jobboard-title">
          <span class="material-icons">assignment</span>
          Job Board
        </h1>
        ${_isReadOnlyLink ? '' : `
        <div class="jobboard-actions">
          <button class="btn-dvir" onclick="window.location.hash='#/pretrip'">
            <span class="material-icons">fact_check</span>
            Start DVIR
          </button>
          <button class="btn-jsa" onclick="window.JobBoardModule.startJSA()">
            <span class="material-icons">health_and_safety</span>
            Start JSA
          </button>
          <button id="btn-driver-timeoff" class="btn-jsa" onclick="window.JobBoardModule.openTimeOffRequest()" style="background:#7C3AED;display:none;">
            <span class="material-icons">event_busy</span>
            Time Off
          </button>
          <button id="btn-driver-record-expense" class="btn-jsa" onclick="window.JobBoardModule.openExpenseModal(null, null, null)" style="background:#D97706;display:none;">
            <span class="material-icons">attach_money</span>
            Record Expense
          </button>
          <button id="btn-driver-clockin" class="btn-dvir" data-action="clock-in" style="background:#166534;display:none;">
            <span class="material-icons">login</span>
            <span id="btn-driver-clockin-label"></span>
          </button>
        </div>`}
        <!-- TASK-9374: calendar-mode customer-filter chip mounts here
             (renderCalendarView); display:none in day mode. -->
        <div id="jb-cal-filter-mount" class="jb-cal-filter-mount"></div>
        <!-- TASK-9370 / TASK-9561 — "Add Job". A normal header button now, not
             a full-width bar: right end of the header row in Day view, and in
             Calendar view the header IS the right-hand group of the one-row
             chrome, so it sits beside the customer-filter chip (styles.css
             .jb-addjob-mount). Painted by _jbRenderDriverAddJobStrip() ONLY when
             the tenant's "Drivers can add and edit jobs" setting is on; zero
             chrome otherwise (display:none, empty). -->
        <div id="jb-driver-addjob-strip" class="jb-addjob-mount" style="display:none;"></div>
      </div>
      ${_isReadOnlyLink ? `
      <div class="jobboard-readonly-banner" style="display:flex;align-items:center;gap:10px;margin:0 0 12px 0;padding:12px 14px;background:linear-gradient(135deg,#eff6ff,#dbeafe);border:1px solid #bfdbfe;border-radius:var(--radius-md);">
        <span class="material-icons" style="font-size:20px;color:#2563eb;">visibility</span>
        <div style="flex:1;font-size:0.88rem;color:#1e3a8a;line-height:1.4;">
          <strong>Read-only preview.</strong> You're viewing the schedule. To clock in, build a ticket, or submit an inspection, log in with your driver PIN.
        </div>
        <a href="/" class="btn btn-primary" style="text-decoration:none;white-space:nowrap;font-size:0.85rem;padding:8px 14px;">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">login</span>Log in with PIN
        </a>
      </div>` : ''}

            <!-- View Mode Toggle (Day / Calendar). TASK-9374: former inline styles
           moved to .jb-mode-* (same values) so calendar mode can compact the
           control; labels are spans so phones can show icon-only. -->
      <div class="jb-mode-toggle">
        <div class="jb-mode-seg">
          <button id="jobboard-mode-day" type="button" class="jb-mode-btn" onclick="window.JobBoardModule.setViewMode('day')">
            <span class="material-icons">view_day</span><span class="jb-mode-lbl">Day</span>
          </button>
          <button id="jobboard-mode-calendar" type="button" class="jb-mode-btn" onclick="window.JobBoardModule.setViewMode('calendar')">
            <span class="material-icons">calendar_month</span><span class="jb-mode-lbl">Calendar</span>
          </button>
        </div>
      </div>

      <!-- Date Navigation -->
      <div class="jobboard-date-nav">
        <button class="jobboard-nav-btn" onclick="window.JobBoardModule.navigate(-1)">
          <span class="material-icons">chevron_left</span>
        </button>
        <div class="jobboard-date-display" id="jobboard-current-date">${dateDisplay}</div>
        <button class="jobboard-nav-btn" onclick="window.JobBoardModule.navigate(1)">
          <span class="material-icons">chevron_right</span>
        </button>
      </div>

      <!-- Job Count (day mode only) -->
      <div class="jobboard-count" id="jobboard-job-count">Loading...</div>
      </div><!-- /.jb-chrome -->

      <!-- TASK-592 — "You're in: Duck" self-confirm strip. Populated by
           renderEquipTieStatus() ONLY when equipmentTie is ON and the viewer's
           own active entry carries assetIds[]; stays display:none (zero
           chrome) for OFF tenants and kiosk/legacy entries. -->
      <div id="jb-equip-status" style="display:none;"></div>

      <!-- TASK-4666 — clock-out stash recovery. Populated by
           _jbRecoverAbandonedCsd() on board entry ONLY when an unexpired
           TASK-4594 stash exists that could not be auto-sent; zero chrome
           otherwise. -->
      <div id="jb-stash-recovery"></div>

      <!-- TASK-424 #5 (C3): Load older. Board streams the last ~90 days (+ all
           upcoming) by default; this widens the live window further back.
           Hidden in driver mode and once the window is "all". -->
      <div class="jb-load-older-row">
        <button id="jobboard-load-older" type="button" style="display:none;border:1px solid var(--border-light);background:var(--bg-elevated);color:var(--text-secondary);padding:5px 14px;border-radius:999px;font-size:0.8rem;font-weight:600;cursor:pointer;" onclick="window.JobBoardModule.loadOlderJobBoard()">
          <span class="material-icons" style="font-size:15px;vertical-align:middle;margin-right:4px;">history</span><span class="jb-load-older-label">Load older</span>
        </button>
      </div>

      <!-- Jobs List (day mode) OR Calendar Grid (calendar mode) -->
      <div class="jobboard-jobs${_calMode ? ' jobboard-jobs--calendar' : ''}" id="jobboard-content">
        <div class="loading-spinner"></div>
      </div>

      <!-- TASK-361 (2026-05-26) — "My Recent JSAs" section was relocated to
           Inspections > JSA tab where drivers look for their safety forms.
           Implementation moved to public/src/views/jsa.js renderMyJsaSection. -->

      ${_driverBottomNav}
    </div>
    ${_driverActionsSheet}
  `;
}

// ============================================
// DATA SUBSCRIPTION
// ============================================
function subscribeToData(tenantId) {
  // TASK-4585 — clear the allocation view before re-subscribing. A stale `true`
  // from a previous tenant/session, sitting next to an empty list, would mark a
  // fresh modal "settled" and waive the requirement before the new snapshot
  // lands (COD Code Reviewer). Same shape as dispatch.js resetting its load
  // flags at subscribe time.
  JobBoardState.costAllocations = [];
  JobBoardState._costAllocationsLoaded = false;
  // Safety check for db
  if (!db) {
    console.error('[JobBoard] Firebase db not initialized');
    document.getElementById('jobboard-content').innerHTML = `
      <div class="empty-state" style="padding:40px;text-align:center;">
        <span class="material-icons" style="font-size:64px;color:var(--text-muted);">error_outline</span>
        <div class="empty-state-text">Database connection error. Please refresh the page.</div>
      </div>
    `;
    return;
  }

  function showError() {
    const container = document.getElementById('jobboard-content');
    if (container) {
      container.innerHTML = `
        <div class="jobboard-empty">
          <span class="material-icons">cloud_off</span>
          <div>Failed to load schedule. Please try again.</div>
        </div>
      `;
    }
    const countEl = document.getElementById('jobboard-job-count');
    if (countEl) countEl.textContent = '';
  }

  // TASK-345 Slice M (2026-05-26): PIN-driver date-filter on jobs + jobDays.
  // Mirrors the dispatch.js implementation — see that file for the full
  // rationale + kill-switch. Same dual-query pattern: recent + legacy-active.
  // Kill switch: flip JB_FAST_PIN_DRIVER_FILTER (module scope) to false to revert.
  const _isPinDriver = JB_FAST_PIN_DRIVER_FILTER && window.AppState?.driverModePin === true;

  const _now = new Date();
  // Snap to start-of-day local time (not rolling now - 7*24h) to align
  // deterministically with day-noon-normalized doc timestamps. See dispatch.js
  // for full rationale.
  const _todayStartLocal = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate());
  const _sevenDaysAgo = new Date(_todayStartLocal.getTime() - 7 * 24 * 60 * 60 * 1000);
  const _sevenDaysAgoTs = Timestamp.fromDate(_sevenDaysAgo);

  // TASK-424 #5 (C3): admin window cutoff — same machinery as the driver's 7d
  // window but with the wider, "Load older"-adjustable admin lookback.
  // Number.isFinite (NOT `|| 90`) so the 0 = "all" sentinel survives.
  const _rawAdminDays = Number(JobBoardState.adminWindowDaysBack);
  const _adminDaysBack = Number.isFinite(_rawAdminDays) ? _rawAdminDays : 90;
  const _adminWindowAll = _adminDaysBack <= 0;
  const _adminCutoff = new Date(_todayStartLocal.getTime() - _adminDaysBack * 24 * 60 * 60 * 1000);
  const _adminCutoffTs = Timestamp.fromDate(_adminCutoff);
  const _windowedAdmin = !_isPinDriver && !_adminWindowAll;

  // Subscribe to job days (PIN drivers get 7-day window; admins get the wider
  // _adminCutoffTs window unless expanded to "all" via Load older; jobDays have
  // no status field so the date filter is sufficient. See dispatch.js for notes.)
  const jobDaysQuery = _isPinDriver
    ? query(
        collection(db, 'tenants', tenantId, 'jobDays'),
        where('date', '>=', _sevenDaysAgoTs),
        orderBy('date', 'asc')
      )
    : _windowedAdmin
      ? query(
          collection(db, 'tenants', tenantId, 'jobDays'),
          where('date', '>=', _adminCutoffTs),
          orderBy('date', 'asc')
        )
      : query(
          collection(db, 'tenants', tenantId, 'jobDays'),
          orderBy('date', 'asc')
        );

  let _jobDaysFellBack = false;
  const unsubJobDays = onSnapshot(jobDaysQuery, (snapshot) => {
    JobBoardState.jobDays = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    JobBoardState.dataLoaded.jobDays = true;
    renderJobBoard();
  }, (error) => {
    console.error('[JobBoard] JobDays subscription error:', error);
    // NO-REGRESSION FALLBACK: if the windowed admin jobDays query errors (e.g.
    // missing index), fall back to the unbounded full-collection stream so the
    // board never blanks. Fires at most once.
    if (_windowedAdmin && !_jobDaysFellBack) {
      _jobDaysFellBack = true;
      console.warn('[JobBoard] Windowed admin jobDays query failed — falling back to unbounded full-collection stream.');
      const unsubJobDaysFallback = onSnapshot(
        query(collection(db, 'tenants', tenantId, 'jobDays'), orderBy('date', 'asc')),
        (snapshot) => {
          JobBoardState.jobDays = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          JobBoardState.dataLoaded.jobDays = true;
          renderJobBoard();
        },
        (err2) => { console.error('[JobBoard] Admin fallback jobDays subscription error:', err2); showError(); }
      );
      JobBoardState.unsubscribers.push(unsubJobDaysFallback);
      return;
    }
    showError();
  });

  JobBoardState.unsubscribers.push(unsubJobDays);

  // Subscribe to jobs
  if (_isPinDriver) {
    console.log('[JobBoard] TASK-345 Slice M: PIN-driver date-filtered jobs subscription');

    const _jobsRecent = new Map();
    const _jobsLegacy = new Map();
    const _jobsStringDated = new Map(); // TASK-4610 quarantine — normally empty
    let _gotRecent = false;
    let _gotLegacy = false;

    const _mergeAndPublish = () => {
      if (!_gotRecent || !_gotLegacy) return;
      const merged = new Map(_jobsLegacy);
      for (const [id, job] of _jobsRecent) merged.set(id, job);
      // TASK-4610 — string-dated jobs the two range queries CANNOT see. Lowest
      // precedence: a correctly-typed copy always wins.
      for (const [id, job] of _jobsStringDated) if (!merged.has(id)) merged.set(id, job);
      JobBoardState.jobs = Array.from(merged.values());
      JobBoardState.dataLoaded.jobs = true;
      console.log('[JobBoard] Jobs updated (PIN-filtered):', JobBoardState.jobs.length, '(recent:', _jobsRecent.size, '+ legacy-active:', _jobsLegacy.size, ')');
      renderJobBoard();
    };

    // Query 1: recent + future jobs (startDate >= 7 days ago).
    const recentJobsQuery = query(
      collection(db, 'tenants', tenantId, 'jobs'),
      where('startDate', '>=', _sevenDaysAgoTs),
      orderBy('startDate', 'desc')
    );
    const unsubRecentJobs = onSnapshot(recentJobsQuery, (snapshot) => {
      _jobsRecent.clear();
      snapshot.docs.forEach(d => _jobsRecent.set(d.id, normalizeJobDoc(d.id, d.data())));
      _gotRecent = true;
      _mergeAndPublish();
    }, (error) => {
      console.error('[JobBoard] Recent jobs subscription error:', error);
      _gotRecent = true;
      _mergeAndPublish();
      showError();
    });
    JobBoardState.unsubscribers.push(unsubRecentJobs);

    // Query 2 (legacy multi-week + recently-closed fallback): endDate >= 7d ago.
    // Window matches jobDays window (also 7d-back). Without this, jobs
    // closed within the last week (startDate older than 7d, endDate within
    // the last week) have jobDays in our jobDays subset but the renderer
    // (getJobsForDate at line ~1603) silently drops them because the
    // job-doc dereference fails. Status filter is CLIENT-SIDE (drop
    // 'finished') so jobs with status===undefined are still picked up.
    const legacyJobsQuery = query(
      collection(db, 'tenants', tenantId, 'jobs'),
      where('endDate', '>=', _sevenDaysAgoTs),
      orderBy('endDate', 'asc')
    );
    const unsubLegacyJobs = onSnapshot(legacyJobsQuery, (snapshot) => {
      _jobsLegacy.clear();
      snapshot.docs.forEach(d => {
        const data = d.data();
        if (data.status === 'finished') return;
        _jobsLegacy.set(d.id, normalizeJobDoc(d.id, data));
      });
      _gotLegacy = true;
      _mergeAndPublish();
    }, (error) => {
      console.error('[JobBoard] Legacy-active jobs subscription error:', error);
      _gotLegacy = true;
      _mergeAndPublish();
    });
    JobBoardState.unsubscribers.push(unsubLegacyJobs);

    // Query 3 (TASK-4610 quarantine): jobs whose startDate was stored as a
    // STRING. Queries 1 and 2 are range filters against a Timestamp and are
    // structurally incapable of returning them — this is the ONLY way a PIN
    // driver ever sees a job an outside writer (seed script, GAS import,
    // Cloud Function) typed wrong. Zero docs in a healthy tenant; never gates
    // _mergeAndPublish, so a failure here cannot stall the board.
    const unsubStringDatedJobs = subscribeStringDatedJobs(tenantId, (snapshot) => {
      _jobsStringDated.clear();
      for (const [id, job] of stringDatedJobsInWindow(snapshot, _sevenDaysAgoTs)) _jobsStringDated.set(id, job);
      if (_jobsStringDated.size) {
        console.warn('[JobBoard] TASK-4610 — rescued', _jobsStringDated.size, 'in-window job(s) with a non-Timestamp startDate/endDate:', Array.from(_jobsStringDated.keys()));
      }
      _mergeAndPublish();
    });
    JobBoardState.unsubscribers.push(unsubStringDatedJobs);

    // UX timeout: 3s fallback if legacy query lags — publish recent set
    // rather than stalling the spinner. _mergeAndPublish is idempotent —
    // when legacy DOES land it republishes the union. See dispatch.js for
    // full Skeptic-V2-rebuttal rationale. Defensive gate: only publish-
    // partial if JobBoardState.jobs is still empty.
    // HOTFIX 2026-05-28 (mirror of dispatch.js v1.15.904): the original
    // condition required `_gotRecent && !_gotLegacy` — if BOTH queries hung
    // (rules block / network fail / both errored) the timeout did nothing and
    // the spinner stayed forever. Now fires if EITHER snapshot hasn't landed,
    // publishes the union of whatever we got. Empty result is acceptable UX
    // (renders empty board) vs infinite spinner which strands the driver.
    setTimeout(() => {
      if (_gotRecent && _gotLegacy) return; // both arrived in time
      if (JobBoardState.jobs && JobBoardState.jobs.length > 0) return; // already populated
      console.warn('[JobBoard] Jobs subscription slow after 3s — publishing partial set', {
        gotRecent: _gotRecent,
        gotLegacy: _gotLegacy,
        recentSize: _jobsRecent.size,
        legacySize: _jobsLegacy.size,
      });
      const merged = new Map(_jobsLegacy);
      for (const [id, job] of _jobsRecent) merged.set(id, job);
      JobBoardState.jobs = Array.from(merged.values());
      JobBoardState.dataLoaded.jobs = true;
      _gotRecent = true;
      _gotLegacy = true;
      renderJobBoard();
    }, 3000);
  } else if (_adminWindowAll) {
    // Admin "Load older → all" — stream the entire collection (original
    // pre-TASK-424 behavior), only after explicit opt-in via Load older.
    const unsubJobs = onSnapshot(collection(db, 'tenants', tenantId, 'jobs'), (snapshot) => {
      // TASK-4610 — the unbounded stream DOES return string-dated jobs (no range
      // filter to exclude them), so normalizing here is what puts them on the
      // right DAY instead of the day before (UTC-midnight parse).
      JobBoardState.jobs = snapshot.docs.map(doc => normalizeJobDoc(doc.id, doc.data()));
      JobBoardState.dataLoaded.jobs = true;
      renderJobBoard();
    }, (error) => {
      console.error('[JobBoard] Jobs subscription error:', error);
      showError();
    });

    JobBoardState.unsubscribers.push(unsubJobs);
  } else {
    // Admin / non-PIN session — TASK-424 #5 (C3): windowed jobs stream.
    // Mirrors the PIN-driver dual-query (recent + legacy-active) with the wider
    // admin lookback (_adminCutoffTs). `>=` lower bound, no upper bound → ALL
    // upcoming/future jobs always present; only OLDER jobs excluded. Load older
    // widens the window + resubscribes.
    console.log('[JobBoard] TASK-424: admin date-filtered jobs subscription (window:', _adminDaysBack, 'd back)');

    const _jobsRecent = new Map();
    const _jobsLegacy = new Map();
    const _jobsStringDated = new Map(); // TASK-4610 quarantine — normally empty
    let _gotRecent = false;
    let _gotLegacy = false;
    let _fellBackToUnbounded = false;

    const _mergeAndPublish = () => {
      if (!_gotRecent || !_gotLegacy) return;
      const merged = new Map(_jobsLegacy);
      for (const [id, job] of _jobsRecent) merged.set(id, job);
      // TASK-4610 — string-dated jobs the two range queries CANNOT see.
      for (const [id, job] of _jobsStringDated) if (!merged.has(id)) merged.set(id, job);
      JobBoardState.jobs = Array.from(merged.values());
      JobBoardState.dataLoaded.jobs = true;
      console.log('[JobBoard] Jobs updated (admin-windowed):', JobBoardState.jobs.length, '(recent:', _jobsRecent.size, '+ legacy-active:', _jobsLegacy.size, ')');
      renderJobBoard();
    };

    // NO-REGRESSION FALLBACK: if a windowed query errors (e.g. missing index),
    // fall back to the original unbounded full-collection stream so the board
    // never blanks. Fires at most once.
    const _fallbackToUnbounded = (error) => {
      if (_fellBackToUnbounded) return;
      _fellBackToUnbounded = true;
      console.warn('[JobBoard] Windowed admin jobs query failed — falling back to unbounded full-collection stream.', error);
      const unsubFallback = onSnapshot(collection(db, 'tenants', tenantId, 'jobs'), (snapshot) => {
        JobBoardState.jobs = snapshot.docs.map(doc => normalizeJobDoc(doc.id, doc.data()));
        JobBoardState.dataLoaded.jobs = true;
        console.log('[JobBoard] Jobs updated (admin fallback, unbounded):', JobBoardState.jobs.length);
        renderJobBoard();
      }, (err2) => { console.error('[JobBoard] Admin fallback jobs subscription error:', err2); showError(); });
      JobBoardState.unsubscribers.push(unsubFallback);
    };

    const recentJobsQuery = query(
      collection(db, 'tenants', tenantId, 'jobs'),
      where('startDate', '>=', _adminCutoffTs),
      orderBy('startDate', 'desc')
    );
    const unsubRecentJobs = onSnapshot(recentJobsQuery, (snapshot) => {
      _jobsRecent.clear();
      snapshot.docs.forEach(d => _jobsRecent.set(d.id, normalizeJobDoc(d.id, d.data())));
      _gotRecent = true;
      _mergeAndPublish();
    }, (error) => {
      console.error('[JobBoard] Admin recent jobs subscription error:', error);
      _fallbackToUnbounded(error);
    });
    JobBoardState.unsubscribers.push(unsubRecentJobs);

    const legacyJobsQuery = query(
      collection(db, 'tenants', tenantId, 'jobs'),
      where('endDate', '>=', _adminCutoffTs),
      orderBy('endDate', 'asc')
    );
    const unsubLegacyJobs = onSnapshot(legacyJobsQuery, (snapshot) => {
      _jobsLegacy.clear();
      snapshot.docs.forEach(d => {
        const data = d.data();
        if (data.status === 'finished') return;
        _jobsLegacy.set(d.id, normalizeJobDoc(d.id, data));
      });
      _gotLegacy = true;
      _mergeAndPublish();
    }, (error) => {
      console.error('[JobBoard] Admin legacy jobs subscription error:', error);
      _fallbackToUnbounded(error);
    });
    JobBoardState.unsubscribers.push(unsubLegacyJobs);

    // Query 3 (TASK-4610 quarantine) — see the PIN-driver branch above for the
    // full rationale. Same window (_adminCutoffTs), zero docs in a healthy
    // tenant, never gates the publish.
    const unsubAdminStringDatedJobs = subscribeStringDatedJobs(tenantId, (snapshot) => {
      _jobsStringDated.clear();
      for (const [id, job] of stringDatedJobsInWindow(snapshot, _adminCutoffTs)) _jobsStringDated.set(id, job);
      if (_jobsStringDated.size) {
        console.warn('[JobBoard] TASK-4610 — rescued', _jobsStringDated.size, 'in-window job(s) with a non-Timestamp startDate/endDate:', Array.from(_jobsStringDated.keys()));
      }
      _mergeAndPublish();
    });
    JobBoardState.unsubscribers.push(unsubAdminStringDatedJobs);
  }

  // Subscribe to people (for showing assignments)
  const unsubPeople = onSnapshot(collection(db, 'tenants', tenantId, 'people'), (snapshot) => {
    JobBoardState.people = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    window.ClockMode?.notePeople?.(JobBoardState.people, !snapshot.metadata?.fromCache); // TASK-9553 per-person clock mode
    // TASK-459 — publish people to the shared store so the driver Team Contact
    // Directory (dispatch.js openDriverContactDirectory) can resolve them for
    // PIN drivers, who never run dispatch's subscribeToData. Mirrors the
    // fleetTrucks bridge below — decoupled via AppState, no circular dep.
    if (window.AppState) { window.AppState.fleetPeople = JobBoardState.people; window.AppState.fleetTenantId = tenantId; } // P0 1019 — bridge stamped with its tenant
    renderJobBoard();
  }, (error) => {
    console.error('[JobBoard] People subscription error:', error);
  });
  JobBoardState.unsubscribers.push(unsubPeople);

  // Subscribe to trucks
  const unsubTrucks = onSnapshot(collection(db, 'tenants', tenantId, 'trucks'), (snapshot) => {
    JobBoardState.trucks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    // TASK-379 — publish fleet to the shared store so dispatch.js's
    // openDriverVehicleList / showDriverVehicleInfo (the single renderer) can
    // fall back to it for PIN drivers, who never run dispatch's subscribeToData
    // (DispatchState.trucks stays empty). Decoupled via AppState rather than a
    // direct cross-module call — no circular dependency.
    if (window.AppState) { window.AppState.fleetTrucks = JobBoardState.trucks; window.AppState.fleetTenantId = tenantId; } // P0 1019
    renderJobBoard();
  }, (error) => {
    console.error('[JobBoard] Trucks subscription error:', error);
  });
  JobBoardState.unsubscribers.push(unsubTrucks);

  // Subscribe to assets (for load counter — admin feature)
  // TASK-345 follow-up 2026-05-28: skip for PIN drivers. _sharedComputeLoadInfo
  // uses assets only when a job carries an asset reference; the driver job-card
  // displays a load counter chip when present. Empty assets array → no chip,
  // graceful fallback. Saves a full-collection download on cold load for PIN.
  if (!_isPinDriver) {
    const unsubAssets = onSnapshot(collection(db, 'tenants', tenantId, 'assets'), (snapshot) => {
      JobBoardState.assets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    }, (error) => {
      console.error('[JobBoard] Assets subscription error:', error);
    });
    JobBoardState.unsubscribers.push(unsubAssets);
  }

  // Subscribe to trailers
  const unsubTrailers = onSnapshot(collection(db, 'tenants', tenantId, 'trailers'), (snapshot) => {
    JobBoardState.trailers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    // TASK-379 — publish to shared store for dispatch's vehicle modal fallback (see trucks sub above).
    if (window.AppState) { window.AppState.fleetTrailers = JobBoardState.trailers; window.AppState.fleetTenantId = tenantId; } // P0 1019
    renderJobBoard();
  }, (error) => {
    console.error('[JobBoard] Trailers subscription error:', error);
  });
  JobBoardState.unsubscribers.push(unsubTrailers);

  // Subscribe to customers — TASK-9694 S6a: the crew-safe customerDirectory
  // (name + flags), never the full `customers` docs (contacts, phones, notes).
  // Every field this board reads (name, company, status, type,
  // internalTimeOnly) is in the directory. See utils/customerSource.js.
  const unsubCustomers = _jbCustomerSource().subscribe(tenantId, (list) => {
    JobBoardState.customers = list;
    renderJobBoard();
  }, {
    label: 'JobBoard customers',
    onError: (error) => console.error('[JobBoard] Customers subscription error:', error),
  });
  JobBoardState.unsubscribers.push(unsubCustomers);

  // Load 811 locate permits ONCE (getDocs, not onSnapshot) — drivers need to
  // open the permit linked to their job via job.permitId. Load-once (not a live
  // listener) keeps the perf-sensitive driver path lean; permits rarely change
  // mid-shift. firestore.rules:410 allows driver/canReadTenant read. Re-renders
  // the board so an already-open day picks up the data when it lands.
  getDocs(collection(db, 'tenants', tenantId, 'permits'))
    .then((snapshot) => {
      if (JobBoardState.tenantId !== tenantId) return; // P0 1019 COD — late answer for a previous company
      JobBoardState.permits = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      renderJobBoard();
    })
    .catch((error) => { console.error('[JobBoard] Permits load error:', error); });

  // Subscribe to tickets (for driver ticket view)
  // TASK-345 follow-up 2026-05-28 (B): for PIN drivers, gate on
  // settings.showTicketsInDriverView. Skip the sub entirely when disabled
  // (mirrors dispatch.js Slice S2 pattern). When enabled, date-filter via
  // `workDate` (YYYY-MM-DD string) to last 7 days — Skeptic V2 catch:
  // ticket `date` field is historically mixed (Timestamp on some, string
  // on legacy), but `workDate` is the consistent YYYY-MM-DD string per
  // billing.js:3768 (the canonical ticket-date source-of-truth).
  const _ticketsEnabledForDriver = window.AppState?.settings?.showTicketsInDriverView === true;
  if (!_isPinDriver || _ticketsEnabledForDriver) {
    let ticketsQuery;
    if (_isPinDriver) {
      const _yyyy = _sevenDaysAgo.getFullYear();
      const _mm = String(_sevenDaysAgo.getMonth() + 1).padStart(2, '0');
      const _dd = String(_sevenDaysAgo.getDate()).padStart(2, '0');
      const _sevenDaysAgoISO = `${_yyyy}-${_mm}-${_dd}`;
      ticketsQuery = query(
        collection(db, 'tenants', tenantId, 'tickets'),
        where('workDate', '>=', _sevenDaysAgoISO)
      );
    } else {
      ticketsQuery = collection(db, 'tenants', tenantId, 'tickets');
    }
    const unsubTickets = onSnapshot(ticketsQuery, (snapshot) => {
      JobBoardState.tickets = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      renderJobBoard();
    }, (error) => {
      console.error('[JobBoard] Tickets subscription error:', error);
    });
    JobBoardState.unsubscribers.push(unsubTickets);
  }

  // Subscribe to cost allocations for the driver-expense submission flow.
  // Drivers need read access to populate the modal dropdown; rules at
  // firestore.rules:costAllocations allow isTenantMember read.
  // 2026-05-28: lazy-load was COD-blocked (UX Lead ❌ on modal-open delay
  // causing multi-click). Keeping the sub for all sessions.
  // TASK-4602 — drain queued ticket photos when a connection returns, mirroring
  // how pretrip drives its own drain. Fire-and-forget: a drain must never block
  // or throw into the board's init path.
  if (!JobBoardState._pqOnlineBound) {
    JobBoardState._pqOnlineBound = true;
    // TASK-4635 — both arms drain on reconnect. Independent catches: a photo
    // drain that throws must not stop a queued permit from going up.
    const _drainAll = () => {
      _jbDrainPhotoQueue().catch(() => {});
      _jbDrainSectionFileQueue().catch(() => {});
    };
    window.addEventListener('online', _drainAll);
    if (navigator.onLine) _drainAll();
  }

  const unsubAlloc = onSnapshot(collection(db, 'tenants', tenantId, 'costAllocations'), (snapshot) => {
    JobBoardState.costAllocations = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(a => a.active !== false)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || String(a.label || '').localeCompare(String(b.label || '')));
    JobBoardState._costAllocationsLoaded = true;   // TASK-4585
  }, (error) => {
    // TASK-4585 — a read failure must not leave the driver staring at a disabled
    // select forever, so this counts as settled and the client waives. Be precise
    // about what that does and does not mean (COD Blast + Code Reviewer, rev 3):
    // on THIS path the waiver is not a bypass, because createDriverExpense does
    // its own authoritative .select('active') read and rejects if the tenant
    // really does have allocations — the driver gets a round-trip error instead
    // of an inline one. The ctx-supplied entry points (AP "Record Expense",
    // dispatch "Record Expense") do NOT do this: they pass an explicit
    // costAllocationsSettled=false on a failed fetch and the submit guard
    // refuses, because createExpenseAdmin / createMemberExpense have no
    // equivalent server gate to fall back on.
    JobBoardState._costAllocationsLoaded = true;
    // Most likely cause for failure: tenant has not enabled driverExpenses;
    // costAllocations collection may be empty + rules still allow read. So
    // log but don't crash.
    console.warn('[JobBoard] CostAllocations subscription warning:', error?.message);
  });
  JobBoardState.unsubscribers.push(unsubAlloc);

  // TASK-361 (2026-05-26) — Driver "My Recent JSAs" subscription removed.
  // Relocated to Inspections > JSA tab where the JSA module already subscribes
  // to all jsaReports tenant-wide and filters client-side by submittedByPersonId.
  // See public/src/views/jsa.js renderMyJsaSection.

  // TASK-794 — JSA-on-job lookup. Subscribe to recent jsaReports so the job
  // detail modal can show "Sign JSA" (crew) / "Get Signatures" (admin) on the
  // existing report, or "Start JSA" pre-linked when none. Tenant-wide read is
  // allowed for both members and driver-PIN sessions (firestore.rules:1164
  // jsaReports read = canReadTenant). Windowed to recent + capped to bound the
  // payload. Most-recent non-dismissed report wins per jobDay/job.
  const jsaThirtyDaysAgo = new Date();
  jsaThirtyDaysAgo.setDate(jsaThirtyDaysAgo.getDate() - 30);
  const jbJsaRef = query(
    collection(db, 'tenants', tenantId, 'jsaReports'),
    where('createdAt', '>=', Timestamp.fromDate(jsaThirtyDaysAgo)),
    orderBy('createdAt', 'desc'),
    limit(500)
  );
  const unsubJbJsa = onSnapshot(jbJsaRef, (snapshot) => {
    const byDay = {};
    const byJob = {};
    const listByDay = {};
    const listByJob = {};
    snapshot.docs.forEach(d => {
      const data = d.data();
      if (data.dismissed === true) return; // dismissed placeholder — nothing to sign
      if (data.jobDayId && !byDay[data.jobDayId]) byDay[data.jobDayId] = { id: d.id };
      if (data.jobId && !byJob[data.jobId]) byJob[data.jobId] = { id: d.id };
      // TASK-9371 — keep every report, not just the first (docs arrive createdAt desc).
      const summary = _jbJsaSummary(d.id, data);
      if (data.jobDayId) (listByDay[data.jobDayId] = listByDay[data.jobDayId] || []).push(summary);
      if (data.jobId) (listByJob[data.jobId] = listByJob[data.jobId] || []).push(summary);
    });
    JobBoardState.jsaByJobDay = byDay;
    JobBoardState.jsaByJob = byJob;
    JobBoardState.jsaListByJobDay = listByDay;
    JobBoardState.jsaListByJob = listByJob;
    // Re-render only the open detail modal's JSA section if it's showing (cheap,
    // no full board re-render needed — the section reads these lookups live).
    refreshOpenJobDetailJsaSection();
  }, (error) => {
    console.warn('[JobBoard] jsaReports subscription warning:', error?.message);
    JobBoardState.jsaByJobDay = {};
    JobBoardState.jsaByJob = {};
    JobBoardState.jsaListByJobDay = {};
    JobBoardState.jsaListByJob = {};
  });
  JobBoardState.unsubscribers.push(unsubJbJsa);

  // Subscribe to active clock entries (Driver PIN flow OR SPA member with personId).
  // TASK-355 2026-05-22: member-aware refactor. Before, only PIN drivers got
  // clock state; SPA members with `jobboard` granted saw a stale board with no
  // Clock In/Out affordances. Now both surfaces share the subscription, scoped
  // by `getActiveJobBoardPersonId()` which resolves PIN → SPA in priority order.
  const _jbPid = getActiveJobBoardPersonId();
  if (_jbPid && window.driverHasClockAccess?.()) {
    // TASK-452 — personId-scope this tenant-wide read. The Job Board clock
    // button only ever needs THIS viewer's own active entry (the .find below
    // already filters to `_jbPid`). Under the tightened clockEntries READ rule
    // a non-workforce member / PIN driver may read ONLY their own entries, so
    // an un-scoped `where status==active` query is permission-denied. Adding
    // `where personId == self` both satisfies the rule and trims the payload.
    const clockQuery = query(
      collection(db, 'tenants', tenantId, 'clockEntries'),
      where('personId', '==', _jbPid),
      where('status', '==', 'active')
    );
    const unsubClock = onSnapshot(clockQuery, (snapshot) => {
      JobBoardState.clockEntries = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      // Track current viewer's active entry
      const driverId = getActiveJobBoardPersonId();
      const myEntry = JobBoardState.clockEntries.find(e => e.personId === driverId);
      JobBoardState.clockedInEntryId = myEntry ? myEntry.id : null;
      // v1.15.924 — sync the bottom-nav Clock button label/icon to current
      // state. dispatch.js's updateDriverBottomNavClock reads
      // DispatchState._clockEntries (empty for PIN drivers on /jobboard) so
      // Sean's button was stuck on "Clock In" even when he was already
      // clocked in. Mirror of dispatch.js:15341 but sourced from
      // JobBoardState. Safe no-op on /dispatch view (driver-btmnav-clock
      // not rendered there).
      syncBottomNavClockFromJobBoard(myEntry);
      renderJobBoard();
      refreshOpenJobDetailClockSection(); // TASK-9364 — modal button follows the header

      // Auto-open Build Ticket if navigated from dispatch
      if (window._pendingBuildTicket && myEntry) {
        const pending = window._pendingBuildTicket;
        window._pendingBuildTicket = null;
        setTimeout(() => {
          clockOutFromBoard(myEntry.id, myEntry.jobId || pending.jobId, myEntry.jobDayId || '');
        }, 50);
      }
    }, (error) => {
      console.error('[JobBoard] clockEntries subscription error:', error);
      JobBoardState.clockEntries = [];
    });
    JobBoardState.unsubscribers.push(unsubClock);
  }
}

// ============================================
// RENDERING
// ============================================
function renderJobBoard() {
  const container = document.getElementById('jobboard-content');
  if (!container) return;

  // TASK-424 #5 (C3): keep the Load older control's label/visibility in sync.
  syncJobBoardLoadOlder();
  // TASK-9740 — every jobs-snapshot path lands here; an open job modal's
  // Documents list follows job.documents[] (the server appends the signed PDF).
  refreshOpenJobDocumentsSection();

  // TASK-9370 — "Add Job" strip; settings may land after the first paint.
  _jbRenderDriverAddJobStrip();

  // Show/hide Time Off button based on addon access (may load after initial render)
  const timeOffBtn = document.getElementById('btn-driver-timeoff');
  if (timeOffBtn) {
    timeOffBtn.style.display = driverHasTimeOffAccess() ? '' : 'none';
  }

  // Show/hide Record Expense button on driver-PIN landing — gate parity with the
  // in-card "+ Expense" badge (line 658) and clock.js openRecordExpense. This is
  // the top-level entry point for drivers who aren't currently clocked in: general
  // expense path (meals, tolls, ad-hoc) per UX Lead semantic split. Bug #3 fix
  // from E2E v1.15.434 — previously drivers w/o active clock had no entry point.
  // v1.15.495: dropped legacy BI-OR (`hasAddOnPackage('bi')`) — gate is now strictly
  // `settings.driverExpenses.enabled === true`. Pre-flight scan confirmed 0 tenants
  // were relying on the BI-OR path (no driver_submitted costEntries existed).
  const recordExpenseBtn = document.getElementById('btn-driver-record-expense');
  if (recordExpenseBtn) {
    const isDriverPin = !!window.AppState?.driverModePin;
    const expenseEnabled = window.AppState?.settings?.driverExpenses?.enabled === true;
    recordExpenseBtn.style.display = (isDriverPin && expenseEnabled) ? '' : 'none';
  }

  // Show/hide standalone Clock In/Out button based on clock access.
  // TASK-355 2026-05-22: dropped the `driverModePin`-only gate. SPA members
  // with linked personId also need the Clock In/Out + Build Ticket affordance
  // on Job Board (they were silently locked out before).
  const clockInBtn = document.getElementById('btn-driver-clockin');
  const _viewerPid = getActiveJobBoardPersonId();
  if (clockInBtn && _viewerPid && window.driverHasClockAccess?.()) {
    const driverId = _viewerPid;
    const myEntry = JobBoardState.clockEntries.find(e => e.personId === driverId);
    console.log('[JobBoard] Clock button render:', { driverId, myEntry: myEntry?.id, entriesCount: JobBoardState.clockEntries.length, isTicket: isTicketMode() });
    if (myEntry) {
      // Clocked in — show Build Ticket / Clock Out button
      clockInBtn.style.display = '';
      clockInBtn.style.background = '#DC2626';
      clockInBtn.dataset.action = 'clock-out';
      clockInBtn.dataset.entryId = myEntry.id;
      clockInBtn.dataset.jobId = myEntry.jobId || '';
      clockInBtn.dataset.jobDayId = myEntry.jobDayId || '';
      const label = document.getElementById('btn-driver-clockin-label');
      if (label) label.textContent = isTicketMode() ? 'Build Ticket' : 'Clock Out';
      const icon = clockInBtn.querySelector('.material-icons');
      if (icon) icon.textContent = isTicketMode() ? 'receipt_long' : 'logout';
    } else {
      // No active entry — three-mode label (v1.15.359):
      //   simple/ticket → 'Clock In'
      //   ticketonly    → 'Build Ticket' (same picker flow, ends in ticket form)
      const _isTicketOnly = isTicketOnlyMode();
      clockInBtn.style.display = '';
      clockInBtn.style.background = _isTicketOnly ? '#3B82F6' : '#166534';
      clockInBtn.dataset.action = 'clock-in';
      delete clockInBtn.dataset.entryId;
      delete clockInBtn.dataset.jobId;
      delete clockInBtn.dataset.jobDayId;
      const label = document.getElementById('btn-driver-clockin-label');
      if (label) label.textContent = _isTicketOnly ? 'Build Ticket' : 'Clock In';
      const icon = clockInBtn.querySelector('.material-icons');
      if (icon) icon.textContent = _isTicketOnly ? 'receipt_long' : 'login';
    }
  }

  // TASK-592 — driver self-confirm strip ("You're in: Duck"). Lives outside
  // #jobboard-content so it survives both day and calendar modes; runs before
  // the dataLoaded early-return so it tracks the clockEntries snapshot even
  // while jobs are still loading. No-op (hidden) when equipmentTie is OFF.
  renderEquipTieStatus();

  // Don't render until jobs and jobDays have loaded at least once
  if (!JobBoardState.dataLoaded.jobs || !JobBoardState.dataLoaded.jobDays) return;

  // Highlight active mode toggle (visible in both modes)
  const dayBtn = document.getElementById('jobboard-mode-day');
  const calBtn = document.getElementById('jobboard-mode-calendar');
  if (dayBtn && calBtn) {
    const active = JobBoardState.viewMode;
    dayBtn.style.background = active === 'day' ? 'var(--brand-blue)' : 'transparent';
    dayBtn.style.color = active === 'day' ? '#fff' : 'var(--text-primary)';
    calBtn.style.background = active === 'calendar' ? 'var(--brand-blue)' : 'transparent';
    calBtn.style.color = active === 'calendar' ? '#fff' : 'var(--text-primary)';
  }

  // Calendar mode short-circuits the day-render path.
  if (JobBoardState.viewMode === 'calendar') {
    renderCalendarView();
    return;
  }

  const currentDate = new Date(JobBoardState.currentDate);
  currentDate.setHours(0, 0, 0, 0);

  const dateStr = formatDateISO(currentDate);
  const dayJobs = getJobsForDate(dateStr);

  // Update date display
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dateEl = document.getElementById('jobboard-current-date');
  if (dateEl) {
    const isToday = currentDate.toDateString() === today.toDateString();
    const isTomorrow = currentDate.toDateString() === new Date(today.getTime() + 86400000).toDateString();

    let dateDisplay;
    if (isToday) {
      dateDisplay = 'Today';
    } else if (isTomorrow) {
      dateDisplay = 'Tomorrow';
    } else {
      dateDisplay = currentDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric'
      });
    }
    dateEl.textContent = dateDisplay;
  }

  // Update job count
  const countEl = document.getElementById('jobboard-job-count');
  if (countEl) {
    countEl.textContent = `${dayJobs.length} Job${dayJobs.length !== 1 ? 's' : ''} Scheduled`;
  }

  // Render jobs
  if (dayJobs.length === 0) {
    container.innerHTML = `
      <div class="jobboard-empty">
        <span class="material-icons">event_available</span>
        <div>No jobs scheduled for this day</div>
      </div>
    `;
    return;
  }

  container.innerHTML = dayJobs.map(({ job, jobDay }) => renderJobCard(job, jobDay)).join('');
  if (dayJobs.some(x => _jbJl(x.job))) __ddChunkWrap(import('./jobUpdates.js?v=96946')).then(m => m.boot({ tenantId: JobBoardState.tenantId, jobs: () => JobBoardState.jobs }), () => {});
}
// TASK-9694 S6 — the new-updates dot (jobUpdates.js, lazy): only on the job
// screen tenants (the Updates card lives there) and only for a job with crew
// Updates; every other card renders exactly as before.
function _jbJl(job) {
  return isTicketOnJobScreen() && (job.crewUpdates || []).some(e => e && e.kind !== 'change');
}

// ============================================
// LOAD COUNTER (Driver-facing)
// ============================================
function getLoadInfo(jobDay) {
  return _sharedComputeLoadInfo(jobDay, window.AppState?.settings, JobBoardState.assets, JobBoardState.trucks);
}
function buildDriverLoadCounterHTML(jobDay) {
  return _sharedBuildDriverLoadCounterHTML(
    jobDay,
    window.AppState?.settings,
    JobBoardState.assets,
    JobBoardState.trucks,
    { recordModule: 'JobBoardModule' }
  );
}

async function recordLoadPickup(jobDayId, currentCount, totalLoads) {
  const newCount = currentCount + 1;
  try {
    await updateDoc(doc(db, 'tenants', JobBoardState.tenantId, 'jobDays', jobDayId), {
      loadsCompleted: newCount,
      lastLoadAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    window.showToast?.(totalLoads > 0 ? `Load ${newCount}/${totalLoads} recorded` : `Load ${newCount} recorded`, 'success');
  } catch (error) {
    console.error('[JobBoard] Load pickup error:', error);
    window.showToast?.('Error recording load', 'error');
  }
}

async function undoLoadPickup(jobDayId, currentCount) {
  if (currentCount <= 0) return;
  try {
    await updateDoc(doc(db, 'tenants', JobBoardState.tenantId, 'jobDays', jobDayId), {
      loadsCompleted: currentCount - 1,
      updatedAt: Timestamp.now()
    });
    window.showToast?.('Load undone', 'info');
  } catch (error) {
    console.error('[JobBoard] Undo load error:', error);
    window.showToast?.('Error undoing load', 'error');
  }
}

// TASK-415: PIN/driver-code views only ever rendered job.location (a text field).
// A job whose location was dropped as a waypoint PIN (job.waypoints) but with no
// text location was invisible to drivers — they navigate by driver code, dispatchers
// (app login) read job.waypoints so they saw it. Render the waypoint(s) as tappable
// map links so drivers get directions. (event.stopPropagation so a card tap still
// opens the detail modal.)
function _jbWaypointLinks(job, opts) {
  const wps = (job.waypoints || []).filter(w => w && (w.url || (w.lat != null && w.lng != null)));
  // TASK-9500 — the job modal / job screen pass the day so the driver's
  // "I'm here" row renders under the stop pills ('' everywhere else).
  const arrive = opts && opts.arrivalDay ? _jbStopArrivalHtml(job, opts.arrivalDay) : '';
  if (!wps.length) return arrive;
  return wps.map((wp, i) => {
    const href = wp.url || `https://www.google.com/maps/search/?api=1&query=${wp.lat},${wp.lng}`;
    const nm = (wp.name || '').trim();
    const label = (nm && nm.toLowerCase() !== 'location') ? nm : (wps.length > 1 ? `Destination ${i + 1}` : 'Directions');
    // TASK-10116 — one scheme gate for every stop link (dispatch.js
    // waypointOpenHref, via window — never a cross-import). It also turns a
    // plain-address stop into a Maps search. Not loaded → the old http(s)-only
    // check, which fails closed.
    const _gate = globalThis.window?.DispatchModule?.waypointOpenHref;
    const safeHref = typeof _gate === 'function' ? _gate(href) : (/^https?:\/\//i.test(href) ? href : '');
    // .waypoint-pill (styles.css) = tactile button: depth/border/shading + 44px
    // truck-cab target + hover/active states (COD-picked "Tactile White").
    return safeHref
      ? `<a href="${escapeHtml(safeHref)}" target="_blank" rel="noopener noreferrer" onclick="event.stopPropagation();" class="waypoint-pill"><span class="material-icons">near_me</span>${escapeHtml(label)}</a>`
      : `<span class="waypoint-pill">${escapeHtml(label)}</span>`;
  }).join('') + arrive;
}

// ── TASK-9500 — driver "I'm here" per stop (stamp only) ──
// Each stop (job order) with a permanent id that this job day has no stamp
// for gets a row [Navigate] [I'm here], 48px, outlined; the first is "Next
// (the screen's one gradient stays Clock In / Finish). Stops already marked
// show the server time. PIN drivers on the day only, around the day's date
// (the stampStopArrival callable re-checks all of it). The location is what
// the phone reports, so dispatch shows it as "Reported by driver".
const _jbArriveLocal = new Map(); // `${jobDayId}|${stopId}` → atMs, until the jobDay snapshot catches up
const _JB_ARRIVE_STOP_ID = /^[A-Za-z0-9_-]{8,64}$/;
function _jbArriveAtMs(jobDay, stopId) {
  const st = jobDay && jobDay.stopArrivals && typeof jobDay.stopArrivals === 'object' ? jobDay.stopArrivals[stopId] : null;
  // A stamp copied onto another day (a spread copy) is not this day's.
  if (st && typeof st === 'object' && (!st.jobDayId || st.jobDayId === jobDay.id)) {
    const at = st.at;
    const ms = at && typeof at.toMillis === 'function' ? at.toMillis() : (at && typeof at.seconds === 'number' ? at.seconds * 1000 : null);
    // TASK-10073: the snapshot has caught up, so drop the local copy; a later
    // snapshot without the stamp (the office cleared it) then shows the
    // button again instead of a stale "marked".
    if (ms) { _jbArriveLocal.delete(`${jobDay.id}|${stopId}`); return ms; }
  }
  // TASK-10073 (COD r2): the local copy only bridges the few seconds until the
  // snapshot arrives, so it expires after 2 min even if that row never rendered.
  const k = `${jobDay && jobDay.id}|${stopId}`;
  const lm = _jbArriveLocal.get(k) || null;
  if (lm && Date.now() - lm > 120000) { _jbArriveLocal.delete(k); return null; }
  return lm;
}
function _jbArriveTime(ms) {
  return new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
function _jbStopArrivalHtml(job, jobDay) {
  if (window.AppState?.driverModePin !== true) return '';
  if (!job || !jobDay || !jobDay.id || jobDay.status === 'cancelled') return '';
  const pid = window.AppState?.driverPersonId || '';
  const crew = [].concat(jobDay.drivers || [], jobDay.helpers || []).map(String);
  // Legacy name refs: the session name OR the roster name, each on its own (same as the server).
  const names = [window.AppState?.driverPersonName,
    typeof JobBoardState !== 'undefined' && (JobBoardState.people || []).find(p => p.id === pid)?.name].filter(Boolean);
  if (!pid || !(crew.includes(pid) || names.some(n => crew.includes(n)))) return '';
  const d = jobDay.date;
  const dms = d && typeof d.toMillis === 'function' ? d.toMillis()
    : (d && typeof d.seconds === 'number' ? d.seconds * 1000
      // a legacy 'YYYY-MM-DD' day reads as noon UTC, as the server does (COD r3)
      : (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d) ? Date.parse(d.slice(0, 10) + 'T12:00:00Z') : (d ? new Date(d).getTime() : NaN)));
  if (!Number.isFinite(dms) || Math.abs(Date.now() - dms) > 36 * 3600 * 1000) return '';
  const all = Array.isArray(job.waypoints) ? job.waypoints : [];
  const stops = [];
  all.forEach((w, i) => { if (w && typeof w === 'object' && typeof w.id === 'string' && _JB_ARRIVE_STOP_ID.test(w.id)) stops.push({ w, i }); });
  if (!stops.length) return '';
  const label = ({ w, i }) => {
    const nm = String(w.name || '').trim();
    return (nm && nm.toLowerCase() !== 'location') ? `Destination ${i + 1} · ${nm}` : `Destination ${i + 1}`;
  };
  // Every unmarked stop gets its row (COD r1 UX/Domain: real days run out of
  // order); the first one is the active stop and is labelled "Next".
  const open = [];
  const done = [];
  stops.forEach((s) => {
    const ms = _jbArriveAtMs(jobDay, s.w.id);
    if (ms) done.push({ s, ms }); else open.push(s);
  });
  const btnBase = 'display:inline-flex;align-items:center;justify-content:center;gap:6px;min-height:48px;padding:0 16px;border-radius:12px;font-size:1rem;font-weight:800;text-decoration:none;cursor:pointer;';
  const doneHtml = done.map(({ s, ms }) => `<div style="display:flex;align-items:center;gap:6px;min-height:32px;font-size:0.9rem;font-weight:700;color:#065f46;"><span class="material-icons" aria-hidden="true" style="font-size:18px;color:#047857;">check_circle</span><span>${escapeHtml(label(s))} — marked ${escapeHtml(_jbArriveTime(ms))}</span></div>`).join('');
  const activeHtml = open.map((st, k) => {
    const w = st.w;
    const rawHref = w.url || ((w.lat != null && w.lng != null) ? `https://www.google.com/maps/search/?api=1&query=${w.lat},${w.lng}` : '');
    const href = /^https:\/\//i.test(String(rawHref)) ? String(rawHref) : '';
    return `<div style="font-size:0.85rem;font-weight:800;color:#1e293b;margin-top:8px;">${k ? '' : 'Next: '}${escapeHtml(label(st))}</div>
      <div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:6px;">
        ${href ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" onclick="event.stopPropagation();" style="${btnBase}flex:1 1 140px;background:#fff;border:2px solid #94a3b8;color:#0f172a;"><span class="material-icons" aria-hidden="true" style="font-size:20px;color:#1d4ed8;">near_me</span><span>Navigate</span></a>` : ''}
        <button type="button" class="jb-arrive-btn" data-stop-id="${escapeHtml(w.id)}" onclick="event.stopPropagation(); window._jbStopArriveTap && window._jbStopArriveTap(this)" style="${btnBase}flex:1 1 140px;background:#fff;border:2px solid #1d4ed8;color:#1d4ed8;"><span class="material-icons" aria-hidden="true" style="font-size:20px;">where_to_vote</span><span class="jb-arrive-label">I'm here</span></button>
      </div>`;
  }).join('');
  return `<div class="jb-arrive" data-jb-arrive-jd="${escapeHtml(jobDay.id)}" data-jb-arrive-job="${escapeHtml(job.id || '')}" style="margin-top:8px;">${doneHtml}${activeHtml}<div class="jb-arrive-msg" role="status" aria-live="polite" style="font-size:0.9rem;font-weight:700;color:#1e293b;margin-top:4px;"></div></div>`;
}
async function _jbStopArriveTap(btn) {
  if (!btn || btn.dataset.busy === '1') return; // one tap in flight (button debounce)
  const box = btn.closest('[data-jb-arrive-jd]');
  if (!box) return;
  const jobDayId = box.dataset.jbArriveJd;
  const jobId = box.dataset.jbArriveJob;
  const stopId = btn.dataset.stopId;
  const say = (el, text, warn) => {
    const m = el && el.querySelector('.jb-arrive-msg');
    if (m) { m.textContent = text; m.style.color = warn ? '#92400E' : '#065f46'; }
  };
  // Offline: no queue — a queued stamp would carry the later server time,
  // which is the one thing this button must get right.
  if (navigator.onLine === false) { say(box, 'No signal — tap again when you have service.', true); return; }
  btn.dataset.busy = '1';
  btn.disabled = true;
  const lab = btn.querySelector('.jb-arrive-label');
  if (lab) lab.textContent = 'Marking…';
  let text = '';
  let warn = false;
  try {
    const loc = await captureClockLocation(); // ≤6s, null when denied / no fix
    const res = await httpsCallable(functions, 'stampStopArrival', { timeout: 20000 })({
      jobDayId, stopId,
      location: loc ? { lat: loc.lat, lng: loc.lng, accuracy: loc.accuracy } : null,
    });
    const r = (res && res.data) || {};
    if (r.atMs) _jbArriveLocal.set(`${jobDayId}|${stopId}`, r.atMs);
    const t = r.atMs ? _jbArriveTime(r.atMs) : '';
    text = r.already ? `Already marked${t ? ' ' + t : ''}.` : `Marked ${t} — dispatch can see it.`;
  } catch (e) {
    const code = String((e && e.code) || '').replace(/^functions\//, '');
    warn = true;
    text = (['unavailable', 'deadline-exceeded', 'internal', 'unknown'].includes(code) || navigator.onLine === false)
      ? 'No signal — tap again.'
      : String((e && e.message) || 'Could not mark this destination. Tap again.');
  }
  // Re-render every copy of this day's row (modal + job screen), then speak.
  const job = _findJobAnywhere(jobId);
  const jd = _findJobDayAnywhere(jobDayId);
  const boxes = document.querySelectorAll(`[data-jb-arrive-jd="${CSS.escape(jobDayId)}"]`);
  const html = !warn && job && jd ? _jbStopArrivalHtml(job, jd) : '';
  if (html) {
    boxes.forEach((b) => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const next = tmp.firstElementChild;
      b.replaceWith(next);
      say(next, text, false);
    });
  } else {
    // Failed — or the row can no longer render (day changed mid-call, COD r4):
    // never leave the button stuck on "Marking…"; the message still shows.
    btn.dataset.busy = '';
    btn.disabled = !warn;
    if (lab) lab.textContent = warn ? "I'm here" : 'Marked';
    boxes.forEach((b) => say(b, text, warn));
  }
}
if (typeof window !== 'undefined') window._jbStopArriveTap = _jbStopArriveTap;

// 811 locate tickets linked to a job. TASK-9648: a job can link several
// (jobs.permitIds; permitId mirrors the first; a legacy job has only permitId) and
// drivers must see EVERY locate, not just the first. Mirrors dispatch.js
// _jobPermitIdsOf — a local copy on purpose (no cross-import between views).
// Gated on enable811 (matches dispatch is811Enabled); demo always on.
// http(s)-only href + escaped name + rel=noopener.
function _jbPermitIdsOf(job) {
  if (!job) return [];
  const raw = Array.isArray(job.permitIds) ? job.permitIds : (job.permitId ? [job.permitId] : []);
  const out = [];
  for (const id of raw) {
    const v = typeof id === 'string' ? id.trim() : '';
    if (v && !out.includes(v)) out.push(v);
  }
  return out.slice(0, 20);
}

// TASK-9859 — expiry for a driver's 811 row. A local copy of dispatch.js
// _p811Expiry on purpose (no cross-import between views); the unit test runs
// both and requires they agree. Date-only, in the tenant zone.
function _jb811Expiry(expiresOn, todayKey) {
  const ms = (k) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(typeof k === 'string' ? k : '');
    if (!m) return NaN;
    const v = Date.UTC(+m[1], +m[2] - 1, +m[3]);
    return new Date(v).toISOString().slice(0, 10) === k ? v : NaN;
  };
  const e = ms(expiresOn), t = ms(todayKey);
  if (!Number.isFinite(e) || !Number.isFinite(t)) return { state: 'none', days: null, label: '' };
  const days = Math.round((e - t) / 86400000);
  if (days < 0) return { state: 'expired', days, label: 'Expired' };
  if (days === 0) return { state: 'soon', days, label: 'Expires today' };
  return { state: days <= 3 ? 'soon' : 'ok', days, label: `Expires in ${days} day${days === 1 ? '' : 's'}` };
}

function _jb811TodayKey() {
  const tz = (window.AppState?.settings || window.AppState?.tenantData?.settings || {}).timezone || 'America/Chicago';
  try {
    const p = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
    const g = (t) => (p.find(x => x.type === t) || {}).value;
    return `${g('year')}-${g('month')}-${g('day')}`;
  } catch (_) { return ''; }
}

function _jb811BadgeHtml(permit, todayKey) {
  const e = _jb811Expiry(permit && permit.expiresOn, todayKey);
  if (e.state === 'none') return '';
  const c = e.state === 'expired' ? 'background:#FEE2E2;color:#991B1B;border-color:#FCA5A5;'
    : e.state === 'soon' ? 'background:#FEF3C7;color:#92400E;border-color:#FCD34D;'
    : 'background:#F1F5F9;color:#475569;border-color:#CBD5E1;';
  return `<span class="jb-811-exp" style="display:inline-block;margin-left:6px;padding:0 6px;border:1px solid;border-radius:6px;font-size:0.75rem;font-weight:700;white-space:nowrap;${c}">${escapeHtml(e.label)}</span>`;
}

// TASK-9862 — the day a driver is looking at, as a company-zone date key.
function _jb811DayKey(v) {
  const tz = (window.AppState?.settings || window.AppState?.tenantData?.settings || {}).timezone || 'America/Chicago';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = v && typeof v.toDate === 'function' ? v.toDate() : (v && typeof v.seconds === 'number' ? new Date(v.seconds * 1000) : (v instanceof Date ? v : null));
  if (!d || isNaN(d.getTime())) return '';
  try {
    const p = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
    const g = (t) => (p.find(x => x.type === t) || {}).value;
    return `${g('year')}-${g('month')}-${g('day')}`;
  } catch (_) { return ''; }
}
function _jb811Short(key) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(key || ''))) return '';
  return new Date(key + 'T12:00:00Z').toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' });
}
// integ/2094 pre-ship (SAFETY) — the shared 811 rule's clock plus the ticket
// finder a RENEWAL needs: it is clear before its own dig start only while the
// ticket it renews is loaded here, still good and itself clear.
function _jb811Clock(now, tz) {
  return { now, tz, ticketOf: (id) => (JobBoardState.permits || []).find(p => p && p.id === id) || null };
}
// The tickets on this job (own + project) that are expired FOR this day:
// window.Locates811.hardStops (the dispatch board's own rule). Past days count
// here too: a driver looking at a day must never read an expired locate as good.
function _jb811StopsForDay(permits, dayKey) {
  const L = window.Locates811;
  if (!L || !dayKey) return [];
  // Past days are history (COD r2): judge the viewed day only when it is today or later.
  const today = _jb811TodayKey();
  // TASK-9925 — the shared SITE-zone rule (clock), same as the office board.
  const tzNow = (window.AppState?.settings || window.AppState?.tenantData?.settings || {}).timezone || 'America/Chicago';
  const stops = L.hardStops(permits, [dayKey], today, _jb811Clock(Date.now(), tzNow));
  // TASK-9922 (COD) — TODAY also stops on a ticket that has run out by now in
  // the dig SITE's zone (a Central-site ticket ends 11:00 PM Mountain).
  if (dayKey === today && typeof L.expiresAt === 'function') {
    const tz = (window.AppState?.settings || window.AppState?.tenantData?.settings || {}).timezone || 'America/Chicago';
    const now = Date.now();
    const have = new Set(stops.filter(s => s.reason === 'expired').map(s => s.permit.id));
    for (const p of (Array.isArray(permits) ? permits : [])) {
      if (!p || !p.id || have.has(p.id)) continue;
      if (L.effectiveStatus(p, today, -1, _jb811Clock(now, tz)) === 'expired') {
        have.add(p.id);
        stops.push({ permit: p, reason: 'expired', expiresOn: L.validDateKey(p.expiresOn), digStartOn: L.validDateKey(p.digStartOn), lateKeys: [dayKey] });
      }
    }
  }
  return stops;
}
// TASK-10283 — settings.show811DriverLines (absent = ON). OFF hides the two
// driver "Don't dig" lines (this card flag + the day-detail stop) only.
function _jb811DriverLines() { return window.AppState?.settings?.show811DriverLines !== false; }
// Driver card: a red line when one of the job's OWN tickets is expired for the day.
function _jb811CardFlag(job, jobDay) {
  const on = window.AppState?.settings?.enable811 === true || window.AppState?.isDemo === true;
  if (!on || !_jb811DriverLines() || !job || !jobDay) return '';
  const permits = _jbPermitIdsOf(job).map(id => (JobBoardState.permits || []).find(p => p.id === id)).filter(Boolean);
  if (!_jb811StopsForDay(permits, _jb811DayKey(jobDay.date)).length) return '';
  return `<div class="jb-811-flag" role="note" style="margin-top:6px;background:#DC2626;color:#fff;border-radius:6px;padding:4px 8px;font-weight:800;font-size:0.8rem;display:inline-flex;gap:4px;align-items:center;"><span class="material-icons" style="font-size:16px;" aria-hidden="true">block</span>Locate expired. Don't dig</div>`;
}

function _jb811PermitSection(job, projectIds, dayKeyIn) {
  const on = window.AppState?.settings?.enable811 === true || window.AppState?.isDemo === true;
  // TASK-9767 — the job's own tickets, then its PROJECT's (filled in after the
  // project fetch by _jbFillProject811), deduped. View/open only, same rows.
  const own = on ? _jbPermitIdsOf(job) : [];
  const extra = on && Array.isArray(projectIds) ? projectIds.filter(id => typeof id === 'string' && id && !own.includes(id)) : [];
  const ids = [...own, ...extra].slice(0, 40);
  if (!ids.length) return '';
  const todayKey = _jb811TodayKey(); // TASK-9859
  const dayKey = dayKeyIn || todayKey;
  const L = window.Locates811;
  const permits = ids.map(id => (JobBoardState.permits || []).find(p => p.id === id)).filter(Boolean);
  const live = permits.filter(p => !(p.status === 'renewed' || p.renewedById));
  const stops = _jb811StopsForDay(live, dayKey);
  const stopIds = new Set(stops.map(s => s.permit.id));
  // TASK-9920 (SAFETY) — on the dig-start day itself a ticket is not clear
  // until its start time (or all that day when no time was read off the PDF).
  // 2090.1 (COD): a LATER day that is the dig-start day says so too, never
  // "All clear" (no clock for a day that is not today: the whole day counts).
  const tz = (window.AppState?.settings || window.AppState?.tenantData?.settings || {}).timezone || 'America/Chicago';
  const isToday = dayKey === todayKey;
  // TASK-9922 — TODAY is judged at this instant in the dig SITE's zone (the
  // ticket prints site time; not sure of the zone = the later one). A later
  // day is the dig-start day when the site's start falls on it in company time.
  const timed = !!(L && typeof L.digStartAt === 'function' && typeof L.digStartLabelParts === 'function' && typeof L.beforeDigStart === 'function' && typeof L.todayKey === 'function' && typeof L.validDateKey === 'function');
  const nowMs = Date.now();
  // integ/2092 lane (COD), kept at integ/2094 — shared rules not loaded: fail SAFE, never "All clear" on or before a dig start on the ticket.
  const notYet = stops.length || dayKey < todayKey ? []
    : timed ? live.filter(p => {
      if (isToday) return L.beforeDigStart(p, dayKey, -1, _jb811Clock(nowMs, tz));
      const d = L.digStartAt(p, tz);
      // at - 1 min: a midnight start belongs to the day before. TASK-9925 (COD r2): no start
      // time read = the printed day only (its "next midnight" in a western site zone is the
      // company's NEXT day, which would flag the day after the dig day too).
      const lastNotYet = typeof L.digStartMinutes === 'function' && L.digStartMinutes(p) < 0 ? L.validDateKey(p.digStartOn) : (d ? L.todayKey(tz, d.at - 60000) : '');
      return !!d && dayKey <= lastNotYet && L.beforeDigStart(p, dayKey, -1, _jb811Clock(nowMs, tz));
    })
    : live.filter(p => /^\d{4}-\d{2}-\d{2}$/.test(String(p.digStartOn || '')) && p.digStartOn >= dayKey);
  const notYetDay = isToday ? 'today' : _jb811Short(dayKey);
  const notYetParts = timed ? notYet.map(p => L.digStartLabelParts(p, tz)).filter(Boolean) : [];
  // The LATEST start; any unknown time = no time claimed.
  const notYetLast = notYet.length && notYetParts.length === notYet.length && notYetParts.every(x => x.time) ? notYetParts.slice().sort((a, b) => b.at - a.at)[0] : null;
  // A ticket whose dig-start date (the ticket's own, site calendar) is LATER than the day viewed names that day.
  const notYetLater = notYet.map(p => String(p.digStartOn || '')).filter(k => /^\d{4}-\d{2}-\d{2}$/.test(k) && k > dayKey).sort().pop() || '';
  // TASK-9925 (SAFETY) — past the start, but not every utility has marked or
  // cleared (the one-call positive response): amber, never "All clear".
  const dated = live.filter(p => /^\d{4}-\d{2}-\d{2}$/.test(String(p.expiresOn || '')));
  const utilWait = !stops.length && !notYet.length && typeof L?.membersOf === 'function'
    ? dated.filter(p => !L.membersOf(p).clear) : [];
  const utilTxt = utilWait.length === 1 && typeof L.utilitiesLabel === 'function' ? L.utilitiesLabel(utilWait[0])
    : (utilWait.length ? `Waiting on utilities (${utilWait.length} tickets)` : '');
  const notYetTxt = notYetLater ? 'Locate not active until ' + _jb811Short(notYetLater) + ': check the start time on the ticket'
    : notYetLast ? 'Locate active at ' + notYetLast.time + (notYetLast.zone ? ' ' + notYetLast.zone : '') + ' ' + notYetDay : 'Locate starts ' + notYetDay + ': check the start time on the ticket';
  // TASK-9862 — the locate card (mockup 7): one status line, then each ticket
  // with its number and good-through date, one tap to the PDF.
  // TASK-10283 — "Show 811 lines to drivers" OFF: no red stop and no orange "starts on" line (Chris 11/4: hide both); never "All clear".
  const status = (stops.length || notYet.length) && !_jb811DriverLines() ? ''
    : stops.length
    ? `<div role="alert" style="background:#FEF2F2;border:2px solid #DC2626;border-radius:10px;padding:10px 12px;color:#7F1D1D;font-weight:700;display:flex;gap:8px;align-items:flex-start;"><span class="material-icons" style="color:#DC2626;" aria-hidden="true">block</span><span><span style="display:block;font-size:1.05rem;color:#991B1B;">${stops.some(s => s.reason !== 'early') ? 'Locate expired for ' : 'Locate not active yet on '}${escapeHtml(_jb811Short(dayKey))}</span><span>Don't dig. Call the office.</span></span></div>`
    : notYet.length
      ? `<div role="status" style="display:inline-flex;gap:6px;align-items:center;background:#FFF7ED;color:#9A3412;border:1px solid #FDBA74;border-radius:8px;padding:4px 10px;font-weight:700;"><span class="material-icons" style="font-size:18px;" aria-hidden="true">hourglass_top</span>${escapeHtml(notYetTxt)}. Don't dig before then.</div>`
    : utilWait.length
      ? `<div role="status" data-jb811-util="1" style="display:inline-flex;gap:6px;align-items:center;background:#FFF7ED;color:#9A3412;border:1px solid #FDBA74;border-radius:8px;padding:4px 10px;font-weight:700;"><span class="material-icons" style="font-size:18px;" aria-hidden="true">hourglass_top</span>${escapeHtml(utilTxt)}. Check with the office before digging.</div>`
    : (live.length && live.every(p => /^\d{4}-\d{2}-\d{2}$/.test(String(p.expiresOn || ''))) && typeof L?.membersOf === 'function' && live.every(p => L.membersOf(p).clear) // no rule loaded = never "All clear"
      ? `<div style="display:inline-flex;gap:6px;align-items:center;background:#DCFCE7;color:#166534;border:1px solid #86EFAC;border-radius:8px;padding:4px 10px;font-weight:700;"><span class="material-icons" style="font-size:18px;" aria-hidden="true">check_circle</span>All clear to dig ${dayKey === todayKey ? 'today' : escapeHtml(_jb811Short(dayKey))}</div>` : '');
  const rows = ids.map((id) => {
    const permit = (JobBoardState.permits || []).find(p => p.id === id);
    if (!permit) {
      // linked but the permit doc isn't loaded/found — say so, plainly
      return `<span class="waypoint-pill" style="color:var(--danger,#dc2626);"><span class="material-icons">info</span>811 ticket not available here. Ask dispatch for a copy before digging.</span>`;
    }
    const urlKey = permit.pdfUrl ? 'pdfUrl' : (permit.url ? 'url' : 'fileUrl');
    const url = permit[urlKey] || '';
    const safeUrl = /^https?:\/\//i.test(url) ? url : '';
    const tn = String(permit.ticketNumber || ((/^\s*(\d{5,})/.exec(String(permit.name || '')) || [])[1]) || '');
    const site = L ? L.siteNameOf(permit) : '';
    const exp = /^\d{4}-\d{2}-\d{2}$/.test(String(permit.expiresOn || '')) ? permit.expiresOn : '';
    const bad = stopIds.has(id);
    // integ/2092 lane — an EARLY stop (before dig start) is not "Expired": say when it starts.
    const expired = stops.some(s => s.permit.id === id && s.reason === 'expired');
    const early = stops.find(s => s.permit.id === id && s.reason === 'early' && _jb811Short(s.digStartOn));
    const line =`<span style="display:flex;flex-direction:column;min-width:0;flex:1;">
        <span style="font-family:'Courier New',Courier,monospace;font-weight:700;font-size:1.05rem;">${escapeHtml(tn || permit.name || '811 ticket')}</span>
        <span style="font-size:0.85rem;font-weight:600;color:${bad ? '#991B1B' : 'var(--text-secondary)'};">${escapeHtml([site && tn ? site : '', early && !expired ? 'Not active until ' + _jb811Short(early.digStartOn) : exp ? (bad ? 'Expired. Was good through ' : 'Good through ') + _jb811Short(exp) : 'No date on file'].filter(Boolean).join(' · '))}</span>
      </span>`;
      // TASK-9855 — opened through the Storage SDK on the permit's PATH (rules
      // decide), not the stored token URL. The permit library stores `storagePath`.
      return (safeUrl || permit.storagePath)
      ? `<a ${window.StorageFile.linkAttrs(permit, { urlKey, pathKey: permit.storagePath ? 'storagePath' : undefined })} class="waypoint-pill" style="min-height:48px;${bad ? 'border-color:#DC2626;' : ''}"><span class="material-icons">picture_as_pdf</span>${line}<span class="material-icons" style="margin-left:auto;font-size:18px;">open_in_new</span></a>`
      : `<span class="waypoint-pill" style="min-height:48px;">${line}</span>`;
  }).join('');
  return `
      <div class="jobboard-detail-section">
        <div class="jobboard-detail-label">Locates · ${ids.length} ticket${ids.length > 1 ? 's' : ''}</div>
        <div class="jobboard-detail-value" style="display:flex;flex-direction:column;gap:6px;">
          ${status}
          ${rows}
        </div>
      </div>`;
}

// TASK-9865 — "J-1248 · " ahead of a title on the card and the modal header,
// so the identifier survives a long title that wraps or truncates. '' for the
// legacy unnumbered jobs (card byte-identical). Read through window.ClockModule
// (Pitfall #1); no clock module → no prefix, never a thrown card.
function _jbJobNumberPrefixHtml(job) {
  const CM = window.ClockModule;
  if (!CM || typeof CM.clockJobNumberLabel !== 'function') return '';
  let n = '';
  try { n = CM.clockJobNumberLabel(job); } catch (_) { n = ''; }
  if (!n) return '';
  // ONE span (number + separator, nowrap): the modal <h3> is a flex row, so
  // separate spans would become separate columns and "J-1247" would wrap.
  return `<span class="jobboard-job-number" data-no-i18n style="font-weight:700;color:var(--text-secondary);font-variant-numeric:tabular-nums;white-space:nowrap;">${escapeHtml(n)} ·</span> `;
}

// TASK-9750 — a tentative day is a soft request the office has not confirmed;
// a half day is a label (it does not change billing). Labels only — a driver
// cannot confirm a day. Same words and colours as the dispatch board chips.
function _jbDayFlagsHtml(jobDay) {
  if (!jobDay) return '';
  const base = 'display:inline-flex;align-items:center;gap:3px;max-width:100%;box-sizing:border-box;font-size:0.75rem;font-weight:700;line-height:1.2;border-radius:10px;padding:2px 8px;';
  let html = '';
  if (jobDay.tentative === true) {
    html += `<span class="jobboard-day-flag" data-day-flag="tentative" style="${base}background:rgba(245,158,11,0.15);color:var(--text-primary);border:1px dashed #F59E0B;">Tentative: not confirmed yet</span>`;
  }
  if (jobDay.halfDay === true) {
    html += `<span class="jobboard-day-flag" data-day-flag="half" style="${base}background:rgba(59,130,246,0.12);color:var(--text-primary);border:1px solid rgba(59,130,246,0.35);">½ day</span>`;
  }
  return html ? `<div style="display:flex;flex-wrap:wrap;gap:4px;margin:4px 0 2px;">${html}</div>` : '';
}

function renderJobCard(job, jobDay) {
  const customer = getCustomerName(job.customerId);
  // 2026-05-29 — left stripe is color-coded by CUSTOMER (not status) to match
  // the driver-mode dispatch board, which colors the driver card stripe with
  // customerColor (dispatch.js:4246 `isDriverMode ? customerColor : statusColor`,
  // and dispatch.js:3391). The job board IS the driver-facing view, so it
  // should match. Supersedes the 2026-05-28 status-color expansion (28f3879c) —
  // that fixed the wrong axis; the intent was always customer-matching the
  // dispatch board. _getCustomerColor (declared below, hoisted) falls back to
  // blue when no customerId is present, so cards never go un-striped.
  const stripeColor = _getCustomerColor(job.customerId || job.customer);

  // Get assignments
  const drivers = (jobDay.drivers || []).map(ref => getPersonName(ref)).filter(Boolean);
  const trucks = (jobDay.trucks || []).map(ref => getTruckUnit(ref)).filter(Boolean);
  const trailers = (jobDay.trailers || []).map(ref => getTrailerUnit(ref)).filter(Boolean);

  // Fallback chain mirrors billing.js: per-day override → job-level dispatcher
  // start (the field the dispatch form actually writes) → legacy startTime.
  // All values are TZ-naive wall-clock strings like "06:00".
  const time = jobDay.startTime || job.defaultStart || job.startTime || '';
  const location = job.location || '';

  return `
    <div class="jobboard-card"${_jbJl(job) ? ` data-jl-job="${escapeHtml(job.id)}"` : ''} onclick="window.JobBoardModule.showJobDetails('${job.id}', '${jobDay.id}')">
      <div class="jobboard-card-status" style="background:${stripeColor}"></div>
      <div class="jobboard-card-content">
        <div class="jobboard-card-header">
          <span class="jobboard-card-customer">${escapeHtml(customer)}</span>
          ${time ? `<span class="jobboard-card-time">${escapeHtml(formatTime(time))}</span>` : ''}
        </div>
        <div class="jobboard-card-title">${_jbJobNumberPrefixHtml(job)}${escapeHtml(job.title || job.name || 'Untitled Job')}</div>
        ${_jbDayFlagsHtml(jobDay)}
        ${_jbYourTruckLineHtml(jobDay)}
        ${_jb811CardFlag(job, jobDay)}
        ${location ? `
          <div class="jobboard-card-location">
            <span class="material-icons">location_on</span>
            ${escapeHtml(location)}
          </div>
        ` : ''}
        ${(() => { const wl = _jbWaypointLinks(job); return wl ? `<div style="margin:4px 0 2px;">${wl}</div>` : ''; })()}
        <div class="jobboard-card-assignments">
          ${drivers.length ? `<span class="assignment-badge driver"><span class="material-icons">person</span>${drivers.map(escapeHtml).join(', ')}</span>` : ''}
          ${trucks.length ? `<span class="assignment-badge truck"><span class="material-icons">local_shipping</span>${trucks.map(escapeHtml).join(', ')}</span>` : ''}
          ${trailers.length ? `<span class="assignment-badge trailer"><span class="material-icons">rv_hookup</span>${trailers.map(escapeHtml).join(', ')}</span>` : ''}
          ${(() => {
            const loadInfo = getLoadInfo(jobDay);
            if (!loadInfo) return '';
            const lColor = loadInfo.pct >= 100 ? '#10B981' : loadInfo.pct >= 50 ? '#F59E0B' : '#3B82F6';
            return `<span class="assignment-badge" style="background:${lColor}20;color:${lColor};font-weight:600;">
              <span style="font-size:14px;">📦</span>${loadInfo.completed}/${loadInfo.totalLoads}
            </span>`;
          })()}
          ${(() => {
            // Ticket-count badge removed from the job placard v1.15.946 per Chris
            // (2026-06-01): "We never used to have the ticket showing on the job
            // placard." Came in with 72292fa9 (Clock & Billing ticket-building
            // upgrade). Ticket status still lives inside the job-details modal /
            // ticket view — it just no longer clutters the card. Block kept as a
            // no-op to preserve the surrounding render template structure.
            return '';
          })()}
          ${''}
          ${(() => {
            // Inline Clock In/Out badge removed v1.15.923 per Chris (2026-05-28):
            // "I don't recall there ever being a 'clock in' on the specific job
            // placard." Bottom-nav Clock tile (Inspections / Clock / Exam /
            // Actions) remains the canonical Clock In affordance for PIN
            // drivers. Keeping the Ticket badge above per COD Skeptic 🔴 — only
            // the Clock In pill was rejected; Ticket count is established status
            // cue. If a clocked-in driver needs "+ Expense" inline, restore via
            // Actions sheet tile, not here. Block kept as a no-op to preserve
            // the surrounding render template structure.
            return '';
          })()}
        </div>
      </div>
      <div class="jobboard-card-chevron">
        <span class="material-icons">chevron_right</span>
      </div>
    </div>
  `;
}

// ============================================
// JOB DETAILS MODAL
// ============================================
// TASK-4535-family (renders-audit #7) — the month calendar renders old months
// from _calMonthCache (TASK-569 on-demand fetch), but the card CLICK resolved
// against the live windowed arrays only: the job toasted "not found" and a
// cache-only jobDay silently degraded to the job's defaults (wrong crew/times).
// Resolve live-first, then every fetched month cache — mirror of the render
// path's own closure-scoped _findJob.
function _findJobAnywhere(jobId) {
  const live = JobBoardState.jobs.find(j => j.id === jobId);
  if (live) return live;
  for (const m of Object.values(JobBoardState._calMonthCache || {})) {
    if (m && m.jobsById && m.jobsById[jobId]) return m.jobsById[jobId];
  }
  return null;
}
function _findJobDayAnywhere(jobDayId) {
  const live = JobBoardState.jobDays.find(d => d.id === jobDayId);
  if (live) return live;
  for (const m of Object.values(JobBoardState._calMonthCache || {})) {
    const jd = m && Array.isArray(m.jobDays) && m.jobDays.find(d => d.id === jobDayId);
    if (jd) return jd;
  }
  return null;
}

// TASK-9373 — one hideable section of the job-details modal. Tags the section's
// .jobboard-detail-section div with data-section="<id>" (the id the Driver App
// settings page and the layout reader use) and returns '' when the tenant's
// settings.driverApp.jobModal.hidden names it — PIN driver sessions only; the
// office board is not the driver app. clock + jsa are NEVER_HIDE in the reader,
// so they always come back. Known gap (documented on the settings page): the
// non-clock driver quickview (dispatch.js showDriverQuickview) and the placard
// badges do not read this.
// ── TASK-9650 BEGIN — project library docs on the driver's job view ────────
// jobs/{id}.projectDocs = [{projectId, docId, name, mimeType}] refs picked in
// the job form from the project's "Use on jobs" library docs. The file opens
// by ids (tenants/{t}/project-docs/{pid}/{docId}); storage.rules lets a crew
// session read it only while the library doc is still "Use on jobs". Shown
// next to Load Permits and hidden with them (Driver App 'permits' setting).
const _JB_PROJ_DOC_ID = /^[A-Za-z0-9_-]{1,128}$/;
function _jbProjectDocRefs(job) {
  return (Array.isArray(job && job.projectDocs) ? job.projectDocs : [])
    .filter(r => r && r.projectId === job.projectId && _JB_PROJ_DOC_ID.test(String(r.projectId || '')) && _JB_PROJ_DOC_ID.test(String(r.docId || '')))
    .slice(0, 20);
}
function _jbProjectDocsSection(job) {
  const refs = _jbProjectDocRefs(job);
  if (!refs.length) return '';
  if (window.AppState?.driverModePin && window.driverAppIsHidden && window.driverAppIsHidden('jobModal', 'permits')) return '';
  return `
        <div class="jobboard-detail-section" data-section="projectDocs">
          <div class="jobboard-detail-label">
            <span class="material-icons" style="font-size:16px;vertical-align:-3px;color:var(--brand-blue);">folder_special</span>
            Project documents
          </div>
          <div style="display:flex;flex-direction:column;gap:6px;margin-top:8px;">${refs.map((r, i) => {
            const pdf = r.mimeType === 'application/pdf' || /\.pdf$/i.test(String(r.name || ''));
            return `<button type="button" class="jb-project-doc" onclick="window.JobBoardModule.openJbProjectDoc('${escJsAttr(job.id || '')}', ${i})"
              style="display:flex;align-items:center;gap:10px;width:100%;min-height:48px;padding:8px 12px;text-align:left;font:inherit;color:var(--text-primary);background:var(--bg-elevated,#fff);border:1px solid var(--border-light);border-radius:8px;cursor:pointer;">
              <span class="material-icons" aria-hidden="true" style="color:${pdf ? '#DC2626' : '#2563EB'};">${pdf ? 'picture_as_pdf' : 'image'}</span>
              <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:600;">${escapeHtml(r.name || 'Document')}</span>
              <span style="font-size:0.85rem;font-weight:600;color:var(--brand-blue);">View</span>
            </button>`;
          }).join('')}</div>
        </div>`;
}
async function openJbProjectDoc(jobId, i) {
  const job = (JobBoardState.jobs || []).find(j => j && j.id === jobId);
  const r = job ? _jbProjectDocRefs(job)[Number(i)] : null;
  const t = JobBoardState.tenantId;
  if (!r || !t || !_JB_PROJ_DOC_ID.test(t)) return;
  const pdf = r.mimeType === 'application/pdf' || /\.pdf$/i.test(String(r.name || ''));
  const win = pdf ? window.open('', '_blank') : null;
  if (win) { try { win.opener = null; } catch (_e) { /* ignore */ } }
  try {
    const url = await getDownloadURL(ref(storage, `tenants/${t}/project-docs/${r.projectId}/${r.docId}`));
    if (pdf) { if (win) win.location = url; else window.open(url, '_blank', 'noopener'); }
    else if (window.PhotoLightbox?.show) window.PhotoLightbox.show(url);
    else window.open(url, '_blank', 'noopener');
  } catch (err) {
    try { win?.close(); } catch (_e) { /* ignore */ }
    console.warn('[JobBoard] project doc open failed:', err && err.code);
    window.showToast?.(err && err.code === 'storage/unauthorized' ? 'This document is no longer shared to jobs. Ask dispatch.' : 'Could not open that file', 'error');
  }
}
// ── TASK-9650 END ───────────────────────────────────────────────────────────

function _jbDetailSec(id, html) {
  if (!html) return '';
  if (window.AppState?.driverModePin && window.driverAppIsHidden && window.driverAppIsHidden('jobModal', id)) return '';
  if (html.indexOf('data-section=') !== -1) return html;
  return html.replace('class="jobboard-detail-section"', 'class="jobboard-detail-section" data-section="' + id + '"');
}

// TASK-9390 — the job type a DRIVER sees: the tenant list's label for the
// stored slug (a legacy free-text value shows as itself), '' for the default
// "Job" so the modal is unchanged for the ~95% of jobs that never set one.
// Plain text; the caller escapes. window.JobTypes absent → '' (never a slug).
function _jbJobTypeLabel(job) {
  const JT = window.JobTypes;
  if (!job || !JT || JT.isDefault(job.type)) return '';
  return JT.labelForJob(job, JT.list(window.AppState?.settings || {}));
}

// TASK-10106 S1b — rentals a driver ASKED the office for on this job (Add /
// Edit Job on the driver board) and the office's answer: waiting, OK'd, OK'd
// at a different number, or no. Nothing here is held stock. Text only, escaped.
function _jbRentalRequestsHtml(job) {
  const m = job && job.assetRequests;
  if (!m || typeof m !== 'object' || Array.isArray(m)) return '';
  const ms = (v) => (v && typeof v.toMillis === 'function' ? v.toMillis() : (v && typeof v.seconds === 'number' ? v.seconds * 1000 : 0));
  const rows = Object.entries(m)
    .filter(([k, e]) => /^[A-Za-z0-9_-]{1,128}$/.test(k) && e && typeof e === 'object')
    .sort((a, b) => ms(b[1].resolvedAt || b[1].at) - ms(a[1].resolvedAt || a[1].at))
    .slice(0, 20)
    .map(([, e]) => {
      const nm = String(e.name || 'Rental').slice(0, 80);
      const q = Number(e.qty) || 1;
      let txt = `Waiting on the office: ${q} ${nm} (asked by ${String(e.personName || 'a driver').slice(0, 60)}) — nothing is held yet`;
      let color = '#B45309';
      if (e.status === 'declined') { txt = `Office said no: ${q} ${nm}`; color = '#B91C1C'; }
      else if (e.status === 'edited') { txt = `Office OK'd ${Number(e.resolvedQty) || 1} ${nm} (asked for ${q})`; color = 'var(--text-primary)'; }
      else if (e.status === 'confirmed') { txt = `Office OK'd ${q} ${nm}`; color = 'var(--text-primary)'; }
      return `<div style="color:${color};padding:2px 0;">${escapeHtml(txt)}</div>`;
    });
  if (!rows.length) return '';
  return `<div class="jobboard-detail-section jb-rental-requests">
          <div class="jobboard-detail-label">Rentals asked for</div>
          <div class="jobboard-detail-value">${rows.join('')}</div>
        </div>`;
}

function showJobDetails(jobId, jobDayId) {
  const job = _findJobAnywhere(jobId);
  const jobDay = _findJobDayAnywhere(jobDayId);

  if (!job) {
    window.showToast?.('Job not found', 'error');
    return;
  }

  const customer = getCustomerName(job.customerId);
  const status = jobDay?.status || job.status || 'scheduled';

  // Get assignments
  const drivers = (jobDay?.drivers || []).map(ref => getPersonName(ref)).filter(Boolean);
  const truckRefs = jobDay?.trucks || [];
  const trucks = truckRefs.map(ref => getTruckUnit(ref)).filter(Boolean);
  const trailers = (jobDay?.trailers || []).map(ref => getTrailerUnit(ref)).filter(Boolean);

  const time = jobDay?.startTime || job.defaultStart || job.startTime || '';
  const location = job.location || '';
  const notes = jobDay?.notes || job.notes || '';
  const poNumber = job.poNumber || '';
  const vendorNumber = job.vendorNumber || '';
  // TASK-9390 — the job's type LABEL (tenant list), hidden for the default "Job".
  const jobTypeLabel = _jbJobTypeLabel(job);

  // Create modal
  const modal = document.createElement('div');
  modal.className = 'modal hidden';
  modal.id = 'jobboard-detail-modal';
  modal.dataset.jobId = jobId || ''; // TASK-794: lets the JSA snapshot refresh this modal's section
  modal.dataset.jobDayId = jobDayId || '';
  // TASK-9371 — "every JSA on the job" cannot lean on the 30-day/500 window;
  // take a one-shot job-scoped read and re-render the JSA slot when it lands.
  _jbFetchJobJsas(jobId);
  // TASK-9372 — same idea for equipment inspections: a one-shot job-scoped
  // pretrips read + asset-name resolution; the slot re-renders when it lands.
  _jbFetchJobInspections(jobId, jobDay);
  modal.innerHTML = `
    <div class="modal-content" style="max-width:500px;">
      <div class="modal-header">
        <h3>${_jbJobNumberPrefixHtml(job)}${escapeHtml(job.title || job.name || 'Job Details')}</h3>
        <button class="modal-close" onclick="window.JobBoardModule.closeJobDetails()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        ${_jbDetailSec('customer', `<div class="jobboard-detail-section">
          <div class="jobboard-detail-label">Customer</div>
          <div class="jobboard-detail-value">${escapeHtml(customer)}</div>
        </div>`)}

        <div id="jobboard-detail-project-strip"></div>
        <div id="jobboard-detail-site-notes"></div>
        <div id="jobboard-detail-map-notes"></div>

        ${_jbDetailSec('po', poNumber ? `
        <div class="jobboard-detail-section">
          <div class="jobboard-detail-label">PO Number</div>
          <div class="jobboard-detail-value">${escapeHtml(poNumber)}</div>
        </div>
        ` : '')}

        ${_jbDetailSec('vendor', vendorNumber ? `
        <div class="jobboard-detail-section">
          <div class="jobboard-detail-label">Vendor Number</div>
          <div class="jobboard-detail-value">${escapeHtml(vendorNumber)}</div>
        </div>
        ` : '')}

        ${_jbDetailSec('type', jobTypeLabel ? `
        <div class="jobboard-detail-section">
          <div class="jobboard-detail-label">Type</div>
          <div class="jobboard-detail-value">${escapeHtml(jobTypeLabel)}</div>
        </div>
        ` : '')}

        ${_jbDetailSec('start', time ? `
        <div class="jobboard-detail-section">
          <div class="jobboard-detail-label">Start Time</div>
          <div class="jobboard-detail-value">${escapeHtml(formatTime(time))}</div>
        </div>
        ` : '')}

        ${_jbDetailSec('location', (location || _jbWaypointLinks(job, { arrivalDay: jobDay })) ? `
        <div class="jobboard-detail-section">
          <div class="jobboard-detail-label">Location</div>
          <div class="jobboard-detail-value">
            ${location ? escapeHtml(location) : ''}
            ${job.googleMapsUrl ? `<a href="${encodeURI(job.googleMapsUrl)}" target="_blank" class="maps-link"><span class="material-icons">near_me</span></a>` : ''}
            ${(() => { const wl = _jbWaypointLinks(job, { arrivalDay: jobDay }); return wl ? `${location ? '<div style="margin-top:6px;">' : '<div>'}${wl}</div>` : ''; })()}
          </div>
        </div>
        ` : '')}

        ${_jbDetailSec('status', `<div class="jobboard-detail-section">
          <div class="jobboard-detail-label">Status</div>
          <div class="jobboard-detail-value">
            <span class="status-badge status-${escapeHtml(status)}">${escapeHtml(capitalizeFirst(status.replace('-', ' ')))}</span>
          </div>
        </div>`)}

        ${_jbDetailSec('crew', drivers.length ? `
        <div class="jobboard-detail-section">
          <div class="jobboard-detail-label">Crew</div>
          <div class="jobboard-detail-value">${drivers.map(d => escapeHtml(d)).join(', ')}</div>
        </div>
        ` : '')}

        ${_jbDetailSec('trucks', trucks.length ? `
        <div class="jobboard-detail-section">
          <div class="jobboard-detail-label" style="display:flex;align-items:center;gap:6px;">Trucks${truckRefs.length > 2 ? `<span class="jb-swipe-hint">Swipe for more</span><span class="material-icons jb-swipe-hint-icon" aria-hidden="true">swipe_left</span>` : ''}</div>
          ${jobDay ? _jbYourTruckLineHtml(jobDay) : ''}
          <div class="jobboard-detail-value">${truckRefs.map((ref, i) => {
            const label = trucks[i] || ref;
            return `<button class="btn btn-secondary btn-small" style="margin:2px 4px 2px 0;" onclick="window.JobBoardModule.showVehicleInfo('${escJsAttr(String(ref))}')"><span class="material-icons" style="font-size:16px;vertical-align:middle;">local_shipping</span> ${escapeHtml(label)}</button>`;
          }).join('')}</div>
        </div>
        ` : '')}

        ${_jbDetailSec('trailers', trailers.length ? `
        <div class="jobboard-detail-section">
          <div class="jobboard-detail-label">Trailers</div>
          <div class="jobboard-detail-value">${trailers.map(t => escapeHtml(t)).join(', ')}</div>
        </div>
        ` : '')}

        ${_jbRentalRequestsHtml(job)}

        ${jobDay ? buildDriverLoadCounterHTML(jobDay) : ''}

        ${_jbDetailSec('notes', notes ? `
        <div class="jobboard-detail-section">
          <div class="jobboard-detail-label">Notes</div>
          <div class="jobboard-detail-value jobboard-notes">${escapeHtml(notes)}</div>
        </div>
        ` : '')}

        ${_jbDetailSec('attachments', job.photosVisible && job.photos && job.photos.length > 0 ? `
        <div class="jobboard-detail-section">
          <div class="jobboard-detail-label">Attachments</div>
          <div class="driver-qv-photos-grid" style="margin-top:8px;">
            ${job.photos.map((photo, idx) => {
              // TASK-9855 — SDK read on the stored path (fullPath/thumbPath), not the token URL.
              const isPdf = photo.name?.toLowerCase().endsWith('.pdf');
              return isPdf
                ? `<a ${window.StorageFile.linkAttrs(photo)} class="driver-qv-photo-item driver-qv-pdf">
                    <span class="material-icons">picture_as_pdf</span>
                    <span class="driver-qv-pdf-name">${escapeHtml(photo.name || 'Document')}</span>
                  </a>`
                : `<a ${window.StorageFile.linkAttrs(photo)} class="driver-qv-photo-item">
                    <img ${window.StorageFile.imgAttrs(photo, { urlKey: photo.thumbUrl ? 'thumbUrl' : undefined })} alt="Attachment ${idx + 1}" loading="lazy">
                  </a>`;
            }).join('')}
          </div>
        </div>
        ` : '')}

        ${_jbDetailSec('permits', job.permits && job.permits.length > 0 ? `
        <div class="jobboard-detail-section">${[['Load Permits', job.permits.filter(f => !isJobDocEntry(f, job))], ['Docs', job.permits.filter(f => isJobDocEntry(f, job))]].filter(([, l]) => l.length).map(([label, list]) => `
          <div class="jobboard-detail-label">
            <span class="material-icons" style="font-size:16px;vertical-align:-3px;color:var(--brand-blue);">description</span>
            ${label}
          </div>
          <div class="driver-qv-photos-grid" style="margin-top:8px;">
            ${list.map((permit, idx) => {
              // TASK-9855 — SDK read on the stored path (fullPath/thumbPath), not the token URL.
              const isPdf = permit.isPdf || permit.mimeType === 'application/pdf' || permit.name?.toLowerCase().endsWith('.pdf');
              return isPdf
                ? `<a ${window.StorageFile.linkAttrs(permit)} class="driver-qv-photo-item driver-qv-pdf">
                    <span class="material-icons">picture_as_pdf</span>
                    <span class="driver-qv-pdf-name">${escapeHtml(permit.name || (isJobDocEntry(permit, job) ? 'Document' : 'Permit'))}</span>
                  </a>`
                : `<a ${window.StorageFile.linkAttrs(permit)} class="driver-qv-photo-item">
                    <img ${window.StorageFile.imgAttrs(permit, { urlKey: permit.thumbUrl ? 'thumbUrl' : undefined })} alt="Permit ${idx + 1}" loading="lazy">
                  </a>`;
            }).join('')}
          </div>`).join('')}
        </div>
        ` : '')}

        ${_jbProjectDocsSection(job) /* TASK-9650 */}

        ${(() => { // TASK-9767 — project jobs: the wrapper IS the 811 section (data-section="811"), so the job screen moves it with its async target
          const inner = _jbDetailSec('811', _jb811PermitSection(job, null, _jb811DayKey(jobDay?.date))); // TASK-9862 — judged for THIS day
          const on811 = window.AppState?.settings?.enable811 === true || window.AppState?.isDemo === true;
          const hidden = !!(window.AppState?.driverModePin && window.driverAppIsHidden && window.driverAppIsHidden('jobModal', '811'));
          return job.projectId && on811 && !hidden
            ? `<div id="jb-811-wrap" class="jobboard-detail-section-wrap" data-section="811" data-job-id="${escapeHtml(job.id || '')}" data-day-key="${escapeHtml(_jb811DayKey(jobDay?.date))}">${inner.replace(' data-section="811"', '')}</div>` : inner; })()}

        <div id="jobboard-detail-clock-section" data-section="clock">${_jbJobClockSection(job, jobDay)}</div>

        <div id="jobboard-detail-jsa-section" data-section="jsa">${_jbJobJsaSection(job, jobDay)}</div>

        ${_jbDetailSec('inspections', `<div id="jobboard-detail-inspections-section" data-section="inspections">${_jbJobInspectionsSection(job, jobDay)}</div>`)}

        ${_jbDetailSec('tickets', (() => {
          const jobTickets = getTicketsForJob(job.id);
          if (!jobTickets.length) return '';
          return `
            <div style="margin-top:16px;border-top:1px solid var(--border-light);padding-top:16px;" data-section="tickets">
              <div style="font-weight:600;color:var(--text-primary);margin-bottom:8px;">
                <span class="material-icons" style="font-size:18px;vertical-align:-3px;">receipt_long</span>
                Tickets (${jobTickets.length})
              </div>
              ${jobTickets.map(t => `
                <button class="btn btn-secondary" style="width:100%;margin-bottom:6px;text-align:left;display:flex;align-items:center;gap:8px;"
                  onclick="event.stopPropagation();window.JobBoardModule.showDriverTicket('${t.id}')">
                  <span class="material-icons" style="color:var(--brand-blue);">description</span>
                  <span>Ticket #${t.ticketNo || '—'} — ${t.workDate || ''}</span>
                  ${_ticketDriverSigUrl(t) ? '<span class="material-icons" style="margin-left:auto;color:#10B981;font-size:18px;">verified</span>' : ''}
                </button>
              `).join('')}
            </div>
          `;
        })())}

        ${_jbDetailSec('documents', `<div id="jobboard-detail-documents-section" data-section="documents">${_jbJobDocumentsSection(job, jobDay)}</div>`)}

        ${_jbDriverEditJobButton(job, jobDay)}
      </div>
    </div>
  `;

  // TASK-9694 — per-tenant switch (settings.ticketOnJobScreen, default OFF):
  // the job screen IS the ticket. Re-uses the rows built above verbatim (moved
  // into a "Job info" card), so OFF renders exactly what it rendered before.
  const _jobScreen = _jbUseJobScreen(job, jobDay);
  if (_jobScreen) {
    const _prevScreen = document.getElementById('jobboard-detail-modal');
    if (_prevScreen && _prevScreen.dataset.jobscreen === '1') closeJobDetails();
    _jbBuildJobScreen(modal, job, jobDay);
  }

  document.body.appendChild(modal);
  modal.classList.remove('hidden');
  document.body.classList.add('modal-open');

  // Close on backdrop click
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeJobDetails();
  });

  // Async-fill project context strip if this job belongs to a project.
  if (job.projectId) fillProjectContextStrip(job.projectId);
  if (job.projectId) _jbFillProject811(job); // TASK-9767
  _jbFillSiteNotes(job, jobDay); // TASK-9498
  _jbFillMapNotes(job, jobDay); // TASK-4789 S3

  if (_jobScreen) {
    _jbJobScreenRefresh({ force: true });
    _jbJsFetchFinished(job);
    _jbNpdMount(); // TASK-9694 S5
  }
}

// TASK-9498 — "Site notes" at the top of the job: the marks saved to the
// customer site this job is at (gate, staging, hazard, route in / avoid), with
// who drew them and when, plus one tap to directions for a pin. LAZY chunk
// (views/siteMarksStrip.js); the getJobSiteMarks callable decides what this
// viewer may see (a driver: only their own job day, around the day). Nothing
// here renders stored text — the strip builds its DOM with textContent.
function _jbFillSiteNotes(job, jobDay) {
  if (!job || !job.customerId || !jobDay || !jobDay.id || !JobBoardState.tenantId) return;
  __ddChunkWrap(import('./siteMarksStrip.js?v=9498002')).then((m) => m.fillForJob(job, jobDay, { functions, httpsCallable, tenantId: JobBoardState.tenantId }))
    .catch((e) => console.warn('[JobBoard] site notes', e));
}

// TASK-4789 S3 — "Map notes": drawn map layers dispatch attached to a stop on
// this job (lazy views/jobMapLayersStrip.js; the getJobMapLayers callable
// decides what this viewer may see). Only jobs with stop ids can have any.
function _jbFillMapNotes(job, jobDay) {
  if (!job || !jobDay || !jobDay.id || !JobBoardState.tenantId || !(job.waypoints || []).some((w) => w && w.id)) return;
  __ddChunkWrap(import('./jobMapLayersStrip.js?v=1')).then((m) => m.fillForJob(jobDay, { functions, httpsCallable, tenantId: JobBoardState.tenantId }))
    .catch((e) => console.warn('[JobBoard] map notes', e));
}

async function fetchProjectIfNeeded(projectId) {
  if (!projectId || !JobBoardState.tenantId) return null;
  if (Object.prototype.hasOwnProperty.call(JobBoardState.projectsCache, projectId)) {
    return JobBoardState.projectsCache[projectId];
  }
  try {
    // TASK-9563 slice 0 — a PIN driver can no longer read a project doc (it
    // carried the portal PIN and revenue). It gets id/name/status/startDate/
    // endDate from the session-bound callable instead; members keep the direct
    // read. Dates arrive as epoch ms — fillProjectContextStrip's `new Date(raw)`
    // handles both shapes.
    let data = null;
    if (window.AppState?.driverModePin === true) {
      const res = await httpsCallable(functions, 'driverProjectInfo')({ ids: [projectId] });
      const p = (res?.data?.projects || []).find(x => x && x.id === projectId) || null;
      data = p ? { ...p } : null;
    } else {
      const snap = await getDoc(doc(db, 'tenants', JobBoardState.tenantId, 'projects', projectId));
      data = snap.exists() ? { id: snap.id, ...snap.data() } : null;
    }
    JobBoardState.projectsCache[projectId] = data;
    return data;
  } catch (e) {
    console.warn('[JobBoard] project fetch failed', projectId, e);
    JobBoardState.projectsCache[projectId] = null;
    return null;
  }
}

// TASK-395 Slice 4: resolveRequireDriverSignature removed. Signing moved into the
// Build Ticket modal (jbBuildSignatureSection), gated on the jobDay's denormalized
// fieldTicketSettings — PIN drivers can't read contracts, so the old contract
// fetch here failed closed and the canvas never appeared for them anyway.

// TASK-9767 — a project's 811 tickets on its jobs. A PIN driver cannot read
// projects (TASK-9563); fetchProjectIfNeeded goes through driverProjectInfo,
// which now carries the project's permitIds. Repaints only the 811 block of the
// SAME job's open modal.
async function _jbFillProject811(job) {
  const project = await fetchProjectIfNeeded(job.projectId);
  const ids = Array.isArray(project && project.permitIds) ? project.permitIds : [];
  if (!ids.length) return;
  const wrap = document.getElementById('jb-811-wrap');
  if (!wrap || wrap.getAttribute('data-job-id') !== String(job.id || '')) return; // closed / another job
  wrap.innerHTML = _jbDetailSec('811', _jb811PermitSection(job, ids, wrap.getAttribute('data-day-key') || '')).replace(' data-section="811"', '');
}

async function fillProjectContextStrip(projectId) {
  const project = await fetchProjectIfNeeded(projectId);
  const slot = document.getElementById('jobboard-detail-project-strip');
  if (!slot) return; // modal closed before fetch resolved
  if (!project) return;
  if (project.status === 'archived' || project.status === 'cancelled') return;
  if (!project.startDate) return;

  const toLocalMidnight = (raw) => {
    const d = raw?.toDate ? raw.toDate() : new Date(raw);
    if (!d || isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
  };
  const start = toLocalMidnight(project.startDate);
  const end = project.endDate ? toLocalMidnight(project.endDate) : null;
  if (!start) return;

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dayNum = Math.floor((today.getTime() - start.getTime()) / 86400000) + 1;
  const safeDay = Math.max(1, dayNum);

  let label;
  let progressHtml = '';
  if (end) {
    const totalDays = Math.max(1, Math.floor((end.getTime() - start.getTime()) / 86400000) + 1);
    const overrun = dayNum > totalDays;
    const cappedDay = overrun ? totalDays : safeDay;
    const pct = Math.min(100, Math.max(0, Math.round((cappedDay / totalDays) * 100)));
    label = overrun
      ? `Day ${totalDays} of ${totalDays} <span style="color:#DC2626;font-weight:600;">(Overrun)</span>`
      : `Day ${cappedDay} of ${totalDays}`;
    progressHtml = `<div style="height:4px;background:var(--border-light);border-radius:999px;margin-top:6px;overflow:hidden;"><div style="height:100%;width:${pct}%;background:var(--brand-blue);border-radius:999px;"></div></div>`;
  } else {
    label = `Day ${safeDay}`;
  }

  // TASK-9694 polish — on the job screen the project rides the header card as
  // one chip ("EN-Rothchild · Day 43"). label is built from numbers here.
  if (slot.dataset.chip === '1') {
    slot.innerHTML = `<span class="jb-js-chip"><span class="material-icons" aria-hidden="true">folder_open</span><span class="jb-js-chip-name">${escapeHtml(project.name || 'Project')}</span><span>·</span><span style="white-space:nowrap;">${label}</span></span>`;
    return;
  }
  slot.innerHTML = `
    <div class="jobboard-detail-section" style="background:rgba(59,130,246,0.06);border-radius:8px;padding:10px 12px;border:1px solid rgba(59,130,246,0.18);">
      <div style="display:flex;align-items:center;gap:8px;font-size:0.85rem;font-weight:600;color:var(--text-primary);">
        <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">folder_open</span>
        <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(project.name || 'Project')}</span>
      </div>
      <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:4px;">${label}</div>
      ${progressHtml}
    </div>
  `;
}

// TASK-9511 — reopen a job's detail modal when coming BACK from a JSA form the
// job launched. Returns false (no "Job not found" toast) while the board is still
// loading, so the caller can retry; true once the modal is open.
function reopenJobDetails(jobId, jobDayId) {
  if (!jobId || !_findJobAnywhere(jobId)) return false;
  showJobDetails(jobId, jobDayId || undefined);
  return true;
}

function closeJobDetails() {
  const modal = document.getElementById('jobboard-detail-modal');
  // TASK-9694 (COD S1 fold) — closing the job SCREEN must reset the _bt* ticket
  // state it may hold (inline editor, or the Switch sheet's entry), or the next
  // job / next driver inherits it. Skipped while a CLASSIC #clockout-modal is up:
  // that editor owns the state. The classic job modal (switch OFF) is untouched.
  if (modal && modal.dataset.jobscreen === '1') {
    _jbJsTeardown();
    const _ed = document.getElementById('clockout-modal');
    if (!_ed || _ed.dataset.btInline === '1') jbResetBuildTicketState();
  }
  if (modal) modal.remove();
  document.body.classList.remove('modal-open');
}

// =============================================================================
// TASK-9694 — DRIVER JOB SCREEN = THE TICKET (approved layout A, Chris
// 2026-10-07). Behind a per-company switch, settings.ticketOnJobScreen (default
// OFF; a top-level key on purpose — settings.driverApp is rewritten whole by the
// Driver App page through normalizeDriverAppLayout, which would drop it, and
// its shape is rules-validated). OFF = every tenant renders today's job modal
// and today's Build Ticket, byte-for-byte.
//
// ON, the job modal becomes one full-screen page:
//   header   — the Build Ticket hero. Four states:
//              pre      "Not started", dispatch start time or "All day", plain
//                       placards, ONE bar action (Clock In / Start Job). No live
//                       clock (11/11 COD lanes: device time ≠ recorded time).
//              here     clocked in on THIS job: the inline ticket editor, its
//                       own hero unchanged (IN / hours / OUT, pencils, Fix it,
//                       "Switch Jobs"), Cancel · Save · Clock Out|Finish Job.
//              other    clocked in elsewhere: amber "On another job", the only
//                       action is Switch Jobs to this job (server switchJob) —
//                       never a second clock-in (double punch).
//              finished ticket sent: In / hours / Out kept, green read-only
//                       line, no pencils, no bar.
//   Job info — the job modal's rows VERBATIM (built by showJobDetails, then moved
//              into a collapsible card), section order rows → JSA → Inspections
//              → (clock) → Tickets → Edit Job.
// Clock in/out go through the SAME actions as today (clockInFromBoard →
// _jbFinalizeClockIn; submitClockOut), so GPS + geofence stamping is unchanged.
// =============================================================================
function isTicketOnJobScreen() {
  return window.AppState?.settings?.ticketOnJobScreen === true && isTicketMode();
}

// Same gates the job modal's clock section uses (_jbJobClockSection): a person,
// clock access, the job + day on the LIVE board. Anything else keeps the
// classic modal.
function _jbUseJobScreen(job, jobDay) {
  if (!job || !isTicketOnJobScreen()) return false;
  if (window.isReadOnlyLinkSession?.()) return false;
  if (!getActiveJobBoardPersonId() || !window.driverHasClockAccess?.()) return false;
  if (!(JobBoardState.jobs || []).some(j => j.id === job.id)) return false;
  if (jobDay?.id && !(JobBoardState.jobDays || []).some(jd => jd.id === jobDay.id)) return false;
  return true;
}

function _jbJsEnsureStyles() {
  if (document.getElementById('jb-js-styles')) return;
  const s = document.createElement('style');
  s.id = 'jb-js-styles';
  // Thin overlay scrollbar (the mockup's phone scroller) — no desktop gutter.
  s.textContent = '.jb-js-content{scrollbar-width:thin;scrollbar-color:rgba(100,116,139,.45) transparent}'
    + '.jb-js-content::-webkit-scrollbar{width:3px}.jb-js-content::-webkit-scrollbar-track{background:transparent}'
    + '.jb-js-content::-webkit-scrollbar-thumb{background:rgba(100,116,139,.45);border-radius:3px}'
    + '#jb-js-jobinfo .modal-body{padding:0;max-height:none;overflow:visible}'
    // TASK-9694 polish (Chris approved mockup 2026-10-08) — scoped to the job
    // screen ONLY (.jb-jobscreen is set by _jbBuildJobScreen, switch ON): one
    // 12px gutter, #EEF2F6 canvas, separate white cards, no thin dividers.
    // !important only where the existing sections carry INLINE dividers.
    + '.jb-jobscreen .jb-js-content{background:#EEF2F6!important}'
    // Full-bleed on a phone: no .modal frame padding around the screen.
    + '.modal.jb-jobscreen{padding:0!important}'
    + '.jb-jobscreen>.jb-js-content{width:100%!important;border-radius:0!important}'
    // TASK-9714 — the sticky bar is 12 + 52 + max(12, safe-area) px tall. The
    // bottom padding now grows with the safe area (130px alone was 4px short
    // on a 34px-inset iPhone), and scroll-padding-bottom keeps anything scrolled
    // INTO view (the JSA card after returning from a JSA) above the bar instead
    // of under it — scrollIntoView ignores padding, not scroll-padding.
    + '.jb-jobscreen #jb-js-jobinfo-home{padding:0 12px max(130px, calc(100px + env(safe-area-inset-bottom, 0px)))!important}'
    + '.jb-jobscreen #jb-bt-sections{padding:0 12px max(130px, calc(100px + env(safe-area-inset-bottom, 0px)))!important}'
    + '.modal.jb-jobscreen,.jb-jobscreen>.jb-js-content{scroll-padding-bottom:calc(90px + env(safe-area-inset-bottom, 0px))}'
    + '.jb-jobscreen #jb-js-hero,.jb-jobscreen .jb-bt-inline>div:first-child{margin-bottom:12px!important}'
    + '.jb-js-stack{display:flex;flex-direction:column;gap:12px;margin-bottom:12px}'
    + '.jb-js-card{background:#fff;border:1.5px solid #A9B6C7;border-radius:14px;padding:14px;box-shadow:0 1px 2px rgba(15,23,42,.06);min-width:0}'
    + '.jb-js-card .jobboard-detail-section{border:0!important;margin:0 0 10px!important;padding:0!important}'
    + '.jb-js-card .jobboard-detail-section:last-child{margin-bottom:0!important}'
    + '.jb-js-card .jobboard-detail-label{color:#475569;font-weight:700}'
    + '.jb-js-card>[data-section]>div:first-child,.jb-js-card>[data-section="tickets"],.jb-js-card>div[style*="border-top"],.jb-js-card>div>div[style*="border-top"]{margin-top:0!important;border-top:0!important;padding-top:0!important}'
    + '.jb-js-loads>div{background:transparent!important;padding:0!important;margin:0!important}'
    + '.jb-js-stack>:empty{display:none}'
    + '.jb-js-cust{font-size:0.78rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#1d4ed8}'
    + '.jb-js-title{font-size:1.2rem;font-weight:800;color:#0f172a;margin:2px 0 8px;overflow-wrap:anywhere}'
    + '.jb-js-chip{display:inline-flex;align-items:center;gap:6px;max-width:100%;background:#EAF0FB;border:1px solid #9DB5E6;border-radius:999px;padding:4px 10px;font-size:0.85rem;font-weight:700;color:#1e3a8a}'
    + '.jb-js-chip .material-icons{font-size:16px}.jb-js-chip-name{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}'
    + '.jb-js-line{display:flex;align-items:flex-start;gap:6px;font-size:0.95rem;color:#1e293b;margin-top:8px;overflow-wrap:anywhere}'
    + '.jb-js-line .material-icons{font-size:18px;color:#475569;margin-top:1px}'
    + '.jb-js-nav{display:inline-flex;align-items:center;gap:4px;min-height:44px;color:#1d4ed8;font-weight:800;text-decoration:none}'
    + '.jb-js-more{margin-top:6px}.jb-js-more>summary{list-style:none;min-height:48px;display:flex;align-items:center;gap:4px;font-size:0.9rem;font-weight:800;color:#1d4ed8;cursor:pointer}'
    + '.jb-js-more>summary::-webkit-details-marker{display:none}.jb-js-more[open]>summary .jb-js-caret{transform:rotate(180deg)}'
    + '.jb-js-more-body{padding-top:4px}'
    + '.jb-js-tag{margin-left:auto;font-size:0.75rem;font-weight:800;border-radius:6px;padding:3px 8px;white-space:nowrap}'
    + '.jb-js-tag-need{color:#7c2d12;background:#FEF3C7;border:1px solid #F59E0B}.jb-js-tag-done{color:#065f46;background:#D1FAE5;border:1px solid #10B981}'
    + '.jb-jobscreen .jb-js-card .btn{min-height:48px!important}'
    + '.jb-jobscreen #jb-btn-save-ticket .btn-spinner{border-color:rgba(15,23,42,.2);border-top-color:#0f172a}'
    + '#jb-js-cin-status:empty{display:none}'
    + '.jb-js-cin{margin:0 0 12px;border-radius:10px;padding:9px 12px;font-weight:800;font-size:0.92rem;text-align:center;color:#fff;line-height:1.3}'
    + '.jb-js-cin-ok{background:#14532D;border:1px solid #4ADE80}.jb-js-cin-wait{background:#78350F;border:1px solid #FBBF24}.jb-js-cin-busy{background:#1e293b;border:1px solid #64748b}'
    + '.jb-js-spin{display:inline-block;width:18px;height:18px;border:2.5px solid rgba(255,255,255,.35);border-top-color:#fff;border-radius:50%;animation:jbJsSpin .8s linear infinite;flex:0 0 auto}'
    + '@keyframes jbJsSpin{to{transform:rotate(360deg)}}'
    + '.jb-js-sub{display:block;font-size:0.8rem;font-weight:600;opacity:.95;margin-top:2px}';
  document.head.appendChild(s);
}

function _jbJsInfoKey(job) { return 'dd-jbjs-jobinfo:' + (job?.id || ''); }

// Turn the freshly built (not yet attached) job modal into the job screen.
function _jbBuildJobScreen(modal, job, jobDay) {
  _jbJsEnsureStyles();
  const body = modal.querySelector('.modal-body');
  if (!body) return;
  // The clock slot is kept (ids stay stable for any reader) but empty — the
  // header + bar own clock state on this screen.
  const clockSlot = body.querySelector('#jobboard-detail-clock-section');
  if (clockSlot) clockSlot.innerHTML = '';
  modal.dataset.jobscreen = '1';
  modal.classList.add('jb-jobscreen');
  const content = document.createElement('div');
  content.className = 'modal-content modal-fullscreen jb-js-content';
  content.style.cssText = 'max-width:480px;margin:0 auto;padding:0;font-family:var(--font-body);min-height:100vh;min-height:100dvh;background:#EEF2F6;overflow-y:auto;';
  content.innerHTML = '<div id="jb-js-top"></div><div id="jb-js-ticket"></div>'
    + '<div id="jb-js-jobinfo-home" style="padding:0 12px 130px;"></div><div id="jb-js-bar"></div>';

  // TASK-9694 polish (Chris approved the mockup 2026-10-08) — the job rows built
  // by showJobDetails are split into SEPARATE cards instead of one nested card:
  // job header · Loads · Safety (JSA) · Inspections · Tickets · Edit Job. Every
  // section is the SAME node showJobDetails built (moved, not re-rendered), so
  // the tenant's Driver App hidden sections stay hidden, and the refresh slots
  // (#jobboard-detail-jsa-section / -inspections- / -project-strip) keep their
  // ids. The header is built here from job fields — every value escaped.
  const take = (sel) => { const el = body.querySelector(sel); if (el) el.remove(); return el || null; };
  const has = (id) => !!body.querySelector(`[data-section="${id}"]`);
  const stack = document.createElement('div');
  stack.id = 'jb-js-jobinfo';
  stack.className = 'jb-js-stack';
  const card = (cls, ...nodes) => {
    const c = document.createElement('div');
    c.className = 'jb-js-card' + (cls ? ' ' + cls : '');
    nodes.filter(Boolean).forEach(n => c.appendChild(n));
    return c;
  };

  // ── Job header card ──
  const cust = getCustomerName(job.customerId) || '';
  const title = job.title || job.name || 'Job';
  const showCust = has('customer');
  const showCrew = has('crew');
  const showLoc = has('location');
  const showPo = has('po') && !!job.poNumber;
  const crewNames = showCrew ? (jobDay?.drivers || []).map(ref => getPersonName(ref)).filter(Boolean) : [];
  const mapsUrl = String(job.googleMapsUrl || '');
  // https only (COD Adversary): encodeURI never blocked javascript:.
  const navHref = /^https:\/\//i.test(mapsUrl) ? mapsUrl : '';
  const wps = showLoc ? _jbWaypointLinks(job, { arrivalDay: jobDay }) : '';
  const locText = showLoc ? String(job.location || '') : '';
  const head = document.createElement('div');
  head.className = 'jb-js-card jb-js-head';
  head.innerHTML = `
    ${showCust && cust ? `<div class="jb-js-cust">${escapeHtml(cust)}</div>` : ''}
    <div class="jb-js-title">${escapeHtml(title)}</div>
    <div class="jb-js-chiprow"></div>
    ${crewNames.length ? `<div class="jb-js-line"><span class="material-icons" aria-hidden="true">group</span><span>${crewNames.map(n => escapeHtml(n)).join(', ')}</span></div>` : ''}
    ${(locText || navHref) && showLoc ? `<div class="jb-js-line"><span class="material-icons" aria-hidden="true">place</span><span>${locText ? `<span>${escapeHtml(locText)}</span>` : ''}${navHref ? `${locText ? ' ' : ''}<a class="jb-js-nav" href="${escapeHtml(navHref)}" target="_blank" rel="noopener noreferrer" onclick="event.stopPropagation();"><span class="material-icons" aria-hidden="true" style="font-size:18px;color:#1d4ed8;">near_me</span><span>Navigate</span></a>` : ''}</span></div>` : ''}
    ${wps ? `<div style="margin-top:6px;">${wps}</div>` : ''}
    ${showPo ? `<div class="jb-js-line"><span class="material-icons" aria-hidden="true">tag</span><span><b>PO</b> ${escapeHtml(job.poNumber)}</span></div>` : ''}
  `;
  // Project chip (EN-Rothchild · Day 43) — the same async-filled slot, chip style.
  const proj = take('#jobboard-detail-project-strip');
  if (proj) { proj.dataset.chip = '1'; head.querySelector('.jb-js-chiprow').appendChild(proj); }
  // TASK-9498 — the site notes slot rides in the header card (async-filled).
  const siteNotes = take('#jobboard-detail-site-notes');
  if (siteNotes) head.appendChild(siteNotes);
  const mapNotes = take('#jobboard-detail-map-notes'); // TASK-4789 S3
  if (mapNotes) head.appendChild(mapNotes);
  // Notes / attachments / permits / 811 stay VISIBLE in the header card (rule:
  // nothing a driver needs hides behind a collapse).
  ['notes', 'attachments', 'permits', 'projectDocs', '811'].forEach((id) => { // TASK-9650 projectDocs
    const n = take(`[data-section="${id}"]`);
    if (n) { n.style.marginTop = '10px'; head.appendChild(n); }
  });
  // "More job details ▾" — admin rows only (type, vendor #, start, status,
  // trucks, trailers). Native <details>: a ≥48px summary, no new fetch.
  const moreIds = ['type', 'vendor', 'start', 'status', 'trucks', 'trailers'];
  const moreNodes = moreIds.map(id => take(`[data-section="${id}"]`)).filter(Boolean);
  if (moreNodes.length) {
    const det = document.createElement('details');
    det.className = 'jb-js-more';
    let open = false;
    try { open = window.sessionStorage.getItem(_jbJsInfoKey(job)) === '1'; } catch (_) { /* storage blocked — default closed */ }
    det.open = open;
    det.innerHTML = '<summary><span>More job details</span><span class="material-icons jb-js-caret" aria-hidden="true" style="font-size:20px;">expand_more</span></summary><div class="jb-js-more-body"></div>';
    const mb = det.querySelector('.jb-js-more-body');
    moreNodes.forEach(n => mb.appendChild(n));
    det.addEventListener('toggle', () => { try { window.sessionStorage.setItem(_jbJsInfoKey(job), det.open ? '1' : '0'); } catch (_) { /* best effort */ } });
    head.appendChild(det);
  }
  take('[data-section="customer"]'); take('[data-section="po"]'); take('[data-section="location"]'); take('[data-section="crew"]');
  stack.appendChild(head);

  // ── Loads (the shared load counter — no data-section of its own) ──
  const loads = Array.from(body.children).find(el => !el.dataset.section && !el.id && /recordLoadPickup|Load Progress|All loads complete/.test(el.innerHTML));
  if (loads) { loads.remove(); stack.appendChild(card('jb-js-loads', loads)); }
  // ── Safety (JSA) — never collapsed; status tag ──
  const jsa = take('#jobboard-detail-jsa-section');
  if (jsa) {
    jsa.innerHTML = _jbJobJsaSection(job, jobDay, { statusTag: true });
    stack.appendChild(card('jb-js-safety', jsa));
  }
  // ── Equipment Inspections — never collapsed ──
  const insp = take('#jobboard-detail-inspections-section');
  if (insp) stack.appendChild(card('jb-js-insp', insp));
  if (clockSlot) { clockSlot.remove(); stack.appendChild(clockSlot); }
  // ── Tickets ──
  const tks = take('[data-section="tickets"]');
  if (tks) stack.appendChild(card('jb-js-tickets', tks));
  // ── Edit Job (driverJobEdit tenants) ──
  const edit = Array.from(body.children).find(el => /driverEditJob\(/.test(el.innerHTML));
  if (edit) { edit.remove(); stack.appendChild(card('jb-js-edit', edit)); }
  // Anything else showJobDetails rendered (a future section) keeps showing.
  Array.from(body.children).forEach((el) => { if (el.innerHTML.trim()) stack.appendChild(card('', el)); });

  content.querySelector('#jb-js-jobinfo-home').appendChild(stack);
  modal.replaceChildren(content);
}

// TASK-9694 S5 — "Notes / Pics / Docs" on the job screen: see AND add notes,
// pics and docs without leaving the job. The card + add flow live in the lazy
// views/jobScreenFiles.js (cold-load perf budget). Until it loads — or if it
// cannot (no signal on a cold cache) — the header keeps today's Notes /
// Attachments / Load Permits rows, so a driver never loses sight of a permit.
// jobboard.js hands in what it owns (no ?v= cross-import, Pitfall #1); the add
// gate is the job screen's Edit Job gate (_jbDriverJobEditAllowed). The add
// buttons are rendered BY the module and call window.JobScreenFiles, so every
// tap finds it already loaded; the module follows the job doc itself (it
// repaints the list when JobBoardState's copy of the job changes).
function _jbNpdMount() {
  __ddChunkWrap(import('./jobScreenFiles.js?v=9694601')).then((m) => m.run({ S: JobBoardState, me: getActiveJobBoardPersonId, ok: _jbDriverJobEditAllowed }),
    (e) => console.warn('[JobBoard] S5 files', e));
}

// The Job info card lives in #jb-js-jobinfo-home, or — while the inline ticket
// is mounted — docked as the first card of the ticket (layout A). DOM moves
// keep the JSA / inspections / project-strip slots inside it alive.
function _jbJsParkJobInfo() {
  const card = document.getElementById('jb-js-jobinfo');
  const home = document.getElementById('jb-js-jobinfo-home');
  if (card && home && card.parentElement !== home) home.appendChild(card);
  if (home) home.style.display = '';
}
function _jbJsDockJobInfo() {
  const card = document.getElementById('jb-js-jobinfo');
  const slot = document.getElementById('jb-js-jobinfo-slot');
  const home = document.getElementById('jb-js-jobinfo-home');
  if (card && slot) { slot.appendChild(card); if (home) home.style.display = 'none'; }
}

function _jbJsTodayKey() { return formatDateISO(new Date()); }

function _jbJsState(job) {
  const pid = getActiveJobBoardPersonId();
  const fin = (JobBoardState._jsFinished || {})[job.id + '|' + pid] || null;
  const my = (JobBoardState.clockEntries || []).find(e => e.personId === pid && e.status === 'active') || null;
  if (my && !(fin && fin.entryId === my.id)) {
    if (my.jobId && my.jobId === job.id) return { kind: 'here', key: 'here:' + my.id, entry: my };
    return { kind: 'other', key: 'other:' + my.id, entry: my };
  }
  if (fin && fin.dayKey === _jbJsTodayKey()) return { kind: 'finished', key: 'fin:' + fin.entryId, fin };
  return { kind: 'pre', key: 'pre' };
}

let _jbJsLastUnmounted = null;
let _jbJsClosingEntry = null; // entry id whose inline Clock Out / Finish Job is in flight
let _jbJsMountInFlight = null; // the inline ticket mount (restore) currently running
// Re-derive the screen's STATE. Same state → touch nothing (a mounted ticket is
// never re-rendered by a snapshot, so a half-typed note survives — COD S2 fold).
function _jbJobScreenRefresh(opts) {
  const modal = document.getElementById('jobboard-detail-modal');
  if (!modal || modal.dataset.jobscreen !== '1') return;
  const job = (JobBoardState.jobs || []).find(j => j.id === modal.dataset.jobId);
  if (!job) return;
  const jobDay = modal.dataset.jobDayId ? (JobBoardState.jobDays || []).find(jd => jd.id === modal.dataset.jobDayId) || null : null;
  const st = _jbJsState(job);
  const force = !!(opts && opts.force);
  if (!force && modal.dataset.jsState === st.key) return;
  const ed = document.getElementById('clockout-modal');
  const edInline = !!(ed && ed.dataset.btInline === '1');
  // The ticket's own Clock Out / Fix it / Switch is mid-flight: its first write
  // fires this snapshot BEFORE it has finished saving the ticket. Never tear the
  // editor down under it — that action owns the teardown (closeClockOutModal /
  // _jbJsAfterRepoint) and refreshes this screen itself when done.
  if (edInline && ((_jbJsClosingEntry && ed.dataset.entryId === _jbJsClosingEntry)
    || document.getElementById('jb-repoint-modal') || document.getElementById('jb-switch-modal'))) return;
  if (edInline && (st.kind !== 'here' || ed.dataset.entryId !== st.entry.id)) {
    // The open shift ended or moved — this editor no longer has an entry.
    _jbJsParkJobInfo();
    ed.remove();
    jbResetBuildTicketState();
  }
  const top = modal.querySelector('#jb-js-top');
  const bar = modal.querySelector('#jb-js-bar');
  const host = modal.querySelector('#jb-js-ticket');
  if (st.kind === 'here') {
    const cur = document.getElementById('clockout-modal');
    if (cur && cur.dataset.btInline === '1' && cur.dataset.entryId === st.entry.id) { modal.dataset.jsState = st.key; return; }
    // Just closed out from this editor; the snapshot that drops the entry is on
    // its way — don't re-open a ticket for a shift that is ending.
    if (_jbJsLastUnmounted === st.entry.id && !(opts && opts.remount)) return;
    // COD (Code Reviewer P1): one mount at a time. A restore writes the shared
    // _bt* state across awaits, so a second mount must not start until the first
    // settles — then this refresh re-runs against the state as it is THEN.
    if (_jbJsMountInFlight) {
      _jbJsMountInFlight.finally(() => _jbJobScreenRefresh({ force: true }));
      return;
    }
    modal.dataset.jsState = st.key;
    if (top) top.innerHTML = '';
    if (bar) bar.innerHTML = '';
    _jbJsTicker(true);
    _jbJsMountInFlight = Promise.resolve(clockOutFromBoard(st.entry.id, st.entry.jobId || '', _jbEntryOutDayId(st.entry, job, jobDay),
      { shiftChosen: true, inlineHost: host, expectState: st.key }))
      .catch((e) => console.warn('[JobBoard] inline ticket mount failed:', e?.message || e))
      .finally(() => { _jbJsMountInFlight = null; });
    return;
  }
  modal.dataset.jsState = st.key;
  _jbJsTicker(false);
  if (host) host.innerHTML = '';
  _jbJsParkJobInfo();
  if (top) top.innerHTML = _jbJsHeroHtml(st, job, jobDay);
  if (bar) bar.innerHTML = _jbJsBarHtml(st, job);
}

function _jbJsTimeStr(ms) {
  const d = new Date(ms);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function _jbJsHeroHtml(st, job, jobDay) {
  const lbl = 'font-size:0.8rem;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;font-weight:600;';
  const rule = '<div style="width:40px;height:1px;background:#334155;"></div>';
  const first = (getActiveJobBoardPersonName() || '').split(/\s+/)[0];
  const cust = getCustomerName(job.customerId) || 'Shop';
  const title = job.title || job.name || '—';
  const pill = (k, v) => `<div style="flex:1;min-width:0;background:rgba(255,255,255,0.07);border-radius:12px;padding:10px 12px;text-align:center;">
            <div style="font-size:0.6rem;text-transform:uppercase;letter-spacing:0.5px;color:#475569;margin-bottom:3px;">${k}</div>
            <div style="font-size:0.85rem;font-weight:600;color:#e2e8f0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(v)}</div>
          </div>`;
  let times = '';
  let below = '';
  if (st.kind === 'pre') {
    const t = jobDay?.startTime || job.defaultStart || job.startTime || '';
    times = `<div style="text-align:center;"><div style="${lbl}">Scheduled</div><div style="font-size:1.1rem;font-weight:700;color:#e2e8f0;">${escapeHtml(t ? formatTime(t) : 'All day')}</div></div>
          <div style="flex:0 0 auto;display:flex;flex-direction:column;align-items:center;gap:4px;">${rule}<div style="font-size:1.3rem;font-weight:800;color:#94a3b8;line-height:1.1;">Not started</div>${rule}</div>
          <div style="text-align:center;visibility:hidden;" aria-hidden="true"><div style="${lbl}">Out</div><div style="font-size:1.1rem;font-weight:700;">—</div></div>`;
  } else if (st.kind === 'other') {
    const e = st.entry;
    const ci = e.clockIn?.toDate?.() || (e.clockIn ? new Date(e.clockIn) : null);
    const since = ci && !isNaN(ci.getTime()) ? ` · since ${_jbJsTimeStr(ci.getTime())}` : '';
    const where = e.jobTitle || e.customerName || 'another job';
    times = `<div style="flex:1;display:flex;flex-direction:column;align-items:center;gap:4px;">${rule}<div style="font-size:1.25rem;font-weight:800;color:#fbbf24;line-height:1.1;">On another job</div>
          <div style="font-size:0.8rem;color:#94a3b8;font-weight:500;text-align:center;">${escapeHtml(where)}${since}</div>${rule}</div>`;
  } else if (st.kind === 'finished') {
    const f = st.fin;
    times = `<div style="text-align:center;"><div style="${lbl}">In</div><div style="font-size:1.4rem;font-weight:700;color:#34d399;">${_jbJsTimeStr(f.inMs)}</div></div>
          <div style="flex:0 0 auto;display:flex;flex-direction:column;align-items:center;gap:2px;">${rule}<div style="font-size:1.8rem;font-weight:800;color:#fff;line-height:1;">${f.hours != null ? escapeHtml(String(f.hours)) : '—'}</div><div style="font-size:0.8rem;color:#94a3b8;font-weight:500;">hours · finished</div>${rule}</div>
          <div style="text-align:center;"><div style="${lbl}">Out</div><div style="font-size:1.4rem;font-weight:700;color:#f87171;">${f.outMs ? _jbJsTimeStr(f.outMs) : '—'}</div></div>`;
    const msg = f.unacked
      ? 'No signal — clocked out on this phone. Keep the app open; it sends when you get bars.'
      : (f.ticketLineFailed ? 'Clocked out — dispatch will finish the ticket.' : 'Ticket sent to the office · read-only');
    const color = (f.unacked || f.ticketLineFailed) ? '#fbbf24' : '#34d399';
    const icon = (f.unacked || f.ticketLineFailed) ? 'info' : 'check_circle';
    // COD (Domain, FLSA): a driver who comes BACK to this job after lunch / a
    // breakdown / a reload wait must be able to get on the clock again from
    // here — the finished state is read-only, never a lock-out. Same action as
    // the "Not started" bar (clockInFromBoard), so no new write path.
    const again = isTicketOnlyMode() ? 'Back on this job? Start Job again' : 'Back on this job? Clock In again';
    below = `<div style="text-align:center;margin-top:10px;"><span id="jb-js-finished-line" style="font-size:0.8rem;color:${color};display:inline-flex;gap:4px;align-items:center;"><span class="material-icons" style="font-size:16px;">${icon}</span>${msg}</span></div>
        <div style="text-align:center;margin-top:6px;"><button type="button" id="jb-js-restart" onclick="window.JobBoardModule._jbJsStartTap(this)" style="background:none;border:none;color:#60a5fa;font-size:0.8rem;font-weight:600;cursor:pointer;padding:8px 10px;min-height:44px;display:inline-flex;align-items:center;gap:4px;"><span class="material-icons" style="font-size:1rem;">replay</span> <span class="jb-detail-clock-label">${again}</span></button></div>`;
  }
  if (st.kind === 'other') {
    below = `<div style="text-align:center;margin-top:8px;"><button type="button" onclick="window.JobBoardModule._jbJsSwitchHere()" style="background:none;border:none;color:#60a5fa;font-size:0.85rem;font-weight:600;cursor:pointer;padding:6px 8px;display:inline-flex;align-items:center;gap:4px;"><span class="material-icons" style="font-size:1rem;">swap_horiz</span> Switch Jobs to ${escapeHtml(title)}</button></div>`;
  }
  return `
      <div id="jb-js-hero" data-js-kind="${st.kind}" style="background:linear-gradient(135deg, #1e293b 0%, #0f172a 100%);padding:24px 20px 28px;border-radius:0 0 24px 24px;margin-bottom:20px;position:relative;">
        <button type="button" onclick="window.JobBoardModule.closeJobDetails()" aria-label="Close" style="position:absolute;top:12px;right:12px;background:rgba(255,255,255,0.1);border:none;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;cursor:pointer;z-index:10;">
          <span class="material-icons" style="font-size:20px;color:#94a3b8;">close</span>
        </button>
        <div style="display:flex;align-items:center;justify-content:center;gap:12px;margin-bottom:16px;">
          ${times}
        </div>
        <div style="display:flex;gap:8px;">
          ${pill('Employee', first)}
          ${pill('Customer', cust)}
          ${pill('Job', title)}
        </div>
        ${below}
      </div>`;
}

function _jbJsBarHtml(st, job) {
  // A "Start again" from the finished state that failed still needs its Retry.
  if (st.kind === 'finished' && !(_jbJsCinFor(job.id) && _jbJsCinFor(job.id).phase === 'failed')) return '';
  const wrap = (inner) => `
      <div style="position:fixed;bottom:0;left:0;right:0;background:#fff;border-top:1px solid #e2e8f0;padding:12px 16px;padding-bottom:max(12px, env(safe-area-inset-bottom));z-index:100;box-shadow:0 -4px 20px rgba(0,0,0,0.08);">
        <div style="max-width:480px;margin:0 auto;display:flex;gap:10px;">${inner}</div>
      </div>`;
  const btn = 'flex:1;min-height:52px;padding:13px;font-size:1rem;font-weight:700;border:none;border-radius:12px;color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;';
  if (st.kind === 'other') {
    return wrap(`<button type="button" id="jb-js-primary" onclick="window.JobBoardModule._jbJsSwitchHere()" style="${btn}background:linear-gradient(135deg, #3b82f6, #2563eb);">
          <span class="material-icons" style="font-size:20px;">swap_horiz</span> Switch Jobs to ${escapeHtml(job.title || job.name || 'this job')}</button>`);
  }
  const startMode = isTicketOnlyMode();
  const pending = _jbDetailClockInPending;
  // TASK-9694 polish — clock-in feedback (rulebook #7). The button locks AT the
  // tap and shows the captured time; after ~3s it says the time is kept while
  // GPS settles. A failed write shows Retry (same tap time, same entry id).
  const cin = _jbJsCinFor(job.id);
  if (cin && cin.phase === 'failed') {
    return wrap(`<div style="flex:1;display:flex;flex-direction:column;gap:8px;">
          <div role="alert" style="font-size:0.88rem;font-weight:700;color:#991b1b;text-align:center;"><span>Clock-in did not save.</span> <span>Your time</span> <span>${escapeHtml(_jbJsTimeStr(cin.tapMs))}</span> <span>is kept.</span></div>
          <button type="button" id="jb-js-primary" onclick="window.JobBoardModule._jbJsCinRetry()" style="${btn}background:#b91c1c;">
          <span class="material-icons" style="font-size:20px;">refresh</span> <span>Try again</span></button></div>`);
  }
  // acked/queued but the snapshot has not flipped the screen yet: stay locked
  // (bounded — a stale attempt never locks a later day's button).
  const _landing = cin && (cin.phase === 'acked' || cin.phase === 'queued') && Date.now() - cin.tapMs < 30000;
  if (cin && (_JB_CIN_BUSY[cin.phase] || _landing)) {
    return wrap(`<button type="button" id="jb-js-primary" disabled aria-busy="true" style="${btn}background:#15803d;opacity:1;cursor:default;flex-direction:column;gap:2px;">
          <span style="display:flex;align-items:center;gap:10px;"><span class="jb-js-spin" aria-hidden="true"></span><span class="jb-detail-clock-label">Clocking in…</span> <span>${escapeHtml(_jbJsTimeStr(cin.tapMs))}</span></span>
          ${cin.slow ? '<span class="jb-js-sub">Getting GPS — your time is saved</span>' : ''}</button>`);
  }
  // TASK-9865 — same advisory crew line as the classic modal, above the bar's
  // Clock In / Start Job. Judged against the day the screen was opened from.
  const _modal = document.getElementById('jobboard-detail-modal');
  const _day = _modal?.dataset?.jobDayId ? (JobBoardState.jobDays || []).find(jd => jd.id === _modal.dataset.jobDayId) || null : null;
  const _crew = _jbCrewWarnForJobModal(job, _day);
  const _primary = `<button type="button" id="jb-js-primary" ${pending ? 'disabled' : ''} onclick="window.JobBoardModule._jbJsStartTap(this)" style="${btn}background:#16a34a;">
          <span class="material-icons" style="font-size:20px;">${startMode ? 'play_arrow' : 'login'}</span> <span class="jb-detail-clock-label">${pending ? (startMode ? 'Starting…' : 'Clocking in…') : (startMode ? 'Start Job' : 'Clock In')}</span></button>`;
  if (_crew) {
    return wrap(`<div style="flex:1;display:flex;flex-direction:column;gap:8px;"><div id="jb-js-crew-warn">${_crew.html}</div>${_primary}</div>`);
  }
  return wrap(_primary);
}

// TASK-9694 polish — clock-in feedback state for the job screen (switch ON).
// ONE attempt at a time: { jobId, jobDayId, tapMs, phase, entryId, clockInMs,
// slow }. Phases: tapping → gps → acked | queued | saving | failed | refused.
let _jbJsCin = null;
const _JB_CIN_BUSY = { tapping: 1, gps: 1, saving: 1 };
function _jbJsCinFor(jobId) { return (_jbJsCin && _jbJsCin.jobId === jobId) ? _jbJsCin : null; }

function _jbJsMakeFb(st) {
  return {
    tapMs: st.tapMs,
    get entryId() { return st.entryId; },
    onPhase: (phase, info) => {
      if (_jbJsCin !== st) return; // a newer attempt owns the screen
      st.phase = phase;
      if (info && info.entryId) st.entryId = info.entryId;
      if (info && Number.isFinite(info.clockInMs)) st.clockInMs = info.clockInMs;
      if (phase === 'refused') _jbJsCin = null;
      _jbJsCinRepaint();
    },
  };
}

function _jbJsCinArmSlow(st) {
  setTimeout(() => {
    if (_jbJsCin !== st || !_JB_CIN_BUSY[st.phase]) return;
    st.slow = true;
    _jbJsCinRepaint();
  }, 3000);
}

// Repaint whatever shows the attempt: the pre-state bar, the finished state's
// "Start again" button, and the status line in the inline ticket header.
function _jbJsCinRepaint() {
  const modal = document.getElementById('jobboard-detail-modal');
  if (!modal || modal.dataset.jobscreen !== '1') return;
  const job = (JobBoardState.jobs || []).find(j => j.id === modal.dataset.jobId);
  if (!job) return;
  const st = _jbJsState(job);
  const bar = modal.querySelector('#jb-js-bar');
  if (bar && (st.kind === 'pre' || st.kind === 'finished')) bar.innerHTML = _jbJsBarHtml(st, job);
  const again = document.getElementById('jb-js-restart');
  const cin = _jbJsCinFor(job.id);
  if (again && cin && _JB_CIN_BUSY[cin.phase]) {
    again.disabled = true;
    const l = again.querySelector('.jb-detail-clock-label');
    if (l) l.textContent = 'Clocking in…';
  }
  _jbJsCinPaintStatus();
}

// The persistent line under IN / hours / OUT on the inline ticket — readable in
// sun glare, never a fading toast, and never a claim the server has not made.
function _jbJsCinPaintStatus() {
  const slot = document.getElementById('jb-js-cin-status');
  if (!slot) return;
  const ed = document.getElementById('clockout-modal');
  const cin = _jbJsCin;
  if (!cin || !ed || ed.dataset.entryId !== cin.entryId) { slot.innerHTML = ''; return; }
  const t = escapeHtml(_jbJsTimeStr(Number.isFinite(cin.clockInMs) ? cin.clockInMs : cin.tapMs));
  if (cin.phase === 'acked') {
    slot.innerHTML = `<div class="jb-js-cin jb-js-cin-ok" data-cin="acked"><span>✓ Clocked in at</span> <span>${t}</span></div>`;
  } else if (cin.phase === 'queued') {
    slot.innerHTML = `<div class="jb-js-cin jb-js-cin-wait" data-cin="queued"><span>Saved on this phone — will send when you have signal</span><span class="jb-js-sub"><span>Clock-in</span> <span>${t}</span></span></div>`;
  } else if (cin.phase === 'saving' || _JB_CIN_BUSY[cin.phase]) {
    slot.innerHTML = `<div class="jb-js-cin jb-js-cin-busy" data-cin="saving" style="display:flex;align-items:center;justify-content:center;gap:8px;"><span class="jb-js-spin" aria-hidden="true"></span><span>Still saving your clock-in…</span></div>`;
  } else {
    slot.innerHTML = '';
  }
}

// Clock In / Start Job — the SAME action the job modal's Clock In button runs
// (clockInFromBoard → _jbFinalizeClockIn: double-punch guard, equipment sheet,
// GPS + geofence). The modal stays open; the snapshot flips it to the ticket.
// TASK-9694 polish — the TAP time is captured here, before the guard read and
// the GPS wait, and the button locks in the same instant (no double tap).
function _jbJsStartTap(btn) {
  const tapMs = Date.now();
  const modal = document.getElementById('jobboard-detail-modal');
  if (!modal || modal.dataset.jobscreen !== '1') return;
  if (_jbDetailClockInPending) return;
  if (_jbJsCin && _JB_CIN_BUSY[_jbJsCin.phase]) return;
  const jobId = modal.dataset.jobId || '';
  const jobDayId = modal.dataset.jobDayId || '';
  const st = { jobId, jobDayId, tapMs, phase: 'tapping', entryId: null, clockInMs: null, slow: false };
  _jbJsCin = st;
  try { if (navigator.vibrate) navigator.vibrate(50); } catch (_) { /* no haptics */ }
  _jbJsCinRepaint();
  _jbJsCinArmSlow(st);
  return _jbDetailClockInTap(null, jobId, jobDayId, _jbJsMakeFb(st));
}

// Retry after a failed write: same tap time, same entry id — a write that did
// land is reconciled to (no duplicate), one that did not is written under that id.
function _jbJsCinRetry() {
  const st = _jbJsCin;
  if (!st || st.phase !== 'failed' || _jbDetailClockInPending) return;
  if (st.entryId && (JobBoardState.clockEntries || []).some(e => e.id === st.entryId && e.status === 'active')) {
    st.phase = 'queued';
    _jbJsCinRepaint();
    _jbJobScreenRefresh({ force: true });
    return;
  }
  st.phase = 'tapping';
  st.slow = false;
  _jbJsCinRepaint();
  _jbJsCinArmSlow(st);
  return _jbDetailClockInTap(null, st.jobId, st.jobDayId, _jbJsMakeFb(st));
}

// "On another job" → Switch Jobs to THIS job. Runs the existing switch sheet +
// server switchJob for the OPEN entry (job A): the equipment step stays, the
// "truck still clocked in under another operator" confirm stays. The note box
// (what you did on A) is seeded from the driver's OWN saved draft for entry A —
// read from the clock entry, never from this screen (job B's note always starts
// empty; first-person, one shift).
// The sheet + its job-screen wiring live in views/jobScreenSwitch.js, loaded on
// the tap (cold-load perf budget). jobboard.js hands in what it owns (no ?v=
// cross-import, Pitfall #1). A double tap just re-opens the same sheet
// (jbOpenSwitchJob replaces any open one), so no in-flight flag.
function _jbJsSwitchHere() {
  __ddChunkWrap(import('./jobScreenSwitch.js?v=9694401')).then((m) => m.switchHere({
    S: JobBoardState, personId: getActiveJobBoardPersonId, refresh: _jbJobScreenRefresh,
    open: (e) => { _jbSwitchFor = e; jbOpenSwitchJob(); },
    custChange: jbSwitchCustomerChange, jobChange: jbSwitchJobChange, custName: getCustomerName,
  })).catch((e) => {
    console.warn('[JobBoard] switch sheet did not load', e);
    window.showToast?.('Could not open Switch Jobs — check your signal and try again.', 'error');
  });
}

// After a switch that started on this screen, follow the driver to the new
// job's screen once the new shift reaches the board.
async function _jbJsFollowSwitch(newEntryId) {
  if (!newEntryId) return;
  await _jbWaitUntil(() => (JobBoardState.clockEntries || []).some(e => e.id === newEntryId), 15000);
  if (!document.getElementById('jobboard-detail-modal')) return; // driver closed it meanwhile
  if (!_jbOpenJobScreenForEntryId(newEntryId)) _jbJobScreenRefresh({ force: true });
}

function _jbJsUnmountEditor(ed) {
  _jbJsLastUnmounted = ed.dataset.entryId || null;
  _jbJsParkJobInfo();
  ed.remove();
  setTimeout(() => _jbJobScreenRefresh({ force: true }), 0);
}

// The inline header's OUT / hours follow the clock while the ticket is open (the
// classic modal froze them at open, which was fine for a sheet opened at the end
// of a shift — this screen is open all day). Display only: the recorded clock-out
// is still the server time at Finish. Stops the moment the driver edits a time.
let _jbJsTickTimer = null;
function _jbJsTicker(on) {
  if (_jbJsTickTimer) { clearInterval(_jbJsTickTimer); _jbJsTickTimer = null; }
  if (!on) return;
  _jbJsTickTimer = setInterval(() => {
    const ed = document.getElementById('clockout-modal');
    if (!ed || ed.dataset.btInline !== '1') return;
    if (JobBoardState._btEditedClockOut || JobBoardState._btEditedClockIn) return;
    const cin = parseInt(ed.dataset.clockInMs || '0', 10);
    if (!cin) return;
    const now = new Date();
    const h = parseFloat(((now.getTime() - cin) / 3600000).toFixed(2));
    const hEl = document.getElementById('jb-clockout-hours');
    const oEl = document.getElementById('jb-clockout-out-time');
    if (hEl) hEl.textContent = String(h);
    if (oEl) oEl.textContent = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const edPanel = document.getElementById('jb-clockout-times-edit');
    const cout = document.getElementById('jb-clockout-edit-cout');
    if (cout && (!edPanel || edPanel.style.display === 'none')) cout.value = jbToLocalDT(now);
  }, 30000);
}

function _jbJsTeardown() {
  _jbJsTicker(false);
  _jbJsCloseSheet();
}

// Clock Out / Finish Job ASKS FIRST (every COD lane: gloved thumbs hit Clock Out
// meaning Save). The sheet only asks — the clock-out itself is the unchanged
// submitClockOut, which stamps the server time when "Yes" is tapped.
function _jbJsConfirmFinish() {
  const ed = document.getElementById('clockout-modal');
  if (!ed || ed.dataset.btInline !== '1') return submitClockOut();
  if (document.getElementById('btn-clockout-submit')?.disabled) return;
  _jbJsCloseSheet();
  const startMode = isTicketOnlyMode();
  const now = JobBoardState._btEditedClockOut || new Date();
  const cin = JobBoardState._btEditedClockIn || new Date(parseInt(ed.dataset.clockInMs || '0', 10));
  const hrs = parseFloat(((now - cin) / 3600000).toFixed(2));
  const tStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const jobLbl = escapeHtml(JobBoardState._btEntry?.jobTitle || JobBoardState._btJob?.title || JobBoardState._btJob?.name || 'this job');
  const noteEl = document.getElementById('jb-shift-notes');
  const needNote = window.AppState?.settings?.requireShiftNotes === true && !!noteEl && !noteEl.value.trim();
  const when = JobBoardState._btEditedClockOut ? `at ${tStr}` : `now (${tStr})`;
  const body = startMode
    ? `Stops the hours on <b>${jobLbl}</b> ${when} — ${hrs} hrs — and sends the ticket to the office.`
    : `Ends your shift ${when} — ${hrs} hrs — and sends the ticket to the office.`;
  const sheet = document.createElement('div');
  sheet.id = 'jb-js-finish-sheet';
  sheet.className = 'modal';
  sheet.style.cssText = 'display:flex;align-items:flex-end;justify-content:center;background:rgba(15,23,42,0.5);z-index:10060;';
  sheet.innerHTML = `
    <div role="dialog" aria-modal="true" aria-labelledby="jb-js-finish-title" style="background:#fff;border-radius:20px 20px 0 0;padding:20px 16px;padding-bottom:max(18px, env(safe-area-inset-bottom));width:100%;max-width:480px;box-sizing:border-box;">
      <h3 id="jb-js-finish-title" style="margin:0 0 6px;font-size:1.05rem;color:#0f172a;">${startMode ? 'Finish this job?' : 'Clock out now?'}</h3>
      <p style="margin:0 0 14px;font-size:0.88rem;color:#334155;line-height:1.45;">${body} To keep working, tap <b>Not yet</b>. Save keeps your notes without stopping the clock.</p>
      ${needNote ? `<label for="jb-js-sheet-note" style="display:block;font-size:0.8rem;font-weight:600;color:#334155;margin-bottom:6px;">Shift note — what did you do today? <span style="font-weight:400;">(required)</span></label>
      <textarea id="jb-js-sheet-note" rows="3" style="width:100%;box-sizing:border-box;padding:10px;font-size:0.95rem;border:1px solid #fbbf24;border-radius:10px;background:#fffbeb;color:#0f172a;margin-bottom:6px;"></textarea>
      <div id="jb-js-sheet-err" style="display:none;font-size:0.8rem;color:#b91c1c;margin-bottom:8px;"></div>` : ''}
      <div style="display:flex;gap:8px;">
        <button type="button" onclick="window.JobBoardModule._jbJsCloseSheet()" style="flex:1;min-height:52px;font-size:0.95rem;font-weight:700;border:none;border-radius:12px;background:#f1f5f9;color:#334155;cursor:pointer;">Not yet</button>
        <button type="button" id="jb-js-finish-yes" onclick="window.JobBoardModule._jbJsFinishYes()" style="flex:1;min-height:52px;font-size:0.95rem;font-weight:700;border:none;border-radius:12px;background:linear-gradient(135deg, #dc2626, #991b1b);color:#fff;cursor:pointer;">${startMode ? 'Finish Job' : 'Clock Out'}</button>
      </div>
    </div>`;
  sheet.addEventListener('click', (e) => { if (e.target === sheet) _jbJsCloseSheet(); });
  document.body.appendChild(sheet);
}

function _jbJsCloseSheet() {
  document.getElementById('jb-js-finish-sheet')?.remove();
}

function _jbJsFinishYes() {
  const sheetNote = document.getElementById('jb-js-sheet-note');
  if (sheetNote) {
    const v = sheetNote.value.trim();
    if (!v) {
      const err = document.getElementById('jb-js-sheet-err');
      if (err) { err.textContent = 'Add a shift note first — what did you do today?'; err.style.display = 'block'; }
      sheetNote.focus();
      return;
    }
    const noteEl = document.getElementById('jb-shift-notes');
    if (noteEl) noteEl.value = v;
  }
  _jbJsCloseSheet();
  _jbJsClosingEntry = document.getElementById('clockout-modal')?.dataset?.entryId || null;
  return Promise.resolve(submitClockOut()).finally(() => {
    _jbJsClosingEntry = null;
    _jbJobScreenRefresh({ force: true });
  });
}

// Called by submitClockOut AFTER the clock-out write succeeded (inline only).
// Records the finished state for this screen, then — S3 — runs the explicit
// finalize through the Admin-SDK gateway (op finishTicket: person + tenant from
// the session, entry ownership checked server-side). The finalize never runs
// before, and never changes, the recorded clock-out.
function _jbJsAfterFinish(info) {
  const pid = getActiveJobBoardPersonId();
  JobBoardState._jsFinished = JobBoardState._jsFinished || {};
  JobBoardState._jsFinished[info.jobId + '|' + pid] = {
    entryId: info.entryId, inMs: info.clockInMs, outMs: Date.now(), hours: info.hours,
    ticketLineFailed: !!info.ticketLineFailed, unacked: !!info.unacked, dayKey: _jbJsTodayKey(),
  };
  const run = () => _jbRunFinishTicketOp(info.entryId).catch((e) => {
    console.warn('[JobBoard] finishTicket finalize failed (clock-out already recorded):', e?.code || e?.message || e);
    window.logError?.(e, { module: 'jobboard', operation: 'FINISH_TICKET_FINALIZE' });
  });
  if (info.unacked || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
    // No signal: the clock-out is queued on this phone. Finalize once we're back.
    window.addEventListener('online', () => setTimeout(run, 4000), { once: true });
  } else {
    run();
  }
}

async function _jbRunFinishTicketOp(entryId) {
  if (!entryId) return null;
  const payload = { entryId };
  const scope = 'finishTicket:' + entryId;
  const fn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: GW_CLIENT_TIMEOUT_MS });
  let res;
  try {
    res = await fn({ op: 'finishTicket', tenantId: JobBoardState.tenantId || window.AppState?.tenant, payload, opKey: _gwOpKey(scope, payload) });
  } catch (e) {
    // The clock-out write may still be landing — one retry after a beat.
    if (/failed-precondition/i.test(String(e?.code || ''))) {
      await new Promise(r => setTimeout(r, 5000));
      res = await fn({ op: 'finishTicket', tenantId: JobBoardState.tenantId || window.AppState?.tenant, payload, opKey: _gwOpKey(scope, payload) });
    } else {
      throw e;
    }
  }
  _gwOpDone(scope);
  return res?.data || null;
}

// Finished-today lookup for a screen opened AFTER the shift ended (another tab,
// a reopen). One read of the viewer's OWN completed entries on this job — the
// query the clockEntries read rule allows a PIN session. Failure = "Not started".
async function _jbJsFetchFinished(job) {
  const pid = getActiveJobBoardPersonId();
  const tid = JobBoardState.tenantId || window.AppState?.tenant;
  if (!pid || !tid || !job?.id) return;
  const key = job.id + '|' + pid;
  if ((JobBoardState._jsFinished || {})[key]) return;
  try {
    const snap = await getDocs(query(collection(db, 'tenants', tid, 'clockEntries'),
      where('personId', '==', pid), where('jobId', '==', job.id), where('status', '==', 'completed')));
    const today = _jbJsTodayKey();
    const ms = (v) => (v?.toMillis ? v.toMillis() : (v ? new Date(v).getTime() : 0));
    let best = null;
    snap.forEach((d) => {
      const e = d.data() || {};
      const inMs = ms(e.clockIn);
      if (!inMs || formatDateISO(new Date(inMs)) !== today) return;
      const outMs = ms(e.clockOut);
      if (!best || outMs > best.outMs) best = { entryId: d.id, inMs, outMs, hours: e.totalHours != null ? e.totalHours : e.hours, dayKey: today, ticketLineFailed: false, unacked: false };
    });
    if (!best) return;
    JobBoardState._jsFinished = JobBoardState._jsFinished || {};
    if (!JobBoardState._jsFinished[key]) JobBoardState._jsFinished[key] = best;
    _jbJobScreenRefresh();
  } catch (e) {
    console.warn('[JobBoard] finished-today lookup skipped:', e?.code || e?.message || e);
  }
}

// Open (or refresh) the job screen for an open clock entry. false = not
// eligible (job off the live board, switch OFF, …) → caller uses the classic
// Build Ticket.
function _jbOpenJobScreenForEntryId(entryId) {
  const e = (JobBoardState.clockEntries || []).find(x => x.id === entryId);
  if (!e || !e.jobId) return false;
  const job = (JobBoardState.jobs || []).find(j => j.id === e.jobId);
  if (!job) return false;
  const jd = (e.jobDayId && (JobBoardState.jobDays || []).find(d => d.id === e.jobDayId)) || _jbResolveJobDayForEntry(e) || null;
  if (!_jbUseJobScreen(job, jd)) return false;
  const open = document.getElementById('jobboard-detail-modal');
  if (open && open.dataset.jobscreen === '1' && open.dataset.jobId === job.id) { _jbJobScreenRefresh(); return true; }
  if (open) closeJobDetails();
  showJobDetails(job.id, jd?.id || '');
  return true;
}

// Fix it (repoint) from the inline ticket: same job → re-mount here; a
// different job → that job's screen (or the classic Build Ticket off-board).
function _jbJsAfterRepoint(ed, entryId, newJobId) {
  const screen = document.getElementById('jobboard-detail-modal');
  _jbJsParkJobInfo();
  ed.remove();
  jbResetBuildTicketState();
  if (screen && screen.dataset.jobscreen === '1' && newJobId && screen.dataset.jobId === newJobId) {
    _jbJobScreenRefresh({ force: true, remount: true });
    return;
  }
  closeJobDetails();
  if (!_jbOpenJobScreenForEntryId(entryId)) clockOutFromBoard(entryId, newJobId || null, null, { shiftChosen: true });
}

function showVehicleInfo(truckRef) {
  const truck = JobBoardState.trucks.find(t => t.id === String(truckRef) || String(t.id) === String(truckRef) || t.unit === truckRef || t.name === truckRef);
  if (!truck) { window.showToast?.('Vehicle not found', 'error'); return; }

  const unit = window.getUnitDisplay ? window.getUnitDisplay(truck) : (truck.unit || truck.name || '?');
  // TASK-9855 — vehicle docs open through the Storage SDK on their path. A
  // driver session is denied truck-docs by storage.rules, so the
  // 'driverVehicle' door (job-day-scoped callable) serves them instead.
  const _vDoor = { door: 'driverVehicle', doorArgs: { vtype: 'truck', vid: String(truck.id) } };
  const buildDocList = (docs, fallback) => {
    if (!docs || docs.length === 0) return `<p style="color:var(--text-muted);font-size:0.85rem;">${fallback}</p>`;
    return docs.map(d => `<a ${window.StorageFile.linkAttrs(d, _vDoor)} style="display:flex;align-items:center;gap:8px;padding:8px;background:var(--bg-elevated);border-radius:var(--radius-sm);text-decoration:none;color:var(--text-primary);font-size:0.85rem;margin-bottom:4px;">
      <span class="material-icons" style="font-size:18px;">${d.name?.endsWith('.pdf') ? 'picture_as_pdf' : 'image'}</span>
      <span style="flex:1;">${escapeHtml(d.name || 'Document')}</span>
      <span class="material-icons" style="font-size:16px;color:var(--text-muted);">open_in_new</span>
    </a>`).join('');
  };

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'jobboard-vehicle-modal';
  modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
  modal.innerHTML = `
    <div class="modal-content" style="max-width:450px;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;">local_shipping</span> Vehicle - ${escapeHtml(unit)}</h3>
        <button class="modal-close" onclick="document.getElementById('jobboard-vehicle-modal').remove()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;">
        ${truck.photoUrl ? `<img src="${escapeHtml(String(truck.photoUrl))}" alt="${escapeHtml(unit)}" loading="lazy" style="width:100%;aspect-ratio:4/3;object-fit:cover;border-radius:10px;background:#0F172A;display:block;margin:0 0 14px;">` : ''}
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:16px;">
          <div><span style="font-size:0.75rem;color:var(--text-muted);">Year/Make</span><br><strong>${escapeHtml(String(truck.year || ''))} ${escapeHtml(truck.make || 'N/A')}</strong></div>
          <div><span style="font-size:0.75rem;color:var(--text-muted);">Model</span><br><strong>${escapeHtml(truck.model || 'N/A')}</strong></div>
          <div><span style="font-size:0.75rem;color:var(--text-muted);">VIN</span><br><strong style="font-size:0.85rem;">${escapeHtml(truck.vin || 'N/A')}</strong></div>
          <div><span style="font-size:0.75rem;color:var(--text-muted);">License</span><br><strong>${escapeHtml(truck.licensePlate || 'N/A')} ${escapeHtml(truck.licensePlateState)}</strong></div>
        </div>
        <div style="margin-bottom:12px;">
          <h4 style="font-size:0.9rem;margin:0 0 8px;display:flex;align-items:center;gap:6px;"><span class="material-icons" style="font-size:18px;">verified</span> DOT Inspection</h4>
          ${truck.inspectionExpiry ? `<p style="font-size:0.8rem;color:var(--text-secondary);margin:0 0 6px;">Expires: <strong>${escapeHtml(String(truck.inspectionExpiry))}</strong></p>` : ''}
          ${buildDocList(truck.inspectionDocs, 'No documents uploaded')}
        </div>
        <div style="margin-bottom:12px;">
          <h4 style="font-size:0.9rem;margin:0 0 8px;display:flex;align-items:center;gap:6px;"><span class="material-icons" style="font-size:18px;">description</span> Registration</h4>
          ${buildDocList(truck.registrationDocs, 'No documents uploaded')}
        </div>
        <div>
          <h4 style="font-size:0.9rem;margin:0 0 8px;display:flex;align-items:center;gap:6px;"><span class="material-icons" style="font-size:18px;">shield</span> Insurance</h4>
          ${buildDocList(truck.insuranceDocs, 'No documents uploaded')}
        </div>
        ${truck.ownership && truck.ownership !== 'owned' ? `
        <div style="margin-top:12px;padding-top:12px;border-top:1px solid var(--border);">
          <h4 style="font-size:0.9rem;margin:0 0 8px;display:flex;align-items:center;gap:6px;">
            <span class="material-icons" style="font-size:18px;">gavel</span> Lease Statement (Part 376)
            <span style="font-size:0.7rem;color:var(--text-muted);font-weight:normal;">Carry for roadside DOT</span>
          </h4>
          ${(truck.leaseStatementPdfPath || (truck.leaseStatementPdfUrl && /^https?:\/\//i.test(String(truck.leaseStatementPdfUrl)))) ? `<a ${window.StorageFile.linkAttrs(truck, Object.assign({ urlKey: 'leaseStatementPdfUrl' }, _vDoor))} style="display:flex;align-items:center;gap:10px;padding:14px;background:var(--bg-elevated);border-radius:var(--radius-sm);text-decoration:none;color:var(--text-primary);font-size:0.95rem;border:1px solid var(--border);min-height:48px;">
            <span class="material-icons" style="font-size:24px;color:var(--accent);">picture_as_pdf</span>
            <span style="flex:1;font-weight:600;">View &sect;376.12 Statement</span>
            <span class="material-icons" style="font-size:20px;color:var(--text-muted);">open_in_new</span>
          </a>` : '<p style="margin:0;font-size:0.8rem;color:var(--text-muted);">Statement not generated yet. Ask dispatch to generate one in Roster &rarr; this vehicle &rarr; Ownership &amp; Lease.</p>'}
          ${truck.leaseDocs && truck.leaseDocs.length ? `
          <div style="margin-top:8px;">
            <p style="font-size:0.75rem;color:var(--text-muted);margin:0 0 4px;">Underlying lease document(s):</p>
            ${buildDocList(truck.leaseDocs, '')}
          </div>` : ''}
        </div>` : ''}
        ${(truck.documents || []).filter(d => d.showOnJobBoard).length > 0 ? (truck.documents || []).filter(d => d.showOnJobBoard).map(d => `
        <div style="margin-top:12px;">
          <h4 style="font-size:0.9rem;margin:0 0 8px;display:flex;align-items:center;gap:6px;">
            <span class="material-icons" style="font-size:18px;">folder_open</span> ${escapeHtml(d.label || d.type || 'Document')}
            ${d.expiresAt ? `<span style="font-size:0.75rem;color:var(--text-muted);font-weight:normal;">Exp: ${escapeHtml(String(d.expiresAt))}</span>` : ''}
          </h4>
          <a ${window.StorageFile.linkAttrs(d, _vDoor)} style="display:flex;align-items:center;gap:8px;padding:8px;background:var(--bg-elevated);border-radius:var(--radius-sm);text-decoration:none;color:var(--text-primary);font-size:0.85rem;">
            <span class="material-icons" style="font-size:18px;">${d.name?.endsWith('.pdf') ? 'picture_as_pdf' : 'image'}</span>
            <span style="flex:1;">${escapeHtml(d.name || 'Document')}</span>
            <span class="material-icons" style="font-size:16px;color:var(--text-muted);">open_in_new</span>
          </a>
        </div>
        `).join('') : ''}
        ${truck.inventoryEnabled ? `
        <div style="margin-top:16px;padding-top:16px;border-top:1px solid var(--border);">
          <button class="btn btn-primary" style="width:100%;display:flex;align-items:center;justify-content:center;gap:8px;" onclick="document.getElementById('jobboard-vehicle-modal').remove(); window.JobBoardModule.showDriverInventory('${escJsAttr(String(truck.id))}')">
            <span class="material-icons">inventory_2</span> Submit Inventory / Log Purchase
          </button>
        </div>
        ` : ''}
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

// ============================================
// DRIVER-PIN INVENTORY (TASK-197 Phase 3)
// ============================================
// Slim driver-mode inventory modal. Two tabs: Submit Count + Add Purchase.
// All writes go through Cloud Functions which validate the driver token,
// server-stamp identity, and clamp inputs. Submitter is locked — driver is
// known via PIN/access token.

function showDriverInventory(vehicleIdOrTruck) {
  // Accept either an ID (jobboard caller) or a hydrated truck object (dispatch caller)
  let truck = null;
  let vehicleId = null;
  if (vehicleIdOrTruck && typeof vehicleIdOrTruck === 'object') {
    truck = vehicleIdOrTruck;
    vehicleId = truck.id;
  } else {
    vehicleId = vehicleIdOrTruck;
    truck = (JobBoardState.trucks || []).find(t => t.id === vehicleId)
      || (JobBoardState.trailers || []).find(t => t.id === vehicleId);
  }
  if (!truck) { window.showToast?.('Vehicle not found', 'error'); return; }
  if (!truck.inventoryEnabled) { window.showToast?.('Inventory not enabled', 'error'); return; }
  const items = Array.isArray(truck.inventoryItems) ? truck.inventoryItems : [];
  // TASK-355 2026-05-22: PIN -> SPA member name fallback.
  const driverName = getActiveJobBoardPersonName() || 'Driver';
  const unit = window.getUnitDisplay ? window.getUnitDisplay(truck) : (truck.unit || truck.name || '?');

  const existing = document.getElementById('jb-driver-inv-modal');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'jb-driver-inv-modal';
  modal.dataset.vehicleId = vehicleId;
  modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
  modal.innerHTML = `
    <div class="modal-content modal-fullscreen-mobile" style="max-width:560px;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;">inventory_2</span> Inventory · ${escapeHtml(unit)}</h3>
        <button class="modal-close" onclick="document.getElementById('jb-driver-inv-modal').remove()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="jb-driver-inv-tabs">
        <button class="jb-driver-inv-tab active" data-tab="count" onclick="window.JobBoardModule.switchDriverInventoryTab('count')">
          <span class="material-icons">checklist</span> Count
        </button>
        <button class="jb-driver-inv-tab" data-tab="purchase" onclick="window.JobBoardModule.switchDriverInventoryTab('purchase')">
          <span class="material-icons">shopping_cart</span> Log Purchase
        </button>
      </div>
      <div class="modal-body" style="padding:14px;">
        <div class="jb-driver-inv-driver-banner">
          <span class="material-icons">badge</span>
          Submitting as <strong>${escapeHtml(driverName)}</strong>
        </div>

        <!-- Count tab -->
        <div id="jb-driver-inv-tab-count" class="jb-driver-inv-tab-content">
          ${items.length === 0 ? `
            <div class="empty-state">
              <div class="empty-state-icon">📦</div>
              <div class="empty-state-title">No inventory items configured</div>
              <div class="empty-state-text">Ask your dispatcher to set up the parts list for this vehicle.</div>
            </div>
          ` : `
            <table class="inventory-table" style="width:100%;">
              <thead><tr><th>Item</th><th>Expected</th><th>Actual</th></tr></thead>
              <tbody>
                ${items.map(it => `
                  <tr data-item-id="${escapeHtml(it.id)}">
                    <td>${escapeHtml(it.name || 'Unknown')}</td>
                    <td>${it.expectedQty || 0} ${escapeHtml(it.unit || 'ea')}</td>
                    <td><input type="number" class="jb-driver-inv-actual" data-item-id="${escapeHtml(it.id)}" min="0" step="1" value="${it.expectedQty || 0}" inputmode="numeric"></td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            <div class="form-group" style="margin-top:12px;">
              <label for="jb-driver-inv-count-notes">Notes (optional)</label>
              <textarea id="jb-driver-inv-count-notes" rows="2" placeholder="Anything we should know?"></textarea>
            </div>
            <button id="jb-driver-inv-submit-count" class="btn btn-primary btn-large" style="width:100%;margin-top:8px;" onclick="window.JobBoardModule.submitDriverInventoryCount()">
              <span class="material-icons">check_circle</span> Submit Count
            </button>
          `}
        </div>

        <!-- Purchase tab -->
        <div id="jb-driver-inv-tab-purchase" class="jb-driver-inv-tab-content hidden">
          <form id="jb-driver-inv-purchase-form" onsubmit="window.JobBoardModule.submitDriverInventoryPurchase(event)">
            <div class="form-group">
              <label for="jb-driver-inv-purchase-item">What did you buy? *</label>
              <select id="jb-driver-inv-purchase-item" required>
                <option value="">Select an item...</option>
                ${items.map(it => `<option value="${escapeHtml(it.id)}">${escapeHtml(it.name || 'Unknown')}</option>`).join('')}
              </select>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label for="jb-driver-inv-purchase-qty">Quantity *</label>
                <input type="number" id="jb-driver-inv-purchase-qty" min="1" step="1" required value="1" inputmode="numeric">
              </div>
              <div class="form-group">
                <label for="jb-driver-inv-purchase-cost">Unit Cost ($) *</label>
                <input type="number" id="jb-driver-inv-purchase-cost" min="0" step="0.01" required inputmode="decimal" oninput="window.JobBoardModule.updateDriverPurchaseTotal()">
              </div>
            </div>
            <div class="form-group">
              <label>Total</label>
              <div id="jb-driver-inv-purchase-total" style="font-size:1.2rem;font-weight:600;color:var(--brand-blue);">$0.00</div>
            </div>
            <div class="form-group">
              <label for="jb-driver-inv-purchase-receipt">Receipt photo (optional but helpful)</label>
              <input type="file" id="jb-driver-inv-purchase-receipt" accept="image/*,.pdf" style="display:none" onchange="window.JobBoardModule.handleDriverPurchaseReceipt(event)">
              <button type="button" class="btn btn-secondary" style="width:100%;display:flex;align-items:center;justify-content:center;gap:8px;" onclick="document.getElementById('jb-driver-inv-purchase-receipt').click()">
                <span class="material-icons">camera_alt</span>
                <span id="jb-driver-inv-purchase-receipt-label">Take photo / Upload</span>
              </button>
            </div>
            <div class="form-group">
              <label for="jb-driver-inv-purchase-notes">Notes (optional)</label>
              <textarea id="jb-driver-inv-purchase-notes" rows="2" placeholder="Anything we should know?"></textarea>
            </div>
            <button type="submit" id="jb-driver-inv-submit-purchase" class="btn btn-primary btn-large" style="width:100%;margin-top:8px;">
              <span class="material-icons">check_circle</span> Submit Purchase
            </button>
            <div class="jb-driver-inv-pending-note">Purchases go to your dispatcher for review before being added to costs.</div>
          </form>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  // Reset transient state
  JobBoardState.driverInventoryReceipt = null;
}

function switchDriverInventoryTab(tab) {
  document.querySelectorAll('.jb-driver-inv-tab').forEach(b => {
    b.classList.toggle('active', b.dataset.tab === tab);
  });
  document.getElementById('jb-driver-inv-tab-count')?.classList.toggle('hidden', tab !== 'count');
  document.getElementById('jb-driver-inv-tab-purchase')?.classList.toggle('hidden', tab !== 'purchase');
}

function updateDriverPurchaseTotal() {
  const qty = parseFloat(document.getElementById('jb-driver-inv-purchase-qty')?.value) || 0;
  const cost = parseFloat(document.getElementById('jb-driver-inv-purchase-cost')?.value) || 0;
  const display = document.getElementById('jb-driver-inv-purchase-total');
  if (display) display.textContent = `$${(qty * cost).toFixed(2)}`;
}

async function handleDriverPurchaseReceipt(e) {
  const file = e?.target?.files?.[0];
  if (!file) return;
  const label = document.getElementById('jb-driver-inv-purchase-receipt-label');
  if (label) label.textContent = file.name.length > 28 ? file.name.slice(0, 25) + '…' : file.name;
  JobBoardState.driverInventoryReceipt = file;
}

async function _uploadDriverPurchaseReceipt(file) {
  if (!file) return { url: null, fileName: null };
  const tenantId = JobBoardState.tenantId;
  if (!tenantId) throw new Error('No tenant context');
  // TASK-9402 — was uploaded as picked under `file.type`: an iPhone HEIC went
  // up raw (unviewable off Safari) and a MIME-less file got an empty label.
  // Converted here, inside the locked Submit, so nothing can outrun it. A
  // failed conversion throws HeicConversionError; the caller submits the
  // purchase without the receipt and says why.
  const { file: storable, contentType } = await prepareUploadFile(file);
  file = storable;
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 60);
  const path = `tenants/${tenantId}/inventory-receipts/${Date.now()}_${safeName}`;
  // TASK-4831 — this was a lazy `await import(gstatic 10.7.1/firebase-storage.js)`,
  // a SECOND SDK instance. `storage` is built by firebase-config.js on 10.8.0, and
  // 10.7.1's ref() fails its `instanceof FirebaseStorageImpl` check on it, falls
  // through to the Reference branch and dereferences `_location`: measured as
  // `TypeError: Cannot read properties of undefined (reading 'path')`. Worse than
  // the Firestore variant, because the caller CATCHES this and submits the purchase
  // WITHOUT the receipt ("Receipt upload failed — submitting purchase without
  // receipt"), so it would have thrown away every driver receipt in silence.
  // ref/uploadBytes/getDownloadURL are already imported at the top of this file from
  // the same firebase-config.js instance as `storage`; the local was named `ref`,
  // which shadowed the correct import, so it is `fileRef` now.
  const fileRef = ref(storage, path);
  await uploadBytes(fileRef, file, { contentType });
  const url = await getDownloadURL(fileRef);
  return { url, fileName: file.name };
}

// TASK-4634 S4 — persistent in-modal failure banner. The transient toast alone
// was the whole "vanished submission" bug: a driver glancing away for two
// seconds saw a modal that looked exactly like it did before tapping Submit.
// Built with textContent (no innerHTML) so the reason string needs no escaping.
function _jbShowDriverInvError(reason) {
  try {
    const btn = document.getElementById('jb-driver-inv-submit-count');
    if (!btn) return;
    let box = document.getElementById('jb-driver-inv-error');
    if (!box) {
      box = document.createElement('div');
      box.id = 'jb-driver-inv-error';
      box.setAttribute('role', 'alert');
      box.style.cssText = 'margin-top:10px;padding:10px 12px;border-radius:8px;'
        + 'background:rgba(220,53,69,0.12);border:1px solid var(--status-danger, #dc3545);'
        + 'color:var(--status-danger, #dc3545);font-weight:600;font-size:0.9rem;'
        + 'display:flex;align-items:flex-start;gap:8px;';
      btn.insertAdjacentElement('beforebegin', box);
    }
    box.textContent = '';
    const icon = document.createElement('span');
    icon.className = 'material-icons';
    icon.style.fontSize = '20px';
    icon.textContent = 'error_outline';
    const txt = document.createElement('span');
    txt.textContent = `Count NOT saved — ${reason}. Fix and retry, or tell your dispatcher.`;
    box.append(icon, txt);
  } catch (_) { /* banner is best-effort — never block the retry path */ }
}

async function submitDriverInventoryCount() {
  const btn = document.getElementById('jb-driver-inv-submit-count');
  if (!btn || btn.disabled) return;
  btn.disabled = true;
  btn.innerHTML = '<span class="material-icons rotating">sync</span> Submitting...';

  const modal = document.getElementById('jb-driver-inv-modal');
  const vehicleId = modal?.dataset?.vehicleId;
  const token = window.AppState?.driverAccessToken;
  if (!token || !vehicleId) {
    // TASK-4634 S4 (COD) — this pre-flight failure is a real failed submit too:
    // same loud treatment as the callable path, or it reads as saved.
    const _reason = !token ? 'Session expired — log in again, your count was NOT saved'
      : 'Could not tell which vehicle this is — reopen the inventory and try again';
    window.showToast?.(`Count NOT saved — ${_reason}`, 'error');
    _jbShowDriverInvError(_reason);
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">error</span> Retry — Count Not Saved';
    return;
  }

  // Iterate by <tr> only — the inputs also carry data-item-id, so a bare
  // `[data-item-id]` selector double-counts every row.
  const rows = document.querySelectorAll('#jb-driver-inv-tab-count tr[data-item-id]');
  const items = [];
  rows.forEach(row => {
    const itemId = row.dataset.itemId;
    const actualQty = parseInt(row.querySelector('.jb-driver-inv-actual')?.value) || 0;
    items.push({ itemId, actualQty });
  });
  const notes = document.getElementById('jb-driver-inv-count-notes')?.value?.trim() || '';

  try {
    const callable = httpsCallable(functions, 'submitDriverInventoryCount');
    const result = await callable({ token, vehicleId, items, notes });
    if (result?.data?.ok) {
      window.showToast?.('Inventory count submitted', 'success');
      modal.remove();
    } else {
      const declined = new Error(result?.data?.error || 'Submission failed');
      declined._serverDeclined = true;
      throw declined;
    }
  } catch (error) {
    console.error('[JobBoard] submitDriverInventoryCount error:', error);
    const reason = error?.message || 'Error submitting count';
    // TASK-4634 S4 — a failed count used to vanish: transient generic toast,
    // nothing logged anywhere, and on weak signal the tab died before any
    // Firestore write could land. Queue a durable breadcrumb through the
    // TASK-4666 localStorage outbox (delivered now when the network allows,
    // else on the next job-board entry). Server-DECLINED submissions
    // (ok:false) are also logged server-side; the breadcrumb still matters
    // for failures the server never saw (network / callable exception).
    try {
      _jbQueueAbandonBreadcrumb({
        operation: 'INVENTORY_SUBMIT_FAILED',
        vehicleId,
        reason: String(reason).slice(0, 200),
        itemCount: items.length,
        serverDeclined: error?._serverDeclined === true,
      });
    } catch (_) { /* breadcrumb is best-effort */ }
    // Loud + durable in the UI: persistent in-modal banner with the real
    // reason, a reason-naming error toast (12s, dismissible), and a button
    // that flips to Retry — a repeat failure can never read as success.
    _jbShowDriverInvError(reason);
    window.showToast?.(`Count NOT saved — ${reason}`, 'error');
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">refresh</span> Retry — Count Not Saved';
  }
}

async function submitDriverInventoryPurchase(e) {
  e?.preventDefault?.();
  const btn = document.getElementById('jb-driver-inv-submit-purchase');
  if (!btn || btn.disabled) return;
  btn.disabled = true;
  const originalLabel = btn.innerHTML;
  btn.innerHTML = '<span class="material-icons rotating">sync</span> Submitting...';

  const modal = document.getElementById('jb-driver-inv-modal');
  const vehicleId = modal?.dataset?.vehicleId;
  const token = window.AppState?.driverAccessToken;
  if (!token || !vehicleId) {
    window.showToast?.('Session expired — please log in again', 'error');
    btn.disabled = false;
    btn.innerHTML = originalLabel;
    return;
  }

  const itemId = document.getElementById('jb-driver-inv-purchase-item')?.value;
  const quantity = parseInt(document.getElementById('jb-driver-inv-purchase-qty')?.value) || 0;
  const unitCost = parseFloat(document.getElementById('jb-driver-inv-purchase-cost')?.value) || 0;
  const notes = document.getElementById('jb-driver-inv-purchase-notes')?.value?.trim() || '';

  if (!itemId) {
    window.showToast?.('Please select an item', 'error');
    btn.disabled = false;
    btn.innerHTML = originalLabel;
    return;
  }
  if (quantity < 1) {
    window.showToast?.('Quantity must be at least 1', 'error');
    btn.disabled = false;
    btn.innerHTML = originalLabel;
    return;
  }
  if (unitCost < 0) {
    window.showToast?.('Cost cannot be negative', 'error');
    btn.disabled = false;
    btn.innerHTML = originalLabel;
    return;
  }

  try {
    let receiptUrl = null;
    let receiptFileName = null;
    if (JobBoardState.driverInventoryReceipt) {
      btn.innerHTML = '<span class="material-icons rotating">sync</span> Uploading receipt...';
      try {
        const uploaded = await _uploadDriverPurchaseReceipt(JobBoardState.driverInventoryReceipt);
        receiptUrl = uploaded.url;
        receiptFileName = uploaded.fileName;
      } catch (uploadErr) {
        // Don't block submission on a receipt upload failure — driver can resend later.
        console.warn('[JobBoard] receipt upload failed, submitting without:', uploadErr);
        // TASK-9402 — an iPhone photo that wouldn't convert says so, not "upload failed".
        window.showToast?.(uploadErr?.userMessage
          ? `Receipt photo skipped — ${uploadErr.userMessage} Submitting purchase without it.`
          : 'Receipt upload failed — submitting purchase without receipt', 'info');
      }
      btn.innerHTML = '<span class="material-icons rotating">sync</span> Submitting...';
    }

    const callable = httpsCallable(functions, 'submitDriverInventoryPurchase');
    const result = await callable({ token, vehicleId, itemId, quantity, unitCost, notes, receiptUrl, receiptFileName });
    if (result?.data?.ok) {
      window.showToast?.('Purchase submitted for review', 'success');
      modal.remove();
    } else {
      throw new Error(result?.data?.error || 'Submission failed');
    }
  } catch (error) {
    console.error('[JobBoard] submitDriverInventoryPurchase error:', error);
    window.showToast?.(error?.message || 'Error submitting purchase', 'error');
    btn.disabled = false;
    btn.innerHTML = originalLabel;
  }
}

// ============================================
// CALENDAR (MONTH-GRID) VIEW — TimeTree-style
// ============================================
// Customer-colored job bars (up to 3 per day, +N more overflow), inline job
// titles. Mirrors dispatch.js renderMonthView pattern. Uses the shared
// buildCalendarMonthGridHTML helper which builds the grid; we provide the
// per-date bar resolver that maps jobDays → {title, color}.

// Inlined customer color palette + hash (matches dispatch.js:5227-5241).
// Cross-module ES imports of dispatch.js helpers are forbidden (CLAUDE.md
// pitfall #1) so we duplicate. If the dispatch palette ever changes, the
// driver-side palette won't auto-sync — flagged as known drift risk.
const _customerColors = [
  '#3B82F6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1'
];
function _getCustomerColor(customerIdOrName) {
  if (!customerIdOrName) return _customerColors[0];
  let hash = 0;
  const str = String(customerIdOrName);
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return _customerColors[Math.abs(hash) % _customerColors.length];
}

// TASK-9399 FILTER STORE BEGIN
// TASK-563 → TASK-9399 — remembered customer filter for the DRIVER calendar.
// SECURITY: this Set only ever NARROWS the already-scoped (jobDayVisibleToCurrentDriver)
// set. It is sanitized against the scoped customer list and applied AFTER scoping,
// so a stored customerId can never resurface a job the personalized-schedule
// filter hid.
// TASK-9399: localStorage (remembered across days, like the dispatch calendar
// chips), keyed by tenant AND viewer — a shared PIN tablet must not hand driver
// A's filter to driver B. Ids only, never names. Cleared on driver-PIN sign-out
// (_teardownDriverPinChrome in app.js removes this prefix for the exiting driver).
// Only USER actions (row toggle / Only / All customers / banner Show all) write
// it; renderCalendarView never does (the old write-back erased the choice the
// moment the driver paged to a month without that customer).
const _JB_CALFILTER_LS_PREFIX = 'dd_jobboard_calfilter_v1_';
function _jbCustomerFilterTenantId() {
  // AppState carries the tenant as `.tenant`; `.tenantId` kept as the pre-9399 fallback.
  return JobBoardState.tenantId || window.AppState?.tenant || window.AppState?.tenantId || '_';
}
function _jbCustomerFilterKey() {
  return `${_JB_CALFILTER_LS_PREFIX}${_jbCustomerFilterTenantId()}_${getActiveJobBoardPersonId() || 'member'}`;
}
// Pre-9399 sessionStorage key (tenant-only). Migrated once, then removed so an
// "All customers" clear can't be undone by the old value on a same-tab reload.
function _jbLegacyCustomerFilterKey() {
  return `jb_calfilter_${_jbCustomerFilterTenantId()}`;
}
function _jbParseCustomerFilter(raw) {
  if (!raw) return null; // null = no filter = show all (scoped) customers
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return null;
    const set = new Set(arr.map(String));
    return set.size ? set : null;
  } catch (_) { return null; }
}
function _jbGetCustomerFilter() {
  const key = _jbCustomerFilterKey();
  let raw = null;
  try { raw = localStorage.getItem(key); } catch (_) {}
  try {
    const legacyKey = _jbLegacyCustomerFilterKey();
    const legacy = sessionStorage.getItem(legacyKey);
    if (legacy != null) {
      sessionStorage.removeItem(legacyKey);
      const migrated = raw == null ? _jbParseCustomerFilter(legacy) : null;
      if (migrated) {
        raw = JSON.stringify([...migrated]);
        try { localStorage.setItem(key, raw); } catch (_) {}
      }
    }
  } catch (_) {}
  return _jbParseCustomerFilter(raw);
}
function _jbSetCustomerFilter(set) {
  try {
    if (!set || set.size === 0) {
      localStorage.removeItem(_jbCustomerFilterKey());
    } else {
      localStorage.setItem(_jbCustomerFilterKey(), JSON.stringify([...set].map(String)));
    }
  } catch (_) {}
  try { sessionStorage.removeItem(_jbLegacyCustomerFilterKey()); } catch (_) {}
}
// What the grid SHOWS this month for a stored filter. Pure — never writes.
// Stored ids outside this month's scoped `available` are dropped from the
// DISPLAY only (they stay stored for the month where they have jobs). An empty
// or full intersection displays as "all".
function _jbResolveDisplayedFilter(rawFilter, available) {
  const avail = (available || []).map(String);
  const activeFilter = rawFilter
    ? new Set([...rawFilter].map(String).filter(id => avail.includes(id)))
    : null;
  const isFiltering = !!(activeFilter && activeFilter.size > 0 && activeFilter.size < avail.length);
  return { activeFilter: isFiltering ? activeFilter : null, isFiltering };
}
// Stable customer-id resolver for an entry. Falls back to a sentinel so jobs with
// no customer still bucket together (and remain filterable / visible).
const _JB_NO_CUSTOMER = '__none__';
function _jbCustomerIdOf(job) {
  return String(job?.customerId || job?.customer || _JB_NO_CUSTOMER);
}
function _jbFilterCustomerLabel(id) {
  return id === _JB_NO_CUSTOMER ? 'No customer' : getCustomerName(id);
}
// Customer names are data — fence them from the driver Spanish layer. The
// "No customer" sentinel alone is our own chrome and stays translatable
// ('No customer' → 'Sin cliente').
function _jbFenceI18n(ids) {
  return (ids || []).length === 1 && String(ids[0]) === _JB_NO_CUSTOMER ? '' : ' data-no-i18n';
}
// Sheet row order: most jobDays this month first, then name; "No customer" last.
function _jbSortFilterCustomers(available, counts, labelOf) {
  const rows = (available || []).map(id => ({
    id: String(id),
    label: String(labelOf(String(id)) || ''),
    count: Number((counts || {})[id]) || 0
  }));
  rows.sort((a, b) => {
    const an = a.id === _JB_NO_CUSTOMER, bn = b.id === _JB_NO_CUSTOMER;
    if (an !== bn) return an ? 1 : -1;
    if (b.count !== a.count) return b.count - a.count;
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });
  return rows;
}
// Case- and diacritic-insensitive search key ("José" matches "jose").
function _jbFilterSearchKey(s) {
  return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}
// "Loenbro" · "Loenbro, Acme" · "Loenbro, Acme +3" — the banner's subject.
function _jbFilterSummaryLabel(activeFilter, labelOf) {
  const names = [...(activeFilter || [])].map(id => String(labelOf(id) || ''));
  if (names.length <= 2) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
}
// One-line strip above the grid whenever the filter narrows the month, so a
// remembered filter never reads as "no jobs today". Text nodes are split so the
// driver Spanish layer can key 'Showing' / 'only' / 'Show all'; the customer
// name is data (fenced by _jbFenceI18n).
function _jbCalendarFilterBannerHTML(activeFilter, isFiltering, labelOf) {
  if (!isFiltering || !activeFilter || !activeFilter.size) return '';
  const subject = _jbFilterSummaryLabel(activeFilter, labelOf);
  return `<div class="jb-cal-filter-banner" role="status">
      <span class="material-icons" aria-hidden="true">filter_list</span>
      <span class="jb-cal-filter-banner-text"><span>Showing</span> <strong${_jbFenceI18n([...activeFilter])}>${escapeHtml(subject)}</strong> <span>only</span></span>
      <button type="button" class="jb-cal-filter-banner-reset" data-jb-filter-action="all">Show all</button>
    </div>`;
}
// Translated string for ARIA (the i18n layer only rewrites text nodes and
// placeholders, never aria-label).
function _jbT(s) {
  try { return window.DriverI18n?.t ? window.DriverI18n.t(s) : s; } catch (_) { return s; }
}

// Toggle a customer in the filter, persist, re-render. Invoked by the delegated
// sheet listener (data-cust-id → dataset), NOT an inline onclick.
// '__all__' is the "clear / show all" sentinel.
function toggleCalendarCustomerFilter(customerId) {
  if (String(customerId) === '__all__') {
    _jbSetCustomerFilter(null);
    renderCalendarView();
    return;
  }
  // Start from what the driver SEES this month (a displayed "all" is
  // materialized as every available id before one is toggled off).
  const available = (JobBoardState._calFilterAvailable || []).map(String);
  const stored = _jbGetCustomerFilter();
  const { activeFilter } = _jbResolveDisplayedFilter(stored, available);
  const set = activeFilter ? new Set(activeFilter) : new Set(available);
  const id = String(customerId);
  // A row left over from before a live data update (customer no longer in this
  // month's scoped set) is ignored — toggling it would add, not remove.
  if (!available.includes(id)) { renderCalendarView(); return; }
  if (set.has(id)) set.delete(id); else set.add(id);
  // Stored picks for customers with no jobs THIS month were not on screen, so
  // this tap says nothing about them — keep them for the months they belong to.
  const offMonth = stored ? [...stored].filter(x => !available.includes(x)) : [];
  // Empty selection is meaningless (would hide everything) → "all" for this
  // month, which is what the off-month remainder alone displays as here.
  const inMonth = (set.size === 0 || set.size >= available.length) ? [] : [...set];
  const next = new Set([...offMonth, ...inMonth]);
  _jbSetCustomerFilter(next.size ? next : null);
  renderCalendarView();
}
// TASK-9399 — "Only": filter to exactly this customer. An explicit whole-set
// choice, so it replaces the stored set (off-month picks included). An id no
// longer in this month's scoped set (stale row) is a no-op — never a widen.
function onlyCalendarCustomerFilter(customerId) {
  const available = (JobBoardState._calFilterAvailable || []).map(String);
  const id = String(customerId);
  if (available.includes(id) && available.length >= 2) {
    _jbSetCustomerFilter(new Set([id]));
  }
  renderCalendarView();
}
// TASK-9399 FILTER STORE END

// TASK-569 — lower bound (epoch ms) of the live jobDays subscription window.
// Below this, the live JobBoardState.jobDays array does NOT hold the data, so
// the calendar must fetch the rendered month on demand. Mirrors the exact
// windowing math in subscribeToData (L342-358). Returns -Infinity when the
// window is unbounded ("all" admin, or a windowed-admin query that fell back to
// the full-collection stream) — in that case every month is already live.
function _jbLiveWindowStartMs() {
  // Driver sub is windowed (7d) only when the kill-switch is on; if a PIN driver
  // and the filter is OFF, the sub is unbounded → every month already live.
  const isPinDriver = window.AppState?.driverModePin === true;
  if (isPinDriver && !JB_FAST_PIN_DRIVER_FILTER) return -Infinity;
  const now = new Date();
  const todayStartLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (isPinDriver) {
    return todayStartLocal.getTime() - 7 * 24 * 60 * 60 * 1000;
  }
  const rawAdminDays = Number(JobBoardState.adminWindowDaysBack);
  const adminDaysBack = Number.isFinite(rawAdminDays) ? rawAdminDays : 90;
  if (adminDaysBack <= 0) return -Infinity; // "all" — fully covered
  return todayStartLocal.getTime() - adminDaysBack * 24 * 60 * 60 * 1000;
}

// TASK-569 — ensure the rendered month's jobDays are available. If the month's
// last day is still within the live window (>= window start), the live arrays
// already cover it → no-op. Otherwise fetch that month's jobDays + referenced
// parent jobs ONCE and cache them, then re-render. Non-blocking: the calendar
// renders immediately (empty for the month until the fetch lands); on error it
// degrades to an empty month + console warn, never throws.
async function _ensureCalendarMonthLoaded(year, month) {
  const tenantId = JobBoardState.tenantId;
  if (!tenantId) return;
  const monthStart = new Date(year, month, 1, 0, 0, 0, 0);
  const monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999); // last day of month
  // If the month's last instant is within the live window, the live subscription
  // already streams it — nothing to fetch.
  if (monthEnd.getTime() >= _jbLiveWindowStartMs()) return;

  const key = `${year}-${String(month + 1).padStart(2, '0')}`;
  if (JobBoardState._calMonthCache[key] || JobBoardState._calMonthFetching[key]) return;

  JobBoardState._calMonthFetching[key] = true;
  delete JobBoardState._calMonthError[key];
  try {
    // jobDays for the month — single-field date range (already indexed; no new
    // index). Scoped to this tenant's jobDays subcollection exactly like the
    // live sub; driver-scope is enforced at render time via
    // jobDayVisibleToCurrentDriver (same gate the live path uses).
    const jdSnap = await getDocs(query(
      collection(db, 'tenants', tenantId, 'jobDays'),
      where('date', '>=', Timestamp.fromDate(monthStart)),
      where('date', '<=', Timestamp.fromDate(monthEnd)),
      orderBy('date', 'asc')
    ));
    const jobDays = jdSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    // Fetch the parent jobs these jobDays reference (the jobs subscription is
    // windowed too, so titles/customer-color would be missing). De-dupe jobIds,
    // skip any already in the live jobs array, getDoc the rest in parallel.
    const jobsById = {};
    const liveJobIds = new Set(JobBoardState.jobs.map(j => j.id));
    const neededJobIds = [...new Set(
      jobDays.map(jd => jd.jobId).filter(id => id && !liveJobIds.has(id))
    )];
    await Promise.all(neededJobIds.map(async (jid) => {
      try {
        const jdoc = await getDoc(doc(db, 'tenants', tenantId, 'jobs', jid));
        if (jdoc.exists()) jobsById[jid] = { id: jdoc.id, ...jdoc.data() };
      } catch (_) { /* missing/denied single job — skip, day renders without it */ }
    }));

    // Tenant-switch guard (COD Code-Reviewer, folded): initJobBoard clears the
    // cache on re-init, but an in-flight fetch started under the OLD tenant can
    // resolve AFTER the switch. Drop the result if the tenant changed mid-fetch
    // so we never write a prior tenant's jobDays into the new tenant's cache.
    if (JobBoardState.tenantId !== tenantId) return;
    JobBoardState._calMonthCache[key] = { jobDays, jobsById };
  } catch (err) {
    console.warn('[JobBoard] TASK-569 on-demand month fetch failed for', key, '— rendering empty month.', err);
    JobBoardState._calMonthError[key] = true;
  } finally {
    delete JobBoardState._calMonthFetching[key];
    // Re-render only if the user is still on this month in calendar mode AND the
    // tenant hasn't changed underneath us (see guard above).
    if (JobBoardState.tenantId === tenantId && JobBoardState.viewMode === 'calendar') {
      const a = new Date(JobBoardState.calendarMonth);
      if (a.getFullYear() === year && a.getMonth() === month) renderCalendarView();
    }
  }
}

// TASK-9374 — the calendar layout is pure CSS keyed off two classes:
// .jb-cal-mode on .jobboard-view (one-row chrome, 100dvh flex column, bottom-nav
// padding) and .jobboard-jobs--calendar on #jobboard-content (1100px cap instead
// of the day-mode 700px card width). Set at first paint in getJobBoardHTML and
// re-synced on every mode change / calendar render. Leaving calendar mode also
// empties the header filter-chip mount.
function _jbSyncCalendarModeClasses() {
  const cal = JobBoardState.viewMode === 'calendar';
  const view = document.querySelector('.jobboard-view');
  if (view) view.classList.toggle('jb-cal-mode', cal);
  const content = document.getElementById('jobboard-content');
  if (content) content.classList.toggle('jobboard-jobs--calendar', cal);
  if (!cal) {
    const mount = document.getElementById('jb-cal-filter-mount');
    if (mount) mount.innerHTML = '';
  }
}

// TASK-9374 — bars per cell come from CSS (`--jb-cal-max-bars`: 6 desktop, 5 on
// a phone, 3 on a ≤700px-tall phone) so the builder's cut-off and the
// viewport-sized row height agree instead of the builder guessing.
function _jbCalendarMaxBars(container) {
  try {
    const raw = getComputedStyle(container).getPropertyValue('--jb-cal-max-bars');
    const v = parseInt(String(raw).trim(), 10);
    if (Number.isFinite(v) && v >= 2 && v <= 12) return v;
  } catch (_) {}
  return 5;
}

function renderCalendarView() {
  const container = document.getElementById('jobboard-content');
  if (!container) return;
  _jbSyncCalendarModeClasses();

  const anchor = new Date(JobBoardState.calendarMonth);
  anchor.setHours(0, 0, 0, 0);
  anchor.setDate(1);
  const year = anchor.getFullYear();
  const month = anchor.getMonth();

  // TASK-569 — if this month falls outside the live subscription window, fetch
  // it on demand (cached after first load). Fire-and-forget: the render below
  // proceeds immediately with whatever is loaded; _ensureCalendarMonthLoaded
  // re-renders when the fetch lands. Never awaited → never blocks the calendar.
  const _monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
  _ensureCalendarMonthLoaded(year, month);
  const _cachedMonth = JobBoardState._calMonthCache[_monthKey];
  const _monthFetching = !!JobBoardState._calMonthFetching[_monthKey];
  // Render source = live windowed jobDays + this month's cache (if any). The
  // cache is render-only and de-duped against the live array by id below.
  const _renderJobDays = _cachedMonth
    ? (() => {
        const seen = new Set(JobBoardState.jobDays.map(j => j.id));
        return JobBoardState.jobDays.concat(_cachedMonth.jobDays.filter(jd => !seen.has(jd.id)));
      })()
    : JobBoardState.jobDays;
  // Job resolver: live jobs first, then this month's fetched parents.
  const _findJob = (jobId) =>
    JobBoardState.jobs.find(j => j.id === jobId)
    || (_cachedMonth ? _cachedMonth.jobsById[jobId] : undefined);

  // Update top date display to "Month Year"
  const dateEl = document.getElementById('jobboard-current-date');
  if (dateEl) {
    dateEl.textContent = anchor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
  // Hide the day-mode count line in calendar mode
  const countEl = document.getElementById('jobboard-job-count');
  if (countEl) countEl.textContent = '';

  // ── STEP 1: SCOPE FIRST (security boundary) ──────────────────────────────
  // Index visible jobDays per ISO date AFTER the personalized-schedule filter.
  // EVERY downstream surface (filter chips, the grid, the bottom sheet) reads
  // ONLY from this scoped index — never from raw JobBoardState.jobDays — so the
  // customer filter can only narrow, never widen, what a driver can see.
  const scopedByIso = {};
  const scopedCustomerIds = new Set(); // distinct customers across the FULL scoped set
  const scopedCounts = {};
  for (const jobDay of _renderJobDays) {
    const d = jobDay.date?.toDate ? jobDay.date.toDate() : new Date(jobDay.date);
    if (!d || isNaN(d.getTime())) continue;
    if (d.getFullYear() !== year || d.getMonth() !== month) continue;
    if (!jobDayVisibleToCurrentDriver(jobDay)) continue;          // ← scope gate
    const job = _findJob(jobDay.jobId);
    if (!job || job.status === 'cancelled') continue;
    const iso = formatDateISO(d);
    if (!scopedByIso[iso]) scopedByIso[iso] = [];
    scopedByIso[iso].push({ jobDay, job });
    const _cid = _jbCustomerIdOf(job);
    scopedCustomerIds.add(_cid);
    // TASK-9399: per-customer jobDay count for the filter sheet (one per bar).
    scopedCounts[_cid] = (scopedCounts[_cid] || 0) + 1;
  }

  // ── STEP 2: build filter-chip universe FROM the scoped set only ──────────
  // available[] is the list of customerIds the driver is actually allowed to
  // see this month. The filter is gated AGAINST this — never against raw data.
  const available = [...scopedCustomerIds];
  JobBoardState._calFilterAvailable = available; // read back by toggle handler
  JobBoardState._calFilterCounts = scopedCounts; // read back by the filter sheet
  // Sanitize the remembered filter to this month's scoped ids FOR DISPLAY ONLY.
  // A stored id not in `available` (scoped-away, or simply no jobs this month)
  // is dropped from what renders — it cannot widen the view. TASK-9399: the
  // sanitized set is deliberately NOT written back; doing so erased the
  // driver's choice as soon as they paged to a month without that customer.
  const { activeFilter, isFiltering } = _jbResolveDisplayedFilter(_jbGetCustomerFilter(), available);

  // ── STEP 3: NARROW the scoped index by the (sanitized) filter ────────────
  const jobDaysByIso = {};
  for (const iso of Object.keys(scopedByIso)) {
    const kept = scopedByIso[iso].filter(({ job }) =>
      !isFiltering || activeFilter.has(_jbCustomerIdOf(job))
    );
    if (kept.length) jobDaysByIso[iso] = kept;
  }
  // Stash for the bottom sheet (already scoped + filtered).
  JobBoardState._calDaysByIso = jobDaysByIso;

  const grid = _sharedBuildCalendarMonthGridHTML(anchor, {
    getBarsForDate: (y, m, day, iso) => {
      const list = jobDaysByIso[iso] || [];
      return list.map(({ job }) => ({
        title: job.title || job.name || 'Untitled',
        color: _getCustomerColor(job.customerId || job.customer)
      }));
    },
    onCellClick: 'window.JobBoardModule.openDayDetailSheet',
    wrapperClass: 'jobboard-month-view',
    cellCountBadge: true,
    // TASK-570 → TASK-9374: bars per cell now follow the breakpoint (CSS var)
    // instead of a fixed 5; the scoped CSS no longer hides any nth bar — the
    // builder alone decides what paints. Dispatch passes none of these opts, so
    // its month view is byte-identical.
    maxVisible: _jbCalendarMaxBars(container),
    // TASK-9374 TimeTree layout: always 42 cells (greyed adjacent-month days),
    // full titles (CSS ellipsis), and "+N more" in the last slot counting only
    // the bars actually hidden.
    fillAdjacentDays: true,
    fullTitle: true,
    reserveMoreSlot: true
  });

  // ── Filter trigger — only render with ≥2 distinct customers (right-size) ──
  // TASK-570: the old pill row (overflow-x scroll) degraded badly at ~14
  // customers. Replaced with a compact trigger button that opens a multi-select
  // bottom sheet (openCalendarFilterSheet, built below from this same scoped
  // `available` list). State/security are UNCHANGED — the trigger only swaps the
  // control. activeFilter still NARROWS the already-scoped set; the sheet rows
  // ride `data-cust-id` (escapeHtml'd) + a delegated listener, never an inline
  // onclick="fn('${id}')" (stored-XSS fold from 563 preserved).
  // TASK-9374: the trigger is now an icon + count CHIP in the one-row header
  // (#jb-cal-filter-mount inside .jobboard-header), not a row above the grid.
  // Same gate (≥2 customers), same sheet, same scoped `available` list.
  let filterBar = '';
  if (available.length >= 2) {
    // TASK-9399: exactly one customer → its name (≈12 chars, full name in the
    // title); several → "n/N"; none → "All". The name is data, never translated.
    const oneName = isFiltering && activeFilter.size === 1
      ? _jbFilterCustomerLabel([...activeFilter][0])
      : '';
    const triggerLabel = oneName
      || (isFiltering ? `${activeFilter.size} of ${available.length}` : 'All customers');
    const chipText = oneName
      ? (oneName.length > 12 ? `${oneName.slice(0, 11).trimEnd()}…` : oneName)
      : (isFiltering ? `${activeFilter.size}/${available.length}` : 'All');
    filterBar = `
        <button type="button" class="jb-cust-filter-trigger jb-cal-filter-chip${isFiltering ? ' is-filtering' : ''}"
          title="${escapeHtml(triggerLabel)}" aria-label="${escapeHtml(`${_jbT('Filter by customer')}: ${oneName || _jbT(triggerLabel)}`)}">
          <span class="material-icons" aria-hidden="true">filter_list</span>
          <span class="jb-cal-filter-count"${oneName ? _jbFenceI18n([...activeFilter]) : ''}>${escapeHtml(chipText)}</span>
        </button>`;
  }
  // TASK-9399: "Showing X only · Show all" strip above the grid whenever the
  // remembered filter narrows this month (built from the SAME sanitized set).
  const filterBanner = available.length >= 2
    ? _jbCalendarFilterBannerHTML(activeFilter, isFiltering, _jbFilterCustomerLabel)
    : '';

  const personalizedNote = isPersonalizedScheduleActive()
    ? `<div class="jb-cal-note">
         Showing only jobs you're assigned to. Tap any day with jobs to open it.
       </div>`
    : `<div class="jb-cal-note">
         Tap any day with jobs to open it.
       </div>`;

  // TASK-569 — brief loading note while an out-of-window month is fetched.
  const loadingNote = _monthFetching
    ? `<div class="jb-cal-note" style="display:flex;align-items:center;justify-content:center;gap:6px;">
         <span class="material-icons" style="font-size:14px;animation:spin 1s linear infinite;">autorenew</span>
         Loading this month…
       </div>`
    : '';

  container.innerHTML = filterBanner + grid + personalizedNote + loadingNote;
  container.querySelector('.jb-cal-filter-banner-reset')
    ?.addEventListener('click', () => toggleCalendarCustomerFilter('__all__'));

  // Filter chip → header mount (falls back to the grid container if the mount
  // is missing, so the control is never lost). Opens the multi-select bottom
  // sheet built from the SAME scoped `available` list.
  const mount = document.getElementById('jb-cal-filter-mount');
  if (mount) mount.innerHTML = filterBar;
  else container.insertAdjacentHTML('afterbegin', filterBar);
  const triggerEl = (mount || container).querySelector('.jb-cust-filter-trigger');
  if (triggerEl) {
    triggerEl.addEventListener('click', () => openCalendarFilterSheet());
  }
  // TASK-9399: a live jobDays update while the sheet is open repaints its
  // checks/counts in place (never a rebuild — search and scroll survive).
  _jbRefreshFilterSheet();

  // Language pill (mounted by driver-i18n-es.js into .jobboard-header) renders
  // as a globe in calendar mode (CSS); its text stays its accessible name —
  // mirror it into title so desktop hover still says which language it flips to.
  const langBtn = document.getElementById('driverLangToggle');
  if (langBtn) langBtn.title = (langBtn.textContent || '').trim() || 'Language';
}

// TASK-570 → TASK-9399 — customer-filter bottom sheet (driver calendar).
// Rows = the driver's OWN scoped customers, sorted by jobDay count this month
// (digits shown), "No customer" last. Each row is TWO SIBLING buttons (never
// nested): [checkbox + swatch + name + count] toggles multi-select, and a
// separate 44×44 "Only" filters to that customer and closes. "All customers"
// is always the first row. Search (pinned under the header, outside the
// scroller) only past 8 customers. Toggling updates the sheet IN PLACE — it is
// never destroyed and rebuilt, so the search text, focus and scroll survive.
// SECURITY: built ONLY from JobBoardState._calFilterAvailable (the scoped
// customer-id list set in STEP 2), never raw data — so a row can never reference
// a scoped-away customer. Ids ride `data-cust-id` (escapeHtml'd) read back via
// dataset in a delegated listener — never an inline onclick (563 stored-XSS fold).
const _JB_FILTER_SEARCH_MIN = 8;
function openCalendarFilterSheet() {
  const available = (JobBoardState._calFilterAvailable || []).map(String);
  if (available.length < 2) return;
  const counts = JobBoardState._calFilterCounts || {};
  const sorted = _jbSortFilterCustomers(available, counts, _jbFilterCustomerLabel);
  const showSearch = available.length > _JB_FILTER_SEARCH_MIN;

  const rows = sorted.map(({ id, label, count }) => {
    const color = _getCustomerColor(id === _JB_NO_CUSTOMER ? '' : id);
    return `<div class="jb-filter-row" data-cust-id="${escapeHtml(id)}" data-search="${escapeHtml(_jbFilterSearchKey(label))}">
      <button type="button" class="jb-filter-toggle" role="checkbox" aria-checked="false" data-cust-id="${escapeHtml(id)}">
        <span class="material-icons jb-filter-check" aria-hidden="true">check_box_outline_blank</span>
        <span class="jb-filter-swatch" style="background:${color};" aria-hidden="true"></span>
        <span class="jb-filter-name"${_jbFenceI18n([id])}>${escapeHtml(label)}</span>
        <span class="jb-filter-count" data-no-i18n>${count}</span>
      </button>
      <button type="button" class="jb-filter-only" data-cust-id="${escapeHtml(id)}" aria-label="${escapeHtml(`${_jbT('Only')}: ${label}`)}">Only</button>
    </div>`;
  }).join('');

  const searchBlock = showSearch
    ? `<div class="jb-filter-search">
        <span class="material-icons" aria-hidden="true">search</span>
        <input type="search" class="jb-filter-search-input" placeholder="Search customers…"
          aria-label="${escapeHtml(_jbT('Search customers…'))}" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" enterkeyhint="search">
      </div>`
    : '';

  const existing = document.getElementById('jobboard-filter-sheet');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.className = 'modal hidden modal-kb-aware';
  modal.id = 'jobboard-filter-sheet';
  modal.innerHTML = `
    <div class="modal-content modal-fullscreen jb-filter-sheet" role="dialog" aria-modal="true" aria-labelledby="jb-filter-sheet-title">
      <div class="modal-header jb-filter-header">
        <h3 id="jb-filter-sheet-title">Filter by customer</h3>
        <div class="jb-filter-header-actions">
          <button type="button" class="jb-filter-done" data-jb-filter-action="done">Done</button>
          <button type="button" class="modal-close jb-filter-close" data-jb-filter-action="done" aria-label="${escapeHtml(_jbT('Close'))}">
            <span class="material-icons" aria-hidden="true">close</span>
          </button>
        </div>
      </div>
      ${searchBlock}
      <div class="modal-body jb-filter-list">
        <button type="button" class="jb-filter-all" role="checkbox" aria-checked="false" data-jb-filter-action="all">
          <span class="material-icons jb-filter-check" aria-hidden="true">check_box_outline_blank</span>
          <span class="jb-filter-name">All customers</span>
        </button>
        ${rows}
        <div class="jb-filter-empty hidden">No customers match</div>
      </div>
    </div>
  `;

  document.body.appendChild(modal);
  _jbRefreshFilterSheet(modal);
  modal.classList.remove('hidden');
  document.body.classList.add('modal-open');

  // One delegated listener for every control — data-attr → dataset, no inline JS.
  modal.addEventListener('click', (e) => {
    if (e.target === modal) { closeCalendarFilterSheet(); return; }
    const only = e.target.closest?.('.jb-filter-only');
    if (only) {
      onlyCalendarCustomerFilter(only.dataset.custId);
      closeCalendarFilterSheet();
      return;
    }
    const toggle = e.target.closest?.('.jb-filter-toggle');
    if (toggle) {
      toggleCalendarCustomerFilter(toggle.dataset.custId);
      _jbRefreshFilterSheet(modal); // in place — search/scroll/focus untouched
      return;
    }
    const action = e.target.closest?.('[data-jb-filter-action]')?.dataset.jbFilterAction;
    if (action === 'all') {
      toggleCalendarCustomerFilter('__all__');
      closeCalendarFilterSheet();
    } else if (action === 'done') {
      closeCalendarFilterSheet();
    }
  });
  const searchInput = modal.querySelector('.jb-filter-search-input');
  if (searchInput) {
    searchInput.addEventListener('input', () => _jbApplyFilterSheetSearch(modal, searchInput.value));
  }
}

// Re-derive every row's checked state (and count) from the stored filter,
// inside the existing DOM.
function _jbRefreshFilterSheet(modal) {
  const root = modal || document.getElementById('jobboard-filter-sheet');
  if (!root) return;
  const available = (JobBoardState._calFilterAvailable || []).map(String);
  const { activeFilter, isFiltering } = _jbResolveDisplayedFilter(_jbGetCustomerFilter(), available);
  const counts = JobBoardState._calFilterCounts || {};
  const paint = (btn, on) => {
    btn.setAttribute('aria-checked', on ? 'true' : 'false');
    btn.classList.toggle('is-on', on);
    const icon = btn.querySelector('.jb-filter-check');
    if (icon) icon.textContent = on ? 'check_box' : 'check_box_outline_blank';
  };
  root.querySelectorAll('.jb-filter-toggle').forEach(btn => {
    const id = btn.dataset.custId;
    const live = available.includes(id);
    // A customer that left the scoped month after a live update is disabled
    // (not removed — removing rows would shift the driver's scroll position).
    btn.disabled = !live;
    const onlyBtn = btn.parentElement?.querySelector('.jb-filter-only');
    if (onlyBtn) onlyBtn.disabled = !live;
    btn.closest('.jb-filter-row')?.classList.toggle('is-gone', !live);
    paint(btn, live && (!isFiltering || activeFilter.has(id)));
    const countEl = btn.querySelector('.jb-filter-count');
    if (countEl) {
      const txt = String(counts[id] || 0);
      if (countEl.textContent !== txt) countEl.textContent = txt;
    }
  });
  const allBtn = root.querySelector('.jb-filter-all');
  if (allBtn) paint(allBtn, !isFiltering);
}

function _jbApplyFilterSheetSearch(modal, query) {
  const q = _jbFilterSearchKey(query);
  let shown = 0;
  modal.querySelectorAll('.jb-filter-row').forEach(row => {
    const hit = !q || (row.dataset.search || '').includes(q);
    row.classList.toggle('hidden', !hit);
    if (hit) shown++;
  });
  modal.querySelector('.jb-filter-empty')?.classList.toggle('hidden', shown > 0);
}

function closeCalendarFilterSheet() {
  const modal = document.getElementById('jobboard-filter-sheet');
  if (modal) modal.remove();
  if (!document.querySelector('.modal:not(.hidden)')) {
    document.body.classList.remove('modal-open');
  }
}

// TASK-563 — Day-detail bottom sheet (driver READ-ONLY). Tap a day in the month
// grid → in-place .modal-fullscreen sheet listing that day's jobs with full
// detail (customer, time, location, crew, trucks, title, loads). Reuses
// renderJobCard markup — which deliberately does NOT render notes (job/jobDay
// `notes` are dispatcher-internal; excluded per COD Adversary/Domain leak flag).
// Reads ONLY from the already-scoped+filtered JobBoardState._calDaysByIso, never
// raw jobDays, so it can never reveal a job hidden by personalized-schedule
// scoping or the customer filter. Tapping a job card opens the existing
// showJobDetails modal (pre-existing surface, unchanged).
function openDayDetailSheet(isoDateStr) {
  // TASK-9374 — greyed adjacent-month cells carry a date OUTSIDE the rendered
  // month, and the scoped index is this-month-only. Page the calendar to that
  // month first (re-scopes + re-indexes through the same visibility gate), then
  // open the sheet for the day. Same-month taps are unchanged.
  try {
    const [ty, tm] = String(isoDateStr).split('-').map(Number);
    const cur = new Date(JobBoardState.calendarMonth);
    if (JobBoardState.viewMode === 'calendar' && Number.isFinite(ty) && Number.isFinite(tm)
        && (cur.getFullYear() !== ty || cur.getMonth() !== tm - 1)) {
      JobBoardState.calendarMonth = new Date(ty, tm - 1, 1);
      renderCalendarView();
    }
  } catch (_) {}
  const byIso = JobBoardState._calDaysByIso || {};
  const entries = (byIso[isoDateStr] || []).slice();
  // Sort by start time (jobDay override → job default → legacy), mirrors getJobsForDate.
  entries.sort((a, b) => {
    const ta = a.jobDay.startTime || a.job.defaultStart || a.job.startTime || '99:99';
    const tb = b.jobDay.startTime || b.job.defaultStart || b.job.startTime || '99:99';
    return ta.localeCompare(tb);
  });

  // Friendly heading date — parse ISO as a local date (avoid UTC shift).
  let heading = isoDateStr;
  try {
    const [yy, mm, dd] = isoDateStr.split('-').map(Number);
    heading = new Date(yy, mm - 1, dd).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric'
    });
  } catch (_) {}

  // renderJobCard wires each card's onclick to showJobDetails directly, which
  // would stack a second modal over the sheet. Rewrite that call to a sheet-aware
  // wrapper that closes the sheet first, then opens the detail modal — cleaner
  // stacking + correct scroll-lock teardown. (String swap on the generated markup
  // keeps renderJobCard the single source of card layout.)
  const body = entries.length
    ? entries.map(({ job, jobDay }) =>
        renderJobCard(job, jobDay).replace(
          /window\.JobBoardModule\.showJobDetails\(/g,
          'window.JobBoardModule.openJobFromDaySheet('
        )
      ).join('')
    : `<div style="text-align:center;padding:48px 16px;color:var(--text-muted);">
         <span class="material-icons" style="font-size:42px;opacity:0.5;">event_available</span>
         <div style="margin-top:8px;font-size:0.95rem;">No jobs scheduled for this day</div>
       </div>`;

  const existing = document.getElementById('jobboard-day-sheet');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.className = 'modal hidden';
  modal.id = 'jobboard-day-sheet';
  modal.innerHTML = `
    <div class="modal-content modal-fullscreen" style="display:flex;flex-direction:column;">
      <div class="modal-header">
        <h3 style="font-size:1.05rem;">${escapeHtml(heading)}</h3>
        <button class="modal-close" onclick="window.JobBoardModule.closeDayDetailSheet()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;">
        ${entries.length ? `<div style="font-size:0.78rem;color:var(--text-muted);margin:0 0 10px;">${entries.length} job${entries.length !== 1 ? 's' : ''} scheduled</div>` : ''}
        ${body}
      </div>
    </div>
  `;

  document.body.appendChild(modal);
  modal.classList.remove('hidden');
  document.body.classList.add('modal-open');
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeDayDetailSheet();
  });
}

function closeDayDetailSheet() {
  const modal = document.getElementById('jobboard-day-sheet');
  if (modal) modal.remove();
  // Only drop modal-open if no other modal is present (showJobDetails may stack).
  if (!document.querySelector('.modal:not(.hidden)')) {
    document.body.classList.remove('modal-open');
  }
}

// Tap a job card inside the day sheet → close the sheet, open the full detail
// modal (the pre-existing showJobDetails surface). Keeps modals from stacking.
function openJobFromDaySheet(jobId, jobDayId) {
  closeDayDetailSheet();
  showJobDetails(jobId, jobDayId);
}

// ============================================
// NAVIGATION
// ============================================
function navigate(days) {
  if (JobBoardState.viewMode === 'calendar') {
    // In calendar mode, the chevrons step by month, not by day.
    const m = new Date(JobBoardState.calendarMonth);
    m.setMonth(m.getMonth() + (days > 0 ? 1 : -1));
    JobBoardState.calendarMonth = m;
  } else {
    const newDate = new Date(JobBoardState.currentDate);
    newDate.setDate(newDate.getDate() + days);
    JobBoardState.currentDate = newDate;
  }
  renderJobBoard();
}

// TASK-424 #5 (C3): "Load older" — widen the admin live window further back and
// resubscribe. Mirrors dispatch.js loadOlderDispatch. subscribeToData does NOT
// clear its own unsubscribers (cleanup lives at initJobBoard, L133-136), so we
// MUST tear down the live listeners here before resubscribing or we duplicate
// them. Debounced via _jbLoadOlderBusy against double-tap.
let _jbLoadOlderBusy = false;
function loadOlderJobBoard() {
  if (_jbLoadOlderBusy) return;
  if (!JobBoardState.tenantId) return;
  _jbLoadOlderBusy = true;
  const _raw = Number(JobBoardState.adminWindowDaysBack);
  const cur = Number.isFinite(_raw) ? _raw : 90; // NOT `|| 90` — 0 = "all"
  // *3 growth: 90 → 270 → 810 → 2430 → all (0 = no lower bound).
  let next;
  if (cur <= 0) { _jbLoadOlderBusy = false; return; } // already "all"
  else if (cur >= 730) next = 0; // 0 = all (no lower bound)
  else next = cur * 3;
  JobBoardState.adminWindowDaysBack = next;
  console.log('[JobBoard] Load older — widening admin window from', cur, 'to', next === 0 ? 'ALL' : next, 'days back');
  try {
    JobBoardState.unsubscribers.forEach(unsub => { try { unsub(); } catch (_) {} });
  } catch (_) {}
  JobBoardState.unsubscribers = [];
  subscribeToData(JobBoardState.tenantId);
  syncJobBoardLoadOlder();
  setTimeout(() => { _jbLoadOlderBusy = false; }, 1200);
}

// Show/hide + relabel the jobboard "Load older" control. Hidden ONLY in driver
// mode. At the "all history" boundary render a terminal DISABLED affordance
// ("Showing all history") instead of hiding it — a control that vanishes after
// 3 taps reads as broken. Mirrors dispatch.js syncLoadOlderControl (TASK-1254).
function syncJobBoardLoadOlder() {
  const el = document.getElementById('jobboard-load-older');
  if (!el) return;
  const isPinDriver = !!window.AppState?.driverModePin;
  const _raw = Number(JobBoardState.adminWindowDaysBack);
  const cur = Number.isFinite(_raw) ? _raw : 90; // NOT `|| 90` — 0 = "all"
  const label = el.querySelector('.jb-load-older-label');
  // Driver mode: admin-only control — hide entirely.
  if (isPinDriver) { el.style.display = 'none'; return; }
  el.style.display = '';
  if (cur <= 0) {
    // Terminal "all history" state — disabled, non-clickable. TASK-1254.
    el.setAttribute('disabled', '');
    el.style.cursor = 'default';
    el.style.opacity = '0.55';
    el.style.pointerEvents = 'none';
    if (label) label.textContent = 'Showing all history';
    return;
  }
  el.removeAttribute('disabled');
  el.style.cursor = 'pointer';
  el.style.opacity = '';
  el.style.pointerEvents = '';
  if (label) {
    const months = Math.round(cur / 30);
    label.textContent = `Showing last ~${months} mo — Load older`;
  }
}

function setViewMode(mode) {
  if (mode !== 'day' && mode !== 'calendar') return;
  JobBoardState.viewMode = mode;
  try { localStorage.setItem(VIEW_MODE_LS_KEY, mode); } catch (_) {}
  // When switching to calendar, sync calendarMonth to whatever date is currently shown.
  if (mode === 'calendar') {
    JobBoardState.calendarMonth = new Date(JobBoardState.currentDate);
  }
  _jbSyncCalendarModeClasses(); // TASK-9374 — layout classes follow the mode
  renderJobBoard();
}

function jumpToDayFromCalendar(isoDateStr) {
  const [y, m, d] = isoDateStr.split('-').map(Number);
  const target = new Date(y, m - 1, d);
  JobBoardState.currentDate = target;
  setViewMode('day');
}

// ============================================
// HELPERS
// ============================================
function formatDateISO(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatTime(time) {
  if (!time) return '';
  // Handle "HH:MM" format
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
}

function capitalizeFirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Driver-scope filter — delegates to shared util so behavior matches
// dispatch.js exactly. Honors tenant-level personalizedSchedule plus
// per-person showAllJobs precedence, with legacy-safe ID+name matching
// across drivers / helpers / people refs.
function isPersonalizedScheduleActive() {
  if (!window.AppState?.settings?.personalizedSchedule) return false;
  return !!getActiveJobBoardPersonId();
}
// H3 READ-ONLY LINK — a view-only access-link session must not write anything
// (clock/ticket/expense/JSA). The firestore rules + read-only member doc are the
// real enforcement; this client guard short-circuits the write entry points so
// the driver gets a clear "log in with your PIN" message instead of a raw
// permission-denied. Returns true (and toasts) when the action was blocked.
function jbBlockIfReadOnlyLink(actionLabel) {
  if (window.isReadOnlyLinkSession?.()) {
    window.showToast?.(
      `Read-only preview — log in with your PIN to ${actionLabel || 'do that'}.`,
      'info'
    );
    return true;
  }
  return false;
}

// TASK-355 2026-05-22 — single source of truth for the "who is viewing the Job
// Board right now" identity. Resolves PIN driver -> SPA member personId in
// priority order. SPA members were previously stuck on driver-PIN-only visibility
// (window.AppState.driverPersonId was unset for non-PIN sessions, so the view
// fell through to "show everything" or empty depending on settings).
function getActiveJobBoardPersonId() {
  return window.AppState?.driverPersonId
    || window.AppState?.memberAccess?.personId
    || null;
}
function getActiveJobBoardPersonName() {
  // PIN driver name first (most authoritative — set by validateDriverPin).
  // Then SPA member: roster person record -> memberAccess.displayName -> ''.
  if (window.AppState?.driverPersonName) return window.AppState.driverPersonName;
  const pid = window.AppState?.memberAccess?.personId;
  if (pid) {
    const person = JobBoardState.people.find(p => p.id === pid);
    if (person?.name) return person.name;
  }
  return window.AppState?.memberAccess?.displayName || '';
}
// TASK-1338 — the jobs a person may legitimately tie an expense to.
//
// Four COD rounds landed here. The first three gated on HOW someone logged in
// (driverModePin, then the driverMode/PIN/public triple) and each time a door
// was left open — a ?access=TOKEN link, then a restricted SPA member who sets
// none of those flags. Worse, `!ctx?.jobs` was an unconditional trust escape:
// app.js openPersonalExpense and dispatch.js openDriverRecordExpenseForPerson
// both hand in DispatchState.jobs, the full unfiltered tenant list.
//
// Restriction is a property of the PERSON — exactly what jobDayVisibleToCurrentDriver
// already encodes: tenant `personalizedSchedule`, or that person's
// `showAllJobs:false`. Someone with no resolved personId (office/admin) is never
// restricted, so this is safe to apply to EVERY caller with no trust escape,
// and an admin's list is never truncated to "jobs that happen to have a jobDay".
//
// Used by BOTH the picker and the submit path. Submit must re-derive rather than
// trust the posted id, or DOM tampering re-opens the leak (Codex, v4).
function _expenseVisibleJobs(ctx) {
  const people = ctx?.people || JobBoardState.people || [];
  const personId = getActiveJobBoardPersonId();
  const person = personId ? people.find(p => p && p.id === personId) : null;
  const restricted = !!personId
    && (window.AppState?.settings?.personalizedSchedule === true || person?.showAllJobs === false);
  const jobs = (ctx?.jobs || JobBoardState.jobs || []).filter(j => j && j.id);
  if (!restricted) return jobs;
  // Call the SHARED helper directly with this context's person source. The
  // jobboard wrapper resolves driverPerson from JobBoardState.people, which is
  // empty on the dispatch/SPA routes — so going through it would re-introduce
  // the same "resolves to unrestricted" hole one layer down (Codex v6).
  const visibleJobIds = new Set(
    (ctx?.jobDays || JobBoardState.jobDays || [])
      .filter(jd => {
        try {
          return _sharedJobDayVisible(jd, {
            settings: window.AppState?.settings,
            driverPersonId: personId,
            driverPerson: person,
          });
        } catch (_) { return false; }
      })
      .map(jd => jd.jobId)
      .filter(Boolean)
  );
  // A restricted person whose jobDays haven't loaded gets an empty set and no
  // Job field. Deliberate direction to fail — Job is OPTIONAL so the expense
  // still saves exactly as today; showing the fleet until scoping loads IS the
  // leak. Unrestricted users never reach this branch.
  return jobs.filter(j => visibleJobIds.has(j.id));
}

function jobDayVisibleToCurrentDriver(jobDay, personIdOverride) {
  // TASK-355 2026-05-22 — accept optional personIdOverride so future callers
  // (e.g. multi-viewer reports) can pin a specific identity without mutating
  // global AppState. Default path reads PIN -> SPA member via the resolver.
  const driverPersonId = personIdOverride || getActiveJobBoardPersonId();
  const driverPerson = driverPersonId
    ? JobBoardState.people.find(p => p.id === driverPersonId)
    : null;
  return _sharedJobDayVisible(jobDay, {
    settings: window.AppState?.settings,
    driverPersonId,
    driverPerson,
  });
}

function getJobsForDate(dateStr) {
  const results = [];

  for (const jobDay of JobBoardState.jobDays) {
    // date field is a Firestore Timestamp — convert to ISO string for comparison
    const jobDayDate = jobDay.date?.toDate ? formatDateISO(jobDay.date.toDate()) : jobDay.date;
    if (jobDayDate === dateStr) {
      if (!jobDayVisibleToCurrentDriver(jobDay)) continue;
      const job = JobBoardState.jobs.find(j => j.id === jobDay.jobId);
      if (job && job.status !== 'cancelled') {
        results.push({ job, jobDay });
      }
    }
  }

  // Sort by start time
  results.sort((a, b) => {
    const timeA = a.jobDay.startTime || a.job.defaultStart || a.job.startTime || '99:99';
    const timeB = b.jobDay.startTime || b.job.defaultStart || b.job.startTime || '99:99';
    return timeA.localeCompare(timeB);
  });

  return results;
}

// TASK-9694 S6a — the job board's customer reads go through the directory
// (window.CustomerSourceCore, utils/customerSource.js; read at call time).
function _jbCustomerSource() {
  return window.CustomerSourceCore.create({ db, collection, doc, getDoc, getDocs, onSnapshot, query, orderBy });
}

function getCustomerName(customerId) {
  if (!customerId) return 'No Customer';
  const customer = JobBoardState.customers.find(c => c.id === customerId);
  return customer?.name || customer?.company || 'Unknown Customer';
}

function getPersonName(ref) {
  if (!ref) return null;
  const r = String(ref);
  const person = JobBoardState.people.find(p => p.id === r || String(p.id) === r || p.name === r);
  return person?.name || null;
}

function getTruckUnit(ref) {
  if (!ref) return null;
  const r = String(ref);
  const truck = _resolveFleetRef(r, JobBoardState.trucks);
  return truck ? (window.getUnitDisplay ? window.getUnitDisplay(truck) : (truck.unit || truck.name)) : null;
}

function getTrailerUnit(ref) {
  if (!ref) return null;
  const r = String(ref);
  const trailer = _resolveFleetRef(r, JobBoardState.trailers);
  return trailer ? (window.getUnitDisplay ? window.getUnitDisplay(trailer) : (trailer.unit || trailer.name)) : null;
}

// Start JSA - navigate to pretrip with JSA mode
function startJSA(jobId, jobDayId) {
  // TASK-794 — fixed: there is no window.startJSA / PretripModule.startJSA; the
  // real entry is window.JSAModule.showNewJSA(jobId, jobDayId). Navigate to the
  // Inspections/JSA tab first (that's where the JSA modal + JsaState live), then
  // open a new JSA pre-linked to this job (jobId/jobDayId optional — a bare call
  // from the toolbar still opens a blank JSA).
  // TASK-9511 — remember where we came from so Cancel on the JSA form brings
  // the user back to THIS job, not to Inspections. Only when a job is known.
  // TASK-9694 (COD UX) — leaving the job screen for the JSA form closes the
  // screen, and with it the inline ticket. Save what the driver typed first
  // (the same Save as the button), then go — never silently drop the notes.
  const _inlineEd = document.getElementById('clockout-modal');
  if (_inlineEd && _inlineEd.dataset.btInline === '1' && _inlineEd.dataset.jsaSaving !== '1') {
    _inlineEd.dataset.jsaSaving = '1';
    Promise.resolve().then(() => jbSaveTicket()).catch((e) => console.warn('[JobBoard] save before JSA failed:', e?.message || e))
      .finally(() => startJSA(jobId, jobDayId));
    return;
  }
  const _returnTo = jobId ? { hash: window.location.hash || '', jobId: String(jobId), jobDayId: jobDayId ? String(jobDayId) : '', surface: 'jobboard' } : null;
  closeJobDetails?.();
  // TASK-10011 — the shared opener (also used by the office Job Details) waits
  // for the JSA view's first jobs/templates snapshots, so the first form from a
  // job is prefilled and shows the company's template. Fallback below = the
  // pre-10011 path, kept for a bundle where jsa.js predates startForJob.
  if (typeof window.JSAModule?.startForJob === 'function') {
    window.JSAModule.startForJob(jobId ? String(jobId) : '', jobDayId ? String(jobDayId) : '', { returnTo: _returnTo });
    return;
  }
  window.location.hash = '#/pretrip';
  let _tries = 0;
  const _open = () => {
    if (window.JSAModule?.showNewJSA) {
      // Ensure the JSA tab is the active panel so #modal-jsa exists.
      if (window.PretripModule?.switchDvirTab) {
        try { window.PretripModule.switchDvirTab('jsa'); } catch (_) {}
      }
      window.JSAModule.showNewJSA(jobId, jobDayId, { returnTo: _returnTo });
      return;
    }
    if (_tries++ < 20) setTimeout(_open, 150); // JSA module loads with pretrip view
  };
  setTimeout(_open, 200);
}

// TASK-794 — resolve the existing (non-dismissed) jsaReport id for a job day,
// preferring an exact jobDay match, then the most-recent for the job.
function _jbJsaIdForJob(job, jobDay) {
  if (jobDay && JobBoardState.jsaByJobDay?.[jobDay.id]) return JobBoardState.jsaByJobDay[jobDay.id].id;
  if (job && JobBoardState.jsaByJob?.[job.id]) return JobBoardState.jsaByJob[job.id].id;
  return null;
}

// ============================================
// TASK-9370 — "Drivers can add and edit jobs": the Job Board entry points
// ============================================
// Both doors open the SAME Add/Edit Job modal (dispatch.js, driver-trimmed)
// through window.DispatchModule — never a ?v= import (Pitfall #1). The save
// itself goes to the driverSaveJob callable, which re-checks the setting and
// binds the driver from the PIN session; these gates only decide whether to
// SHOW a door, they are not the enforcement.
//
// Gate = tenant setting ON + a resolved board identity + not a read-only
// share link + a driver-PIN session. The last leg is deliberate: the callable
// authorizes via driverSessions/{uid}, which only a PIN login creates. An SPA
// member with a personId already has the dispatch module's own Add Job (or
// does not hold dispatch, in which case the setting is not theirs to use) —
// showing them this door would end in a permission-denied toast.
//
// TASK-9710 — plus the per-driver opt-out: the office can switch ONE driver off
// from their Roster card (settings.driverJobEdit.blockedPersonIds). This is the
// ONE gate the Add Job strip, the job-details Edit button and the TASK-9694 job
// screen all read, so a switched-off driver sees none of them. The callable
// re-checks the list against the bound session person.
function _jbDriverJobEditBlocked() {
  const list = window.AppState?.settings?.driverJobEdit?.blockedPersonIds;
  const pid = getActiveJobBoardPersonId();
  return !!pid && Array.isArray(list) && list.includes(pid);
}
function _jbDriverJobEditAllowed() {
  return window.AppState?.settings?.driverJobEdit?.enabled === true
    && !!getActiveJobBoardPersonId()
    && !_jbDriverJobEditBlocked()
    && !window.isReadOnlyLinkSession?.()
    && window.AppState?.driverModePin === true
    && typeof window.DispatchModule?.driverOpenAddJob === 'function';
}

function _jbRenderDriverAddJobStrip() {
  const el = document.getElementById('jb-driver-addjob-strip');
  if (!el) return;
  if (!_jbDriverJobEditAllowed()) { el.style.display = 'none'; el.innerHTML = ''; el.classList.remove('is-on'); return; }
  el.style.display = '';
  el.classList.add('is-on');
  // TASK-9561 — sized by .jb-addjob-btn (styles.css), not inline: a header
  // button in Day view, a compact chip-height button in Calendar view.
  el.innerHTML = `
    <button type="button" class="jb-addjob-btn" onclick="window.JobBoardModule.driverAddJob()" aria-label="Add Job">
      <span class="material-icons">add</span><span class="jb-addjob-lbl">Add Job</span>
    </button>`;
}

// Footer button in the driver job-details modal. Only for a job in the LIVE
// window: a calendar-month cache hit outside JobBoardState.jobs may be stale,
// and the modal's job object is what gets handed to editJob (same rule the
// clock section applies). Ids go through window.escJsAttr (sharedFormat.js) —
// this is a JS string inside an attribute, no HTML escaper is enough.
function _jbDriverEditJobButton(job, jobDay) {
  if (!job || !_jbDriverJobEditAllowed()) return '';
  if (!(JobBoardState.jobs || []).some(j => j.id === job.id)) return '';
  const esc = window.escJsAttr || ((s) => String(s == null ? '' : s));
  return `
    <div style="margin-top:16px;border-top:1px solid var(--border-light);padding-top:16px;">
      <button type="button" class="btn btn-secondary" style="width:100%;min-height:44px;display:flex;align-items:center;justify-content:center;gap:8px;"
        onclick="event.stopPropagation();window.JobBoardModule.driverEditJob('${esc(job.id)}','${esc(jobDay?.id || '')}')">
        <span class="material-icons" style="font-size:18px;">edit</span> Edit Job
      </button>
    </div>`;
}

function driverAddJob() {
  if (jbBlockIfReadOnlyLink('add a job')) return;
  if (!_jbDriverJobEditAllowed()) {
    window.showToast?.(_jbDriverJobEditBlocked() ? 'Adding jobs from the driver board is turned off for you. Ask dispatch.' : 'Adding jobs from the driver board is turned off for your company.', 'info');
    return;
  }
  // Default the new job to the day the driver is looking at, like a calendar click.
  const d = JobBoardState.currentDate instanceof Date && !isNaN(JobBoardState.currentDate) ? JobBoardState.currentDate : new Date();
  const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  window.DispatchModule.driverOpenAddJob(ymd);
}

function driverEditJob(jobId, jobDayId) {
  if (jbBlockIfReadOnlyLink('edit a job')) return;
  if (!_jbDriverJobEditAllowed()) {
    window.showToast?.(_jbDriverJobEditBlocked() ? 'Editing jobs from the driver board is turned off for you. Ask dispatch.' : 'Editing jobs from the driver board is turned off for your company.', 'info');
    return;
  }
  const job = (JobBoardState.jobs || []).find(j => j.id === jobId);
  if (!job) { window.showToast?.('That job could not be loaded. Try refreshing the page.', 'error'); return; }
  const jobDay = jobDayId ? (JobBoardState.jobDays || []).find(jd => jd.id === jobDayId) || null : null;
  closeJobDetails();
  window.DispatchModule.driverOpenEditJob(job, jobDay);
}

// TASK-9371 — one row's worth of a jsaReport for the job-modal list. Reads
// only the fields the list needs; never the signature images.
function _jbJsaSummary(id, data) {
  const d = data || {};
  const isXto = d.formMode === 'xto';
  const isTemplate = d.formMode === 'template';
  // TASK-4839: a template sheet's sign-ins live in a subcollection; the parent
  // carries the server-maintained signInCount.
  const crewSigs = isXto
    ? ((d.jsaIssuer?.signature ? 1 : 0) + (d.jsaReceiver?.signature ? 1 : 0))
    : isTemplate
      ? (Number(d.signInCount) || 0)
      : (Array.isArray(d.crewAcknowledgments) ? d.crewAcknowledgments.filter(a => a && a.signature).length : 0);
  const createdMs = d.createdAt?.toMillis?.() ?? d.createdAt?.toDate?.()?.getTime?.() ?? d.date?.toMillis?.() ?? 0;
  return {
    id,
    jobId: d.jobId || '',
    jobDayId: d.jobDayId || '',
    formMode: isXto ? 'xto' : (isTemplate ? 'template' : 'tits'),
    templateName: isTemplate ? String(d.templateName || '') : '',
    createdMs,
    submittedByPersonId: d.submittedByPersonId || '',
    submittedByName: d.submittedByPersonName || d.completedByName || '',
    signatureCount: crewSigs + (Number(d.externalSignersCount) || 0),
    revisionCount: Number(d.revisionCount) || 0,
    signaturesStale: d.signaturesStale === true,
  };
}

// TASK-9371 — every non-dismissed JSA for this modal: the jobDay's rows when
// it has any, else the job's rows (the pre-existing jobDay→job fallback, now
// for a list). Unions the live subscription with the one-shot job fetch taken
// on modal open, de-duplicated by id, newest first.
function _jbJsaListFor(job, jobDay) {
  if (!job) return { rows: [], fromJob: false };
  const seen = new Set();
  const all = [];
  const push = (s) => { if (s && s.id && !seen.has(s.id)) { seen.add(s.id); all.push(s); } };
  (JobBoardState.jsaListByJob?.[job.id] || []).forEach(push);
  (JobBoardState.jsaJobFetch?.[job.id] || []).forEach(push);
  if (jobDay) (JobBoardState.jsaListByJobDay?.[jobDay.id] || []).forEach(push);
  all.sort((a, b) => (b.createdMs || 0) - (a.createdMs || 0));
  if (jobDay) {
    const dayRows = all.filter(s => s.jobDayId === jobDay.id);
    if (dayRows.length) return { rows: dayRows, fromJob: false };
  }
  return { rows: all.filter(s => s.jobId === job.id), fromJob: !!jobDay };
}

// TASK-9371 — one-shot job-scoped read (rules: jsaReports read = canReadTenant,
// members AND driver-PIN sessions). Fills JobBoardState.jsaJobFetch[jobId] and
// re-renders the open modal's JSA slot. Never throws into the modal.
async function _jbFetchJobJsas(jobId) {
  const tenantId = JobBoardState.tenantId;
  if (!tenantId || !jobId) return;
  try {
    const snap = await getDocs(query(collection(db, 'tenants', tenantId, 'jsaReports'), where('jobId', '==', jobId)));
    JobBoardState.jsaJobFetch[jobId] = snap.docs
      .filter(d => d.data().dismissed !== true)
      .map(d => _jbJsaSummary(d.id, d.data()));
    refreshOpenJobDetailJsaSection();
  } catch (e) {
    console.warn('[JobBoard] job JSA fetch failed:', e?.message);
  }
}

// TASK-794 → TASK-9371 — JSA section inside the job detail modal (the
// calendar-native surface Guy's crew lives in). Lists EVERY JSA on this job
// day (newest first) with time · format · submitted-by · signature count ·
// "Revised ×N" / "Re-sign needed" badges. Per-row CTA keeps the security split:
// dispatcher/admin get "Get Signatures" (token link + QR); driver-PIN crew get
// "Open to sign" (their OWN attributable signature). "Edit" shows for members
// on every row and for a driver-PIN session ONLY on their own JSA (the rules'
// driver arm is own-JSA only). Under the list there is ALWAYS a button: "Start
// JSA" (primary) when empty, "Add another JSA" (secondary) otherwise.
// Driver screens STACK the row buttons (UX Lead: never side-by-side).
function _jbJobJsaSection(job, jobDay, opts) {
  if (!job) return '';
  const isPinDriver = window.AppState?.driverModePin === true;
  const esc = (s) => window.escJsAttr(s);
  const { rows, fromJob } = _jbJsaListFor(job, jobDay);
  const viewerPersonId = getActiveJobBoardPersonId();

  const addBtn = rows.length
    ? `<button class="btn btn-secondary" style="width:100%;display:flex;align-items:center;gap:8px;justify-content:center;min-height:44px;" onclick="event.stopPropagation();window.JobBoardModule.startJSA('${esc(job.id)}','${esc(jobDay?.id || '')}')">
         <span class="material-icons">add</span> Add another JSA
       </button>`
    : `<button class="btn btn-primary" style="width:100%;display:flex;align-items:center;gap:8px;justify-content:center;min-height:44px;" onclick="event.stopPropagation();window.JobBoardModule.startJSA('${esc(job.id)}','${esc(jobDay?.id || '')}')">
         <span class="material-icons">add</span> Start JSA
       </button>`;

  const rowsHtml = rows.map(s => {
    const when = s.createdMs ? new Date(s.createdMs) : null;
    const sameDay = when && jobDay?.date ? (formatDateISO(when) === formatDateISO(jobDay.date?.toDate ? jobDay.date.toDate() : new Date(jobDay.date))) : true;
    const timeStr = when
      ? (sameDay ? when.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                 : when.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }))
      : '';
    const kind = s.formMode === 'xto' ? 'Oilfield JSA' : (s.formMode === 'template' ? escapeHtml(s.templateName || 'JSA') : 'JSA');
    const sigs = `${s.signatureCount} ${s.signatureCount === 1 ? 'signature' : 'signatures'}`;
    const badges = [
      s.revisionCount > 0 ? `<span style="background:#FEF3C7;color:#92400E;font-size:11px;padding:2px 8px;border-radius:999px;font-weight:600;">Revised ×${s.revisionCount}</span>` : '',
      s.signaturesStale ? `<span style="background:#FEF2F2;color:#991B1B;font-size:11px;padding:2px 8px;border-radius:999px;font-weight:600;">Re-sign needed</span>` : '',
    ].join('');
    // TASK-10024 — "Open to sign" for EVERY signed-in viewer (PIN crew and office
    // logins alike): it opens the report scrolled to the viewer's own line with a
    // working Sign button. Office keeps "Get Signatures" (token link + QR) too.
    const signCta = `<button class="btn btn-secondary jb-jsa-open-to-sign" style="${isPinDriver ? 'width:100%;' : 'flex:1 1 140px;'}display:flex;align-items:center;gap:8px;justify-content:center;min-height:44px;" onclick="event.stopPropagation();window.JobBoardModule.openJsaToSign('${esc(s.id)}')">
           <span class="material-icons" style="color:#10B981;">draw</span> Open to sign
         </button>`;
    const primaryCta = isPinDriver
      ? signCta
      : `<button class="btn btn-secondary" style="flex:1 1 140px;display:flex;align-items:center;gap:8px;justify-content:center;min-height:44px;" onclick="event.stopPropagation();window.JobBoardModule.getJsaSignatures('${esc(s.id)}')">
           <span class="material-icons" style="color:#10B981;">qr_code_2</span> Get Signatures
         </button>${s.formMode === 'xto' || s.formMode === 'template' ? '' : signCta}`;
    const canEdit = !isPinDriver || (!!viewerPersonId && s.submittedByPersonId === viewerPersonId);
    const editCta = canEdit
      ? `<button class="btn btn-secondary" style="${isPinDriver ? 'width:100%;' : ''}display:flex;align-items:center;gap:8px;justify-content:center;min-height:44px;" onclick="event.stopPropagation();window.JobBoardModule.openJsaToEdit('${esc(s.id)}')">
           <span class="material-icons">edit</span> Edit
         </button>`
      : '';
    return `
      <div class="jb-jsa-row" style="border:1px solid var(--border-light);border-radius:8px;padding:10px 12px;margin-bottom:8px;background:#fff;">
        <div style="display:flex;justify-content:space-between;gap:8px;align-items:flex-start;flex-wrap:wrap;">
          <div style="min-width:0;flex:1;">
            <div style="font-weight:600;color:var(--text-primary);">${escapeHtml(timeStr)}${timeStr ? ' · ' : ''}${kind}</div>
            <div style="font-size:0.8rem;color:var(--text-secondary);margin-top:2px;">${s.submittedByName ? escapeHtml(s.submittedByName) + ' · ' : ''}${sigs}</div>
          </div>
          ${badges ? `<div style="display:flex;gap:6px;flex-wrap:wrap;">${badges}</div>` : ''}
        </div>
        <div style="display:flex;flex-direction:${isPinDriver ? 'column' : 'row'};flex-wrap:wrap;gap:6px;margin-top:8px;">${primaryCta}${editCta}</div>
      </div>`;
  }).join('');

  return `
    <div style="margin-top:16px;border-top:1px solid var(--border-light);padding-top:16px;">
      <div style="font-weight:600;color:var(--text-primary);margin-bottom:8px;display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:18px;vertical-align:-3px;color:${rows.length ? '#10B981' : 'var(--text-secondary)'};">health_and_safety</span>
        Job Safety Analysis${rows.length ? ` <span style="font-weight:500;color:var(--text-secondary);">(${rows.length})</span>` : ''}${opts && opts.statusTag ? _jbJsaStatusTag(rows, fromJob, opts) : ''}
      </div>
      ${fromJob && rows.length ? `<div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:6px;">No JSA on this day yet — showing this job's other days.</div>` : ''}
      ${rowsHtml}
      ${addBtn}
    </div>`;
}

// TASK-9694 polish — the job screen's Safety card says where the JSA stands at a
// glance. Revisions keep their own per-row "Revised ×N" badge; a stale crew
// signature reads "Re-sign needed" (never "Done"). '' unless asked (switch OFF
// renders exactly as before).
function _jbJsaStatusTag(rows, fromJob, opts) {
  if (!opts || opts.statusTag !== true) return '';
  if (!rows.length || fromJob) return '<span class="jb-js-tag jb-js-tag-need">JSA needed</span>';
  if (rows.some(s => s && s.signaturesStale)) return '<span class="jb-js-tag jb-js-tag-need">Re-sign needed</span>';
  // COD (Code Reviewer): a JSA nobody has signed is not "Done".
  if (rows.some(s => s && Number(s.signatureCount || 0) < 1)) return '<span class="jb-js-tag jb-js-tag-need">Signatures needed</span>';
  return '<span class="jb-js-tag jb-js-tag-done">Done ✓</span>';
}

// TASK-794 — re-render the JSA section of an OPEN job detail modal (called when
// the jsaReports snapshot updates). No-op if the modal is closed.
function refreshOpenJobDetailJsaSection() {
  const slot = document.getElementById('jobboard-detail-jsa-section');
  if (!slot) return;
  const modal = document.getElementById('jobboard-detail-modal');
  const jobId = modal?.dataset?.jobId;
  const jobDayId = modal?.dataset?.jobDayId;
  if (!jobId) return;
  const job = JobBoardState.jobs.find(j => j.id === jobId);
  const jobDay = jobDayId ? JobBoardState.jobDays.find(jd => jd.id === jobDayId) : null;
  if (!job) return;
  slot.innerHTML = _jbJobJsaSection(job, jobDay, modal?.dataset?.jobscreen === '1' ? { statusTag: true } : undefined);
}

// ============================================
// TASK-9740 — driver-signed job documents: Bill of Lading / Disposal Ticket
// ============================================
// The driver fills a standard form on the phone, signs it, gets the receiver
// to sign it, and taps Submit. ONE Firestore write (setDoc through
// jbAckOrQueue, so it queues with no signal exactly like a ticket line) carries
// the field values and both signatures INLINE as PNG data URLs — nothing goes
// to Storage from the phone. The Cloud Function onJobDocumentCreate paints the
// PDF with the tenant's letterhead (the field-ticket painter), parks the
// signatures, and appends an entry to job.documents[]; the jobs snapshot then
// shows it here as a link. Field list = ../shared/job-document-spec.js (the
// server validates against the same list). Signature pads = window.ClockModule
// (clock.js), roles 'jdDriver' / 'jdReceiver' — no second pad implementation.

const JD_SIG_ROLES = { driver: 'jdDriver', receiver: 'jdReceiver' };

function _jdPendingFor(jobId) {
  if (!JobBoardState._jobDocsPending) JobBoardState._jobDocsPending = {};
  return JobBoardState._jobDocsPending[jobId] || [];
}

function _jdFmtWhen(iso) {
  const ms = Date.parse(iso || '');
  if (!Number.isFinite(ms)) return '';
  return new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// TASK-9740 — job.documents[] entries carry no URL any more (the PDF opens
// through getJobDocumentPdf), so nothing on this screen opens a stored URL.

// TASK-9740 — the whole feature sits behind a company setting, OFF by default
// (dispatcher decision, rel2087 COD: crew-scoped filing + server-stamped
// attribution must land before any tenant turns it on). Owner/admin flips
// settings.jobDocumentsEnabled in Settings; the rules arm and
// onJobDocumentCreate both refuse while it is off — this is only the UI half.
function _jdEnabled() {
  return window.AppState?.settings?.jobDocumentsEnabled === true;
}

// TASK-9740 — a driver-PIN session is "the driver"; anyone else on this
// screen is a signed-in member (the office). Same flag submitJobDocument uses.
function _jdIsPinDriver() {
  return window.AppState?.driverModePin === true;
}

// TASK-9740 — the jobDay that lets THIS driver file on this job: they are on
// its crew (drivers or helpers) and it is within ±1 day of today. The server
// (onJobDocumentCreate) and the rules decide; this only stops the driver
// filling a form that would be refused. The day the modal was opened on wins.
function _jdLocalDayKey(v) {
  const ms = v?.toMillis ? v.toMillis() : (v?.seconds ? v.seconds * 1000 : Date.parse(v || ''));
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function _jdCrewDayFor(job, preferDay) {
  const pid = getActiveJobBoardPersonId();
  if (!job || !pid) return null;
  const today = Date.parse(_jdLocalDayKey(new Date().toISOString()) + 'T00:00:00Z');
  const ok = (d) => {
    if (!d || d.jobId !== job.id) return false;
    const onCrew = [...(Array.isArray(d.drivers) ? d.drivers : []), ...(Array.isArray(d.helpers) ? d.helpers : [])].some(r => String(r) === String(pid));
    const k = _jdLocalDayKey(d.date);
    return onCrew && !!k && Math.abs(Date.parse(k + 'T00:00:00Z') - today) <= 864e5;
  };
  if (ok(preferDay)) return preferDay;
  return (JobBoardState.jobDays || []).find(ok) || null;
}

// TASK-9740 harvest 10/13 (Chris: Allow) — a driver SWAPPED onto the day but
// not on the job's crew files against their OWN clock entry on this job. The
// board's clockEntries subscription is personId-scoped and active-only, so
// this finds the shift the driver is on right now; the rules (jdClockOk) and
// onJobDocumentCreate (pickClockEntry, ±1 calendar day) decide.
function _jdMyClockEntryOn(job) {
  const pid = getActiveJobBoardPersonId();
  if (!job || !pid) return null;
  return (JobBoardState.clockEntries || []).find(e => e && e.personId === pid && e.jobId === job.id && e.status === 'active') || null;
}

// TASK-9809 dispatcher call 10/13 — a swapped-in driver who already clocked
// OUT can still file: the driver's OWN clock entries on this job (personId
// scoped — the only clockEntries read a PIN session is allowed), clocked in
// within ±1 local day. Same set the rules (jdClockOk) and the trigger
// (pickClockEntry) already accept; this only lets the phone find it.
async function _jdMyRecentClockEntryOn(job) {
  const pid = getActiveJobBoardPersonId();
  const tid = JobBoardState.tenantId;
  if (!job || !pid || !tid) return null;
  try {
    const snap = await getDocs(query(collection(db, 'tenants', tid, 'clockEntries'), where('personId', '==', pid), where('jobId', '==', job.id)));
    const today = Date.parse(_jdLocalDayKey(new Date().toISOString()) + 'T00:00:00Z');
    let best = null;
    snap.forEach((d) => {
      const k = Date.parse(_jdLocalDayKey(d.data()?.clockIn) + 'T00:00:00Z');
      if (!Number.isFinite(k) || Math.abs(k - today) > 864e5) return;
      if (!best || Math.abs(k - today) < best.gap) best = { id: d.id, gap: Math.abs(k - today), ...d.data() };
    });
    return best;
  } catch (e) {
    console.warn('[JobBoard] recent clock entry lookup failed', e?.code || e);
    return null;
  }
}

// TASK-9740 harvest 10/13 — RELAY (Chris: Allow): the day keys (UTC-midnight
// ms of the local date) of every crew day of this job carrying THIS driver
// within ±1 day of today. The Documents list also shows the OTHER drivers'
// Bills of Lading dated within a day of one of them; getJobDocumentPdf
// (relayMayOpen) decides whether one actually opens. A clock entry does not
// count for reading (COD Adversary 10/13), only for filing.
function _jdRelayDayKeys(job) {
  const pid = getActiveJobBoardPersonId();
  if (!job || !pid) return [];
  const today = Date.parse(_jdLocalDayKey(new Date().toISOString()) + 'T00:00:00Z');
  const out = [];
  for (const d of (JobBoardState.jobDays || [])) {
    if (!d || d.jobId !== job.id) continue;
    const onCrew = [...(Array.isArray(d.drivers) ? d.drivers : []), ...(Array.isArray(d.helpers) ? d.helpers : [])].some(r => String(r) === String(pid));
    const k = Date.parse(_jdLocalDayKey(d.date) + 'T00:00:00Z');
    if (onCrew && Number.isFinite(k) && Math.abs(k - today) <= 864e5) out.push(k);
  }
  return out;
}

// TASK-9740 — after the write reaches the server, follow the doc until the
// server says ready / refused / error, so a refused filing is TOLD to the
// driver instead of sitting as "being made" forever. The rules let the caller
// that wrote it (createdByUid) read it. A write the server rejects outright
// (rules) lands in the same place.
function _jdMarkPending(jobId, docId, patch) {
  const p = _jdPendingFor(jobId).find(x => x.id === docId);
  if (p) Object.assign(p, patch);
  refreshOpenJobDocumentsSection();
}
function _jdWatch(tenant, ref, jobId, kind) {
  let unsub = null;
  const stop = () => { try { unsub && unsub(); } catch (_) { /* gone */ } unsub = null; };
  const t = setTimeout(stop, 10 * 60 * 1000);
  try {
    unsub = onSnapshot(ref, (snap) => {
      const d = snap.exists() ? (snap.data() || {}) : null;
      if (!d || d.status === 'submitted' || !d.status) return;
      clearTimeout(t); stop();
      if (d.status === 'ready') {
        _jdMarkPending(jobId, ref.id, { status: 'ready', number: d.number || '' });
        return;
      }
      const msg = String(d.error || 'This document was not filed.');
      _jdMarkPending(jobId, ref.id, { status: 'refused', error: msg });
      window.showToast?.(`${jobDocumentKindLabel(kind)} not filed — ${msg}`, 'error', null, 10000);
    }, () => { clearTimeout(t); stop(); });
  } catch (_) { clearTimeout(t); }
}

function _jbJobDocumentsSection(job, jobDay) {
  if (!job) return '';
  if (!_jdEnabled()) return '';
  const esc = (x) => window.escJsAttr(x);
  // A driver sees only the documents they filed (the server opens nothing
  // else for them); the office sees every one.
  const me = _jdIsPinDriver() ? String(getActiveJobBoardPersonId() || '') : null;
  // Relay: another driver's BOL from within a day of this driver's own day on the job.
  const relayKeys = me ? _jdRelayDayKeys(job) : [];
  const isRelay = (d) => !!(me && relayKeys.length && d.kind === 'bol' && !d.enteredByOffice && d.driverPersonId && d.driverPersonId !== me
    && relayKeys.some(k => Math.abs(Date.parse(_jdLocalDayKey(d.createdAt) + 'T00:00:00Z') - k) <= 864e5));
  const ready = (Array.isArray(job.documents) ? job.documents : [])
    .filter(d => d && typeof d === 'object' && typeof d.id === 'string' && d.id)
    .filter(d => me === null || (me && d.driverPersonId === me) || isRelay(d))
    .slice()
    .sort((a, b) => Date.parse(b.createdAt || '') - Date.parse(a.createdAt || ''));
  const readyIds = new Set(ready.map(d => d.id));
  const pending = _jdPendingFor(job.id).filter(p => !readyIds.has(p.id));
  const count = ready.length + pending.filter(p => p.status !== 'refused').length;

  const rowsHtml = pending.map(p => p.status === 'refused' ? `
      <div style="border:1px solid #FECACA;border-radius:8px;padding:10px 12px;margin-bottom:8px;background:#FEF2F2;display:flex;align-items:flex-start;gap:10px;" data-jd-refused="${escapeHtml(p.id)}">
        <span class="material-icons" style="color:#DC2626;">error_outline</span>
        <div style="min-width:0;flex:1;">
          <div style="font-weight:600;color:#991B1B;">${escapeHtml(jobDocumentKindLabel(p.kind))} not filed</div>
          <div style="font-size:0.85rem;color:#7F1D1D;">${escapeHtml(p.error || 'This document was not filed.')}</div>
        </div>
      </div>` : `
      <div style="border:1px dashed #cbd5e1;border-radius:8px;padding:10px 12px;margin-bottom:8px;background:#f8fafc;display:flex;align-items:center;gap:10px;">
        <span class="material-icons" style="color:#f59e0b;">${p.queued ? 'cloud_off' : 'hourglass_top'}</span>
        <div style="min-width:0;flex:1;">
          <div style="font-weight:600;color:var(--text-primary);">${escapeHtml(jobDocumentKindLabel(p.kind))}${p.number ? ' ' + escapeHtml(p.number) : ''}</div>
          <div style="font-size:0.8rem;color:var(--text-secondary);">${p.queued ? 'Saved on this phone — sends when you have signal' : (p.status === 'ready' ? 'Filed — the signed PDF is being added to the job' : 'Submitted — the signed PDF is being made')}</div>
        </div>
      </div>`).join('')
    + ready.map(d => `
      <button type="button" class="btn btn-secondary" style="width:100%;margin-bottom:8px;text-align:left;display:flex;align-items:center;gap:10px;min-height:48px;"
        onclick="event.stopPropagation();window.JobBoardModule.openJobDocumentPdf('${esc(job.id)}','${esc(d.id)}')">
        <span class="material-icons" style="color:#DC2626;">picture_as_pdf</span>
        <span style="min-width:0;flex:1;">
          <span style="display:block;font-weight:600;">${escapeHtml(d.name || jobDocumentKindLabel(d.kind) || 'Document')}</span>
          <span style="display:block;font-size:0.8rem;color:var(--text-secondary);font-weight:400;">${escapeHtml([d.driverName ? (d.enteredByOffice ? d.driverName + ' (office)' : d.driverName) : '', _jdFmtWhen(d.createdAt), me && d.driverPersonId !== me ? 'previous driver on this load' : ''].filter(Boolean).join(' · '))}</span>
        </span>
        <span class="material-icons" style="color:#10B981;font-size:18px;">verified</span>
      </button>`).join('');

  const btn = (kind, primary) => `
      <button type="button" class="btn ${primary ? 'btn-primary' : 'btn-secondary'}" style="width:100%;display:flex;align-items:center;gap:8px;justify-content:center;min-height:44px;margin-top:6px;"
        onclick="event.stopPropagation();window.JobBoardModule.openJobDocumentForm('${esc(job.id)}','${esc(jobDay?.id || '')}','${kind}')">
        <span class="material-icons">add</span> New ${escapeHtml(jobDocumentKindLabel(kind))}
      </button>`;

  return `
    <div style="margin-top:16px;border-top:1px solid var(--border-light);padding-top:16px;">
      <div style="font-weight:600;color:var(--text-primary);margin-bottom:8px;display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:18px;vertical-align:-3px;color:${ready.length ? '#10B981' : 'var(--text-secondary)'};">assignment</span>
        Documents${count ? ` <span style="font-weight:500;color:var(--text-secondary);">(${count})</span>` : ''}
      </div>
      ${rowsHtml || `<div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:6px;">Bill of lading and disposal tickets signed on this job show here.</div>`}
      ${btn('bol', !ready.length && !pending.length)}
      ${btn('disposal', false)}
    </div>`;
}

// Re-render the Documents slot of an OPEN job modal (jobs snapshot / submit).
function refreshOpenJobDocumentsSection() {
  const slot = document.getElementById('jobboard-detail-documents-section');
  if (!slot) return;
  const modal = document.getElementById('jobboard-detail-modal');
  const jobId = modal?.dataset?.jobId;
  if (!jobId) return;
  const job = _findJobAnywhere(jobId);
  const jobDay = modal?.dataset?.jobDayId ? _findJobDayAnywhere(modal.dataset.jobDayId) : null;
  if (!job) return;
  slot.innerHTML = _jbJobDocumentsSection(job, jobDay);
}

// PWA rule (memory/lessons): a link that leaves the DD screen opens in a new
// window, never in-app with no way back.
// TASK-9740 — the PDF has no download token and the job entry no URL: bytes
// come from getJobDocumentPdf, which opens a signed BOL only for the office
// (dispatch / billing module, owner) and the driver who filed it. Same Blob
// pattern as My Documents (hr.js _driverMyDocOpen). Shared with dispatch.js's
// Edit Job list through window.JobBoardModule (Pitfall #1).
let _jdOpening = false;
async function openJobDocumentPdf(jobId, docId) {
  if (_jdOpening) return;
  if (!docId) { window.showToast?.('That document is not available yet', 'info'); return; }
  _jdOpening = true;
  try {
    window.showToast?.('Opening document…', 'info');
    const res = await httpsCallable(functions, 'getJobDocumentPdf')({ docId: String(docId) });
    const data = res?.data || {};
    if (!(data.ok && data.base64)) throw new Error('That document is not available yet');
    const bin = atob(data.base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([arr], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    console.error('[JobBoard] open job document failed', e);
    window.showToast?.(e?.message || 'Could not open the document', 'error');
  } finally {
    _jdOpening = false;
  }
}

function _jdSigPadHtml(role, label, allowNA) {
  const btnStyle = 'font-size:0.8rem;font-weight:600;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#334155;padding:6px 12px;cursor:pointer;min-height:36px;';
  return `
    <label style="font-size:0.75rem;font-weight:600;color:#64748b;display:block;margin-bottom:6px;">${escapeHtml(label)}</label>
    <div class="sig-canvas-wrap" id="sig-wrap-${role}">
      <canvas id="sig-canvas-${role}"></canvas>
      <span class="sig-placeholder">Sign here</span>
    </div>
    <div style="margin-top:6px;display:flex;gap:6px;">
      <button type="button" onclick="window.ClockModule.clearSignature('${role}')" style="${btnStyle}">Clear</button>
      ${allowNA ? `<button type="button" id="btn-sig-na-${role}" onclick="window.ClockModule.markSignatureNA('${role}')" style="${btnStyle}">Not Available</button>` : ''}
    </div>`;
}

function _jdFieldHtml(f, value) {
  const id = 'jd-f-' + f.id;
  const req = f.required ? ' <span style="color:#DC2626;">*</span>' : '';
  const hint = f.hint ? `<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">${escapeHtml(f.hint)}</div>` : '';
  const v = escapeHtml(value == null ? '' : String(value));
  let input;
  if (f.type === 'textarea') {
    input = `<textarea id="${id}" rows="2" maxlength="1000" style="width:100%;min-height:64px;">${v}</textarea>`;
  } else if (f.type === 'select') {
    input = `<select id="${id}" style="width:100%;min-height:44px;"><option value="">Select…</option>${(f.options || []).map(o => `<option value="${escapeHtml(o)}"${o === value ? ' selected' : ''}>${escapeHtml(o)}</option>`).join('')}</select>`;
  } else if (f.type === 'number') {
    input = `<input type="text" inputmode="decimal" id="${id}" value="${v}" maxlength="14" placeholder="0" style="width:100%;min-height:44px;">`;
  } else {
    input = `<input type="text" id="${id}" value="${v}" maxlength="1000" style="width:100%;min-height:44px;">`;
  }
  return `<div class="form-group" style="margin-bottom:12px;"><label for="${id}" style="font-weight:600;">${escapeHtml(f.label)}${req}</label>${input}${hint}</div>`;
}

function closeJobDocumentForm() {
  const m = document.getElementById('jb-jobdoc-modal');
  if (m) m.remove();
  if (!document.querySelector('.modal:not(.hidden)')) document.body.classList.remove('modal-open');
}

// Open the form. Prefills come from the job / day the driver is on and every
// one of them is editable: the customer as shipper or generator, the job
// location as the delivery (BOL) or pickup (disposal), the trailer as the
// container id, the job's PO.
// COD 10/13 — the open is async now (a swapped-in driver's clock-entry
// lookup); a second tap while it runs is ignored.
let _jdFormOpening = false;
async function openJobDocumentForm(jobId, jobDayId, kind) {
  if (_jdFormOpening) return;
  _jdFormOpening = true;
  try { return await _jdOpenJobDocumentForm(jobId, jobDayId, kind); } finally { _jdFormOpening = false; }
}
async function _jdOpenJobDocumentForm(jobId, jobDayId, kind) {
  if (!_jdEnabled()) { window.showToast?.('Bills of lading and disposal tickets are turned off for this company', 'info'); return; }
  const job = _findJobAnywhere(jobId);
  if (!job) { window.showToast?.('Job not found', 'error'); return; }
  const fields = jobDocumentFieldsFor(kind);
  if (!fields.length) { window.showToast?.('Unknown document type', 'error'); return; }
  let jobDay = jobDayId ? _findJobDayAnywhere(jobDayId) : null;
  // TASK-9740 — a driver files only on a job they are crewed on, today ±1 day,
  // or (harvest 10/13) one they are clocked in on after a swap.
  let clockEntry = null;
  if (_jdIsPinDriver()) {
    jobDay = _jdCrewDayFor(job, jobDay);
    if (!jobDay) clockEntry = _jdMyClockEntryOn(job) || await _jdMyRecentClockEntryOn(job);
    if (!jobDay && !clockEntry) { window.showToast?.('You can file this only on a job you are on today or clocked in on. Clock in on the job, or ask dispatch to put you on it.', 'error', null, 8000); return; }
  }
  const hazmatJob = job.hazmat === true;
  const jobPo = String(job.poNumber || '');
  const customerName = getCustomerName(job.customerId);
  const custLabel = customerName === 'No Customer' || customerName === 'Unknown Customer' ? '' : customerName;
  const driverName = getActiveJobBoardPersonName() || '';
  const vehicleUnit = (jobDay?.trucks || []).map(r => getTruckUnit(r)).filter(Boolean)[0] || '';
  const trailerUnit = (jobDay?.trailers || []).map(r => getTrailerUnit(r)).filter(Boolean)[0] || '';
  const title = jobDocumentKindLabel(kind);

  const prefill = {
    shipper: custLabel, generator: custLabel,
    delivery: kind === 'bol' ? (job.location || '') : '',
    pickup: kind === 'disposal' ? (job.location || '') : '',
    containerId: trailerUnit,
    poNumber: job.poNumber || '',
  };

  closeJobDocumentForm();
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'jb-jobdoc-modal';
  modal.dataset.jobId = job.id;
  modal.dataset.jobDayId = jobDay?.id || '';
  modal.dataset.clockEntryId = clockEntry?.id || '';
  modal.dataset.kind = kind;
  modal.innerHTML = `
    <div class="modal-content modal-fullscreen" style="max-width:560px;display:flex;flex-direction:column;height:100vh;height:100dvh;">
      <div class="modal-header" style="flex-shrink:0;">
        <h3><span class="material-icons" style="vertical-align:middle;">assignment</span> New ${escapeHtml(title)}</h3>
        <button type="button" class="modal-close" onclick="window.JobBoardModule.closeJobDocumentForm()" aria-label="Close">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:14px;">
        <div style="background:#f8fafc;border:1px solid var(--border-light);border-radius:10px;padding:10px 12px;margin-bottom:14px;font-size:0.85rem;color:var(--text-primary);">
          <div style="font-weight:600;margin-bottom:4px;">On this document</div>
          ${custLabel ? `<div><b>Customer</b> ${escapeHtml(custLabel)}</div>` : ''}
          <div><b>Job</b> ${escapeHtml(job.title || job.name || '')}</div>
          <div><b>Date</b> ${escapeHtml(new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }))}</div>
          ${driverName ? `<div><b>Driver</b> ${escapeHtml(driverName)}</div>` : ''}
          ${clockEntry ? `<div style="color:var(--text-secondary);">You are not on this job's crew — filing because you are clocked in on it.</div>` : ''}
          ${vehicleUnit ? `<div><b>Vehicle</b> ${escapeHtml(vehicleUnit)}</div>` : ''}
          ${trailerUnit ? `<div><b>Trailer</b> ${escapeHtml(trailerUnit)}</div>` : ''}
        </div>
        ${fields.filter(f => !f.hazmat).map(f => _jdFieldHtml(f, prefill[f.id] || '') + (f.id === 'poNumber' ? `<div id="jd-po-warn" style="display:none;margin:-6px 0 12px;color:#92400E;background:#FEF3C7;border:1px solid #FDE68A;border-radius:8px;padding:8px 10px;font-size:0.85rem;"></div>` : '')).join('')}
        ${fields.some(f => f.hazmat) ? `
        <div style="border-top:1px solid var(--border-light);margin-top:6px;padding-top:12px;" data-jd-hazmat="${hazmatJob ? 'required' : 'optional'}">
          ${hazmatJob ? `<div style="font-weight:600;margin-bottom:4px;">Hazardous materials <span style="color:#DC2626;">*</span></div>
          <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:10px;">This job is marked hazmat — fill every line (49 CFR 172.202).</div>` : `
          <label for="jd-hazmat-declared" style="display:flex;align-items:center;gap:10px;font-weight:600;min-height:44px;cursor:pointer;">
            <input type="checkbox" id="jd-hazmat-declared" style="width:22px;height:22px;margin:0;"> Is this load hazmat?
          </label>
          <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:10px;">Tick it if the shipper's paper shows hazardous materials. The office is told you marked it.</div>`}
          <div id="jd-hazmat-block" style="${hazmatJob ? '' : 'display:none;'}">
          ${fields.filter(f => f.hazmat).map(f => _jdFieldHtml({ ...f, required: true }, prefill[f.id] || '')).join('')}
          </div>
        </div>` : ''}
        <div style="border-top:1px solid var(--border-light);margin-top:6px;padding-top:12px;">
          <div style="font-weight:600;margin-bottom:8px;">Signatures</div>
          <div id="jd-sig-slot-driver" style="margin-bottom:14px;">${_jdSigPadHtml(JD_SIG_ROLES.driver, 'Driver signature' + (driverName ? ' — ' + driverName : ''), false)}</div>
          <div class="form-group" style="margin-bottom:8px;">
            <label for="jd-receiver-name" style="font-weight:600;">${kind === 'disposal' ? 'Facility representative' : 'Receiver / consignee'} (printed name)</label>
            <input type="text" id="jd-receiver-name" maxlength="120" style="width:100%;min-height:44px;" placeholder="Who signed for it">
          </div>
          <div id="jd-sig-slot-receiver" style="margin-bottom:8px;">${_jdSigPadHtml(JD_SIG_ROLES.receiver, (kind === 'disposal' ? 'Facility representative' : 'Receiver / consignee') + ' signature', true)}</div>
        </div>
        <div id="jd-form-error" style="display:none;color:#991B1B;background:#FEF2F2;border:1px solid #FECACA;border-radius:8px;padding:8px 10px;font-size:0.85rem;margin-top:8px;"></div>
      </div>
      <div class="form-actions" style="flex-shrink:0;display:flex;gap:8px;padding:12px 14px;border-top:1px solid var(--border-light);background:#fff;">
        <button type="button" class="btn btn-secondary" style="flex:1;min-height:48px;" onclick="window.JobBoardModule.closeJobDocumentForm()">Cancel</button>
        <button type="button" id="jd-submit-btn" class="btn btn-primary" style="flex:2;min-height:48px;display:flex;align-items:center;justify-content:center;gap:8px;" onclick="window.JobBoardModule.submitJobDocument()">
          <span class="material-icons">check_circle</span> Submit
        </button>
      </div>
    </div>`;
  modal.addEventListener('click', (e) => { if (e.target === modal) closeJobDocumentForm(); });
  document.body.appendChild(modal);
  document.body.classList.add('modal-open');
  // TASK-9740 harvest 10/13 — the job's PO stays the PO. A different one the
  // driver types is kept as a second, driver-entered PO the office checks.
  // TASK-9809 dispatcher call 10/13 — on a job NOT marked hazmat, the driver
  // can declare the load hazmat; the block opens and becomes required.
  const hzBox = modal.querySelector('#jd-hazmat-declared');
  const hzBlock = modal.querySelector('#jd-hazmat-block');
  if (hzBox && hzBlock) hzBox.addEventListener('change', () => { hzBlock.style.display = hzBox.checked ? '' : 'none'; });
  const poEl = modal.querySelector('#jd-f-poNumber');
  const poWarn = modal.querySelector('#jd-po-warn');
  if (poEl && poWarn) {
    const sync = () => {
      const other = jobDocumentDriverPo(jobPo, poEl.value);
      poWarn.style.display = other ? '' : 'none';
      poWarn.textContent = other
        ? (jobPo ? `Not the job's PO (${jobPo}). The job PO stays; ${other} is saved as a driver-entered PO and the office is asked to check it.` : `This job has no PO. ${other} is saved as a driver-entered PO and the office is asked to check it.`)
        : '';
    };
    poEl.addEventListener('input', sync);
    sync();
  }
  const body = modal.querySelector('.modal-body');
  if (body) body.scrollTop = 0;
  // The pads need layout before they can size their canvas.
  setTimeout(() => {
    window.ClockModule?.initSignaturePad?.(JD_SIG_ROLES.driver);
    window.ClockModule?.initSignaturePad?.(JD_SIG_ROLES.receiver);
  }, 40);
}

function _jdShowError(msg, fieldId) {
  const box = document.getElementById('jd-form-error');
  if (box) { box.textContent = msg; box.style.display = ''; }
  window.showToast?.(msg, 'error');
  const el = fieldId ? document.getElementById('jd-f-' + fieldId) : null;
  if (el) { el.closest?.('details')?.setAttribute('open', ''); el.style.borderColor = '#DC2626'; el.focus?.(); el.scrollIntoView?.({ block: 'center', behavior: 'smooth' }); }
}

async function submitJobDocument() {
  const modal = document.getElementById('jb-jobdoc-modal');
  if (!modal) return false;
  if (JobBoardState._jdSubmitting) return false;
  if (!_jdEnabled()) { _jdShowError('Bills of lading and disposal tickets are turned off for this company'); return false; }
  const kind = modal.dataset.kind;
  const job = _findJobAnywhere(modal.dataset.jobId);
  if (!job) { _jdShowError('Job not found'); return false; }
  let jobDay = modal.dataset.jobDayId ? _findJobDayAnywhere(modal.dataset.jobDayId) : null;
  let clockEntry = null;
  if (_jdIsPinDriver()) {
    jobDay = _jdCrewDayFor(job, jobDay);
    if (!jobDay) clockEntry = _jdMyClockEntryOn(job) || (modal.dataset.clockEntryId ? { id: modal.dataset.clockEntryId } : null);
    if (!jobDay && !clockEntry) { _jdShowError('You can file this only on a job you are on today or clocked in on. Clock in on the job, or ask dispatch to put you on it.'); return false; }
  }
  const CM = window.ClockModule;
  if (!CM?.getSignatureDataUrl) { _jdShowError('Signature pad not ready — reopen the form'); return false; }

  // Fields — the SAME validator the server runs.
  const raw = {};
  for (const f of jobDocumentFieldsFor(kind)) {
    const el = document.getElementById('jd-f-' + f.id);
    if (el) { el.style.borderColor = ''; raw[f.id] = el.value; }
  }
  const hazmatDeclared = job.hazmat !== true && document.getElementById('jd-hazmat-declared')?.checked === true;
  // COD 10/13 — unticked on a non-hazmat job = the hidden hazmat values are
  // NOT sent (a stale hidden value can neither block Submit nor print).
  if (job.hazmat !== true && !hazmatDeclared) {
    for (const f of jobDocumentFieldsFor(kind)) if (f.hazmat) delete raw[f.id];
  }
  const v = validateJobDocumentFields(kind, raw, { hazmat: job.hazmat === true || hazmatDeclared });
  if (!v.ok) { _jdShowError(v.errors[0].message, v.errors[0].id); return false; }

  // Signatures — driver required; receiver optional / Not Available.
  const driverSig = CM.getSignatureDataUrl(JD_SIG_ROLES.driver);
  if (!driverSig || driverSig === 'N/A') { _jdShowError('Driver signature is required'); document.getElementById('sig-wrap-' + JD_SIG_ROLES.driver)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }); return false; }
  const receiverSig = CM.getSignatureDataUrl(JD_SIG_ROLES.receiver);
  const receiverName = String(document.getElementById('jd-receiver-name')?.value || '').trim().slice(0, 120);
  const now = Timestamp.now();
  const receiver = (receiverSig && receiverSig !== 'N/A') ? { dataUrl: receiverSig, name: receiverName } : null;

  const tenant = JobBoardState.tenantId;
  const uid = auth.currentUser?.uid || '';
  if (!tenant || !uid) { _jdShowError('Not signed in — reopen the app and try again'); return false; }
  const isPinDriver = _jdIsPinDriver();
  // TASK-9740 — the server stamps who filed it (from this login), the date
  // (when it reaches the server), the number (server counter), the customer,
  // job, PO and equipment (from the job and the crew day). The phone sends
  // only what the driver entered: fields, signatures, the receiver's printed
  // name. driverPersonId stays because the rules pin a PIN session to it.
  const data = {
    kind,
    jobId: job.id,
    jobDayId: jobDay?.id || null,
    // TASK-9740 harvest 10/13 — swapped in: the driver's own clock entry on this job.
    ...(clockEntry ? { clockEntryId: clockEntry.id } : {}),
    // TASK-9809 — the driver ticked "Is this load hazmat?" on a job not marked hazmat.
    ...(hazmatDeclared ? { hazmatDeclared: true } : {}),
    driverPersonId: isPinDriver ? (getActiveJobBoardPersonId() || '') : '',
    fields: v.clean,
    signatures: {
      driver: { dataUrl: driverSig },
      receiver,
      receiverUnavailable: !receiver,
    },
    status: 'submitted',
    createdByUid: uid,
    createdAt: now,
    source: isPinDriver ? 'driver-pin' : 'member',
  };

  JobBoardState._jdSubmitting = true;
  const btn = document.getElementById('jd-submit-btn');
  if (btn) { btn.disabled = true; btn.style.opacity = '0.6'; }
  const ref = doc(collection(db, 'tenants', tenant, 'jobDocuments'));
  const write = setDoc(ref, data);
  try {
    // TASK-1642 contract: resolves on ack, resolves undefined when the write is
    // queued with no signal, REJECTS on a real refusal (rules) — which is the
    // one case the driver must hear about.
    await jbAckOrQueue(write, 'jobDocument:' + kind);
  } catch (e) {
    console.error('[JobBoard] job document submit failed', e);
    JobBoardState._jdSubmitting = false;
    if (btn) { btn.disabled = false; btn.style.opacity = ''; }
    _jdShowError(/permission/i.test(String(e?.message || e)) ? 'You do not have access to file documents on this job' : 'Could not submit — ' + String(e?.message || e));
    return false;
  }
  JobBoardState._jdSubmitting = false;
  const queued = jbNetLooksOffline();
  if (!JobBoardState._jobDocsPending) JobBoardState._jobDocsPending = {};
  const list = JobBoardState._jobDocsPending[job.id] || (JobBoardState._jobDocsPending[job.id] = []);
  list.push({ id: ref.id, kind, number: '', atMs: Date.now(), queued, status: 'pending' }); // the number comes from the server counter
  // TASK-9740 — once the write lands (now, or when signal comes back), follow
  // the doc to ready / refused; a queued write the server later rejects is
  // shown as not filed instead of "sends when you have signal" forever.
  write.then(
    () => { _jdMarkPending(job.id, ref.id, { queued: false }); _jdWatch(tenant, ref, job.id, kind); },
    (e) => {
      // Reached only when the write was queued (no signal, or no ack in time)
      // and the server refused it on arrival; an on-line refusal returned from
      // the catch above before this row existed, so this is a no-op there.
      const msg = /permission/i.test(String(e?.message || e)) ? 'You are not on the crew for this job today, or filing is turned off. Ask dispatch.' : String(e?.message || e);
      if (!_jdPendingFor(job.id).some(x => x.id === ref.id)) return;
      _jdMarkPending(job.id, ref.id, { status: 'refused', error: msg, queued: false });
      window.showToast?.(`${jobDocumentKindLabel(kind)} not filed — ${msg}`, 'error', null, 10000);
    }
  );
  closeJobDocumentForm();
  refreshOpenJobDocumentsSection();
  window.showToast?.(queued
    ? 'Saved on this phone — it will send when you have signal'
    : `${jobDocumentKindLabel(kind)} submitted — the signed PDF will show on this job in a moment`, 'success');
  return true;
}

// ============================================
// TASK-9372 — "Inspect equipment" inside the job details modal
// ============================================
// Guy (Sunbelt / Split Rock) wants the crew to inspect the gear they are
// running from the job they are on. The DVIR form already does equipment; this
// is the path to it: one "Inspect <name>" button per jobDay.assets[] entry
// (NOT trucks/trailers — they keep the DOT DVIR path), a prefilled form that
// stamps jobId/jobDayId on the record, and a per-job list under the buttons.
//
// TASK-9707 — the list is the DAY's inspections of the day's equipment. Truck
// and trailer DVIRs are taken from the standalone Inspections screen and carry
// jobId:null (only the asset button above stamps a jobId), so a jobId-only read
// listed nothing for the job's own trucks. Rows now come from truckId (every
// pretrip writer sets it — for trailers and equipment too) on the jobDay's
// trucks/trailers/assets, filtered to that day; stamped rows still show.
//
// LIST SCOPE — Chris call still open on the row (crew-wide vs own-only). Built
// to the row's recommendation: CREW-WIDE for THIS job only ("so two guys don't
// re-inspect the same gear"), projected to asset · pass/defects · driver ·
// date. The rules already let any PIN session read any pretrip (canReadTenant);
// TASK-544 narrowed only the STANDALONE list to own-driver. Flip to own-only by
// setting this to 'own' — a PIN session then keeps only rows whose driverId is
// the viewer's person id (members still see the job's crew).
//
// WHY jobId IS NOT BOUND IN firestore.rules (COD Adversary asked): the only
// cheap check is "the PIN writer is on that jobDay's crew", which costs a
// driverSessions get() + a jobDays get() on EVERY stamped DVIR create, and it
// would DENY the federal inspection record (49 CFR 396.11) whenever the crew
// list lags reality — a driver swapped in mid-day, a helper not on the day,
// the no-day fallback button. That is the TASK-428 stranding trap on the
// one write that must never be lost. And it would not close the claim anyway:
// driverId/driverName on a pretrip are client-chosen too. So the list is made
// honest instead (_jbJobDayEquipmentKeys): who, when, from which day, and a
// row only counts when its asset is on the day's equipment.
const _JB_INSP_LIST_SCOPE = 'crew';
const _JB_INSP_LIST_CAP = 3;

// One pretrip doc → the fields the list needs. Never the signature/photos.
// TASK-9707 — defectCount is EXACTLY what the Inspections list prints
// (pretrip.js renderInspectionsList: `defects.length`, and 0 on a defect DVIR
// reads "Defects noted"). The old count added 1 for typed defectNotes, so the
// two screens disagreed about the same record.
function _jbInspectionSummary(id, data) {
  const d = data || {};
  const hasDefects = d.status === 'defects';
  const createdMs = d.createdAt?.toMillis?.() ?? d.createdAt?.toDate?.()?.getTime?.() ?? d.submittedAt?.toMillis?.() ?? 0;
  const equipIds = [];
  [d.truckId, d.assetId, d.trailerId].forEach(v => {
    if (v === null || v === undefined || v === '') return;
    const sv = String(v);
    if (!equipIds.includes(sv)) equipIds.push(sv);
  });
  return {
    id,
    jobId: d.jobId || '',
    jobDayId: d.jobDayId || '',
    assetId: d.assetId || d.truckId || '',
    equipIds,
    assetName: d.assetUnit || d.truckUnit || d.trailerUnit || '',
    assetType: d.assetType || 'truck',
    hasDefects,
    defectCount: hasDefects && Array.isArray(d.defects) ? d.defects.length : 0,
    // Closure inputs for window.dvirDefectClosure — presence only, never the
    // signature images. `linked` (repair work orders) is filled by the fetch.
    mechanicSigned: !!d.mechanicSignature,
    nextDriverSigned: !!d.nextDriverSignature,
    linked: null,
    driverId: d.driverId || '',
    driverName: d.driverName || '',
    createdMs,
    dayKey: createdMs ? _jb811DayKey(new Date(createdMs)) : '',
    archived: d.archived === true,
  };
}

// TASK-9707 — the stage of a defect row, from the ONE closure predicate
// (pretrip.js dvirDefectClosure, TASK-9405/9706). Not loaded → treated as open:
// this screen never calls a defect repaired that it cannot prove.
const _JB_INSP_STAGE_TEXT = {
  'needs-mechanic': 'Needs mechanic sign-off',
  'checking': 'Signed off · checking repairs',
  'repair-unknown': 'Signed off · repair status unknown',
  'repair-open': 'Signed off · repair open',
  'awaiting-next-driver': 'Signed off · awaiting next driver',
  'closed': 'Signed off',
};
function _jbInspectionClosure(r) {
  if (!r || !r.hasDefects) return { open: false, stage: 'none' };
  const fn = window.dvirDefectClosure;
  if (typeof fn !== 'function') return { open: true, stage: '' };
  return fn({ status: 'defects', mechanicSignature: r.mechanicSigned, nextDriverSignature: r.nextDriverSigned }, r.linked || undefined);
}

// The rows the modal lists for a job: newest first, archived dropped, and the
// own-only projection applied when that scope is chosen.
function _jbInspectionRowsFor(jobId, scope) {
  const rows = (JobBoardState.inspectionsByJob?.[jobId] || []).filter(r => r && !r.archived);
  let out = rows;
  if ((scope || _JB_INSP_LIST_SCOPE) === 'own' && window.AppState?.driverModePin === true) {
    const me = getActiveJobBoardPersonId();
    out = me ? rows.filter(r => r.driverId === me) : [];
  }
  return out.slice().sort((a, b) => (b.createdMs || 0) - (a.createdMs || 0));
}

// Display name for one jobDay.assets[] ref. Members have the live assets list;
// a PIN session relies on the one-shot getDoc cache. While a lookup is still in
// flight say "equipment" rather than print a raw doc id.
function _jbInspAssetLabel(ref) {
  const sid = String(ref);
  const live = _resolveFleetRef(sid, JobBoardState.assets);
  if (live) return live.name || live.unit || sid;
  const cache = JobBoardState.inspAssetNames || {};
  if (Object.prototype.hasOwnProperty.call(cache, sid)) return cache[sid] || sid;
  return 'equipment';
}

// TASK-9372 (COD Adversary) — a pretrip's jobId/jobDayId is a CLIENT stamp:
// any PIN session can write any jobId on its own DVIR (see the rules note on
// _JB_INSP_LIST_SCOPE for why it is not bound server-side). So the list
// never treats the stamp as proof. A row only COUNTS as "inspected" for this
// job when its assetId is actually on this jobDay's equipment, and every row
// says who did it, when, and whether it was logged from this day.
// Keys = every jobDay.assets[] ref plus the doc id it resolves to on the live
// assets list (a legacy ref may be a name).
// TASK-9707 — trucks and trailers too, not just assets: a pretrip on the day's
// own truck is the most common inspection there is.
function _jbJobDayEquipmentKeys(jobDay) {
  const keys = new Set();
  [['trucks', JobBoardState.trucks], ['trailers', JobBoardState.trailers], ['assets', JobBoardState.assets]].forEach(([field, list]) => {
    (Array.isArray(jobDay?.[field]) ? jobDay[field] : []).forEach(r => {
      if (r === null || r === undefined || r === '') return;
      const s = String(r);
      keys.add(s);
      const live = _resolveFleetRef(s, list);
      if (live && live.id !== undefined && live.id !== null) keys.add(String(live.id));
    });
  });
  return keys;
}

// TASK-9707 — the jobDay's calendar day in the tenant's zone ('' when unknown).
function _jbJobDayKey(jobDay) {
  return jobDay?.date ? _jb811DayKey(jobDay.date) : '';
}

// TASK-9707 — a truck/trailer row is named the way the modal's Trucks /
// Trailers lines name it (vehicle name lives in `nickname`; window.getUnitDisplay
// gives "Unit 7 — #7"), not the bare unit the DVIR stamped. Equipment and
// anything not on the live lists keep the stamped name.
function _jbInspRowLabel(r) {
  const ids = Array.isArray(r?.equipIds) ? r.equipIds : [];
  if (r?.assetType !== 'equipment') {
    for (const id of ids) {
      const v = (JobBoardState.trucks || []).find(t => t && String(t.id) === id) || (JobBoardState.trailers || []).find(t => t && String(t.id) === id);
      if (!v) continue;
      const shown = typeof window.getUnitDisplay === 'function' ? window.getUnitDisplay(v) : (v.nickname ? `${v.nickname} — #${v.unit}` : (v.unit || v.name || ''));
      if (shown) return String(shown);
    }
  }
  return r?.assetName || 'Equipment';
}

// Section markup. Guards match the TASK-9364 clock section: nothing on a
// read-only share link or for a job outside the LIVE window. Otherwise it
// NEVER renders empty — no assets on the day still gives "Inspect equipment…".
// Ids go through window.escJsAttr (sharedFormat.js) — a JS string inside an
// attribute, no HTML escaper is enough.
function _jbJobInspectionsSection(job, jobDay) {
  if (!job) return '';
  if (window.isReadOnlyLinkSession?.()) return '';
  if (!(JobBoardState.jobs || []).some(j => j.id === job.id)) return '';
  if (typeof window.checkModuleAccess === 'function' && window.checkModuleAccess('pretrip', true) === false) return '';
  const esc = window.escJsAttr || ((s) => String(s == null ? '' : s));
  const jid = esc(job.id);
  const jdid = esc(jobDay?.id || '');
  const refs = [];
  (Array.isArray(jobDay?.assets) ? jobDay.assets : []).forEach(r => {
    if (r === null || r === undefined || r === '') return;
    const s = String(r);
    if (!refs.includes(s)) refs.push(s);
  });

  const loaded = Array.isArray(JobBoardState.inspectionsByJob?.[job.id]);
  const equipKeys = _jbJobDayEquipmentKeys(jobDay);
  const dayKey = _jbJobDayKey(jobDay);
  const rows = (loaded ? _jbInspectionRowsFor(job.id) : []).map(r => {
    const ids = Array.isArray(r.equipIds) && r.equipIds.length ? r.equipIds : (r.assetId ? [String(r.assetId)] : []);
    return {
      ...r,
      onJobEquipment: ids.some(id => equipKeys.has(String(id))),
      sameDay: (!!jobDay?.id && r.jobDayId === jobDay.id) || (!!dayKey && r.dayKey === dayKey),
    };
  });
  // TASK-9707 — a row counts as "inspected" only when it is on this day's
  // equipment AND from this day (a DVIR covers the day it was done).
  const counted = rows.filter(r => r.onJobEquipment && r.sameDay);
  const fmtWhen = (ms) => {
    if (!ms) return '';
    const d = new Date(ms);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  // Caption under a button: the newest inspection of THAT asset logged from
  // THIS day — who and when, never just "done".
  const latestFor = (ref) => {
    const ids = new Set([String(ref)]);
    const live = _resolveFleetRef(String(ref), JobBoardState.assets);
    if (live && live.id != null) ids.add(String(live.id));
    return counted.find(r => (r.equipIds || [r.assetId]).some(x => ids.has(String(x)))) || null;
  };
  const btn = (ref, label) => {
    const last = ref ? latestFor(ref) : null;
    const caption = last
      ? `<div class="jb-insp-last" style="font-size:0.78rem;color:var(--text-secondary);margin:-2px 0 8px;text-align:center;">Inspected by ${escapeHtml(last.driverName || 'unknown driver')} · ${escapeHtml(fmtWhen(last.createdMs))}${last.hasDefects ? ' · <span style="color:#991B1B;font-weight:600;">defects</span>' : ''}</div>`
      : '';
    return `
      <button type="button" class="btn btn-secondary jb-insp-btn" style="width:100%;min-height:44px;margin-bottom:6px;display:flex;align-items:center;gap:8px;justify-content:center;"
        onclick="event.stopPropagation();window.JobBoardModule.inspectAssetFromJob('${esc(ref)}','${jid}','${jdid}')">
        <span class="material-icons" style="font-size:18px;">construction</span> ${escapeHtml(label)}
      </button>${caption}`;
  };
  const buttons = refs.length
    ? refs.map(r => btn(r, `Inspect ${_jbInspAssetLabel(r)}`)).join('')
    : btn('', 'Inspect equipment…');

  const showAll = !!JobBoardState.inspShowAll?.[job.id];
  const shown = showAll ? rows : rows.slice(0, _JB_INSP_LIST_CAP);
  const rowsHtml = shown.map(r => {
    const dateStr = fmtWhen(r.createdMs);
    const where = r.sameDay ? 'Done this day' : (r.jobDayId ? 'Logged from another day of this job' : 'Logged from this job (no day)');
    const notOnJob = r.onJobEquipment ? '' : `<span class="jb-insp-offjob" style="display:block;font-size:0.75rem;color:#92400E;margin-top:2px;">Not on this day's equipment list — not counted</span>`;
    // TASK-9707 — same words as the Inspections card; the stage line comes
    // from dvirDefectClosure, so a repaired + signed-off DVIR is not red here.
    const closure = _jbInspectionClosure(r);
    const defectText = r.defectCount > 0 ? `${r.defectCount} ${r.defectCount === 1 ? 'defect' : 'defects'}` : 'Defects noted';
    const chip = r.hasDefects
      ? `<span style="display:flex;flex-direction:column;align-items:flex-end;gap:2px;flex-shrink:0;"><span style="background:${closure.open === false ? '#F1F5F9' : '#FEF2F2'};color:${closure.open === false ? 'var(--text-secondary)' : '#991B1B'};font-size:11px;padding:2px 8px;border-radius:999px;font-weight:600;white-space:nowrap;">${defectText}</span>${_JB_INSP_STAGE_TEXT[closure.stage] ? `<span class="jb-insp-stage" style="font-size:11px;color:${closure.open === false ? '#065F46' : 'var(--text-secondary)'};text-align:right;">${_JB_INSP_STAGE_TEXT[closure.stage]}</span>` : ''}</span>`
      : `<span style="background:#ECFDF5;color:#065F46;font-size:11px;padding:2px 8px;border-radius:999px;font-weight:600;white-space:nowrap;">Pass</span>`;
    return `
      <button type="button" class="jb-insp-row" style="width:100%;text-align:left;border:1px solid var(--border-light);border-radius:8px;padding:10px 12px;margin-bottom:6px;background:#fff;min-height:44px;cursor:pointer;display:flex;justify-content:space-between;gap:8px;align-items:center;"
        onclick="event.stopPropagation();window.JobBoardModule.openJobInspection('${esc(r.id)}')">
        <span style="min-width:0;flex:1;">
          <span style="display:block;font-weight:600;color:var(--text-primary);">${escapeHtml(_jbInspRowLabel(r))}</span>
          <span style="display:block;font-size:0.8rem;color:var(--text-secondary);margin-top:2px;">${escapeHtml(r.driverName || 'Unknown driver')} · ${escapeHtml(dateStr)}</span>
          <span class="jb-insp-where" style="display:block;font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">${where}</span>
          ${notOnJob}
        </span>
        ${chip}
      </button>`;
  }).join('');
  const moreBtn = rows.length > _JB_INSP_LIST_CAP
    ? `<button type="button" class="btn btn-secondary" style="width:100%;min-height:44px;" onclick="event.stopPropagation();window.JobBoardModule.toggleJobInspectionsShowAll('${jid}')">${showAll ? 'Show fewer' : `Show all (${rows.length})`}</button>`
    : '';
  const status = !loaded
    ? `<div style="font-size:0.8rem;color:var(--text-secondary);margin:4px 0 6px;">Loading inspections…</div>`
    : (rows.length ? '' : `<div style="font-size:0.8rem;color:var(--text-secondary);margin:4px 0 6px;">${dayKey ? "No inspections on this day's equipment yet." : 'No inspections on this job yet.'}</div>`);
  // TASK-9707 — a failed read is said out loud, never shown as "none".
  const failNote = loaded && JobBoardState.inspLoadFailed?.[job.id]
    ? `<button type="button" class="jb-insp-failed" style="width:100%;min-height:44px;text-align:left;background:#FFFBEB;border:1px solid #FCD34D;border-radius:8px;padding:8px 12px;font-size:0.8rem;color:#92400E;margin:4px 0 6px;cursor:pointer;" onclick="event.stopPropagation();window.JobBoardModule.closeJobDetails?.();window.location.hash='#/pretrip';">Some inspections could not load. <span style="text-decoration:underline;font-weight:600;">Open Inspections</span></button>`
    : '';

  return `
    <div style="margin-top:16px;border-top:1px solid var(--border-light);padding-top:16px;">
      <div style="font-weight:600;color:var(--text-primary);margin-bottom:8px;display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:18px;vertical-align:-3px;color:${counted.length ? '#10B981' : 'var(--text-secondary)'};">fact_check</span>
        Equipment Inspections${counted.length ? ` <span style="font-weight:500;color:var(--text-secondary);">(${counted.length})</span>` : ''}
      </div>
      ${buttons}
      <div style="margin-top:6px;">${failNote}${status}${rowsHtml}${moreBtn}</div>
    </div>`;
}

// One-shot reads on modal open. TASK-9707: pretrips on the jobDay's
// trucks/trailers/assets for that day — where(truckId in ids) + createdAt
// window + orderBy(createdAt desc), served by the existing (truckId, createdAt
// DESC) composite index; ±36h around the day's noon, then filtered to the
// tenant-zone day key — UNION the pretrips stamped with this jobId (equality
// only, no index). Then, for mechanic-signed defect rows only, that DVIR's own
// repair work orders (the query pretrip.js uses) so dvirDefectClosure can
// judge it. Plus a getDoc per jobDay.assets[] id the live list cannot name.
// Invalidates first; a read that lands after a newer one started is dropped.
// Never throws; a failed read is flagged (inspLoadFailed), never shown as none.
async function _jbFetchJobInspections(jobId, jobDay) {
  const tenantId = JobBoardState.tenantId;
  if (!tenantId || !jobId) return;
  if (window.isReadOnlyLinkSession?.()) return;
  if (window.AppState?.driverModePin && window.driverAppIsHidden && window.driverAppIsHidden('jobModal', 'inspections')) return;
  if (!JobBoardState.inspectionsByJob) JobBoardState.inspectionsByJob = {};
  if (!JobBoardState.inspAssetNames) JobBoardState.inspAssetNames = {};
  delete JobBoardState.inspectionsByJob[jobId];

  const refs = [];
  (Array.isArray(jobDay?.assets) ? jobDay.assets : []).forEach(r => {
    if (r === null || r === undefined || r === '') return;
    const s = String(r);
    if (refs.includes(s)) return;
    if (_resolveFleetRef(s, JobBoardState.assets)) return;
    if (Object.prototype.hasOwnProperty.call(JobBoardState.inspAssetNames, s)) return;
    refs.push(s);
  });
  const names = Promise.all(refs.map(async (id) => {
    try {
      // A legacy ref may be a NAME, not a doc id — a slash would make an
      // invalid path, so treat that as "not an id" and show it verbatim.
      if (id.includes('/')) { JobBoardState.inspAssetNames[id] = null; return; }
      const snap = await getDoc(doc(db, 'tenants', tenantId, 'assets', id));
      JobBoardState.inspAssetNames[id] = snap.exists() ? (snap.data().name || snap.data().unit || null) : null;
    } catch (e) {
      JobBoardState.inspAssetNames[id] = null;
    }
  }));
  if (!JobBoardState.inspReqSeq) JobBoardState.inspReqSeq = {};
  if (!JobBoardState.inspLoadFailed) JobBoardState.inspLoadFailed = {};
  const seq = (JobBoardState.inspReqSeq[jobId] || 0) + 1;
  JobBoardState.inspReqSeq[jobId] = seq;
  delete JobBoardState.inspLoadFailed[jobId];

  const pretrips = collection(db, 'tenants', tenantId, 'pretrips');
  const reads = [getDocs(query(pretrips, where('jobId', '==', jobId)))];
  const dayKey = _jbJobDayKey(jobDay);
  // COD r1 — anchor the window on the jobDay's own INSTANT (stored at tenant
  // noon), never on browser-local noon: ±36h around it covers the whole tenant
  // day whatever zone the phone is in. A bare 'YYYY-MM-DD' key → UTC noon.
  const dv = jobDay?.date;
  const anchorMs = dv && typeof dv.toMillis === 'function' ? dv.toMillis()
    : (dv && typeof dv.toDate === 'function' ? dv.toDate().getTime()
      : (dv && typeof dv.seconds === 'number' ? dv.seconds * 1000
        : (dv instanceof Date ? dv.getTime()
          : (dayKey ? Date.UTC(Number(dayKey.slice(0, 4)), Number(dayKey.slice(5, 7)) - 1, Number(dayKey.slice(8, 10)), 12) : NaN))));
  const dayDate = dayKey && Number.isFinite(anchorMs) ? new Date(anchorMs) : null;
  const equipIds = [..._jbJobDayEquipmentKeys(jobDay)].filter(id => !id.includes('/'));
  if (dayDate && !isNaN(dayDate.getTime()) && equipIds.length) {
    const from = Timestamp.fromMillis(dayDate.getTime() - 36 * 3600000);
    const to = Timestamp.fromMillis(dayDate.getTime() + 36 * 3600000);
    for (let i = 0; i < equipIds.length; i += 30) {
      reads.push(getDocs(query(pretrips, where('truckId', 'in', equipIds.slice(i, i + 30)), where('createdAt', '>=', from), where('createdAt', '<=', to), orderBy('createdAt', 'desc'))));
    }
  }
  const list = (async () => {
    const settled = await Promise.allSettled(reads);
    const byId = new Map();
    let failed = false;
    settled.forEach((res, idx) => {
      if (res.status !== 'fulfilled') {
        failed = true;
        console.warn('[JobBoard] job inspections fetch failed:', res.reason?.code || '', res.reason?.message);
        return;
      }
      res.value.docs.forEach(d => {
        if (byId.has(d.id)) return;
        const row = _jbInspectionSummary(d.id, d.data());
        // Equipment reads (idx > 0) are only that DAY's — drop the window's edges.
        if (idx > 0 && row.dayKey !== dayKey) return;
        byId.set(d.id, row);
      });
    });
    const rows = [...byId.values()];
    // COD r1 — repair work orders only for THIS day's mechanic-signed defect
    // rows; a stamped row from another day keeps linked=null and reads
    // "checking", never "repaired" (fails open). Bounds reads on a long job.
    await Promise.all(rows.filter(r => r.hasDefects && r.mechanicSigned && !r.archived && (!dayKey || r.dayKey === dayKey)).map(async (r) => {
      try {
        const ts = await getDocs(query(collection(db, 'tenants', tenantId, 'maintenance'), where('source', '==', 'pretrip'), where('sourceId', '==', r.id)));
        const docs = ts.docs.map(x => x.data() || {});
        r.linked = { state: 'ok', total: docs.length, open: docs.filter(t => t.status !== 'completed').length };
      } catch (e) {
        r.linked = { state: 'error' };
      }
    }));
    if (JobBoardState.inspReqSeq[jobId] !== seq) return;
    JobBoardState.inspectionsByJob[jobId] = rows;
    if (failed) JobBoardState.inspLoadFailed[jobId] = true;
  })();
  await Promise.all([names, list]);
  if (JobBoardState.inspReqSeq[jobId] !== seq) return;
  refreshOpenJobDetailInspectionsSection();
}

// Re-render the inspections slot of an OPEN job modal. No-op when closed, when
// the section is layout-hidden (the slot is absent), or on another job's modal.
function refreshOpenJobDetailInspectionsSection() {
  const slot = document.getElementById('jobboard-detail-inspections-section');
  if (!slot) return;
  const modal = document.getElementById('jobboard-detail-modal');
  const jobId = modal?.dataset?.jobId;
  const jobDayId = modal?.dataset?.jobDayId;
  if (!jobId) return;
  const job = (JobBoardState.jobs || []).find(j => j.id === jobId);
  const jobDay = jobDayId ? _findJobDayAnywhere(jobDayId) : null;
  slot.innerHTML = job ? _jbJobInspectionsSection(job, jobDay) : '';
}

function toggleJobInspectionsShowAll(jobId) {
  if (!JobBoardState.inspShowAll) JobBoardState.inspShowAll = {};
  JobBoardState.inspShowAll[jobId] = !JobBoardState.inspShowAll[jobId];
  refreshOpenJobDetailInspectionsSection();
}

// Open the pretrip view on a NEW inspection prefilled for this job's asset.
// Mirrors startJSA: close the modal, route to #/pretrip, retry until the
// module + its modal exist. An empty assetId opens on Equipment with nothing
// selected (the no-assets fallback button).
function inspectAssetFromJob(assetId, jobId, jobDayId) {
  if (jbBlockIfReadOnlyLink('start an inspection')) return;
  closeJobDetails?.();
  window.location.hash = '#/pretrip';
  let _tries = 0;
  const _open = () => {
    const pm = window.PretripModule;
    if (pm?.showNewInspectionFor && document.getElementById('modal-inspection')) {
      try { pm.switchDvirTab?.('inspections'); } catch (_) {}
      pm.showNewInspectionFor({ assetType: 'equipment', assetId: assetId || '', jobId: jobId || '', jobDayId: jobDayId || '' });
      return;
    }
    if (_tries++ < 20) setTimeout(_open, 150);
    else window.showToast?.('Inspections did not load. Open Inspections and try again.', 'error');
  };
  setTimeout(_open, 200);
}

// Open one listed inspection. PretripModule.openInspectionById loads the doc
// by id when it is not in the viewer's own (driverId-scoped) list, so a
// crewmate's row opens instead of silently doing nothing.
function openJobInspection(inspectionId) {
  if (!inspectionId) return;
  closeJobDetails?.();
  window.location.hash = '#/pretrip';
  let _tries = 0;
  const _open = () => {
    const pm = window.PretripModule;
    if (pm?.openInspectionById) {
      try { pm.switchDvirTab?.('inspections'); } catch (_) {}
      pm.openInspectionById(inspectionId);
      return;
    }
    if (_tries++ < 20) setTimeout(_open, 150);
    else window.showToast?.('Inspections did not load. Open Inspections and try again.', 'error');
  };
  setTimeout(_open, 200);
}

// TASK-9364 — job-bound clock section inside the job details modal (Guy /
// Sunbelt: a driver looking at a job should not have to back out, hit Clock In
// and re-pick the customer + job he was just looking at). ONE full-width
// button whose state comes ONLY from JobBoardState.clockEntries — the header's
// subscription — so header and modal can never disagree; it flips on the
// snapshot (refreshOpenJobDetailClockSection), never on the tap.
//
// Gate = EXACTLY the header button's (renderJobBoard → #btn-driver-clockin):
// a resolved person AND window.driverHasClockAccess(). Tenants that don't clock
// in / build tickets never see it. A read-only share link renders nothing —
// driverHasClockAccess already says no, the flag is checked anyway because the
// modal still opens from a share link. Live jobs only: a calendar-month cache
// hit (_findJobAnywhere) is not in JobBoardState.jobs and
// clockInFromBoardForPerson looks the job up THERE — a cached job would write
// an entry with no customer/title.
//
// Labels + colours + icons follow the tenant's clock-out mode exactly like the
// header: no entry → Clock In (#166534, login) or Build Ticket when
// isTicketOnlyMode() (#3B82F6, receipt_long); active entry → Clock Out
// (#DC2626, logout) or Build Ticket when isTicketMode() (receipt_long).
//
// Escaping: ids go into the inline onclick through window.escJsAttr
// (sharedFormat.js) — NOT the apostrophe→&#39; trick _jbJobJsaSection uses (the
// HTML parser decodes that back to a quote before the JS is compiled). Titles
// through escapeHtml.
function _jbJobClockSection(job, jobDay) {
  if (!job) return '';
  if (window.isReadOnlyLinkSession?.()) return '';
  const personId = getActiveJobBoardPersonId();
  if (!personId || !window.driverHasClockAccess?.()) return '';
  if (!(JobBoardState.jobs || []).some(j => j.id === job.id)) return '';
  // COD (Edge Case #1): a calendar-month jobDay outside the live window is not
  // in JobBoardState.jobDays — clockInFromBoardForPerson resolves the day from
  // the LIVE array only, so a cache-hit day would skip the equipment sheet and
  // write assetIds: []. Same rule as the job above.
  if (jobDay?.id && !(JobBoardState.jobDays || []).some(jd => jd.id === jobDay.id)) return '';

  const esc = window.escJsAttr || ((s) => String(s == null ? '' : s));
  const myEntry = (JobBoardState.clockEntries || []).find(e => e.personId === personId && e.status === 'active');
  const wrap = (inner) => `
    <div style="margin-top:16px;border-top:1px solid var(--border-light);padding-top:16px;">
      <div style="font-weight:600;color:var(--text-primary);margin-bottom:8px;">
        <span class="material-icons" style="font-size:18px;vertical-align:-3px;color:var(--brand-blue);">schedule</span>
        Clock
      </div>
      ${inner}
    </div>`;

  if (!myEntry) {
    const _ticketOnly = isTicketOnlyMode();
    // COD (Code Reviewer): the double-submit guard is module state, not the DOM
    // button — closing and re-opening the modal mid-GPS re-renders a fresh
    // button, which must come back disabled while the first tap is in flight.
    const _pending = _jbDetailClockInPending;
    // TASK-9865 — opened a job whose today crew does not include you while
    // another job today does: one advisory line + "Open J-1248". Never blocks.
    const _crew = _jbCrewWarnForJobModal(job, jobDay);
    return wrap(`
      ${_crew ? `<div id="jobboard-detail-crew-warn" style="margin-bottom:10px;">${_crew.html}</div>` : ''}
      <button type="button" class="btn btn-primary" id="jobboard-detail-clock-btn" ${_pending ? 'disabled' : ''} style="width:100%;min-height:48px;display:flex;align-items:center;gap:8px;justify-content:center;background:${_ticketOnly ? '#3B82F6' : '#166534'};color:#fff;"
        onclick="event.stopPropagation();window.JobBoardModule._jbDetailClockInTap(this,'${esc(job.id)}','${esc(jobDay?.id || '')}')">
        <span class="material-icons">${_ticketOnly ? 'receipt_long' : 'login'}</span>
        <span class="jb-detail-clock-label">${_pending ? 'Clocking in…' : (_ticketOnly ? 'Build Ticket' : 'Clock In')}</span>
      </button>`);
  }

  const _ticket = isTicketMode();
  const ci = myEntry.clockIn?.toDate?.() || (myEntry.clockIn ? new Date(myEntry.clockIn) : null);
  const since = ci && !isNaN(ci.getTime()) ? ci.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
  const onThisJob = !!myEntry.jobId && myEntry.jobId === job.id;
  const otherTitle = escapeHtml(myEntry.jobTitle || '');
  const status = onThisJob
    ? `<div style="font-size:13px;color:var(--text-secondary);margin-bottom:8px;">Clocked in${since ? ` since ${since}` : ''}</div>`
    : `<div style="font-size:13px;color:var(--text-secondary);margin-bottom:8px;">You're clocked in${otherTitle ? ` on <b>${otherTitle}</b>` : ''}${since ? ` since ${since}` : ''}</div>`;
  // The ENTRY's ids — jbBuildSignatureSection(jobDayId) would otherwise pick up
  // a DIFFERENT job's signature/stamp rules. But _jbFinalizeClockIn never writes
  // jobDayId (COD Independent + Edge Case #2), so an entry-only id is always ''
  // and the ticket builder loses the day's signature/stamp/email-copy rules —
  // the header has the same gap. Fall back to the modal's day ONLY when it is
  // the same job AND the same calendar day as the entry's clockIn (COD round 2:
  // same job ≠ same day — Tuesday's card of a Monday clock-in must not hand
  // Tuesday's rules to the clock-out). Otherwise stay with '' like the header.
  const outDayId = _jbEntryOutDayId(myEntry, job, jobDay);
  return wrap(`
    ${status}
    <button type="button" class="btn btn-primary" id="jobboard-detail-clock-btn" style="width:100%;min-height:48px;display:flex;align-items:center;gap:8px;justify-content:center;background:#DC2626;color:#fff;"
      onclick="event.stopPropagation();window.JobBoardModule.clockOutStandalone('${esc(myEntry.id)}','${esc(myEntry.jobId || '')}','${esc(outDayId)}')">
      <span class="material-icons">${_ticket ? 'receipt_long' : 'logout'}</span>
      <span class="jb-detail-clock-label">${_ticket ? 'Build Ticket' : 'Clock Out'}</span>
    </button>`);
}

// The day id a clock-out of `myEntry` should carry when opened from `job`'s
// screen (see the comment in _jbJobClockSection). Lifted out unchanged so the
// TASK-9694 job screen mounts its inline ticket with the SAME rule.
function _jbEntryOutDayId(myEntry, job, jobDay) {
  const ci = myEntry.clockIn?.toDate?.() || (myEntry.clockIn ? new Date(myEntry.clockIn) : null);
  const onThisJob = !!myEntry.jobId && myEntry.jobId === job.id;
  const _sameDay = (() => {
    if (!onThisJob || !jobDay?.id || !ci || isNaN(ci.getTime())) return false;
    const d = jobDay.date?.toDate?.() || (jobDay.date ? new Date(jobDay.date) : null);
    return !!d && !isNaN(d.getTime()) && formatDateISO(d) === formatDateISO(ci);
  })();
  return myEntry.jobDayId || (_sameDay ? jobDay.id : '');
}

// TASK-9364 — re-render the clock section of an OPEN job detail modal. Called
// from the clockEntries snapshot handler next to renderJobBoard(), so the modal
// button flips in lockstep with the header. No-op if the modal is closed.
function refreshOpenJobDetailClockSection() {
  // TASK-9694 — the job screen owns its clock state (header + bar + inline
  // ticket). It re-derives the STATE only and never re-renders a mounted
  // ticket, so a snapshot can't wipe a half-typed note.
  if (document.getElementById('jobboard-detail-modal')?.dataset?.jobscreen === '1') { _jbJobScreenRefresh(); return; }
  const slot = document.getElementById('jobboard-detail-clock-section');
  if (!slot) return;
  const modal = document.getElementById('jobboard-detail-modal');
  const jobId = modal?.dataset?.jobId;
  const jobDayId = modal?.dataset?.jobDayId;
  if (!jobId) return;
  const job = JobBoardState.jobs.find(j => j.id === jobId);
  const jobDay = jobDayId ? JobBoardState.jobDays.find(jd => jd.id === jobDayId) : null;
  // COD round 2 (Edge Case #3): the modal was opened on a calendar-cache day that
  // is not in the live array — the first render hid the section; the snapshot
  // re-render must not resurrect it with a blank jobDayId.
  if (jobDayId && !jobDay) { slot.innerHTML = ''; return; }
  // TASK-9373 — honour the Driver App layout on the snapshot repaint too, so a
  // clock snapshot can never put back a section the layout hides. (clock is
  // NEVER_HIDE today, so this is a no-op guard that keeps the reader honest.)
  if (window.AppState?.driverModePin && window.driverAppIsHidden && window.driverAppIsHidden('jobModal', 'clock')) { slot.innerHTML = ''; return; }
  slot.innerHTML = job ? _jbJobClockSection(job, jobDay) : '';
}

// TASK-9364 — tap handler for the modal's Clock In button. Disables the button
// from the tap until the clockEntries snapshot re-renders the section:
// _jbFinalizeClockIn awaits GPS (up to 5s) before addDoc, and a second tap in
// that window is the only way past the client's already-clocked-in guard.
// Delegates to clockInFromBoard — the existing picker-free helper (refuses a
// second active entry, runs the equipment-confirm sheet when equipmentTie is
// on, writes via _jbFinalizeClockIn, toasts "Clocked in at h:mm").
// The modal stays open after clock-in (Chris, 2026-09-24) — the JSA button is
// right underneath.
let _jbDetailClockInPending = false; // module state — survives modal close/reopen
async function _jbDetailClockInTap(btn, jobId, jobDayId, fb) {
  if (_jbDetailClockInPending) return;
  _jbDetailClockInPending = true;
  if (btn) {
    btn.disabled = true;
    const label = btn.querySelector('.jb-detail-clock-label');
    if (label) label.textContent = 'Clocking in…';
  }
  try {
    await clockInFromBoard(jobId, jobDayId, fb);
    // Equipment-tie tenants: clockInFromBoardForPerson returns BEFORE the write
    // and hands it to the sheet — and BOTH sheet exits write: Confirm with the
    // chosen equipment, X/dismiss as a labor-only clock-in (TASK-558). Neither
    // callback returns its promise, so the write cannot be awaited here. Hold
    // the guard (a) while the sheet is up — NO timer: the sheet is modal and the
    // button is unreachable behind it, and a timer would release the guard on a
    // slow driver (COD round 3, Independent) — then (b) for a bounded window
    // while GPS (≤6s) + addDoc + the snapshot land. Do NOT release early on
    // dismiss (COD round 2: that reopened the double-entry window).
    // TASK-1572 Slice 2 — the empty-day truck-ask sheet (jb-fleet-ask-modal) has
    // the same shape (returns before the write, every exit writes), so it holds
    // the guard the same way. COD Code Reviewer ❌: without it the guard released
    // while the sheet was up and a second tap could create a duplicate punch.
    const _sheetUp = () => !!(document.getElementById('jb-equip-confirm-modal') || document.getElementById('jb-fleet-ask-modal'));
    if (_sheetUp()) {
      await _jbWaitUntil(() => !_sheetUp(), Infinity);
      const pid = getActiveJobBoardPersonId();
      await _jbWaitUntil(() => (JobBoardState.clockEntries || []).some(e => e.personId === pid && e.status === 'active'), 30000);
    }
  } finally {
    _jbDetailClockInPending = false;
    // Refused / errored → nothing arrives on the snapshot, so re-render from
    // state (re-enables the button). Success → the snapshot already flipped
    // it; this re-render is a no-op.
    refreshOpenJobDetailClockSection();
  }
}

// Poll `cond` every 250ms until true or `maxMs` elapses (Infinity = no bound).
// Never rejects; a throwing `cond` counts as done so the caller is never stuck.
function _jbWaitUntil(cond, maxMs) {
  return new Promise((resolve) => {
    const t0 = Date.now();
    const iv = setInterval(() => {
      let done = false;
      try { done = !!cond(); } catch (_) { done = true; }
      if (done || Date.now() - t0 >= maxMs) { clearInterval(iv); resolve(); }
    }, 250);
  });
}

// TASK-794 — dispatcher/admin: open the existing token link + QR modal.
function getJsaSignatures(jsaId) {
  if (window.JSAModule?.showJsaSignatureLink) {
    window.JSAModule.showJsaSignatureLink(jsaId);
  } else {
    window.showToast?.('Open Inspections → JSA to get signatures', 'info');
  }
}

// TASK-794 — route to the JSA report so the viewer signs their own line
// (attributable, present). Navigates to the Inspections/JSA tab, then opens the
// report detail. No token regeneration (the crowd link is admin-only).
// TASK-10024 — the detail opens SCROLLED to the viewer's line with a working
// Sign button (focusSign); before, it opened read-only at the top and nobody
// could sign a filed JSA from there (Chris 2026-10-22).
function openJsaToSign(jsaId) {
  closeJobDetails?.();
  window.location.hash = '#/pretrip';
  let _tries = 0;
  const _open = () => {
    if (window.PretripModule?.switchDvirTab && window.JSAModule?.viewJSADetail) {
      try { window.PretripModule.switchDvirTab('jsa'); } catch (_) {}
      // viewJSADetail needs JsaState.reports populated; retry briefly if the
      // JSA subscription hasn't hydrated yet on first navigation.
      let _t2 = 0;
      const _show = () => {
        const ok = window.JSAModule.viewJSADetail(jsaId, { focusSign: true });
        if (ok === false && _t2++ < 20) { setTimeout(_show, 150); }
      };
      _show();
      return;
    }
    if (_tries++ < 20) setTimeout(_open, 150);
  };
  setTimeout(_open, 200);
}

// TASK-9371 — open an existing JSA in the EDIT form from the job modal. Same
// navigation dance as openJsaToSign; editJSAReport returns false until
// JsaState.reports has hydrated, so retry briefly and quietly (the last try is
// loud). Who may SAVE is decided by firestore.rules (members: hr module;
// driver-PIN: own JSA only) — this only routes.
function openJsaToEdit(jsaId) {
  closeJobDetails?.();
  window.location.hash = '#/pretrip';
  let _tries = 0;
  const _open = () => {
    if (window.PretripModule?.switchDvirTab && window.JSAModule?.editJSAReport) {
      try { window.PretripModule.switchDvirTab('jsa'); } catch (_) {}
      let _t2 = 0;
      const _edit = () => {
        const ok = window.JSAModule.editJSAReport(jsaId, { quiet: _t2 < 20 });
        if (ok === false && _t2++ < 20) { setTimeout(_edit, 150); }
      };
      _edit();
      return;
    }
    if (_tries++ < 20) setTimeout(_open, 150);
  };
  setTimeout(_open, 200);
}

// ============================================
// DRIVER TICKET VIEW
// ============================================
function isDriverTicketViewEnabled() {
  return window.AppState?.settings?.showTicketsInDriverView === true;
}

function showDriverPrices() {
  return window.AppState?.settings?.showPricesOnDriverTickets === true;
}

// TASK-9646 S5 — prices for a driver's ticket come from the SERVER
// (`driverTicketPrices`: tenant from auth, the Billing "Show prices on driver
// tickets" switch enforced there, caller must be on the ticket), never off the
// line docs this session can read. Null = no prices (setting off, not on the
// ticket, offline): the ticket still renders, hours only — never blocks.
async function jbFetchTicketPrices(ticketId) {
  try {
    const res = await httpsCallable(functions, 'driverTicketPrices')({ ticketId });
    const d = res && res.data;
    return d && d.enabled === true ? d : null;
  } catch (err) {
    // integ/2100 COD r1 — a failed call is NOT "prices off": the ticket says so
    // (only reached when the company shows prices, see the caller's gate).
    console.warn('[JobBoard] driverTicketPrices unavailable:', err?.message || err);
    return { enabled: false, failed: true };
  }
}

// TASK-1340 — ONE predicate for "should a driver see this ticket," because the
// two surfaces had two status vocabularies: AR/AP soft-deletes by setting
// status:'deleted' (billing.js filters it in 6 places) while the driver side
// only ever knew 'cancelled'. So a deleted ticket kept rendering for drivers —
// live case #100369 (Loenbro, $340) stayed on Cassian's job detail after Lacey
// deleted it, stacked under the same job as its live replacement #100399 with
// the same visible date, which is what read as "duplicate tickets."
// The subscription (:772-787) applies no status filter at all, so this is the
// only fence. Add new hidden statuses HERE, not at each call site.
const DRIVER_HIDDEN_TICKET_STATUSES = ['cancelled', 'deleted'];
function isDriverVisibleTicket(t) {
  return !!t && !DRIVER_HIDDEN_TICKET_STATUSES.includes(t.status);
}

function getTicketsForJob(jobId) {
  if (!jobId || !isDriverTicketViewEnabled()) return [];
  return JobBoardState.tickets.filter(t => t.jobId === jobId && isDriverVisibleTicket(t));
}

// A driver signature can live at the top level (job-board flow) OR nested under
// fieldTicketData (clock.js clock-out flow). Read both everywhere so a ticket
// signed via clock-out never falsely shows as unsigned on cards/badges or in the
// ticket modal.
function _ticketDriverSigUrl(t) {
  return t?.driverSignatureUrl || t?.fieldTicketData?.driverSignatureUrl || null;
}

async function loadTicketLines(ticketId) {
  if (JobBoardState.ticketLinesCache[ticketId]) {
    return JobBoardState.ticketLinesCache[ticketId];
  }
  try {
    const tenant = JobBoardState.tenantId;
    const snap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'));
    const lines = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    lines.sort((a, b) => (a.type || '').localeCompare(b.type || '') || (a.sortOrder || 0) - (b.sortOrder || 0));
    JobBoardState.ticketLinesCache[ticketId] = lines;
    return lines;
  } catch (err) {
    console.error('[JobBoard] Error loading ticket lines:', err);
    return [];
  }
}

function formatHours(hours) {
  if (!hours && hours !== 0) return '—';
  return parseFloat(hours).toFixed(1) + 'h';
}

function formatMoney(amount) {
  if (!amount && amount !== 0) return '$0.00';
  return '$' + parseFloat(amount).toFixed(2);
}

async function showDriverTicket(ticketId) {
  const ticket = JobBoardState.tickets.find(t => t.id === ticketId);
  // TASK-1340 — same fence on open-by-id: a stale card, a queued tap, or a
  // deep link must not let a driver open a ticket the office deleted.
  if (!ticket || !isDriverVisibleTicket(ticket)) {
    window.showToast?.('Ticket not found', 'error');
    return;
  }

  const lines = await loadTicketLines(ticketId);
  const empLines = lines.filter(l => l.type === 'EMPLOYEE');
  const equipLines = lines.filter(l => l.type === 'EQUIPMENT');
  const downLines = lines.filter(l => l.type === 'DOWNTIME');
  const standbyLines = lines.filter(l => l.type === 'STANDBY');
  // TASK-9646 S5 — the setting is read on the SERVER; a ticket this session
  // is not on, or a company with the switch off, gets no money at all.
  const priceData = showDriverPrices() ? await jbFetchTicketPrices(ticketId) : null;
  const prices = !!(priceData && priceData.enabled);
  // integ/2100 COD r1 — the company shows prices but the call failed: say so, no money.
  const pricesUnavailableNote = (priceData && priceData.failed)
    ? '<div class="driver-ticket-group" style="font-size:0.8rem;color:var(--text-secondary);">Prices couldn\'t be checked just now — reopen the ticket to try again.</div>'
    : '';
  const priceOf = (l) => (priceData && priceData.lines && priceData.lines[l.id]) || null;
  // TASK-9791 (Chris 2026-10-12, q13) — a driver who is allowed to see prices
  // gets a total that INCLUDES the fuel surcharge. ticket.grandTotal is
  // server-owned and already carries the FUEL_SURCHARGE line
  // (functions/src/services/fuelSurcharge.js -> computeTicketTotals), so the
  // Total below was right; what was missing is the row that makes it add up.
  // Price-gated exactly like Rate/Amount: with prices OFF the surcharge lines
  // are never read into the markup (TASK-9440: drivers never see or touch it).
  const fuelLines = prices ? lines.filter(l => l.type === 'FUEL_SURCHARGE' && l.includeOnInvoice !== false) : [];

  const customer = ticket.customerId
    ? JobBoardState.customers.find(c => c.id === ticket.customerId)
    : null;

  const renderLineGroup = (title, icon, groupLines) => {
    if (!groupLines.length) return '';
    return `
      <div class="driver-ticket-group">
        <div class="driver-ticket-group-title">
          <span class="material-icons">${icon}</span> ${title}
        </div>
        <table class="driver-ticket-table">
          <thead>
            <tr>
              <th>Description</th>
              <th style="text-align:right;">Hours</th>
              ${prices ? '<th style="text-align:right;">Rate</th><th style="text-align:right;">Amount</th>' : ''}
            </tr>
          </thead>
          <tbody>
            ${groupLines.map(l => `
              <tr>
                <td>${escapeHtml(l.description || l.personName || l.assetName || l.subtype || '—')}</td>
                <td style="text-align:right;">${formatHours(l.hours)}</td>
                ${prices ? `<td style="text-align:right;">${formatMoney(priceOf(l)?.rate)}/hr</td><td style="text-align:right;">${priceOf(l)?.pending ? '<span title="Price being calculated" aria-label="Price being calculated" style="color:var(--text-secondary);white-space:nowrap;">… <span style="font-size:0.75rem;font-style:italic;">Price being calculated</span></span>' : formatMoney(priceOf(l)?.lineAmount)}</td>` : ''}
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  };

  // TASK-428 Slice 1 — DERIVED equipment display. When no EQUIPMENT ticketLines
  // have been materialized yet (nobody's built/saved the ticket), show the
  // jobDay's assigned truck(s)/trailer(s) as a read-only derived view so the
  // driver/supervisor SEES the assigned equipment the moment work starts —
  // instead of an empty section (the TITS-Steve confusion). This auto-follows a
  // morning truck-swap because it reads the live jobDay. Real EQUIPMENT lines
  // still materialize only at the existing build/save event; once they exist,
  // they win and this derived block is suppressed.
  const renderDerivedEquipment = () => {
    if (equipLines.length) return '';
    const jobDay = _resolveTicketJobDay(ticket);
    if (!jobDay) return '';
    const { trucks, trailers } = getAssignedEquipment(jobDay.id);
    const assets = [...trucks, ...trailers];
    if (!assets.length) return '';
    return `
      <div class="driver-ticket-group">
        <div class="driver-ticket-group-title">
          <span class="material-icons">construction</span> Equipment
          <span style="font-size:0.7rem;font-weight:600;color:var(--text-secondary);background:var(--bg-subtle,#f1f5f9);padding:2px 8px;border-radius:8px;margin-left:8px;">Assigned by Dispatch</span>
        </div>
        <table class="driver-ticket-table">
          <thead>
            <tr><th>Description</th><th style="text-align:right;">Hours</th></tr>
          </thead>
          <tbody>
            ${assets.map(a => `
              <tr>
                <td>${escapeHtml(a.name || 'Equipment')}</td>
                <td style="text-align:right;color:var(--text-secondary);">—</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div style="font-size:0.75rem;color:var(--text-secondary);padding:6px 2px 0;">
          Hours are recorded when the ticket is built at clock-out.
        </div>
      </div>
    `;
  };
  const derivedEquipmentHtml = renderDerivedEquipment();

  // A ticket can carry a driver signature in two places: the top-level
  // driverSignatureUrl (written by this job-board flow) OR nested under
  // fieldTicketData.driverSignatureUrl (written by the clock.js clock-out flow).
  // Read both so a clock-out-signed ticket never falsely renders as "unsigned"
  // and historical signatures are always shown. Normalize the display fields too.
  const sigUrl = _ticketDriverSigUrl(ticket);
  const hasSignature = !!sigUrl;
  const sigSignedBy = ticket.driverSignedBy || ticket.fieldTicketData?.submittedByName || 'Driver';
  const sigSignedAt = ticket.driverSignedAt || ticket.fieldTicketData?.submittedAt || null;

  // Close any existing detail modal first
  closeJobDetails();

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'jobboard-ticket-modal';
  modal.innerHTML = `
    <div class="modal-content modal-fullscreen" style="max-width:540px;">
      <div class="modal-header">
        <h3>Ticket #${escapeHtml(String(ticket.ticketNo || '—'))}</h3>
        <button class="modal-close" onclick="window.JobBoardModule.closeTicketModal()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;">
        <!-- Ticket info -->
        <div style="margin-bottom:16px;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
            <span style="font-weight:600;color:var(--text-primary);">${escapeHtml(customer?.name || ticket.customerName || 'Unknown Customer')}</span>
            <span style="font-size:0.85rem;color:var(--text-secondary);">${escapeHtml(ticket.workDate || '')}</span>
          </div>
          ${ticket.location ? `<div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:4px;"><span class="material-icons" style="font-size:14px;vertical-align:-2px;">location_on</span> ${escapeHtml(ticket.location)}</div>` : ''}
          ${ticket.jobName ? `<div style="font-size:0.85rem;color:var(--text-secondary);"><span class="material-icons" style="font-size:14px;vertical-align:-2px;">work</span> ${escapeHtml(ticket.jobName)}</div>` : ''}
        </div>

        <!-- Line items -->
        ${(lines.length === 0 && !derivedEquipmentHtml) ? `
          <div style="text-align:center;padding:24px;color:var(--text-secondary);">
            <span class="material-icons" style="font-size:36px;display:block;margin-bottom:8px;">receipt_long</span>
            No line items on this ticket yet
          </div>
        ` : `
          ${pricesUnavailableNote}
          ${renderLineGroup('Employees', 'person', empLines)}
          ${renderLineGroup('Equipment', 'construction', equipLines)}
          ${derivedEquipmentHtml}
          ${renderLineGroup('Downtime', 'pause_circle', downLines)}
          ${renderLineGroup('Standby', 'hourglass_empty', standbyLines)}
          ${(prices && fuelLines.length) ? `
            <div class="driver-ticket-group" id="driver-ticket-fuel-surcharge">
              <div class="driver-ticket-group-title">
                <span class="material-icons">local_gas_station</span> Fuel Surcharge
              </div>
              <table class="driver-ticket-table">
                <thead>
                  <tr><th>Description</th><th style="text-align:right;">Amount</th></tr>
                </thead>
                <tbody>
                  ${fuelLines.map(l => `
                    <tr>
                      <td>${escapeHtml(l.description || 'Fuel Surcharge')}</td>
                      <td style="text-align:right;">${formatMoney(priceOf(l)?.lineAmount)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}

          ${prices ? `
            <div style="text-align:right;padding:12px 0;border-top:2px solid var(--border-light);margin-top:12px;">
              <span style="font-weight:700;font-size:1.1rem;color:var(--text-primary);">Total: ${formatMoney(priceData.grandTotal)}</span>
            </div>
          ` : ''}
        `}

        <!-- Signature section — READ-ONLY (TASK-395 Slice 4).
             Show a historical signature if one exists (either field location).
             Signing now happens in the Build Ticket modal at clock-out, not here;
             this viewer is for reviewing an already-closed ticket. -->
        ${hasSignature ? `
        <div style="margin-top:20px;border-top:1px solid var(--border-light);padding-top:16px;">
            <div style="text-align:center;">
              <div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:8px;">
                Signed by ${escapeHtml(sigSignedBy)} on ${sigSignedAt?.toDate ? sigSignedAt.toDate().toLocaleDateString() : ''}
              </div>
              <img src="${escapeHtml(sigUrl)}" alt="Signature" style="max-width:300px;border:1px solid var(--border-light);border-radius:8px;background:#fff;">
            </div>
        </div>
        ` : ''}
      </div>
    </div>
  `;

  document.body.appendChild(modal);
  document.body.classList.add('modal-open');

  // Close on backdrop click
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeTicketModal();
  });
}

function closeTicketModal() {
  const modal = document.getElementById('jobboard-ticket-modal');
  if (modal) modal.remove();
  document.body.classList.remove('modal-open');
  JobBoardState.sigCanvas = null;
  JobBoardState.sigCtx = null;
  JobBoardState.sigTicketId = null;
}

function escapeHtml(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// HOTFIX (stored XSS) — onclick="fn('${…}')" is a JS string inside an attribute;
// no HTML escaper fixes that position. Delegates to sharedFormat.js escJsAttr
// (TASK-4838), looked up at call time (Pitfall #1).
function escJsAttr(str) {
  return window.escJsAttr(str);
}

// ============================================
// CLOCK IN/OUT (Driver PIN Access)
// ============================================

/**
 * Best-effort location capture for clock-in/clock-out events. Never throws,
 * never blocks longer than 5s. Resolves `null` when geolocation is unavailable,
 * denied, or times out. Adding lat/lng to clock entries is essential for
 * geofence compliance checks (TASK-191) and for dispatcher shift review.
 *
 * Returns: { lat, lng, accuracy } | null
 */
function captureClockLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    let settled = false;
    const done = (val) => { if (!settled) { settled = true; resolve(val); } };
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => done({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
        () => done(null),
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 30000 }
      );
    } catch (e) { done(null); }
    // Belt-and-suspenders: never hang longer than 6s even if the API misbehaves
    setTimeout(() => done(null), 6000);
  });
}
// Expose for clock.js / admin flows that want the same helper
if (typeof window !== 'undefined') window.captureClockLocation = captureClockLocation;

// ── TASK-191 Phase 3 — GPS geofence distance flag (NON-BLOCKING, audit only) ──
// Haversine distance in FEET between two coord pairs. Mirrors dispatch.js
// haversineMiles (R=3958.8 mi) × 5280 so we reuse the exact same proven math
// rather than reinventing a metric formula. Pure, never throws on valid nums.
function _jbHaversineFeet(lat1, lng1, lat2, lng2) {
  const R = 3958.8; // Earth radius, miles (same constant as dispatch.haversineMiles)
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  const miles = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return miles * 5280;
}

/**
 * Resolve the geofence (center + radius) that applies to a clock event, per the
 * TASK-191 data model precedence:
 *   1. job.geofence.enabled === false  → NO check (explicit per-job disable).
 *   2. job.geofence with lat/lng        → use it (admin pinned/typed/geocoded).
 *   3. isShop + tenant.settings.geofence.shopFence → use the shop fence.
 *   4. tenant.settings.geofence.autoFromFirstWaypoint (default ON) AND the job
 *      has a first waypoint with coords → derive center from that waypoint,
 *      radius from the tenant default (or 500).
 *   5. otherwise → null (no fence, driver always treated in-zone).
 *
 * Returns { lat, lng, radiusFeet } or null. NEVER throws.
 */
function _jbResolveGeofence(job, isShop) {
  try {
    const ts = window.AppState?.settings?.geofence || {};
    const defRadius = (typeof ts.defaultRadiusFeet === 'number' && ts.defaultRadiusFeet > 0)
      ? ts.defaultRadiusFeet : 500;

    // (1)+(2) Per-job override is the strongest signal.
    const gf = job?.geofence;
    if (gf && gf.enabled === false) return null; // explicit per-job disable
    if (gf && gf.enabled === true
        && typeof gf.lat === 'number' && typeof gf.lng === 'number') {
      const r = (typeof gf.radiusFeet === 'number' && gf.radiusFeet > 0) ? gf.radiusFeet : defRadius;
      return { lat: gf.lat, lng: gf.lng, radiusFeet: r };
    }

    // (3) Shop clock-in → tenant shop fence, if configured.
    if (isShop) {
      const sf = ts.shopFence;
      if (sf && typeof sf.lat === 'number' && typeof sf.lng === 'number') {
        const r = (typeof sf.radiusFeet === 'number' && sf.radiusFeet > 0) ? sf.radiusFeet : defRadius;
        return { lat: sf.lat, lng: sf.lng, radiusFeet: r };
      }
      return null; // no shop fence configured → no check
    }

    // (4) Auto-derive from the job's first waypoint — ONLY when the tenant has
    // explicitly opted in (default OFF). COD Domain+Skeptic: drivers commonly
    // clock in at the yard/shop while the first waypoint is the pickup 50-200mi
    // away — auto-ON would false-flag nearly every clock-in and train dispatchers
    // to ignore the pill (alert fatigue). Opt-in keeps the feature trustworthy.
    // NOTE: this DEVIATES from the plan doc (which locked autoFromFirstWaypoint
    // default ON) — flagged for Chris. Per-job explicit fences are unaffected.
    if (ts.autoFromFirstWaypoint === true) {
      const wps = Array.isArray(job?.waypoints) ? job.waypoints : [];
      const first = wps.find(w => w && typeof w.lat === 'number' && typeof w.lng === 'number');
      if (first) return { lat: first.lat, lng: first.lng, radiusFeet: defRadius };
    }

    return null; // (5) no resolvable center → no check
  } catch (e) {
    console.warn('[JobBoard] geofence resolve failed (treating as no-fence):', e);
    return null;
  }
}

/**
 * Compute the geofence audit fields for a clock event, given the captured
 * location (from captureClockLocation), the job, and whether it's a Shop punch.
 * `kind` is 'in' or 'out' and selects which top-level flag key to emit.
 *
 * Returns an object to SPREAD onto the clock entry write. Always returns {} when
 * there's no coords, no resolvable fence, or any error — so the field is simply
 * omitted (timesheet can distinguish "in-zone" / "out-of-zone" / "not-checked").
 *
 * 🔴 NEVER throws, NEVER blocks. Geofence is display/audit only — it must never
 * gate or trap a clock-in/out (clock-out trap history, TASK-428). Wrapped fully
 * in try/catch; any failure → {} (treated as in-zone, write proceeds).
 */
function _jbComputeGeofenceFields(loc, job, isShop, kind) {
  try {
    if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') return {};
    const fence = _jbResolveGeofence(job, isShop);
    if (!fence) return {}; // no fence applies → not-checked (omit flag)

    const distFt = Math.round(_jbHaversineFeet(loc.lat, loc.lng, fence.lat, fence.lng));
    if (!isFinite(distFt)) return {};

    // Separate distance fields for clock-IN vs clock-OUT so a clock-out write
    // never overwrites the clock-in distance (COD Code Reviewer: shared field
    // would make the timesheet pill show the wrong number when only one side is
    // flagged). geofenceAppliedJobId records which job's fence was applied (the
    // fence may change later — preserves what was checked at the time).
    const out = { geofenceAppliedJobId: job?.id || null };
    if (kind === 'out') {
      out.clockOutGeofenceDistanceFt = distFt;
      out.clockOutOfGeofence = distFt > fence.radiusFeet;
    } else {
      out.geofenceDistanceFt = distFt;
      out.outOfGeofence = distFt > fence.radiusFeet;
    }
    return out;
  } catch (e) {
    // Any failure → no flag, write through as in-zone. NEVER block the clock.
    console.warn('[JobBoard] geofence compute failed (treating as in-zone):', e);
    return {};
  }
}

// TASK-9553 — the clock mode of the person on this board (their own override,
// else the company's). Falls back to the company setting if app.js's resolver
// is absent, which is exactly the pre-9553 behavior.
function _jbClockMode() {
  const cm = window.ClockMode;
  if (cm && typeof cm.effective === 'function') return cm.effective(getActiveJobBoardPersonId() || null);
  return window.AppState?.settings?.clockOutMode || 'simple';
}

// primeSelf timed out at PIN login: clock actions await ClockMode.confirmSelf
// (bounded, toast); still unconfirmed, ClockMode resolves to a ticket path.
window.addEventListener('clockmode:self', () => { if (JobBoardState.tenantId) renderJobBoard(); });

function isTicketMode() {
  const mode = _jbClockMode();
  return mode === 'ticket' || mode === 'ticketonly';
}

function isTicketOnlyMode() {
  return _jbClockMode() === 'ticketonly';
}

// TASK-428 Slice 1 — resolve the jobDay that a ticket belongs to, so the
// read-only ticket viewer can DERIVE the assigned equipment from the live
// jobDay (the source of truth for assigned vehicles) when no EQUIPMENT
// ticketLines have been materialized yet. Prefer a workDate match (a job can
// recur across days). v1.15.2000 COD 🔴 — workDate match ONLY, through the same
// tenant-day resolver as the clock-out fill: the old first-day fallback showed
// another day's truck as "Assigned by Dispatch". No match → null (no derived
// block). Returns the jobDay object or null.
function _resolveTicketJobDay(ticket) {
  if (!ticket?.jobId || typeof ticket.workDate !== 'string') return null;
  const wd = ticket.workDate.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(wd)) return null;
  // The bare date IS the calendar day — never via the viewer's zone (COD).
  return _jbResolveJobDayForEntry({ jobId: ticket.jobId, clockIn: wd, jobDayId: ticket.jobDayId || null });
}

function getAssignedEquipment(jobDayId) {
  const jobDay = JobBoardState.jobDays.find(jd => jd.id === jobDayId);
  if (!jobDay) return { trucks: [], trailers: [] };
  const trucks = (jobDay.trucks || []).map(ref => {
    const r = String(ref);
    const truck = _resolveFleetRef(r, JobBoardState.trucks);
    return truck ? { id: truck.id, name: window.getUnitDisplay ? window.getUnitDisplay(truck) : (truck.unit || truck.name), type: truck.type || '', raw: truck } : null;
  }).filter(Boolean);
  const trailers = (jobDay.trailers || []).map(ref => {
    const r = String(ref);
    const trailer = _resolveFleetRef(r, JobBoardState.trailers);
    return trailer ? { id: trailer.id, name: window.getUnitDisplay ? window.getUnitDisplay(trailer) : (trailer.unit || trailer.name), type: trailer.type || '', raw: trailer } : null;
  }).filter(Boolean);
  return { trucks, trailers };
}

// ============================================
// BUILD TICKET FORM HELPERS
// ============================================

function jbCOFEnabled(key) {
  const saved = window.AppState?.settings?.clockOutFormSections;
  if (!saved || !Array.isArray(saved)) return true;
  const section = saved.find(s => s.key === key);
  return section ? section.enabled : true;
}

// TASK-1483 — Personal Vehicle section on the driver-PIN clock-out. The
// builder, eligibility gate and reader live ONCE in clock.js and are reached
// through window.ClockModule (Pitfall #1 — no cross-import). Missing module =
// no section (fail closed: nothing is claimed, the clock-out is unaffected).
function jbBuildPersonalVehicleSection(entry) {
  const CM = window.ClockModule;
  if (!CM?.pvSectionOn || !CM?.pvSectionHTML) return '';
  const pid = entry?.personId;
  const person = pid ? (JobBoardState.people || []).find(p => p.id === pid) : null;
  // Release 1.15.1973 (live E2E) — 'light': this section renders in the body of the
  // clock-out sheet (#f1f5f9), not the dark header. The dark variant's pale text
  // (#e2e8f0 / #94a3b8) was unreadable there. Light = var(--text-primary) /
  // var(--text-secondary) (#0F172A / #475569), never --text-muted.
  return CM.pvSectionOn(person) ? CM.pvSectionHTML('jb', 'light', null) : '';
}

// TASK-1483 — the clock-out write with the vehicle claim riding along. NEVER
// TRAP A DRIVER (TASK-428): the reader validates to the exact rules bound, but
// if the write is still refused, retry ONCE without the two claim keys so the
// clock-out lands, and tell the driver the miles did not save. Any other
// error, or a refusal with no claim attached, propagates unchanged to the
// existing handlers (the TASK-1180 skew retry).
async function jbWriteWithVehicleClaim(entryRef, payload, pvFields, label) {
  if (!pvFields) return jbAckOrQueue(updateDoc(entryRef, payload), label);
  try {
    return await jbAckOrQueue(updateDoc(entryRef, { ...payload, ...pvFields }), label);
  } catch (err) {
    if (err?.code !== 'permission-denied') throw err;
    await jbAckOrQueue(updateDoc(entryRef, payload), label + ':noVehicle');
    console.warn('[JobBoard] vehicle claim refused — clocked out without it');
    window.showToast?.(`Clocked out — but your ${window.ClockModule?.pvName?.() || 'Vehicle Reimbursement'} miles did not save. Tell the office so they can add them.`, 'warning');
    return undefined;
  }
}

// TASK-10014 (Guy's demo, 2026-10-21) — "Permission denied" at clock-out on a
// phone whose clock runs FAST. The clock-in stored the phone's tap time
// (TASK-9694); the clock-out stamps serverTimestamp() (TASK-1180). Clock out
// within the skew of clocking in and the server's "now" is EARLIER than the
// stored clock-in, so the rule's clockOut >= clockIn refuses it (measured: phone
// 4m17s fast, refusal 13 s before the stored clock-in). The rule is right; a
// shift can't end before it starts. So retry ONCE with the clock-out read from
// the SAME phone clock that stamped the clock-in (never earlier than it): the
// window and the hours then agree, and the rule's server-now + 10 min ceiling
// still caps it. Time edits are not carried (they are what a human chose, and
// may be what was refused); a dropped edit is flagged needsReview with the
// requested value, exactly as the TASK-1180 retry does. A plain fast phone is
// not a driver adjustment (TASK-9190), so that case is only marked
// clockOutFromPhoneClock (job-screen entries also carry clockInTapSkewFlag).
// A refusal here propagates to submitClockOut's catch.
async function _jbPhoneClockOutRetry({ entryRef, clockInMs, pvFields, base, droppedEdit, carry }) {
  const inMs = Number(clockInMs);
  if (!Number.isFinite(inMs) || inMs <= 0) {
    const e = new Error('clock-out refused and the clock-in time is unreadable');
    e.code = 'permission-denied';
    throw e;
  }
  const nowMs = Date.now();
  const outMs = Math.max(nowMs, inMs);
  // COD Domain/Edge: the phone reads EARLIER than its own clock-in (its clock
  // was moved back mid-shift). The window is floored at zero, which is never
  // the honest elapsed time, so the office is asked to look.
  const floored = nowMs < inMs;
  const hours = parseFloat(((outMs - inMs) / 3600000).toFixed(2));
  const { notes, shiftNotes, outLoc, outGeo } = base || {};
  console.warn('[JobBoard] TASK-10014 clock-out refused at server time — retrying on the phone clock');
  await jbWriteWithVehicleClaim(entryRef, {
    clockOut: Timestamp.fromMillis(outMs),
    status: 'completed',
    totalHours: hours,
    hours,
    notes: notes ?? null,
    ...(shiftNotes ? { clockOutNotes: shiftNotes, draftShiftNotes: shiftNotes } : {}),
    updatedAt: Timestamp.fromMillis(outMs),
    // Its own marker (COD Blast-Radius): clockSkewUnreconciled means "TASK-9190
    // measured a skew and did not correct it", which is not what happened here.
    clockOutFromPhoneClock: true,
    // COD Code Reviewer: the STEP-1 write's ticket-time review marks
    // (ticketTimesUnsaved + needsReview) must survive the retry.
    ...(carry || {}),
    ...(droppedEdit || floored ? {
      needsReview: true,
      driverAdjustReason: (droppedEdit
        ? `Driver requested ${droppedEdit} but it was refused; the phone's time was recorded instead. Correct the entry in Workforce.`
        : "Clock-out was refused at server time and the phone's clock read earlier than its own clock-in, so 0 hours were recorded. Correct the entry in Workforce.").slice(0, 500),
    } : {}),
    ...(outLoc ? { clockOutLat: outLoc.lat, clockOutLng: outLoc.lng, clockOutAccuracy: outLoc.accuracy } : {}),
    ...(outGeo || {})
  }, pvFields, 'clockEntry:clockOutPhoneClockRetry');
  return { clockInTime: new Date(inMs), clockOutTime: new Date(outMs), hours };
}
// The review marks a STEP-1 clock-out payload carries that a retry must keep.
function _jbReviewCarry(upd) {
  const c = {};
  if (upd && upd.ticketTimesUnsaved) { c.ticketTimesUnsaved = upd.ticketTimesUnsaved; c.needsReview = true; }
  // TASK-9264 (COD r1) — the driver's materials claim rides every retry too:
  // once the entry completes on a retry, losing it here would drop his list.
  if (upd && upd.consumablesReported) c.consumablesReported = upd.consumablesReported;
  return c;
}

// clock-dst-all — typed clock times are COMPANY time, never phone time. Every
// box below is filled by jbToLocalDT (company-zone wall clock) and read back
// through window.TenantLocalTime (read at CALL time, Pitfall #1): a phone in
// another zone no longer shifts the hours, a time in the repeated fall-back hour
// is ASKED (never silently the first one), a spring-forward gap time and an
// unreadable company zone are refused.
function _jbTLT() { return window.TenantLocalTime || null; }

function jbToLocalDT(d, key) {
  const T = _jbTLT();
  if (T) return T.formatLocal(d, key ? { key } : undefined);
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,'0')}-${String(x.getDate()).padStart(2,'0')}T${String(x.getHours()).padStart(2,'0')}:${String(x.getMinutes()).padStart(2,'0')}`;
}

// The company zone for LABELS (falls back to Chicago when unset/unknown — a
// label is never what gets saved; the save path refuses an unreadable zone).
function _jbLabelTz() {
  const T = _jbTLT();
  const z = T ? T.resolveZone({ allowDefault: true }) : null;
  return (z && z.ok && z.tz) || undefined;
}
function _jbFmtTime(d) {
  try { return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: _jbLabelTz() }); } catch (_) { return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); }
}
function _jbFmtDateTime(d) {
  try { return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: _jbLabelTz() }); } catch (_) { return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
}

// A wall string (or Date/ms) → epoch ms. `strict` = save path: NaN unless the
// string resolves (repeated hour unresolved → NaN, so Timestamp.fromDate throws
// rather than guessing). Non-strict = preview: an unresolved repeated-hour time
// reads as its first instant (callers that show hours use jbHoursPreview).
function _jbMs(v, opts) {
  if (v == null || v === '') return NaN;
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  const T = _jbTLT();
  if (!T) return new Date(v).getTime();
  const key = opts && opts.key;
  return (opts && opts.strict) ? T.toMs(v, key ? { key } : undefined) : T.peekMs(v, key ? { key } : undefined);
}
// Save path: a wall string → Date in the company zone (invalid Date when it
// does not resolve — callers resolve with _jbResolveTimes first).
function _jbDate(v, key) { return new Date(_jbMs(v, { strict: true, key })); }

function jbCalcHours(start, end) {
  const s = _jbMs(start), e = _jbMs(end);
  if (!Number.isFinite(s) || !Number.isFinite(e)) return 0;
  return Math.max(0, (e - s) / 3600000);
}

// Hours text for a start/end pair that matches what will be saved: "7.50h", or
// for a repeated-hour time both answers ("7h 30m or 8h 30m — …pick on save").
function jbHoursPreview(start, end) {
  const T = _jbTLT();
  if (T && typeof start === 'string' && typeof end === 'string' && start && end) {
    const hb = T.hoursBetween(start, end);
    if (hb.ambiguous && hb.options && hb.options.length) {
      return hb.options.map((o) => o.hours.toFixed(2) + 'h').join(' or ') + ' (clocks fell back — pick on save)';
    }
  }
  return jbCalcHours(start, end).toFixed(2) + 'h';
}

// COD 1.15.2009 — a ticket ROW's time is read through the ROW itself (its own
// answer: stamped from a known instant, or its own pick) — never a shared cache,
// never another row's answer.
function _jbRowMs(row, field, strict) {
  const T = _jbTLT();
  if (!row) return NaN;
  const v = row[field];
  if (v == null || v === '') return NaN;
  if (!T) return new Date(v).getTime();
  return strict ? T.rowMs(row, field) : T.rowPeekMs(row, field);
}
function _jbRowDate(row, field) { return new Date(_jbRowMs(row, field, true)); }
function jbRowHours(row) {
  const a = _jbRowMs(row, 'startTime', true); const b = _jbRowMs(row, 'endTime', true);
  if (Number.isFinite(a) && Number.isFinite(b)) return Math.max(0, (b - a) / 3600000);
  const h = (_jbRowMs(row, 'endTime') - _jbRowMs(row, 'startTime')) / 3600000;
  return Number.isFinite(h) ? Math.max(0, h) : 0;
}
function jbRowHoursPreview(row) {
  const T = _jbTLT();
  if (T && row && row.startTime && row.endTime) {
    const hb = T.hoursBetween(row.startTime, row.endTime, { row });
    if (hb.ambiguous && hb.options && hb.options.length) {
      return hb.options.map((o) => o.hours.toFixed(2) + 'h').join(' or ') + ' (clocks fell back — pick on save)';
    }
  }
  return jbRowHours(row).toFixed(2) + 'h';
}
function _jbRowUnresolved(row) {
  if (!row) return false;
  return (!!row.startTime && !Number.isFinite(_jbRowMs(row, 'startTime', true)))
    || (!!row.endTime && !Number.isFinite(_jbRowMs(row, 'endTime', true)));
}

// Resolve every wall string a save is about to write (asks once for any
// repeated-hour time; refuses gap / unreadable zone). { ok, ms[] } | { ok:false, message }.
async function _jbResolveTimes(items, opts) {
  const T = _jbTLT();
  if (!T) return { ok: false, message: 'Refresh the page and try again — the company time settings did not load.' };
  return T.resolveAll(items, opts);
}
// Every wall string jbSaveTicket is about to write: the equipment / downtime /
// standby rows it will create or rewrite (same selection as its loops) and the
// work start/stop boxes. Rows carry no key (their strings are written by
// jbToLocalDT with no key, so a stored repeated-hour instant round-trips).
function _jbTicketTimeItems() {
  const items = [];
  const add = (list, label) => (list || []).forEach((it) => {
    if (!it) return;
    const writes = ((it._restored || it._saved) && it._dirty && it._lineId) || !(it._restored || it._saved);
    if (!writes) return;
    const name = label + (it.assetName ? ' (' + it.assetName + ')' : '');
    items.push({ str: it.startTime, row: it, field: 'startTime', label: name + ' start', optional: false });
    items.push({ str: it.endTime, row: it, field: 'endTime', label: name + ' end', optional: false });
  });
  add(JobBoardState.btEquipment, 'Equipment');
  add(JobBoardState.btDowntime, 'Downtime');
  add(JobBoardState.btStandby, 'Standby');
  if (jbCOFEnabled('startStopTimes')) {
    const ws = document.getElementById('jb-work-start')?.value;
    const we = document.getElementById('jb-work-stop')?.value;
    if (ws) items.push({ str: ws, key: 'jb-work-start', label: 'Work start' });
    if (we) items.push({ str: we, key: 'jb-work-stop', label: 'Work stop' });
  }
  return items;
}

// Resolve those times; hours per choice shown for a row whose other end resolves.
// opts: { reusePicks, noPrompt } (passed to resolveAll).
async function _jbResolveTicketTimes(opts) {
  const items = _jbTicketTimeItems();
  if (!items.length) return { ok: true };
  const T = _jbTLT();
  return _jbResolveTimes(items, {
    ...(opts || {}),
    describe: (it, ms) => {
      if (!T || !it.row) return '';
      const otherField = it.field === 'startTime' ? 'endTime' : 'startTime';
      const o = T.rowMs(it.row, otherField);
      if (!Number.isFinite(o)) return '';
      const h = (it.field === 'startTime' ? o - ms : ms - o) / 3600000;
      return h > 0 ? ' — ' + T.hm(h) : '';
    },
  });
}

function jbCollapsible(id, icon, title, content, startOpen, badge) {
  const badgeHtml = badge ? `<span style="background:#3b82f6;color:#fff;font-size:0.7rem;font-weight:700;padding:2px 8px;border-radius:10px;min-width:18px;text-align:center;line-height:1.4;">${badge}</span>` : '';
  return `
    <div style="background:#fff;border-radius:14px;margin-bottom:10px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.05),0 1px 2px rgba(0,0,0,0.03);">
      <div onclick="event.stopPropagation();window.JobBoardModule.jbToggle('${id}')" role="button" tabindex="0" style="display:flex;align-items:center;gap:10px;padding:14px 16px;cursor:pointer;user-select:none;-webkit-tap-highlight-color:rgba(0,0,0,0.05);">
        <div style="width:32px;height:32px;border-radius:8px;background:#f1f5f9;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
          <span class="material-icons" style="font-size:18px;color:#3b82f6;">${icon}</span>
        </div>
        <span style="flex:1;font-weight:600;font-size:0.9rem;color:#0f172a;">${title}</span>
        ${badgeHtml}
        <span class="material-icons" id="${id}-chev" style="font-size:20px;color:#94a3b8;transition:transform 0.2s;">${startOpen ? 'expand_less' : 'expand_more'}</span>
      </div>
      <div id="${id}-body" style="padding:0 16px 16px;${startOpen ? '' : 'display:none;'}">
        ${content}
      </div>
    </div>`;
}

function jbToggle(id) {
  const body = document.getElementById(id + '-body');
  const chev = document.getElementById(id + '-chev');
  if (!body) return;
  const isHidden = body.style.display === 'none';
  body.style.display = isHidden ? 'block' : 'none';
  if (chev) chev.textContent = isHidden ? 'expand_less' : 'expand_more';
}

// ============================================
// BUILD TICKET SECTION BUILDERS
// ============================================

// TASK-395: Field-ticket signatures, captured inside the Build Ticket modal.
// Which pads show is driven by the jobDay's denormalized fieldTicketSettings
// (Slice 1) — NOT a contract read (PIN drivers can't read contracts). Pad code
// is reused verbatim from clock.js via window.ClockModule.* (no third impl).
function _jbSigRoleMeta(role) {
  // TASK-395 never-force: no '*' required cue — these are requested, not mandatory.
  return role === 'driver'
    ? { label: 'Driver Signature', required: false, na: false, placeholder: 'Sign here' }
    : { label: 'Customer Signature', required: false, na: true, placeholder: 'Customer signs here' };
}

function _jbSigPadInnerHtml(role) {
  const m = _jbSigRoleMeta(role);
  const btnStyle = 'font-size:0.8rem;font-weight:600;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#334155;padding:6px 12px;cursor:pointer;';
  return `
    <label style="font-size:0.75rem;font-weight:600;color:#64748b;display:block;margin-bottom:6px;">${m.label}${m.required ? ' *' : ''}</label>
    <div class="sig-canvas-wrap" id="sig-wrap-${role}">
      <canvas id="sig-canvas-${role}"></canvas>
      <span class="sig-placeholder">${m.placeholder}</span>
    </div>
    <div style="margin-top:6px;display:flex;gap:6px;">
      <button type="button" onclick="window.ClockModule.clearSignature('${role}')" style="${btnStyle}">Clear</button>
      ${m.na ? `<button type="button" id="btn-sig-na-${role}" onclick="window.ClockModule.markSignatureNA('${role}')" style="${btnStyle}">Not Available</button>` : ''}
    </div>`;
}

function _jbSigCapturedHtml(role, url) {
  const m = _jbSigRoleMeta(role);
  const btnStyle = 'margin-top:6px;font-size:0.8rem;font-weight:600;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#334155;padding:6px 12px;cursor:pointer;';
  const body = url === 'N/A'
    ? `<div style="border:1px dashed #cbd5e1;border-radius:10px;padding:14px;background:#f8fafc;text-align:center;color:#94a3b8;font-size:0.85rem;">Marked Not Available</div>`
    : `<div style="border:1px solid #e2e8f0;border-radius:10px;padding:8px;background:#f8fafc;text-align:center;"><img src="${escapeHtml(url)}" alt="${m.label}" style="max-height:90px;max-width:100%;"></div>`;
  return `
    <label style="font-size:0.75rem;font-weight:600;color:#64748b;display:block;margin-bottom:6px;">${m.label}${m.required ? ' *' : ''}</label>
    ${body}
    <button type="button" onclick="window.JobBoardModule.jbResignSignature('${role}')" style="${btnStyle}">${url === 'N/A' ? 'Capture signature' : 'Re-sign'}</button>`;
}

// TASK-10190 — the settings the three Build Ticket sections read: the day's
// dispatch stamp when it has one, else what _jbEnsureJobDayFts fetched at read
// time (days stamped null before TASK-10188, Add Day on another book), else
// null = no pads, as before.
function _jbJobDayFts(jobDay) {
  if (!jobDay) return null;
  const st = jobDay.fieldTicketSettings;
  if (st && typeof st === 'object') return st;
  const c = JobBoardState.jobDaySigReadTime || {};
  const k = _jbJobDayFtsKey(jobDay);
  return Object.prototype.hasOwnProperty.call(c, k) ? c[k] : null;
}

// Cache key = the day + what decides the answer (its book and the job's
// customer), so an office edit seen mid-session re-asks instead of serving the
// old book's pads (COD Code Reviewer).
function _jbJobDayFtsKey(jobDay) {
  const job = (JobBoardState.jobs || []).find(j => j.id === jobDay?.jobId);
  return `${jobDay?.id || ''}|${jobDay?.selectedContractId || ''}|${job?.customerId || ''}`;
}

// TASK-10190 — read-time fill for a day with no stamp. Same rule as dispatch
// (the day's book when Active, else every Active book combined, strictest
// wins), computed by the server because a PIN session cannot read contracts:
// PIN -> driverFieldTicketSettings (session-bound, person must be on the day);
// a signed-in member -> getFieldTicketItems (its module fence). Booleans only.
// Nothing is written to the day. A failed call is not cached (retried on the
// next open) and never blocks the ticket.
async function _jbEnsureJobDayFts(jobDayId) {
  const jobDay = (JobBoardState.jobDays || []).find(jd => jd.id === jobDayId);
  if (!jobDay || !jobDayId) return;
  const st = jobDay.fieldTicketSettings;
  if (st && typeof st === 'object') return;
  const cache = JobBoardState.jobDaySigReadTime || (JobBoardState.jobDaySigReadTime = {});
  const key = _jbJobDayFtsKey(jobDay);
  if (Object.prototype.hasOwnProperty.call(cache, key)) return;
  const tenantAtCall = JobBoardState.tenantId;
  // TASK-10190 rest — marked while the call is out (the 4 s bound may pass
  // first). An answer lands in the cache, which outranks the mark (see
  // jbBuildSignatureSection); a refusal or a tenant switch drops it, so it only
  // survives a failure a retry can fix.
  const q = JobBoardState.jobDaySigReadState || (JobBoardState.jobDaySigReadState = {});
  q[key] = 1;
  try {
    let fts = null;
    if (window.AppState?.driverModePin === true) {
      const r = await httpsCallable(functions, 'driverFieldTicketSettings', { timeout: 8000 })({ jobDayId: String(jobDayId) });
      fts = r?.data?.fieldTicketSettings || null;
    } else {
      const job = (JobBoardState.jobs || []).find(j => j.id === jobDay.jobId);
      const customerId = job?.customerId;
      if (!customerId || customerId === '__shop__' || !tenantAtCall) { cache[key] = null; return; }
      const req = { tenantId: tenantAtCall, customerId: String(customerId) };
      if (jobDay.selectedContractId) req.contractId = String(jobDay.selectedContractId);
      const r = await httpsCallable(functions, 'getFieldTicketItems', { timeout: 8000 })(req);
      fts = r?.data?.fieldTicketSettings || null;
    }
    if (JobBoardState.tenantId !== tenantAtCall) return void delete q[key]; // switched tenants mid-call
    const FTS = window.FieldTicketSettings;
    const stamp = (FTS && typeof FTS.toJobDayStamp === 'function') ? FTS.toJobDayStamp(fts)
      : (fts ? { requireDriverSignature: fts.requireDriverSignature === true, requireCustomerSignature: fts.requireCustomerSignature === true,
        requireStamp: fts.requireStamp === true, requireEmailCopy: fts.requireEmailCopy === true, defaultEmailRecipient: null } : null);
    (JobBoardState.jobDaySigReadTime || (JobBoardState.jobDaySigReadTime = {}))[key] = stamp;
  } catch (e) {
    // A refusal (no ticket module, not on the day: permission-denied, or what
    // _jbTsfIsTerminal calls terminal) is an answer a retry won't change: no
    // 'couldn't check' note for it (TASK-10190 rest). 'unauthenticated' stays
    // marked (a token not yet refreshed is transient — COD Domain).
    if (_jbTsfIsTerminal(e) || /^(functions\/)?permission-denied$/.test(e?.code)) delete q[key];
    // Never cached (COD r2 Adversary/Domain): a transient refusal (token not yet
    // refreshed) must not hide a requested pad for the whole session. Every
    // failure is retried on the next open.
    console.warn('[JobBoard] TASK-10190 read-time field ticket settings unavailable:', e?.code || e?.message || e);
  }
}

function jbBuildSignatureSection(jobDayId) {
  const jobDay = (JobBoardState.jobDays || []).find(jd => jd.id === jobDayId);
  const fts = _jbJobDayFts(jobDay);
  // No flags known (no contract / older jobDay / neither required) → no pads,
  // matching clock.js when fieldTicketSettings is null. TASK-10190 rest: no
  // stamp and the read-time call is still out past the 4 s bound or failed in
  // a way a retry can fix → a one-line note (still never blocks). A refusal,
  // an answered "no settings" or never attempted → no note, as before.
  // Keys always hold '|' (so `in` never meets an Object.prototype name), and a
  // missing day's key '||' is never marked (_jbEnsureJobDayFts needs the day).
  let k;
  if (!fts && JobBoardState.jobDaySigReadState[k = _jbJobDayFtsKey(jobDay)]
    && !(k in (JobBoardState.jobDaySigReadTime || {}))) {
    return `<div class="jb-fts-unknown" role="status" style="margin:8px 0 12px;padding:8px 10px;border:1px solid #FDE68A;border-radius:8px;background:#FFFBEB;color:#92400E;font-size:0.82rem;">Couldn't check whether this customer wants a signature. Close and reopen the ticket to try again.</div>`;
  }
  if (!fts || (!fts.requireDriverSignature && !fts.requireCustomerSignature)) return '';

  const ftd = JobBoardState._btFieldTicketData || {};
  const capturedUrl = (role) => role === 'driver'
    ? (ftd.driverSignatureUrl || null)
    : (ftd.customerSignatureUnavailable ? 'N/A' : (ftd.customerSignatureUrl || null));

  let captured = 0;
  const slot = (role) => {
    const url = capturedUrl(role);
    if (url) captured++;
    const inner = url ? _jbSigCapturedHtml(role, url) : _jbSigPadInnerHtml(role);
    return `<div id="jb-sig-slot-${role}" data-captured="${url ? '1' : '0'}" style="margin-bottom:14px;">${inner}</div>`;
  };

  let content = '';
  if (fts.requireDriverSignature) content += slot('driver');
  if (fts.requireCustomerSignature) content += slot('customer');

  return jbCollapsible('jb-sec-signatures', 'draw', 'Signatures', content, true, captured || null);
}

// Swap a captured-signature slot back to a live blank pad (redo a signature).
function jbResignSignature(role) {
  const slot = document.getElementById(`jb-sig-slot-${role}`);
  if (!slot) return;
  slot.dataset.captured = '0';
  slot.innerHTML = _jbSigPadInnerHtml(role);
  setTimeout(() => window.ClockModule?.initSignaturePad?.(role), 30);
}

// Init any blank signature pads after the modal is mounted (canvas needs layout).
function jbInitSignaturePads() {
  ['driver', 'customer'].forEach(role => {
    const slot = document.getElementById(`jb-sig-slot-${role}`);
    if (slot && slot.dataset.captured === '0' && document.getElementById(`sig-canvas-${role}`)) {
      window.ClockModule?.initSignaturePad?.(role);
    }
  });
}

// Upload any freshly-drawn pads → Storage → write fieldTicketData.*Url on the
// ticket (clock.js canonical shape). Only acts on live (non-captured) slots, so
// repeated saves don't re-upload. Non-fatal: a failed upload toasts but doesn't
// block the save/clock-out.
async function jbPersistSignatures(ticketId) {
  if (!ticketId) return;
  const CM = window.ClockModule;
  if (!CM?.getSignatureDataUrl) return;
  const tenant = JobBoardState.tenantId;
  // TASK-1642 — signatures and the stamp photo go to Cloud Storage, which does
  // not queue offline (see jbUploadPhotos). Skip the whole persist with no
  // signal. On the SAVE path the pads keep dataset.captured === '0' and the
  // stamp capture is not reset, so a later Save re-uploads exactly what the
  // driver already drew. On the CLOCK-OUT path the modal closes and that state
  // goes with it — same pre-existing Storage limit as photos, and the same
  // answer: _btMediaSkipped makes the message tell the truth.
  if (jbNetLooksOffline()) {
    const _pendingSig = ['driver', 'customer'].some(r =>
      document.getElementById('jb-sig-slot-' + r)?.dataset.captured === '0' && !!CM.getSignatureDataUrl(r));
    if (_pendingSig || CM.getStampCapture?.()?.file) {
      JobBoardState._btMediaSkipped = true;
      console.warn('[JobBoard] TASK-1642 skipping signature/stamp upload — no connection');
    }
    return;
  }
  const updates = {};
  try {
    const driverSlot = document.getElementById('jb-sig-slot-driver');
    if (driverSlot && driverSlot.dataset.captured === '0') {
      const d = CM.getSignatureDataUrl('driver');
      if (d && d !== 'N/A') {
        const blob = CM.dataUrlToBlob(d);
        const sigRef = ref(storage, `tenants/${tenant}/signatures/${ticketId}-driver.png`);
        await uploadBytes(sigRef, blob, { contentType: 'image/png' });
        updates['fieldTicketData.driverSignatureUrl'] = await getDownloadURL(sigRef);
      }
    }
    const custSlot = document.getElementById('jb-sig-slot-customer');
    if (custSlot && custSlot.dataset.captured === '0') {
      const c = CM.getSignatureDataUrl('customer');
      if (c === 'N/A') {
        updates['fieldTicketData.customerSignatureUnavailable'] = true;
      } else if (c) {
        const blob = CM.dataUrlToBlob(c);
        const sigRef = ref(storage, `tenants/${tenant}/signatures/${ticketId}-customer.png`);
        await uploadBytes(sigRef, blob, { contentType: 'image/png' });
        updates['fieldTicketData.customerSignatureUrl'] = await getDownloadURL(sigRef);
        // COD Code Reviewer #1: clear a prior N/A so the new sig isn't masked
        // (capturedUrl prioritizes the NA flag).
        updates['fieldTicketData.customerSignatureUnavailable'] = false;
      }
    }
    if (Object.keys(updates).length > 0) {
      updates.updatedAt = Timestamp.now();
      // TASK-4699 — a signature (or a "customer unavailable" mark) is printed on
      // the ticket the customer was emailed. Inside the keys > 0 guard, so a
      // pass that captured nothing stamps nothing.
      updates.ticketEmailDirtyAt = Timestamp.now();
      await jbAckOrQueue(updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), updates), 'ticket:signatures');

      // Reflect captured state into the DOM + state so a second Save is idempotent
      // and the driver gets visual confirmation.
      const ftd = JobBoardState._btFieldTicketData = { ...(JobBoardState._btFieldTicketData || {}) };
      const reflect = (role, url) => {
        const s = document.getElementById(`jb-sig-slot-${role}`);
        if (s) { s.dataset.captured = '1'; s.innerHTML = _jbSigCapturedHtml(role, url); }
      };
      if (updates['fieldTicketData.driverSignatureUrl']) {
        ftd.driverSignatureUrl = updates['fieldTicketData.driverSignatureUrl'];
        reflect('driver', ftd.driverSignatureUrl);
      }
      if (updates['fieldTicketData.customerSignatureUrl']) {
        ftd.customerSignatureUrl = updates['fieldTicketData.customerSignatureUrl'];
        ftd.customerSignatureUnavailable = false;
        reflect('customer', ftd.customerSignatureUrl);
      } else if (updates['fieldTicketData.customerSignatureUnavailable']) {
        ftd.customerSignatureUnavailable = true;
        reflect('customer', 'N/A');
      }
    }
  } catch (err) {
    console.error('[JobBoard] Signature persist failed:', err);
    window.logError?.(err, { module: 'jobboard', operation: 'SIGNATURE_PERSIST', ticketId });
    // 🔴 TASK-4641 — this used to toast "Signature upload failed — try again"
    // and then let jbSaveTicket go on to show "Ticket data saved" a few lines
    // later, because this whole function is non-fatal. Two messages on one
    // screen with opposite meanings, and the green one is the one a driver
    // believes. Nothing is said HERE any more; the reason is handed to the
    // single terminal message at the end of the save.
    //
    // It also named the wrong thing. A permission-denied on this path is the
    // TICKET DOC refusing the write (the office closed the ticket out), not
    // Cloud Storage refusing the image — and "try again" is advice that can
    // never work against a lock.
    _jbNoteRefused(err?.code === 'permission-denied'
      ? 'Your signature did NOT go on this ticket — the office closed it. Do not sign again; tell dispatch.'
      : 'Your signature did NOT upload. Sign it again next time you have a bar.');
  }

  // TASK-395-B: stamp / proof photo. Reuses clock.js capture state via ClockModule.
  try {
    const stamp = CM.getStampCapture?.();
    if (stamp?.file) {
      const stampRef = ref(storage, `tenants/${tenant}/ticketStamps/${ticketId}.jpg`);
      // TASK-9402 — label from the bytes (handleStampCapture now converts HEIC first).
      await uploadBytes(stampRef, stamp.file, { contentType: await uploadContentType(stamp.file, 'image/jpeg') });
      const stampUrl = await getDownloadURL(stampRef);
      await jbAckOrQueue(updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
        // TASK-4699 — the stamp/proof photo is part of the customer's copy.
        'fieldTicketData.stampPhotoUrl': stampUrl, 'fieldTicketData.stampUnavailable': false,
        ticketEmailDirtyAt: Timestamp.now(), updatedAt: Timestamp.now(),
      }), 'ticket:stampPhoto');
      JobBoardState._btFieldTicketData = { ...(JobBoardState._btFieldTicketData || {}), stampPhotoUrl: stampUrl, stampUnavailable: false };
      CM.resetStampCapture?.(); // so a repeat Save doesn't re-upload
    } else if (stamp?.na && !JobBoardState._btFieldTicketData?.stampPhotoUrl) {
      await jbAckOrQueue(updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
        // TASK-4699 — "no stamp available" is itself a visible mark on the copy.
        'fieldTicketData.stampUnavailable': true,
        ticketEmailDirtyAt: Timestamp.now(), updatedAt: Timestamp.now(),
      }), 'ticket:stampUnavailable');
      JobBoardState._btFieldTicketData = { ...(JobBoardState._btFieldTicketData || {}), stampUnavailable: true };
    }
  } catch (stampErr) {
    // TASK-4641 — same defect as the signature arm above, same fix: the reason
    // goes to the one terminal message instead of racing a success toast.
    console.error('[JobBoard] Stamp persist failed:', stampErr);
    window.logError?.(stampErr, { module: 'jobboard', operation: 'STAMP_PERSIST', ticketId });
    _jbNoteRefused(stampErr?.code === 'permission-denied'
      ? 'Your stamp photo did NOT go on this ticket — the office closed it. Tell dispatch.'
      : 'Your stamp photo did NOT upload. Take it again next time you have a bar.');
  }

  // TASK-395-B: email ticket copy. Dedup — only send when the recipient changed,
  // so repeated Saves / sign-anytime re-opens don't spam the customer.
  try {
    const emailTo = document.getElementById('ft-email-to')?.value?.trim();
    const already = JobBoardState._btFieldTicketData?.emailSentTo;
    if (emailTo && emailTo !== already) {
      const sendFieldTicketEmail = httpsCallable(functions, 'sendFieldTicketEmail');
      // TASK-1642 — a callable is neither a Firestore write nor queueable: on a
      // dead connection it sits on the SDK's 70s default timeout, which is the
      // longest single hang left on this path. Don't await it at all — this
      // block was already documented "non-blocking, the ticket is still saved".
      //
      // Marking the recipient as sent from the RESOLUTION (not from a bounded
      // wait) is what keeps a repeat Save in this session from re-firing: a send
      // that succeeds slowly still records it, and a send that truly failed
      // leaves it unset so the next Save retries. Awaiting with a timeout could
      // only ever get one of those two right.
      //
      // TASK-4713 — LOCAL STATE ONLY. The ticket doc's `fieldTicketData.emailSentTo`
      // / `emailSentAt` is stamped by the SERVER (Admin SDK, same batch as its
      // dedup witness) and the driver arm of firestore.rules now DENIES a client
      // write of it. The updateDoc that used to sit here was redundant with the
      // server stamp, and a driver-writable stamp is exactly what let a pre-stamp
      // make the customer's FIRST copy skip as "already sent" (the `already`
      // pre-check above never reached the server). The next session's `already`
      // comes from the server-written value via jbRestoreTicketData.
      //
      // COD round 4 (Code Reviewer ❌): that is only true for a DRIVER-PIN
      // session. A signed-in MEMBER takes the server's OFFICE branch, which
      // deliberately never touches fieldTicketData (TASK-4662 S1) — so for a
      // member the client stamp is still the only persistent dedup, and
      // dropping it would re-send the ticket on the next session's Save. Members
      // keep the pre-4713 write (the member arm allows it); PIN drivers do not
      // write it at all. NO `ticketEmailDirtyAt` (TASK-4699): a send marker is
      // not a content edit.
      sendFieldTicketEmail({ ticketId, emailTo })
        .then(() => {
          JobBoardState._btFieldTicketData = { ...(JobBoardState._btFieldTicketData || {}), emailSentTo: emailTo };
          // TASK-10026 — a send can close this ticket to driver edits
          // (sentLockPatch). TASK-10267 — but the server DEFERS that lock while
          // anyone (this driver included) is still clocked in (sentLockDecision),
          // so read the ticket back. The send's stamp (emailSentTo) lands in the
          // SAME server transaction as the lock decision: a new stamp (not the
          // pre-send `already`; the server may repair the address) + no sent
          // lock = deferred, keep saving. A failed read or a read still holding
          // the old stamp (stale) proves nothing, so the lock shows as before.
          const _isDrv = !!(window.AppState?.driverMode || window.AppState?.driverModePin || window.AppState?.isPublicAccess);
          if (_isDrv && JobBoardState._btTicketId === ticketId && !JobBoardState._btLockReason) {
            const _lockNow = () => {
              if (JobBoardState._btTicketId !== ticketId || JobBoardState._btLockReason) return;
              JobBoardState._btLockReason = 'sent';
              try { _jbShowTicketBillingState({}, ticketId, 0, 'sent'); } catch (_) { /* banner is best-effort */ }
            };
            getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId)).then((s) => {
              const t = s.exists() ? s.data() : null;
              const _stamp = t?.fieldTicketData?.emailSentTo;
              const deferred = !!_stamp && _stamp !== already && _jbTicketLockReason(t) !== 'sent';
              if (!deferred) _lockNow();
            }).catch(_lockNow);
          }
          if (window.AppState?.driverModePin) return;
          return updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
            'fieldTicketData.emailSentTo': emailTo, 'fieldTicketData.emailSentAt': Timestamp.now(), updatedAt: Timestamp.now(),
          });
        })
        .catch((e) => {
          // TASK-4641 — was console-only. No toast, deliberately: by the time
          // this settles the customer copy has already gone (or already failed)
          // and the driver has moved on — there is nothing to act on. The office
          // still learns the send itself failed, which is what would cost a
          // duplicate or a missing customer email on the next Save. Breadcrumb,
          // not silence. No address in the payload.
          console.warn('[JobBoard] Field ticket email failed:', e?.code || e?.message);
          try {
            window.logError?.(e, {
              module: 'jobboard', operation: 'FIELD_TICKET_EMAIL_DEDUP',
              ticketId, hadRecipient: true,
            });
          } catch (_) { /* breadcrumb is best-effort */ }
        });
    }
  } catch (emailErr) {
    console.warn('[JobBoard] Field ticket email failed:', emailErr);
    // Non-blocking — ticket is still saved.
  }
}

// TASK-395-B: Stamp / proof-photo card in the Build Ticket modal. Reuses clock.js
// handleStampCapture/markStampNA (which write ClockState._stamp*) via the same DOM
// ids; persisted by jbPersistSignatures.
function jbBuildStampSection(jobDayId) {
  const jobDay = (JobBoardState.jobDays || []).find(jd => jd.id === jobDayId);
  if (!_jbJobDayFts(jobDay)?.requireStamp) return ''; // TASK-10190
  const existing = JobBoardState._btFieldTicketData?.stampPhotoUrl || null;
  const na = !!JobBoardState._btFieldTicketData?.stampUnavailable;
  const preview = existing
    ? `<img src="${escapeHtml(existing)}" class="stamp-preview" style="margin-bottom:8px;max-height:120px;max-width:100%;border:1px solid #e2e8f0;border-radius:8px;">`
    : (na ? `<span style="font-size:13px;color:#64748b;font-style:italic;">Stamp not available</span>` : '');
  const btn = 'font-size:0.8rem;font-weight:600;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#334155;padding:6px 12px;cursor:pointer;display:inline-flex;align-items:center;gap:4px;';
  const content = `
    <div id="stamp-preview-area" style="margin-bottom:8px;">${preview}</div>
    <div style="display:flex;gap:6px;flex-wrap:wrap;">
      <button type="button" onclick="document.getElementById('stamp-camera-input').click()" style="${btn}"><span class="material-icons" style="font-size:16px;">photo_camera</span> Capture</button>
      <button type="button" onclick="document.getElementById('stamp-file-input').click()" style="${btn}"><span class="material-icons" style="font-size:16px;">upload_file</span> Upload</button>
      <button type="button" id="btn-stamp-na" onclick="window.ClockModule.markStampNA()" style="${btn}">${na ? 'Undo N/A' : 'Not Available'}</button>
    </div>
    <input type="file" id="stamp-camera-input" hidden accept="image/*" capture="environment" onchange="window.ClockModule.handleStampCapture(this.files)">
    <input type="file" id="stamp-file-input" hidden accept="image/*" onchange="window.ClockModule.handleStampCapture(this.files)">`;
  return jbCollapsible('jb-sec-stamp', 'verified', 'Stamp / Proof Photo', content, true, existing ? 1 : null);
}

// TASK-395-B: Email-copy card. Sends a ticket copy on Save/Clock Out (dedup in persist).
function jbBuildEmailCopySection(jobDayId) {
  const jobDay = (JobBoardState.jobDays || []).find(jd => jd.id === jobDayId);
  const fts = _jbJobDayFts(jobDay); // TASK-10190
  if (!fts?.requireEmailCopy) return '';
  const prefill = JobBoardState._btFieldTicketData?.emailSentTo || fts.defaultEmailRecipient || '';
  const content = `
    <input type="email" id="ft-email-to" value="${escapeHtml(prefill)}" placeholder="Email address…" style="width:100%;padding:10px;font-size:0.9rem;border:1px solid #e2e8f0;border-radius:10px;background:#f8fafc;color:#0f172a;">
    <div style="font-size:0.72rem;color:#94a3b8;margin-top:6px;">A copy of the ticket is emailed here when you save or clock out. Once it is sent, only the office can change the ticket.</div>`;
  return jbCollapsible('jb-sec-emailcopy', 'email', 'Email Ticket Copy', content, true, null);
}

// ============================================
// TASK-9169 — "What's on this ticket" recap, directly above the Shift Notes box.
//
// Ethan (TITS driver, 2026-09-11): in the clock-out form the Shift Notes box is
// the FIRST thing on the screen, and everything that says what he actually did
// — Equipment, Downtime, Standby, the custom sections — is below it, several of
// them COLLAPSED (TASK-1188 collapses Equipment on a labor-only session; every
// custom section starts closed). So to write the note he scrolls down, opens
// cards, scrolls back up, and writes from memory. His ask, verbatim: go over
// "all of the ticket notes ... to verify what work was done" while writing the
// shift note.
//
// A READ-ONLY recap of what is already on this same screen. Four properties:
//
//   * ZERO READS. Everything comes from state jbRestoreTicketData already
//     loaded (btEquipment / btDowntime / btStandby / btPhotos) or from the live
//     DOM (jbCollectCustomSectionData). No Firestore call, no subscription —
//     this must not add a millisecond to a clock-out on a phone in the field.
//   * NEVER STALE. He fills Equipment BELOW this card and scrolls back up; a
//     recap rendered once at modal-assembly time shows him the state from
//     before he typed — COD's Code Reviewer caught exactly that in the first
//     cut of this feature. Three triggers keep it honest: every expand; a
//     debounced delegated input/change/CLICK listener on the modal (click
//     matters — removing equipment, adding downtime, deleting a photo are all
//     buttons that mutate state and emit no input event, COD round 2); and one
//     bounded catch-up pass 1.2s after open for the ASYNC restores (custom
//     sections, own shift notes, photo thumbnails) that land after assembly
//     and fire no event at all. For the same reason there is deliberately NO
//     count badge on the header — a number rendered once is a number that is
//     wrong by the time he reads it.
//   * OPEN WHEN THERE IS SOMETHING TO SEE. Three COD lanes (Skeptic, UX,
//     Domain) landed on the same objection to a collapsed default: "a driver
//     who doesn't tap it gets zero benefit — the same problem the ticket
//     exists to solve, one card higher." So it starts EXPANDED whenever state
//     already holds equipment/downtime/standby/photos (the normal case — the
//     equipment auto-fill has run by assembly time) and collapsed only when
//     there is genuinely nothing on the ticket yet.
//   * NO MONEY, EVER. eq.rate / rateUnit sit right there in btEquipment and are
//     never rendered. TASK-655's COD put dollars off the driver self-view
//     (shared-tablet shoulder-surfing) and TITS runs
//     settings.showPricesOnDriverTickets = false. Hours and descriptions only,
//     under either regime — so there is no price toggle to honor here because
//     there is no price.
//   * NO NEW EXPOSURE. Every value is already rendered to this same person in
//     this same modal a few hundred pixels lower. This moves data on one
//     screen; it does not widen access. No firestore.rules change, no schema
//     change, no new field written.
//
// Collapsed by default: the Shift Notes box must stay exactly where it is today
// (first element of the first card), or the fix for "I have to scroll" becomes
// a new reason to scroll.
// ============================================

// One recap line. TEXT CONTENT ONLY — no value reaches an attribute or an
// inline handler argument, which is what makes jobboard.js's local escapeHtml
// (:3477, no `'`) sufficient on this surface. Keep it that way: an asset name
// like O'Brien's would break a quoted inline handler, and no HTML escaper fixes
// that context ([[lesson_escapehtml_is_not_attribute_safe]]).
// TASK-9169 — the transient the GPS button parks in the field while the fix is
// in flight. Named because the recap must SKIP it (a recap line reading
// "Location: Getting location..." is worse than no line) and the two places
// that care must not drift apart.
const JB_CS_GPS_PENDING = 'Getting location...';

// 🔴 COD Code Reviewer ❌ (blocking, first pass): `icon` is not always a
// literal — the custom-section rows pass tenant-authored `cs.icon` straight out
// of Firestore into innerHTML. escapeHtml alone would neuter it, but a Material
// ligature is only ever [a-z0-9_], so an ALLOWLIST is both stricter and
// self-documenting: anything else becomes the neutral default rather than
// markup. Applies to every caller, not just the custom-section one.
function _jbRecapIcon(icon) {
  return /^[a-z0-9_]{1,40}$/.test(String(icon || '')) ? String(icon) : 'tune';
}

function _jbRecapRow(icon, title, meta, body) {
  return `
    <div style="display:flex;gap:8px;padding:8px 0;border-top:1px solid #e2e8f0;">
      <span class="material-icons" style="font-size:16px;color:#94a3b8;flex-shrink:0;margin-top:2px;">${_jbRecapIcon(icon)}</span>
      <div style="min-width:0;flex:1;">
        <div style="display:flex;gap:8px;align-items:baseline;justify-content:space-between;">
          <span style="font-size:0.8rem;font-weight:600;color:#0f172a;word-break:break-word;">${escapeHtml(title)}</span>
          ${meta ? `<span style="font-size:0.75rem;color:#64748b;white-space:nowrap;flex-shrink:0;">${escapeHtml(meta)}</span>` : ''}
        </div>
        ${body ? `<div style="font-size:0.78rem;color:#475569;white-space:pre-wrap;margin-top:2px;word-break:break-word;">${escapeHtml(body)}</div>` : ''}
      </div>
    </div>`;
}

// Flatten the LIVE custom-section values into "Label: value" lines. Goes
// through jbCollectCustomSectionData so it reads what is on screen right now —
// including instances added this session and not yet saved, which is exactly
// the work a driver is most likely to have forgotten by the time he writes the
// note. A collapsed section is display:none, not detached, so its inputs still
// answer querySelector.
function _jbRecapCustomRows() {
  const defs = _jbCsDefs();
  if (!defs.length) return '';
  let collected = {};
  try {
    collected = jbCollectCustomSectionData() || {};
  } catch (e) {
    console.warn('[JobBoard] recap: custom-section read failed:', e?.message);
    return '';
  }
  let html = '';
  for (const cs of defs) {
    if (!jbCOFEnabled('custom_' + cs.id)) continue;
    const val = collected[cs.id];
    if (!val) continue;
    // COD Code Reviewer — reuse the existing normalizer instead of re-deriving
    // the repeatable/legacy shape (it is the same rule, in one place).
    const instances = _jbCsdInstancesOf(val);
    instances.forEach((inst, i) => {
      const parts = [];
      for (const f of (cs.fields || [])) {
        const v = inst?.[f.id];
        // Skip anything the driver left alone: empty text, an unchecked box, a
        // number still at 0. A recap padded with "Loads: 0" teaches him to stop
        // reading it.
        if (v === undefined || v === null || v === '' || v === false) continue;
        if (f.type === 'number' && Number(v) === 0) continue;
        // The GPS button parks a transient in the field while the fix is in
        // flight. Never echo it — a callback refresh replaces it within
        // seconds, and until then no line beats a wrong one.
        if (v === JB_CS_GPS_PENDING) continue;
        // 🔴 COD Code Reviewer — a FILE field's collected value is the raw
        // Storage PATH out of the hidden input (TASK-4607, :5635), not a
        // filename. Printing it puts internal bucket paths on the driver's
        // screen and tells him nothing about the work. Report presence only.
        if (f.type === 'file') { parts.push(`${f.label || f.name || f.id}: attached`); continue; }
        parts.push(`${f.label || f.name || f.id}: ${f.type === 'checkbox' ? 'Yes' : v}`);
      }
      if (!parts.length) return;
      const title = instances.length > 1 ? `${cs.name || 'Section'} ${i + 1}` : (cs.name || 'Section');
      html += _jbRecapRow(cs.icon || 'tune', title, '', parts.join(' · '));
    });
  }
  return html;
}

// Build the recap body from CURRENT state. Each category is gated by the same
// jbCOFEnabled key that decides whether its section renders at all, so a
// carrier that turned Downtime off never sees downtime here either.
function _jbRecapBuild() {
  let html = '';
  let rows = 0;

  if (jbCOFEnabled('equipment')) {
    // 🔴 Found by the live driver-PIN run, not by review: btEquipment comes back
    // in ticketLines DOC-ID order, so on a shared ticket a COWORKER's truck can
    // head the list — the driver opens the panel and the first thing he reads is
    // somebody else's work. His own lines lead; crew context follows. Stable
    // within each group (no reordering beyond the own/crew split), and a copy —
    // never sort btEquipment itself, the equipment rows below index into it and
    // jbRemoveEquip/jbUpdateEquipTime address lines BY INDEX.
    // TASK-9188 — rank, not a boolean: his OWN earlier session (the lunch
    // re-clock) used to sort into the crew bucket and land at the bottom of his
    // own summary. Order is now this session, then his earlier shift, then crew.
    const _eq = (JobBoardState.btEquipment || []).slice().sort((a, b) =>
      _jbRecapOwnerRank(a) - _jbRecapOwnerRank(b));
    for (const eq of _eq) {
      const name = [eq.assetName || 'Equipment', eq.fleetNumber ? `#${eq.fleetNumber}` : ''].filter(Boolean).join(' ');
      const hrs = jbRowHours(eq);
      // TASK-1188 — a coworker's line is legitimate context on a shared ticket
      // ("what did the crew do"), but it must stay labelled as theirs so he
      // doesn't summarize someone else's hours as his own.
      // TASK-9188 — and his OWN earlier segment is not the crew's. It stays
      // tagged (these are not this session's hours) but tagged TRUTHFULLY.
      const _rank = _jbRecapOwnerRank(eq);
      const meta = `${jbRowHoursPreview(eq)}${_rank === 2 ? ' · crew' : _rank === 1 ? ' · earlier shift' : ''}`;
      html += _jbRecapRow(eq.assetType === 'trailer' ? 'rv_hookup' : 'local_shipping', name, meta, eq.notes);
      rows++;
    }
  }

  if (jbCOFEnabled('downtime')) {
    for (const d of (JobBoardState.btDowntime || [])) {
      html += _jbRecapRow('pause_circle', 'Downtime', jbRowHoursPreview(d), d.notes);
      rows++;
    }
  }

  if (jbCOFEnabled('standby')) {
    for (const s of (JobBoardState.btStandby || [])) {
      html += _jbRecapRow('hourglass_empty', 'Standby', jbRowHoursPreview(s), s.notes);
      rows++;
    }
  }

  const csHtml = _jbRecapCustomRows();
  if (csHtml) { html += csHtml; rows++; }

  if (jbCOFEnabled('photos')) {
    const n = (JobBoardState.btPhotos || []).length;
    if (n > 0) { html += _jbRecapRow('photo_camera', `${n} photo${n === 1 ? '' : 's'} on this ticket`, '', ''); rows++; }
  }

  if (rows === 0) {
    return { rows, html: `<div style="font-size:0.8rem;color:#64748b;padding:8px 0;">Nothing recorded on this ticket yet — the sections below are where it goes.</div>` };
  }
  return { rows, html: `<div style="font-size:0.72rem;color:#94a3b8;padding-bottom:2px;">Read-only — change any of it in the sections below.</div>${html}` };
}

// NEVER TRAP THE DRIVER: a recap that throws must not take the Notes card — and
// therefore the shift-notes box he is REQUIRED to fill — down with it. Every
// entry point into the renderer goes through this wrapper.
function jbWorkRecapBodyHtml() {
  try {
    return _jbRecapBuild().html;
  } catch (e) {
    console.warn('[JobBoard] work recap render failed:', e?.message);
    return `<div style="font-size:0.8rem;color:#64748b;padding:8px 0;">Couldn't load the recap — the sections below still have everything.</div>`;
  }
}

// Repaint IF AND ONLY IF the recap is currently open. Cheap enough to call from
// a debounced listener; a no-op while collapsed or once the modal is gone.
function _jbRecapRefresh() {
  const body = document.getElementById('jb-recap-body');
  if (!body || body.style.display === 'none') return;
  body.innerHTML = jbWorkRecapBodyHtml();
}

// 🔴 COD round 2, Code Reviewer ❌ — "custom-only tickets remain collapsed."
// jbBuildNotesSection runs BEFORE jbBuildCustomSectionsHtml, so at assembly the
// custom inputs do not exist yet and the collector legitimately finds nothing.
// On a labor-only shift whose ONLY content is custom-section data (a TITS
// driver with Field Activity dumps and no equipment line) that meant rows === 0
// → collapsed → and the catch-up pass then returned early *because* it was
// collapsed. The recap stayed shut on exactly the ticket it exists for.
//
// So the catch-up may OPEN, not merely repaint. It never CLOSES an open recap:
// collapsing something the driver may be mid-read is strictly worse than a
// stale row, and he can always collapse it himself.
function _jbRecapCatchUp() {
  const body = document.getElementById('jb-recap-body');
  if (!body) return;
  if (body.style.display !== 'none') { _jbRecapRefresh(); return; }
  let built;
  try {
    built = _jbRecapBuild();
  } catch (e) {
    console.warn('[JobBoard] work recap catch-up failed:', e?.message);
    return;
  }
  if (!built || built.rows === 0) return;
  body.innerHTML = built.html;
  body.style.display = 'block';
  const chev = document.getElementById('jb-recap-chev');
  if (chev) chev.textContent = 'expand_less';
  const hdr = document.getElementById('jb-recap-header');
  if (hdr) hdr.setAttribute('aria-expanded', 'true');
}

// Live-refresh wiring. Called once, after the clock-out modal is in the DOM.
// The listeners hang off the modal ELEMENT itself, so closeClockOutModal()'s
// `modal.remove()` disposes of them — nothing to unbind, nothing to leak. The
// `_jbRecapBound` guard is per-element for the same reason: a reopen builds a
// fresh element that has never been bound, so it binds once and only once.
function _jbInstallRecapLiveRefresh() {
  const modal = document.getElementById('clockout-modal');
  if (!modal || modal._jbRecapBound) return;
  modal._jbRecapBound = true;
  let t = null;
  const kick = (e) => {
    // 🔴 COD round 2, Independent Code Reviewer — a pending timer must never
    // act on a torn-down or, worse, a REPLACED modal. A reopen inside the
    // debounce window would otherwise let the old modal's timer drive the new
    // modal's recap. `isConnected` is the precise test; the getElementById
    // null-check alone does not catch the replaced-modal case.
    if (!modal.isConnected) { clearTimeout(t); return; }
    // The two notes textareas are the one thing whose own keystrokes must not
    // drive a repaint — they are not ticket content, and this fires on every
    // character the driver types into the box he is standing in.
    const id = e?.target?.id || '';
    if (id === 'jb-shift-notes' || id === 'jb-ticket-notes') return;
    clearTimeout(t);
    t = setTimeout(() => { if (modal.isConnected) _jbRecapRefresh(); }, 250);
  };
  modal.addEventListener('input', kick);
  modal.addEventListener('change', kick);
  // 🔴 COD round 2, Code Reviewer ❌ — "staleness remains: input/change only,"
  // and the list was exhaustive: removing equipment (jbRemoveEquip), adding or
  // removing downtime/standby, removing a repeatable custom instance, deleting
  // a photo — all BUTTON presses that mutate state and emit neither event.
  // Listening for `click` too covers every one of them, and every future one,
  // without threading a refresh call through six hot handlers (more edits, more
  // drift surface, and each one a place to forget). The repaint is debounced,
  // read-only, and rewrites only #jb-recap-body, so a click that changed
  // nothing costs one wasted innerHTML of about ten rows.
  modal.addEventListener('click', kick);
  // The async restores (custom sections via _jbRestoreCustomSections' bounded
  // retry, own shift notes, photo thumbnails) land AFTER assembly and set
  // element values programmatically — which fires no event at all. One bounded
  // catch-up pass, not a poll.
  setTimeout(() => { if (modal.isConnected) _jbRecapCatchUp(); }, 1200);
}

// Expand = rebuild (see the header comment: as of the tap, not as of assembly).
function jbToggleWorkRecap() {
  const body = document.getElementById('jb-recap-body');
  const chev = document.getElementById('jb-recap-chev');
  const hdr = document.getElementById('jb-recap-header');
  if (!body) return;
  const opening = body.style.display === 'none';
  if (opening) body.innerHTML = jbWorkRecapBodyHtml();
  body.style.display = opening ? 'block' : 'none';
  if (chev) chev.textContent = opening ? 'expand_less' : 'expand_more';
  if (hdr) hdr.setAttribute('aria-expanded', opening ? 'true' : 'false');
}

function jbBuildNotesSection() {
  // TASK-9169 — start EXPANDED when the ticket already carries work (COD:
  // Skeptic/UX/Domain all rejected a collapsed default), collapsed only when
  // there is nothing to show. Rendering the body here is safe against
  // staleness because _jbInstallRecapLiveRefresh keeps it current afterwards.
  const _recap = (() => {
    try { return _jbRecapBuild(); }
    catch (e) {
      console.warn('[JobBoard] work recap build failed:', e?.message);
      return { rows: 0, html: '' };
    }
  })();
  const _recapOpen = _recap.rows > 0;
  const recap = `
    <div style="margin-bottom:12px;border:1px solid #e2e8f0;border-radius:10px;background:#f8fafc;overflow:hidden;">
      <div id="jb-recap-header" role="button" tabindex="0" aria-expanded="${_recapOpen ? 'true' : 'false'}" aria-controls="jb-recap-body"
        onclick="window.JobBoardModule.jbToggleWorkRecap()"
        onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();window.JobBoardModule.jbToggleWorkRecap();}"
        style="display:flex;align-items:center;gap:8px;padding:10px 12px;min-height:44px;cursor:pointer;user-select:none;-webkit-tap-highlight-color:rgba(0,0,0,0.05);">
        <span class="material-icons" style="font-size:18px;color:#3b82f6;flex-shrink:0;">fact_check</span>
        <span style="flex:1;min-width:0;font-size:0.82rem;font-weight:600;color:#0f172a;">Review today's logged work</span>
        <span class="material-icons" id="jb-recap-chev" style="font-size:20px;color:#94a3b8;flex-shrink:0;">${_recapOpen ? 'expand_less' : 'expand_more'}</span>
      </div>
      <!-- 🔴 max-height is LOAD-BEARING, found by the live 400px run and not by
           any amount of reading: on a 400x780 phone the expanded recap measured
           359px tall and pushed the Shift Notes textarea to y=771 of 780 — i.e.
           the box the driver is REQUIRED to fill was below the fold, buried by
           the panel that exists to help him fill it. Capping the body and
           letting it scroll internally keeps the note box on screen (~y=590)
           while still showing four or five lines of work at a glance. Do not
           remove the cap to "show everything" — everything is one scroll away
           inside the panel, and the note box is the thing that must stay
           visible.
           NOTE: no -webkit-overflow-scrolling:touch here, deliberately (COD
           flagged the nested-scroller risk) — that legacy property is the one
           associated with iOS trapping momentum inside a nested scroller, and
           since iOS 13 momentum is the default anyway. Scroll CHAINING is also
           left at its default: never set overscroll-behavior:contain on this
           element, or reaching the end of the recap would stop the modal
           itself from scrolling. Still unverified on a real iPhone.
           (This comment is inside a template literal — no backticks.) -->
      <div id="jb-recap-body" style="${_recapOpen ? '' : 'display:none;'}padding:0 12px 12px;max-height:190px;overflow-y:auto;">${_recapOpen ? _recap.html : ''}</div>
    </div>
  `;
  const content = `
    ${recap}
    <div style="margin-bottom:12px;">
      <label style="font-size:0.75rem;font-weight:500;color:#64748b;display:block;margin-bottom:4px;">Shift Notes <span style="font-size:0.7rem;font-weight:400;">(internal)</span></label>
      <textarea id="jb-shift-notes" rows="3" placeholder="${window.AppState?.settings?.requireShiftNotes === true ? 'REQUIRED — What did you do today?' : 'What did you do today?'}" style="width:100%;padding:10px;font-size:0.9rem;border:1px solid #e2e8f0;border-radius:10px;resize:vertical;background:#f8fafc;color:#0f172a;"></textarea>
    </div>
    <div>
      <label style="font-size:0.75rem;font-weight:500;color:#64748b;display:block;margin-bottom:4px;">Ticket Notes <span style="font-size:0.7rem;font-weight:400;">(appears on invoice)</span></label>
      <textarea id="jb-ticket-notes" rows="3" placeholder="Notes for customer..." style="width:100%;padding:10px;font-size:0.9rem;border:1px solid #e2e8f0;border-radius:10px;resize:vertical;background:#f8fafc;color:#0f172a;"></textarea>
      <div style="font-size:0.72rem;color:#475569;margin-top:6px;line-height:1.4;">Shared with the whole crew — add your line, everyone's is kept.</div>
    </div>
  `;
  return jbCollapsible('jb-sec-notes', 'notes', 'Notes', content, true);
}

function jbBuildWorkStartStopSection() {
  const wt = JobBoardState.btTicketWorkTimes;
  const wsVal = wt?.workStartTime ? jbToLocalDT(wt.workStartTime.toDate ? wt.workStartTime.toDate() : new Date(wt.workStartTime), 'jb-work-start') : '';
  const weVal = wt?.workStopTime ? jbToLocalDT(wt.workStopTime.toDate ? wt.workStopTime.toDate() : new Date(wt.workStopTime), 'jb-work-stop') : '';
  const content = `
    <div style="display:flex;gap:8px;">
      <div style="flex:1;">
        <label style="font-size:0.75rem;font-weight:500;color:#64748b;display:block;margin-bottom:4px;">Work Start</label>
        <input type="datetime-local" id="jb-work-start" value="${wsVal}" style="width:100%;padding:10px;font-size:0.85rem;border:1px solid #e2e8f0;border-radius:10px;background:#f8fafc;color:#0f172a;">
      </div>
      <div style="flex:1;">
        <label style="font-size:0.75rem;font-weight:500;color:#64748b;display:block;margin-bottom:4px;">Work Stop</label>
        <input type="datetime-local" id="jb-work-stop" value="${weVal}" style="width:100%;padding:10px;font-size:0.85rem;border:1px solid #e2e8f0;border-radius:10px;background:#f8fafc;color:#0f172a;">
      </div>
    </div>
  `;
  return jbCollapsible('jb-sec-worktimes', 'timer', 'Work Start / Stop', content, false);
}

// TASK-428 Slice 3 — driver equipment-edit gate (UI-only, mirrors the
// allowDriverTimeEdit pattern at jobboard.js:4357). A non-driver (workforce/
// admin/SPA member) ALWAYS has full equipment edit. A driver-PIN/link session
// may add/remove/re-time equipment lines ONLY when the tenant has opted in via
// settings.allowDriverEquipmentEdit (DEFAULT OFF — dispatch owns equipment).
// When OFF, the driver still SEES the auto-filled equipment and can edit the
// per-equipment NOTES, but the Add button + per-row remove (x) are hidden and
// the start/end inputs are readonly. This NEVER blocks the clock-out save — the
// auto-filled equipment lines persist via jbSaveTicket exactly as today.
function jbCanDriverEditEquipment() {
  const isDriverScope = !!(window.AppState?.driverMode || window.AppState?.driverModePin || window.AppState?.isPublicAccess);
  if (!isDriverScope) return true;
  return window.AppState?.settings?.allowDriverEquipmentEdit === true;
}

// ============================================
// TASK-1572 Slice 1 — ASK THE DRIVER FOR THE TRUCK INSTEAD OF HIDING THE QUESTION
//
// MEASURED (re-measured 2026-08-26, all-time, live, deleted excluded, TITS):
// 57 non-internal $0 tickets. 70% carry a real clock punch, 70% carry EMPLOYEE
// hours > 0, and 344.7 recorded labor hours bill nothing. Only 4 are truly
// empty. **These are not garbage tickets — they are faithful records of labor
// that are SILENT ABOUT EQUIPMENT.** TITS bills the truck, never the person, so
// a ticket with hours and no truck bills exactly zero. Category C ("lines exist,
// NO equipment line at all") is 21 of the 57 and STILL GROWING — #100571 landed
// 2026-08-25. #100472 and #100478 are the canonical pair: both still $0, both
// still carrying a real customer invoice (INV-202608-062 / -059).
//
// The one person who knows which truck he drove is the driver, standing in the
// Build Ticket modal at close-out. Today he is told "Auto-filled from dispatch
// when assigned" — i.e. *it is not your job* — and TASK-1188 starts the section
// COLLAPSED. This replaces that dead label with an actual question.
//
// 🔴 THIS CAN NEVER BLOCK A CLOCK-OUT, AND STRUCTURALLY CANNOT.
// Everything here is RENDER-ONLY: it runs while building the modal's HTML, and
// the only state it touches is `JobBoardState._btTruckAskDismissed`. It adds no
// gate, no early return, and no write to `submitClockOut`, which still completes
// the clock entry in STEP 1 before the ticket is touched at all (TASK-428/489:
// a validation that returned before the write stranded Carlos and Jayson clocked
// in 14+ hrs). A missing truck is a line a biller fixes in ten seconds; a blocked
// clock-out is a person stuck in a yard at midnight. The prompt is an invitation,
// never a precondition — see the "Explicitly NOT doing" note on the board.
//
// 🔑 WHY THIS IS DERIVED AND NOT A NEW SETTING — and why it is NOT the Price
// Book check the board originally specified. The board's derived rule was "a
// ticket is incomplete when it has no line of a type that is billable for this
// customer per their Price Book." That rule is correct, and it ALREADY SHIPPED
// where it can actually be evaluated: billing.js `_zeroTotalGate` (TASK-1567),
// which sums the persisted lineAmounts the same way the invoice does. It cannot
// be evaluated HERE, for two independent reasons verified at HEAD:
//   1. **PIN drivers cannot read contracts.** `firestore.rules` gates
//      customers/{id}/contracts (and its lineItems) on `isTenantMember`, while
//      `customers` itself is `canReadTenant` (member OR driver session). A
//      contract read from a driver PIN session fails closed — the exact trap
//      TASK-395 already hit here ("PIN drivers can't read contracts, so the old
//      contract fetch failed closed and the canvas never appeared for them").
//   2. **At clock-out NO line has a rate yet.** jobboard.js writes EVERY line —
//      EMPLOYEE and EQUIPMENT alike — at `rate: 0, lineAmount: 0` (:8523/:8543)
//      and never reads the Price Book at all. Pricing is applied later, by a
//      biller. So "does this ticket have a billable line?" is unanswerable on
//      this surface for every tenant, not just TITS.
// The signal that IS available and IS derived: the tenant's own `equipmentTie`
// opt-in (a tenant that turned it on has DECLARED that a punch is expected to
// carry equipment), the customer's `internalTimeOnly` flag (readable by drivers,
// the canonical "$0 is legitimate here" owner), and the TASK-1547 assetProvenance
// that already distinguishes "never asked" from "deliberately labor-only".
// No checkbox, nothing to drift, and the other 78 tenants see nothing new.

// Mirrors billing.js `_ticketCustomerIsInternalTime` / clock.js TASK-1175
// detection: the flag first, the name fallback ONLY when the flag is undefined.
// Third copy by design — Pitfall #1 forbids cross-importing a `?v=`-busted
// module, and each surface reads its own customer list. Change all three or they
// silently disagree about which customers are allowed to bill $0.
const _JB_INTERNAL_NAME_FALLBACK = /^(shop|office|internal|yard)$/i;
function _jbCustomerIsInternalTime(customerId) {
  if (!customerId) return false;
  const cust = (JobBoardState.customers || []).find(c => c.id === customerId);
  if (!cust) return false;
  if (cust.internalTimeOnly === true) return true;
  if (cust.internalTimeOnly === undefined && _JB_INTERNAL_NAME_FALLBACK.test(cust.name || '')) return true;
  return false;
}

// Should the Build Ticket modal ASK for the truck? Every arm is a reason NOT to
// ask, because a prompt the driver cannot act on (or has already answered) is
// nagging, and nagging is how "No Equipment (Labor Only)" stops meaning anything.
//
// 🔴 THE WHOLE BODY IS try/caught AND FAILS TO `false`. This runs inside
// jbBuildEquipmentSection, which runs while ASSEMBLING THE CLOCK-OUT MODAL. A
// throw here would break the modal build and leave a driver with no way to end
// his shift — the TASK-428 failure shape exactly, arriving through a cosmetic
// feature instead of a security gate. Failing to `false` degrades to today's
// "Auto-filled from dispatch when assigned" behavior, which is merely the bug
// this task is fixing, not an outage.
function _jbShouldAskForTruck() {
  try {
    return _jbShouldAskForTruckInner();
  } catch (err) {
    console.warn('[JobBoard] TASK-1572 truck-ask predicate failed (degrading to no prompt):', err?.message);
    return false;
  }
}

function _jbShouldAskForTruckInner() {
  // Already answered THIS session (the driver tapped the labor-only out below).
  if (JobBoardState._btTruckAskDismissed === true) return false;
  // The ticket already has equipment — nothing to ask about.
  if ((JobBoardState.btEquipment || []).length > 0) return false;
  // The tenant has not declared that punches carry equipment. OFF by default;
  // this is what keeps the other 78 tenants byte-identical.
  if (!equipmentTieEnabled()) return false;
  // Asking someone who is not permitted to add equipment is cruel and useless —
  // the Add Equipment button is hidden for them anyway.
  if (!jbCanDriverEditEquipment()) return false;
  const entry = JobBoardState._btEntry;
  if (!entry) return false;
  // internalTimeOnly customers (Shop / Office / yard) legitimately produce $0
  // records forever. 77 TITS tickets are exactly this and are CORRECT.
  const job = (JobBoardState.jobs || []).find(j => j.id === entry.jobId);
  if (_jbCustomerIsInternalTime(entry.customerId || job?.customerId || null)) return false;
  // The heart of it: was the driver ever actually ASKED? TASK-1547's provenance
  // made "picked nothing from a real menu" (rules 4+5 — deliberate, 26 drivers
  // use it on purpose) distinguishable from "there was nothing to pick" (rule 6
  // — the bug). Only the second one earns a prompt.
  //
  // 🔴 `splitJob: false` IS DELIBERATE and is NOT the rule-2 hazard. Rule 2
  // exists because the mid-week LEGACY ALL-ASSETS fill would land the whole
  // day's rigs on EVERY sibling ticket of a split job (TASK-1221 double-bill).
  // Nothing is auto-filled here: the driver names ONE rig and it goes on HIS OWN
  // ticket, which is the correct outcome on a split job too. Passing false asks
  // the pure provenance question ("was he asked?") instead of the billing-policy
  // question, which is what this predicate is for.
  return _jbEntryCountsAsMissingAssetIds(entry, {
    splitJob: false,
    jobDayHasAssetsNow: _jbJobDayAssetIds(_jbResolveJobDayForEntry(entry)).size > 0
  });
}

// ORDER the picker by this operator's own recent equipment — never PRE-SELECT it.
// 🔴 Measured driver-to-truck concentration: mean top-1 share 32%, top-2 53%
// (Sean Wood's top truck is 16%) across 14 trucks. A "your usual truck" default
// would be WRONG ABOUT TWO TIMES IN THREE, and a wrong pre-fill converts MISSING
// data into CONFIDENTLY WRONG data — strictly worse, because missing is
// detectable and wrong is not. That is the same "a default that reads as a
// decision" failure that created this entire $0 class. Ordering is free when it
// guesses wrong; pre-selection is not.
//
// Source is the ALREADY-LOADED, already-windowed jobDays subscription (PIN driver
// = 7d back, admin = 90d) — no new query, no composite index, and therefore no
// read that could fail or hang on the clock-out path. Returns a Map of
// assetId -> times seen; absent means "no signal", which sorts last.
function _jbRecentAssetRank(personId) {
  const rank = new Map();
  if (!personId) return rank;
  const person = (JobBoardState.people || []).find(p => p.id === personId) || null;
  for (const jd of (JobBoardState.jobDays || [])) {
    // Scope to days this operator was actually crewed on. On an admin session
    // the subscription carries the WHOLE tenant's days, so without this the
    // "recent use" would be the yard's, not his.
    if (!_sharedJobDayAssignedToDriver(jd, personId, person)) continue;
    _jbJobDayAssetIds(jd).forEach(ref => {
      // 🔴 Pitfall #5 (legacy dual lookup) — a jobDay's trucks/trailers/assets
      // arrays hold RAW REFS, and GAS-imported days store a unit number or a
      // name where newer days store a doc id. The picker keys on `t.id`, so
      // ranking the raw ref would silently never match on exactly the older
      // days that carry the most history. Canonicalize to the doc id first;
      // an unresolvable ref (asset deleted since) keeps its raw key, which
      // simply never matches and sorts last — the correct degradation.
      const hit = _resolveFleetRef(ref, JobBoardState.trucks)
        || _resolveFleetRef(ref, JobBoardState.trailers)
        || _resolveFleetRef(ref, JobBoardState.assets);
      const key = hit && hit.id != null ? String(hit.id) : String(ref);
      rank.set(key, (rank.get(key) || 0) + 1);
    });
  }
  return rank;
}

// Sort helper shared by the picker: higher recent-use first, then by display
// name so the tail is stable and alphabetical rather than arbitrary.
function _jbSortByRecentUse(list, rank) {
  const nameOf = t => (window.getUnitDisplay ? window.getUnitDisplay(t) : '') || t.nickname || t.unit || t.name || '';
  return list.slice().sort((a, b) => {
    const ra = rank.get(String(a.id)) || 0;
    const rb = rank.get(String(b.id)) || 0;
    if (ra !== rb) return rb - ra;
    return nameOf(a).localeCompare(nameOf(b));
  });
}

// The prompt itself. Replaces the "Auto-filled from dispatch when assigned" dead
// label. Deliberately phrased as a question to the person who knows the answer.
function _jbTruckAskHtml() {
  return `
    <div id="jb-truck-ask" style="padding:12px;border:1px solid #fcd34d;background:#fffbeb;border-radius:10px;">
      <div style="display:flex;gap:8px;align-items:flex-start;">
        <span class="material-icons" style="font-size:20px;color:#b45309;flex:0 0 auto;">local_shipping</span>
        <div style="min-width:0;">
          <div style="font-size:0.9rem;font-weight:700;color:#78350f;line-height:1.3;">No equipment on this ticket. Were you in a truck?</div>
          <div style="font-size:0.78rem;color:#92400e;margin-top:4px;line-height:1.35;">Dispatch didn't have one assigned. Pick what you ran and it goes on the ticket — otherwise this day bills nothing.</div>
        </div>
      </div>
    </div>`;
}

// The one-tap out. 26 drivers use "No Equipment (Labor Only)" deliberately and it
// works — this must stay exactly that cheap. Render-only: it flips a local flag
// and repaints. It writes NOTHING, so it cannot fail, cannot hang, and cannot
// affect the clock-out that follows. The ticket is then labor-only exactly as it
// is today, and billing.js's $0 gate (TASK-1567) still catches it for the biller.
function jbDismissTruckAsk() {
  JobBoardState._btTruckAskDismissed = true;
  const listEl = document.getElementById('jb-equipment-list');
  if (listEl) listEl.innerHTML = '<div style="font-size:0.85rem;color:#64748b;padding:8px 0;">Labor only — no equipment on this ticket.</div>';
  const pickerEl = document.getElementById('jb-equipment-picker');
  if (pickerEl) { pickerEl.style.display = 'none'; pickerEl.innerHTML = ''; }
  // COD Code Reviewer catch: the button that was just tapped stayed on screen,
  // so the driver had no confirmation his answer registered and could sit there
  // tapping it. Retire it — the same thing jbSelectEquip does on the other exit.
  document.getElementById('jb-truck-ask-labor-only')?.remove();
}

// Post-render hook: open the picker beneath the prompt so the answer is one tap
// away instead of one tap plus a hunt for "Add Equipment". Idempotent, guarded,
// and wrapped — a throw here must never reach the clock-out path.
function _jbOpenTruckAskPicker() {
  try {
    if (!_jbShouldAskForTruck()) return;
    if (!document.getElementById('jb-truck-ask')) return;
    // jbShowEquipPicker toasts "All equipment already added" when the fleet is
    // empty. That is right for a deliberate tap and pure noise for an automatic
    // open, so check first rather than teaching the picker a new mode.
    const _used = new Set((JobBoardState.btEquipment || []).map(e => e.assetId));
    const _any = (JobBoardState.trucks || []).some(t => !_used.has(t.id))
      || (JobBoardState.trailers || []).some(t => !_used.has(t.id));
    if (!_any) return;
    jbShowEquipPicker();
  } catch (err) {
    console.warn('[JobBoard] TASK-1572 truck-ask picker open failed (non-fatal):', err?.message);
  }
}

function jbBuildEquipmentSection() {
  // Pre-existing: this read was unguarded. jbResetBuildTicketState always sets
  // btEquipment to [], so null never happens today — but this line is the FIRST
  // thing that runs when the clock-out modal is assembled, and a throw here means
  // a driver with no modal and no way to end his shift. The module's first law is
  // "never trap a driver"; a `|| []` is a cheap way to honor it.
  const count = (JobBoardState.btEquipment || []).length;
  const canEdit = jbCanDriverEditEquipment();
  // TASK-1572 Slice 1 — ask instead of hiding the question.
  const _ask = _jbShouldAskForTruck();
  // TASK-9579 — a date miss replaces the "auto-filled" label (which reads as
  // "dispatch assigned nothing"); the truck-ask, when it applies, already asks.
  const _miss = JobBoardState._btEquipDayMiss;
  const _missHere = !!_miss && (!_miss.entryId || _miss.entryId === (JobBoardState._btEntry?.id || JobBoardState._btEntryId || null));
  const eqHtml = count === 0
    ? (_ask
        // COD — when both apply, keep the date-specific explanation above the ask.
        ? (_missHere ? _jbEquipDayMissHtml(_miss) + '<div style="height:8px;"></div>' : '') + _jbTruckAskHtml()
        : (_missHere
            ? _jbEquipDayMissHtml(_miss)
            : '<div style="font-size:0.85rem;color:#64748b;padding:8px 0;">Auto-filled from dispatch when assigned</div>'))
    : jbRenderEquipmentList();

  const addBtnHtml = canEdit ? `
    <button type="button" style="margin-top:10px;padding:10px 14px;font-size:0.85rem;font-weight:600;border:2px dashed #cbd5e1;border-radius:10px;background:transparent;color:#3b82f6;cursor:pointer;display:flex;align-items:center;gap:6px;width:100%;justify-content:center;" onclick="window.JobBoardModule.jbShowEquipPicker()">
      <span class="material-icons" style="font-size:18px;">add_circle_outline</span> Add Equipment
    </button>
    <div id="jb-equipment-picker" style="display:none;margin-top:8px;"></div>` : '';

  // TASK-1572 Slice 1 — the one-tap out, rendered right under the picker so
  // "I wasn't in a truck" costs exactly one tap, same as it always has.
  const laborOnlyBtnHtml = _ask ? `
    <button type="button" id="jb-truck-ask-labor-only" onclick="window.JobBoardModule.jbDismissTruckAsk()"
      style="width:100%;min-height:48px;margin-top:8px;padding:12px;font-size:0.9rem;font-weight:600;border:2px dashed #cbd5e1;border-radius:12px;background:#f8fafc;color:#334155;cursor:pointer;">
      <span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:18px;">person</span>No Equipment (Labor Only)
    </button>` : '';

  const content = `
    <div id="jb-equipment-list">${eqHtml}</div>
    ${addBtnHtml}
    ${laborOnlyBtnHtml}
  `;
  // TASK-1188 — labor-only sessions (clocked in with no assets) whose only
  // visible equipment is the crew's shared context start COLLAPSED, so the
  // ticket doesn't lead with equipment "that followed them." Any own line
  // (auto-filled or manually added) keeps it open.
  const _entry = JobBoardState._btEntry;
  const _laborOnly = Array.isArray(_entry?.assetIds) && _entry.assetIds.length === 0;
  const _ownLineCount = (JobBoardState.btEquipment || []).filter(e => !jbIsCrewEquipmentLine(e)).length;
  // TASK-1572 Slice 1 — when we are ASKING, the section must start EXPANDED.
  // TASK-1188 deliberately collapses a labor-only session's equipment section,
  // and that is right when the [] means "he chose labor-only" — but _jbShouldAskForTruck
  // only returns true when the [] means "he was never asked", which is the one
  // case where a collapsed section hides the entire question.
  // TASK-9579 — same for the date-miss warning: collapsed, it is silent.
  const startOpen = _ask || (count === 0 && _missHere) || (count > 0 && !(_laborOnly && _ownLineCount === 0));
  return jbCollapsible('jb-sec-equipment', 'local_shipping', 'Equipment', content, startOpen, count > 0 ? count : null);
}

// TASK-1188 — Is this equipment line another crew member's, on a SHARED ticket?
// A line RESTORED from Firestore that carries a clockEntryId different from the
// current clock-out session's entry belongs to a coworker's shift. It renders
// read-only (a labor-only worker deleted 3 such lines believing he'd nuked the
// crew's billing — those deletes never persisted, but the editable UI misled
// him). Lines with no _clockEntryId (legacy pre-TASK-427, or freshly added this
// session) and lines matching the current session stay fully editable.
// ⚠️ TWIN — clock.js carries the same predicate as `clockIsCrewEquipmentLine`
// (ported in TASK-1396, whose trap was caused by clock.js NOT having it). A shared
// helper is impossible here: cross-importing a `?v=`-busted module is Pitfall #1, and
// each module reads its own state for "the current session's entry". Change both or
// the two surfaces silently diverge again.
function jbIsCrewEquipmentLine(eq) {
  if (!eq || eq._restored !== true || !eq._clockEntryId) return false;
  const curId = JobBoardState._btEntryId || null;
  return eq._clockEntryId !== curId;
}

// TASK-1188 — attribution for a read-only crew line. The owning session's
// operator is only resolvable when their clock entry is visible; a PIN driver's
// clockEntries subscription is personId-scoped (can't read coworkers), so this
// usually falls back to the generic "Crew equipment" label.
function jbCrewEquipmentOwnerLabel(eq) {
  const owner = (JobBoardState.clockEntries || []).find(e => e.id === eq._clockEntryId);
  const name = (owner?.personName || '').trim();
  return name ? `Crew equipment · added by ${name}'s shift` : 'Crew equipment';
}

// ============================================
// TASK-9188 - DISPLAY-ONLY OWNERSHIP OF A RESTORED LINE
// ============================================
// jbIsCrewEquipmentLine answers "is this line FROM ANOTHER SESSION?" - it never
// asked WHOSE. Re-clocking on the SAME ticket (the lunch case, TASK-427) restores
// session 1's lines carrying the PRIOR clockEntryId, so the driver's OWN morning
// truck came back labelled "Crew equipment", and TASK-9169's recap both tagged it
// `. crew` and sorted it to the bottom of his own work summary.
//
// The split COD demanded, and the reason this is a SECOND predicate rather than a
// change to the first: jbIsCrewEquipmentLine is a MUTATION GUARD
// (jbUpdateEquipTime / jbUpdateEquipNotes / jbRemoveEquip) whose twin
// clockIsCrewEquipmentLine in clock.js additionally governs validation and writes.
// Loosening it to "not mine" would re-open deletion of a closed segment's billed
// lines on two surfaces at once. Everything below is READ-ONLY: it changes what the
// line SAYS, never what the driver may do to it. A prior segment stays read-only,
// which is correct - it is closed.
//
// Resolution: the driver may `get` his OWN clockEntry by id (firestore.rules
// isOwnClockEntry, which matches the driverSession's personId) but NOT a
// coworker's, so one lazy read per DISTINCT foreign clockEntryId settles it, and a
// denial settles it the safe way. jbCrewEquipmentOwnerLabel above cannot do this
// job: it reads JobBoardState.clockEntries, whose query is personId-scoped AND
// `status == 'active'`, so a COMPLETED earlier session of his own is never in it.
//
// Cache key carries the personId so a PIN hand-off on the same device can never
// inherit the previous driver's verdicts. Values: 'me' | 'other'. Never cleared -
// a clockEntry's owner does not change.
const _jbLineOwnerCache = new Map();

function _jbLineOwnerKey(personId, entryId) { return `${personId}|${entryId}`; }

// TRUE only for a line this viewer wrote during an EARLIER session on this ticket.
// Unresolved, unreadable, or someone else's => false, i.e. it keeps the existing
// crew treatment. Fail closed by construction: a `get` we never made and a `get`
// that was denied are the same "not proven mine".
function jbIsOwnEarlierShiftLine(eq) {
  if (!jbIsCrewEquipmentLine(eq)) return false;
  const me = getActiveJobBoardPersonId();
  if (!me) return false;
  return _jbLineOwnerCache.get(_jbLineOwnerKey(me, eq._clockEntryId)) === 'me';
}

// Attribution caption for a read-only line. Own-earlier lines get named as his;
// a MERGED line (see _dedupMerged at the restore site) says so plainly rather than
// claiming hours that may be the interval-union of two operators' windows.
function jbLineAttributionLabel(eq) {
  if (!jbIsOwnEarlierShiftLine(eq)) return jbCrewEquipmentOwnerLabel(eq);
  return eq._dedupMerged
    ? "Your earlier shift · hours combined with the crew's"
    : 'Your earlier shift on this ticket';
}

// Recap ordering rank. 0 = this session, 1 = his own earlier session, 2 = crew.
// cbb4157e introduced the own-before-crew sort so a coworker's truck could not head
// the driver's own summary; before this task it demoted his own morning work with it.
function _jbRecapOwnerRank(eq) {
  if (!jbIsCrewEquipmentLine(eq)) return 0;
  return jbIsOwnEarlierShiftLine(eq) ? 1 : 2;
}

// One bounded pass, fired after the clock-out modal is in the DOM. Fire-and-forget
// and TOTALLY swallowed: attribution is a caption, and NOTHING here may take down
// the equipment card or the shift-note box the driver is required to fill.
async function _jbResolveOwnEarlierShiftLines() {
  try {
    const me = getActiveJobBoardPersonId();
    const tenant = JobBoardState.tenantId;
    if (!me || !tenant) return;
    const ids = [...new Set((JobBoardState.btEquipment || [])
      .filter(eq => jbIsCrewEquipmentLine(eq))
      .map(eq => eq._clockEntryId))]
      .filter(id => id && !_jbLineOwnerCache.has(_jbLineOwnerKey(me, id)));
    if (!ids.length) return;
    let foundOwn = false;
    for (const id of ids) {
      let verdict = 'other';   // fail closed - a denied read stays "crew"
      try {
        const snap = await getDoc(doc(db, 'tenants', tenant, 'clockEntries', id));
        if (snap.exists() && snap.data()?.personId === me) verdict = 'me';
      } catch (err) {
        console.warn('[JobBoard] line owner read failed (staying crew):', id, err?.message);
      }
      _jbLineOwnerCache.set(_jbLineOwnerKey(me, id), verdict);
      if (verdict === 'me') foundOwn = true;
    }
    if (!foundOwn) return;   // nothing the driver would see change
    const listEl = document.getElementById('jb-equipment-list');
    if (listEl) listEl.innerHTML = jbRenderEquipmentList();
    _jbRecapRefresh();
  } catch (err) {
    console.warn('[JobBoard] own-shift attribution pass failed:', err?.message);
  }
}

function jbRenderEquipmentList() {
  const canEdit = jbCanDriverEditEquipment();
  return JobBoardState.btEquipment.map((eq, idx) => {
    const hrs = jbRowHours(eq);
    // TASK-1188 — a coworker's restored line is READ-ONLY context: no delete,
    // no editable times/notes, clearly attributed. Own lines are unaffected
    // (notes stay editable even when the driver can't edit equipment, matching
    // the pre-existing jbCanDriverEditEquipment behavior).
    const isCrew = jbIsCrewEquipmentLine(eq);
    // TASK-9188 — DISPLAY ONLY. `isCrew` still decides read-only (a closed
    // segment's billed line is not editable here whoever wrote it); `isOwnPrior`
    // only decides what the row SAYS about who wrote it.
    const isOwnPrior = jbIsOwnEarlierShiftLine(eq);
    const rowCanEdit = canEdit && !isCrew;
    const timeBg = rowCanEdit ? '#fff' : '#f1f5f9';
    return `
      <div style="padding:12px;margin-bottom:8px;background:#f8fafc;border:1px solid #e2e8f0;${isCrew ? 'border-left:3px solid #94a3b8;' : ''}border-radius:10px;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;">
          <div style="display:flex;align-items:center;gap:8px;">
            <div style="width:28px;height:28px;border-radius:6px;background:${isCrew ? '#e2e8f0' : '#dbeafe'};display:flex;align-items:center;justify-content:center;">
              <span class="material-icons" style="font-size:16px;color:${isCrew ? '#64748b' : '#3b82f6'};">${eq.assetType === 'truck' ? 'local_shipping' : eq.assetType === 'trailer' ? 'rv_hookup' : 'build'}</span>
            </div>
            <div>
              <div style="font-size:0.85rem;font-weight:600;color:#0f172a;">${escapeHtml(eq.assetName)}</div>
              ${eq.fleetNumber ? `<div style="font-size:0.75rem;color:#64748b;">#${escapeHtml(eq.fleetNumber)}</div>` : ''}
              ${isCrew ? `<div style="font-size:0.7rem;color:#64748b;margin-top:2px;display:flex;align-items:center;gap:3px;"><span class="material-icons" style="font-size:12px;">${isOwnPrior ? 'history' : 'groups'}</span>${escapeHtml(jbLineAttributionLabel(eq))}</div>` : ''}
            </div>
          </div>
          <div style="display:flex;align-items:center;gap:8px;">
            <span style="font-weight:700;font-size:0.9rem;color:#3b82f6;">${escapeHtml(jbRowHoursPreview(eq))}</span>
            ${isCrew
              ? `<span title="${isOwnPrior ? 'From your earlier shift on this ticket — already closed out, read only' : 'Added by another crew member — read only'}" style="display:inline-flex;align-items:center;gap:3px;font-size:0.68rem;font-weight:600;color:#64748b;background:#e2e8f0;padding:3px 8px;border-radius:8px;"><span class="material-icons" style="font-size:13px;">lock</span>${isOwnPrior ? 'Earlier' : 'Crew'}</span>`
              : (canEdit ? `<button type="button" onclick="window.JobBoardModule.jbRemoveEquip(${idx})" style="background:none;border:none;cursor:pointer;padding:2px;display:flex;">
                  <span class="material-icons" style="font-size:18px;color:#94a3b8;">close</span>
                </button>` : '')}
          </div>
        </div>
        <div style="display:flex;gap:8px;">
          <!-- Release 1.15.1973 — min-width:0 on the flex columns + border-box inputs: a
               datetime-local's intrinsic width plus 8px padding ran the End box off the
               right edge of the 390px clock-out sheet. -->
          <div style="flex:1;min-width:0;">
            <label style="font-size:0.7rem;font-weight:500;color:#64748b;display:block;margin-bottom:3px;">Start</label>
            <input type="datetime-local" value="${eq.startTime}" ${rowCanEdit ? `onchange="window.JobBoardModule.jbUpdateEquipTime(${idx},'startTime',this.value)"` : 'readonly'} style="width:100%;min-width:0;box-sizing:border-box;font-size:0.8rem;padding:8px;border:1px solid #e2e8f0;border-radius:8px;background:${timeBg};color:#0f172a;">
          </div>
          <div style="flex:1;min-width:0;">
            <label style="font-size:0.7rem;font-weight:500;color:#64748b;display:block;margin-bottom:3px;">End</label>
            <input type="datetime-local" value="${eq.endTime}" ${rowCanEdit ? `onchange="window.JobBoardModule.jbUpdateEquipTime(${idx},'endTime',this.value)"` : 'readonly'} style="width:100%;min-width:0;box-sizing:border-box;font-size:0.8rem;padding:8px;border:1px solid #e2e8f0;border-radius:8px;background:${timeBg};color:#0f172a;">
          </div>
        </div>
        ${isCrew
          ? (eq.notes ? `<div style="margin-top:8px;padding:10px;font-size:0.85rem;border:1px solid #e2e8f0;border-radius:10px;background:#f1f5f9;color:#475569;white-space:pre-wrap;">${escapeHtml(eq.notes)}</div>` : '')
          : `<div style="margin-top:8px;">
              <textarea id="jb-eq-notes-${idx}" rows="2" placeholder="What did this equipment do? Loads hauled, task, location..." oninput="window.JobBoardModule.jbUpdateEquipNotes(${idx},this.value)"
                style="width:100%;padding:10px;font-size:0.85rem;border:1px solid #e2e8f0;border-radius:10px;background:#f8fafc;color:#0f172a;resize:vertical;">${escapeHtml(eq.notes || '')}</textarea>
            </div>`}
      </div>
    `;
  }).join('');
}

function jbUpdateEquipTime(idx, field, value) {
  const eq = JobBoardState.btEquipment[idx];
  if (!eq || jbIsCrewEquipmentLine(eq)) return; // TASK-1188: crew lines are read-only
  eq[field] = value;
  eq._dirty = true;
  // TASK-572: the driver took manual control of this window — it must never be
  // clobbered by a later clock-time edit re-sync (_jbSyncClockTrackedLines).
  eq._followsClock = false;
  document.getElementById('jb-equipment-list').innerHTML = jbRenderEquipmentList();
}

function jbUpdateEquipNotes(idx, value) {
  const eq = JobBoardState.btEquipment[idx];
  if (!eq || jbIsCrewEquipmentLine(eq)) return; // TASK-1188: crew lines are read-only
  eq.notes = value;
  // TASK-4594 — the ONLY field handler in the equipment/downtime/standby trio
  // that never set this. Save's equipment branch re-writes a line only when
  // `_dirty && _lineId`, and a restored line comes back `_dirty:false,
  // _saved:true` — so editing just the notes on an already-saved line hit
  // neither the update nor the create branch and was dropped, with the button
  // still reading "Saved". `jbUpdateTimeEntry` (:4031) sets it unconditionally
  // for exactly this reason, including for its own notes edits; this is the
  // same rule, applied to the one handler that was missing it.
  eq._dirty = true;
}

function jbRemoveEquip(idx) {
  const eq = JobBoardState.btEquipment[idx];
  if (!eq || jbIsCrewEquipmentLine(eq)) return; // TASK-1188: crew lines can't be deleted here
  JobBoardState.btEquipment.splice(idx, 1);
  document.getElementById('jb-equipment-list').innerHTML = jbRenderEquipmentList();
}

function jbShowEquipPicker() {
  // TASK-428 Slice 3 — defensive: when a driver isn't permitted to edit
  // equipment the Add button is hidden, but guard here too so a stale handler
  // can't add equipment. Non-driver / opted-in tenant always passes.
  if (!jbCanDriverEditEquipment()) return;
  const usedIds = new Set(JobBoardState.btEquipment.map(e => e.assetId));
  let trucks = JobBoardState.trucks.filter(t => !usedIds.has(t.id));
  let trailers = JobBoardState.trailers.filter(t => !usedIds.has(t.id));

  // TASK-1572 Slice 1 — ORDER by this operator's own recent equipment so the
  // likely answer is near the top of the list. NEVER pre-selected: the first
  // <option> stays the empty "Select equipment..." placeholder, so a driver who
  // opens and dismisses this picker adds NOTHING. Ordering only, and only where
  // the tenant tied equipment to punches — the other 78 tenants keep the exact
  // list order they have today.
  // Ordering is a nicety; Add Equipment working is not. A throw here would break
  // the picker for every tenant, so it degrades to the unsorted list.
  try {
    if (equipmentTieEnabled()) {
      const _rank = _jbRecentAssetRank(getActiveJobBoardPersonId());
      if (_rank.size > 0) {
        trucks = _jbSortByRecentUse(trucks, _rank);
        trailers = _jbSortByRecentUse(trailers, _rank);
      }
    }
  } catch (err) {
    console.warn('[JobBoard] TASK-1572 recent-use ordering failed (using default order):', err?.message);
  }

  if (trucks.length === 0 && trailers.length === 0) {
    window.showToast?.('All equipment already added', 'info');
    return;
  }

  let options = '<option value="">Select equipment...</option>';
  if (trucks.length > 0) {
    options += '<optgroup label="Trucks">';
    trucks.forEach(t => {
      const display = window.getUnitDisplay ? window.getUnitDisplay(t) : (t.nickname ? `${t.nickname} — #${t.unit}` : (t.unit || t.name || ''));
      options += `<option value="truck:${t.id}">${escapeHtml(display)}</option>`;
    });
    options += '</optgroup>';
  }
  if (trailers.length > 0) {
    options += '<optgroup label="Trailers">';
    trailers.forEach(t => {
      const display = window.getUnitDisplay ? window.getUnitDisplay(t) : (t.nickname ? `${t.nickname} — #${t.unit}` : (t.unit || t.name || ''));
      options += `<option value="trailer:${t.id}">${escapeHtml(display)}</option>`;
    });
    options += '</optgroup>';
  }

  const pickerEl = document.getElementById('jb-equipment-picker');
  pickerEl.innerHTML = `<select class="form-control" onchange="window.JobBoardModule.jbSelectEquip(this.value)" style="width:100%;padding:10px;font-size:0.9rem;">${options}</select>`;
  pickerEl.style.display = 'block';
}

function jbSelectEquip(value) {
  if (!value) return;
  const [type, id] = value.split(':');
  const entry = JobBoardState._btEntry;
  const clockIn = entry?.clockIn?.toDate?.() || (entry?.clockIn ? new Date(entry.clockIn) : new Date());

  let item = null, assetName = 'Equipment', fleetNumber = '';
  if (type === 'truck') {
    item = JobBoardState.trucks.find(t => t.id === id);
    assetName = ((item && window.getUnitDisplay) ? window.getUnitDisplay(item) : '') || item?.name || item?.unit || 'Truck';
    fleetNumber = item?.unit || '';
  } else if (type === 'trailer') {
    item = JobBoardState.trailers.find(t => t.id === id);
    assetName = ((item && window.getUnitDisplay) ? window.getUnitDisplay(item) : '') || item?.name || item?.unit || 'Trailer';
    fleetNumber = item?.unit || '';
  }
  if (!item) return;

  JobBoardState.btEquipment.push({
    assetId: item.id, assetName, assetType: type, fleetNumber,
    rate: 0, rateUnit: 'Hourly',
    startTime: jbToLocalDT(clockIn), endTime: jbToLocalDT(new Date()),
    // TASK-572: picker default = the clock window, so it tracks the clock
    // window too until the driver manually edits its times.
    notes: '', _dirty: false, _saved: false, _followsClock: true
  });
  document.getElementById('jb-equipment-picker').style.display = 'none';
  document.getElementById('jb-equipment-list').innerHTML = jbRenderEquipmentList();
  // TASK-1572 Slice 1 — the question has been answered: retire the "No Equipment
  // (Labor Only)" out so it can't sit under a truck the driver just named and
  // read as an offer to delete it.
  document.getElementById('jb-truck-ask-labor-only')?.remove();
}

// ============================================
// TASK-9264 — MATERIALS USED: the driver says what he used up.
//
// The equipment picker above lists trucks and trailers only. The person who
// knows "2 gallons of antimicrobial" or "6 T-posts" is the driver, at close-out,
// and until now he had no box for it. Chris 2026-10-28: build it.
//
// THE SHAPE, and why (TASKBOARD TASK-9264 hazards a-d):
//   - It is a CLAIM, never a stock move. The driver's list is saved on HIS OWN
//     clock entry (`consumablesReported`), not on `assets`, not on
//     `assetTransactions`, not as a ticket line. Drivers can read assets but
//     never write them (firestore.rules assets stanza; pinned by
//     tests/rules/task9264-driverAssetLedgerWrites.test.mjs) — nothing here
//     asks them to. The office commits the count later (follow-up on the row).
//   - Not a ticket line, on purpose: ticketLineDedup buckets EQUIPMENT lines by
//     assetId + rate and DELETES the extras, so two crew members claiming the
//     same item on one ticket would silently lose one (hazard a). One claim per
//     clock entry = one per person, nothing to merge away.
//   - Not a callable: driver clock-outs must work with no signal (the Firestore
//     queue — see ticketGateway.js header). The clock entry is already written
//     by this session at clock-out, bound to his personId by the clockEntries
//     self arm, so the claim rides a write that is already allowed offline.
//   - Not inside `assetProvenance`: that map is the clock-IN record the billing
//     predicates read (_jbEntryCountsAsMissingAssetIds); creating it on an entry
//     that never had one would change what those predicates decide.
//   - Dispatch's own deduction is untouched; reconciling the two (hazard b) is
//     the office's job at commit time, with both numbers in front of it.
//   - `confirmedBy: 'driver'` vs `'office'` — the distinction IS the value.
//
// 🔴 NEVER BLOCKS A CLOCK-OUT. Every function here is try/caught and fails to
// "show nothing / write nothing". The section is hidden until its list loads,
// the list load is not awaited by anything, and the save patch is {} on any
// doubt. Never pre-selected: the picker opens on an empty placeholder and a
// picked item's amount starts blank.
//
// GATE (Chris 2026-10-30: opt-in per tenant, the office accepts before stock
// moves): its OWN switch, settings.driverMaterialsReport === true, read
// EXPLICITLY (absent = off) — set on Settings → Features / AR/AP → Driver App.
// It no longer rides allowDriverEquipmentEdit: saying what you used up is not
// editing the dispatcher's equipment lines, and a tenant must be able to turn
// one on without the other. Tenants that never opted in see no change at all.
// Consumables only: rentals have their own go-out / come-back flow, PPE has its
// own sign-out (safety_gear), and neither belongs in "what did you use up".
// ============================================
// The list, its normalizers and the clock-entry patch live in the lazy
// views/jbMaterialsUsed.js (integ/2128: app cold-load budget). The list can only
// be changed (`_btConsumablesDirty`) through that chunk's UI, so once it is
// dirty the chunk is loaded: the patch never needs a network load at clock-out.
let _jbMatMod = null;

function _jbConsumablesOffered() {
  try {
    if (window.AppState?.settings?.driverMaterialsReport !== true) return false;
    return !!JobBoardState._btEntry;
  } catch (_) { return false; }
}

function _jbMatDeps() {
  return {
    S: JobBoardState, offered: _jbConsumablesOffered, jbCollapsible,
    now: () => Timestamp.now(), activePid: () => getActiveJobBoardPersonId(),
    fs: { db, collection, getDocs, query, where }
  };
}

// The clock-entry patch, or {} when there is nothing to write (chunk patch()).
// Plain values only — an `undefined` would make updateDoc throw, and on the
// clock-out path that throw would land in STEP 1. {} on ANY doubt.
function _jbConsumablesUpdate() {
  try {
    if (JobBoardState._btConsumablesDirty !== true || !_jbMatMod) return {};
    return _jbMatMod.patch(_jbMatDeps());
  } catch (err) {
    console.warn('[JobBoard] TASK-9264 materials patch failed (saving without it):', err?.message);
    return {};
  }
}

// An empty hidden wrap the chunk fills. A chunk that fails to load leaves the
// section hidden and writes nothing.
function jbBuildConsumablesSection() {
  try {
    if (!_jbConsumablesOffered()) return '';
    // Hidden until the chunk loads the list of items: a tenant with no
    // consumables set up never sees an empty box.
    return '<div id="jb-consumables-wrap" style="display:none;"></div>';
  } catch (err) {
    console.warn('[JobBoard] TASK-9264 materials section failed (skipping it):', err?.message);
    return '';
  }
}

// NOT awaited by the modal builder; a failure leaves the section hidden.
async function _jbLoadConsumableCatalog() {
  try {
    if (!_jbConsumablesOffered()) return;
    const entryAtStart = JobBoardState._btEntry;
    const m = await __ddChunkWrap(import('./jbMaterialsUsed.js?v=1'));
    _jbMatMod = m;
    await m.load(_jbMatDeps(), entryAtStart);
  } catch (err) {
    console.warn('[JobBoard] TASK-9264 materials list did not load (section stays hidden):', err?.message);
  }
}

function jbBuildDowntimeSection() {
  const content = `
    <div id="jb-downtime-list"><div style="font-size:0.85rem;color:#64748b;padding:8px 0;">No downtime entries</div></div>
    <button type="button" style="margin-top:10px;padding:10px 14px;font-size:0.85rem;font-weight:600;border:2px dashed #cbd5e1;border-radius:10px;background:transparent;color:#3b82f6;cursor:pointer;display:flex;align-items:center;gap:6px;width:100%;justify-content:center;" onclick="window.JobBoardModule.jbAddDowntime()">
      <span class="material-icons" style="font-size:18px;">add_circle_outline</span> Add Downtime
    </button>
  `;
  return jbCollapsible('jb-sec-downtime', 'pause_circle', 'Downtime', content, false);
}

function jbBuildStandbySection() {
  const content = `
    <div id="jb-standby-list"><div style="font-size:0.85rem;color:#64748b;padding:8px 0;">No standby entries</div></div>
    <button type="button" style="margin-top:10px;padding:10px 14px;font-size:0.85rem;font-weight:600;border:2px dashed #cbd5e1;border-radius:10px;background:transparent;color:#3b82f6;cursor:pointer;display:flex;align-items:center;gap:6px;width:100%;justify-content:center;" onclick="window.JobBoardModule.jbAddStandby()">
      <span class="material-icons" style="font-size:18px;">add_circle_outline</span> Add Standby
    </button>
  `;
  return jbCollapsible('jb-sec-standby', 'hourglass_empty', 'Standby', content, false);
}

function jbAddDowntime() {
  const entry = JobBoardState._btEntry;
  const clockIn = entry?.clockIn?.toDate?.() || (entry?.clockIn ? new Date(entry.clockIn) : new Date());
  // TASK-572: default = the clock window → tracks clock-time edits until the driver sets custom times
  JobBoardState.btDowntime.push({ startTime: jbToLocalDT(clockIn), endTime: jbToLocalDT(new Date()), notes: '', _saved: false, _dirty: false, _followsClock: true });
  jbRefreshTimeEntryList('downtime');
}

function jbAddStandby() {
  const entry = JobBoardState._btEntry;
  const clockIn = entry?.clockIn?.toDate?.() || (entry?.clockIn ? new Date(entry.clockIn) : new Date());
  // TASK-572: default = the clock window → tracks clock-time edits until the driver sets custom times
  JobBoardState.btStandby.push({ startTime: jbToLocalDT(clockIn), endTime: jbToLocalDT(new Date()), notes: '', _saved: false, _dirty: false, _followsClock: true });
  jbRefreshTimeEntryList('standby');
}

function jbRemoveDowntime(idx) {
  JobBoardState.btDowntime.splice(idx, 1);
  jbRefreshTimeEntryList('downtime');
}

function jbRemoveStandby(idx) {
  JobBoardState.btStandby.splice(idx, 1);
  jbRefreshTimeEntryList('standby');
}

function jbUpdateTimeEntry(type, idx, field, value) {
  const list = type === 'downtime' ? JobBoardState.btDowntime : JobBoardState.btStandby;
  list[idx][field] = value;
  list[idx]._dirty = true;
  // TASK-572: manual TIME edit — stop tracking the clock window (see
  // jbUpdateEquipTime). This handler also fires for notes edits — a notes-only
  // change must NOT detach the window from clock tracking.
  if (field === 'startTime' || field === 'endTime') list[idx]._followsClock = false;
  jbRefreshTimeEntryList(type);
}

function jbRefreshTimeEntryList(type) {
  const list = type === 'downtime' ? JobBoardState.btDowntime : JobBoardState.btStandby;
  const el = document.getElementById(`jb-${type}-list`);
  if (!el) return;

  if (list.length === 0) {
    el.innerHTML = `<div style="font-size:0.85rem;color:#64748b;padding:8px 0;">No ${type} entries</div>`;
    return;
  }

  const removeFn = type === 'downtime' ? 'jbRemoveDowntime' : 'jbRemoveStandby';
  el.innerHTML = list.map((item, idx) => {
    const hrs = jbRowHours(item);
    return `
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:12px;margin-bottom:8px;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;">
          <span style="font-weight:700;font-size:0.9rem;color:#3b82f6;">${escapeHtml(jbRowHoursPreview(item))}</span>
          <button type="button" onclick="window.JobBoardModule.${removeFn}(${idx})" style="background:none;border:none;cursor:pointer;padding:2px;display:flex;">
            <span class="material-icons" style="font-size:18px;color:#94a3b8;">close</span>
          </button>
        </div>
        <div style="display:flex;gap:8px;margin-bottom:8px;">
          <div style="flex:1;">
            <label style="font-size:0.7rem;font-weight:500;color:#64748b;display:block;margin-bottom:3px;">Start</label>
            <input type="datetime-local" value="${item.startTime}" onchange="window.JobBoardModule.jbUpdateTimeEntry('${type}',${idx},'startTime',this.value)" style="width:100%;font-size:0.8rem;padding:8px;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#0f172a;">
          </div>
          <div style="flex:1;">
            <label style="font-size:0.7rem;font-weight:500;color:#64748b;display:block;margin-bottom:3px;">End</label>
            <input type="datetime-local" value="${item.endTime}" onchange="window.JobBoardModule.jbUpdateTimeEntry('${type}',${idx},'endTime',this.value)" style="width:100%;font-size:0.8rem;padding:8px;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#0f172a;">
          </div>
        </div>
        <input type="text" value="${escapeHtml(item.notes)}" placeholder="Notes..." onchange="window.JobBoardModule.jbUpdateTimeEntry('${type}',${idx},'notes',this.value)" style="width:100%;padding:8px;font-size:0.85rem;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#0f172a;">
      </div>
    `;
  }).join('');
}

function _csGrabGPS(fieldId) {
  // Scoped to the open editor like every other custom-section lookup
  // (TASK-1204) — a bare getElementById returns the FIRST match in the page.
  const el = _jbCsRoot().querySelector(`[id="${fieldId}"]`) || document.getElementById(fieldId);
  if (!el) return;
  if (!navigator.geolocation) { window.showToast?.('GPS not available', 'error'); return; }
  el.value = JB_CS_GPS_PENDING;
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      el.value = `${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}`;
      // 🔴 TASK-9169, COD round 4 — an async .value assignment emits no input
      // event, so without this the recap keeps whatever it had when the button
      // was tapped. Both callbacks, not just the happy one: a DENIED fix
      // blanks the field, and a recap still showing a stale coordinate for a
      // location the driver never captured is the worse of the two lies.
      _jbRecapRefresh();
    },
    (err) => {
      el.value = '';
      _jbRecapRefresh();
      // err.code 1 = PERMISSION_DENIED — the browser is blocking location (a
      // previously-tapped "Block", or an in-app/text-message browser). Give the
      // driver an ACTIONABLE message + remind them they can just type the location.
      const denied = err && err.code === 1;
      const msg = denied
        ? 'Location is blocked. Turn on location for this site in your browser settings — or open the page in Chrome/Safari instead of a text-message preview — then tap Grab again. You can also just type the address or lat/long in the box.'
        : `Could not get location${err && err.message ? ' (' + err.message + ')' : ''}. Make sure location is on and you have signal — or type the address in the box instead.`;
      window.showToast?.(msg, 'error');
    },
    // maximumAge:0 — every Grab is a NEW pothole; never hand back the last fix.
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
  );
}

function _csInitWaypoints() {
  setTimeout(() => {
    document.querySelectorAll('[id^="jb-custom-"][data-cs-waypoint="true"]').forEach(el => {
      if (window.attachAddressAutocomplete) window.attachAddressAutocomplete(el);
    });
  }, 100);
}

const JB_CS_MAX_INSTANCES = 25;

// TASK-1204 — scope ALL custom-section DOM lookups to the Build Ticket modal.
// clock.js renders the SAME `[data-cs-instances="<csId>"]` markup for its own
// clock-out form (`#clock-out-body` persists hidden after close), so a bare
// document.querySelector can resolve to the WRONG surface when a member uses
// Clock and the Job Board in one SPA session — instances then spawn/collect
// against an invisible container and the visible form silently loses data.
function _jbCsRoot() {
  return document.getElementById('clockout-modal') || document;
}

// TASK-9337 -- settings.customTicketSections is now writable by billing-module
// STAFF, not just admins. Section and field ids are interpolated RAW into HTML
// attributes and onclick JS strings in this file, so a crafted id would run
// script in whoever opens the form (an admin, a driver). The Template Builder
// only ever mints 'cts_' / 'f_' + base36 ids, so drop any section or field whose
// id is not a plain token instead of escaping at every sink.
function _jbCsDefs() {
  const raw = window.AppState?.settings?.customTicketSections;
  if (!Array.isArray(raw)) return [];
  const ok = (v) => /^[A-Za-z0-9_-]{1,64}$/.test(String(v ?? ''));
  return raw
    .filter(cs => cs && ok(cs.id))
    .map(cs => (Array.isArray(cs.fields) ? { ...cs, fields: cs.fields.filter(f => f && ok(f.id)) } : cs));
}
const _jbCsEsc = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');


// =========================================================================
// TASK-4607 — custom-section FILE fields on the driver Build Ticket screen.
//
// The field's VALUE is a Storage path held in a hidden input, which is why none
// of the surrounding machinery needed touching: jbCollectCustomSectionData reads
// it as a string like any text field, _jbCsFillInstanceEls restores it, and the
// merge-save + offline stash carry it unchanged.
//
// Upload goes through the uploadTicketSectionFile CALLABLE, never a direct
// Storage write — the client cannot be trusted to bind a file to a ticket, and
// the storage.rules catch-all has no ticket binding to offer (see the block
// comment on that callable).
// =========================================================================
const JB_TSF_MAX_BYTES = 7 * 1024 * 1024;

// The stored path ends in `{timestamp}_{safeName}`. Show the human half.
function _jbTsfDisplayName(path) {
  const leaf = String(path || '').split('/').pop() || '';
  const m = leaf.match(/^\d{10,}_(.+)$/);
  return m ? m[1] : leaf;
}

// Repaint every file chip inside `root` from its hidden input's current value.
// Called after a restore, after an upload, and after a clear — the input is the
// single source of truth, the chip is only a view of it.
function _jbCsRefreshFileChips(root) {
  // 🔴 TASK-9169, COD round 4 — the file CHOKE POINT, same argument as the
  // photo one. A completed upload (:5684) and a queue drain (:5571) both write
  // the Storage path straight into a hidden input, which emits no event, and
  // the picker element lives OUTSIDE the modal (:5624) so its own change event
  // could never reach the delegated listener either. Every one of those paths
  // ends here. No-ops while the recap is collapsed or absent.
  _jbRecapRefresh();
  const scope = root || _jbCsRoot();
  if (!scope || !scope.querySelectorAll) return;
  scope.querySelectorAll('[data-cs-file-for]').forEach((wrap) => {
    const elId = wrap.getAttribute('data-cs-file-for');
    const input = scope.querySelector(`[id="${elId}"]`);
    const nameEl = wrap.querySelector('.jb-cs-file-name');
    const btn = wrap.querySelector('[data-cs-file-pick]');
    if (!input || !nameEl) return;
    const val = (input.value || '').trim();
    // TASK-4635 — a file sitting in the offline queue is NOT attached yet, and
    // the chip must not imply it is. Three states now: on the server (green),
    // waiting on this phone (amber, and it says so), nothing (grey).
    const queuedName = wrap.getAttribute('data-cs-file-queued-name');
    if (val) {
      // It landed — any queued marker is stale.
      wrap.removeAttribute('data-cs-file-queued-name');
      nameEl.textContent = _jbTsfDisplayName(val);
      nameEl.style.color = '#166534';
      nameEl.style.fontWeight = '600';
      if (btn) btn.lastChild && (btn.lastChild.textContent = ' Replace');
      if (!wrap.querySelector('[data-cs-file-clear]')) {
        const clear = document.createElement('button');
        clear.type = 'button';
        clear.setAttribute('data-cs-file-clear', elId);
        clear.title = 'Remove this attachment';
        clear.style.cssText = 'background:none;border:none;color:#94a3b8;cursor:pointer;padding:2px;line-height:1;';
        clear.innerHTML = '<span class="material-icons" style="font-size:18px;">close</span>';
        wrap.appendChild(clear);
      }
    } else if (queuedName) {
      // Deliberately NOT the word "attached", and no × — there is nothing on the
      // ticket to remove yet. #92400e on white is the same amber the unsaved
      // banner uses and clears 4.5:1.
      nameEl.textContent = `${queuedName} — waiting for signal`;
      nameEl.style.color = '#92400e';
      nameEl.style.fontWeight = '600';
      if (btn) btn.lastChild && (btn.lastChild.textContent = ' Attach');
      wrap.querySelector('[data-cs-file-clear]')?.remove();
    } else {
      nameEl.textContent = 'Nothing attached';
      nameEl.style.color = '#64748b';
      nameEl.style.fontWeight = '400';
      if (btn) btn.lastChild && (btn.lastChild.textContent = ' Attach');
      wrap.querySelector('[data-cs-file-clear]')?.remove();
    }
  });
}

// Read a File as base64 WITHOUT the data: prefix. Uses the shared reader so the
// iOS-WebView case that never settles is handled in one place.
function _jbTsfReadBase64(file) {
  return new Promise((resolve, reject) => {
    const rd = new FileReader();
    rd.onload = () => {
      const s = String(rd.result || '');
      const comma = s.indexOf(',');
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    rd.onerror = () => reject(new Error('Could not read that file off the phone.'));
    rd.onabort = () => reject(new Error('Reading that file was cancelled.'));
    rd.readAsDataURL(file);
  });
}

// =========================================================================
// TASK-4635 — a dead zone must not eat the attachment.
//
// TASK-4607 shipped the upload INLINE: no signal meant a dismissible toast and
// the file was gone. In the Bakken no signal is the normal case, so "attach"
// only worked on the good days.
//
// 🔴 The obvious reuse is the WRONG one. TASK-4602 already put an offline queue
// in this file, but ITS drain ends in a direct client `uploadBytes` to
// root-level `tickets/{tenant}/{ticketId}/`.
//
// ⚠️ Precisely: that path is NOT denied — `storage.rules` allows it on bare
// `canAccess(tenantId)` (any member OR any driver-PIN session in the tenant),
// with only a size/type check and NO ticket binding. That permissiveness is the
// problem, not a prohibition: it verifies the TENANT and has no way to express
// ticket OWNERSHIP, so a driver on a shared yard tablet could read or overwrite
// any ticket's permit and forge compliance ahead of office review. TASK-4607
// answered that by putting section files on a SEPARATE prefix,
// `tenants/{tid}/ticket-section-files/`, which storage.rules carves out as
// server-only in BOTH directions — client-denied even for its own ticket, so the
// callable is the only door. Pointing the file field at the photo drain would
// route around that door and land the bytes back on the permissive path
// (tests/rules/task4607-ticketSectionFiles.test.mjs pins the carve-out, and its
// mutation note records that removing it flips 7 of 11 denials to pass-through).
//
// So: the SAME queue, the same claim/retry/backoff plumbing, a SECOND drain arm
// whose terminal call is the `uploadTicketSectionFile` callable.
//
// How the two arms tell their rows apart: the queue item builder in
// `photoQueue.enqueue` whitelists its fields, so there is nowhere to hang a
// `kind` without editing that module. The tag therefore rides in `name`, which
// is a free string. The split is deliberately ASYMMETRIC and fails CLOSED:
//   • the photo arm EXCLUDES on the raw prefix — a corrupt tag is still excluded,
//     so a section file can never fall through to the direct-Storage upload;
//   • this arm requires a successful PARSE — a corrupt tag is undeliverable and
//     is given up on loudly, never guessed at.
// =========================================================================
const JB_TSF_QUEUE_TAG = 'tsf1:';
// 5 attempts, and `photoQueue.claim`'s 5-minute lease means they are at least
// five minutes apart — ~20 minutes of real-world backoff, not five hammered
// retries against the same dead radio.
const JB_TSF_MAX_ATTEMPTS = 5;
// Same 14 days as the row stash: long enough for a driver off-grid for a week,
// short enough that a warning on a since-invoiced ticket stops being wallpaper.
const JB_TSF_FAILED_TTL_MS = 14 * 24 * 60 * 60 * 1000;

function _jbTsfQueueName(meta) {
  // encodeURIComponent first: a filename can hold anything, and a raw `"` or a
  // newline inside the JSON would survive JSON.stringify but not a round trip
  // through everything that touches this string.
  return JB_TSF_QUEUE_TAG + encodeURIComponent(JSON.stringify(meta || {}));
}
// Prefix ONLY — this is the photo arm's exclusion test and it must not depend on
// the payload parsing.
function _jbTsfIsQueuedFile(item) {
  return String((item && item.name) || '').indexOf(JB_TSF_QUEUE_TAG) === 0;
}
function _jbTsfParseQueued(item) {
  if (!_jbTsfIsQueuedFile(item)) return null;
  try {
    const m = JSON.parse(decodeURIComponent(String(item.name).slice(JB_TSF_QUEUE_TAG.length)));
    // The callable validates ids as /^[a-zA-Z0-9_-]{1,128}$/ — anything else can
    // never land, so refuse it here rather than burn five attempts finding out.
    const idOk = (v) => typeof v === 'string' && /^[a-zA-Z0-9_-]{1,128}$/.test(v);
    if (!m || !idOk(m.s) || !idOk(m.f)) return null;
    return m;
  } catch (_) { return null; }
}

// A file the queue GAVE UP on. The blob is gone, so this record is the only way
// the driver ever learns it did not make it — a silent drop here is a permit the
// office thinks it has. localStorage, same as the row stash and for the same
// reason: it has to survive a tab close.
function _jbTsfFailedKey(ticketId) {
  return `dd_tsf_failed_${JobBoardState.tenantId || 'nt'}_${ticketId}`;
}
function _jbTsfReadFailed(ticketId) {
  if (!ticketId) return [];
  try {
    const raw = localStorage.getItem(_jbTsfFailedKey(ticketId));
    if (!raw) return [];
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    const fresh = list.filter(r => r && isFinite(Date.parse(r.at)) && (Date.now() - Date.parse(r.at)) <= JB_TSF_FAILED_TTL_MS);
    if (fresh.length !== list.length) {
      if (fresh.length) localStorage.setItem(_jbTsfFailedKey(ticketId), JSON.stringify(fresh));
      else localStorage.removeItem(_jbTsfFailedKey(ticketId));
    }
    return fresh;
  } catch (_) { return []; }
}
function _jbTsfRecordFailed(ticketId, rec) {
  try {
    const list = _jbTsfReadFailed(ticketId);
    list.push({ name: String(rec.name || 'the file'), reason: String(rec.reason || 'it could not be sent'), at: new Date().toISOString() });
    localStorage.setItem(_jbTsfFailedKey(ticketId), JSON.stringify(list.slice(-10)));
    return true;
  } catch (_) { return false; }
}
function _jbTsfDismissFailed(ticketId) {
  try { localStorage.removeItem(_jbTsfFailedKey(ticketId)); } catch (_) { /* nothing to do */ }
  _jbShowUnsavedState(ticketId || JobBoardState._btTicketId);
}

// Remember the Storage path a completed upload returned, keyed on the queue row.
// Without this, a placement failure that gets retried re-uploads the SAME bytes
// and leaves another orphan object nothing references — the exact cost the photo
// drain's destination pre-check exists to avoid. Best-effort: if localStorage is
// unavailable we just pay for the re-upload.
function _jbTsfUploadKey(itemId) { return `dd_tsf_up_${itemId}`; }
function _jbTsfRecallUpload(itemId) {
  try { return localStorage.getItem(_jbTsfUploadKey(itemId)) || null; } catch (_) { return null; }
}
function _jbTsfRememberUpload(itemId, path) {
  try { localStorage.setItem(_jbTsfUploadKey(itemId), path); } catch (_) { /* best effort */ }
}
function _jbTsfForgetUpload(itemId) {
  try { localStorage.removeItem(_jbTsfUploadKey(itemId)); } catch (_) { /* best effort */ }
}

// Terminal = the ticket/section/field is gone, the ticket is billed or locked, or
// the file itself is unacceptable. None of those get better by waiting, and
// retrying them just delays the driver learning the truth.
// 🔴 `permission-denied` and `unauthenticated` are deliberately NOT terminal: the
// commonest cause is a PIN session that has not been re-established yet (the
// drain fires on `online`, which can beat auth), and a re-PIN fixes it while the
// queue row is still there.
function _jbTsfIsTerminal(err) {
  const code = String((err && err.code) || '').replace(/^functions\//, '');
  return code === 'not-found' || code === 'invalid-argument' || code === 'failed-precondition';
}
// The callable's own messages are written for a driver and carry no doc paths,
// so they are safe to show verbatim. Anything else gets the generic line.
function _jbTsfTerminalReason(err) {
  const msg = String((err && err.message) || '').trim();
  return msg && msg.length <= 200 ? msg : 'the ticket would not accept it';
}

// Where a queued file's chip lives right now, or null if that instance is not on
// screen. Repeatable sections are POSITIONAL — `data-cs-instance` is a spawn
// counter, not a position — so walk the rendered instances in order, exactly the
// way _jbRestoreCustomSections maps saved[i] onto idxList[i].
function _jbTsfElIdFor(meta) {
  if (!meta) return null;
  if (meta.p === null || meta.p === undefined) return `jb-custom-${meta.s}-${meta.f}`;
  const root = _jbCsRoot();
  const wrap = root && root.querySelector ? root.querySelector(`[data-cs-instances="${meta.s}"]`) : null;
  if (!wrap) return null;
  const node = Array.from(wrap.querySelectorAll('.jb-cs-instance'))[meta.p];
  if (!node) return null;
  const idx = parseInt(node.dataset.csInstance, 10) || 0;
  return `jb-custom-${meta.s}-${idx}-${meta.f}`;
}

// Paint "waiting for signal" on the chips whose file is still in the queue.
// Display only — the durable copy is the IndexedDB row, so a chip we cannot find
// costs nothing but a missing hint.
function _jbTsfMarkQueuedChips(files) {
  const root = _jbCsRoot();
  if (!root || !root.querySelectorAll) return;
  root.querySelectorAll('[data-cs-file-queued-name]').forEach(el => el.removeAttribute('data-cs-file-queued-name'));
  for (const f of (files || [])) {
    const elId = _jbTsfElIdFor(f && f.meta);
    if (!elId) continue;
    const input = root.querySelector(`[id="${elId}"]`);
    // Never draw "waiting" over a field that already holds a real attachment —
    // that reads as "your permit is not up there" when it is.
    if (!input || (input.value || '').trim()) continue;
    const wrap = root.querySelector(`[data-cs-file-for="${elId}"]`);
    if (wrap) wrap.setAttribute('data-cs-file-queued-name', (f.meta && f.meta.n) || 'File');
  }
  _jbCsRefreshFileChips(root);
}

// This driver's queued SECTION FILES for one ticket. Costs a getPending (which
// materialises blob handles), so callers gate it behind the cheap index count —
// see _jbShowUnsavedState. Returns ok:false when the queue could not be read, so
// the caller can say "we could not check" instead of "nothing is waiting".
async function _jbTsfQueuedFor(ticketId) {
  const scope = _jbPqScope();
  if (!scope || !ticketId) return { ok: true, files: [] };
  try {
    const pending = await photoQueue.getPending(scope);
    const files = pending
      .filter(x => x && x.ticketId === ticketId && x.blob && _jbTsfIsQueuedFile(x))
      .map(x => ({ id: x.id, meta: _jbTsfParseQueued(x) }));
    return { ok: true, files };
  } catch (err) {
    console.warn('[JobBoard] section-file queue read failed:', err?.message || err);
    return { ok: false, files: [] };
  }
}

// Stage one picked file. Returns true only if it is genuinely on this phone's
// durable queue — the caller must not claim anything otherwise.
async function _jbTsfEnqueue(ticketId, file, sectionId, fieldId, elId) {
  const scope = _jbPqScope();
  if (!scope || !ticketId) return false;

  const root = _jbCsRoot();
  const input = root && root.querySelector ? root.querySelector(`[id="${elId}"]`) : null;
  const instNode = input && input.closest ? input.closest('.jb-cs-instance') : null;
  let pos = null;
  if (instNode) {
    const wrap = root.querySelector(`[data-cs-instances="${sectionId}"]`);
    if (wrap) {
      const nodes = Array.from(wrap.querySelectorAll('.jb-cs-instance'));
      const i = nodes.indexOf(instNode);
      pos = i >= 0 ? i : null;
    }
  }
  // Fingerprint the row's OTHER fields as they stand right now. At drain time the
  // instance array may have shifted (a row deleted, another added); this is what
  // lets the placement identify the row instead of trusting a bare index.
  // Built from the SECTION DEFINITION, never by parsing the element id — a field
  // id may itself contain a dash (`permit-no`), so splitting the id on the last
  // dash silently fingerprints the wrong key and the match never fires.
  let fp = null;
  const csDef = _jbCsDefs().find(s => s.id === sectionId);
  if (pos !== null && instNode && csDef) {
    const domIdx = parseInt(instNode.dataset.csInstance, 10) || 0;
    fp = {};
    for (const f of (csDef.fields || [])) {
      if (f.id === fieldId) continue;
      const el = root.querySelector(`[id="jb-custom-${sectionId}-${domIdx}-${f.id}"]`);
      if (!el) continue;
      fp[f.id] = el.type === 'checkbox' ? !!el.checked : String(el.value || '');
    }
  }

  // Replace, don't stack: tapping Attach twice on the same field should leave one
  // queued file, not two uploads racing to write the same path.
  try {
    const existing = await _jbTsfQueuedFor(ticketId);
    for (const q of existing.files) {
      if (q.meta && q.meta.s === sectionId && q.meta.f === fieldId && (q.meta.p ?? null) === pos) {
        await photoQueue.dequeue(q.id);
        _jbTsfForgetUpload(q.id);
      }
    }
  } catch (_) { /* a stale duplicate is survivable; failing the enqueue is not */ }

  try {
    await photoQueue.enqueue({
      scope,
      ticketId,
      blob: file,
      pretripId: null,
      thumbBlob: null,
      name: _jbTsfQueueName({ s: sectionId, f: fieldId, p: pos, fp, n: file.name || 'file' }),
      contentType: file.type || 'application/octet-stream',
    });
  } catch (err) {
    // MAX_QUEUE_SIZE is 20 and SHARED with DVIR photos, so "queue full" is a real
    // state with a real driver action. Say which it is.
    console.error('[JobBoard] section-file enqueue failed:', err?.message || err);
    try {
      window.logError?.(err, {
        module: 'jobboard', operation: 'TICKET_SECTION_FILE_ENQUEUE_FAILED',
        ticketId, sectionId, fieldId, reason: String(err?.message || err),
      });
    } catch (_) { /* breadcrumb is best-effort */ }
    const full = /queue full/i.test(String(err?.message || ''));
    window.showToast?.(full
      ? 'This phone’s offline queue is full — get signal and send your pretrip photos first, then attach this again.'
      : 'This phone would not hold the file offline — it is NOT attached. Try again when you have a bar.', 'error');
    return false;
  }

  const wrap = root && root.querySelector ? root.querySelector(`[data-cs-file-for="${elId}"]`) : null;
  if (wrap) wrap.setAttribute('data-cs-file-queued-name', file.name || 'File');
  _jbCsRefreshFileChips(root);
  return true;
}

// File a landed path onto the ticket's customSectionData.
//
// Never a wholesale replace of the driver's form: this reads the CURRENT server
// doc and writes back the same map with ONE field changed. The ticket is shared
// with the crew, so the read has to be the fresh one immediately before the write.
async function _jbTsfPlacePath(ticketId, meta, path) {
  const tenant = JobBoardState.tenantId;
  const cs = _jbCsDefs().find(s => s.id === meta.s);
  if (!cs) return { ok: false, terminal: true, reason: 'that section was removed from the ticket setup' };

  const snap = await getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId));
  if (!snap.exists()) return { ok: false, terminal: true, reason: 'the ticket is gone' };
  const t = snap.data() || {};

  // 🔴 The callable refuses a billed/locked ticket, so a queued file whose ticket
  // was open at upload time can still find it CLOSED by the time we file the
  // path — the office bills while the driver is out of signal. Without this the
  // updateDoc just bounces off ticketLockedForBilling and the driver is told "it
  // would not go through after several tries" instead of the truth (COD Code
  // Reviewer). Same field set the callable and firestore.rules use.
  // TASK-4641 — was an inline copy of this test; the photo drain had no copy at
  // all, which is the asymmetry that let a queued photo die on a console.warn.
  // Both arms now read the same predicate. Message unchanged.
  const _tsfLock = _jbTicketLockReason(t);
  if (_tsfLock) {
    return { ok: false, terminal: true, reason: _tsfLock === 'sent'
      ? 'this ticket was already sent before it could go up — send the file to dispatch directly'
      : 'the office billed this ticket before it could go up — send the file to dispatch directly' };
  }

  const csd = t.customSectionData || {};
  const next = { ...csd };

  if (!cs.repeatable) {
    // Unambiguous: one section, one field.
    const base = (csd[meta.s] && typeof csd[meta.s] === 'object' && !csd[meta.s]._repeatable) ? csd[meta.s] : {};
    next[meta.s] = { ...base, [meta.f]: path };
  } else {
    const arr = _jbCsdInstancesOf(csd[meta.s]).map(x => ({ ...x }));
    const otherIds = (cs.fields || []).map(f => f.id).filter(id => id !== meta.f);
    const empty = arr.map((inst, i) => i).filter(i => !String(arr[i]?.[meta.f] || '').trim());
    let target = -1;
    if (arr.length === 0) {
      // Nothing saved for this section yet — the driver picked a file on a row he
      // never got to save. Mint the row; his next save merges into it.
      arr.push({});
      target = 0;
    } else if (meta.fp) {
      const matches = empty.filter(i => _jbCsdInstEq(arr[i], meta.fp, otherIds));
      if (matches.length === 1) target = matches[0];
    }
    if (target < 0 && empty.length === 1) target = empty[0];
    if (target < 0 && meta.p !== null && meta.p !== undefined && empty.indexOf(meta.p) >= 0) target = meta.p;
    if (target < 0) {
      // 🔴 REFUSE TO GUESS. Two candidate rows and no way to tell them apart means
      // a coin flip between attaching a permit to the right load and attaching it
      // to the wrong one. A wrong permit on a billed line is worse than a message
      // asking the driver to attach it again.
      return { ok: false, terminal: true, reason: 'we could not tell which row on the ticket it belongs to' };
    }
    arr[target] = { ...arr[target], [meta.f]: path };
    next[meta.s] = { _repeatable: true, instances: arr };
  }

  await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
    // TASK-4699 — this lands a queued permit/section FILE onto a ticket row.
    // It can run long after the driver left the page (and after a send), which
    // is precisely the silent case the flag exists for. Only reached once a
    // target row is resolved; the refuse-to-guess branch above returns first.
    customSectionData: next, ticketEmailDirtyAt: Timestamp.now(), updatedAt: Timestamp.now(),
  });

  // The open-time baseline the merge-save reasons against is now stale for this
  // section. Advancing it narrows (it does not close) the window where the
  // driver's next Save re-adds the pre-drain copy of a repeatable row alongside
  // this one — the merge deliberately errs toward preserving rather than deleting.
  if (JobBoardState._btTicketId === ticketId && JobBoardState._csdSnapshot) {
    try { JobBoardState._csdSnapshot[meta.s] = JSON.parse(JSON.stringify(next[meta.s])); } catch (_) { /* baseline hint only */ }
  }
  return { ok: true };
}

// Give up on one row, VISIBLY. The blob is dropped (the queue is 20 rows and
// shared with DVIR — an undeliverable row must not squat in it forever), so the
// localStorage record and the red banner are the only thing standing between the
// driver and a silent loss.
async function _jbTsfGiveUp(item, meta, reason) {
  const name = (meta && meta.n) || 'the file';
  console.warn('[JobBoard] section-file queue giving up on', item.id, '—', reason);
  try {
    window.logError?.(new Error('TICKET_SECTION_FILE_QUEUE_ABANDONED'), {
      module: 'jobboard', operation: 'TICKET_SECTION_FILE_QUEUE_ABANDONED',
      ticketId: item.ticketId, entryId: item.id, retryCount: item.retryCount || 0,
      sectionId: meta?.s || null, fieldId: meta?.f || null, reason: String(reason || ''),
    });
  } catch (_) { /* breadcrumb is best-effort */ }
  _jbTsfRecordFailed(item.ticketId, { name, reason });
  try { await photoQueue.dequeue(item.id); } catch (_) { /* nothing else to do */ }
  _jbTsfForgetUpload(item.id);
}

// The SECOND drain arm. Same queue, same claim, same retry ceiling as the photo
// arm — the only difference is the terminal call, which is the callable rather
// than a client uploadBytes.
async function _jbDrainSectionFileQueue() {
  if (navigator.onLine === false) return;
  const scope = _jbPqScope();
  if (!scope) return;
  let pending = [];
  try { pending = await photoQueue.getPending(scope); } catch (err) {
    console.warn('[JobBoard] section-file queue read failed:', err?.message || err);
    return;
  }
  const mine = pending.filter(x => x && x.ticketId && x.blob && _jbTsfIsQueuedFile(x));
  if (!mine.length) return;

  let landed = 0;
  let changed = 0;
  for (const item of mine) {
    // 🔴 CLAIM FIRST — two tabs, or `online` firing twice, would otherwise both
    // push the same permit. The claim is an IndexedDB readwrite transaction and
    // expires, so a tab that dies mid-upload does not strand the row.
    if (!(await photoQueue.claim(item.id))) continue;
    const meta = _jbTsfParseQueued(item);
    if (!meta) {
      await _jbTsfGiveUp(item, null, 'we could not tell which field it belonged to'); changed++;
      continue;
    }
    if ((item.retryCount || 0) >= JB_TSF_MAX_ATTEMPTS) {
      await _jbTsfGiveUp(item, meta, 'it would not go through after several tries'); changed++;
      continue;
    }
    try {
      let path = _jbTsfRecallUpload(item.id);
      if (!path) {
        const base64 = await _jbTsfReadBase64(item.blob);
        const res = await httpsCallable(functions, 'uploadTicketSectionFile')({
          ticketId: item.ticketId,
          sectionId: meta.s,
          fieldId: meta.f,
          fileBase64: base64,
          fileName: meta.n || 'file',
          mimeType: item.contentType || 'application/octet-stream',
        });
        path = res?.data?.path;
        if (!path) throw new Error('Upload did not return a file location.');
        _jbTsfRememberUpload(item.id, path);
      }

      const placed = await _jbTsfPlacePath(item.ticketId, meta, path);
      if (!placed.ok) {
        if (placed.terminal) { await _jbTsfGiveUp(item, meta, placed.reason); changed++; continue; }
        throw new Error(placed.reason || 'could not file it on the ticket');
      }
      // 🔴 ONLY NOW does the chip go green. An earlier cut painted it before
      // _jbTsfPlacePath ran, so a placement the drain then REFUSED (ambiguous
      // repeatable row, ticket billed mid-flight) left the driver looking at
      // "attached" beside a red "did NOT make it" — and worse, the next Save
      // would have collected that hidden value and filed the path on whatever
      // row the form happened to hold, which is exactly the guess the refusal
      // exists to prevent (COD Code Reviewer).
      if (JobBoardState._btTicketId === item.ticketId) {
        const elId = _jbTsfElIdFor(meta);
        const live = elId ? _jbCsRoot()?.querySelector(`[id="${elId}"]`) : null;
        if (live) {
          live.value = path;
          const wrap = _jbCsRoot()?.querySelector(`[data-cs-file-for="${elId}"]`);
          wrap?.removeAttribute('data-cs-file-queued-name');
          _jbCsRefreshFileChips(_jbCsRoot());
        }
      }
      // Dequeue ONLY after the ticket write lands — dequeuing on upload alone
      // drops the file if the doc write then fails.
      await photoQueue.dequeue(item.id);
      _jbTsfForgetUpload(item.id);
      landed++; changed++;
    } catch (err) {
      if (_jbTsfIsTerminal(err)) { await _jbTsfGiveUp(item, meta, _jbTsfTerminalReason(err)); changed++; continue; }
      console.warn('[JobBoard] section-file drain failed for', item.id, err?.code || err?.message || err);
      try { await photoQueue.incrementRetry(item.id); } catch (_) { /* keep it queued */ }
    }
  }
  if (landed > 0) {
    window.showToast?.(`${landed} attachment${landed === 1 ? '' : 's'} sent — ${landed === 1 ? 'it is' : 'they are'} on the ticket now.`, 'success');
  }

  // 🔴 REPAINT. The drain runs off the `online` event, with nobody watching it —
  // so without this the screen keeps whatever it said before. Caught live on a
  // throwaway tenant 2026-08-26: a file the drain GAVE UP on wrote its record and
  // dropped the row, but the chip still read "waiting for signal" and the red
  // banner never appeared until the driver reopened the ticket. That is the
  // TASK-4594 defect wearing a new coat — the screen lying about what is saved.
  if (changed > 0 && JobBoardState._btTicketId) {
    const tid = JobBoardState._btTicketId;
    // Explicit re-mark FIRST: _jbShowUnsavedState only touches the chips when the
    // queue is non-empty, so draining the last row would leave a stale
    // "waiting for signal" marker with nothing behind it.
    try {
      const q = await _jbTsfQueuedFor(tid);
      if (q.ok) _jbTsfMarkQueuedChips(q.files);
    } catch (_) { /* chrome only */ }
    try { await _jbShowUnsavedState(tid); } catch (_) { /* banner is not worth breaking the drain */ }
  }
}

async function _jbCsPickFile(elId, sectionId, fieldId) {
  const root = _jbCsRoot();
  const input = root?.querySelector(`[id="${elId}"]`);
  if (!input) return;

  // A file has to hang off a REAL ticket — the callable refuses otherwise, and
  // it is right to. Mint one first if this build has not saved yet, exactly as
  // the line-item path does.
  let ticketId = JobBoardState._btTicketId;
  if (!ticketId) {
    ticketId = await jbEnsureTicketId({ surfaceToast: true });
    if (!ticketId) return; // ensure already told the driver what was missing
  }

  let picker = document.getElementById('jb-cs-file-input');
  if (!picker) {
    picker = document.createElement('input');
    picker.type = 'file';
    picker.id = 'jb-cs-file-input';
    // image/* opens the camera on a phone, which is what a driver wants.
    picker.accept = 'image/*,application/pdf';
    picker.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
    document.body.appendChild(picker);
  }
  picker.value = '';
  picker.onchange = async () => {
    const file = picker.files && picker.files[0];
    if (!file) return;
    // 🔴 REFUSE AT PICK TIME, both paths. The callable's ceiling is the onCall
    // base64 limit, so an oversized file can never land — queueing it would only
    // buy the driver twenty minutes of retries and then a loss notice for
    // something we knew was impossible the moment he tapped.
    if (file.size > JB_TSF_MAX_BYTES) {
      window.showToast?.('That file is too big (max 7MB) — nothing was attached. Take the photo again at a smaller size, or send a PDF.', 'error');
      return;
    }

    // TASK-4635 — no signal is the normal case out here. Don't spend the
    // driver's time on an upload that cannot land; stage it and say so.
    if (jbNetLooksOffline()) {
      const ok = await _jbTsfEnqueue(ticketId, file, sectionId, fieldId, elId);
      if (ok) {
        // Never the word "saved". It is on the phone, not on the ticket.
        window.showToast?.('No signal — the file is held on this phone and sends itself when you get a bar. It is not on the ticket yet.', 'warning', null, 9000);
        _jbShowUnsavedState(ticketId);
      }
      return;
    }

    const nameEl = document.querySelector(`[data-cs-file-for="${elId}"] .jb-cs-file-name`);
    if (nameEl) { nameEl.textContent = 'Uploading…'; nameEl.style.color = '#64748b'; }
    try {
      const base64 = await _jbTsfReadBase64(file);
      const fn = httpsCallable(functions, 'uploadTicketSectionFile');
      const res = await fn({
        ticketId,
        sectionId,
        fieldId,
        fileBase64: base64,
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
      });
      const path = res?.data?.path;
      if (!path) throw new Error('Upload did not return a file location.');
      // Re-find the input: an await just happened, and the modal may have been
      // rebuilt underneath us. Writing to a detached node would look like a
      // successful attach that vanishes on save.
      const live = _jbCsRoot()?.querySelector(`[id="${elId}"]`);
      if (!live) {
        window.showToast?.('File uploaded, but the form moved on — reopen the ticket to see it.', 'warning');
        return;
      }
      live.value = path;
      _jbCsRefreshFileChips(_jbCsRoot());
      window.showToast?.('Attached. It saves with the ticket.', 'success');
    } catch (err) {
      console.warn('[JobBoard] ticket-section file upload failed:', err?.code || err?.message);
      // TASK-4635 — the old behaviour ended HERE, with a toast and a lost file.
      // A terminal refusal (ticket billed, section deleted, file rejected) still
      // does, because queueing it would only postpone the same answer — but say
      // the callable's own words, not "no signal".
      if (_jbTsfIsTerminal(err)) {
        _jbCsRefreshFileChips(_jbCsRoot());
        window.showToast?.(`${_jbTsfTerminalReason(err)} Nothing was attached.`, 'error');
        return;
      }
      const ok = await _jbTsfEnqueue(ticketId, file, sectionId, fieldId, elId);
      _jbCsRefreshFileChips(_jbCsRoot());
      if (ok) {
        window.showToast?.('That upload did not go through — the file is held on this phone and sends itself when you have signal. It is not on the ticket yet.', 'warning', null, 9000);
        _jbShowUnsavedState(ticketId);
      }
      // _jbTsfEnqueue already toasted the real reason when it could not stage it.
    }
  };
  setTimeout(() => picker.click(), 50);
}

// Clearing only drops the POINTER on this ticket. The blob is deliberately left
// in Storage: a driver tapping the wrong × on a shared yard tablet must not be
// able to destroy a permit the office may already have billed against.
function _jbCsClearFile(elId) {
  const root = _jbCsRoot();
  const input = root?.querySelector(`[id="${elId}"]`);
  if (!input) return;
  input.value = '';
  _jbCsRefreshFileChips(root);
  window.showToast?.('Removed from this ticket. Save to keep the change.', 'info');
}

// ONE delegated listener for every file chip, installed once. Data attributes
// only — no value is ever interpolated into an onclick string, because a
// Storage path ends in a user-supplied filename and HTML-escaping is not
// JS-string-escaping (the defect COD caught twice on 2026-08-24).
let _jbCsFileDelegated = false;
function _jbCsInstallFileDelegate() {
  if (_jbCsFileDelegated) return;
  _jbCsFileDelegated = true;
  document.addEventListener('click', (ev) => {
    const t = ev.target;
    if (!t || !t.closest) return;
    const pick = t.closest('[data-cs-file-pick]');
    if (pick) {
      ev.preventDefault();
      _jbCsPickFile(
        pick.getAttribute('data-cs-file-pick'),
        pick.getAttribute('data-cs-file-section'),
        pick.getAttribute('data-cs-file-field')
      );
      return;
    }
    const clear = t.closest('[data-cs-file-clear]');
    if (clear) {
      ev.preventDefault();
      _jbCsClearFile(clear.getAttribute('data-cs-file-clear'));
    }
  }, true);
}

// Render one field group for a jobboard custom section. Repeatable sections use
// indexed ids (jb-custom-csId-IDX-fieldId); non-repeatable keep legacy flat ids.
function _jbCsFieldGroupHtml(cs, idx, repeatable) {
  const esc = _jbCsEsc;
  const idFor = (f) => repeatable ? `jb-custom-${cs.id}-${idx}-${f.id}` : `jb-custom-${cs.id}-${f.id}`;
  return cs.fields.map(f => {
    const fieldId = idFor(f);
    if (f.type === 'checkbox') {
      return `<label style="display:flex;align-items:center;gap:8px;flex:1;min-width:120px;cursor:pointer;padding:10px 0;">
        <input type="checkbox" id="${fieldId}"> <span style="font-size:0.85rem;font-weight:500;color:#0f172a;">${esc(f.label)}</span>
      </label>`;
    }
    if (f.type === 'waypoint') {
      return `<div style="flex:1;min-width:200px;">
        <label style="font-size:0.75rem;font-weight:500;color:#64748b;display:block;margin-bottom:4px;"><span class="material-icons" style="font-size:14px;vertical-align:middle;">place</span> ${esc(f.label)}</label>
        <input type="text" id="${fieldId}" value="" placeholder="Address, map link, or lat/long..." style="width:100%;padding:10px;font-size:0.95rem;font-weight:500;border:1px solid #e2e8f0;border-radius:10px;background:#f8fafc;color:#0f172a;" data-cs-waypoint="true">
      </div>`;
    }
    if (f.type === 'gps') {
      return `<div style="flex:1;min-width:200px;">
        <label style="font-size:0.75rem;font-weight:500;color:#64748b;display:block;margin-bottom:4px;"><span class="material-icons" style="font-size:14px;vertical-align:middle;">my_location</span> ${esc(f.label)}</label>
        <div style="display:flex;gap:6px;">
          <button type="button" style="padding:8px 14px;border:1px solid #e2e8f0;border-radius:10px;background:#f8fafc;font-size:0.85rem;font-weight:600;color:#0f172a;cursor:pointer;display:flex;align-items:center;gap:4px;white-space:nowrap;" onclick="window.JobBoardModule._csGrabGPS('${fieldId}')">
            <span class="material-icons" style="font-size:18px;color:#3B82F6;">my_location</span> Grab
          </button>
          <input type="text" id="${fieldId}" value="" placeholder="46.8772, -103.9849" style="flex:1;padding:10px;font-size:0.95rem;font-weight:600;border:1px solid #e2e8f0;border-radius:10px;background:#f8fafc;color:#0f172a;" readonly>
        </div>
      </div>`;
    }
    // TASK-4607 — file field. The VALUE is a Storage path in a hidden input, so
    // every existing mechanism (collector, restore, merge-save, offline stash)
    // keeps working untouched — it is just a string like any text field. Only
    // the chrome around it is new.
    if (f.type === 'file') {
      return `<div style="flex:1;min-width:200px;">
        <label style="font-size:0.75rem;font-weight:500;color:#64748b;display:block;margin-bottom:4px;"><span class="material-icons" style="font-size:14px;vertical-align:middle;">attach_file</span> ${esc(f.label)}</label>
        <input type="hidden" id="${fieldId}" value="">
        <div class="jb-cs-file" data-cs-file-for="${fieldId}" style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;">
          <button type="button" data-cs-file-pick="${fieldId}" data-cs-file-section="${esc(cs.id)}" data-cs-file-field="${esc(f.id)}" style="padding:8px 14px;border:1px solid #e2e8f0;border-radius:10px;background:#f8fafc;font-size:0.85rem;font-weight:600;color:#0f172a;cursor:pointer;display:flex;align-items:center;gap:4px;white-space:nowrap;">
            <span class="material-icons" style="font-size:18px;color:#3B82F6;">upload_file</span> Attach
          </button>
          <span class="jb-cs-file-name" style="font-size:0.8rem;color:#64748b;flex:1;min-width:80px;word-break:break-all;">Nothing attached</span>
        </div>
      </div>`;
    }
    return `<div style="flex:1;min-width:120px;">
      <label style="font-size:0.75rem;font-weight:500;color:#64748b;display:block;margin-bottom:4px;">${esc(f.label)}${f.lineItemBinding ? ' <span style="font-size:0.6rem;color:#3B82F6;">⟶ ' + esc(f.lineItemBinding.itemCode) + '</span>' : ''}</label>
      <input type="${f.type === 'number' ? 'number' : 'text'}" id="${fieldId}" ${f.type === 'number' ? 'min="0" value="0"' : 'value=""'} style="width:100%;padding:10px;font-size:1.1rem;font-weight:600;${f.type === 'number' ? 'text-align:center;' : ''}border:1px solid #e2e8f0;border-radius:10px;background:#f8fafc;color:#0f172a;">
    </div>`;
  }).join('');
}

function _jbCsInstanceHtml(cs, idx) {
  return `<div class="jb-cs-instance" data-cs-instance="${idx}" style="position:relative;border:1px solid #e2e8f0;border-radius:12px;padding:12px;margin-bottom:8px;">
    <button type="button" title="Remove this entry" onclick="window.JobBoardModule._csRemoveInstance('${cs.id}',${idx})" style="position:absolute;top:8px;right:8px;background:none;border:none;color:#94a3b8;cursor:pointer;line-height:1;padding:2px;">
      <span class="material-icons" style="font-size:20px;">close</span>
    </button>
    <div style="display:flex;gap:10px;flex-wrap:wrap;">${_jbCsFieldGroupHtml(cs, idx, true)}</div>
  </div>`;
}

function jbBuildCustomSectionsHtml() {
  const customSections = _jbCsDefs();
  if (customSections.length === 0) return '';
  // TASK-4607 — idempotent; the chips are rendered as HTML strings so there is
  // no per-node wiring step to hang a listener off.
  _jbCsInstallFileDelegate();
  let html = '';
  for (const cs of customSections) {
    const csKey = 'custom_' + cs.id;
    if (!jbCOFEnabled(csKey)) continue;
    let content;
    if (cs.repeatable) {
      content = `
        <div data-cs-repeatable="1" data-custom-section="${cs.id}">
          <div class="jb-cs-instances" data-cs-instances="${cs.id}">
            ${_jbCsInstanceHtml(cs, 0)}
          </div>
          <button type="button" data-cs-add="${cs.id}" onclick="window.JobBoardModule._csAddInstance('${cs.id}')" style="padding:8px 14px;font-size:0.85rem;font-weight:600;border:1px dashed #cbd5e1;border-radius:10px;background:transparent;color:#3b82f6;cursor:pointer;display:flex;align-items:center;gap:6px;">
            <span class="material-icons" style="font-size:18px;">add</span> Add another
          </button>
        </div>
      `;
    } else {
      content = `
        <div style="display:flex;gap:10px;flex-wrap:wrap;" data-custom-section="${cs.id}">
          ${_jbCsFieldGroupHtml(cs, 0, false)}
        </div>
      `;
    }
    html += jbCollapsible('jb-sec-custom-' + cs.id, _jbCsEsc(cs.icon || 'tune'), _jbCsEsc(cs.name), content, false);
  }
  return html;
}

let _jbCsAddBusy = false;
// bypassDebounce=true is used by restore (jbRestoreTicketData) to spawn N saved
// instances synchronously — the 250ms tap-debounce must NOT swallow those adds,
// or saved repeatable instances are lost on re-save (data loss).
function _jbCsAddInstance(csId, bypassDebounce) {
  if (!bypassDebounce) {
    if (_jbCsAddBusy) return;
    _jbCsAddBusy = true;
    setTimeout(() => { _jbCsAddBusy = false; }, 250);
  }
  const cs = _jbCsDefs().find(s => s.id === csId);
  if (!cs) return;
  const root = _jbCsRoot();
  const wrap = root.querySelector(`[data-cs-instances="${csId}"]`);
  if (!wrap) return;
  const existing = wrap.querySelectorAll('.jb-cs-instance').length;
  if (existing >= JB_CS_MAX_INSTANCES) {
    if (!bypassDebounce) window.showToast?.(`Up to ${JB_CS_MAX_INSTANCES} entries per section.`, 'error');
    return;
  }
  let maxIdx = -1;
  wrap.querySelectorAll('.jb-cs-instance').forEach(el => { maxIdx = Math.max(maxIdx, parseInt(el.dataset.csInstance, 10) || 0); });
  const idx = maxIdx + 1;
  wrap.insertAdjacentHTML('beforeend', _jbCsInstanceHtml(cs, idx));
  _csInitWaypoints();
  if (existing + 1 >= JB_CS_MAX_INSTANCES) {
    const addBtn = root.querySelector(`[data-cs-add="${csId}"]`);
    if (addBtn) addBtn.style.display = 'none';
  }
}

function _jbCsRemoveInstance(csId, idx) {
  const root = _jbCsRoot();
  const wrap = root.querySelector(`[data-cs-instances="${csId}"]`);
  if (!wrap) return;
  const instances = wrap.querySelectorAll('.jb-cs-instance');
  if (instances.length <= 1) {
    const only = wrap.querySelector('.jb-cs-instance');
    if (only) only.querySelectorAll('input').forEach(el => { if (el.type === 'checkbox') el.checked = false; else el.value = (el.type === 'number') ? '0' : ''; });
    return;
  }
  const target = wrap.querySelector(`.jb-cs-instance[data-cs-instance="${idx}"]`);
  if (target) target.remove();
  const addBtn = root.querySelector(`[data-cs-add="${csId}"]`);
  if (addBtn && wrap.querySelectorAll('.jb-cs-instance').length < JB_CS_MAX_INSTANCES) addBtn.style.display = '';
}

// Collect jobboard custom-section data into the persisted shape (repeatable + single).
// TASK-1204: all lookups scoped to the Build Ticket modal (_jbCsRoot) so a stale
// clock.js clock-out form elsewhere in the DOM can never shadow the live inputs.
function jbCollectCustomSectionData() {
  const customSections = _jbCsDefs();
  const customSectionData = {};
  const root = _jbCsRoot();
  const readInstance = (cs, idForField) => {
    const sectionData = {};
    let hasData = false;
    for (const f of cs.fields) {
      const el = root.querySelector(`[id="${idForField(f)}"]`);
      if (!el) continue;
      if (f.type === 'checkbox') { sectionData[f.id] = el.checked; if (el.checked) hasData = true; }
      else if (f.type === 'number') { sectionData[f.id] = parseFloat(el.value) || 0; if (sectionData[f.id] !== 0) hasData = true; }
      else { sectionData[f.id] = el.value?.trim() || ''; if (sectionData[f.id]) hasData = true; }
    }
    return { data: sectionData, hasData };
  };
  for (const cs of customSections) {
    if (!jbCOFEnabled('custom_' + cs.id)) continue;
    if (cs.repeatable) {
      const wrap = root.querySelector(`[data-cs-instances="${cs.id}"]`);
      if (!wrap) continue;
      const instances = [];
      wrap.querySelectorAll('.jb-cs-instance').forEach(el => {
        const idx = parseInt(el.dataset.csInstance, 10) || 0;
        const { data, hasData } = readInstance(cs, (f) => `jb-custom-${cs.id}-${idx}-${f.id}`);
        if (hasData) instances.push(data);
      });
      if (instances.length > 0) customSectionData[cs.id] = { _repeatable: true, instances };
    } else {
      const { data, hasData } = readInstance(cs, (f) => `jb-custom-${cs.id}-${f.id}`);
      if (hasData) customSectionData[cs.id] = data;
    }
  }
  return customSectionData;
}

// ============================================
// TASK-1204 — CUSTOM-SECTION RESTORE + MERGE-SAVE (never silently lose data)
// ============================================

// Normalize a persisted section value to a list of instance objects.
// Repeatable: { _repeatable:true, instances:[...] } -> instances.
// Legacy single-value object -> a one-element list.
function _jbCsdInstancesOf(val) {
  if (!val || typeof val !== 'object') return [];
  if (val._repeatable && Array.isArray(val.instances)) return val.instances;
  return [val];
}

// Instance equality over the section's CURRENT field ids only (fields removed
// from the section definition can't be displayed or re-collected, so they must
// not count toward identity). Loose string compare tolerates number/string drift.
function _jbCsdInstEq(a, b, fieldIds) {
  for (const fId of fieldIds) {
    const va = a?.[fId], vb = b?.[fId];
    if (va === vb) continue;
    if (String(va ?? '') === String(vb ?? '')) continue;
    return false;
  }
  return true;
}

// Fill one instance's inputs inside `root`. Returns true only if EVERY saved
// field that still exists in the section definition found its element — the
// signal the restore actually landed on-screen for this instance.
function _jbCsFillInstanceEls(root, cs, csId, idx, fields, repeatable) {
  const defIds = new Set((cs?.fields || []).map(f => f.id));
  let ok = true;
  for (const [fId, val] of Object.entries(fields || {})) {
    if (fId.startsWith('_')) continue;
    const elId = repeatable ? `jb-custom-${csId}-${idx}-${fId}` : `jb-custom-${csId}-${fId}`;
    const el = root.querySelector(`[id="${elId}"]`);
    if (!el) { if (defIds.has(fId)) ok = false; continue; }
    if (el.type === 'checkbox') el.checked = !!val;
    else el.value = val ?? '';
  }
  // TASK-4607 — a file field restores as a hidden input value, which changes
  // nothing visible on its own. Repaint the chips so a reopened ticket shows
  // the attachment instead of reading "Nothing attached" over a real file.
  try { _jbCsRefreshFileChips(root); } catch (_) { /* chrome only */ }
  return ok;
}

const JB_CS_RESTORE_RETRY_MS = 150;
const JB_CS_RESTORE_MAX_TRIES = 40; // ~6s hard timeout

// Restore saved customSectionData into the Build Ticket form. Replaces the old
// bare setTimeout(...,50) one-shot: retries until the modal exists (bounded),
// spawns missing repeatable instances idempotently, VERIFIES every instance
// actually landed in the DOM, and records a per-section report on
// JobBoardState._csdRestoreReport ({ expected, okPositions[] }). Any instance
// that could not be shown is breadcrumbed (debugLogs/errorLogs) — the merge-save
// below uses the report so a degraded restore can never be clobbered by Save.
function _jbRestoreCustomSections(ticketId, csd) {
  if (!csd || typeof csd !== 'object' || Object.keys(csd).length === 0) return;
  let tries = 0;
  const _finalize = (report, failReason) => {
    JobBoardState._csdRestoreReport = report;
    if (failReason) {
      const detail = Object.entries(report).map(([k, r]) => `${k}:${r.okPositions.length}/${r.expected}`).join(' ');
      console.warn('[JobBoard] CSD_RESTORE_DEGRADED —', failReason, detail);
      try {
        window.logError?.(new Error('CSD_RESTORE_DEGRADED'), {
          module: 'jobboard', operation: 'CSD_RESTORE_DEGRADED',
          ticketId, reason: failReason, detail, tries
        });
      } catch (_) { /* breadcrumb is best-effort */ }
    }
  };
  const attempt = () => {
    tries++;
    const modal = document.getElementById('clockout-modal');
    if (!modal) {
      // Modal not built yet — retry (guards any future await between restore
      // and modal append, and slow-device ordering changes).
      if (tries < JB_CS_RESTORE_MAX_TRIES) setTimeout(attempt, JB_CS_RESTORE_RETRY_MS);
      else _finalize({}, 'modal never appeared');
      return;
    }
    // Modal present: the section DOM is settled (built synchronously with the
    // modal), so run exactly ONE fill+verify pass — re-running fills later
    // would clobber anything the driver already started typing.
    const sections = _jbCsDefs();
    const report = {};
    let anyFailed = false;
    for (const [csId, raw] of Object.entries(csd)) {
      const cs = sections.find(s => s.id === csId);
      const saved = _jbCsdInstancesOf(raw);
      const rep = { expected: saved.length, okPositions: [] };
      report[csId] = rep;
      if (!cs) { anyFailed = true; continue; } // section deleted from settings — data preserved by merge-save
      if (cs.repeatable) {
        const wrap = modal.querySelector(`[data-cs-instances="${csId}"]`);
        if (!wrap) { anyFailed = true; continue; } // COF-disabled / not rendered in this modal
        // Spawn the missing instance nodes (instance 0 renders with the modal).
        // bypassDebounce=true — the 250ms tap-debounce would otherwise swallow
        // every add after the first, dropping saved instances on the next save.
        let have = wrap.querySelectorAll('.jb-cs-instance').length;
        while (have < saved.length && have < JB_CS_MAX_INSTANCES) { _jbCsAddInstance(csId, true); have++; }
        const idxList = Array.from(wrap.querySelectorAll('.jb-cs-instance')).map(el => parseInt(el.dataset.csInstance, 10) || 0);
        saved.forEach((inst, i) => {
          const idx = idxList[i];
          if (idx === undefined) { anyFailed = true; return; }
          if (_jbCsFillInstanceEls(modal, cs, csId, idx, inst, true)) rep.okPositions.push(i);
          else anyFailed = true;
        });
      } else {
        if (_jbCsFillInstanceEls(modal, cs, csId, 0, saved[0], false)) rep.okPositions.push(0);
        else anyFailed = true;
      }
    }
    _finalize(report, anyFailed ? 'section/instance elements missing' : null);
  };
  attempt();
}

// Merge-save customSectionData (TASK-1204). Wholesale document replace is the
// clobber amplifier: after ANY restore the form didn't fully show (biller
// locked the ticket mid-shift, getDoc error, settings landed late, section
// COF-disabled) — or while a crew-mate edits the same shared ticket — Save
// used to permanently wipe every instance not sitting in THIS form.
//
// Rule per repeatable section: final = preservedServerInstances + collected.
// A server instance is DROPPED only when the driver provably saw it (it deep-
// equals a snapshot instance whose restore verified OK) and chose to remove or
// edit it — so deliberate deletions still work — or when it's already present
// in the collected form data. Everything the form never displayed is preserved.
// Preservation events are breadcrumbed so degraded surfaces stay visible.
// ===========================================================================
// TASK-4594 — the local stash that stops a stale-baseline abandon from LOSING
// the driver's typing.
// ===========================================================================
// Steve entered three Line Information rows on ticket #100558; ONE reached
// Firestore, with its GPS and Notes empty. And it was not new: across 46 live
// rows, 9 (20%) have a blank GPS, and on ticket #100240 (2026-07-13) a driver
// typed into the Notes field, verbatim, "Grabbed coordinates but did not save."
// A driver documented this bug inside the product six weeks before anyone
// noticed.
//
// The abandon itself is CORRECT and stays: customSectionData is written WHOLE
// onto a ticket shared with the crew, so writing against a stale baseline can
// delete a crew-mate's row. What was wrong is that the abandoned data existed
// nowhere but the DOM — one tab close and it was gone — and nothing was logged,
// so it could not even be counted.
//
// localStorage, not the Firestore offline queue: TASK-1652 is open precisely
// because that queue is MEMORY-ONLY here and does not survive a tab close.
// Every access is try/caught — private mode, quota and locked-down browsers all
// throw, and a driver must never lose a save because a stash failed.
function _jbCsdStashKey(ticketId) {
  return `dd_csd_unsaved_${JobBoardState.tenantId || 'nt'}_${ticketId}`;
}
function _jbStashCsd(ticketId, collected) {
  try {
    if (!ticketId || !collected || !Object.keys(collected).length) return false;
    localStorage.setItem(_jbCsdStashKey(ticketId), JSON.stringify({
      savedAt: new Date().toISOString(), data: collected,
      // TASK-4666 (COD Code Reviewer) — bind the stash to the person who typed
      // it. On a SHARED phone the next morning's driver session must not
      // auto-send someone else's rows under their own identity; the recovery
      // pass only auto-sends on a personId match.
      personId: JobBoardState._btEntry?.personId || window.AppState?.driverPersonId || null,
    }));
    return true;
  } catch (_) { return false; }
}
// 14 days. Long enough that a driver off-grid for a week still gets his rows
// back; short enough that a stash on a since-invoiced ticket stops nagging
// forever (COD). Expiry DELETES rather than hides — a banner nobody can clear is
// how a warning becomes wallpaper.
const JB_CSD_STASH_TTL_MS = 14 * 24 * 60 * 60 * 1000;
function _jbReadCsdStash(ticketId) {
  try {
    const raw = localStorage.getItem(_jbCsdStashKey(ticketId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.data) return null;
    const age = Date.now() - new Date(parsed.savedAt).getTime();
    if (!isFinite(age) || age > JB_CSD_STASH_TTL_MS) { _jbClearCsdStash(ticketId); return null; }
    return parsed;
  } catch (_) { return null; }
}
function _jbClearCsdStash(ticketId) {
  try { localStorage.removeItem(_jbCsdStashKey(ticketId)); } catch (_) { /* nothing to do */ }
}

// TASK-4594 — the reopen signal. Two states only, because a driver on a jobsite
// reads a colour, not a legend:
//   • on the server  → one quiet line, deliberately low-contrast. It must be
//     boring, or by day 60 nobody sees it and the amber below stops registering.
//   • NOT on the server → loud amber, and it says what to DO, not what happened.
// COD's call, and I agree with the reasoning: restore LOUDLY, never silently.
// A silent restore would have let Steve believe all three rows were fine.
// TASK-4608 — ONE unsaved-work banner.
//
// There used to be two: unsent custom-section ROWS and queued PHOTOS each
// rendered their own amber box with its own retry button, both `prepend`ed to
// the same host. A driver with both pending saw two near-identical warnings and
// had to press two different buttons to clear them — and since neither knew
// about the other, "Try now" on one left the other still complaining, which
// reads as "the retry didn't work."
//
// Drivers don't distinguish rows from photos; they know "my stuff didn't send."
// So: one banner, one button, one story. Partial success falls out for free —
// after a retry we re-render and the banner names only what is STILL pending.
//
// serverCsd is cached per ticket because the photo-side entry point doesn't
// carry it; without the cache, a photo re-render would drop the "saved on the
// server" confirmation that the row-side call had just established.
function _jbCacheServerCsd(ticketId, serverCsd) {
  if (serverCsd === undefined) return;
  JobBoardState._btServerCsdFor = ticketId;
  JobBoardState._btServerCsd = serverCsd;
}

function _jbCountCsd(csd) {
  return Object.values(csd || {})
    .reduce((n, s) => n + ((s && s.instances) ? s.instances.length : (s ? 1 : 0)), 0);
}

async function _jbShowUnsavedState(ticketId, serverCsd, _tries) {
  // Stale-modal guard — the retry can outlive a modal close, and telling a
  // driver the WRONG ticket is unsaved is worse than saying nothing.
  if (JobBoardState._btTicketId && JobBoardState._btTicketId !== ticketId) return;
  _jbCacheServerCsd(ticketId, serverCsd);
  const host = document.getElementById('jb-bt-sections');
  if (!host) {
    // The modal is appended AFTER ticket data is restored, so on the first call
    // the host does not exist yet and a bare `return` meant this banner silently
    // never rendered — the whole feature, dead on arrival (COD Code Reviewer).
    const n = (_tries || 0) + 1;
    if (n > 40) return; // ~4s — the modal is not coming
    setTimeout(() => _jbShowUnsavedState(ticketId, serverCsd, n), 100);
    return;
  }

  const effectiveCsd = (JobBoardState._btServerCsdFor === ticketId)
    ? JobBoardState._btServerCsd : (serverCsd || null);
  const stash = _jbReadCsdStash(ticketId);
  const serverCount = _jbCountCsd(effectiveCsd);
  const stashCount = stash ? _jbCountCsd(stash.data) : 0;

  // null = the queue could not be read. NOT the same as 0, and the difference
  // decides whether we are allowed to tell the driver his work is safe.
  let photoCount = null;
  try {
    photoCount = await photoQueue.pendingTicketCount(_jbPqScope(), ticketId);
  } catch (err) {
    // A broken queue read must not take the ROW half of the banner down with
    // it — that half is the one carrying the "not saved until you tap" warning.
    console.warn('[JobBoard] pending photo count failed:', err?.message);
    photoCount = null;
  }
  const photoUnknown = photoCount === null;

  // TASK-4635 — the ticket-photo queue now also carries SECTION FILES, so the
  // index count above is a total, not a photo count. Only split it when there is
  // actually something queued: the split costs a getPending (blob handles), and
  // the whole point of the index count on this driver-facing open path is that
  // the common case — nothing waiting — costs nothing.
  let fileCount = 0;
  let filesUnknown = false;
  if (!photoUnknown && photoCount > 0) {
    const q = await _jbTsfQueuedFor(ticketId);
    if (q.ok) { fileCount = q.files.length; _jbTsfMarkQueuedChips(q.files); }
    else filesUnknown = true;   // fall back to calling everything a photo
  }
  const photoPending = photoUnknown ? 0 : Math.max(0, photoCount - (filesUnknown ? 0 : fileCount));

  // Files the queue GAVE UP on. Read before the early return below — a driver
  // whose permit was dropped has to be told even when everything else is clean.
  const failed = _jbTsfReadFailed(ticketId);

  // Re-check: an await happened above, so the driver may have moved on.
  if (JobBoardState._btTicketId && JobBoardState._btTicketId !== ticketId) return;

  // Clear the merged banner AND both legacy ids, so a stale box from an older
  // cached bundle can never sit alongside the new one.
  document.getElementById('jb-unsaved-banner')?.remove();
  document.getElementById('jb-csd-sync-state')?.remove();
  document.getElementById('jb-photoq-banner')?.remove();
  document.getElementById('jb-tsf-failed-banner')?.remove();

  // 🔴 The one RED box on this screen, and the only one that means "gone".
  // Everything else here is amber ("it is on your phone, it will go"); this says
  // the file is not coming back and names it, because the alternative is an
  // office that thinks it has a permit it never got.
  let fx = null;
  if (failed.length) {
    fx = document.createElement('div');
    fx.id = 'jb-tsf-failed-banner';
    fx.className = 'jb-csd-unsynced';
    fx.style.cssText = 'background:#fef2f2;border-color:#fca5a5;border-left-color:#b91c1c;';
    const rows = failed.map(r => `<li>${escapeHtml(r.name)} — ${escapeHtml(r.reason)}</li>`).join('');
    // TASK-4641 — "Attach it again" is advice that cannot work when the reason
    // is that the office CLOSED the ticket: the next attempt bounces off the
    // same rule. When every row is one of those, drop the retry half.
    const _allTerminal = failed.every(r => /closed this ticket|billed this ticket|ticket is gone/i.test(String(r.reason || '')));
    fx.innerHTML = `
      <div class="jb-csd-unsynced-title" style="color:#7f1d1d;">
        <span class="material-icons" aria-hidden="true">report</span>
        ${failed.length} attachment${failed.length === 1 ? '' : 's'} did NOT make it
      </div>
      <div class="jb-csd-unsynced-body" style="color:#7f1d1d;">
        ${_allTerminal
          ? `Send ${failed.length === 1 ? 'it' : 'them'} to dispatch directly — attaching ${failed.length === 1 ? 'it' : 'them'} here again will not work.`
          : `Attach ${failed.length === 1 ? 'it' : 'them'} again, or send ${failed.length === 1 ? 'it' : 'them'} to dispatch directly.`}
        <ul style="margin:6px 0 0;padding-left:18px;">${rows}</ul>
      </div>
      <button type="button" class="jb-csd-retry" style="background:linear-gradient(135deg,#b91c1c,#7f1d1d);" onclick="window.JobBoardModule._jbTsfDismissFailed('${(window.escAttr || escapeHtml)(ticketId)}');return false;">
        <span class="material-icons" aria-hidden="true">close</span> Got it — hide this
      </button>`;
  }
  // Prepended LAST everywhere so the red box sits above the amber one — "this is
  // gone" outranks "this is still going".
  const _paintFailed = () => { if (fx) host.prepend(fx); };

  if (!serverCount && !stashCount && !photoPending && !fileCount && !photoUnknown && !filesUnknown) { _paintFailed(); return; } // nothing entered — say nothing

  const el = document.createElement('div');
  el.id = 'jb-unsaved-banner';

  if (stashCount || photoPending || fileCount) {
    const bits = [];
    if (stashCount) bits.push(`${stashCount} ${stashCount === 1 ? 'entry' : 'entries'}`);
    if (photoPending) bits.push(`${photoPending} photo${photoPending === 1 ? '' : 's'}`);
    // TASK-4635 — named separately from photos. A driver who queued a permit is
    // owed "your file is still on this phone", not "your photos".
    if (fileCount) bits.push(`${fileCount} file${fileCount === 1 ? '' : 's'}`);
    const what = bits.length > 1 ? `${bits.slice(0, -1).join(', ')} and ${bits[bits.length - 1]}` : bits[0];
    // `escapeHtml` on the timestamp: it comes back out of localStorage, which is
    // writable by anything running on this origin.
    const when = stash ? (() => {
      try { return new Date(stash.savedAt).toLocaleString(); } catch (_) { return 'earlier'; }
    })() : '';
    // The rows are the dangerous half — they are NOT saved until the retry
    // succeeds. Photos re-send themselves. Say which is which, because "we'll
    // handle it" over unsaved typing is how work gets lost.
    const body = stashCount
      ? `Typed ${escapeHtml(when)} and kept on this phone only. Get a bar of signal, then tap below — ${(photoPending || fileCount) ? 'the entries are' : 'it is'} not saved until you do.`
      : 'Kept on this phone, not on the ticket yet. They send themselves when you get a bar — you do not need to re-take or re-attach them.';
    el.className = 'jb-csd-unsynced';
    el.innerHTML = `
      <div class="jb-csd-unsynced-title">
        <span class="material-icons" aria-hidden="true">cloud_off</span>
        ${what} still ${stashCount ? 'unsent' : 'waiting to upload'}
      </div>
      <div class="jb-csd-unsynced-body">${body}</div>
      <button type="button" class="jb-csd-retry" onclick="window.JobBoardModule._jbRetryUnsaved('${(window.escAttr || escapeHtml)(ticketId)}');return false;">
        <span class="material-icons" aria-hidden="true">cloud_upload</span> Send ${stashCount && (photoPending || fileCount) ? 'everything' : 'now'}
      </button>`;
  } else if (photoUnknown) {
    // 🔴 We could not read the photo queue, so we do NOT get to say
    // "saved". Claiming safety on the strength of a read that failed is the
    // whole defect this branch exists to prevent: the rows genuinely are on the
    // server, but photos may still be sitting on this phone and we cannot see.
    // State exactly that, and leave the retry available so a drain can be forced.
    el.className = 'jb-csd-unsynced';
    el.innerHTML = `
      <div class="jb-csd-unsynced-title">
        <span class="material-icons" aria-hidden="true">help_outline</span>
        ${serverCount} ${serverCount === 1 ? 'entry is' : 'entries are'} saved — photos and files could not be checked
      </div>
      <div class="jb-csd-unsynced-body">This phone would not let us look at the offline queue, so we cannot promise your pictures or attachments went up. Tap below to push anything still waiting.</div>
      <button type="button" class="jb-csd-retry" onclick="window.JobBoardModule._jbRetryUnsaved('${(window.escAttr || escapeHtml)(ticketId)}');return false;">
        <span class="material-icons" aria-hidden="true">cloud_upload</span> Send now
      </button>`;
  } else {
    el.className = 'jb-csd-synced';
    el.innerHTML = `<span class="material-icons" aria-hidden="true">cloud_done</span> ${serverCount} ${serverCount === 1 ? 'entry is' : 'entries are'} saved on the server`;
  }
  host.prepend(el);
  _paintFailed();
}

// One retry for both halves. Photos first (cheap, self-healing), then the rows,
// which go through the SAME baseline-read guard as any other save. Then repaint
// so a partial success shows exactly what is left rather than all-or-nothing.
async function _jbRetryUnsaved(ticketId) {
  const tid = ticketId || JobBoardState._btTicketId;
  try { await _jbDrainPhotoQueue(); } catch (err) { console.warn('[JobBoard] photo drain failed:', err?.message); }
  // TASK-4635 — the second arm. Before the rows, so a landed path is already in
  // the hidden input when the stash retry re-collects and merge-saves the form.
  try { await _jbDrainSectionFileQueue(); } catch (err) { console.warn('[JobBoard] section-file drain failed:', err?.message); }
  try { await _jbRetryCsdStash(tid); } catch (err) { console.warn('[JobBoard] csd stash retry failed:', err?.message); }
  await _jbShowUnsavedState(tid);
}

// Legacy entry points — both now paint the single merged banner. Kept so every
// existing call site keeps working instead of relying on me having found them all.
function _jbShowCsdSyncState(ticketId, serverCsd) {
  return _jbShowUnsavedState(ticketId, serverCsd);
}


// Re-run the normal merge-save with the stashed rows put back into the form, so
// the retry goes through the SAME baseline-read guard as any other save. It must
// never bypass that — the merge rule protects a crew-mate's rows and a retry is
// not special.
async function _jbRetryCsdStash(ticketId) {
  const stash = _jbReadCsdStash(ticketId);
  if (!stash) { window.showToast?.('Nothing left to send — it is already saved.', 'info'); return; }
  try {
    _jbRestoreCustomSections(ticketId, stash.data);
    await _jbMergeSaveCustomSectionData(ticketId);
  } catch (err) {
    console.warn('[JobBoard] CSD retry failed:', err?.code || err?.message);
  }
  // Re-read rather than trusting the attempt: the save may have abandoned again.
  const stillStashed = _jbReadCsdStash(ticketId);
  if (stillStashed) {
    window.showToast?.('Still no connection — your entries are safe on this phone. Try again when you have a bar.', 'warning');
  } else {
    window.showToast?.('Sent — your entries are on the server now.', 'success');
    const snap = await getDoc(doc(db, 'tenants', JobBoardState.tenantId, 'tickets', ticketId)).catch(() => null);
    _jbShowCsdSyncState(ticketId, snap?.exists() ? snap.data().customSectionData : null);
  }
}

// ===========================================================================
// TASK-4666 — surface the TASK-4594 stash OUTSIDE the Build Ticket sheet.
// ===========================================================================
// The stash+banner above only ever rendered when the SAME ticket's sheet was
// reopened. Rows abandoned during a CLOCK-OUT never got that chance — after
// clocking out nobody reopens the old ticket, so the rows sat on the phone
// until the 14-day TTL deleted them (#100406, #100517, #100558, #100567:
// multi-line pothole records dying as one/zero rows). Board entry is the one
// surface every driver comes back to, so the recovery pass runs there.
//
// The in-sheet merge-save cannot be reused here: jbCollectCustomSectionData
// walks the modal DOM and the preservation rule needs the restore report —
// neither exists outside the sheet. This pass is instead STRICTLY ADDITIVE:
//   • repeatable sections — append only stashed instances that deep-equal no
//     server instance (_jbCsdInstEq, same identity the merge-save uses);
//   • single-value sections — fill only fields EMPTY on the server. Days may
//     have passed; a crew-mate's newer value always outranks the stash.
// Additive means idempotent: a second run finds every stashed instance already
// on the server, adds nothing, and clears the stash. It also means this path
// can never delete or overwrite anything — the risk that made the original
// abandon correct does not exist here.
//
// TASK-428 rule: every refusal lands visibly. Offline → amber banner with a
// Send button. Ticket billed/archived/locked/gone (the write would be DENIED by
// the firestore.rules six-field lock, same predicate as _jbTicketLockReason) →
// red banner that LISTS the typed rows so the driver can read them to dispatch,
// with an explicit dismiss. Never a modal, never blocks clock-in.

function _jbCsdStashPrefix() {
  return `dd_csd_unsaved_${JobBoardState.tenantId || 'nt'}_`;
}

// Every unexpired stash on this device for this tenant. _jbReadCsdStash owns
// the TTL — an expired entry is deleted there, not returned here.
function _jbScanCsdStashes() {
  const found = [];
  try {
    const prefix = _jbCsdStashPrefix();
    const ids = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) ids.push(k.slice(prefix.length));
    }
    for (const ticketId of ids) {
      if (!ticketId) continue;
      const stash = _jbReadCsdStash(ticketId);
      if (stash) found.push({ ticketId, stash });
    }
  } catch (_) { /* locked-down browser — nothing to recover */ }
  return found;
}

// ---------------------------------------------------------------------------
// TASK-4666 — durable CSD_SAVE_ABANDONED breadcrumbs.
// The abandon branch used to call window.logError directly — but logError's
// two Firestore addDocs sit in the MEMORY-ONLY offline queue under exactly the
// weak signal that caused the abandon, and died with the tab. Result: ZERO
// abandon rows in errorLogs while rows were provably dying in the field. The
// breadcrumb now goes through a localStorage outbox: delivered immediately
// when the network allows, re-flushed on the next job-board entry when not.
// ---------------------------------------------------------------------------
function _jbAbandonBcKey() {
  return `dd_csd_abandon_bc_${JobBoardState.tenantId || 'nt'}`;
}

function _jbQueueAbandonBreadcrumb(ctx) {
  try {
    const key = _jbAbandonBcKey();
    let arr;
    try { arr = JSON.parse(localStorage.getItem(key)) || []; } catch (_) { arr = []; }
    if (!Array.isArray(arr)) arr = [];
    arr.push({
      ...ctx,
      bcId: `bc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
      at: new Date().toISOString(),
    });
    while (arr.length > 25) arr.shift(); // bounded — a stuck outbox must not grow forever
    localStorage.setItem(key, JSON.stringify(arr));
  } catch (_) { /* breadcrumb is best-effort — storage may be locked down */ }
  // Immediate attempt: on flaky-but-alive signal it lands now; on none, the
  // row waits for the next board entry. Fire-and-forget.
  try { _jbFlushAbandonBreadcrumbs(); } catch (_) { /* nothing to do */ }
}

async function _jbFlushAbandonBreadcrumbs() {
  const key = _jbAbandonBcKey();
  let arr;
  try { arr = JSON.parse(localStorage.getItem(key)) || []; } catch (_) { return; }
  if (!Array.isArray(arr) || arr.length === 0) return;
  if (JobBoardState._csdBcFlushing) return; // one flush at a time
  JobBoardState._csdBcFlushing = true;
  const deliveredIds = new Set();
  try {
    // logError resolves only after both addDocs are ACKED (it swallows real
    // failures internally and resolves too — a rules denial is terminal and
    // must not retry forever). A row that outlives the bound stays queued;
    // bcId makes any rare timeout-then-landed duplicate identifiable.
    await Promise.all(arr.map(row => {
      const { bcId, at, ...ctx } = row || {};
      // TASK-4634 reuses this outbox for other job-board failure breadcrumbs
      // (e.g. INVENTORY_SUBMIT_FAILED): a queued row may carry its own
      // ctx.operation, which wins; rows without one keep the original
      // CSD_SAVE_ABANDONED identity, so existing callers are unchanged.
      const _op = ctx.operation || 'CSD_SAVE_ABANDONED';
      return Promise.race([
        Promise.resolve(window.logError?.(new Error(_op), {
          ...ctx,
          module: ctx.module || 'jobboard', operation: _op,
          breadcrumbId: bcId || null, abandonedAt: at || null, deferredDelivery: true,
        })).then(() => { deliveredIds.add(bcId); }),
        new Promise(res => setTimeout(res, 6000)),
      ]).catch(() => { deliveredIds.add(bcId); });
    }));
  } finally {
    // Re-read before writing back — a new abandon may have queued mid-flush,
    // and rewriting from the stale copy would silently drop it.
    try {
      let cur;
      try { cur = JSON.parse(localStorage.getItem(key)) || []; } catch (_) { cur = []; }
      const remaining = (Array.isArray(cur) ? cur : []).filter(r => !deliveredIds.has(r?.bcId));
      if (remaining.length) localStorage.setItem(key, JSON.stringify(remaining));
      else localStorage.removeItem(key);
    } catch (_) { /* best-effort */ }
    JobBoardState._csdBcFlushing = false;
  }
}

// Cross-tab guard: two tabs both landing on the board must not double-append
// the same stashed instances inside each other's read-write window. 60s is
// far past the bounded read + ack; a crashed holder simply expires.
function _jbStashRetryLock(ticketId) {
  try {
    const k = `dd_csd_retry_lock_${JobBoardState.tenantId || 'nt'}_${ticketId}`;
    const cur = parseInt(localStorage.getItem(k) || '0', 10);
    if (cur && (Date.now() - cur) < 60000) return false;
    localStorage.setItem(k, String(Date.now()));
    return true;
  } catch (_) { return true; } // no storage = no cross-tab either
}
function _jbStashRetryUnlock(ticketId) {
  try { localStorage.removeItem(`dd_csd_retry_lock_${JobBoardState.tenantId || 'nt'}_${ticketId}`); } catch (_) { /* nothing */ }
}

// The DOM-independent additive merge. Returns:
//   { ok:true,  added:N }                    — landed (N=0: already all there)
//   { ok:false, reason:'offline' }           — no fresh baseline / no ack; stash kept
//   { ok:false, reason:'locked', lockReason, ticket } — rules would deny; stash kept
//   { ok:false, reason:'gone' }              — ticket deleted; stash kept
//   { ok:false, reason:'denied' }            — write refused server-side; stash kept
async function _jbMergeSaveStashedCsd(ticketId, stashData) {
  const tenant = JobBoardState.tenantId;
  const sections = _jbCsDefs();

  // Fresh bounded read — the SAME no-baseline-no-write guard as the in-sheet
  // merge-save. A rejected read is as blind as a timed-out one.
  let snap;
  try {
    snap = await jbBoundedRead(getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId)), 'ticket:csdStashRecovery');
  } catch (_e) {
    console.warn('[JobBoard] stash-recovery baseline read failed:', _e?.code || _e?.message);
    return { ok: false, reason: 'offline' };
  }
  if (!snap) return { ok: false, reason: 'offline' };
  if (!snap.exists()) return { ok: false, reason: 'gone' };
  const t = snap.data();
  const lockReason = _jbTicketLockReason(t);
  if (lockReason) return { ok: false, reason: 'locked', lockReason, ticket: t };

  const server = t.customSectionData || {};
  const final = { ...server };
  let added = 0;
  let changed = false;
  for (const [csId, stashVal] of Object.entries(stashData || {})) {
    if (!stashVal || typeof stashVal !== 'object') continue;
    const cs = sections.find(s => s.id === csId);
    // Identity over the section's current field ids, exactly like the in-sheet
    // merge. When the section def is gone (deleted from settings since the
    // stash), fall back to the keys on the stashed instance — stricter equality
    // beats treating everything as equal.
    const defIds = (cs?.fields || []).map(f => f.id);
    const srvArr = _jbCsdInstancesOf(server[csId]);
    const stArr = _jbCsdInstancesOf(stashVal);
    if (stashVal._repeatable || cs?.repeatable) {
      const toAdd = stArr.filter(sti => {
        const ids = defIds.length ? defIds : Object.keys(sti || {}).filter(k => !k.startsWith('_'));
        return ids.length > 0 && !srvArr.some(sv => _jbCsdInstEq(sv, sti, ids));
      });
      if (!toAdd.length) continue;
      final[csId] = { _repeatable: true, instances: [...srvArr, ...toAdd] };
      added += toAdd.length;
      changed = true;
    } else {
      const sti = stArr[0];
      if (!sti) continue;
      // Shape mismatch — the server holds a REPEATABLE value for a section the
      // stash (and current def) treat as single. Writing a single object here
      // would REPLACE the server's instances: exactly the deletion this pass
      // promises never to do. Leave the server value verbatim.
      if (server[csId] && typeof server[csId] === 'object' && server[csId]._repeatable) continue;
      const cur = (server[csId] && typeof server[csId] === 'object')
        ? { ...server[csId] } : {};
      let filled = false;
      for (const [fId, v] of Object.entries(sti)) {
        if (fId.startsWith('_')) continue;
        const curV = cur[fId];
        const curEmpty = curV === undefined || curV === null || curV === '' || curV === 0 || curV === false;
        const vNonEmpty = typeof v === 'boolean' ? v === true
          : typeof v === 'number' ? v !== 0
          : !!(v && String(v).trim());
        if (curEmpty && vNonEmpty) { cur[fId] = v; filled = true; }
      }
      if (!filled) continue;
      final[csId] = cur;
      added += 1;
      changed = true;
    }
  }

  if (!changed) {
    // Everything stashed is already on the server — the SUCCESS case for a
    // write that queued-then-landed after the last visit. Clear and go quiet.
    _jbClearCsdStash(ticketId);
    return { ok: true, added: 0 };
  }

  // Own bounded ack, NOT jbAckOrQueue: that helper short-circuits on the
  // sticky _btWriteUnacked flag from an earlier clock-out in this same page
  // session, and this pass must not inherit (or set) modal-scoped state.
  const wp = updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
    // TASK-4699 — a stashed-offline section save landing later. Guarded by the
    // `!changed` early-return above, so a stash already on the server (the
    // success case) returns before this and stamps nothing.
    customSectionData: final, ticketEmailDirtyAt: Timestamp.now(), updatedAt: Timestamp.now(),
  });
  const acked = await new Promise((resolve) => {
    const timer = setTimeout(() => resolve('timeout'), JB_WRITE_ACK_MS);
    wp.then(
      () => { clearTimeout(timer); resolve('acked'); },
      (e) => { clearTimeout(timer); resolve(e?.code === 'permission-denied' ? 'denied' : 'failed'); }
    );
  });
  if (acked !== 'acked') {
    // Timed out: the SDK keeps it queued; if it lands later the next pass
    // finds nothing to add and clears the stash — the same conservative
    // direction as the in-sheet save. Swallow the deferred rejection so a
    // late failure never surfaces as an unhandled rejection.
    wp.catch(() => { /* already reported via the banner */ });
    if (acked === 'denied') return { ok: false, reason: 'denied' };
    return { ok: false, reason: 'offline' };
  }

  // 🔴 Confirmed ack — the one condition that may drop the durable copy.
  _jbClearCsdStash(ticketId);
  try {
    window.logError?.(new Error('CSD_STASH_RECOVERED'), {
      module: 'jobboard', operation: 'CSD_STASH_RECOVERED', ticketId, added,
      via: 'jobboard-entry',
    });
  } catch (_) { /* breadcrumb is best-effort */ }
  return { ok: true, added };
}

// Readable listing of what a stash holds — for the terminal banner, where the
// only remaining path is the driver reading the rows to dispatch. Everything
// is escaped: the values come back out of localStorage.
function _jbStashSummaryHtml(stashData) {
  const sections = _jbCsDefs();
  const lines = [];
  for (const [csId, val] of Object.entries(stashData || {})) {
    const cs = sections.find(s => s.id === csId);
    for (const inst of _jbCsdInstancesOf(val)) {
      const bits = [];
      for (const [fId, v] of Object.entries(inst || {})) {
        if (fId.startsWith('_')) continue;
        if (v === '' || v === null || v === undefined || v === false || v === 0) continue;
        const f = cs?.fields?.find(x => x.id === fId);
        bits.push(`${escapeHtml(f?.label || 'Field')}: ${escapeHtml(String(v))}`);
      }
      if (bits.length) lines.push(`<li>${escapeHtml(cs?.name || 'Entry')} — ${bits.join(' · ')}</li>`);
    }
  }
  if (!lines.length) return '';
  const shown = lines.slice(0, 12);
  const more = lines.length - shown.length;
  return `<ul style="margin:6px 0 0;padding-left:18px;">${shown.join('')}${more > 0 ? `<li>…and ${more} more</li>` : ''}</ul>`;
}

// Paint the board-level banner(s) into #jb-stash-recovery. Red (terminal,
// per-ticket, listing the rows) sits above amber (retryable, one combined
// banner) — "can never send" outranks "will send later", the tsf precedent.
function _jbRenderStashRecoveryBanner(problems) {
  const host = document.getElementById('jb-stash-recovery');
  if (!host) return;
  host.innerHTML = '';
  if (!problems || !problems.length) return;
  // escapeHtml is NOT attribute-safe (skips quotes) — if escAttr is somehow
  // absent, strip to id-safe chars rather than fall back to it. Ticket ids are
  // alphanumeric Firestore ids, so this is lossless for every real value.
  const escA = window.escAttr || ((s) => String(s).replace(/[^A-Za-z0-9_-]/g, ''));
  const refused = problems.filter(p => p.res?.reason === 'locked' || p.res?.reason === 'gone' || p.res?.reason === 'denied');
  const pending = problems.filter(p => !refused.includes(p));

  for (const p of refused) {
    const n = _jbCountCsd(p.stash?.data);
    const num = p.res?.ticket?.ticketNumber ? ` #${escapeHtml(String(p.res.ticket.ticketNumber))}` : '';
    const WHY = {
      billed: `The office has billed that ticket${num}`,
      archived: `The office has closed out that ticket${num}`,
      lockedByOffice: `Dispatch locked that ticket${num}`,
      sent: `That ticket${num} was already sent — ask the office to change it`,
      closed: `That ticket${num} is finished and with the office`,
      gone: 'That ticket is gone',
      denied: `That ticket${num} is closed to changes`,
    };
    const why = WHY[p.res?.reason === 'locked' ? (p.res.lockReason || 'closed') : p.res?.reason] || WHY.closed;
    const el = document.createElement('div');
    el.className = 'jb-csd-unsynced';
    el.dataset.stashTicket = p.ticketId;
    el.style.cssText = 'background:#fef2f2;border-color:#fca5a5;border-left-color:#b91c1c;';
    el.innerHTML = `
      <div class="jb-csd-unsynced-title" style="color:#7f1d1d;">
        <span class="material-icons" aria-hidden="true">report</span>
        ${n} clock-out ${n === 1 ? 'entry' : 'entries'} could NOT be added to the ticket
      </div>
      <div class="jb-csd-unsynced-body" style="color:#7f1d1d;">
        ${why} — sending ${n === 1 ? 'it' : 'them'} from here will not work. Read ${n === 1 ? 'it' : 'them'} to dispatch instead:
        ${_jbStashSummaryHtml(p.stash?.data)}
      </div>
      <button type="button" class="jb-csd-retry" style="background:linear-gradient(135deg,#b91c1c,#7f1d1d);" onclick="window.JobBoardModule._jbDismissStashRecovery('${escA(p.ticketId)}');return false;">
        <span class="material-icons" aria-hidden="true">close</span> Got it — hide this
      </button>`;
    host.appendChild(el);
  }

  if (pending.length) {
    const n = pending.reduce((s, p) => s + _jbCountCsd(p.stash?.data), 0);
    const when = (() => {
      try { return new Date(pending[0].stash.savedAt).toLocaleString(); } catch (_) { return 'earlier'; }
    })();
    const el = document.createElement('div');
    el.className = 'jb-csd-unsynced';
    el.innerHTML = `
      <div class="jb-csd-unsynced-title">
        <span class="material-icons" aria-hidden="true">cloud_off</span>
        ${n} ticket ${n === 1 ? 'entry' : 'entries'} from clock-out still unsent
      </div>
      <div class="jb-csd-unsynced-body">Typed ${escapeHtml(when)} and kept on this phone only. Get a bar of signal, then tap below — ${n === 1 ? 'it is' : 'they are'} not saved until you do.</div>
      <button type="button" class="jb-csd-retry" onclick="window.JobBoardModule._jbRetryStashFromBoard();return false;">
        <span class="material-icons" aria-hidden="true">cloud_upload</span> Send now
      </button>`;
    host.appendChild(el);
  }
}

// The recovery pass. Runs on board entry (deferred, never blocks first paint)
// and again from the banner's Send button. Auto-sends when the network allows;
// otherwise every failure lands as a visible banner. `manual` adds the toasts
// a button press is owed.
async function _jbRecoverAbandonedCsd(manual) {
  try {
    if (window.isReadOnlyLinkSession?.()) return; // read-only link — cannot write
    if (JobBoardState._csdRecoveryRunning) return;
    JobBoardState._csdRecoveryRunning = true;
    try {
      // Settings can land after first paint; identity + labels read better with
      // them. Bounded wait, then go with what we have — the merge tolerates a
      // missing section def.
      for (let i = 0; i < 10 && !window.AppState?.settings; i++) {
        await new Promise(r => setTimeout(r, 500));
      }
      try { _jbFlushAbandonBreadcrumbs(); } catch (_) { /* fire-and-forget */ }

      let stashes = _jbScanCsdStashes();
      // An OPEN Build Ticket sheet owns its own ticket's stash + banner (4594).
      if (JobBoardState._btTicketId) {
        stashes = stashes.filter(s => s.ticketId !== JobBoardState._btTicketId);
      }
      if (!stashes.length) {
        _jbRenderStashRecoveryBanner([]);
        if (manual) window.showToast?.('Nothing left to send — it is already saved.', 'info');
        return;
      }

      const results = [];
      const curPid = window.AppState?.driverPersonId || null;
      for (const { ticketId, stash } of stashes) {
        // TASK-4666 (COD Code Reviewer) — ownership binding on a shared device:
        //  * stash carries a DIFFERENT personId → not this driver's rows. Skip
        //    silently and leave the stash intact for its owner's next login.
        //  * stash predates the personId field (legacy 4594 stash) → never
        //    auto-send; fall to the amber banner so the driver confirms with a
        //    tap ("manual" pass sends it — a deliberate tap on the device that
        //    typed the rows is the ownership evidence we can get).
        if (stash.personId && curPid && stash.personId !== curPid) continue;
        if (stash.personId && !curPid) continue; // can't prove ownership — leave it
        if (!stash.personId && !manual) {
          results.push({ ticketId, stash, res: { ok: false, reason: 'confirm' } });
          continue;
        }
        if (!_jbStashRetryLock(ticketId)) continue; // another tab is on it
        let res;
        try {
          res = await _jbMergeSaveStashedCsd(ticketId, stash.data);
        } catch (err) {
          console.warn('[JobBoard] stash recovery failed:', err?.code || err?.message);
          res = { ok: false, reason: 'offline' };
        } finally {
          _jbStashRetryUnlock(ticketId);
        }
        results.push({ ticketId, stash, res });
      }

      const recoveredN = results.filter(r => r.res.ok).reduce((s, r) => s + (r.res.added || 0), 0);
      const problems = results.filter(r => !r.res.ok);
      if (recoveredN > 0) {
        // Loud on success, per 4594's own reasoning: a silent recovery would
        // leave the driver believing rows the office never saw were fine.
        window.showToast?.(
          `Recovered ${recoveredN} ticket ${recoveredN === 1 ? 'entry' : 'entries'} you typed at clock-out — ${recoveredN === 1 ? 'it is' : 'they are'} on the ticket now.`,
          'success', null, 10000
        );
      }
      if (manual && !recoveredN && problems.some(p => p.res.reason === 'offline')) {
        window.showToast?.('Still no connection — your entries are safe on this phone. Try again when you have a bar.', 'warning');
      }
      _jbRenderStashRecoveryBanner(problems);
    } finally {
      JobBoardState._csdRecoveryRunning = false;
    }
  } catch (err) {
    console.warn('[JobBoard] stash recovery pass failed:', err?.message);
  }
}

function _jbRetryStashFromBoard() {
  return _jbRecoverAbandonedCsd(true);
}

// Deliberate, informed discard — the banner just showed the rows and said they
// can never be added. Breadcrumbed so the metric sees the discard too.
function _jbDismissStashRecovery(ticketId) {
  try {
    window.logError?.(new Error('CSD_STASH_DISMISSED'), {
      module: 'jobboard', operation: 'CSD_STASH_DISMISSED', ticketId,
    });
  } catch (_) { /* breadcrumb is best-effort */ }
  _jbClearCsdStash(ticketId);
  // Match by dataset, not a selector — no reliance on selector-escaping the id.
  document.getElementById('jb-stash-recovery')?.querySelectorAll('[data-stash-ticket]').forEach(el => {
    if (el.dataset.stashTicket === ticketId) el.remove();
  });
}

// TASK-4598 — tell the driver, in words, that the office has billed this ticket.
// Deliberately reads the BROAD predicate (flag OR number OR timestamp), the same
// one the biller's card uses, so the two surfaces cannot disagree — that
// disagreement is the entire bug. Bounded retry because the modal is appended
// after ticket data loads (the same trap TASK-4594's banner hit).
// TASK-4641 — it used to fire ONLY when the ticket was BILLED, so an archived
// (or 'ready' / 'sent' / office-locked) ticket came up as an EMPTY form with
// nothing on screen to explain it. The driver typed a whole ticket into that
// form and only learned on Save that it could never be saved. The banner now
// covers every reason jbRestoreTicketData skipped the fill, and it leads with
// the one thing that matters BEFORE any typing: nothing entered here will save.
function _jbShowTicketBillingState(ticketData, ticketId, _tries, lockReason) {
  const t = ticketData || {};
  // The reason is computed by the caller from the SAME expression that skipped
  // the form fill, so the empty form and the banner cannot disagree about why —
  // that disagreement is both halves of this bug. The fallback keeps the legacy
  // one-argument call shape honest if anything else ever calls this.
  const reason = lockReason
    || ((t.invoiced === true || !!t.invoiceNumber || !!t.invoicedAt) ? 'billed' : null);
  if (!reason) return;
  // Same stale-modal guard as _jbShowCsdSyncState, and for the same reason —
  // COD caught that I had written it once and not reused it. The retry runs up
  // to 4s while closeClockOutModal() can reset state and a NEW modal can mount
  // with the same #jb-bt-sections, so without this a driver could be told an
  // unbilled ticket is billed and read-only. That is worse than the silence
  // this banner exists to replace.
  if (ticketId && JobBoardState._btTicketId && JobBoardState._btTicketId !== ticketId) return;
  const host = document.getElementById('jb-bt-sections');
  if (!host) {
    const n = (_tries || 0) + 1;
    if (n > 40) return;
    setTimeout(() => _jbShowTicketBillingState(ticketData, ticketId, n, reason), 100);
    return;
  }
  document.getElementById('jb-billed-banner')?.remove();
  const el = document.createElement('div');
  el.id = 'jb-billed-banner';
  el.className = 'jb-billed-banner';
  const num = t.invoiceNumber ? ` (${t.invoiceNumber})` : '';
  const TITLE = {
    billed: `The office has billed this ticket${num}`,
    archived: 'The office has closed out this ticket',
    lockedByOffice: 'Dispatch locked this ticket',
    sent: 'This ticket was already sent',
    closed: 'This ticket is finished and with the office',
  };
  // 🔴 The sentence that has to land: the form above is empty and STAYS empty.
  // "It is read-only now" on its own is what let a driver fill in a whole
  // ticket and find out at Save. Say that typing here does not save, and say
  // what to do instead, in the same breath.
  const BODY = {
    billed: 'It is read-only now — anything you type here will NOT save. If something on it is wrong, tell dispatch; do not re-enter it here.',
    archived: 'It is filed and read-only — anything you type here will NOT save. If something on it is wrong, tell dispatch; do not re-enter it here.',
    lockedByOffice: 'It is read-only on your phone — anything you type here will NOT save. If something on it is wrong, call dispatch.',
    sent: JB_SENT_MSG + ' Anything you type here will NOT save.',
    closed: 'It is read-only now — anything you type here will NOT save. If something on it is wrong, tell dispatch; do not re-enter it here.',
  };
  el.innerHTML = `
    <div class="jb-billed-title"><span class="material-icons" aria-hidden="true">receipt_long</span> ${escapeHtml(TITLE[reason] || TITLE.closed)}</div>
    <div class="jb-billed-body">${escapeHtml(BODY[reason] || BODY.closed)}</div>`;
  host.prepend(el);
}

// After a customSectionData write, the open form shows exactly `collected`, and
// `final` = [...preserved, ...collected] per repeatable section. Make `final`
// the new merge baseline with the collected positions marked as on-screen, so
// the next Save in the same sitting can replace an edited row and drop a
// removed one instead of duplicating / resurrecting it. Pure state update.
function _jbAdvanceCsdBaseline(final, collected, serverBefore) {
  const snap = JSON.parse(JSON.stringify(final || {}));
  const prevReport = JobBoardState._csdRestoreReport || {};
  const report = {};
  for (const [csId, val] of Object.entries(snap)) {
    const col = collected ? collected[csId] : null;
    if (val && val._repeatable && Array.isArray(val.instances)) {
      const n = val.instances.length;
      const colN = (col && Array.isArray(col.instances)) ? col.instances.length : 0;
      const okPositions = [];
      for (let p = n - colN; p < n; p++) if (p >= 0) okPositions.push(p);
      report[csId] = { expected: n, okPositions };
    } else {
      // COD (Code Reviewer) — a single-value section is merged field-by-field
      // when its restore was degraded; marking it "seen" just because the form
      // sent a value would make the form authoritative over fields it never
      // displayed. Only claim it when it was already seen, or when the server
      // had nothing for it before this save (the form then wrote all of it).
      const wasSeen = ((prevReport[csId] && prevReport[csId].okPositions) || []).length > 0;
      const wasEmpty = !(serverBefore && serverBefore[csId]);
      report[csId] = { expected: 1, okPositions: (col && (wasSeen || wasEmpty)) ? [0] : [] };
    }
  }
  JobBoardState._csdSnapshot = Object.keys(snap).length ? snap : null;
  JobBoardState._csdRestoreReport = report;
}

async function _jbMergeSaveCustomSectionData(ticketId) {
  const tenant = JobBoardState.tenantId;
  const sections = _jbCsDefs();
  if (sections.length === 0) return; // nothing renderable — never write (matches legacy)

  const collected = jbCollectCustomSectionData();

  // Fresh server read — never base a wholesale write on the stale open-time copy.
  let server = null;
  let _csdBaselineStale = false;
  try {
    // TASK-1642 — bounded. Unbounded, this single read was the LARGEST chunk of
    // the driver's wait in a dead zone (12.7s measured) — bigger than the write
    // hang everyone was looking at.
    const snap = await jbBoundedRead(getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId)), 'ticket:csdBaseline');
    if (!snap) _csdBaselineStale = true;
    else server = snap.exists() ? (snap.data().customSectionData || null) : null;
  } catch (_e) {
    // A rejected read is no better a baseline than a timed-out one — both leave
    // us blind to what the crew has saved. Same decision for both.
    console.warn('[JobBoard] csd baseline read failed:', _e?.code || _e?.message);
    _csdBaselineStale = true;
  }
  if (_csdBaselineStale) server = JobBoardState._csdSnapshot || null;

  const snapshot = JobBoardState._csdSnapshot || null;
  const report = JobBoardState._csdRestoreReport || {};
  const modal = document.getElementById('clockout-modal');

  const final = {};
  let preservedCount = 0;
  const keys = new Set([...Object.keys(server || {}), ...Object.keys(collected)]);
  for (const csId of keys) {
    const cs = sections.find(s => s.id === csId);
    const rendered = !!(modal && modal.querySelector(`[data-custom-section="${csId}"]`));
    const srvVal = server?.[csId];
    if (!cs || !rendered) {
      // Section wasn't shown in this form (deleted from settings, COF-disabled,
      // or settings landed after the modal was built) — keep server data verbatim.
      // Expected behavior for permanently-hidden sections, so no breadcrumb here.
      if (srvVal) final[csId] = srvVal;
      continue;
    }
    const fieldIds = (cs.fields || []).map(f => f.id);
    if (cs.repeatable) {
      const colArr = (collected[csId] && Array.isArray(collected[csId].instances)) ? collected[csId].instances : [];
      const srvArr = _jbCsdInstancesOf(srvVal);
      const snapArr = _jbCsdInstancesOf(snapshot?.[csId]);
      const okPos = report[csId]?.okPositions || [];
      const preserved = srvArr.filter(s =>
        !colArr.some(c => _jbCsdInstEq(s, c, fieldIds)) &&
        !snapArr.some((sn, p) => okPos.includes(p) && _jbCsdInstEq(s, sn, fieldIds))
      );
      preservedCount += preserved.length;
      const finalArr = [...preserved, ...colArr];
      if (finalArr.length > 0) final[csId] = { _repeatable: true, instances: finalArr };
    } else {
      const colVal = collected[csId] || null;
      const restoredOk = (report[csId]?.okPositions || []).length > 0;
      if (!srvVal || restoredOk) {
        // Healthy (or nothing on the server): the form is authoritative.
        if (colVal) final[csId] = colVal;
      } else {
        // Degraded single-value section: server fields survive; the driver's
        // non-empty inputs win field-by-field.
        const merged = (typeof srvVal === 'object') ? { ...srvVal } : {};
        for (const f of (cs.fields || [])) {
          const v = colVal ? colVal[f.id] : undefined;
          const nonEmpty = f.type === 'checkbox' ? v === true
            : f.type === 'number' ? (typeof v === 'number' && v !== 0)
            : !!(v && String(v).trim());
          if (nonEmpty) merged[f.id] = v;
        }
        if (Object.keys(merged).length > 0) { final[csId] = merged; if (!colVal) preservedCount++; }
      }
    }
  }

  // Legacy semantics preserved: never write an all-empty map (a clear-everything
  // form stays a no-op, exactly as before this fix).
  if (Object.keys(final).length === 0) return;
  if (JSON.stringify(final) === JSON.stringify(server || {})) {
    // TASK-4594 — "nothing changed" means the server ALREADY holds exactly this,
    // which is the SUCCESS case for a retry of a previously-stashed write (the
    // earlier attempt landed late). Returning without clearing left the stash on
    // disk and the "never reached the server" banner permanently stuck, with
    // "Send them now" unable to clear it (COD run 2).
    _jbClearCsdStash(ticketId);
    JobBoardState._btCsdUnsaved = false;
    JobBoardState._btCsdStashed = false;
    _jbAdvanceCsdBaseline(final, collected, server);
    return;
  }

  if (preservedCount > 0) {
    console.warn('[JobBoard] CSD_SAVE_PRESERVED —', preservedCount, 'saved entr(ies) not shown in this form were kept');
    try {
      window.logError?.(new Error('CSD_SAVE_PRESERVED'), {
        module: 'jobboard', operation: 'CSD_SAVE_PRESERVED', ticketId, preservedCount
      });
    } catch (_) { /* breadcrumb is best-effort */ }
  }

  // TASK-1642 x TASK-1204 — the read above never answered, so `server` is the
  // open-time snapshot, not the truth. customSectionData is written WHOLE, and
  // the ticket is SHARED with the crew: merging against a guess can silently
  // delete an instance a crew-mate saved since this modal opened. Not writing
  // is recoverable (the form still holds the driver's entries and the message
  // says a connection is needed); deleting a coworker's row is not.
  if (_csdBaselineStale) {
    JobBoardState._btCsdUnsaved = true;
    // TASK-4594 — keep the typing somewhere that survives a tab close, and leave
    // a breadcrumb. This was the ONLY data-losing path in the file with no
    // `logError`: the sibling CSD_SAVE_PRESERVED branch above has one, which is
    // why preserved-instance events were visible and abandoned ones were not.
    const _stashed = _jbStashCsd(ticketId, collected);
    JobBoardState._btCsdStashed = _stashed;
    console.warn('[JobBoard] TASK-1642 custom sections NOT written — no fresh server baseline to merge against');
    // TASK-4666 — through the durable outbox, not straight to logError: its two
    // Firestore addDocs sit in the MEMORY-ONLY offline queue under exactly the
    // weak signal that caused this abandon, and died with the tab — which is
    // why errorLogs showed ZERO abandons while rows were dying in the field.
    // The outbox delivers now when it can, else on the next job-board entry.
    try {
      _jbQueueAbandonBreadcrumb({
        ticketId,
        sections: Object.keys(collected || {}).length,
        instances: Object.values(collected || {})
            .reduce((n, s) => n + ((s && s.instances) ? s.instances.length : 0), 0),
        stashedLocally: _stashed,
      });
    } catch (_) { /* breadcrumb is best-effort */ }
    return;
  }

  await jbAckOrQueue(updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
    // TASK-4699 — custom sections are the ticket body on tenants that use them.
    customSectionData: final, ticketEmailDirtyAt: Timestamp.now(), updatedAt: Timestamp.now()
  }), 'ticket:customSectionData');
  // Steve (TITS) 2026-10-08 — "Line Information says saved but my changes
  // aren't showing up." The baseline below was only ever set when the ticket
  // OPENED, so a row the driver added and saved in THIS sitting counted as
  // "never shown" on the next Save. Edit its notes or re-grab its GPS and the
  // old copy was preserved next to the new one; remove it and it came back.
  // Live: #100815 and #100825 carry those duplicates (CSD_SAVE_PRESERVED).
  // The form now provably shows exactly the collected rows, so they become
  // the "seen" positions; preserved rows (a crew-mate's, or never rendered)
  // stay unseen and keep being protected.
  _jbAdvanceCsdBaseline(final, collected, server);
  // 🔴 ONLY drop the local copy on a CONFIRMED server ack. `jbAckOrQueue`
  // resolves `undefined` for a write it merely QUEUED (offline, or no ack inside
  // JB_WRITE_ACK_MS) and sets `_btWriteUnacked` — and `updateDoc` also resolves
  // undefined on success, so the return value cannot tell them apart. Clearing
  // on the queued case would delete the one durable copy of a write that never
  // landed, and a tab close would then lose the rows for good: the cure worse
  // than the disease (COD Code Reviewer + Independent, both blocking).
  // Conservative on purpose — if anything in this save was queued we KEEP the
  // stash. Worst case the driver sees a "not uploaded" banner for data that did
  // land, and tapping Send clears it. That error direction is free; the other
  // one costs him his afternoon.
  if (JobBoardState._btWriteUnacked !== true) {
    _jbClearCsdStash(ticketId);
    JobBoardState._btCsdUnsaved = false;
    JobBoardState._btCsdStashed = false;
  } else {
    JobBoardState._btCsdStashed = _jbStashCsd(ticketId, collected);
  }
}

function jbBuildPhotosSection() {
  const content = `
    <div id="jb-photo-thumbnails" style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:8px;"></div>
    <button type="button" style="padding:10px 14px;font-size:0.85rem;font-weight:600;border:2px dashed #cbd5e1;border-radius:10px;background:transparent;color:#3b82f6;cursor:pointer;display:flex;align-items:center;gap:6px;width:100%;justify-content:center;" onclick="document.getElementById('jb-photo-input').click()">
      <span class="material-icons" style="font-size:18px;">add_photo_alternate</span> Add Photos
    </button>
    <input type="file" id="jb-photo-input" hidden multiple accept="image/*,.pdf" onchange="window.JobBoardModule.jbHandlePhotos(this.files)">
  `;
  return jbCollapsible('jb-sec-photos', 'photo_camera', 'Photos', content, false);
}

// TASK-9402 — pick-time photo prep for the Build Ticket photos. jbSaveTicket
// and submitClockOut settle it BEFORE locking, so a Save pressed mid-conversion
// still carries the photo.
const _jbPhotoPrep = createPhotoPrepTracker();
// Fold a still-running stamp conversion (clock.js) into _jbPhotoPrep so the
// Save's single settlePhotoPrepBeforeSave both waits for it AND refuses a
// second Save meanwhile. No-op when no stamp is converting.
// COD r3 — a Save that waited on photo prep must still be saving the SAME
// ticket editor it was pressed in. jbResetBuildTicketState replaces the photo
// array on every close/open, so it (plus the entry object) identifies the
// editor session; an in-editor job change keeps both, so it doesn't abort.
function _jbEditorIdentity() {
  return { entry: JobBoardState._btEntry, photos: JobBoardState.btPhotos };
}
function _jbSameEditor(id) {
  const same = !!id && JobBoardState._btEntry === id.entry && JobBoardState.btPhotos === id.photos;
  if (!same) console.warn('[JobBoard] ticket editor changed while photos converted — that Save was dropped');
  return same;
}
function _jbTrackStampPrep() {
  const p = window.ClockModule?.settleStampCapture?.();
  if (p && typeof p.then === 'function') _jbPhotoPrep.track(p);
}

function jbHandlePhotos(files) {
  const list = Array.from(files || []);
  if (!list.length) return;
  // TASK-9402 — an iPhone HEIC was staged raw: broken thumbnail here, and
  // uploaded as HEIC bytes nobody off Safari can open. Convert images to JPEG
  // (PDFs pass through). The ticket carries other data, so a photo that won't
  // convert is SKIPPED with a toast and the rest still stage.
  const target = JobBoardState.btPhotos; // stale-form guard: a reset swaps the array
  return _jbPhotoPrep.track((async () => {
    const { prepared, skipped } = await prepareUploadFiles(list);
    if (JobBoardState.btPhotos !== target) return; // modal closed / another ticket opened
    for (const p of prepared) {
      // TASK-9868 — default type Other; the picker under the thumbnail changes it.
      target.push({ file: p.file, name: p.file?.name || p.source?.name || 'photo.jpg', docType: 'other' });
    }
    if (skipped.length) window.showToast?.(skippedPhotosMessage(skipped), 'error');
    jbRefreshPhotoThumbnails();
  })());
}

// TASK-9868 — the driver says what a staged photo is. Only NEW photos have a
// picker; one already on the ticket keeps the type it was saved with.
function jbSetPhotoType(idx, value) {
  const photo = JobBoardState.btPhotos[idx];
  if (!photo || photo._existing) return;
  photo.docType = normalizeTicketDocType(value);
}

// TASK-9868 — who is uploading, from the signed-in session: the auth uid, plus
// the PIN driver's server-validated name/personId (or the staff member's).
// Never typed. null when there is no uid — store nothing rather than a guess.
function _jbTicketDocUploader() {
  return buildTicketDocUploader({
    uid: auth.currentUser?.uid || '',
    name: getActiveJobBoardPersonName(),
    personId: getActiveJobBoardPersonId() || '',
  });
}

// TASK-9958 — one auditLog FILE_ADDED row per photo now on the ticket. The
// driver's `user` (person name) and `uid` are rule-pinned on the row. Called
// after the ticket write resolves (acked, or queued by the SDK offline — the
// audit addDoc then queues behind it). Fire-and-forget.
function _jbAuditTicketPhotosAdded(ticketId, photos) {
  try {
    const uid = auth.currentUser?.uid || '';
    for (const ph of (photos || [])) {
      if (!ph) continue;
      window.logAudit?.('ticket', 'FILE_ADDED', fileAddedAuditPayload({
        entry: { name: ph.name, fullPath: ph.path || null },
        target: 'ticket_photos', via: 'driver_app', ticketId, uid,
      }));
    }
  } catch (_) { /* best-effort */ }
}

async function jbRemovePhoto(idx) {
  const photo = JobBoardState.btPhotos[idx];
  if (!photo) return;
  if (photo._existing && photo.url) {
    // TASK-1214 — already on the ticket doc: the old splice-only path left it
    // there (reappeared on reopen + rendered on the customer PDF). Route
    // through the shared helper (confirm + arrayRemove + photoCount + audit).
    const _isDriverScope = !!(window.AppState?.driverMode || window.AppState?.driverModePin || window.AppState?.isPublicAccess);
    const res = await window.TicketPhotos?.removeUploaded({
      tenantId: JobBoardState.tenantId,
      ticketId: JobBoardState._btTicketId || JobBoardState._btEntry?.ticketId || null,
      photo: photo._originalPhoto || { url: photo.url, name: photo.name },
      source: 'jobboard-ticket-builder',
      personId: JobBoardState._btEntry?.personId || window.AppState?.driverPersonId || null,
      personName: JobBoardState._btEntry?.personName || null,
      respectDriverLock: _isDriverScope
    });
    if (!res?.ok) return;
    // Splice by identity — the index may have shifted during the await.
    const i = JobBoardState.btPhotos.indexOf(photo);
    if (i !== -1) JobBoardState.btPhotos.splice(i, 1);
  } else {
    JobBoardState.btPhotos.splice(idx, 1);
  }
  jbRefreshPhotoThumbnails();
}

// TASK-1220 — tap a thumbnail → full-size lightbox (PDFs keep a new tab;
// staged PDFs have no url, so they stay non-clickable).
function jbShowPhoto(idx) {
  const photo = JobBoardState.btPhotos[idx];
  if (!photo) return;
  const isPdf = photo.name?.toLowerCase().endsWith('.pdf');
  // TASK-9855 — a saved ticket photo opens through the Storage SDK on its path
  // (derived from the stored URL when no path was saved), not the token URL.
  if (photo._existing) {
    const stored = photo._originalPhoto || photo;
    if (isPdf) { window.StorageFile.open(stored); return; }
    window.StorageFile.blobUrl(stored)
      .then((u) => window.PhotoLightbox?.show(u))
      .catch(() => window.showToast?.('Could not open the photo', 'error'));
    return;
  }
  const url = photo.file ? URL.createObjectURL(photo.file) : '';
  if (!url) return;
  if (isPdf) return;
  window.PhotoLightbox?.show(url);
}

function jbRefreshPhotoThumbnails() {
  // 🔴 TASK-9169, COD round 3, Code Reviewer ❌ — photos are the ONE mutation
  // the recap's delegated click listener cannot see. Two independent reasons,
  // both verified: the delete button calls `event.stopPropagation()` (:7079) so
  // the click never bubbles to the modal, and `jbRemovePhoto` AWAITS the
  // Storage/Firestore removal before splicing state (:7032-7044), so even a
  // capture-phase listener's 250ms timer would read the pre-delete array.
  // Hooking the choke point instead is exact: every add and every delete ends
  // here, state already settled. FIRST statement on purpose — the `!el` and
  // empty-list branches below both return early, and "the driver deleted his
  // last photo" is precisely when the recap's photo line must disappear.
  // No-ops while the recap is collapsed or absent.
  _jbRecapRefresh();
  const el = document.getElementById('jb-photo-thumbnails');
  if (!el) return;
  const photos = JobBoardState.btPhotos;
  if (photos.length === 0) { el.innerHTML = ''; return; }

  el.innerHTML = photos.map((photo, idx) => {
    const isPdf = photo.name.toLowerCase().endsWith('.pdf');
    // TASK-9855 — a saved photo renders through the Storage SDK (imgAttrs, on
    // its stored path / path derived from the URL); only a local staged file
    // uses an object URL of the picked File.
    const imgSrc = photo._existing ? '' : (photo.file ? URL.createObjectURL(photo.file) : '');
    const viewable = !isPdf ? !!(imgSrc || (photo._existing && photo.url)) : !!(photo._existing && photo.url);
    // TASK-9868 — a NEW photo gets a "what is it?" picker under it; a saved one
    // shows its type if it has one (photos saved before this have none, and
    // render exactly as before).
    const _savedType = photo._existing ? ticketDocTypeLabel(photo.docType) : '';
    const typeUi = !photo._existing
      ? `<select aria-label="What is this photo?" onchange="window.JobBoardModule.jbSetPhotoType(${idx}, this.value)" style="width:100px;min-height:40px;font-size:13px;padding:4px;border:1px solid #cbd5e1;border-radius:8px;background:#fff;color:#0f172a;">${ticketDocTypeOptionsHtml(photo.docType || 'other')}</select>`
      : (_savedType ? `<div style="font-size:12px;font-weight:600;color:#334155;max-width:100px;">${escapeHtml(_savedType)}</div>` : '');
    const tile = `
      <div ${viewable ? `onclick="window.JobBoardModule.jbShowPhoto(${idx})"` : ''} style="position:relative;width:68px;height:68px;border-radius:10px;overflow:hidden;border:1px solid #e2e8f0;background:#f8fafc;${viewable ? 'cursor:pointer;' : ''}">
        ${isPdf
          ? `<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;"><span class="material-icons" style="font-size:28px;color:#64748b;">description</span></div>`
          : `<img ${photo._existing ? window.StorageFile.imgAttrs(photo._originalPhoto || photo) : `src="${escapeHtml(imgSrc)}"`} style="width:100%;height:100%;object-fit:cover;" alt="${escapeHtml(photo.name)}">`
        }
        <button type="button" aria-label="Delete photo" onclick="event.stopPropagation();window.JobBoardModule.jbRemovePhoto(${idx})" style="position:absolute;top:0;right:0;width:44px;height:44px;background:transparent;border:none;cursor:pointer;padding:0;display:flex;align-items:flex-start;justify-content:flex-end;">
          <span style="margin:3px 3px 0 0;background:rgba(0,0,0,0.55);border-radius:50%;width:22px;height:22px;display:flex;align-items:center;justify-content:center;"><span class="material-icons" style="font-size:14px;color:#fff;">close</span></span>
        </button>
      </div>
    `;
    return typeUi ? `<div style="display:flex;flex-direction:column;gap:4px;">${tile}${typeUi}</div>` : tile;
  }).join('');
}

// ============================================
// AUTO-FILL EQUIPMENT FROM JOB DAY
// ============================================

// TASK-491 — master tenant setting gate. DEFAULT OFF. When OFF, every clock-in
// + auto-fill path below is byte-for-byte today's behavior (the new code only
// runs inside `if (equipmentTieEnabled())`). Reads AppState (not a module
// import) so there's no ?v= cross-import instance risk.
// Simple clock-out mode turns it OFF (follow-up to TASK-9751): the switch only
// decides which equipment lands on each ticket, Simple mode has no tickets, and
// Billing Settings hides the toggle there — so a tie turned on BEFORE a switch to
// Simple kept asking drivers for equipment at clock-in with no way to turn it
// off. Unset clockOutMode = Simple (every reader defaults it that way).
// Mirrored in clock.js equipmentTieEnabled + dispatch.js _equipmentTieOn — keep in sync.
function equipmentTieEnabled() {
  const s = window.AppState?.settings;
  if (s?.equipmentTie?.enabled !== true) return false;
  return isTicketMode(); // TASK-9553 — this person's mode (override, else company)
}

// TASK-491 — resolve THE jobDay a clock entry belongs to. A recurring job has
// many jobDay docs sharing one jobId; match on the entry's clock-in calendar
// date so multi-day jobs attribute to the right day.
// v1.15.2000 COD Code Reviewer 🔴 (money) — EXACT DATE OR NOTHING. This used to
// fall back to the job's first loaded day when no day matched, and the legacy
// fill skipped the resolver entirely and took the first day outright — so on a
// multi-day job with a different truck each day, Monday's truck landed on
// Tuesday's ticket. A different day's truck is a GUESS; returning null makes
// every caller fill nothing and leave the pick to the driver (the clock-in
// sheets fall through to the never-pre-selected fleet ask).
// The day is the TENANT's calendar day (settings.timezone, via window.ClockDay —
// the same rule as the clock day offset), not the viewer's browser zone.
// jobDay.date is anchored to noon local, so its tenant day is unambiguous; a
// bare 'YYYY-MM-DD' string (GAS import) is read as that calendar day, never as
// UTC midnight (which is the PREVIOUS day in Central).
// When several days of the job share the date, the entry's own jobDayId wins.
// _jbPickJobDayForEntry is the pure core (any day list, no module state) so the
// cold close-out hydration filters its by-jobId read by the SAME rule. It is
// self-contained on purpose — unit harnesses extract it by name.
// mirrors clock.js _clockResolveJobDayForEntry — keep in sync.
function _jbResolveJobDayForEntry(entry) {
  return _jbPickJobDayForEntry(JobBoardState.jobDays || [], entry);
}

function _jbPickJobDayForEntry(jobDays, entry) {
  if (!entry?.jobId) return null;
  const days = (jobDays || []).filter(jd => jd && jd.jobId === entry.jobId);
  if (!days.length) return null;
  if (entry.clockIn == null || entry.clockIn === '') return null;
  const W = (typeof window !== 'undefined' && window) || {};
  const _s = W.AppState?.settings || W.AppState?.tenantData?.settings || {};
  const _tz = _s.timezone || 'America/Chicago';
  const _CD = W.ClockDay;
  const dayKey = (v) => {
    if (typeof v === 'string') {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
      if (m) return Math.round(Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86400000);
    }
    const d = v?.toDate?.() || (v instanceof Date ? v : (v != null ? new Date(v) : null));
    if (!d || isNaN(d.getTime())) return null;
    if (_CD && typeof _CD.localDaySerial === 'function') {
      const k = _CD.localDaySerial(d, _tz);
      if (k != null) return k;
    }
    return Math.round(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000);
  };
  // clockIn may be a bare 'YYYY-MM-DD' (a ticket's workDate) — same day rule.
  const ciKey = dayKey(entry.clockIn);
  if (ciKey == null) return null;
  const sameDay = days.filter(jd => dayKey(jd.date) === ciKey);
  if (!sameDay.length) return null;
  return (entry.jobDayId && sameDay.find(jd => jd.id === entry.jobDayId)) || sameDay[0];
}

// TASK-491 — union of a jobDay's asset ids (trucks + trailers + assets). Used to
// intersect a driver's chosen assetIds against the day's actual equipment and to
// list the assets in the clock-in confirm modal.
function _jbJobDayAssetIds(jobDay) {
  if (!jobDay) return new Set();
  const ids = new Set();
  (jobDay.trucks || []).forEach(r => { if (r != null) ids.add(String(r)); });
  (jobDay.trailers || []).forEach(r => { if (r != null) ids.add(String(r)); });
  (jobDay.assets || []).forEach(r => { if (r != null) ids.add(String(r)); });
  return ids;
}

// TASK-592 — resolve ONE asset id to a display {name, icon} against
// JobBoardState's roster lists (legacy dual lookup: id OR unit OR name).
// Per-id sibling of _jbBuildEquipRows (which walks a whole jobDay). Unknown
// ids (asset deleted since clock-in) fall back to the raw id string.
function _jbResolveAssetDisplay(id) {
  const sid = String(id);
  let asset = _resolveFleetRef(sid, JobBoardState.trucks);
  let icon = 'local_shipping';
  if (!asset) { asset = _resolveFleetRef(sid, JobBoardState.trailers); if (asset) icon = 'rv_hookup'; }
  if (!asset) { asset = _resolveFleetRef(sid, JobBoardState.assets); if (asset) icon = 'construction'; }
  const name = (window.getUnitDisplay && asset ? window.getUnitDisplay(asset) : '') || asset?.name || asset?.unit || sid;
  return { name, icon };
}

// TASK-592 — driver self-confirm strip ("You're in: Duck"). Lets the driver
// verify their clock-in equipment tap without asking dispatch. Render-only:
// reads the viewer's own active entry off the existing personId-scoped
// clockEntries subscription. Hidden (zero chrome) when:
//   • equipmentTie is OFF (tenant gate),
//   • not clocked in,
//   • the entry has NO assetIds field (kiosk/legacy pre-feature entry),
//   • assetIds is [] on a job day with no equipment anyway.
// assetIds:[] on a day that HAS equipment shows the subtle labor-only line so
// a driver who fat-fingered "No Equipment" can catch it.
function renderEquipTieStatus() {
  const el = document.getElementById('jb-equip-status');
  if (!el) return;
  const hide = () => { el.style.display = 'none'; el.innerHTML = ''; };

  if (!equipmentTieEnabled()) return hide();
  const pid = getActiveJobBoardPersonId();
  const myEntry = pid ? (JobBoardState.clockEntries || []).find(e => e.personId === pid) : null;
  if (!myEntry || !Array.isArray(myEntry.assetIds)) return hide();

  const pillBase = 'display:inline-flex;align-items:center;gap:5px;font-size:0.8rem;font-weight:600;border-radius:999px;padding:4px 12px;';
  let inner;
  if (myEntry.assetIds.length === 0) {
    const jobDay = _jbResolveJobDayForEntry(myEntry);
    if (!jobDay || _jbJobDayAssetIds(jobDay).size === 0) return hide();
    inner = `
      <span style="${pillBase}color:var(--text-secondary);border:1px dashed var(--border-light);background:var(--bg-elevated);">
        <span class="material-icons" style="font-size:15px;">person</span>You're in: no equipment (labor only)
      </span>`;
  } else {
    // One pill: "You're in: Duck · 01" (multi-asset → comma-joined names).
    const resolved = myEntry.assetIds.map(id => _jbResolveAssetDisplay(id));
    const names = resolved.map(r => escapeHtml(r.name)).join(', ');
    const icon = resolved[0]?.icon || 'local_shipping';
    inner = `
      <span style="${pillBase}color:#1d4ed8;background:#eff6ff;border:1px solid #bfdbfe;">
        <span class="material-icons" style="font-size:15px;">${escapeHtml(icon)}</span>You're in: ${names}
      </span>`;
  }
  el.style.display = 'flex';
  el.style.flexWrap = 'wrap';
  el.style.justifyContent = 'center';
  el.style.gap = '6px';
  el.style.margin = '0 0 10px';
  el.innerHTML = inner;
}

// TASK-1221 — the job's ticketing mode ('combined' default). A job missing from
// the loaded list (subscription race / legacy window) resolves 'combined',
// matching the ticket resolver's own default for the same entry.
function _jbJobSplitBy(jobId) {
  if (!jobId) return 'combined';
  const j = (JobBoardState.jobs || []).find(x => x.id === jobId);
  return (j && j.splitBy) || 'combined';
}

// TASK-1547 — normalize the assetProvenance map written on the clockEntry at
// clock-in. Records the CHOICE (what was rendered, what was picked, whether
// the driver touched it) alongside the outcome (assetIds), so tech support can
// answer "I picked no equipment" and the TASK-1539 fill can tell deliberate
// labor-only from nothing-to-pick. Firestore rejects `undefined`
// (memory/lessons) — every field is explicitly false/[]/'' here. Written ONLY
// on the equipmentTie ON paths (always alongside an assetIds array); OFF-path
// entries stay byte-identical to pre-1547.
// mirrors clock.js _clockBuildAssetProvenance — keep in sync.
function _jbBuildAssetProvenance(source, sel, jobDayAssetIdSet) {
  return {
    source: source || 'jobboard',
    renderedAssetIds: Array.isArray(sel?.renderedAssetIds) ? sel.renderedAssetIds.map(String) : [],
    selectedAssetIds: Array.isArray(sel?.selectedAssetIds) ? sel.selectedAssetIds.map(String) : [],
    explicitLaborOnly: sel?.explicitLaborOnly === true,
    jobDayAssetIds: jobDayAssetIdSet ? Array.from(jobDayAssetIdSet).map(String) : [],
    defaulted: sel?.defaulted === true
  };
}

// TASK-1539 (💰) — does this clock entry count as "missing assetIds" for the
// mid-week LEGACY fallback in _jbAutoFillEquipmentTied? Historically the guard
// was `!Array.isArray(e.assetIds)`, which made `assetIds: []` permanently
// labor-only — even when the [] only exists because the jobDay had NO
// equipment at clock-in (exactly what SMS-assistant jobs produce). Dispatch
// adding the truck to the jobDay later could then NEVER reach the ticket
// (#100473: $1,275 unbilled; #100476: no equipment line). The fix
// distinguishes "picked nothing from a real menu" (deliberate — MUST stay
// empty) from "there was nothing to pick" (fallback-eligible once equipment
// exists), via the TASK-1547 assetProvenance when present and a conservative
// reconstruction for legacy entries.
//
// DECISION TABLE (top rule that matches wins):
//   1. assetIds not an array (field absent) ………………………… MISSING (unchanged —
//      kiosk-admin / dispatch_pin / switch-job / pre-feature entries).
//   2. SPLIT job (opts.splitJob) ………………………………………… only rule 1 applies; [] NEVER
//      promotes (TASK-1221 🔴 hard constraint: the legacy all-assets fill on a
//      split job lands the whole day's rigs on EVERY sibling ticket =
//      double-bill; both TASK-1539 incident tickets were splitBy:"combined").
//   3. assetIds non-empty ……………………………………………………… NOT missing (real selection).
//   4. [] + provenance.explicitLaborOnly === true ………………… NOT missing (driver
//      tapped "No Equipment (Labor Only)" — never fill).
//   5. [] + provenance.renderedAssetIds non-empty ………………… NOT missing (a real
//      menu was shown and nothing was picked — deselect-all or dismissed
//      sheet — deliberate labor-only).
//   6. [] + provenance.renderedAssetIds === [] ……………………… MISSING (nothing to
//      pick at clock-in — becomes fallback-eligible once equipment exists).
//   7. [] + provenance present but renderedAssetIds NOT an array (server
//      bypass-stamp / strip annotation only) ………………………… NOT missing —
//      conservative, and it preserves onClockEntryAssetValidate's stamp
//      defense: a confirm-UI bypass must read labor-only, never re-attribute
//      the whole day's fleet.
//   8. [] + NO provenance (legacy, pre-1547): "did the day have equipment at
//      clock-in" is NOT reconstructible from the entry, so per the TASK-1539
//      spec use the day's state NOW:
//        • jobDay has equipment NOW …… MISSING — eligible ONLY via the
//          day-level legacy all-assets fallback that already exists for
//          rule-1 entries (bills the day exactly as pre-feature). A pre-1547
//          deliberate labor-only pick on such a day degrades to legacy day
//          attribution — accepted trade against the silent $0-equipment
//          ticket.
//        • jobDay has NO equipment now … NOT missing (nothing to fill anyway).
// mirrors clock.js _clockEntryCountsAsMissingAssetIds — keep in sync.
function _jbEntryCountsAsMissingAssetIds(entry, opts) {
  const a = entry ? entry.assetIds : null;
  if (!Array.isArray(a)) return true;                              // rule 1
  if (opts && opts.splitJob === true) return false;                // rule 2
  if (a.length > 0) return false;                                  // rule 3
  const prov = entry.assetProvenance;
  if (prov && typeof prov === 'object') {
    if (prov.explicitLaborOnly === true) return false;             // rule 4
    if (!Array.isArray(prov.renderedAssetIds)) return false;       // rule 7
    return prov.renderedAssetIds.length === 0;                     // rules 5+6
  }
  return !!(opts && opts.jobDayHasAssetsNow === true);             // rule 8
}

// TASK-9579 — the shift is on a job, but that job has no day on the shift's
// clock-in TENANT date (_jbResolveJobDayForEntry — the fill's own rule), so the
// fill below can put no truck on the ticket. Returns { entryId, label } or null.
// Shop / internal-time customers bill no truck by design: null.
// mirrors clock.js _clockEquipDayMiss — keep in sync.
function _jbEquipDayMiss(entry) {
  try {
    if (!entry?.jobId || entry.clockIn == null || entry.clockIn === '') return null;
    if (entry.customerId === '__shop__') return null;
    // COD — an unread day list (cold close-out read failed; it already toasts
    // "couldn't load") is not "no job day found".
    if (JobBoardState._closeOutDaysUnknownFor && JobBoardState._closeOutDaysUnknownFor === entry.id) return null;
    if (_jbCustomerIsInternalTime(entry.customerId || null)) return null;
    if (_jbResolveJobDayForEntry(entry)) return null;
    const d = entry.clockIn?.toDate?.() || (entry.clockIn instanceof Date ? entry.clockIn : new Date(entry.clockIn));
    if (!d || isNaN(d.getTime())) return null;
    const tz = (window.AppState?.settings || window.AppState?.tenantData?.settings || {}).timezone || 'America/Chicago';
    let label;
    try { label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz }); }
    catch (_) { label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); }
    return { entryId: entry.id || null, label };
  } catch (e) {
    console.warn('[JobBoard] TASK-9579 day-miss check failed (no warning shown):', e?.message);
    return null;
  }
}

function _jbEquipDayMissHtml(miss) {
  return `
    <div id="jb-equip-daymiss" role="alert" style="padding:12px;border:1px solid #fcd34d;background:#fffbeb;border-radius:10px;">
      <div style="display:flex;gap:8px;align-items:flex-start;">
        <span class="material-icons" style="font-size:20px;color:#b45309;flex:0 0 auto;">warning</span>
        <div style="min-width:0;">
          <div style="font-size:0.9rem;font-weight:700;color:#78350f;line-height:1.3;">No truck found for this shift's day (${escapeHtml(miss.label || '')}).</div>
          <div style="font-size:0.78rem;color:#92400e;margin-top:4px;line-height:1.35;">No job day for this job was found on that date, so nothing was filled. Check the equipment before you submit — otherwise this ticket bills no truck.</div>
        </div>
      </div>
    </div>`;
}

function jbAutoFillEquipment(entry) {
  // TASK-9579 — a date miss must be SAID on the sheet, never silent. Split
  // jobs with the tie OFF fill nothing on any day by design (TASK-1221), so
  // they are not a date miss.
  JobBoardState._btEquipDayMiss = null;
  if (!entry.jobId) return;
  if (equipmentTieEnabled() || _jbJobSplitBy(entry.jobId) === 'combined') {
    JobBoardState._btEquipDayMiss = _jbEquipDayMiss(entry);
  }

  // TASK-491 — when per-operator equipment tracking is ON, route through the
  // operator-scoped fill (with the mid-week guard). When OFF (default), fall
  // through to the legacy fill below — UNCHANGED.
  if (equipmentTieEnabled()) {
    _jbAutoFillEquipmentTied(entry);
    // TASK-1572 Slice 2 — the driver's clock-in answer on an empty day (a
    // provenance claim, never assetIds). No-op for every entry without one.
    _jbFillDriverClaimedAssets(entry);
    return;
  }

  // TASK-1221 (codex HIGH — MONEY) — split job + equipment-tie OFF: the entry
  // carries no per-operator assets, so every operator degrades to an op: ticket
  // and the legacy ALL-assets fill below would land the whole day's rigs on
  // EVERY one of those sibling tickets = the same truck-day billed N times.
  // Fill NOTHING automatically — the split ticket stays labor-only (visible
  // under-bill the biller fixes on the one right ticket, never a silent
  // double-bill). Combined jobs keep the legacy fill, unchanged.
  if (_jbJobSplitBy(entry.jobId) !== 'combined') return;

  // v1.15.2000 COD 🔴 — the SHIFT's day, never the job's first loaded day. No
  // day on the clock-in date → fill nothing; the driver picks.
  const jobDay = _jbResolveJobDayForEntry(entry);
  if (!jobDay) return;

  const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
  const now = new Date();

  // TASK-427/442: equipment lines are created per-session (keyed to clockEntryId),
  // mirroring employee lines. Dedup-skip an asset ONLY when it's already present FOR
  // THIS clockEntry — a 2nd work session (new clockEntry, e.g. after lunch) must get
  // its own fresh equipment line (with its own [from,to] window) even though
  // session-1's lines were restored. The post-write server dedup (onTicketLineWrite →
  // runServerSideDedup) then consolidates all of an asset's per-session lines into ONE
  // line whose hours are the INTERVAL-UNION of those windows (TASK-442) — so team-
  // driving one truck bills its real hours once, and a re-clock-after-lunch bills the
  // summed worked time with the idle gap excluded.
  const sessionId = entry.id || null;
  const existingAssetIds = new Set(
    (JobBoardState.btEquipment || [])
      .filter(e => (e._clockEntryId || null) === sessionId)
      .map(e => e.assetId)
      .filter(Boolean)
  );

  (jobDay.trucks || []).forEach(tRef => {
    const r = String(tRef);
    const truck = _resolveFleetRef(r, JobBoardState.trucks);
    if (!truck) return;
    if (existingAssetIds.has(truck.id)) return;
    existingAssetIds.add(truck.id);
    JobBoardState.btEquipment.push({
      assetId: truck.id,
      assetName: (window.getUnitDisplay ? window.getUnitDisplay(truck) : '') || truck.name || truck.unit || 'Truck',
      assetType: 'truck', fleetNumber: truck.unit || '',
      rate: 0, rateUnit: 'Hourly',
      startTime: jbToLocalDT(clockIn), endTime: jbToLocalDT(now),
      // TASK-572: auto-filled window tracks the clock window until manually edited
      notes: '', _dirty: false, _saved: false, _clockEntryId: sessionId, _followsClock: true
    });
  });

  (jobDay.trailers || []).forEach(tRef => {
    const r = String(tRef);
    const trailer = _resolveFleetRef(r, JobBoardState.trailers);
    if (!trailer) return;
    if (existingAssetIds.has(trailer.id)) return;
    existingAssetIds.add(trailer.id);
    JobBoardState.btEquipment.push({
      assetId: trailer.id,
      assetName: (window.getUnitDisplay ? window.getUnitDisplay(trailer) : '') || trailer.name || trailer.unit || 'Trailer',
      assetType: 'trailer', fleetNumber: trailer.unit || '',
      rate: 0, rateUnit: 'Hourly',
      startTime: jbToLocalDT(clockIn), endTime: jbToLocalDT(now),
      // TASK-572: auto-filled window tracks the clock window until manually edited
      notes: '', _dirty: false, _saved: false, _clockEntryId: sessionId, _followsClock: true
    });
  });
}

// TASK-491 — per-operator auto-fill (ON path only; OFF path never reaches here).
// Fills ONLY the equipment THIS driver tied to at clock-in (entry.assetIds),
// intersected with the day's actual assets, each stamped with the driver's own
// [clockIn, clockOut] window — so a truck that left early no longer inherits the
// rest of the crew's later hours.
//
// MID-WEEK GUARD: if ANY clock entry on this jobDay lacks an assetIds field
// (created before the tenant turned the setting on, or via a path that didn't
// capture it — kiosk clock.js / dispatch.js / standalone-no-job), the WHOLE day
// falls back to the LEGACY fill (all trucks+trailers) so we never half-attribute
// a day. This is correct-by-design, not a bug: a day is either fully
// per-operator or fully legacy, never mixed.
//
// VISIBILITY SCOPE (COD Code-Reviewer/Blast 2026-06-08): the JobBoardState
// clockEntries subscription is personId-scoped + status=='active' (firestore
// rules forbid a PIN driver from reading coworkers' entries — jobboard.js:615).
// So this client guard reliably sees only the CURRENT driver's own entries. That
// is sufficient for correctness here because each driver's auto-fill ONLY ever
// writes THAT driver's own equipment lines (keyed to their clockEntryId) — a
// coworker's kiosk/legacy entry can't corrupt this driver's lines. The trigger
// (functions onClockEntryAssetValidate) backfills [] for any jobboard-sourced
// entry that bypassed the modal, so the driver's OWN entries are never "missing"
// when ON. Cross-crew, full-day "never-mix" is enforced at the billing layer
// (TASK-495), not here. The guard below is the per-driver first line.
// TASK-1635 (💰) — the driver's clock-in-tied equipment must ALWAYS materialize
// as EQUIPMENT lines at ticket build, whether or not dispatch listed the rig on
// that day's jobDay. The old per-operator fill intersected entry.assetIds with
// the day's list and silently DISCARDED the rest — the truck the driver was
// standing next to lost to a plan nothing reconciles (Steve hand-added
// $1,582.50 of dropped lines in one morning; #100496/#100497/#100499, all
// `clockEntryId: undefined` — a human rebuilding what this fill dropped). The
// intersection's PURPOSE (a stray pick must not silently bill another crew's
// rig) is kept, but as a billing-review FLAG on a real line instead of a
// silent drop:
//   * on-day tied assets fill exactly as before (unflagged);
//   * off-day tied assets STILL fill, marked `_offJobDay` so the save path
//     stamps `_billingReviewFlag{reason:'equipment_not_on_jobday'}` (the
//     server's own flag convention — equipmentLineEnrichment.js) and the
//     billing review banner surfaces it to a human;
//   * the driver is told in plain words at the modal, not left to wonder.
// Rates stay 0 client-side (TASK-1165 S1b): the server prices from the price
// book; an off-day asset resolves via contract/item-code exactly like any
// other, and a no-match lands in the existing unpriced review badge.

// Resolve an id/unit/name reference against trucks → trailers → generic assets.
function _jbResolveAnyAsset(id) {
  let asset = _resolveFleetRef(id, JobBoardState.trucks);
  let assetType = 'truck';
  if (!asset) { asset = _resolveFleetRef(id, JobBoardState.trailers); assetType = 'trailer'; }
  if (!asset) { asset = _resolveFleetRef(id, JobBoardState.assets); assetType = 'asset'; }
  return asset ? { asset, assetType } : null;
}

// Day refs may be doc ids OR unit/name strings (live jobDays mix both:
// `["GGkyu9fi…","5"]`), while entry.assetIds are usually doc ids. Compare via
// BOTH forms so an asset the day lists under another ref form counts as
// on-day (unflagged) instead of being false-flagged as off-day.
function _jbExpandedDayAssetIds(jobDay) {
  const raw = _jbJobDayAssetIds(jobDay);
  const out = new Set(raw);
  raw.forEach(r => {
    const hit = _jbResolveAnyAsset(r);
    if (hit && hit.asset.id != null) out.add(String(hit.asset.id));
  });
  return out;
}

function _jbTiedIdIsOnDay(id, expandedDayIds) {
  if (expandedDayIds.has(id)) return true;
  const hit = _jbResolveAnyAsset(id);
  return !!(hit && hit.asset.id != null && expandedDayIds.has(String(hit.asset.id)));
}

// Fill tied-but-off-day assets as flagged EQUIPMENT rows. Never silent: a
// resolvable asset becomes a row (server enrichment prices it); an id that
// resolves to NOTHING client-side (deleted asset / subscription race) still
// becomes a row keyed by the raw id — the server resolves by doc id, and if
// it can't either, the unpriced review badge puts a human on it.
function _jbFillTiedOffDayAssets(entry, offIds) {
  if (!Array.isArray(offIds) || offIds.length === 0) return;
  const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
  const now = new Date();
  const sessionId = entry.id || null;
  const present = new Set(
    (JobBoardState.btEquipment || [])
      .filter(e => (e._clockEntryId || null) === sessionId)
      .map(e => e.assetId)
      .filter(Boolean)
  );
  const added = [];
  offIds.forEach(id => {
    const hit = _jbResolveAnyAsset(id);
    const assetId = hit ? (hit.asset.id || id) : id;
    if (present.has(assetId) || present.has(id)) return;
    present.add(assetId);
    const assetName = hit
      ? ((window.getUnitDisplay ? window.getUnitDisplay(hit.asset) : '') || hit.asset.name || hit.asset.unit || 'Equipment')
      : ('Unit ' + id);
    JobBoardState.btEquipment.push({
      assetId,
      assetName,
      assetType: hit ? hit.assetType : 'truck',
      fleetNumber: hit ? (hit.asset.unit || '') : '',
      rate: 0, rateUnit: 'Hourly',
      startTime: jbToLocalDT(clockIn), endTime: jbToLocalDT(now),
      // TASK-572: auto-filled window tracks the clock window until manually edited
      notes: '', _dirty: false, _saved: false, _clockEntryId: sessionId, _followsClock: true,
      // TASK-1635 — tied at clock-in, not on the jobDay: bill + flag, never drop.
      _offJobDay: true
    });
    added.push(assetName);
  });
  if (added.length > 0) {
    window.showToast?.(
      added.join(', ') + (added.length === 1 ? ' isn\'t' : ' aren\'t')
      + ' on today\'s job — kept on your ticket from your clock-in. Dispatch will confirm.',
      'warning', null, 8000
    );
  }
}

function _jbAutoFillEquipmentTied(entry) {
  const jobDay = _jbResolveJobDayForEntry(entry);
  const _tiedIds = (Array.isArray(entry.assetIds) ? entry.assetIds : []).map(String);
  if (!jobDay) {
    // TASK-1635 — no resolvable jobDay used to mean the tied rig vanished from
    // the ticket entirely. Materialize it flagged instead.
    _jbFillTiedOffDayAssets(entry, _tiedIds);
    return;
  }

  // Collect every (visible) clock entry that belongs to THIS jobDay (same jobId
  // + same TENANT clock-in date — the resolver's own day rule, so grouping and
  // day selection can never disagree), always including the entry being
  // clocked out. Mid-week guard trips if any one is missing assetIds.
  const dayEntries = (JobBoardState.clockEntries || []).filter(e =>
    e.jobId === entry.jobId && _jbPickJobDayForEntry([jobDay], { jobId: e.jobId, clockIn: e.clockIn }) === jobDay);
  // Defensive: the entry being clocked out may not be in the (still-settling)
  // subscription — include it explicitly so its own assetIds always count.
  if (!dayEntries.some(e => e.id === entry.id)) dayEntries.push(entry);

  // TASK-1221 (codex HIGH — MONEY) — on a SPLIT job the mid-week fallback below
  // is forbidden: one assetIds-less entry on the day (e.g. a dispatch remote
  // clock-in) would legacy-fill ALL the day's trucks+trailers onto EVERY
  // operator's own split ticket — the same rig billed on N sibling tickets.
  // For split jobs we always use the per-operator fill (own assets only); an
  // entry with no assetIds fills nothing (labor-only ticket, biller adds the
  // rig on the one ticket it belongs to). Combined jobs keep the mid-week
  // guard exactly as before.
  const _splitJob = _jbJobSplitBy(entry.jobId) !== 'combined';

  // TASK-1539 — "missing" now includes empty-array entries whose [] means
  // "there was nothing to pick at clock-in" (per the decision table on
  // _jbEntryCountsAsMissingAssetIds above), so a jobDay that gets its
  // equipment AFTER a driver clocked in can still bill it via the legacy
  // fallback. Deliberate labor-only picks stay empty; split jobs keep the
  // pre-1539 predicate exactly (rule 2 — the splitJob flag short-circuits
  // before any [] promotion, and the fallback below is `&& !_splitJob` gated
  // anyway — double insurance that split behavior is byte-identical).
  const dayAssetIds = _jbJobDayAssetIds(jobDay);
  // TASK-1635 — partition the driver's tied assets ONCE: on-day fills clean,
  // off-day fills flagged (both branches below append the off-day rows — the
  // mid-week legacy fallback only fills the DAY's list, so a tied off-day rig
  // was dropped there too).
  const _expandedDayIds = _jbExpandedDayAssetIds(jobDay);
  const _offDayTiedIds = _tiedIds.filter(id => !_jbTiedIdIsOnDay(id, _expandedDayIds));
  const anyMissingAssetIds = dayEntries.some(e => _jbEntryCountsAsMissingAssetIds(e, {
    splitJob: _splitJob,
    jobDayHasAssetsNow: dayAssetIds.size > 0
  }));

  const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
  const now = new Date();
  const sessionId = entry.id || null;
  const existingAssetIds = new Set(
    (JobBoardState.btEquipment || [])
      .filter(e => (e._clockEntryId || null) === sessionId)
      .map(e => e.assetId)
      .filter(Boolean)
  );

  // --- MID-WEEK FALLBACK: replicate the legacy fill (trucks + trailers, ALL of
  // them) exactly, so a partially-captured day bills the same as it would have
  // before the feature was turned on. ---
  if (anyMissingAssetIds && !_splitJob) {
    (jobDay.trucks || []).forEach(tRef => {
      const r = String(tRef);
      const truck = _resolveFleetRef(r, JobBoardState.trucks);
      if (!truck || existingAssetIds.has(truck.id)) return;
      existingAssetIds.add(truck.id);
      JobBoardState.btEquipment.push({
        assetId: truck.id,
        assetName: (window.getUnitDisplay ? window.getUnitDisplay(truck) : '') || truck.name || truck.unit || 'Truck',
        assetType: 'truck', fleetNumber: truck.unit || '',
        rate: 0, rateUnit: 'Hourly',
        startTime: jbToLocalDT(clockIn), endTime: jbToLocalDT(now),
        // TASK-572: auto-filled window tracks the clock window until manually edited
        notes: '', _dirty: false, _saved: false, _clockEntryId: sessionId, _followsClock: true
      });
    });
    (jobDay.trailers || []).forEach(tRef => {
      const r = String(tRef);
      const trailer = _resolveFleetRef(r, JobBoardState.trailers);
      if (!trailer || existingAssetIds.has(trailer.id)) return;
      existingAssetIds.add(trailer.id);
      JobBoardState.btEquipment.push({
        assetId: trailer.id,
        assetName: (window.getUnitDisplay ? window.getUnitDisplay(trailer) : '') || trailer.name || trailer.unit || 'Trailer',
        assetType: 'trailer', fleetNumber: trailer.unit || '',
        rate: 0, rateUnit: 'Hourly',
        startTime: jbToLocalDT(clockIn), endTime: jbToLocalDT(now),
        // TASK-572: auto-filled window tracks the clock window until manually edited
        notes: '', _dirty: false, _saved: false, _clockEntryId: sessionId, _followsClock: true
      });
    });
    // TASK-1635 — the legacy fill covers the DAY's list; a rig this driver tied
    // that dispatch never listed still has to bill (flagged).
    _jbFillTiedOffDayAssets(entry, _offDayTiedIds);
    return;
  }

  // --- PER-OPERATOR FILL: THIS entry's chosen assetIds, on-day ones clean. ---
  // (dayAssetIds hoisted above for the TASK-1539 missing predicate.)
  // TASK-1635 — matching is via _jbTiedIdIsOnDay (both ref forms), and the
  // off-day remainder is appended FLAGGED below instead of silently dropped.
  const chosen = _tiedIds.filter(id => _jbTiedIdIsOnDay(id, _expandedDayIds));

  chosen.forEach(id => {
    if (existingAssetIds.has(id)) return;
    // Resolve against trucks, then trailers, then generic assets.
    let asset = _resolveFleetRef(id, JobBoardState.trucks);
    let assetType = 'truck';
    if (!asset) { asset = _resolveFleetRef(id, JobBoardState.trailers); assetType = 'trailer'; }
    if (!asset) { asset = _resolveFleetRef(id, JobBoardState.assets); assetType = 'asset'; }
    if (!asset) return;
    existingAssetIds.add(asset.id || id);
    JobBoardState.btEquipment.push({
      assetId: asset.id || id,
      assetName: (window.getUnitDisplay ? window.getUnitDisplay(asset) : '') || asset.name || asset.unit || (assetType.charAt(0).toUpperCase() + assetType.slice(1)),
      assetType, fleetNumber: asset.unit || '',
      rate: 0, rateUnit: 'Hourly',
      startTime: jbToLocalDT(clockIn), endTime: jbToLocalDT(now),
      // TASK-572: auto-filled window tracks the clock window until manually edited
      notes: '', _dirty: false, _saved: false, _clockEntryId: sessionId, _followsClock: true
    });
  });

  // TASK-1635 — the tied assets dispatch never listed on this day: bill + flag.
  _jbFillTiedOffDayAssets(entry, _offDayTiedIds);
}

// ============================================
// RESTORE SAVED TICKET DATA
// ============================================

async function jbRestoreTicketData(ticketId) {
  const tenant = JobBoardState.tenantId;
  // TASK-4641 — clear BEFORE the read, so a ticket we never got to look at can
  // never inherit the previous ticket's lock and refuse a save that is fine.
  JobBoardState._btLockReason = null;
  try {
    const ticketSnap = await getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId));
    if (!ticketSnap.exists()) return;
    const ticketData = ticketSnap.data();

    // TASK-395: keep already-captured field-ticket signatures so the Signatures
    // card can show them on re-open (sign-anytime) instead of a blank pad.
    JobBoardState._btFieldTicketData = ticketData.fieldTicketData || null;

    // TASK-1204 — snapshot customSectionData BEFORE the lock check below. The
    // locked/'ready' early-return skips the form fill, and the merge-save needs
    // both the baseline and the (empty) restore report to know NOTHING was shown
    // — otherwise the next Save would wholesale-wipe these instances.
    JobBoardState._csdSnapshot = ticketData.customSectionData
      ? JSON.parse(JSON.stringify(ticketData.customSectionData)) : null;
    JobBoardState._csdRestoreReport = {};

    // Check lock. TASK-566 — also honors the biller's per-ticket
    // driverEditLocked 🔒 for DRIVER scope only (members/dispatchers keep
    // editing — the lock is driver-facing, mirroring the firestore.rules
    // driver-session gate). When locked we skip restoring lines into the
    // editable form; the clock-out submit path stays never-trap (STEP 1
    // completes the clock entry first, STEP 2 line writes are best-effort and
    // a rules denial surfaces as the "finalized by dispatch" warning toast).
    const _isDriverScope = !!(window.AppState?.driverMode || window.AppState?.driverModePin || window.AppState?.isPublicAccess);
    // TASK-4600 — the driver lock reads the same DISPLAY predicate the biller's
    // gate now uses (flag OR invoiceNumber OR invoicedAt). It previously keyed on
    // the raw boolean only, so a ticket carrying an invoice number with the flag
    // cleared was editable on a phone while rendering as billed — which is how a
    // $10,153 invoiced ticket got edited 31 minutes after it was billed. The two
    // surfaces disagreeing is the bug; they now cannot.
    // TASK-4641 — the same test, but it now yields the REASON as well as the
    // verdict, and the banner below is driven by that reason. One expression,
    // so "why is this form empty" and "what does the banner say" cannot drift.
    const _hardLock = _jbTicketLockReason(ticketData);   // six-field rules mirror
    const _lkSt = String(ticketData.status || '').toLowerCase();
    const _lockReason =
        ((_hardLock === 'lockedByOffice' || _hardLock === 'sent') && !_isDriverScope) ? null  // biller 🔒 / sent lock are driver-facing only
      : _hardLock ? _hardLock
      : (_lkSt === 'invoiced') ? 'billed'
      : (_lkSt === 'sent') ? 'sent'   // TASK-10026 — legacy status, display only
      : (_lkSt === 'ready') ? 'closed'
      : null;
    const isLocked = !!_lockReason;
    JobBoardState._btLockReason = _lockReason;
    if (isLocked) {
      // TASK-4598 — this used to just `return`. The driver's screen then showed a
      // ticket with none of its lines and NO explanation, and the word "invoiced"
      // appeared nowhere on the driver side of this file except in the condition
      // right above. That is how Steve ended up telling Lacey his ticket "said
      // draft": it did, because `status` is a separate field from `invoiced` and
      // his screen never mentioned the second one at all. The biller's AR/AP card
      // has shown an "Invoiced" chip the whole time — the driver's never did.
      _jbShowTicketBillingState(ticketData, ticketId, 0, _lockReason);
      return;
    }

    // Fetch ticket lines
    const linesSnap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'));
    const lines = linesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    // Equipment
    for (const line of lines.filter(l => l.type === 'EQUIPMENT')) {
      const fromTime = line.fromTime?.toDate?.() || (line.fromTime ? new Date(line.fromTime) : new Date());
      const toTime = line.toTime?.toDate?.() || (line.toTime ? new Date(line.toTime) : new Date());
      JobBoardState.btEquipment.push({
        assetId: line.assetId || '', assetName: line.assetName || 'Equipment',
        assetType: line.assetType || 'asset', fleetNumber: line.fleetNumber || '',
        rate: line.rate || 0, rateUnit: line.rateUnit || 'Hourly',
        startTime: jbToLocalDT(fromTime), endTime: jbToLocalDT(toTime),
        notes: line.notes || '', _dirty: false, _saved: true, _restored: true, _lineId: line.id,
        // TASK-427: tag restored line with its owning session so this clock-out's
        // auto-fill knows session-1 lines belong to a DIFFERENT clockEntry.
        _clockEntryId: line.clockEntryId || null,
        // TASK-9188 — the server's EQUIPMENT dedup (ticketLineDedup
        // findMergeGroups) consolidates per-ASSET and deliberately NOT per
        // clockEntryId, keeping the line with the most hours at the INTERVAL
        // UNION of every session's window. So a surviving line's clockEntryId is
        // honest about WHO WROTE IT and can still be wrong about WHOSE HOURS
        // those are. That is intentional billing behavior (team-driving bills the
        // truck's real 9h, not 18h) and is NOT to be "fixed" here. This flag is
        // the only in-band evidence a consolidation happened, so the attribution
        // label can hedge instead of asserting hours that aren't all his.
        _dedupMerged: !!line.autoDedupMergedAt
      });
    }

    // Downtime
    for (const line of lines.filter(l => l.type === 'DOWNTIME')) {
      const fromTime = line.fromTime?.toDate?.() || (line.fromTime ? new Date(line.fromTime) : new Date());
      const toTime = line.toTime?.toDate?.() || (line.toTime ? new Date(line.toTime) : new Date());
      JobBoardState.btDowntime.push({
        startTime: jbToLocalDT(fromTime), endTime: jbToLocalDT(toTime),
        notes: line.notes || '', _saved: true, _restored: true, _lineId: line.id, _dirty: false
      });
    }

    // Standby
    for (const line of lines.filter(l => l.type === 'STANDBY')) {
      const fromTime = line.fromTime?.toDate?.() || (line.fromTime ? new Date(line.fromTime) : new Date());
      const toTime = line.toTime?.toDate?.() || (line.toTime ? new Date(line.toTime) : new Date());
      JobBoardState.btStandby.push({
        startTime: jbToLocalDT(fromTime), endTime: jbToLocalDT(toTime),
        notes: line.notes || '', _saved: true, _restored: true, _lineId: line.id, _dirty: false
      });
    }

    // Photos (clear existing to prevent dupes on re-open)
    if (ticketData.photos && ticketData.photos.length > 0) {
      const existingUrls = new Set(JobBoardState.btPhotos.filter(p => p._existing).map(p => p.url));
      for (const p of ticketData.photos) {
        if (!existingUrls.has(p.url)) {
          // TASK-1214 (COD binding) — keep the EXACT stored object (the restored
          // copy drops uploadedAt, and arrayRemove needs the stored element).
          JobBoardState.btPhotos.push({ _existing: true, url: p.url, name: p.name || 'photo', uploadedAt: p.uploadedAt || null, docType: p.docType || null, _originalPhoto: p }); // TASK-9868 docType = display only
        }
      }
    }

    // Work times
    if (ticketData.workStartTime || ticketData.workStopTime) {
      JobBoardState.btTicketWorkTimes = {
        workStartTime: ticketData.workStartTime || null,
        workStopTime: ticketData.workStopTime || null
      };
    }

    // Shift notes + ticket notes.
    // TASK-1327 — shift notes are a PER-PERSON record, so this box is NEVER
    // prefilled from the shared ticket-level `shiftNotes` field (that holds
    // whoever saved the ticket last, and the next person appended to their
    // coworker's text). Restore ONLY from THIS person's own clock entry.
    const _btEntryId = JobBoardState._btEntryId || JobBoardState._btEntry?.id || null;
    if (_btEntryId) {
      try {
        const entrySnap = await getDoc(doc(db, 'tenants', tenant, 'clockEntries', _btEntryId));
        const entryData = entrySnap.exists() ? entrySnap.data() : null;
        const ownNotes = entryData?.draftShiftNotes || entryData?.clockOutNotes || '';
        if (ownNotes) {
          setTimeout(() => {
            // TASK-9694 S4 — only into THIS entry's box: a slow read for entry A
            // must never fill job B's (always-empty) note after a switch.
            const el = document.getElementById('jb-shift-notes');
            const edId = document.getElementById('clockout-modal')?.dataset?.entryId;
            if (el && !el.value && (!edId || edId === _btEntryId)) el.value = ownNotes;
          }, 50);
        }
      } catch (err) {
        console.warn('[JobBoard] Could not restore own shift notes:', err);
      }
    }
    // TASK-1397 — invoice notes are ONE shared crew field. Record the exact
    // text this person was prefilled with as their baseline: jbSaveTicket
    // contributes only the delta against it, so the prefill can never be
    // appended back on (the double-paste) and a coworker's later line can
    // never be overwritten from this stale snapshot (the clobber).
    const _jbSharedNotes = ticketData.notes || ticketData.workPerformed || '';
    JobBoardState._invoiceNotesBaseline = _jbSharedNotes;
    if (_jbSharedNotes) {
      setTimeout(() => {
        const el = document.getElementById('jb-ticket-notes');
        if (el) el.value = _jbSharedNotes;
      }, 50);
    }

    // Custom section data restore — TASK-1204: bounded-retry restore that
    // VERIFIES every saved instance landed in the DOM (the old bare
    // setTimeout(...,50) silently skipped getElementById misses, and the next
    // Save then wholesale-wiped whatever failed to render). Records a
    // per-section report for the merge-save and breadcrumbs any degraded fill.
    if (ticketData.customSectionData) {
      _jbRestoreCustomSections(ticketId, ticketData.customSectionData);
    }
    // TASK-4594 — Chris's actual ask: when the driver comes back, the screen has
    // to tell him whether what he typed is really on the server. Silence used to
    // mean "fine" and "gone" equally.
    // TASK-4608 — one call, one banner. This used to be two calls painting two
    // boxes; the merged renderer reports rows and photos together.
    _jbShowUnsavedState(ticketId, ticketData.customSectionData);
  } catch (err) {
    console.warn('[JobBoard] Error restoring ticket data:', err);
  }
}

// ============================================
// SAVE TICKET DATA (without clocking out)
// ============================================

// ============================================
// TASK-1642 — BOUNDED WAITS ON DRIVER-FACING WRITES
// ============================================
// The Firestore Web SDK applies a write to the LOCAL cache immediately and only
// SETTLES the returned promise when the SERVER acks it. With no signal that ack
// never arrives and the promise NEVER settles — so `await updateDoc(...)` is not
// a completion signal, it is an unbounded hang. Reproduced on the driver-PIN
// clock-out (2026-08-18): Save spun for 11 minutes with no toast, no error, and
// nothing for the driver to act on.
//
// The queued write is NOT lost — it flushes the moment connectivity returns
// (measured end-to-end: the ticket landed on reconnect). So failing the save
// would be a DIFFERENT lie, and it would invite a tired driver to redo work that
// is already queued. The honest state is "not sent yet".
//
// Durability caveat that shaped the wording — 🔴 NO LONGER TRUE as of TASK-1652.
// firebase-config.js USED to build Firestore with plain getFirestore(), a MEMORY
// cache with no IndexedDB persistence, so a queued mutation lived in the tab and
// died if the page was closed or reloaded before it flushed. That is why the
// message below tells the driver to KEEP THE PAGE OPEN rather than promising
// delivery outright.
//
// TASK-1652 moved the cache to persistentLocalCache, so the mutation queue now
// survives the page dying and flushes on the next load in coverage (measured:
// offline write -> reload -> reopen -> the write landed; and it survives a
// deliberate sign-out too, since the purge defers while writes are queued).
// The "keep this page open" copy at :8348 and :10099 is therefore now an
// unnecessary constraint on the driver — it is stale, not dangerous, and it is
// left alone deliberately: that string is TASK-1642's driver-facing surface and
// rewording it is a product-voice call, not a side effect of a cache change.
// Hand it back to TASK-1642 rather than editing it here.
const JB_WRITE_ACK_MS = 5000;

// Reads need their own, tighter bound. A read is not queued — offline the SDK
// serves it from cache — but it does not serve it FAST: with the transport dead
// but navigator.onLine still true (a dead zone, the common field case), a single
// getDoc took a MEASURED 12.7s to give up and fall back to cache. That is dead
// time on the driver's Save button before the write bound below ever gets to run.
const JB_READ_MS = 2500;

// Attach a terminal handler to a write we stopped waiting on, so a later failure
// (e.g. a rules denial on flush) can never surface as an unhandled rejection.
function _jbSwallowDeferred(p, label) {
  Promise.resolve(p).then(
    () => {},
    (err) => {
      console.warn('[JobBoard] TASK-1642 deferred write settled with an error:', label, err?.code || err?.message);
      // TASK-4641 — DECIDED, not overlooked: a deferred write that later comes
      // back REFUSED still gets no toast. By the time the offline queue flushes,
      // the modal is closed and the driver is on another screen or another
      // shift; a toast there names a ticket they cannot see and cannot act on,
      // and they have already been told "No signal — saved on this phone".
      // What was actually missing is that the OFFICE never learned either, so
      // the denial now becomes a server-side breadcrumb instead of a console
      // line nobody reads. That is the part that was silent.
      if (err?.code === 'permission-denied' || err?.code === 'unauthenticated') {
        try {
          window.logError?.(err, {
            module: 'jobboard', operation: 'DEFERRED_WRITE_REFUSED',
            label, ticketId: JobBoardState._btTicketId || null,
          });
        } catch (_) { /* breadcrumb is best-effort */ }
      }
    }
  );
}

// TASK-1642 (COD UX + supply-chain) — the REAL error, in words a driver can act
// on. Known Firestore codes map to plain English with the code kept in parens
// (dispatch on the phone can still act on it); an unknown code surfaces as
// itself. `error.message` is used only when there is NO code (non-SDK errors) —
// SDK messages can echo doc paths, so they stay in window.logError, not toasts.
function jbFriendlyErr(err) {
  const code = err?.code || '';
  const map = {
    'unavailable': 'no connection to the server',
    'deadline-exceeded': 'the server took too long',
    'permission-denied': 'this login is not allowed to do that',
    'unauthenticated': 'session expired, reload the page',
    'failed-precondition': 'the app is out of date, reload the page',
    'resource-exhausted': 'too many requests, wait a minute',
  };
  if (code && map[code]) return `${map[code]} (${code})`;
  if (code) return code;
  return err?.message || 'unknown error';
}

// Reset the per-save network verdict. Called at the true entry points only
// (jbSaveTicket when not nested, submitClockOut) — never mid-flow, or a
// clock-out would forget that its own first write never acked.
function jbResetNetOutcome() {
  JobBoardState._btWriteUnacked = false;
  JobBoardState._btMediaSkipped = false;
  JobBoardState._btPhotoQueueFailed = 0;   // TASK-4602
  JobBoardState._btPhotoQueueFull = false; // TASK-4602
  JobBoardState._btNeedsSignal = [];
  JobBoardState._btCsdUnsaved = false;
  // TASK-4666 — per-save flag: "the abandon branch already stashed this save's
  // rows". submitClockOut's catch reads it to avoid double-stashing.
  JobBoardState._btCsdStashed = false;
  // TASK-4641 — driver-readable sentences for things that were REFUSED (not
  // delayed) during this save. Collected here and said ONCE at the end, so a
  // refusal can never end up sitting next to a success toast.
  JobBoardState._btPartialProblems = [];
}

// TASK-4641 — record a refusal in words a driver can act on. Deliberately does
// NOT toast: one save can produce several of these, and a phone in a dead zone
// on 4% battery gets one read. jbSaveTicket says all of them, once, at the end.
function _jbNoteRefused(sentence) {
  if (!sentence) return;
  const list = JobBoardState._btPartialProblems || (JobBoardState._btPartialProblems = []);
  if (!list.includes(sentence)) list.push(sentence);
}

// TASK-4641 — ONE lock predicate for the driver surfaces in this file. The
// section-file placement carried the full six-field test, the photo drain
// carried only exists(), and the restore carried a third variant — three copies
// is how the screen and firestore.rules ended up disagreeing about whether a
// ticket was writable. Same six fields as rules ticketLockedForBilling() and
// functions uploadTicketSectionFile(). Returns null (open) or a reason code.
//
// 🔴 'ready' and 'sent' are DELIBERATELY absent, and COD's Code Reviewer flagged
// their absence as drift. Rejected: rules do NOT lock on those two, so adding
// them here would make the photo/section-file drains REFUSE an upload the server
// would have accepted — throwing away a driver's proof photo that could have
// landed. 'ready' means the biller is about to invoice, which is exactly when
// they still want the evidence. jbRestoreTicketData layers them on top for the
// DISPLAY decision only (its form fill has always skipped them); that asymmetry
// is correct — a banner saying the form is read-only is not a claim that a
// queued photo cannot arrive.
const JB_SENT_LOCK_ACTOR = 'ticket-sent'; // TASK-10026 — mirrors services/ticketSendLog.js SENT_LOCK_ACTOR
const JB_SENT_MSG = 'This ticket was already sent — ask the office to change it.';
function _jbTicketLockReason(t) {
  if (!t) return null;
  const st = String(t.status || '').toLowerCase();
  if (t.invoiced === true || t.invoiceNumber != null || t.invoicedAt != null) return 'billed';
  if (st === 'archived') return 'archived';
  if (st === 'locked') return 'closed';
  // TASK-10026 — a ticket that went to the customer is closed to the driver.
  // The server stamps the existing driver lock with this actor on every
  // successful send (services/ticketSendLog.js sentLockPatch), so rules and
  // this predicate agree; the reason only changes what the driver is told.
  if (t.driverEditLocked === true) return t.driverEditLockedBy === JB_SENT_LOCK_ACTOR ? 'sent' : 'lockedByOffice';
  return null;
}

// navigator.onLine === false is a definite no. true is only a hint (bars but no
// data still reads as online), which is why the ack timeout below is the real
// detector and this is just the free head start.
function jbNetLooksOffline() {
  return JobBoardState._btWriteUnacked === true || navigator.onLine === false;
}

// Bound a READ. ONLY a timeout resolves undefined — a real failure
// (permission-denied, missing index, anything the SDK actually answers with)
// still REJECTS, so a correctness or rules bug keeps surfacing as itself instead
// of being relabelled "no signal". Deliberately does NOT flag the save as
// unacked: a slow read is not proof the write will not land, and flagging here
// would also skip the photo/signature uploads (which do not queue) over it.
function jbBoundedRead(promise, label) {
  const p = Promise.resolve(promise);
  return new Promise((resolve, reject) => {
    let done = false;
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      console.warn('[JobBoard] TASK-1642 read did not answer in', JB_READ_MS, 'ms:', label);
      _jbSwallowDeferred(p, label);
      resolve(undefined);
    }, JB_READ_MS);
    p.then(
      (v) => { if (done) return; done = true; clearTimeout(timer); resolve(v); },
      (e) => { if (done) return; done = true; clearTimeout(timer); reject(e); }
    );
  });
}

// Await a Firestore write, but never longer than JB_WRITE_ACK_MS.
//   server ack in time -> resolves with the write's value (unchanged behaviour)
//   real failure       -> REJECTS, so every existing catch still fires and the
//                         legacy "Error saving ticket data" path is preserved
//   no ack in time     -> flags _btWriteUnacked, stops waiting, resolves undefined.
//                         The SDK keeps the mutation queued; we just stop
//                         pretending we are still working.
// Once one write is unacked every later call short-circuits, so a save holding
// ten writes still reaches a terminal state after ONE timeout, not ten.
function jbAckOrQueue(promise, label) {
  const p = Promise.resolve(promise);
  if (jbNetLooksOffline()) {
    JobBoardState._btWriteUnacked = true;
    _jbSwallowDeferred(p, label);
    return Promise.resolve(undefined);
  }
  return new Promise((resolve, reject) => {
    let done = false;
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      JobBoardState._btWriteUnacked = true;
      console.warn('[JobBoard] TASK-1642 no server ack in', JB_WRITE_ACK_MS, 'ms — write queued offline:', label);
      _jbSwallowDeferred(p, label);
      resolve(undefined);
    }, JB_WRITE_ACK_MS);
    p.then(
      (v) => { if (done) return; done = true; clearTimeout(timer); resolve(v); },
      (e) => { if (done) return; done = true; clearTimeout(timer); reject(e); }
    );
  });
}

async function jbCreateTicketLine(ticketId, lineData) {
  const tenant = JobBoardState.tenantId;
  lineData.createdAt = Timestamp.now();
  // TASK-1642 — mint the line id CLIENT-side and setDoc, instead of addDoc.
  // addDoc's promise only settles on a server ack, so with no signal it hung
  // forever AND the caller never learned the id — which would make the next Save
  // re-create the same BILLABLE line. A locally-minted id is known immediately,
  // so the caller can always stamp _lineId and a retry updates that line instead
  // of duplicating it. Same pre-allocate pattern the TASK-490 batch already uses.
  const lineRef = doc(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'));
  await jbAckOrQueue(setDoc(lineRef, lineData), 'ticketLine:' + lineData.type);
  return lineRef.id;
}

// TASK-9646 S2 — jbRecalcTicketTotal removed. The comments it carried said
// "grandTotal is a DERIVED field the server recomputes on every ticketLine
// write" — that was FALSE until this slice (the server re-totalled only when
// dedup merged or enrichment priced a line), and its bare lineAmount sum never
// subtracted DOWNTIME or added tax. The header is now computed on the SERVER on
// every ticketLine write (onTicketLineWrite → ticketTotalsReconcile); the
// clock-out save no longer spends a read + a write on it.

// TASK-4602 — ticket photos now use the SAME durable queue DVIR photos use. They
// were the odd one out: `jbUploadPhotos` refused to start when offline and, on
// the clock-out path, `jbResetBuildTicketState()` then emptied `btPhotos` — so
// the photos were simply gone. Cloud Storage has no offline queue; this does.
//
// Scope is `tenantId::driverPersonId`, identical to pretrip's, for the reason
// TASK-4604 established: IndexedDB is per-ORIGIN and a yard tablet is shared, and
// the Firebase uid is REUSED across PIN logins so it cannot identify a driver.
function _jbPqScope() {
  const who = window.AppState?.driverPersonId || window.AppState?.user?.uid || null;
  if (!who) return null;
  return `${JobBoardState.tenantId || 'nt'}::${who}`;
}

// Stage photos into the durable queue. Never throws into the save path — a
// queue failure must not stop a driver finishing a ticket (TASK-428 shape), but
// it IS logged so a loss is countable rather than silent.
// TASK-4602 — Chris's ask: the driver must be told, ON REOPENING the ticket,
// that photos are still waiting. Index count only — never getPending() here,
// which materialises blobs; this runs on the Build Ticket open path, which is
// the perf-sensitive driver screen.
// TASK-4608 — folded into the single unsaved-work banner. Kept as a named entry
// point because the `online` listener and the post-save path both call it.
async function _jbShowPhotoQueueState(ticketId) {
  return _jbShowUnsavedState(ticketId || JobBoardState._btTicketId);
}

async function _jbQueuePhotos(ticketId, photos) {
  const scope = _jbPqScope();
  if (!scope || !ticketId) {
    console.warn('[JobBoard] cannot queue photos — no scope or ticketId');
    return 0;
  }
  let n = 0;
  for (const photo of (photos || [])) {
    if (!photo || !photo.file) continue;
    try {
      await photoQueue.enqueue({
        scope,
        ticketId,
        blob: photo.file,
        // The queue's DVIR shape wants these; a ticket photo has neither.
        pretripId: null,
        thumbBlob: null,
        // TASK-4635 — the section-file arm claims rows whose `name` starts with
        // its tag. A camera roll file literally named `tsf1:…` is far-fetched but
        // free to defuse, and the failure mode if it happened is a photo routed
        // into the wrong drain and abandoned.
        name: _jbTsfIsQueuedFile({ name: photo.name }) ? `_${photo.name}` : (photo.name || 'photo.jpg'),
        contentType: photo.file.type || 'image/jpeg',
        docType: photo.docType || null, // TASK-9868 — the driver's pick survives the queue
        uploadedBy: _jbTicketDocUploader(), // TASK-9868 COD — WHO, fixed at enqueue time
      });
      n++;
    } catch (err) {
      // MAX_QUEUE_SIZE is 20 and SHARED with DVIR photos, so a driver with a
      // full queue genuinely cannot add more — say so rather than pretend.
      console.error('[JobBoard] photo queue enqueue failed:', err?.message || err);
      // 🔴 THIS IS THE PHOTO ACTUALLY BEING LOST. The clock-out path clears
      // btPhotos straight after, so if we could not queue it, it is gone. A
      // generic "no signal" message would be a lie by omission — MAX_QUEUE_SIZE
      // is 20 and SHARED with DVIR photos, so "queue full" is a real, reachable
      // state with a real user action (upload your DVIR photos first).
      JobBoardState._btPhotoQueueFailed = (JobBoardState._btPhotoQueueFailed || 0) + 1;
      if (/queue full/i.test(String(err?.message || ''))) JobBoardState._btPhotoQueueFull = true;
      try {
        window.logError?.(err, { module: 'jobboard', operation: 'TICKET_PHOTO_ENQUEUE_FAILED', ticketId, reason: String(err?.message || err) });
      } catch (_) { /* breadcrumb is best-effort */ }
    }
  }
  return n;
}

// Drain this driver's queued TICKET photos when a connection returns. Deliberately
// only touches entries carrying a ticketId — pretrip owns its own drain, and
// draining someone else's rows is what TASK-4604 existed to stop.
async function _jbDrainPhotoQueue() {
  const scope = _jbPqScope();
  if (!scope) return;
  let pending = [];
  try { pending = await photoQueue.getPending(scope); } catch (err) {
    console.warn('[JobBoard] photo queue read failed:', err?.message || err);
    return;
  }
  // 🔴 TASK-4635 — EXCLUDE ticket-section files. This arm ends in a direct client
  // uploadBytes to root-level `tickets/{tenant}/{ticketId}/`, which is precisely
  // the write TASK-4607 denies for section attachments; letting one leak in here
  // would reopen that hole through the back door. The test is the RAW PREFIX, not
  // a successful parse, so a corrupt tag is still excluded — fail closed.
  const mine = pending.filter((x) => x && x.ticketId && x.blob && !_jbTsfIsQueuedFile(x));
  if (!mine.length) return;
  const tenant = JobBoardState.tenantId;
  let changed = 0;   // TASK-4641 — rows that ended VISIBLY, so the screen repaints
  for (const item of mine) {
    // 🔴 CLAIM FIRST. Two tabs, or a flapping connection firing `online` twice,
    // would otherwise both upload the same blob before either dequeued it —
    // duplicate photos on the ticket (COD). The claim is an IndexedDB readwrite
    // transaction and expires, so a tab that dies mid-upload does not strand it.
    if (!(await photoQueue.claim(item.id))) continue;
    // 🔴 RETRY CEILING. A ticket that was deleted, invoiced or locked before the
    // drain runs can never accept this photo — and every attempt uploaded the
    // blob to Storage BEFORE the doc write failed, leaving a fresh orphan object
    // each time. Cap it, drop it, and log it once (COD).
    if ((item.retryCount || 0) >= 5) {
      console.warn('[JobBoard] photo queue giving up on', item.id, 'after', item.retryCount, 'attempts');
      try {
        window.logError?.(new Error('TICKET_PHOTO_QUEUE_ABANDONED'), {
          module: 'jobboard', operation: 'TICKET_PHOTO_QUEUE_ABANDONED',
          ticketId: item.ticketId, entryId: item.id, retryCount: item.retryCount || 0,
        });
      } catch (_) { /* breadcrumb is best-effort */ }
      // TASK-4641 — the blob is DROPPED here. A console.warn is not telling the
      // driver anything; the same red "did NOT make it" ledger the section-file
      // arm writes is the only thing standing between them and a silent loss.
      _jbTsfRecordFailed(item.ticketId, { name: item.name || 'a photo', reason: 'it would not go through after several tries' });
      changed++;
      try { await photoQueue.dequeue(item.id); } catch (_) { /* nothing else to do */ }
      continue;
    }
    try {
      // Check the destination BEFORE spending an upload on it. Without this,
      // every retry against a deleted/locked ticket left another orphan blob in
      // Storage that nothing references and nothing reaps.
      const _dest = await getDoc(doc(db, 'tenants', tenant, 'tickets', item.ticketId));
      if (!_dest.exists()) {
        console.warn('[JobBoard] photo queue: ticket', item.ticketId, 'is gone — dropping queued photo');
        _jbTsfRecordFailed(item.ticketId, { name: item.name || 'a photo', reason: 'the ticket is gone' });
        changed++;
        await photoQueue.dequeue(item.id);
        continue;
      }
      // 🔴 TASK-4641 — this pre-flight checked exists() and NOTHING ELSE, so a
      // photo queued against a ticket the office billed/archived while the
      // driver was out of signal uploaded the blob, bounced off the doc write,
      // and did that five times before abandoning with a console.warn. Its
      // sibling _jbTsfPlacePath has carried the full six-field test with a
      // driver-readable reason the whole time — that asymmetry was the bug.
      // Same predicate, same shape of message.
      const _lock = _jbTicketLockReason(_dest.data() || {});
      if (_lock) {
        _jbTsfRecordFailed(item.ticketId, {
          name: item.name || 'a photo',
          reason: _lock === 'sent'
            ? 'this ticket was already sent before it could go up — send the photo to dispatch directly'
            : 'the office closed this ticket before it could go up — send the photo to dispatch directly',
        });
        changed++;
        try {
          window.logError?.(new Error('TICKET_PHOTO_QUEUE_LOCKED'), {
            module: 'jobboard', operation: 'TICKET_PHOTO_QUEUE_LOCKED',
            ticketId: item.ticketId, entryId: item.id, lockReason: _lock,
          });
        } catch (_) { /* breadcrumb is best-effort */ }
        await photoQueue.dequeue(item.id);
        continue;
      }
      const safeName = String(item.name || 'photo.jpg').replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `tickets/${tenant}/${item.ticketId}/${Date.now()}_${safeName}`;
      const storageRef = ref(storage, path);
      // TASK-9958 — the uploader stamp travels with the queue row; the Storage
      // object's uploadedByUid must be THIS session's uid (storage.rules pin).
      await uploadBytes(storageRef, item.blob, { contentType: item.contentType || 'image/jpeg', customMetadata: fileUploadCustomMetadata(auth.currentUser?.uid, 'driver_app') });
      const url = await getDownloadURL(storageRef);
      // TASK-9868 — the queue is scoped to this session (_jbPqScope), so the
      // signed-in person draining it is the one who queued it. Type rides on
      // the queue row; a row queued before this build has none → Other.
      const _qPhoto = { url, name: item.name || safeName, uploadedAt: new Date().toISOString(), docType: normalizeTicketDocType(item.docType) };
      // COD: prefer the stamp taken at enqueue (a PIN switch mid-drain must not
      // re-attribute); only a row queued before this build falls back to now.
      const _qUploader = (item.uploadedBy && typeof item.uploadedBy === 'object' && item.uploadedBy.uid) ? item.uploadedBy : _jbTicketDocUploader();
      if (_qUploader) _qPhoto.uploadedBy = _qUploader;
      _qPhoto.uploadedVia = 'driver_app'; // TASK-9958 — how it got here
      await updateDoc(doc(db, 'tenants', tenant, 'tickets', item.ticketId), {
        photos: arrayUnion(_qPhoto),
        photoCount: increment(1),
        // TASK-4699 — a QUEUED photo draining. This is the strongest case in the
        // whole set: the queue exists precisely because the write lands later,
        // so it can attach a photo to a ticket the office already emailed, with
        // the driver long gone and nothing on screen to notice it.
        ticketEmailDirtyAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      _jbAuditTicketPhotosAdded(item.ticketId, [{ name: _qPhoto.name, path }]); // TASK-9958
      // Dequeue ONLY after the Firestore write lands. Dequeuing on upload alone
      // would drop the photo if the doc write then failed — uploaded, unreferenced,
      // and gone from the queue.
      await photoQueue.dequeue(item.id);
    } catch (err) {
      console.warn('[JobBoard] photo queue drain failed for', item.id, err?.message || err);
      try { await photoQueue.incrementRetry(item.id); } catch (_) { /* keep it queued */ }
    }
  }
  // 🔴 TASK-4641 — REPAINT, same reason the section-file arm does. This drain
  // runs off the `online` event with nobody watching it, so a photo it gave up
  // on wrote its record and dropped the row while the screen kept saying the
  // photo was still waiting for signal. Screen lying about what is saved =
  // TASK-4594 in a new coat.
  if (changed > 0 && JobBoardState._btTicketId) {
    try { await _jbShowUnsavedState(JobBoardState._btTicketId); } catch (_) { /* banner is not worth breaking the drain */ }
  }
}

async function jbUploadPhotos(ticketId) {
  const tenant = JobBoardState.tenantId;
  const newPhotos = JobBoardState.btPhotos.filter(p => !p._existing && p.file);
  if (newPhotos.length === 0) return true;

  // TASK-1642 — Cloud Storage has NO offline queue. uploadBytes just retries
  // until maxUploadRetryTime (2 minutes PER FILE) and then throws, so with no
  // signal this is minutes of spinner for an upload that was never going to
  // land. Don't start it.
  //
  // On the SAVE path the photos stay staged on JobBoardState.btPhotos (still
  // !_existing, thumbnails still on screen) and the next Save with a connection
  // uploads them. On the CLOCK-OUT path they are gone: submitClockOut always
  // closes the modal and jbResetBuildTicketState() empties btPhotos. That is
  // PRE-EXISTING — Storage cannot queue, so an offline clock-out lost pending
  // photos either way; it just used to spend two minutes per file first. What
  // changes here is that we say so (_btMediaSkipped drives the message) instead
  // of letting the driver believe the photos went with the ticket.
  if (jbNetLooksOffline()) {
    // TASK-4602 — was: mark skipped and drop them. Now they go to the durable
    // queue, so a clock-out that clears btPhotos no longer loses them.
    JobBoardState._btMediaSkipped = true;
    console.warn('[JobBoard] TASK-1642 offline —', newPhotos.length, 'photo(s) to the durable queue');
    await _jbQueuePhotos(ticketId, newPhotos);
    return false;
  }

  const photos = [];
  const uploadedStaged = [];
  let failCount = 0;
  const _uploader = _jbTicketDocUploader(); // TASK-9868
  for (const photo of newPhotos) {
    try {
      const timestamp = Date.now();
      const safeName = photo.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `tickets/${tenant}/${ticketId}/${timestamp}_${safeName}`;
      const storageRef = ref(storage, path);
      // TASK-9402 — label from the bytes (a MIME-less PDF was stored as image/jpeg).
      await uploadBytes(storageRef, photo.file, { contentType: await uploadContentType(photo.file, 'image/jpeg'), customMetadata: fileUploadCustomMetadata(_uploader?.uid, 'driver_app') });
      const url = await getDownloadURL(storageRef);
      const photoObj = { url, name: photo.name, uploadedAt: new Date().toISOString() };
      // TASK-9868 — what it is (driver's pick, default Other) + who uploaded it.
      photoObj.docType = normalizeTicketDocType(photo.docType);
      if (_uploader) photoObj.uploadedBy = _uploader;
      photoObj.uploadedVia = 'driver_app'; // TASK-9958 — how it got here
      photos.push(photoObj);
      uploadedStaged.push({ photo, url, photoObj, path });
    } catch (err) {
      failCount++;
      console.error('[JobBoard] Photo upload error:', err);
      // TASK-4602 — a FAILED upload is as lost as a skipped one once the modal
      // closes. Queue it too, not just the offline case.
      await _jbQueuePhotos(ticketId, [photo]);
    }
  }

  if (photos.length > 0) {
    await jbAckOrQueue(updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
      photos: arrayUnion(...photos),
      photoCount: increment(photos.length),
      // TASK-4699 — photos ride along on the customer's copy. Inside the
      // length > 0 guard, so an upload pass that added nothing stamps nothing.
      ticketEmailDirtyAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    }), 'ticket:photos');
    _jbAuditTicketPhotosAdded(ticketId, uploadedStaged.map(u => ({ name: u.photoObj.name, path: u.path }))); // TASK-9958
    // TASK-1214 — mark just-uploaded photos as persisted so (a) a second Save
    // can't re-upload + duplicate them (mirrors clock.js TASK-806) and (b)
    // their ✕ routes through TicketPhotos.removeUploaded (they're in the
    // ticket doc now — a local splice would resurrect them on reopen).
    // _originalPhoto = the EXACT stored element (COD binding) — never mutate.
    for (const { photo, url, photoObj } of uploadedStaged) {
      photo._existing = true;
      photo.url = url;
      photo.uploadedAt = photoObj.uploadedAt;
      photo._originalPhoto = photoObj;
    }
    jbRefreshPhotoThumbnails();
  }

  if (failCount > 0) {
    window.showToast?.(`${failCount} photo(s) failed to upload — try again`, 'error');
  }
  return failCount === 0;
}

// jbEnsureTicketId — shared bootstrap helper used by the modal-open and
// jbSaveTicket paths. Returns the ticketId on success, null on failure.
// Side effects: stamps JobBoardState._btTicketId + _btEntry.ticketId +
// tenants/{tid}/clockEntries/{entryId}.ticketId (so dedup is consistent
// across modal-open / Save / Clock Out submission). Pulls dedup keys
// (customerId/customerName/jobId/jobTitle/workDate) from JobBoardState._bt*
// scratch fields populated at modal open.
async function jbEnsureTicketId({ surfaceToast = true } = {}) {
  const existing = JobBoardState._btTicketId;
  if (existing) return existing;

  const entry = JobBoardState._btEntry;
  const entryId = JobBoardState._btEntryId;
  const job = JobBoardState._btJob;
  const jobId = JobBoardState._btJobId;

  // TASK-1222 (Chris repro 7/16) -- the ENTRY's own customer is just as valid a
  // ticket anchor as the job's: a driver clocked into a customer WITHOUT a job
  // (customer-only clock-in, or a job the board didn't resolve) got a dead-end
  // "Select a customer on this job" toast and could never save Line Info. The
  // shared findOrCreateTicket fully supports customer + jobId:null (the ticket
  // lands in billing's unassigned pool -- same as the clock.js path).
  const _btCustomerId = job?.customerId || entry?.customerId || null;
  if (!_btCustomerId) {
    if (surfaceToast) {
      window.showToast?.('Select a customer on this job to save ticket data.', 'error');
    }
    return null;
  }

  // TASK-1642 — with no signal findOrCreateTicket cannot run its server-side
  // dedup/lock, so letting it mint a ticket off cache risks a DUPLICATE ticket
  // landing on reconnect. Refuse up front with something the driver can act on
  // instead of hanging. The normal case is untouched: the modal bootstraps the
  // ticket at open time, so an offline Save already has _btTicketId.
  if (jbNetLooksOffline()) {
    JobBoardState._btWriteUnacked = true;
    if (surfaceToast) {
      window.showToast?.('No signal — this ticket has not been started yet. Clock out and dispatch will build it.', 'warning', null, 9000);
    }
    return null;
  }

  try {
    const ticketResult = await window.ClockModule.findOrCreateTicket(
      _btCustomerId,
      getCustomerName(_btCustomerId) || entry?.customerName || '',
      jobId || null,
      job?.title || job?.name || null,
      new Date(),
      JobBoardState.tenantId,
      { source: 'clock_out' },
      // TASK-1221 — split ticket keyed on the entry's own assets + operator.
      { splitBy: job?.splitBy || 'combined', assetIds: Array.isArray(entry?.assetIds) ? entry.assetIds : [], personId: entry?.personId },
    );
    const ticketId = ticketResult.ticketId;
    if (entryId) {
      try {
        await jbAckOrQueue(updateDoc(doc(db, 'tenants', JobBoardState.tenantId, 'clockEntries', entryId), { ticketId }), 'clockEntry:ticketId');
      } catch (stampErr) {
        // TASK-10017 — firestore.rules let a driver ATTACH a ticket to an open
        // shift only while it has none. A refusal here means the shift already
        // points at a ticket this copy did not know about (the office or the
        // gateway set it): use THAT ticket, so lines never land on one ticket
        // while the shift points at another. Anything else surfaces as before.
        let serverTicketId = null;
        try {
          const snap = await getDoc(doc(db, 'tenants', JobBoardState.tenantId, 'clockEntries', entryId));
          serverTicketId = snap.exists() ? (snap.data().ticketId || null) : null;
        } catch (_) { /* fall through to the original error */ }
        if (!serverTicketId) throw stampErr;
        if (entry) entry.ticketId = serverTicketId;
        // Only the editor that asked may take the answer (COD r2): the driver
        // can close this one and open another shift while we waited.
        if (JobBoardState._btEntry === entry) JobBoardState._btTicketId = serverTicketId;
        return serverTicketId;
      }
    }
    if (entry) entry.ticketId = ticketId;
    if (JobBoardState._btEntry === entry) JobBoardState._btTicketId = ticketId;
    return ticketId;
  } catch (e) {
    console.error('[JobBoard] jbEnsureTicketId failed:', e.code, e.message);
    if (surfaceToast) {
      // TASK-1642 — name the real failure so it's actionable, not just "could not".
      window.showToast?.(`Could not start ticket — ${jbFriendlyErr(e)}. Try again or finish clock out.`, 'error');
    }
    return null;
  }
}

// _opts.nested — set ONLY by submitClockOut, which owns the network verdict for
// the whole clock-out. Every other caller (the Save button) is a fresh save and
// gets a fresh verdict. The exported/onclick signature is unchanged.
async function jbSaveTicket(_opts) {
  if (jbBlockIfReadOnlyLink('build a ticket')) return false;
  // TASK-9402 — photos / stamp still converting must join THIS save. Before the
  // button lock (settle-before-save); nested = submitClockOut, which already
  // settled before its own lock, so this is a no-op wait there.
  // COD r2 — the stamp's prep rides the SAME tracker, so ONE guarded wait
  // (button disabled, a second Save refused) covers photos and stamp alike.
  _jbTrackStampPrep();
  // COD r3 — capture WHICH ticket this Save is for before waiting: Cancel stays
  // live during the wait, and a newly opened ticket must never receive it.
  const _editor = _jbEditorIdentity();
  if (!_opts || _opts.nested !== true) {
    if (!(await settlePhotoPrepBeforeSave(_jbPhotoPrep, 'jb-btn-save-ticket'))) return false;
  } else {
    await _jbPhotoPrep.settle();
  }
  if (!_jbSameEditor(_editor)) return false; // closed / another ticket opened while converting
  if (!_opts || _opts.nested !== true) jbResetNetOutcome(); // TASK-1642
  const entry = JobBoardState._btEntry;
  // Try to create the ticket on first save if the modal-open bootstrap was
  // skipped (no customer on the job at the time, etc.). User-facing toast
  // surfaces the real reason on failure instead of the legacy silent bail.
  const ticketId = JobBoardState._btTicketId || await jbEnsureTicketId({ surfaceToast: true });
  if (!ticketId) return false;

  const btn = document.getElementById('jb-btn-save-ticket');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Saving...';
  }

  // 🔴 TASK-4641 — REFUSE BEFORE THE DRIVER SPENDS ANYTHING ELSE.
  // jbRestoreTicketData already read this ticket and found the office had closed
  // it — that is why the form came up empty. Without this, Save fires a dozen
  // writes that every one of them bounces off firestore.rules, and the only
  // feedback is a permission-denied relabelled "try Save again": advice that can
  // never work.
  //
  // NON-NESTED (Save button) ONLY. A clock-out must never be blocked by this:
  // submitClockOut has its own "Dispatch will finish the ticket" message, and
  // trapping a driver on shift over a ticket the office closed is the TASK-428
  // failure mode. That is also why this does not touch the nested return value.
  if (JobBoardState._btLockReason && (!_opts || _opts.nested !== true)) {
    const _sentLock = JobBoardState._btLockReason === 'sent'; // TASK-10026
    window.showToast?.(
      (_sentLock ? JB_SENT_MSG + ' Nothing was saved.' : 'The office has closed this ticket, so nothing can be saved to it.')
      + ' Your entries are still on this screen — do NOT re-type them. Read them to dispatch and they will add them.',
      'error', null, 15000);
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = _sentLock
        ? '<span class="material-icons" style="font-size:18px;">lock</span> Already sent'
        : '<span class="material-icons" style="font-size:18px;">lock</span> Closed by the office';
      btn.style.background = 'linear-gradient(135deg, #b45309, #92400e)';
    }
    return false;
  }

  // clock-dst-all — every typed time this save writes is read in the COMPANY
  // zone; a repeated fall-back time is asked here, a gap / unreadable zone is
  // refused — BEFORE anything is written.
  // Nested (inside submitClockOut — the punch is already written): never ask
  // again and never refuse the whole save; rows that don't resolve are skipped
  // below and reported (the entry is already flagged needsReview).
  const _nestedSave = !!(_opts && _opts.nested);
  const _tRes = await _jbResolveTicketTimes(_nestedSave ? { reusePicks: true, noPrompt: true } : undefined);
  let _skippedRowTimes = false;
  if (!_tRes.ok && _nestedSave) _skippedRowTimes = true;
  else if (!_tRes.ok) {
    window.showToast?.(_tRes.message || 'Not saved — check the times.', 'error', null, 8000);
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons" style="font-size:18px;">save</span> Save';
    }
    return false;
  }

  try {
    const tenant = JobBoardState.tenantId;

    // 1. Equipment lines
    for (const eq of JobBoardState.btEquipment) {
      if (_jbRowUnresolved(eq)) continue; // clock-dst-all — a time that can't be read is never written as a guess
      if ((eq._restored || eq._saved) && eq._dirty && eq._lineId) {
        const eqHours = jbRowHours(eq);
        await jbAckOrQueue(updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', eq._lineId), {
          fromTime: Timestamp.fromDate(_jbRowDate(eq, 'startTime')),
          toTime: Timestamp.fromDate(_jbRowDate(eq, 'endTime')),
          hours: parseFloat(eqHours.toFixed(2)),
          // TASK-9646 S2 — lineAmount is the SERVER's: stamp the line and
          // onTicketLineWrite re-derives it from the STORED hours × rate (the
          // in-memory eq.rate here could be stale — or, once rates are hidden
          // from driver sessions, simply 0).
          amountPending: true,
          // TASK-4594 — `notes` existed ONLY on the create payload below, so
          // even once `_dirty` is set (the other half of this fix) an edit to a
          // restored line's notes had nowhere to land. Setting the flag without
          // this field would have looked fixed and still dropped the text.
          notes: (eq.notes || '').trim(),
          updatedAt: Timestamp.now()
        }), 'ticketLine:EQUIPMENT:update');
        eq._dirty = false;
        continue;
      }
      if (eq._restored || eq._saved) continue;
      const eqHours = jbRowHours(eq);
      // TASK-1165 — EQUIPMENT lines are created UNPRICED (rate 0) BY DESIGN:
      // the server (onTicketLineWrite → equipmentLineEnrichment, Admin SDK)
      // resolves the real contract rate at creation and the firestore.rules
      // driver-session arm REJECTS a non-zero client-authored rate/lineAmount
      // on EQUIPMENT creates (S1b). Do NOT resolve rates client-side here —
      // a client-resolved rate is forgeable; the price book lives server-side.
      const _eqLineData = {
        type: 'EQUIPMENT',
        assetId: eq.assetId, assetName: eq.assetName,
        assetType: eq.assetType, fleetNumber: eq.fleetNumber,
        fromTime: Timestamp.fromDate(_jbRowDate(eq, 'startTime')),
        toTime: Timestamp.fromDate(_jbRowDate(eq, 'endTime')),
        hours: parseFloat(eqHours.toFixed(2)),
        rate: 0,
        lineAmount: 0,
        ratePending: true, // TASK-9646 S5 — the server prices it from the price book
        // TASK-427/442: stamp the session's clockEntryId so each session writes its
        // own equipment line with its own [from,to] window. Server dedup
        // (runServerSideDedup) then consolidates same-asset lines to ONE line at the
        // INTERVAL-UNION of those windows (no team-drive double-bill, no idle gap).
        clockEntryId: eq._clockEntryId || JobBoardState._btEntryId || null,
        notes: eq.notes?.trim() || null
      };
      // TASK-1635 — a clock-in-tied asset that wasn't on the day's jobDay bills
      // instead of vanishing; the flag routes it through billing review (same
      // single-flag convention as the server's equipmentLineEnrichment flags,
      // surfaced by billing.js's equipment review banner). The rules driver-arm
      // pins this field immutable on UPDATE, so it is stamped at CREATE.
      if (eq._offJobDay) {
        _eqLineData._billingReviewFlag = {
          reason: 'equipment_not_on_jobday',
          tiedAtClockIn: true,
          flaggedAt: Timestamp.now()
        };
      }
      const lineId = await jbCreateTicketLine(ticketId, _eqLineData);
      if (lineId) { eq._saved = true; eq._lineId = lineId; }
    }

    // 2. Downtime lines
    for (const dt of JobBoardState.btDowntime) {
      if (_jbRowUnresolved(dt)) continue; // clock-dst-all — a time that can't be read is never written as a guess
      if ((dt._restored || dt._saved) && dt._dirty && dt._lineId) {
        const dtHours = jbRowHours(dt);
        await jbAckOrQueue(updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', dt._lineId), {
          fromTime: Timestamp.fromDate(_jbRowDate(dt, 'startTime')),
          toTime: Timestamp.fromDate(_jbRowDate(dt, 'endTime')),
          hours: parseFloat(dtHours.toFixed(2)),
          notes: dt.notes || null,
          updatedAt: Timestamp.now()
        }), 'ticketLine:DOWNTIME:update');
        dt._dirty = false;
        continue;
      }
      if (dt._restored || dt._saved) continue;
      const dtHours = jbRowHours(dt);
      const lineId = await jbCreateTicketLine(ticketId, {
        type: 'DOWNTIME',
        fromTime: Timestamp.fromDate(_jbRowDate(dt, 'startTime')),
        toTime: Timestamp.fromDate(_jbRowDate(dt, 'endTime')),
        hours: parseFloat(dtHours.toFixed(2)),
        rate: 0, lineAmount: 0,
        // TASK-428 slice 2: stamp the session's clockEntryId so the firestore.rules
        // driver-write gate (driverOwnsTicketLine) binds this line to the driver's
        // own active clock entry on this job. Without it, driver-PIN DOWNTIME
        // writes are DENIED by the new ticketLines rule.
        clockEntryId: dt._clockEntryId || JobBoardState._btEntryId || null,
        notes: dt.notes || null
      });
      if (lineId) { dt._saved = true; dt._lineId = lineId; }
    }

    // 3. Standby lines
    for (const sb of JobBoardState.btStandby) {
      if (_jbRowUnresolved(sb)) continue; // clock-dst-all — a time that can't be read is never written as a guess
      if ((sb._restored || sb._saved) && sb._dirty && sb._lineId) {
        const sbHours = jbRowHours(sb);
        await jbAckOrQueue(updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', sb._lineId), {
          fromTime: Timestamp.fromDate(_jbRowDate(sb, 'startTime')),
          toTime: Timestamp.fromDate(_jbRowDate(sb, 'endTime')),
          hours: parseFloat(sbHours.toFixed(2)),
          notes: sb.notes || null,
          updatedAt: Timestamp.now()
        }), 'ticketLine:STANDBY:update');
        sb._dirty = false;
        continue;
      }
      if (sb._restored || sb._saved) continue;
      const sbHours = jbRowHours(sb);
      const lineId = await jbCreateTicketLine(ticketId, {
        type: 'STANDBY',
        fromTime: Timestamp.fromDate(_jbRowDate(sb, 'startTime')),
        toTime: Timestamp.fromDate(_jbRowDate(sb, 'endTime')),
        hours: parseFloat(sbHours.toFixed(2)),
        rate: 0, lineAmount: 0,
        // TASK-428 slice 2: stamp clockEntryId so the firestore.rules driver-write
        // gate (driverOwnsTicketLine) binds this line to the driver's own active
        // clock entry. Without it, driver-PIN STANDBY writes are DENIED.
        clockEntryId: sb._clockEntryId || JobBoardState._btEntryId || null,
        notes: sb.notes || null
      });
      if (lineId) { sb._saved = true; sb._lineId = lineId; }
    }

    // 4. Custom ticket sections (handles repeatable + single-value shapes).
    // TASK-1204: merge-save — never wholesale-replace customSectionData with
    // only what this form shows. Preserves instances a degraded restore never
    // displayed AND instances a crew-mate saved to the shared ticket meanwhile;
    // deliberate deletions of instances the driver actually saw still apply.
    await _jbMergeSaveCustomSectionData(ticketId);

    // 5. Work start/stop times
    if (jbCOFEnabled('startStopTimes')) {
      const wsVal = document.getElementById('jb-work-start')?.value;
      const weVal = document.getElementById('jb-work-stop')?.value;
      const wtUpdates = { updatedAt: Timestamp.now() };
      if (wsVal && Number.isFinite(_jbMs(wsVal, { strict: true, key: 'jb-work-start' }))) wtUpdates.workStartTime = Timestamp.fromDate(_jbDate(wsVal, 'jb-work-start'));
      if (weVal && Number.isFinite(_jbMs(weVal, { strict: true, key: 'jb-work-stop' }))) wtUpdates.workStopTime = Timestamp.fromDate(_jbDate(weVal, 'jb-work-stop'));
      if (wsVal || weVal) {
        await jbAckOrQueue(updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), wtUpdates), 'ticket:workTimes');
      }
    }

    // 6. Notes
    const shiftNotes = document.getElementById('jb-shift-notes')?.value || '';
    // TASK-4699 — THE headline case for this task: a driver amending hours or
    // notes over PIN after the office emailed the ticket. This write is
    // unconditional in jbSaveTicket, so it is the one reliable place to stamp
    // for the whole save — it covers the equipment / downtime / standby line
    // writes, the custom sections and the work start/stop times above it, all
    // of which are on the copy the customer received.
    //
    // Stamped on the Save PRESS without diffing the form, matching what the
    // office side already does in saveReviewTicket(). A deliberate Save is a
    // different thing from an incidental modal close (see autoSaveOnClose in
    // clock.js, which IS gated on a real change) — and under-flagging here is
    // the failure that costs money, since the whole point is that the customer
    // must not be left holding a copy nobody knows is stale.
    //
    // 🔴 TASK-9693 — `shiftNotes` is NOT in this payload, on purpose. The ticket
    // is ONE shared crew doc that every driver-PIN session can read
    // (firestore.rules tickets: canReadTenant), so writing a person's shift note
    // here published it to the whole crew AND let the last saver overwrite the
    // previous operator's note. Shift notes are PER OPERATOR: they live only on
    // this person's own clock entry (clockOutNotes / draftShiftNotes, written in
    // 6c below), whose read rule is own-entry + office. Do not add it back.
    const noteUpdates = { updatedAt: Timestamp.now(), ticketEmailDirtyAt: Timestamp.now() };
    // TASK-1642 — this is the exact await the 11-minute hang died on.
    await jbAckOrQueue(updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), noteUpdates), 'ticket:emailDirty');

    // 6b. TASK-1397 — invoice notes (customer facing) are SHARED across the
    // crew, so this path can no longer overwrite them from a textarea that
    // was snapshotted at modal-open: that is what gave PIN users different
    // semantics from app users on the same field and silently erased a
    // coworker's line. Both surfaces now go through THE one write path,
    // window.ClockModule.saveInvoiceNotes — reached through `window`, never
    // a ?v= import (Pitfall #1: two busters = two instances = two
    // baselines = the bug back). It re-reads and merges inside a
    // transaction, so the second writer merges instead of clobbering.
    const _jbTicketNotes = document.getElementById('jb-ticket-notes')?.value || '';
    let _jbNotesFailed = false;
    // TASK-1642 — saveInvoiceNotes runs a runTransaction, and a transaction
    // cannot be queued: with no signal it does NOT land like the plain writes
    // above do. Tracked separately so the offline message never claims the
    // customer-facing note was saved when it was not.
    let _jbNotesUnsent = false;
    const _jbSaveNotes = window.ClockModule?.saveInvoiceNotes;
    if (typeof _jbSaveNotes === 'function') {
      try {
        // TASK-10036 (b) — ask about removing a coworker's line BEFORE the
        // ack timer starts, so a person reading the dialog is never treated as
        // "offline". No asker (stale clock bundle) = keep the lines.
        const _jbAsk = window.ClockModule?.askInvoiceNoteRemovals;
        const _jbRemovalDecision = typeof _jbAsk === 'function'
          ? await _jbAsk(ticketId, JobBoardState._invoiceNotesBaseline || '', _jbTicketNotes)
          : undefined;
        const _merge = await jbAckOrQueue(_jbSaveNotes({
          tenantId: tenant,
          ticketId,
          currentText: _jbTicketNotes,
          baseline: JobBoardState._invoiceNotesBaseline || '',
          syncElId: 'jb-ticket-notes',
          stateless: true,
          surface: 'jobboard_build_ticket',
          ...(_jbRemovalDecision !== undefined ? { removalDecision: _jbRemovalDecision } : { noPrompt: true })
        }), 'invoiceNotes');
        if (_merge) JobBoardState._invoiceNotesBaseline = _merge.text;
        else if (_jbTicketNotes.trim() && JobBoardState._btWriteUnacked) _jbNotesUnsent = true;
      } catch (noteErr) {
        _jbNotesFailed = true;
        console.error('[JobBoard] Invoice-notes save failed:', noteErr);
        window.logError?.(noteErr, { module: 'jobboard', operation: 'INVOICE_NOTES_SAVE', ticketId });
        window.showToast?.('Invoice notes did not save — your text is still in the box, try Save again', 'error');
      }
    } else if (_jbTicketNotes.trim()) {
      // Cannot happen in the shipped app (app.js statically imports both
      // modules, so ClockModule exists before any interaction). If it ever
      // does, FAIL LOUD rather than fall back to the clobbering write —
      // the person's text stays in the box and nothing on the invoice is
      // destroyed.
      _jbNotesFailed = true;
      console.error('[JobBoard] window.ClockModule.saveInvoiceNotes unavailable — invoice notes NOT saved');
      window.logError?.(new Error('saveInvoiceNotes unavailable'), { module: 'jobboard', operation: 'INVOICE_NOTES_SAVE', ticketId });
      window.showToast?.('Invoice notes could not be saved — reload the page and try again', 'error');
    }
    // 6c. TASK-1327 / TASK-9693 — the shift note's ONLY home: THIS person's own
    // clock entry. Reopening Build Ticket restores from here, and the office
    // billing view reads clockOutNotes from the linked entries.
    const _entryId = entry?.id || JobBoardState._btEntryId || null;
    if (shiftNotes && _entryId) {
      await jbAckOrQueue(updateDoc(doc(db, 'tenants', tenant, 'clockEntries', _entryId), {
        clockOutNotes: shiftNotes, draftShiftNotes: shiftNotes, updatedAt: Timestamp.now()
      }), 'clockEntry:draftShiftNotes');
    }
    // 6d. TASK-9264 — materials used, on THIS person's own clock entry (a claim
    // for the office, never a stock move). Its own try/catch: a failure here
    // never fails the save or the clock-out.
    try {
      const _matPatch = _jbConsumablesUpdate();
      if (_entryId && _matPatch.consumablesReported) {
        await jbAckOrQueue(updateDoc(doc(db, 'tenants', tenant, 'clockEntries', _entryId), _matPatch), 'clockEntry:consumablesReported');
        JobBoardState._btConsumablesDirty = false;
      }
    } catch (matErr) {
      console.warn('[JobBoard] TASK-9264 materials used did not save:', matErr?.message);
      window.showToast?.('Materials used did not save — tell the office what you used', 'warning');
    }

    // 7. Photos
    await jbUploadPhotos(ticketId);

    // 7.5. Field-ticket signatures (TASK-395) — runs from both Save and Clock Out
    // (submitClockOut calls jbSaveTicket). Non-fatal on failure.
    await jbPersistSignatures(ticketId);

    // 8. Clock-in time adjustment
    if (JobBoardState._btEditedClockIn && entry) {
      const adjClockIn = JobBoardState._btEditedClockIn;
      const origClockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
      if (Math.abs(adjClockIn - origClockIn) > 30000) {
        const adjTimestamp = Timestamp.fromDate(adjClockIn);
        // TASK-1575 — snapshot BEFORE the write + before we mutate entry.clockIn.
        const _clkBefore = { ...entry };
        await jbAckOrQueue(updateDoc(doc(db, 'tenants', tenant, 'clockEntries', entry.id), {
          clockIn: adjTimestamp, adjustedClockIn: adjTimestamp
        }), 'clockEntry:clockInAdjust');
        entry.clockIn = adjTimestamp;
        // TASK-1575 — write the audit row this path never wrote (86/86 driver
        // adjustedClockIn edits had no trail). Reuse the ONE audit helper via
        // window.ClockModule (Pitfall #1 — never cross-import clock.js with ?v=);
        // the driver-session create arm is now allowed by firestore.rules. The
        // helper strips undefined + swallows its own failure, so this is
        // fire-and-forget and never blocks the save.
        window.ClockModule?.logClockEntryChange?.(
          tenant, entry.id, _clkBefore,
          { ..._clkBefore, clockIn: adjTimestamp, adjustedClockIn: adjTimestamp },
          'edit', 'driver-build-ticket-clockin-adjust'
        );
      }
    }

    // 9. Ticket total — TASK-9646 S2: computed on the SERVER from the lines
    // just written (onTicketLineWrite); no client recalc, no extra write.

    // TASK-1397 (COD 🔍 Code Reviewer) — do NOT report a flat success when
    // the customer-facing invoice notes failed to save. We deliberately do
    // not return false: jbSaveTicket gates submitClockOut, and failing a
    // whole clock-out on a note write would trap a driver on shift (the
    // TASK-428 failure mode) over a field dispatch can retype. Instead the
    // success message tells the truth, the text stays in the box, and the
    // error toast above already fired.
    // TASK-1642 — a terminal outcome, always, in seconds. Three states:
    //   queued  — a write never got a server ack. It is in this tab's offline
    //             queue and flushes on reconnect. NOT failed, NOT sent.
    //   partial — saved, but the customer-facing invoice notes did not (1397).
    //   ok      — everything acked.
    // The queued branch still returns TRUE. jbSaveTicket gates submitClockOut,
    // and a driver who cannot finish a clock-out because they have no bars is
    // the TASK-428 failure mode all over again.
    const _jbQueued = JobBoardState._btWriteUnacked === true;
    const _jbMediaSkipped = JobBoardState._btMediaSkipped === true;
    // TASK-4641 — things that were REFUSED, not delayed. Collected by
    // _jbNoteRefused from the non-fatal arms (signatures, stamp). The total
    // recalc arm is gone (TASK-9646 S2 — server-computed).
    const _jbRefused = JobBoardState._btPartialProblems || [];
    if (_jbQueued || _jbMediaSkipped || JobBoardState._btCsdUnsaved) {
      const _needsSignal = [];
      if (_jbNotesFailed || _jbNotesUnsent) _needsSignal.push('invoice notes');
      if (JobBoardState._btCsdUnsaved) _needsSignal.push('ticket sections');
      // TASK-4602 — a photo we could not even QUEUE is lost, not delayed. Say so
      // in its own words rather than letting it hide inside "no signal".
      if (JobBoardState._btPhotoQueueFailed) {
        _needsSignal.push(JobBoardState._btPhotoQueueFull
          ? `${JobBoardState._btPhotoQueueFailed} photo(s) NOT saved — this phone's offline queue is full, upload your pretrip photos first`
          : `${JobBoardState._btPhotoQueueFailed} photo(s) NOT saved — re-take them`);
      }
      if (_jbMediaSkipped) _needsSignal.push('photos/signature');
      // TASK-4641 — a refusal is NOT a signal problem, so it never joins the
      // "still needs a connection" list (retrying will not fix it). It rides
      // along as its own sentence instead. It must not be dropped just because
      // this save ALSO lost signal partway through — that double fault is how a
      // refusal goes silent.
      if (_opts && _opts.nested === true) {
        // submitClockOut says it ONCE, with the hours in it, and names what was
        // lost — hand it the list instead of stacking a second toast on a phone.
        JobBoardState._btNeedsSignal = _needsSignal;
      } else {
        // Save path: the modal stays open, so "needs a connection" is literally
        // true — the text is still in the box and the thumbnails are still there.
        window.showToast?.(
          (_jbQueued
            ? 'No signal — saved on this phone. Keep this page open and it sends when you get bars.'
            : 'No signal.')
          + (_needsSignal.length ? ' Still needs a connection: ' + _needsSignal.join(', ') + '.' : '')
          + (_jbRefused.length ? ' ' + _jbRefused.join(' ') : ''),
          'warning', null, 12000
        );
      }
    } else if (_jbNotesFailed || _jbRefused.length) {
      // 🔴 TASK-4641 — the green "Ticket data saved" used to fire here even when
      // jbPersistSignatures had just been REFUSED, because that path is
      // non-fatal and said its piece in a toast of its own. A refusal and a
      // success on one screen means the driver reads the green one. There is now
      // exactly one message, and a refusal always owns it.
      const _bits = [];
      if (_jbNotesFailed) _bits.push('The invoice notes did NOT save — tell dispatch.');
      for (const _s of _jbRefused) _bits.push(_s);
      window.showToast?.('Ticket saved, but not all of it. ' + _bits.join(' '), 'warning', null, 12000);
    } else {
      window.showToast?.('Ticket data saved', 'success');
    }
    if (btn) {
      btn.disabled = false;
      if (_jbQueued || _jbMediaSkipped || JobBoardState._btCsdUnsaved) {
        // Amber STAYS put — the toast is gone in 10s and this is the only thing
        // still telling the driver the ticket is on the phone, not on the server.
        // #fff on #b45309/#92400e clears 4.5:1 (DESIGN.md warning ramp).
        // TASK-4594 — "Saved on phone" is TRUE for a queued write (Firestore's
        // offline queue really is holding it) and FALSE for an abandoned custom
        // section, which lives only in the DOM plus the localStorage stash. Steve
        // read that label three times and believed it. Say which one happened.
        const _csdOnly = JobBoardState._btCsdUnsaved && !_jbQueued && !_jbMediaSkipped;
        btn.innerHTML = _csdOnly
          ? '<span class="material-icons" style="font-size:18px;">cloud_off</span> NOT saved — needs signal'
          : '<span class="material-icons" style="font-size:18px;">cloud_off</span> Saved on phone';
        btn.style.background = 'linear-gradient(135deg, #b45309, #92400e)';
      } else if (_jbNotesFailed || _jbRefused.length) {
        // TASK-4641 — a green ✓ beside a warning toast is the same lie in
        // miniature: the toast is gone in 12s and the button is what is left on
        // the screen. It stays amber and does not reset itself.
        btn.innerHTML = '<span class="material-icons" style="font-size:18px;">error_outline</span> Saved — not all of it';
        btn.style.background = 'linear-gradient(135deg, #b45309, #92400e)';
      } else {
        btn.innerHTML = '<span class="material-icons" style="font-size:18px;">check</span> Saved';
        btn.style.background = 'linear-gradient(135deg, #16a34a, #15803d)';
        setTimeout(() => {
          btn.innerHTML = '<span class="material-icons" style="font-size:18px;">save</span> Save';
          btn.style.background = 'linear-gradient(135deg, #3b82f6, #2563eb)';
        }, 2000);
      }
    }
    // clock-dst-all — rows skipped for an unreadable time: the caller's
    // "ticket needs attention" path runs (the punch is saved and flagged).
    if (_skippedRowTimes) return false;
    return true;
  } catch (error) {
    console.error('[JobBoard] Save ticket error:', error);
    window.logError?.(error, { module: 'jobboard', operation: 'SAVE_TICKET', ticketId });
    // TASK-1642 — surface the REAL error, not a generic string (showToast is
    // textContent-based, safe to display). The driver's entries are all still
    // in the form; naming the actual failure is what makes it actionable for
    // the dispatcher on the phone with them. jbFriendlyErr maps known codes to
    // plain words and keeps the code; full message stays in logError above.
    // 🔴 TASK-4641 — "try Save again" can NEVER work when the refusal is a lock:
    // the office closed the ticket and every retry bounces off the same rule.
    // Say what happened, say the typing is still on screen, and point at the one
    // thing that can actually fix it. No error code on a lock, either —
    // "permission-denied" means nothing to a driver in a pit.
    const _refusedLock = !!JobBoardState._btLockReason || error?.code === 'permission-denied';
    // TASK-4641 (2nd COD, integrated diff) — RECORD the refusal, do not just toast it.
    // submitClockOut calls this nested and used to discard the boolean, so a ticket
    // locked MID-SAVE landed here, showed the red toast, returned false — and then
    // clock-out still printed a green 'Ticket built — X hours' over the top of it.
    // That is the exact pairing this task exists to kill, surviving in the one window
    // the pre-flight cannot see. Noting it here closes it for EVERY caller.
    const _sentRefusal = JobBoardState._btLockReason === 'sent'; // TASK-10026
    if (_refusedLock) _jbNoteRefused(_sentRefusal
      ? 'Your ticket details did NOT save — ' + JB_SENT_MSG
      : 'Your ticket details did NOT save — the office closed this ticket. Do not re-enter them; read them to dispatch.');
    window.showToast?.(
      _refusedLock
        ? (_sentRefusal ? JB_SENT_MSG + ' Nothing was saved.' : 'The office has closed this ticket, so nothing can be saved to it.')
          + ' Your entries are still on this screen — do NOT re-type them. Read them to dispatch and they will add them.'
        : `Ticket did not save — ${jbFriendlyErr(error)}. Your entries are still on this page; try Save again.`,
      'error', null, _refusedLock ? 15000 : null);
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = _refusedLock
        ? '<span class="material-icons" style="font-size:18px;">lock</span> Closed by the office'
        : '<span class="material-icons" style="font-size:18px;">save</span> Save';
      if (_refusedLock) btn.style.background = 'linear-gradient(135deg, #b45309, #92400e)';
    }
    return false;
  }
}

// ============================================
// TIME EDIT (inline on hero header)
// ============================================

// TASK-572 — re-sync every EQUIPMENT/DOWNTIME/STANDBY entry that is still
// TRACKING the driver's clock window onto a new [in, out] window. Auto-filled
// entries (all fill paths: legacy, mid-week fallback, per-operator tied) and
// default-window manual adds carry `_followsClock: true`; any manual time edit
// (jbUpdateEquipTime / jbUpdateTimeEntry) clears the flag, so a window the
// driver deliberately customized is NEVER clobbered here. Entries already
// saved to ticketLines get `_dirty` so jbSaveTicket's update branch rewrites
// the persisted line with the shifted window. Lines restored from a previous
// modal session never carry the flag (jbRestoreTicketData doesn't set it) and
// are left alone. This closes the EMPLOYEE-vs-EQUIPMENT asymmetry where a
// clock-out time edit moved the EMPLOYEE line (submitClockOut is edit-aware)
// but the EQUIPMENT line kept its frozen modal-open window — equipment billed
// for the wrong hours.
function _jbSyncClockTrackedLines(inDate, outDate) {
  const startDT = jbToLocalDT(inDate);
  const endDT = jbToLocalDT(outDate);
  const T = _jbTLT();
  [JobBoardState.btEquipment, JobBoardState.btDowntime, JobBoardState.btStandby].forEach(list => {
    (list || []).forEach(it => {
      if (it._followsClock !== true) return;
      const same = it.startTime === startDT && it.endTime === endDT;
      // clock-dst-all — set from the KNOWN clock instants, remembered on the row.
      if (T) { T.stampRow(it, 'startTime', inDate); T.stampRow(it, 'endTime', outDate); }
      else { it.startTime = startDT; it.endTime = endDT; }
      if (same) return;
      if (it._saved || it._restored) it._dirty = true;
    });
  });
}

function jbShowTimeEdit() {
  // Defense-in-depth: same gate as the rendered button — block driver scope when toggle is off
  const isDriverScope = !!(window.AppState?.driverMode || window.AppState?.driverModePin || window.AppState?.isPublicAccess);
  if (isDriverScope && window.AppState?.settings?.allowDriverTimeEdit !== true) return;
  const display = document.getElementById('jb-clockout-times-display');
  const editor = document.getElementById('jb-clockout-times-edit');
  if (display) display.style.display = 'none';
  if (editor) editor.style.display = 'block';
}

function jbCancelTimeEdit() {
  const display = document.getElementById('jb-clockout-times-display');
  const editor = document.getElementById('jb-clockout-times-edit');
  if (display) display.style.display = 'flex';
  if (editor) editor.style.display = 'none';
}

let _jbTimeEditBusy = false;
async function jbApplyTimeEdit() {
  // Defense-in-depth: same gate as jbShowTimeEdit — block driver scope when toggle is off
  const isDriverScope = !!(window.AppState?.driverMode || window.AppState?.driverModePin || window.AppState?.isPublicAccess);
  if (isDriverScope && window.AppState?.settings?.allowDriverTimeEdit !== true) return;
  const cinVal = document.getElementById('jb-clockout-edit-cin')?.value;
  const coutVal = document.getElementById('jb-clockout-edit-cout')?.value;
  if (!cinVal || !coutVal) { window.showToast?.('Both times required', 'error'); return; }
  if (_jbTimeEditBusy) return; // a second tap while the which-time picker is open

  // clock-dst-all — read both boxes in the COMPANY zone (never the phone's); a
  // repeated fall-back time is asked (with the hours each gives), a gap time or
  // an unreadable zone is refused.
  const _T = _jbTLT();
  const _items = [
    { str: cinVal, key: 'jb-clockout-edit-cin', label: 'Clock in', optional: false },
    { str: coutVal, key: 'jb-clockout-edit-cout', label: 'Clock out', optional: false },
  ];
  let _res;
  _jbTimeEditBusy = true;
  try {
    _res = await _jbResolveTimes(_items, {
      describe: (it, ms) => {
        if (!_T) return '';
        const other = it === _items[0] ? _items[1] : _items[0];
        const r = _T.read(other.str, { key: other.key });
        if (!r.ok) return '';
        const h = (it === _items[0] ? r.ms - ms : ms - r.ms) / 3600000;
        return h > 0 ? ' — ' + _T.hm(h) + ' shift' : '';
      },
    });
  } finally { _jbTimeEditBusy = false; }
  if (!_res.ok) { window.showToast?.(_res.message || 'Pick which time you meant', 'error', null, 8000); return; }
  const newIn = new Date(_res.ms[0]);
  const newOut = new Date(_res.ms[1]);

  // Guard against iOS AM/PM flip: clock-in can never be in the future
  if (newIn > new Date()) {
    window.showToast?.('Clock-in time can\'t be in the future — check AM vs PM', 'error');
    return;
  }

  if (newOut <= newIn) {
    window.showToast?.('Clock-out must be after clock-in', 'error');
    return;
  }

  const newHours = parseFloat(((newOut - newIn) / 3600000).toFixed(2));

  JobBoardState._btEditedClockIn = newIn;
  JobBoardState._btEditedClockOut = newOut;

  // TASK-572 — shift every clock-tracking equipment/downtime/standby window to
  // the edited clock times and re-render their lists so the driver sees the
  // updated windows immediately (manually-customized windows are untouched).
  _jbSyncClockTrackedLines(newIn, newOut);
  const eqListEl = document.getElementById('jb-equipment-list');
  if (eqListEl) eqListEl.innerHTML = jbRenderEquipmentList();
  jbRefreshTimeEntryList('downtime');
  jbRefreshTimeEntryList('standby');

  // Update display
  const display = document.getElementById('jb-clockout-times-display');
  const editor = document.getElementById('jb-clockout-times-edit');
  const hoursEl = document.getElementById('jb-clockout-hours');
  if (display) {
    const inStr = _jbFmtTime(newIn);
    const outStr = _jbFmtTime(newOut);
    display.innerHTML = `
      <div style="text-align:center;cursor:pointer;" onclick="window.JobBoardModule.jbShowTimeEdit()">
        <div style="font-size:0.6rem;text-transform:uppercase;letter-spacing:1px;color:#475569;">In</div>
        <div style="font-size:1.1rem;font-weight:700;color:#34d399;border-bottom:1px dashed rgba(52,211,153,0.4);">${inStr}</div>
      </div>
      <div style="flex:0 0 auto;display:flex;flex-direction:column;align-items:center;gap:2px;">
        <div style="width:40px;height:1px;background:#334155;"></div>
        <div id="jb-clockout-hours" style="font-size:1.5rem;font-weight:800;color:#fff;line-height:1;">${newHours}</div>
        <div style="font-size:0.65rem;color:#64748b;font-weight:500;">hours</div>
        <div style="width:40px;height:1px;background:#334155;"></div>
      </div>
      <div style="text-align:center;cursor:pointer;" onclick="window.JobBoardModule.jbShowTimeEdit()">
        <div style="font-size:0.6rem;text-transform:uppercase;letter-spacing:1px;color:#475569;">Out</div>
        <div style="font-size:1.1rem;font-weight:700;color:#f87171;border-bottom:1px dashed rgba(248,113,113,0.4);">${outStr}</div>
      </div>
      <span class="material-icons" onclick="window.JobBoardModule.jbShowTimeEdit()" style="font-size:16px;color:#475569;cursor:pointer;margin-left:4px;">edit</span>
    `;
    display.style.display = 'flex';
  }
  if (editor) editor.style.display = 'none';
  if (hoursEl) hoursEl.textContent = newHours;
}

// ============================================
// TASK-1180 — FORGOTTEN-CLOCK-OUT GUARD
// ============================================

// Threshold for the "did you forget to clock out?" banner. Reuses the tenant's
// EXISTING oversizedShiftHours review threshold (clock.js
// getOversizedShiftThreshold, TASK-441) so the driver-facing banner and the
// Workforce "⚠️ Needs Review" flag fire on the SAME boundary — one review
// concept, not two. `settings.clockOutAlertHours` also exists but drives the
// reminder CRON (a nudge cadence a tenant may set short, e.g. 10h), NOT the
// "this punch is probably wrong" review concept, so it's deliberately not used
// here. Prefer the shared clock.js export; fall back to an identical local
// read when ClockModule isn't loaded — NEVER import clock.js (cache-buster
// cross-import mints duplicate module instances, CLAUDE.md Pitfall #1).
function jbOversizedShiftThreshold() {
  const shared = window.ClockModule?.getOversizedShiftThreshold;
  if (typeof shared === 'function') {
    try { return shared(); } catch (_e) { /* fall through to local read */ }
  }
  const n = Number(window.AppState?.settings?.oversizedShiftHours);
  return (Number.isFinite(n) && n >= 1 && n <= 168) ? n : 16;
}

// OUT-ONLY actual-time correction for a forgotten clock-out. Rendered in the
// clock-out modal when the open shift exceeds the review threshold AND the
// tenant does NOT allow full driver time edits (allowDriverTimeEdit=false —
// the TITS/Cassian case: the modal showed 28.69h but the honest driver had NO
// correction path and wrote the real time in his note). Distinct from
// jbApplyTimeEdit on purpose: clockIn is PRESERVED (never editable here), the
// change is constrained SHRINK-ONLY (clockIn < newOut <= now), and a reason is
// MANDATORY before the adjustment applies (FLSA contemporaneous justification
// — also the pressure-to-shave audit trail). Submitting WITHOUT adjusting is
// always allowed — the guard is advisory, never a trap (TASK-428 lesson).
let _jbForgotBusy = false;
async function jbApplyForgotClockOut() {
  const modal = document.getElementById('clockout-modal');
  if (!modal) return;
  const val = document.getElementById('jb-forgot-cout')?.value;
  const reasonEl = document.getElementById('jb-forgot-reason');
  const reason = reasonEl?.value?.trim() || '';
  if (!val) { window.showToast?.('Pick the time you actually clocked out', 'error'); return; }
  if (_jbForgotBusy) return; // a second tap while the which-time picker is open
  const clockInTime = new Date(parseInt(modal.dataset.clockInMs || '0'));
  // clock-dst-all — the typed stop time is COMPANY time; in the repeated
  // fall-back hour the driver picks which 1:30 (hours shown for each).
  const _T = _jbTLT();
  let _res;
  _jbForgotBusy = true;
  try {
    _res = await _jbResolveTimes([{ str: val, key: 'jb-forgot-cout', label: 'Clock out', optional: false }], {
      describe: (_it, ms) => {
        const h = (ms - clockInTime.getTime()) / 3600000;
        return (_T && h > 0) ? ' — ' + _T.hm(h) + ' shift' : '';
      },
    });
  } finally { _jbForgotBusy = false; }
  if (!_res.ok) { window.showToast?.(_res.message || 'Pick which time you meant', 'error', null, 8000); return; }
  if (document.getElementById('clockout-modal') !== modal) return; // closed while the picker was open
  const newOut = new Date(_res.ms[0]);
  if (isNaN(newOut.getTime())) { window.showToast?.('That time doesn\'t look right — try again', 'error'); return; }
  const now = new Date();
  if (newOut > now) {
    window.showToast?.('Clock-out can\'t be in the future — check AM vs PM', 'error');
    return;
  }
  if (newOut <= clockInTime) {
    window.showToast?.('Clock-out must be after your clock-in (' + _jbFmtDateTime(clockInTime) + ')', 'error');
    return;
  }
  if (!reason) {
    window.showToast?.('Add a quick note on why you\'re changing the time (required)', 'error');
    reasonEl?.focus();
    return;
  }

  JobBoardState._btEditedClockOut = newOut;           // submitClockOut already honors this
  JobBoardState._btDriverAdjustedClockOut = true;     // stamps the audit/review fields at submit
  JobBoardState._btDriverAdjustReason = reason;

  // Same follow-the-clock re-sync jbApplyTimeEdit does — equipment/downtime/
  // standby windows still tracking the clock follow the corrected window.
  _jbSyncClockTrackedLines(clockInTime, newOut);
  const eqListEl = document.getElementById('jb-equipment-list');
  if (eqListEl) eqListEl.innerHTML = jbRenderEquipmentList();
  jbRefreshTimeEntryList('downtime');
  jbRefreshTimeEntryList('standby');

  // Reflect the corrected Out + duration in the hero (display-only — this
  // tenant's drivers have no tap-to-edit on the times bar).
  const newHours = parseFloat(((newOut - clockInTime) / 3600000).toFixed(2));
  const hoursEl = document.getElementById('jb-clockout-hours');
  if (hoursEl) hoursEl.textContent = newHours;
  const outEl = document.getElementById('jb-clockout-out-time');
  if (outEl) outEl.textContent = _jbFmtTime(newOut);

  // Flip the banner to a green confirmation so the driver knows it took.
  const banner = document.getElementById('jb-long-shift-banner');
  if (banner) {
    banner.style.background = 'linear-gradient(135deg, #16a34a, #15803d)';
    banner.style.boxShadow = '0 4px 14px rgba(22,163,74,0.35)';
    banner.innerHTML = `
      <span class="material-icons" style="font-size:24px;color:#dcfce7;flex:0 0 auto;">check_circle</span>
      <div style="min-width:0;">
        <div style="font-size:0.95rem;font-weight:800;color:#f0fdf4;line-height:1.3;">Clock-out will be recorded as ${_jbFmtDateTime(newOut)} (${newHours} hours)</div>
        <div style="font-size:0.78rem;color:#bbf7d0;margin-top:4px;line-height:1.35;">Your adjustment and reason will be flagged for office review. Tap Clock Out below to finish.</div>
      </div>`;
  }
  const applyBtn = document.getElementById('jb-forgot-apply');
  if (applyBtn) applyBtn.textContent = 'Time updated — tap again to change';
  window.showToast?.('Clock-out time updated', 'success');
}

// ============================================
// RESET BUILD TICKET STATE
// ============================================

function jbResetBuildTicketState() {
  // TASK-1397 — the shared invoice-notes baseline is per-ticket. Reset it
  // here (not only on restore) so a ticket that has NO notes can never
  // inherit the previous ticket's baseline and "delete" its lines.
  JobBoardState._invoiceNotesBaseline = '';
  JobBoardState.btEquipment = [];
  JobBoardState.btDowntime = [];
  JobBoardState.btStandby = [];
  JobBoardState.btPhotos = [];
  JobBoardState.btTicketWorkTimes = null;
  JobBoardState._btTicketId = null;
  JobBoardState._btEquipDayMiss = null; // TASK-9579
  JobBoardState._btEntry = null;
  JobBoardState._btEntryId = null;
  JobBoardState._btJob = null;
  JobBoardState._btJobId = null;
  JobBoardState._btEditedClockIn = null;
  JobBoardState._btEditedClockOut = null;
  JobBoardState._btDriverAdjustedClockOut = false; // TASK-1180
  JobBoardState._btDriverAdjustReason = null;      // TASK-1180
  JobBoardState._btFieldTicketData = null; // TASK-395 captured signatures for re-open display
  JobBoardState._csdSnapshot = null;       // TASK-1204 customSectionData baseline at restore time
  JobBoardState._csdRestoreReport = null;  // TASK-1204 per-section restore verification report
  // TASK-1572 Slice 1 — the "No Equipment (Labor Only)" dismissal is scoped to
  // ONE clock-out. Reset here with the rest of the _bt* family, or the next
  // driver's session would inherit the previous one's answer.
  JobBoardState._btTruckAskDismissed = false;
  JobBoardState._btLockReason = null;      // TASK-4641 office lock on the open ticket
  // TASK-9264 — materials used: per ONE clock-out, like the rest of _bt*.
  JobBoardState.btConsumables = [];
  JobBoardState._btConsumablesDirty = false;
  JobBoardState._btConsumableCatalog = null;
}

async function clockInFromBoard(jobId, jobDayId, fb) {
  // Legacy driver-PIN call site. Pulls identity from AppState.driverPersonId
  // (PIN flow) and delegates to clockInFromBoardForPerson.
  // TASK-348 refactored 2026-05-22 to extract explicit personId arg so the
  // SPA personal-action wrapper (window.PersonalActions.togglePersonalClock)
  // can reuse this handler without mutating AppState.driverPersonId.
  // TASK-355 2026-05-22: also resolve to SPA member when PIN identity is unset,
  // so members tapping a job-card Clock In go through the same flow.
  return clockInFromBoardForPerson(jobId, jobDayId, {
    personId: getActiveJobBoardPersonId(),
    personName: getActiveJobBoardPersonName(),
    source: window.AppState?.driverModePin ? 'jobboard_pin' : 'jobboard_spa',
    fb: fb || null,
  });
}

async function clockInFromBoardForPerson(jobId, jobDayId, opts) {
  // Own mode unconfirmed: keep the TAP time — the wait must not start the shift late (FLSA).
  const waitTapMs = window.ClockMode?.unconfirmed?.(opts?.personId) ? Date.now() : null;
  if (await window.ClockMode?.confirmSelf?.() === null) return; // session changed while waiting
  // TASK-9576 — session tenant first (initJobBoard is itself given
  // AppState.tenant); the board's copy is null until the board has been opened.
  const tenantId = window.AppState?.tenant || JobBoardState.tenantId;
  const personId = opts?.personId || null;
  const personName = opts?.personName || '';
  const source = opts?.source || 'jobboard_pin';
  // TASK-9694 polish — the job screen's clock-in feedback + tap time (switch ON
  // only). Threaded to EVERY write below, incl. both equipment-sheet exits and
  // the truck-ask sheet, so the tap time survives the sheet.
  const fb = opts?.fb || null;

  if (!tenantId || !personId) {
    if (fb) fb.onPhase('refused');
    window.showToast?.('Unable to clock in — session error', 'error');
    return;
  }

  // Check if already clocked in (use JobBoardState subscription when available;
  // otherwise rely on the caller's pre-check against DispatchState).
  // TASK-9576 (COD Edge Case) — "available" means LIVE. A cold Personal Actions
  // close-out seeds clockEntries one-shot and nothing updates it afterwards, so
  // a non-live cache still shows the just-closed shift as active and would
  // refuse the next clock-in with "Already clocked in".
  const _boardLive = (JobBoardState.unsubscribers || []).length > 0 && JobBoardState.tenantId === tenantId;
  const existing = _boardLive
    ? (JobBoardState.clockEntries || []).find(e => e.personId === personId && e.status === 'active')
    : null;
  if (existing) {
    if (fb) fb.onPhase('refused');
    window.showToast?.('Already clocked in. Clock out first.', 'error');
    return;
  }

  const job = (JobBoardState.jobs || []).find(j => j.id === jobId);

  // TASK-491 — when per-operator equipment tracking is ON and this job's day has
  // equipment, ask the driver to confirm which assets THEY ran BEFORE writing the
  // entry, so assetIds[] is written ATOMICALLY in the same create. When OFF
  // (default) this is skipped entirely and clock-in is one-tap, exactly as today.
  if (equipmentTieEnabled() && jobId) {
    const jobDay = jobDayId
      ? (JobBoardState.jobDays || []).find(jd => jd.id === jobDayId)
      : _jbResolveJobDayForEntry({ jobId, clockIn: new Date() });
    const dayAssetIds = _jbJobDayAssetIds(jobDay);
    if (dayAssetIds.size > 0) {
      _jbShowEquipmentConfirm(jobDay, (chosenAssetIds, prov) => {
        // TASK-1547 — prov carries what the sheet rendered, whether the driver
        // touched it, and whether "No Equipment" was the path to an empty pick.
        _jbFinalizeClockIn({ tenantId, personId, personName, source, job, jobId, fb, waitTapMs, assetIds: chosenAssetIds,
          assetProvenance: _jbBuildAssetProvenance('jobboard', prov, dayAssetIds) });
      }, () => {
        // TASK-558 — dismiss/X falls back to clocking in WITHOUT equipment so
        // the tap-then-dismiss "ghost" (not clocked in) can't happen here either.
        // A clear toast tells the driver they're on the clock (no surprise) —
        // being clocked in is the safe outcome (COD Domain fold).
        window.showToast?.('Clocked in — no equipment recorded', 'info');
        // TASK-1547 — dismissed sheet: a real menu WAS rendered, nothing was
        // selected, never touched. This exact combination (renderedAssetIds
        // non-empty + selectedAssetIds [] + defaulted true) can ONLY arise
        // from a dismiss — no extra field needed to identify it.
        _jbFinalizeClockIn({ tenantId, personId, personName, source, job, jobId, fb, waitTapMs, assetIds: [],
          assetProvenance: _jbBuildAssetProvenance('jobboard', {
            renderedAssetIds: _jbBuildEquipRows(jobDay).map(r => r.id),
            selectedAssetIds: [], explicitLaborOnly: false, defaulted: true
          }, dayAssetIds) });
      }, { personId, personName });
      return;
    }
    // Job has no equipment on this day: still ON, so write an explicit empty
    // assetIds[] (keeps the day fully per-operator — the mid-week guard won't
    // trip for a legitimately equipment-free entry).
    // TASK-1547 — renderedAssetIds:[] here is the "nothing to pick" signal the
    // TASK-1539 fill keys on (rule 6): if dispatch adds equipment to the day
    // later, this entry becomes fallback-eligible instead of a forever-$0
    // labor-only ticket.
    // TASK-1572 Slice 2 — ask which truck instead of clocking in silent. Every
    // exit of the sheet clocks in; the answer rides as a provenance CLAIM.
    // COD round 3 (Code Reviewer ❌): only take the sheet path when there is a
    // fleet to offer. With nothing to pick, fall through to the awaited write
    // below — an un-awaited finalize with no modal up would release
    // _jbDetailClockInTap's double-tap guard mid-write.
    if (_jbFleetAskEnabled() && _jbBuildFleetOptions(personId).count > 0) {
      _jbShowFleetAskSheet(personId, (ask) => {
        _jbFinalizeClockIn({ tenantId, personId, personName, source, job, jobId, fb, waitTapMs, assetIds: [],
          assetProvenance: ask
            ? _jbFleetAskProvenance('jobboard', ask, dayAssetIds)
            : _jbBuildAssetProvenance('jobboard', {
              renderedAssetIds: [], selectedAssetIds: [], explicitLaborOnly: false, defaulted: true
            }, dayAssetIds) });
      });
      return;
    }
    return _jbFinalizeClockIn({ tenantId, personId, personName, source, job, jobId, fb, waitTapMs, assetIds: [],
      assetProvenance: _jbBuildAssetProvenance('jobboard', {
        renderedAssetIds: [], selectedAssetIds: [], explicitLaborOnly: false, defaulted: true
      }, dayAssetIds) });
  }

  // OFF path (default) — UNCHANGED from before TASK-491: no assetIds field.
  return _jbFinalizeClockIn({ tenantId, personId, personName, source, job, jobId, fb, waitTapMs, assetIds: null });
}

// TASK-491 — performs the actual clock-in write. assetIds === null means OFF
// (field omitted → identical to pre-TASK-491). An array (incl. []) means the
// per-operator path: written ATOMICALLY in the same addDoc that creates the entry.
// TASK-1547 — assetProvenance (built by _jbBuildAssetProvenance) rides the same
// create, only ever alongside an assetIds array; OFF path writes neither field.
// clock-double-tap — the Firestore calls window.ClockShiftGuard makes, bound to
// this module's own firebase imports (the guard imports nothing itself).
function _jbCsgDeps() {
  // stuck-shift-morning — updateDoc/Timestamp/addDoc close a stale open shift.
  return { db, doc, collection, query, where, getDocs, getDoc, runTransaction, serverTimestamp, auth, updateDoc, addDoc, Timestamp };
}

// clock-double-tap — step 1 of every board clock-in write (_jbFinalizeClockIn,
// _jbFinalizeStandaloneClockIn): drop a second tap from this tab, refuse when
// the person already has an open shift ("You're already clocked in since 7:02
// AM"), and claim the person's open-shift lock for the new entry id. Runs
// BEFORE the GPS wait, so the refusal is instant. { ok:false } = stop (the
// person was already told, or it was this tab's own second tap).
async function _jbGuardClockInStart(tenantId, personId, personName, surface, reuseEntryId) {
  const csg = window.ClockShiftGuard;
  // TASK-9694 polish — a job-screen RETRY re-uses the failed attempt's entry id,
  // so a write that did land after all is reconciled, never duplicated.
  const ref = reuseEntryId
    ? doc(db, 'tenants', tenantId, 'clockEntries', reuseEntryId)
    : doc(collection(db, 'tenants', tenantId, 'clockEntries'));
  if (!csg) return { ok: true, ref, end: () => {}, claim: null };
  const end = csg.begin(personId);
  if (!end) {
    console.warn('[ClockShiftGuard] second tap while this clock-in is still saving — ignored');
    return { ok: false };
  }
  // stuck-shift-morning — resolveStale: a shift left open on an earlier day is
  // closed with the finish time the person enters, then this clock-in proceeds.
  const claim = await csg.guardClockIn(_jbCsgDeps(), { tenantId, personId, personName, self: true, entryId: ref.id, resolveStale: true, surface: surface || 'board' });
  if (!claim.ok) {
    end();
    window.showToast?.(claim.message, 'warning', null, 9000);
    return { ok: false };
  }
  return { ok: true, ref, end, claim };
}

// clock-double-tap — step 2: write the entry under the claimed id. A refusal
// from the rules (another device clocked this person in a moment ago) is told
// in plain words and rethrown marked _userMessageShown.
async function _jbWriteGuardedEntry(gs, tenantId, personId, data) {
  try {
    // TASK-9713 — late/offline or far-off-clock clock-ins land marked for review.
    await (window.ClockShiftGuard?.writeClockIn ? window.ClockShiftGuard.writeClockIn(setDoc, gs.ref, data) : setDoc(gs.ref, data));
  } catch (err) {
    try { await gs.claim?.release?.(); } catch (_) { /* best effort */ }
    const csg = window.ClockShiftGuard;
    if (csg && csg.isDuplicateRefusal(err)) {
      const open = await csg.openShifts(_jbCsgDeps(), tenantId, personId);
      if (open && open.length) {
        console.warn('[ClockShiftGuard] clock-in refused: already clocked in', open[0].id);
        window.showToast?.(csg.alreadyInMessage(open[0], { self: true }), 'warning', null, 9000);
        err._userMessageShown = true;
      } else if (!gs.claim?.locked) {
        console.warn('[ClockShiftGuard] clock-in refused: another clock-in is still saving');
        window.showToast?.(csg.pendingMessage({ self: true }), 'warning', null, 9000);
        err._userMessageShown = true;
      }
    }
    throw err;
  }
  return gs.ref;
}

// TASK-9694 polish — the tap time to record as clockIn, or null (record now).
// Switch ON (job screen) only; switch OFF never reaches here with a tap.
function _jbTapMsFor(fb) {
  if (!fb || !Number.isFinite(fb.tapMs)) return null;
  if (!isTicketOnJobScreen()) return null;
  // Refuse an implausible device reading instead of trusting it (record now).
  if (Math.abs(Date.now() - fb.tapMs) > 24 * 60 * 60 * 1000) return null;
  return fb.tapMs;
}

// TASK-9694 polish — did a retried clock-in's entry land after all? A PIN
// session cannot read a doc that does not exist (rules), so a denial = no.
async function _jbCinEntryLanded(tenantId, entryId) {
  try {
    const snap = await getDoc(doc(db, 'tenants', tenantId, 'clockEntries', entryId));
    if (!snap.exists()) return null;
    const d = snap.data() || {};
    if (d.status !== 'active') return null;
    const ms = d.clockIn?.toMillis?.();
    // COD (Code Reviewer ❌): a cached copy / unacked local write is NOT a
    // server confirmation — report it as pending so the screen says "saved on
    // this phone", never "✓ Clocked in".
    const md = snap.metadata || {};
    return { clockInMs: Number.isFinite(ms) ? ms : null, pending: !!(md.hasPendingWrites || md.fromCache) };
  } catch (_) { return null; }
}

// TASK-9694 polish (COD Domain/Adversary) — the server time rides next to the
// tap time (clockInServerAt) and a gap over 2 minutes is FLAGGED (a wrong phone
// clock, or a clock-in queued offline and sent later). Audit only — clockIn and
// hours never move here. Best-effort; own active entry, no rules change needed.
const JB_TAP_SKEW_FLAG_MS = 2 * 60 * 1000;
async function _jbFlagClockInTapSkew(ref, tapMs) {
  try {
    if (!ref || !Number.isFinite(tapMs)) return;
    const snap = await getDoc(ref);
    const srvMs = snap.exists() ? snap.data()?.clockInServerAt?.toMillis?.() : null;
    if (!Number.isFinite(srvMs)) return;
    const gap = tapMs - srvMs;
    if (Math.abs(gap) <= JB_TAP_SKEW_FLAG_MS) return;
    await updateDoc(ref, { clockInTapSkewFlag: true, clockInTapServerGapMs: Math.round(gap) });
  } catch (err) {
    console.warn('[JobBoard] clock-in tap skew check skipped:', err?.code || err?.message || err);
  }
}

// TASK-9694 polish — the job screen's clock-in write. Same guarded write as
// every board clock-in; what differs is what the screen is told:
//   acked  — the server confirmed it → "✓ Clocked in at h:mm"
//   queued — no ack yet, but the write sits in this phone's queue (it shows in
//            the local snapshot) → "Saved on this phone — will send when…"
//   saving — no ack and not visible locally → "Still saving…" (no claim)
//   failed — a real error → Retry on the SAME entry id (never a duplicate)
async function _jbWriteClockInWithFeedback(fb, gs, tenantId, personId, data, clockInMs, tapRecorded) {
  const ref = gs.ref;
  const p = _jbWriteGuardedEntry(gs, tenantId, personId, data);
  // TASK-9190 — this device stamped this clockIn (bound to the stored value).
  _jbMarkDeviceStampedClockIn(ref.id, clockInMs);
  let settled = false;
  const done = p.then(() => {
    settled = true;
    fb.onPhase('acked', { entryId: ref.id, clockInMs });
    window.showToast?.(`Clocked in at ${_jbJsTimeStr(clockInMs)}`, 'success');
    window._refreshPersonalClockLabel?.();
    if (tapRecorded) _jbFlagClockInTapSkew(ref, clockInMs);
  }, (err) => {
    settled = true;
    if (err && err._userMessageShown) { fb.onPhase('refused'); window._refreshPersonalClockLabel?.(); return; }
    console.error('[JobBoard] Clock in error:', err?.code || err?.message || err);
    window.showToast?.('Error clocking in. Please try again.', 'error');
    fb.onPhase('failed', { entryId: ref.id });
  });
  const first = await Promise.race([done.then(() => 'settled'), new Promise(r => setTimeout(() => r('slow'), JB_WRITE_ACK_MS))]);
  if (first === 'slow' && !settled) {
    const local = (JobBoardState.clockEntries || []).some(e => e.id === ref.id);
    fb.onPhase(local ? 'queued' : 'saving', { entryId: ref.id, clockInMs });
  }
}

async function _jbFinalizeClockIn({ tenantId, personId, personName, source, job, jobId, assetIds, assetProvenance, fb, waitTapMs }) {
  // TASK-9694 polish — `fb` is the job screen's clock-in feedback (switch ON
  // only; every other caller passes none and runs exactly as before). It carries
  // the TAP time, captured before the guard read and the GPS wait.
  const _fb = (fb && typeof fb.onPhase === 'function') ? fb : null;
  if (_fb && _fb.entryId) {
    // Retry of an attempt whose write failed: if that entry landed after all,
    // reconcile to it — never a second entry.
    const landed = await _jbCinEntryLanded(tenantId, _fb.entryId);
    if (landed) { _fb.onPhase(landed.pending ? 'queued' : 'acked', { entryId: _fb.entryId, clockInMs: landed.clockInMs }); return; }
  }
  // clock-double-tap — one open shift per person; before the GPS wait.
  const _gs = await _jbGuardClockInStart(tenantId, personId, personName, undefined, _fb ? _fb.entryId : undefined);
  if (!_gs.ok) { if (_fb) _fb.onPhase('refused'); window._refreshPersonalClockLabel?.(); return; }
  if (_fb) _fb.onPhase('gps', { entryId: _gs.ref.id });
  // Best-effort GPS capture — never blocks clock-in on failure (rural/denied/timeout)
  const loc = await captureClockLocation();
  // TASK-191 Phase 3 — geofence distance flag (audit only, NEVER blocks). {} when
  // no coords / no fence / any error → field omitted, clock-in proceeds normally.
  const geo = _jbComputeGeofenceFields(loc, job, false, 'in');

  try {
    // TASK-9190 — one Timestamp for the payload AND the same-device marker, so
    // the marker is bound to this exact clockIn value and any later edit voids it.
    // TASK-9694 polish (FLSA, COD Domain) — on the job screen the TAP time is
    // the clockIn: a slow GPS / guard read must not start the shift late.
    const _tapMs = _jbTapMsFor(_fb) ?? (Number.isFinite(waitTapMs) ? waitTapMs : null);
    const _cin = _tapMs != null ? Timestamp.fromMillis(_tapMs) : Timestamp.now();
    const _entryData = {
      personId,
      personName,
      clockIn: _cin,
      clockOut: null,
      status: 'active',
      jobId: jobId || null,
      jobTitle: job?.title || job?.name || '',
      customerId: job?.customerId || null,
      customerName: (typeof getCustomerName === 'function' ? getCustomerName(job?.customerId) : null) || null,
      source,
      notes: null,
      ...(Array.isArray(assetIds) ? { assetIds } : {}),
      ...(Array.isArray(assetIds) && assetProvenance ? { assetProvenance } : {}),
      ...(loc ? { clockInLat: loc.lat, clockInLng: loc.lng, clockInAccuracy: loc.accuracy } : {}),
      ...geo,
      ...(_tapMs != null ? { clockInServerAt: serverTimestamp() } : {}),
      createdAt: Timestamp.now()
    };
    if (_fb) {
      await _jbWriteClockInWithFeedback(_fb, _gs, tenantId, personId, _entryData, _cin.toMillis(), _tapMs != null);
      return;
    }
    const _newEntryRef = await _jbWriteGuardedEntry(_gs, tenantId, personId, _entryData);
    // TASK-9190 — remember that THIS device stamped this entry's clockIn.
    _jbMarkDeviceStampedClockIn(_newEntryRef?.id, _cin.toMillis());

    const now = new Date();
    const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    window.showToast?.(`Clocked in at ${timeStr}`, 'success');
    // fix-1999-followups — the SPA Personal Actions tile label re-reads this
    // person's open shift (it has no live clock state off the Dispatch view).
    window._refreshPersonalClockLabel?.();
  } catch (err) {
    if (_fb) _fb.onPhase(err && err._userMessageShown ? 'refused' : 'failed', { entryId: _gs.ref.id });
    if (err && err._userMessageShown) { window._refreshPersonalClockLabel?.(); return; }
    console.error('[JobBoard] Clock in error:', err);
    window.showToast?.('Error clocking in. Please try again.', 'error');
  } finally {
    _gs.end();
  }
}

// TASK-348 — SPA personal-action wrapper. Validates the entry belongs to the
// caller's personId, then delegates to clockOutFromBoard. (The build-ticket
// modal opens the same way; only the friendly first-name display inside
// clockOutFromBoard pulls from AppState.driverPersonName -- it falls back to
// entry.personName which is correct for the SPA case too.)
async function clockOutFromBoardForPerson(entryId, jobId, jobDayId, opts) {
  const personId = opts?.personId || null;
  if (!personId) {
    window.showToast?.('Identity not resolved.', 'error');
    return;
  }
  // TASK-9576 — the tenant comes from the SESSION, not from the board. This is
  // reached from Personal Actions on any view; JobBoardState.tenantId is only
  // set by initJobBoard (or a caller's _hydrateState), so on a session that never
  // opened the Job Board it was null and doc(db,'tenants',null,…) threw.
  const tenantId = window.AppState?.tenant || JobBoardState.tenantId || null;
  const ctxState = _jbCloseOutContextState(tenantId, entryId);
  // Verify the entry belongs to this person (Edge Case 7-SINS guard from COD).
  // Only a LIVE board's cache is current: a cold one is not this session's
  // data, and a 'known' (one-shot hydrated) one is never updated after its
  // snapshot — the shift may already be closed (COD Edge Case, TASK-9576).
  let entry = ctxState === 'live' ? (JobBoardState.clockEntries || []).find(e => e.id === entryId) : null;
  // COD Adversary hardening (2026-08-19): when the entry is NOT in the cache,
  // clockOutFromBoard's Firestore-fallback fetch used to run with NO ownership
  // check at all — the mismatch guard above only ever saw cache hits. Not an
  // exploit (firestore.rules isOwnClockEntry pins pre- AND post-image personId
  // to the caller on the actual write), but defense-in-depth belongs in the
  // same layer as the guard it completes: fetch here, verify, and seed the
  // cache so the downstream lookup hits.
  if (!entry && tenantId) {
    try {
      const snap = await getDoc(doc(db, 'tenants', tenantId, 'clockEntries', entryId));
      if (snap.exists()) entry = { id: snap.id, ...snap.data() };
    } catch (e) {
      console.warn('[JobBoard] clockOutFromBoardForPerson: fallback fetch failed', e.message);
    }
  }
  // COD round 2 — the read above is async: a session that moved to another
  // tenant meanwhile must not get this entry planted in its board state.
  if (tenantId && window.AppState?.tenant && window.AppState.tenant !== tenantId) return;
  if (!entry) {
    console.error('[JobBoard] Clock entry NOT FOUND anywhere. ID:', entryId);
    window.showToast?.('Clock entry not found. Please refresh.', 'error');
    return;
  }
  if (entry.personId && entry.personId !== personId) {
    console.warn('[JobBoard] clockOutFromBoardForPerson: entry personId mismatch', entry.personId, personId);
    window.showToast?.('Clock entry does not belong to you.', 'error');
    return;
  }
  // A shift that is already closed must not be closed again — submitClockOut
  // would stamp a second clockOut on it. Reachable when the caller's own state
  // is a stale one-shot (app.js togglePersonalClock off the Dispatch view).
  // (Missing status alone is not refused — a legacy entry must never trap a
  // person on the clock, TASK-428 — but a stamped clockOut is.)
  if ((entry.status && entry.status !== 'active') || entry.clockOut) {
    console.warn('[JobBoard] clockOutFromBoardForPerson: entry is not active', entryId, entry.status);
    window.showToast?.('That shift is already clocked out.', 'info');
    return;
  }
  // Ownership is proven BEFORE any board state is replaced.
  if (ctxState === 'cold') {
    const ok = await _jbHydrateCloseOutContext(tenantId, entry);
    if (!ok) return;
  } else {
    // Fresh copy in place of any cached one, so clockOutFromBoard reads it.
    JobBoardState.clockEntries = [...(JobBoardState.clockEntries || []).filter(e => e && e.id !== entry.id), entry];
  }
  // COD round 2 (Blast) — the entry just read is the authority on its job; a
  // caller's copy can be stale (repointed shift). Caller's values only fill gaps.
  return clockOutFromBoard(entryId, entry.jobId || jobId || '', entry.jobDayId || jobDayId || '', { shiftChosen: !!opts?.shiftChosen });
}

// clock-double-tap — a person with more than one open shift is never silently
// closed out of the one a caller happened to pass (the newest, or the first the
// snapshot listed): after a double tap the newest is usually the ACCIDENTAL one.
// Reads the person's open shifts fresh, and when there are two or more shows
// each (start time + job) and lets the person pick. Returns the chosen entry
// id, the passed id when there is nothing to choose, or null on Cancel.
async function _jbChooseShiftToClose(entry, tenantId) {
  const csg = window.ClockShiftGuard;
  if (!csg || !entry || !entry.personId) return entry ? entry.id : null;
  // A LIVE board already holds this person's open shifts (its subscription is
  // personId + status 'active'): use it, no extra read. Otherwise read fresh
  // (bounded by the guard's own timeout).
  const live = (JobBoardState.unsubscribers || []).length > 0 && JobBoardState.tenantId === tenantId;
  let open = live
    ? csg.sortNewestFirst((JobBoardState.clockEntries || []).filter(e => e && e.personId === entry.personId && csg.isOpen(e)))
    : await csg.openShifts(_jbCsgDeps(), tenantId, entry.personId);
  if (!open) {
    // Read failed: fall back to what this board holds (never trap the person).
    open = csg.sortNewestFirst((JobBoardState.clockEntries || []).filter(e => e && e.personId === entry.personId && csg.isOpen(e)));
  }
  if (open.length <= 1) return entry.id;
  console.warn('[JobBoard] more than one open shift — asking which to close:', open.map(e => e.id));
  const picked = await csg.pickShiftToClose(open, { self: true });
  if (!picked) return null;
  // Keep the board's copy current so clockOutFromBoard's lookup hits.
  JobBoardState.clockEntries = [...(JobBoardState.clockEntries || []).filter(e => e && e.id !== picked.id), picked];
  return picked.id;
}

// TASK-9576 — can the board's close-out run on the board's own state?
// clockOutFromBoard and everything under it (jbEnsureTicketId, jbAutoFillEquipment,
// submitClockOut, the in-sheet "Fix it" and "Switch job" pickers) read
// JobBoardState: tenantId, jobs, jobDays, trucks, trailers, assets, people,
// customers, clockEntries. Only initJobBoard's live subscriptions fill those.
//   'live'  — the board is subscribed for this tenant: use it.
//   'known' — not subscribed, but this tenant's state already carries the entry
//             (a caller hydrated it — clock.js _delegateDriverClockOutToBoard,
//             dispatch.js openDriverClockOut — or the board was open earlier).
//             Unchanged from before TASK-9576.
//   'cold'  — never loaded, another tenant's, or the entry is unknown to it:
//             _jbHydrateCloseOutContext loads it first.
function _jbCloseOutContextState(tenantId, entryId) {
  if (!tenantId) return 'cold';
  if (JobBoardState.tenantId !== tenantId) return 'cold';
  if ((JobBoardState.unsubscribers || []).length > 0) return 'live';
  if ((JobBoardState.clockEntries || []).some(e => e && e.id === entryId)) return 'known';
  return 'cold';
}

// TASK-9576 — one-shot load of what the close-out reads, for a session that has
// not opened the Job Board. The same remedy TASK-1440 gave dispatch.js's Record
// Expense on the same Personal Actions route: getDocs, NOT subscribeToData() —
// that would leave nine live listeners running off-view and fire renderJobBoard.
// The queries mirror subscribeToData's admin path (the one a signed-in SPA user
// gets): jobs on BOTH window arms with finished jobs dropped from the endDate
// arm, jobDays on the date window, full fleet / people / customers. The entry's
// own job and job days are read by id as well, so a shift on a job outside the
// window still auto-fills its equipment (an equipment line that never lands is
// how a ticket comes out at $0 — TASK-1417).
// Every read is best-effort: a denied or failed collection degrades that part
// of the sheet; it never stops a person clocking out (TASK-428).
async function _jbHydrateCloseOutContext(tenantId, entry) {
  const settle = async (label, fn) => {
    try { return await fn(); } catch (e) {
      console.warn(`[JobBoard] TASK-9576 cold close-out: ${label} read failed (continuing):`, e?.message);
      return null;
    }
  };
  const _raw = Number(JobBoardState.adminWindowDaysBack);
  const _daysBack = Number.isFinite(_raw) ? _raw : 90;
  // "All" (<= 0) is bounded to 10 years: this is a one-shot read on a phone, and
  // an unbounded jobs read is what TASK-424 removed (TASK-1440 made the same call).
  const _days = _daysBack <= 0 ? 3650 : _daysBack;
  const _now = new Date();
  const _todayStartLocal = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate());
  const cut = Timestamp.fromDate(new Date(_todayStartLocal.getTime() - _days * 86400000));
  const col = (name) => collection(db, 'tenants', tenantId, name);
  const jobId = entry?.jobId || null;

  const [recentSnap, activeSnap, jobSnap, daysSnap, entryDaysSnap, trucksSnap, trailersSnap, assetsSnap, peopleSnap, custSnap] = await Promise.all([
    settle('jobs (startDate window)', () => getDocs(query(col('jobs'), where('startDate', '>=', cut)))),
    settle('jobs (endDate window)', () => getDocs(query(col('jobs'), where('endDate', '>=', cut)))),
    jobId ? settle('entry job', () => getDoc(doc(db, 'tenants', tenantId, 'jobs', jobId))) : null,
    settle('jobDays (date window)', () => getDocs(query(col('jobDays'), where('date', '>=', cut)))),
    jobId ? settle('entry jobDays', () => getDocs(query(col('jobDays'), where('jobId', '==', jobId)))) : null,
    settle('trucks', () => getDocs(col('trucks'))),
    settle('trailers', () => getDocs(col('trailers'))),
    settle('assets', () => getDocs(col('assets'))),
    settle('people', () => getDocs(col('people'))),
    settle('customers', () => _jbCustomerSource().getAll(tenantId)), // TASK-9694 S6a — directory (name + flags)
  ]);

  // COD Code Reviewer — the reads above are async. If the session moved to
  // another tenant meanwhile, publishing would plant this tenant's data under
  // it: stop. If the Job Board went live for this tenant meanwhile, its
  // subscriptions own the state now: leave it alone and use it.
  if ((window.AppState?.tenant || tenantId) !== tenantId) {
    console.warn('[JobBoard] TASK-9576 cold close-out: tenant changed during load — aborting');
    return false;
  }
  if ((JobBoardState.unsubscribers || []).length > 0 && JobBoardState.tenantId === tenantId) {
    if (!(JobBoardState.clockEntries || []).some(e => e && e.id === entry.id)) {
      JobBoardState.clockEntries = [...(JobBoardState.clockEntries || []), entry];
    }
    return true;
  }

  // Another tenant's leftovers must never stand in for a failed read; this
  // tenant's own earlier state may (it is only older, not wrong).
  const tenantChanged = JobBoardState.tenantId !== tenantId;
  const docsOf = (snap) => snap.docs.map(d => ({ id: d.id, ...d.data() }));
  const pick = (snap, current) => (snap ? docsOf(snap) : (tenantChanged ? [] : (current || [])));

  const jobsById = new Map();
  if (!tenantChanged && !recentSnap && !activeSnap) {
    (JobBoardState.jobs || []).forEach(j => j && jobsById.set(j.id, j));
  }
  if (recentSnap) recentSnap.docs.forEach(d => jobsById.set(d.id, normalizeJobDoc(d.id, d.data())));
  if (activeSnap) activeSnap.docs.forEach(d => {
    const data = d.data();
    if (data.status === 'finished') return;
    if (!jobsById.has(d.id)) jobsById.set(d.id, normalizeJobDoc(d.id, data));
  });
  if (jobSnap && jobSnap.exists()) jobsById.set(jobSnap.id, normalizeJobDoc(jobSnap.id, jobSnap.data()));

  const daysById = new Map();
  pick(daysSnap, tenantChanged ? [] : JobBoardState.jobDays).forEach(jd => jd && daysById.set(jd.id, jd));
  // The by-jobId read covers a shift whose day falls outside the window. Only
  // the day(s) ON the shift's clock-in date are merged — the same rule the
  // auto-fill resolves by (_jbResolveJobDayForEntry: exact tenant day or
  // nothing). v1.15.2000 COD 🔴: this used to merge the WHOLE unbounded read
  // when the window had no day for the job, so the fill fell back to the job's
  // oldest-ever day and billed its truck. No day on that date → nothing merged,
  // nothing auto-filled, the driver picks.
  if (entryDaysSnap) {
    const _probe = { jobId, clockIn: entry?.clockIn, jobDayId: entry?.jobDayId || null };
    docsOf(entryDaysSnap).forEach(jd => {
      if (jd && !daysById.has(jd.id) && _jbPickJobDayForEntry([jd], _probe)) daysById.set(jd.id, jd);
    });
  }
  // Date ascending, the order the board's own jobDays query delivers.
  const _jdMs = (jd) => {
    const d = jd?.date?.toDate ? jd.date.toDate() : (jd?.date ? new Date(jd.date) : null);
    const ms = d ? d.getTime() : NaN;
    return Number.isFinite(ms) ? ms : Infinity;
  };
  const sortedDays = [...daysById.values()].sort((a, b) => _jdMs(a) - _jdMs(b));

  if (tenantChanged) {
    // Same per-tenant caches initJobBoard clears on (re)init.
    JobBoardState.contractSigCache = {};
    JobBoardState.jobDaySigReadTime = {}; // TASK-10190
    JobBoardState.jobDaySigReadState = {};
    JobBoardState.projectsCache = {};
    JobBoardState._calMonthCache = {};
    JobBoardState._calMonthFetching = {};
    JobBoardState._calMonthError = {};
  }
  JobBoardState.tenantId = tenantId;
  JobBoardState.jobs = [...jobsById.values()];
  JobBoardState.jobDays = sortedDays;
  // P0 1019 — a refused read keeps the old list only when it is THIS tenant's.
  const _prior = (arr) => (tenantChanged ? [] : arr);
  JobBoardState.trucks = pick(trucksSnap, _prior(JobBoardState.trucks));
  JobBoardState.trailers = pick(trailersSnap, _prior(JobBoardState.trailers));
  JobBoardState.assets = pick(assetsSnap, _prior(JobBoardState.assets));
  JobBoardState.people = pick(peopleSnap, _prior(JobBoardState.people));
  window.ClockMode?.notePeople?.(JobBoardState.people); // TASK-9553
  JobBoardState.customers = Array.isArray(custSnap) ? custSnap : (tenantChanged ? [] : (JobBoardState.customers || [])); // TASK-9694 S6a — getAll returns a list
  JobBoardState.clockEntries = [entry];
  console.log('[JobBoard] TASK-9576 cold close-out context loaded:', {
    jobs: JobBoardState.jobs.length, jobDays: JobBoardState.jobDays.length,
    trucks: JobBoardState.trucks.length, trailers: JobBoardState.trailers.length,
    customers: JobBoardState.customers.length,
  });
  // COD Domain — a failed equipment read must not pass silently: the sheet
  // would open with no truck and the ticket could go out under-billed
  // (TASK-1417). Say so; never block the clock-out.
  JobBoardState._closeOutDaysUnknownFor = (jobId && (!daysSnap || !entryDaysSnap)) ? (entry?.id || null) : null; // TASK-9579
  if (jobId && (!jobSnap || !daysSnap || !entryDaysSnap || !trucksSnap || !trailersSnap)) {
    window.showToast?.('Couldn\'t load this job\'s equipment — check the trucks on this ticket before you submit.', 'warning');
  }
  return true;
}

async function clockOutFromBoard(entryId, jobId, jobDayId, opts) {
  if (await window.ClockMode?.confirmSelf?.() === null) return; // session changed while waiting
  console.log('[JobBoard] clockOutFromBoard:', { entryId, jobId, jobDayId, entriesCount: JobBoardState.clockEntries.length });
  // clock-double-tap — two open shifts: ask which one to close, unless the
  // caller (Clock tab, Personal Actions, the driver clock button) already asked.
  if (!opts?.shiftChosen) {
    const _cur = (JobBoardState.clockEntries || []).find(e => e.id === entryId) || null;
    if (_cur && _cur.personId) {
      const _pickedId = await _jbChooseShiftToClose(_cur, window.AppState?.tenant || JobBoardState.tenantId);
      if (!_pickedId) return; // Cancel — nothing closed
      if (_pickedId !== entryId) {
        const _p = (JobBoardState.clockEntries || []).find(e => e.id === _pickedId);
        entryId = _pickedId;
        jobId = _p?.jobId || '';
        jobDayId = _p?.jobDayId || '';
      }
    }
  }
  let entry = JobBoardState.clockEntries.find(e => e.id === entryId);
  if (!entry) {
    // Fallback: fetch directly from Firestore if subscription is stale
    console.warn('[JobBoard] Clock entry not in local state, fetching from Firestore...');
    try {
      const entryDoc = await getDoc(doc(db, 'tenants', JobBoardState.tenantId, 'clockEntries', entryId));
      if (entryDoc.exists()) {
        entry = { id: entryDoc.id, ...entryDoc.data() };
        console.log('[JobBoard] Fetched entry from Firestore:', entry.id);
      }
    } catch (fetchErr) {
      console.error('[JobBoard] Failed to fetch clock entry:', fetchErr);
    }
  }
  if (!entry) {
    console.error('[JobBoard] Clock entry NOT FOUND anywhere. ID:', entryId);
    window.showToast?.('Clock entry not found. Please refresh.', 'error');
    return;
  }

  return renderTicketEditor(opts?.inlineHost || null, {
    entry, clockEntryId: entryId, ticketId: entry.ticketId || null, jobId, jobDayId,
    expectState: opts?.expectState || null,
  });
}

// TASK-9694 S1 — ONE ticket editor, mountable anywhere. `root` = null builds the
// classic full-screen #clockout-modal exactly as before (switch OFF, every
// tenant today); `root` = an element mounts the same editor INLINE inside it
// (the job screen). Context is explicit — the entry, its ticket, its job and
// day come in `ctx`, never from whatever modal happens to be open. Both mounts
// share every builder, Save (jbSaveTicket), Clock Out (submitClockOut) and the
// _bt* state, which is why only ONE editor may exist at a time: mounting either
// kind removes the other first.
let _jbEditorMountSeq = 0;
async function renderTicketEditor(root, ctx) {
  const entry = ctx.entry;
  const entryId = ctx.clockEntryId;
  const jobId = ctx.jobId;
  const jobDayId = ctx.jobDayId;
  const _btInline = !!root;
  const _btSeq = ++_jbEditorMountSeq;
  // A classic open while the job screen holds the inline editor: close the job
  // screen first (it owns that editor) so the two never coexist. No-op when no
  // inline editor is mounted, i.e. for every tenant with the switch OFF.
  if (!_btInline) {
    const _prevEd = document.getElementById('clockout-modal');
    if (_prevEd && _prevEd.dataset.btInline === '1') closeJobDetails();
  }

  const _ticketMode = isTicketMode();
  // Captured before the ticket is stamped below: an unconfirmed-mode open keeps the ticket at submit.
  const _cmTicket = _ticketMode && window.ClockMode?.unconfirmed?.(getActiveJobBoardPersonId());
  const _ticketOnly = isTicketOnlyMode();
  const job = JobBoardState.jobs.find(j => j.id === jobId);
  const clockInTime = entry.clockIn?.toDate ? entry.clockIn.toDate() : new Date(entry.clockIn);
  const now = new Date();
  const hours = parseFloat(((now - clockInTime) / 3600000).toFixed(2));
  const clockInStr = _jbFmtTime(clockInTime);
  const clockOutStr = _jbFmtTime(now);
  // clock-dst-all — company-zone wall clock; the key ties a stored repeated-hour
  // instant to its box so an unchanged box saves the same instant.
  const cinDT = jbToLocalDT(clockInTime, 'jb-clockout-edit-cin');
  const coutDT = jbToLocalDT(now, 'jb-clockout-edit-cout');
  jbToLocalDT(now, 'jb-forgot-cout');
  // TASK-355 2026-05-22: PIN -> SPA member name; entry.personName is authoritative once a clock entry exists.
  const firstName = (getActiveJobBoardPersonName() || entry.personName || '').split(/\s+/)[0];

  // Reset and populate Build Ticket state
  jbResetBuildTicketState();
  JobBoardState._btEntry = entry;
  JobBoardState._btEntryId = entryId;
  JobBoardState._btJob = job;
  JobBoardState._btJobId = jobId;

  // Find-or-restore the ticket FIRST, so we know whether saved lines already
  // cover the equipment. Auto-fill below only runs for the bootstrap case
  // (no saved equipment yet) to avoid stacking the same trucks/trailers from
  // both the JobDay roster AND the previously-saved ticketLines.
  if (entry.ticketId) {
    JobBoardState._btTicketId = entry.ticketId;
    await jbRestoreTicketData(entry.ticketId);
  } else if (_ticketMode) {
    const ensured = await jbEnsureTicketId({ surfaceToast: false });
    if (ensured) {
      await jbRestoreTicketData(ensured);
    }
  } else {
    console.log('[JobBoard] Skipped ticket creation:', { ticketMode: _ticketMode, hasCustomer: !!job?.customerId, jobId });
  }
  // TASK-10190 — a day with no Field Ticket Settings stamp gets them at read
  // time (signature / stamp / email-copy sections below). Bounded: a slow call
  // never holds the ticket open; a late answer is cached for the next open.
  await Promise.race([_jbEnsureJobDayFts(jobDayId), new Promise(r => setTimeout(r, 4000))]);
  // TASK-9694 — inline only: the job screen was closed, or a newer mount
  // started, while the ticket restored. Mount nothing (the newer mount owns the
  // _bt* state now; a closed screen already reset it).
  if (_btInline && (_btSeq !== _jbEditorMountSeq || !root.isConnected)) return null;
  // COD (Code Reviewer P1): the shift may have ended (or moved) while the ticket
  // restored — the screen has already re-derived its state. Never mount a ticket
  // for an entry the screen is no longer "clocked in here" on.
  if (_btInline && ctx.expectState && document.getElementById('jobboard-detail-modal')?.dataset?.jsState !== ctx.expectState) {
    jbResetBuildTicketState();
    return null;
  }

  // Auto-fill equipment from job day — run when THIS clock-out session has no
  // equipment line yet. TASK-427: a 2nd session (re-clock after lunch) restores
  // session-1's lines (tagged with the prior clockEntryId) but still needs its
  // OWN equipment line, so we gate on "no line for the current entry" instead of
  // "no lines at all". jbAutoFillEquipment dedups per-session internally.
  const _curSessionId = entryId || null;
  const _hasLineForThisSession = (JobBoardState.btEquipment || [])
    .some(e => (e._clockEntryId || null) === _curSessionId);
  if (_ticketMode && !_hasLineForThisSession) {
    jbAutoFillEquipment(entry);
  }

  // Build form sections based on clockOutFormSections template
  let sectionsHtml = '';
  if (jbCOFEnabled('notes')) sectionsHtml += jbBuildNotesSection();
  if (jbCOFEnabled('startStopTimes')) sectionsHtml += jbBuildWorkStartStopSection();
  if (jbCOFEnabled('equipment')) sectionsHtml += jbBuildEquipmentSection();
  sectionsHtml += jbBuildConsumablesSection(); // TASK-9264 — '' unless the tenant opted in
  if (jbCOFEnabled('downtime')) sectionsHtml += jbBuildDowntimeSection();
  if (jbCOFEnabled('standby')) sectionsHtml += jbBuildStandbySection();
  // Custom ticket sections (replaces hardcoded Field Activity)
  sectionsHtml += jbBuildCustomSectionsHtml();
  // TASK-1483 — Personal Vehicle (payroll, paid TO the driver; distinct from
  // Piece Rate's billable "Per Mile"). Its own gates live in the builder.
  sectionsHtml += jbBuildPersonalVehicleSection(entry);
  if (jbCOFEnabled('photos')) sectionsHtml += jbBuildPhotosSection();
  sectionsHtml += jbBuildSignatureSection(jobDayId); // TASK-395 — sign in the builder
  sectionsHtml += jbBuildStampSection(jobDayId);     // TASK-395-B
  sectionsHtml += jbBuildEmailCopySection(jobDayId); // TASK-395-B

  // Driver time-edit gate: admins always can; drivers only if tenant setting allows
  const isDriverScope = !!(window.AppState?.driverMode || window.AppState?.driverModePin || window.AppState?.isPublicAccess);
  const canEditClockTimes = !isDriverScope || window.AppState?.settings?.allowDriverTimeEdit === true;
  const timeEditOnClick = canEditClockTimes ? `onclick="window.JobBoardModule.jbShowTimeEdit()"` : '';
  const timeEditCursor = canEditClockTimes ? 'cursor:pointer;' : '';
  const timeEditUnderline = canEditClockTimes ? 'border-bottom:1px dashed' : '';

  // TASK-1180 — forgotten-clock-out guard (Cassian 28.69h / Hayden 72.41h
  // incident class: driver forgets to clock out, returns next morning, taps
  // Clock Out → NOW gets stamped → inflated payroll hours). When the open
  // shift exceeds the tenant's oversized-shift review threshold, show a
  // prominent banner; when the tenant does NOT allow driver time edits, also
  // expose an OUT-ONLY shrink-constrained correction input (clockIn preserved).
  // ADVISORY ONLY — submit is never blocked (TASK-428 clock-out-trap lesson).
  const _oversizedTh = jbOversizedShiftThreshold();
  const _isLongShift = hours > _oversizedTh;
  const longShiftBanner = _isLongShift ? `
        <div id="jb-long-shift-banner" style="background:linear-gradient(135deg,#f59e0b,#d97706);border-radius:14px;padding:14px;padding-top:max(14px, env(safe-area-inset-top, 0px));margin-bottom:16px;display:flex;gap:10px;align-items:flex-start;box-shadow:0 4px 14px rgba(245,158,11,0.35);">
          <span class="material-icons" style="font-size:24px;color:#7c2d12;flex:0 0 auto;">history_toggle_off</span>
          <div style="min-width:0;">
            <div style="font-size:0.95rem;font-weight:800;color:#431407;line-height:1.3;">You've been clocked in ${hours.toFixed(1)} hours — did you forget to clock out?</div>
            <div style="font-size:0.78rem;color:#7c2d12;margin-top:4px;line-height:1.35;">${canEditClockTimes
              ? 'If you forgot, tap the times above to set when you actually left before clocking out.'
              : 'If you forgot, enter the time you actually left below — your clock-in time stays the same.'}</div>
          </div>
        </div>` : '';
  const forgotClockOutHtml = (_isLongShift && !canEditClockTimes) ? `
        <div id="jb-forgot-clockout" style="background:rgba(255,255,255,0.07);border:1px solid rgba(245,158,11,0.5);border-radius:14px;padding:14px;margin-bottom:16px;">
          <label for="jb-forgot-cout" style="display:block;font-size:0.8rem;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#fde68a;margin-bottom:6px;">When did you actually stop working?</label>
          <input type="datetime-local" id="jb-forgot-cout" value="${coutDT}" max="${coutDT}" style="width:100%;box-sizing:border-box;padding:14px 12px;font-size:1.05rem;border:1px solid #334155;border-radius:10px;background:#1e293b;color:#e2e8f0;color-scheme:dark;">
          <label for="jb-forgot-reason" style="display:block;font-size:0.8rem;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#fde68a;margin:10px 0 6px;">Why the change? <span style="text-transform:none;letter-spacing:0;">(required)</span></label>
          <input type="text" id="jb-forgot-reason" maxlength="300" placeholder="e.g. Forgot to clock out — actually left at 7:00 PM" style="width:100%;box-sizing:border-box;padding:12px;font-size:0.9rem;border:1px solid #334155;border-radius:10px;background:#1e293b;color:#e2e8f0;">
          <button type="button" id="jb-forgot-apply" onclick="window.JobBoardModule.jbApplyForgotClockOut()" style="margin-top:10px;width:100%;padding:12px;font-size:0.9rem;font-weight:700;border:none;border-radius:10px;background:#f59e0b;color:#431407;cursor:pointer;">Use this clock-out time</button>
          <div style="font-size:0.75rem;color:#cbd5e1;margin-top:6px;text-align:center;">Optional — you can still clock out without changing anything.</div>
        </div>` : '';

  const _btChrome = _jbTicketEditorChromeHtml({ longShiftBanner, timeEditCursor, timeEditOnClick, clockInStr, timeEditUnderline, hours, clockOutStr,
    canEditClockTimes, cinDT, coutDT, forgotClockOutHtml, firstName, entry, job, sectionsHtml, _ticketMode, _ticketOnly, inline: _btInline });
  let modal;
  if (_btInline) {
    // TASK-9694 S2 — inline on the job screen, in its OWN container. Same id as
    // the classic modal on purpose: every reader (_jbCsRoot, the TASK-1204
    // merge, submitClockOut, recap refresh, repoint) resolves the ONE mounted
    // editor by it, and removing any prior #clockout-modal first is what keeps
    // the 15+ inner ids unique — never two editors mounted at once.
    _jbJsParkJobInfo();
    document.getElementById('clockout-modal')?.remove();
    document.getElementById('pwa-install-banner')?.remove();
    modal = document.createElement('div');
    modal.id = 'clockout-modal';
    modal.className = 'jb-bt-inline';
    modal.dataset.btInline = '1';
    modal.dataset.entryId = entryId;
    modal.dataset.jobId = jobId;
    modal.dataset.jobDayId = jobDayId;
    modal.dataset.clockInMs = String(clockInTime.getTime());
    modal.dataset.btPrimaryLbl = _ticketOnly ? 'Finish Job' : 'Clock Out';
    modal.innerHTML = _btChrome;
    root.replaceChildren(modal);
    _jbJsDockJobInfo();
    _jbJsCinPaintStatus(); // TASK-9694 polish — "✓ Clocked in at h:mm" in the header
  } else {
    // Build the modal — remove any stale instance first so signature-pad IDs
    // (sig-canvas-*) can't be duplicated by a double-open (COD: shared _sigState).
    document.getElementById('clockout-modal')?.remove();
    // 🔴 The PWA install banner (#pwa-install-banner, app.js, z-index 10000,
    // fixed bottom) sits ABOVE this modal's sticky footer (z-index 100) and
    // swallows taps on the primary Clock Out button — caught by the convergence
    // E2E: click resolved "visible, enabled and stable" and then never landed.
    // A driver mid-clock-out does not need an install prompt; remove it for the
    // session rather than z-index-dueling (the banner deliberately tops content).
    document.getElementById('pwa-install-banner')?.remove();
    modal = document.createElement('div');
    modal.className = 'modal';
    modal.id = 'clockout-modal';
    modal.dataset.entryId = entryId;
    modal.dataset.jobId = jobId;
    modal.dataset.jobDayId = jobDayId;
    modal.dataset.clockInMs = String(clockInTime.getTime());

    modal.innerHTML = _btChrome;

    document.body.appendChild(modal);
    modal.addEventListener('click', (e) => { if (e.target === modal) { /* don't close on backdrop click */ } });
  }
  if (_cmTicket) modal.dataset.cmTicket = '1';

  // Init waypoint autocomplete on custom section waypoint fields
  _csInitWaypoints();

  // TASK-9169 — keep the "Review today's logged work" recap current once the
  // modal is live (debounced input/change + one catch-up pass for the async
  // restores). Must run AFTER the modal is in the DOM; internally guarded and
  // no-ops when the Notes section is disabled for this tenant.
  _jbInstallRecapLiveRefresh();

  // TASK-9188 — resolve whether any restored "other session" equipment line is
  // actually THIS driver's own earlier segment (the lunch re-clock), then repaint
  // the caption. Deliberately not awaited: the modal must be usable instantly, the
  // rows are already correct as to editability, and only the attribution text
  // changes. Self-contained try/catch — see the function.
  _jbResolveOwnEarlierShiftLines();

  // TASK-9264 — load the materials list and reveal the section when there is
  // something to pick. Not awaited; self-contained try/catch.
  _jbLoadConsumableCatalog();

  // TASK-395: init any blank signature pads (canvas needs layout dimensions).
  setTimeout(() => jbInitSignaturePads(), 60);
  // TASK-395-B: clear any prior stamp capture so it doesn't bleed across opens.
  window.ClockModule?.resetStampCapture?.();

  // If ticket exists, refresh photo thumbnails after DOM is built
  if (JobBoardState.btPhotos.length > 0) {
    setTimeout(() => jbRefreshPhotoThumbnails(), 50);
  }

  // 🔴 TASK-1416 — REPAINT DOWNTIME AND STANDBY. jbRestoreTicketData already
  // filled JobBoardState.btDowntime/btStandby from Firestore above, but
  // jbBuildDowntimeSection/jbBuildStandbySection emit a HARDCODED "No downtime
  // entries" / "No standby entries" placeholder and depend on a follow-up
  // jbRefreshTimeEntryList that this function never made. Equipment survived a
  // reopen only because jbBuildEquipmentSection renders its rows inline instead.
  //
  // So a driver reopening a clock-out saw their saved downtime and standby as
  // GONE — and re-entered them. The data was never lost in Firestore; the harm is
  // the duplicate the driver then adds, which double-counts PAID time on the
  // ticket. Live proof on TITS: #1043 carries two identical 7:30–10:00 downtime
  // lines 2.5h each created 48 minutes apart, and #100308 has a real 0.67h
  // downtime followed by a second one at the full 11.9h clock-window default with
  // empty notes — the "Add Downtime" prefill, added because the first was invisible.
  //
  // Broken since 62d019cd (2026-03-31) — this path never worked.
  // Display-only and additive: jbRefreshTimeEntryList no-ops when its element is
  // absent, and an empty list rewrites the same placeholder, so this is idempotent
  // and cannot strand a driver mid-clock-out.
  jbRefreshTimeEntryList('downtime');
  jbRefreshTimeEntryList('standby');

  // TASK-1572 Slice 1 — open the equipment picker beneath the "Were you in a
  // truck?" prompt. Must run AFTER the modal's HTML is in the DOM (the picker
  // renders into #jb-equipment-picker, which does not exist until now). No-ops
  // silently when we are not asking, and is internally try/caught — this is the
  // last thing the modal builder does and it can never reach submitClockOut.
  _jbOpenTruckAskPicker();
}

// TASK-9694 S1 — the Build Ticket chrome (hero header + section host + sticky
// bar) as ONE builder, so the classic #clockout-modal and the inline job-screen
// editor render the SAME markup. Classic output is pinned byte-for-byte to the
// pre-refactor template by tests/task9694-ticket-editor.unit.cjs (golden in
// tests/fixtures/) — switch OFF must render exactly what it rendered before.
// Inline (switch ON, approved layout A) differs ONLY in: X/Cancel close the job
// screen, the link reads "Switch Jobs", ticket-only says Started/Now and
// Finish Job, the primary button asks first (_jbJsConfirmFinish), and the
// Job info card docks at the top of the section host.
// Pure: everything it prints arrives in `m` (plus escapeHtml/getCustomerName).
function _jbTicketEditorChromeHtml(m) {
  const { longShiftBanner, timeEditCursor, timeEditOnClick, clockInStr, timeEditUnderline, hours, clockOutStr,
    canEditClockTimes, cinDT, coutDT, forgotClockOutHtml, firstName, entry, job, sectionsHtml, _ticketMode, _ticketOnly } = m;
  const inline = m.inline === true;
  const _closeFn = inline ? 'closeJobDetails' : 'closeClockOutModal';
  const _inLbl = inline && _ticketOnly ? 'Started' : 'In';
  const _outLbl = inline && _ticketOnly ? 'Now' : 'Out';
  const _swLbl = inline ? 'Switch Jobs' : 'Done here — switch to another job';
  const _jobInfoSlot = inline ? '<div id="jb-js-jobinfo-slot"></div>' : '';
  const _primaryFn = inline ? '_jbJsConfirmFinish' : 'submitClockOut';
  const _primaryLbl = _ticketOnly ? (inline ? 'Finish Job' : 'Finalize Ticket') : 'Clock Out';
  // Let the Employee / Customer / Job placards shrink so a long job name
  // ellipsizes instead of pushing the Job placard off a 390px phone (found by
  // the Guy-tenant E2E). TASK-9711: the classic Build Ticket too, not just the
  // job screen — the classic golden was regenerated on purpose for this.
  const _pillMin = 'min-width:0;';
  // TASK-9694 polish (Chris 2026-10-08, UI rulebook #1/#2) — on the job screen
  // the "…? Fix it" sentence-link and the "Switch Jobs" text link become TWO
  // equal outlined pill buttons (≥48px, gloves), and a status slot under the
  // times carries the clock-in feedback ("✓ Clocked in at 8:03 AM"). Classic
  // (switch OFF) keeps the old links byte-for-byte (golden).
  const _actionsHtml = inline ? `
        <div class="jb-js-pills" style="display:flex;gap:8px;margin-top:12px;">
          <button type="button" id="jb-js-change-btn" class="jb-js-pill" onclick="window.JobBoardModule.jbOpenRepoint()" style="flex:1;min-width:0;min-height:48px;padding:8px 10px;border-radius:999px;border:1.5px solid #94a3b8;background:transparent;color:#fff;font-size:0.88rem;font-weight:800;line-height:1.2;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;">
            <span class="material-icons" style="font-size:18px;" aria-hidden="true">edit</span><span>Change job / customer</span>
          </button>
          <button type="button" id="jb-js-switch-btn" class="jb-js-pill" onclick="window.JobBoardModule.jbOpenSwitchJob()" style="flex:1;min-width:0;min-height:48px;padding:8px 10px;border-radius:999px;border:1.5px solid #94a3b8;background:transparent;color:#bfdbfe;font-size:0.88rem;font-weight:800;line-height:1.2;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;">
            <span class="material-icons" style="font-size:18px;" aria-hidden="true">swap_horiz</span><span>Switch Jobs</span>
          </button>
        </div>` : `
        <div style="text-align:center;margin-top:8px;display:flex;flex-direction:column;gap:4px;align-items:center;">
          <button type="button" onclick="window.JobBoardModule.jbOpenRepoint()" style="background:none;border:none;color:#94a3b8;font-size:0.72rem;cursor:pointer;padding:2px 6px;display:inline-flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:0.85rem;">edit_note</span> Clocked into the wrong customer or job? Fix it
          </button>
          <!-- TASK-837 — Switch Job: finalize THIS shift + immediately start a new
               one on another job (distinct from "Fix it", which corrects in place). -->
          <button type="button" onclick="window.JobBoardModule.jbOpenSwitchJob()" style="background:none;border:none;color:#60a5fa;font-size:0.72rem;cursor:pointer;padding:2px 6px;display:inline-flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:0.85rem;">swap_horiz</span> ${_swLbl}
          </button>
        </div>`;
  const _statusSlot = inline ? '\n        <div id="jb-js-cin-status" aria-live="polite"></div>' : '';
  // TASK-9694 polish (rulebook #4) — job-screen footer: Cancel is a real
  // outlined button spaced away from Save, Save is outlined, the primary
  // (Finish Job / Clock Out) is the ONLY solid button. 52px for gloves.
  const _footerHtml = inline ? `
          <button type="button" class="jb-js-foot-cancel" style="flex:0 0 auto;min-height:52px;padding:0 16px;margin-right:6px;font-size:0.95rem;font-weight:700;border:2px solid #64748b;border-radius:14px;background:#fff;color:#0f172a;cursor:pointer;" onclick="window.JobBoardModule.${_closeFn}()">Cancel</button>
          ${_ticketMode ? `<button type="button" id="jb-btn-save-ticket" style="flex:1;min-width:0;min-height:52px;padding:0 12px;font-size:0.95rem;font-weight:800;border:2px solid #64748b;border-radius:14px;background:#fff;color:#0f172a;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;" onclick="window.JobBoardModule.jbSaveTicket()">
            <span class="material-icons" style="font-size:18px;">save</span> Save
          </button>` : ''}
          <button type="button" id="btn-clockout-submit" style="flex:1.6;min-width:0;min-height:52px;padding:0 12px;font-size:1rem;font-weight:800;border:none;border-radius:14px;background:${_ticketOnly ? '#2f5bd3' : '#dc2626'};color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;" onclick="window.JobBoardModule.${_primaryFn}()">
            <span class="material-icons" style="font-size:20px;">${_ticketOnly ? 'check_circle' : 'logout'}</span> ${_primaryLbl}
          </button>` : `
          <button style="flex:0 0 auto;padding:13px 20px;font-size:0.9rem;font-weight:600;border:1px solid #e2e8f0;border-radius:12px;background:#fff;color:#334155;cursor:pointer;" onclick="window.JobBoardModule.${_closeFn}()">
            Cancel
          </button>
          ${_ticketMode ? `<button id="jb-btn-save-ticket" style="flex:0 0 auto;padding:13px 20px;font-size:0.9rem;font-weight:700;border:none;border-radius:12px;background:linear-gradient(135deg, #3b82f6, #2563eb);color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;box-shadow:0 4px 14px rgba(59,130,246,0.25);" onclick="window.JobBoardModule.jbSaveTicket()">
            <span class="material-icons" style="font-size:18px;">save</span> Save
          </button>` : ''}
          <button id="btn-clockout-submit" style="flex:1;padding:13px;font-size:1rem;font-weight:700;border:none;border-radius:12px;background:linear-gradient(135deg, ${_ticketOnly ? '#3B82F6, #1E40AF' : '#dc2626, #991b1b'});color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;box-shadow:0 4px 14px ${_ticketOnly ? 'rgba(59,130,246,0.25)' : 'rgba(220,38,38,0.25)'};" onclick="window.JobBoardModule.${_primaryFn}()">
            <span class="material-icons" style="font-size:20px;">${_ticketOnly ? 'check_circle' : 'logout'}</span> ${_primaryLbl}
          </button>`;
  const _footerShadow = inline ? '0 -6px 18px rgba(15,23,42,0.16)' : '0 -4px 20px rgba(0,0,0,0.08)';
  const inner = `
      <!-- Hero Header -->
      <div style="background:linear-gradient(135deg, #1e293b 0%, #0f172a 100%);padding:24px 20px 28px;border-radius:0 0 24px 24px;margin-bottom:20px;position:relative;">
        <!-- Close button -->
        <button onclick="window.JobBoardModule.${_closeFn}()" style="position:absolute;top:12px;right:12px;background:rgba(255,255,255,0.1);border:none;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;cursor:pointer;z-index:10;">
          <span class="material-icons" style="font-size:20px;color:#94a3b8;">close</span>
        </button>
        <!-- TASK-1180 — forgotten-clock-out banner + out-only correction (advisory) -->
        ${longShiftBanner}
        <!-- Time bar: In -> duration -> Out -->
        <div id="jb-clockout-times-display" style="display:flex;align-items:center;justify-content:center;gap:12px;margin-bottom:16px;">
          <div style="text-align:center;${timeEditCursor}" ${timeEditOnClick}>
            <div style="font-size:0.8rem;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;font-weight:600;">${_inLbl}</div>
            <div style="font-size:1.4rem;font-weight:700;color:#34d399;${timeEditUnderline} rgba(52,211,153,0.4);">${clockInStr}</div>
          </div>
          <div style="flex:0 0 auto;display:flex;flex-direction:column;align-items:center;gap:2px;">
            <div style="width:40px;height:1px;background:#334155;"></div>
            <div id="jb-clockout-hours" style="font-size:1.8rem;font-weight:800;color:#fff;line-height:1;">${hours}</div>
            <div style="font-size:0.8rem;color:#94a3b8;font-weight:500;">hours</div>
            <div style="width:40px;height:1px;background:#334155;"></div>
          </div>
          <div style="text-align:center;${timeEditCursor}" ${timeEditOnClick}>
            <div style="font-size:0.8rem;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;font-weight:600;">${_outLbl}</div>
            <div id="jb-clockout-out-time" style="font-size:1.4rem;font-weight:700;color:#f87171;${timeEditUnderline} rgba(248,113,113,0.4);">${clockOutStr}</div>
          </div>
          ${canEditClockTimes ? `<button type="button" onclick="window.JobBoardModule.jbShowTimeEdit()" aria-label="Edit times" style="width:44px;height:44px;display:inline-flex;align-items:center;justify-content:center;background:transparent;border:none;color:#475569;cursor:pointer;margin-left:4px;padding:0;"><span class="material-icons" style="font-size:20px;">edit</span></button>` : ''}
        </div>
        <!-- Inline time editor (hidden by default) -->
        <div id="jb-clockout-times-edit" style="display:none;margin-bottom:16px;">
          <div style="display:flex;gap:8px;align-items:center;margin-bottom:8px;">
            <label style="font-size:0.7rem;font-weight:600;color:#34d399;min-width:28px;">In:</label>
            <input type="datetime-local" id="jb-clockout-edit-cin" value="${cinDT}" style="flex:1;padding:8px 10px;border:1px solid #334155;border-radius:8px;font-size:0.85rem;background:#1e293b;color:#e2e8f0;color-scheme:dark;">
          </div>
          <div style="display:flex;gap:8px;align-items:center;margin-bottom:10px;">
            <label style="font-size:0.7rem;font-weight:600;color:#f87171;min-width:28px;">Out:</label>
            <input type="datetime-local" id="jb-clockout-edit-cout" value="${coutDT}" style="flex:1;padding:8px 10px;border:1px solid #334155;border-radius:8px;font-size:0.85rem;background:#1e293b;color:#e2e8f0;color-scheme:dark;">
          </div>
          <div style="display:flex;gap:8px;justify-content:center;">
            <button onclick="window.JobBoardModule.jbCancelTimeEdit()" style="padding:6px 16px;font-size:0.8rem;font-weight:600;border:1px solid #334155;border-radius:8px;background:transparent;color:#94a3b8;cursor:pointer;">Cancel</button>
            <button onclick="window.JobBoardModule.jbApplyTimeEdit()" style="padding:6px 16px;font-size:0.8rem;font-weight:600;border:none;border-radius:8px;background:#3b82f6;color:#fff;cursor:pointer;">Apply</button>
          </div>
        </div>${_statusSlot}
        ${forgotClockOutHtml}

        <!-- Info pills — Customer/Job are tappable to CORRECT a wrong clock-in
             (TASK-771). This re-points the active entry IN PLACE; it is NOT
             "Switch Ticket" (which starts a new segment). -->
        <div style="display:flex;gap:8px;">
          <div style="flex:1;${_pillMin}background:rgba(255,255,255,0.07);border-radius:12px;padding:10px 12px;text-align:center;">
            <div style="font-size:0.6rem;text-transform:uppercase;letter-spacing:0.5px;color:#475569;margin-bottom:3px;">Employee</div>
            <div style="font-size:0.85rem;font-weight:600;color:#e2e8f0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(firstName)}</div>
          </div>
          <div role="button" tabindex="0" aria-label="Correct customer" onclick="window.JobBoardModule.jbOpenRepoint()" style="flex:1;${_pillMin}background:rgba(255,255,255,0.07);border-radius:12px;padding:10px 12px;text-align:center;cursor:pointer;border:1px dashed rgba(148,163,184,0.4);">
            <div style="font-size:0.6rem;text-transform:uppercase;letter-spacing:0.5px;color:#64748b;margin-bottom:3px;">Customer&nbsp;<span class="material-icons" style="font-size:0.7rem;vertical-align:-1px;">edit</span></div>
            <div style="font-size:0.85rem;font-weight:600;color:#e2e8f0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(entry.customerName || getCustomerName(job?.customerId) || 'Shop')}</div>
          </div>
          <div role="button" tabindex="0" aria-label="Correct job" onclick="window.JobBoardModule.jbOpenRepoint()" style="flex:1;${_pillMin}background:rgba(255,255,255,0.07);border-radius:12px;padding:10px 12px;text-align:center;cursor:pointer;border:1px dashed rgba(148,163,184,0.4);">
            <div style="font-size:0.6rem;text-transform:uppercase;letter-spacing:0.5px;color:#64748b;margin-bottom:3px;">Job&nbsp;<span class="material-icons" style="font-size:0.7rem;vertical-align:-1px;">edit</span></div>
            <div style="font-size:0.85rem;font-weight:600;color:#e2e8f0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(entry.jobTitle || job?.title || job?.name || '—')}</div>
          </div>
        </div>${_actionsHtml}
      </div>

      <!-- Form Sections -->
      <div id="jb-bt-sections" style="padding:0 16px 130px;">
        ${_jobInfoSlot}${sectionsHtml}
      </div>

      <!-- Sticky Bottom Actions -->
      <div style="position:fixed;bottom:0;left:0;right:0;background:#fff;border-top:1px solid #e2e8f0;padding:12px 16px;padding-bottom:max(12px, env(safe-area-inset-bottom));z-index:100;box-shadow:${_footerShadow};">
        <div style="max-width:480px;margin:0 auto;display:flex;gap:10px;">${_footerHtml}
        </div>
      </div>
`;
  if (inline) return inner;
  return `
    <div class="modal-content modal-fullscreen" style="max-width:480px;margin:0 auto;padding:0;font-family:var(--font-body);min-height:100vh;min-height:100dvh;background:#f1f5f9;overflow-y:auto;">
${inner}    </div>
  `;
}

function closeClockOutModal() {
  jbResetBuildTicketState();
  const modal = document.getElementById('clockout-modal');
  // TASK-9694 — the inline editor belongs to the job screen: take it off and
  // let the screen re-derive its state (Finished / Not started / On another
  // job) instead of leaving an empty hole where the ticket was.
  if (modal && modal.dataset.btInline === '1') { _jbJsUnmountEditor(modal); return; }
  if (modal) modal.remove();
}

// =============================================================================
// TASK-771 — "Oops, wrong customer/job" CORRECTION (re-point the active entry).
// This is ERROR CORRECTION — re-points the SAME clock entry + its ticket data
// in place to the correct customer/job. Distinct from "Switch Ticket" (which
// finalizes a segment and starts a new one). The actual data move is a single
// atomic server op (mutateTicketAndClockEntry op:'repoint') — the client just
// collects the corrected customer/job and calls it.
// =============================================================================

function _jbRepointShopValue() { return '__shop__'; }

// TASK-1175 — Shop is now a real internalTimeOnly customer (the hardcoded
// "Shop / No Customer" pseudo-customer was removed). Internal-time customers
// (Shop/Office/yard) sit at the TOP of every jobboard customer picker so yard
// time stays one tap. TASK-9548 — they now sit under a labeled "Internal — not
// billed" optgroup, then "Customers" A–Z, built by the ONE shared helper
// (window.PickerGroups, read at CALL time — Pitfall #1), plus a hint line.
const _jbCustomerName = c => c.name || c.company || 'Unnamed';
// TASK-10250 — a shift is never moved onto an Inactive / merged-away customer; the entry's own stays.
function _jbCustomerOptionsHtml(list, selectedId) {
  return window.PickerGroups.customerOptionsHtml(list || [], { selectedId, nameOf: _jbCustomerName, excludeInactive: true });
}
function _jbCustomerHintHtml(list, color) {
  return window.PickerGroups.customerHintHtml(list || [], { nameOf: _jbCustomerName, color });
}

function jbOpenRepoint() {
  const entry = JobBoardState._btEntry;
  if (!entry) { window.showToast?.('Clock entry not loaded — reopen and try again.', 'error'); return; }

  // Online-only: a transactional financial mutation can't run offline.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    window.showToast?.('You’re offline — reconnect to correct the customer/job.', 'error');
    return;
  }

  const customers = JobBoardState.customers || [];
  const curCustId = entry.customerId || '';
  const custOptions = _jbCustomerOptionsHtml(customers, curCustId);

  document.getElementById('jb-repoint-modal')?.remove();
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'jb-repoint-modal';
  modal.style.cssText = 'display:flex;align-items:center;justify-content:center;z-index:2000;';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:420px;width:92%;margin:0 auto;padding:0;border-radius:16px;overflow:hidden;">
      <div style="background:linear-gradient(135deg,#1e293b,#0f172a);padding:18px 18px 14px;">
        <div style="display:flex;align-items:center;gap:8px;color:#fff;">
          <span class="material-icons" style="font-size:22px;color:#fbbf24;">edit_note</span>
          <div style="font-size:1rem;font-weight:700;">Fix customer / job</div>
        </div>
        <div style="font-size:0.78rem;color:#94a3b8;margin-top:4px;line-height:1.35;">
          Re-point this shift to the customer/job you meant to clock into. This corrects the existing entry &mdash; it does <b>not</b> start a new shift.
        </div>
      </div>
      <div style="padding:18px;">
        <div class="form-group" style="margin-bottom:14px;">
          <label style="font-size:0.8rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:6px;">Customer</label>
          <select id="jb-repoint-customer" class="form-control" onchange="window.JobBoardModule.jbRepointCustomerChange()">
            <option value="" ${!curCustId ? 'selected' : ''}>Choose customer…</option>
            ${custOptions}
          </select>
          ${_jbCustomerHintHtml(customers)}
        </div>
        <div class="form-group" id="jb-repoint-job-group" style="margin-bottom:14px;${curCustId ? '' : 'display:none;'}">
          <label style="font-size:0.8rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:6px;">Job</label>
          <select id="jb-repoint-job" class="form-control"></select>
        </div>
        <div id="jb-repoint-error" style="display:none;font-size:0.78rem;color:var(--danger,#dc2626);background:rgba(220,38,38,0.08);border-radius:8px;padding:8px 10px;margin-bottom:12px;"></div>
        <div style="display:flex;gap:10px;justify-content:flex-end;">
          <button type="button" onclick="window.JobBoardModule.jbCloseRepoint()" style="padding:10px 16px;font-size:0.85rem;font-weight:600;border:1px solid #e2e8f0;border-radius:10px;background:#fff;color:#334155;cursor:pointer;">Cancel</button>
          <button type="button" id="jb-repoint-save" onclick="window.JobBoardModule.jbSubmitRepoint()" style="padding:10px 18px;font-size:0.85rem;font-weight:700;border:none;border-radius:10px;background:linear-gradient(135deg,#3b82f6,#2563eb);color:#fff;cursor:pointer;display:flex;align-items:center;gap:6px;">
            <span class="material-icons" style="font-size:16px;">check</span> Save correction
          </button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  modal.addEventListener('click', (e) => { if (e.target === modal) jbCloseRepoint(); });
  // Populate the job dropdown for the current customer + preselect current job.
  jbRepointCustomerChange(entry.jobId || '');
}

function jbRepointCustomerChange(preselectJobId) {
  const custVal = document.getElementById('jb-repoint-customer')?.value;
  const group = document.getElementById('jb-repoint-job-group');
  const jobSelect = document.getElementById('jb-repoint-job');
  if (!jobSelect) return;
  if (!custVal || custVal === _jbRepointShopValue()) {
    if (group) group.style.display = 'none';
    jobSelect.innerHTML = '<option value="">No specific job</option>';
    return;
  }
  const jobs = (JobBoardState.jobs || [])
    .filter(j => j.customerId === custVal && j.status !== 'finished')
    .sort((a, b) => (a.title || a.name || '').localeCompare(b.title || b.name || ''));
  const opts = jobs.map(j =>
    `<option value="${j.id}" ${j.id === preselectJobId ? 'selected' : ''}>${escapeHtml(j.title || j.name || 'Untitled Job')}</option>`
  ).join('');
  jobSelect.innerHTML = '<option value="">No specific job</option>' + opts;
  if (group) group.style.display = '';
}

function jbCloseRepoint() {
  document.getElementById('jb-repoint-modal')?.remove();
}

async function jbSubmitRepoint() {
  const entry = JobBoardState._btEntry;
  if (!entry) { window.showToast?.('Clock entry not loaded.', 'error'); return; }
  const errEl = document.getElementById('jb-repoint-error');
  const showErr = (msg) => { if (errEl) { errEl.textContent = msg; errEl.style.display = 'block'; } else window.showToast?.(msg, 'error'); };

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    showErr('You’re offline — reconnect to correct the customer/job.');
    return;
  }

  // TASK-1175 — the "Shop / No Customer" option was removed from the picker; an
  // empty selection is now an error, not a silent re-point to Shop (which would
  // recreate the ticketless-time hole). Legacy __shop__ handling below stays
  // intact for correcting an entry that is STILL on a legacy Shop punch.
  const custVal = document.getElementById('jb-repoint-customer')?.value || '';
  if (!custVal) { showErr('Choose the customer for this shift.'); return; }
  const isShop = custVal === _jbRepointShopValue();
  const newCustomerId = isShop ? '__shop__' : custVal;
  const cust = isShop ? null : (JobBoardState.customers || []).find(c => c.id === custVal);
  const newCustomerName = isShop ? 'Shop' : (cust?.name || cust?.company || null);
  const newJobId = isShop ? '' : (document.getElementById('jb-repoint-job')?.value || '');
  const job = newJobId ? (JobBoardState.jobs || []).find(j => j.id === newJobId) : null;
  const newJobTitle = job ? (job.title || job.name || null) : null;
  // NOTE: the server resolves the TARGET job's jobDay (for override rates) from
  // newJobId itself — we deliberately do NOT pass the modal's jobDayId, which
  // belongs to the OLD job and would apply the wrong override rate (COD v4).

  // No-op guard (server also guards, but keep the UI honest).
  const sameCust = (entry.customerId || null) === (isShop ? null : newCustomerId);
  const sameJob = (entry.jobId || null) === (newJobId || null);
  if (sameCust && sameJob) { jbCloseRepoint(); window.showToast?.('No change — same customer/job.', 'info'); return; }

  const btn = document.getElementById('jb-repoint-save');
  const origHtml = btn ? btn.innerHTML : '';
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">hourglass_empty</span> Saving…'; }

  try {
    const gwPayload = {
      entryId: entry.id,
      customerId: newCustomerId,
      customerName: newCustomerName,
      jobId: newJobId || null,
      jobTitle: newJobTitle,
    };
    const gwScope = 'repoint:' + entry.id;
    const fn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: GW_CLIENT_TIMEOUT_MS });
    const result = await fn({
      op: 'repoint',
      tenantId: JobBoardState.tenantId,
      payload: gwPayload,
      opKey: _gwOpKey(gwScope, gwPayload), // TASK-1160 — identical retry replays instead of re-running the move
    });
    _gwOpDone(gwScope);
    const updated = result?.data?.updated || {};

    // Re-point local state so the open clock-out modal reflects the correction.
    // PREFER the server-resolved canonical names (updated.*) over our local
    // dropdown strings, which may be stale (COD v8 Blast).
    const srvCustomerName = updated.customerName != null ? updated.customerName : newCustomerName;
    const srvJobTitle = updated.jobTitle !== undefined ? updated.jobTitle : newJobTitle;
    entry.customerId = isShop ? null : newCustomerId;
    entry.customerName = srvCustomerName;
    entry.jobId = newJobId || null;
    entry.jobTitle = srvJobTitle;
    if (updated.ticketId !== undefined) entry.ticketId = updated.ticketId;
    JobBoardState._btEntry = entry;
    JobBoardState._btTicketId = updated.ticketId || null;
    JobBoardState._btJobId = newJobId || null;
    JobBoardState._btJob = job || null;
    // Keep the in-memory subscription copy in sync if present.
    const local = (JobBoardState.clockEntries || []).find(e => e.id === entry.id);
    if (local) {
      local.customerId = entry.customerId; local.customerName = entry.customerName;
      local.jobId = entry.jobId; local.jobTitle = entry.jobTitle; local.ticketId = entry.ticketId;
    }

    jbCloseRepoint();
    window.showToast?.('Corrected — this shift now bills to ' + (srvCustomerName || 'Shop') + (srvJobTitle ? ' / ' + srvJobTitle : '') + '.', 'success');

    // Re-render the clock-out modal so the pills + restored ticket data refresh.
    const modalEl = document.getElementById('clockout-modal');
    const entryId = modalEl?.dataset?.entryId || entry.id;
    const jdId = modalEl?.dataset?.jobDayId || null;
    if (modalEl && modalEl.dataset.btInline === '1') {
      // TASK-9694 — the inline ticket on the job screen: re-mount it there (same
      // job) or follow the entry to its corrected job's screen.
      _jbJsAfterRepoint(modalEl, entryId, entry.jobId || null);
    } else if (modalEl) {
      modalEl.remove();
      // Reopen with the corrected job context.
      await clockOutFromBoard(entryId, entry.jobId || null, jdId && jdId !== 'null' ? jdId : null, { shiftChosen: true }); // same shift, re-opened
    }
  } catch (e) {
    console.error('[JobBoard] repoint failed:', e);
    // TASK-1160 — friendly timeout message; the opKey dedups an identical retry.
    const msg = _gwIsDeadlineExceeded(e)
      ? 'That took too long — the correction may still have saved. Wait a few seconds, then retry.'
      : ((e && e.message) ? e.message : 'Could not save the correction. Try again.');
    showErr(msg);
    if (btn) { btn.disabled = false; btn.innerHTML = origHtml; }
  }
}

// =============================================================================
// TASK-837 — SWITCH JOB (driver-facing "finalize on switch", Chris Option A).
// Finalizes the operator's CURRENT shift (segment A) — out-times their labor
// line + closes their equipment lines on A — and immediately starts a NEW active
// shift (segment B) on another job, re-selecting equipment for B. ONE atomic
// server op (mutateTicketAndClockEntry op:'switchJob'); the client just collects
// the destination + B's equipment. Distinct from "Fix it" (repoint = in-place
// correction, no new shift).
// =============================================================================

// Whether the user has acknowledged a forgot-to-clock-out soft warning (a truck
// they re-selected for B is still open with another operator). Reset on open.
let _jbSwitchConfirmTake = false;
// TASK-9694 S4 — the switch sheet is bound to the entry it was opened for
// (_jbSwitchEntry), never to whatever JobBoardState._btEntry holds at submit
// time. From job B's screen there is no editor and _btEntry must stay
// untouched (Edge Case #1: B's screen never saves against entry A).
// _jbSwitchFor hands an explicit entry to the next jbOpenSwitchJob().
let _jbSwitchEntry = null;
let _jbSwitchFor = null;
// The entry's saved draft when the sheet opened — the server only replaces a
// diverged draft that still equals this (COD r1: a newer draft saved meanwhile
// from another device is never overwritten by this sheet's note).
let _jbSwitchDraftBase = '';

// Resolve A's equipment for the OFF (no equipmentTie) path: the legacy day-fill
// is the entry's jobDay trucks + trailers (mirrors jbAutoFillEquipmentTied's
// legacy branch). Returns real asset ids; the server closes them on the segment.
function _jbLegacyEquipmentIdsForEntry(entry) {
  const jobDay = _jbResolveJobDayForEntry(entry);
  if (!jobDay) return [];
  const ids = [];
  const push = (ref, list) => {
    const r = String(ref);
    const asset = _resolveFleetRef(r, list);
    const id = asset?.id || r;
    if (id && !ids.includes(id)) ids.push(id);
  };
  (jobDay.trucks || []).forEach(r => { if (r != null) push(r, JobBoardState.trucks); });
  (jobDay.trailers || []).forEach(r => { if (r != null) push(r, JobBoardState.trailers); });
  return ids;
}

function jbOpenSwitchJob() {
  const entry = _jbSwitchFor || JobBoardState._btEntry;
  _jbSwitchFor = null;
  if (!entry) { window.showToast?.('Clock entry not loaded — reopen and try again.', 'error'); return; }

  // Online-only: an atomic financial mutation can't run offline.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    window.showToast?.('You’re offline — reconnect to switch jobs.', 'error');
    return;
  }

  _jbSwitchConfirmTake = false;

  const customers = JobBoardState.customers || [];
  const custOptions = _jbCustomerOptionsHtml(customers);

  const fromLabel = entry.jobTitle || entry.customerName || 'this job';

  // TASK-9187 — the driver writes the note for the segment he is CLOSING in this
  // modal (#jb-switch-notes), while the TASK-9169 work recap lives only in the
  // builder behind it — covered on a phone. Chris's call: a ONE-LINE confirmation
  // here, not a second stacked recap card; a driver switching jobs in the yard is
  // in a hurry. So this adds the HOURS to the sentence that already names the job.
  //
  // Derived the way the equipment rows are (jbCalcHours over the clock window),
  // from entry.clockIn to NOW — which is the window the SERVER will actually close
  // (the switchJob payload carries no client times, so a builder time-edit
  // deliberately does not move this number; showing an edited window the switch
  // won't bill would be the worse lie). It is a snapshot at open, like the hours
  // readout in the builder itself.
  //
  // NEVER TRAP THE DRIVER: this modal is how he gets off a job. An unparseable
  // clockIn drops the clause and nothing else — it can never throw the innerHTML
  // that carries the Cancel button.
  let _hrsClause = '';
  try {
    const _cin = entry.clockIn?.toDate ? entry.clockIn.toDate() : (entry.clockIn ? new Date(entry.clockIn) : null);
    const _h = (_cin && !isNaN(_cin.getTime())) ? parseFloat(jbCalcHours(_cin, new Date()).toFixed(2)) : null;
    if (_h != null && isFinite(_h) && _h > 0) {
      _hrsClause = ` — <b style="color:#fff;">${_h} hrs logged</b> —`;
    }
  } catch (_) { _hrsClause = ''; }

  document.getElementById('jb-switch-modal')?.remove();
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'jb-switch-modal';
  modal.style.cssText = 'display:flex;align-items:center;justify-content:center;z-index:2000;';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:440px;width:92%;margin:0 auto;padding:0;border-radius:16px;overflow:hidden;max-height:92vh;max-height:92dvh;display:flex;flex-direction:column;">
      <div style="background:linear-gradient(135deg,#1e3a8a,#1e40af);padding:18px 18px 14px;flex:0 0 auto;">
        <div style="display:flex;align-items:center;gap:8px;color:#fff;">
          <span class="material-icons" style="font-size:22px;color:#93c5fd;">swap_horiz</span>
          <div style="font-size:1rem;font-weight:700;">Switch job</div>
        </div>
        <div style="font-size:0.78rem;color:#bfdbfe;margin-top:4px;line-height:1.35;">
          Finalizes your shift on <b>${escapeHtml(fromLabel)}</b>${_hrsClause} and starts a new one. Your hours and equipment on this job are closed out now.
        </div>
      </div>
      <div style="padding:18px;overflow-y:auto;flex:1 1 auto;">
        <div class="form-group" style="margin-bottom:14px;">
          <label style="font-size:0.8rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:6px;">New customer</label>
          <select id="jb-switch-customer" class="form-control" onchange="window.JobBoardModule.jbSwitchCustomerChange()">
            <option value="">Choose customer…</option>
            ${custOptions}
          </select>
          ${_jbCustomerHintHtml(customers)}
        </div>
        <div class="form-group" id="jb-switch-job-group" style="margin-bottom:14px;display:none;">
          <label style="font-size:0.8rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:6px;">New job</label>
          <select id="jb-switch-job" class="form-control" onchange="window.JobBoardModule.jbSwitchJobChange()"></select>
          <div id="jb-switch-crew-warn" style="display:none;margin-top:8px;"></div>
        </div>
        <div id="jb-switch-equipment" style="margin-bottom:14px;display:none;"></div>
        <div class="form-group" style="margin-bottom:14px;">
          <label style="font-size:0.8rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:6px;">Shift note for this job <span style="font-weight:400;color:#94a3b8;">(${window.AppState?.settings?.requireShiftNotes === true ? 'required' : 'optional'})</span></label>
          <textarea id="jb-switch-notes" class="form-control" rows="2" placeholder="What did you do on this job?" style="resize:vertical;"></textarea>
        </div>
        <div id="jb-switch-warning" style="display:none;font-size:0.78rem;color:#92400e;background:#fef3c7;border:1px solid #fde68a;border-radius:8px;padding:10px 12px;margin-bottom:12px;line-height:1.4;"></div>
        <div id="jb-switch-error" style="display:none;font-size:0.78rem;color:var(--danger,#dc2626);background:rgba(220,38,38,0.08);border-radius:8px;padding:8px 10px;margin-bottom:12px;"></div>
      </div>
      <div style="padding:14px 18px;border-top:1px solid #e2e8f0;display:flex;gap:10px;justify-content:flex-end;flex:0 0 auto;">
        <button type="button" onclick="window.JobBoardModule.jbCloseSwitchJob()" style="padding:10px 16px;font-size:0.85rem;font-weight:600;border:1px solid #e2e8f0;border-radius:10px;background:#fff;color:#334155;cursor:pointer;">Cancel</button>
        <button type="button" id="jb-switch-save" onclick="window.JobBoardModule.jbSubmitSwitchJob()" style="padding:10px 18px;font-size:0.85rem;font-weight:700;border:none;border-radius:10px;background:linear-gradient(135deg,#2563eb,#1d4ed8);color:#fff;cursor:pointer;display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:16px;">swap_horiz</span> Switch job
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  _jbSwitchEntry = entry;
  _jbSwitchDraftBase = String(entry.draftShiftNotes ?? '');
  modal.dataset.entryId = entry.id;

  // TASK-4563 (A, jobboard twin) — the PIN-driver sheet has the SAME defect as
  // clock.js: the driver types his note in #jb-shift-notes, taps 'Done here —
  // switch to another job', and lands on an EMPTY #jb-switch-notes box that
  // jbSubmitSwitchJob then REQUIRES when settings.requireShiftNotes is on. Carry
  // his own text for the segment he is closing. Seeded via .value after append,
  // never interpolated. Same person, same segment — first-person standard intact.
  try {
    const _sw = document.getElementById('jb-switch-notes');
    const _co = document.getElementById('jb-shift-notes');
    // TASK-9694 S4 — only from the editor of the entry being closed.
    const _edId = document.getElementById('clockout-modal')?.dataset?.entryId;
    if (_sw && _co && !_sw.value && (!_edId || _edId === entry.id)) _sw.value = _co.value || '';
  } catch (_) { /* seeding is a convenience — never block the switch */ }

  modal.addEventListener('click', (e) => { if (e.target === modal) jbCloseSwitchJob(); });
}

function jbSwitchCustomerChange() {
  const custVal = document.getElementById('jb-switch-customer')?.value;
  const group = document.getElementById('jb-switch-job-group');
  const jobSelect = document.getElementById('jb-switch-job');
  // Customer changed → any prior forgot-to-clock-out acknowledgement is stale.
  _jbSwitchConfirmTake = false;
  _jbResetSwitchWarning();
  if (!jobSelect) return;
  if (!custVal || custVal === '__shop__') {
    if (group) group.style.display = 'none';
    jobSelect.innerHTML = '<option value="">No specific job</option>';
    _jbRenderSwitchEquipment('');
    return;
  }
  const jobs = _jbSortJobsForPicker((JobBoardState.jobs || [])
    .filter(j => j.customerId === custVal && j.status !== 'finished'));
  const opts = jobs.map(j =>
    `<option value="${j.id}">${escapeHtml(_jbJobPickLabel(j))}</option>`
  ).join('');
  jobSelect.innerHTML = '<option value="">No specific job</option>' + opts;
  if (group) group.style.display = jobs.length > 0 ? '' : 'none';
  // TASK-9865 — preselect the job this driver is crewed on today + crew line.
  const pick = _jbPickJobForViewer(jobs);
  if (pick && pick.jobId) jobSelect.value = pick.jobId;
  _jbRenderSwitchEquipment(jobSelect.value || '');
  _jbRenderCrewWarn('jb-switch-job', 'jb-switch-crew-warn', jobs);
}

function jbSwitchJobChange() {
  _jbSwitchConfirmTake = false;
  _jbResetSwitchWarning();
  _jbRenderSwitchEquipment(document.getElementById('jb-switch-job')?.value || '');
  _jbRenderCrewWarn('jb-switch-job', 'jb-switch-crew-warn', _jbPickerJobs('jb-switch-job')); // TASK-9865
}

// Render the equipment re-select rows for the NEW job (segment B) — mirrors the
// clock-in confirm rows. Only when equipmentTie is ON and B's jobDay has assets.
function _jbRenderSwitchEquipment(jobId) {
  const container = document.getElementById('jb-switch-equipment');
  if (!container) return;
  if (!equipmentTieEnabled() || !jobId) {
    container.style.display = 'none';
    container.innerHTML = '';
    return;
  }
  const jobDay = _jbResolveJobDayForEntry({ jobId, clockIn: new Date() });
  const dayAssetIds = _jbJobDayAssetIds(jobDay);
  if (dayAssetIds.size === 0) {
    container.style.display = 'none';
    container.innerHTML = '';
    return;
  }
  const rows = _jbBuildEquipRows(jobDay);
  // TASK-9763 — the new job's day may pair this driver to a unit (rule 5).
  const pre = _jbCrewPairPreselect(jobDay, rows, getActiveJobBoardPersonId(), getActiveJobBoardPersonName());
  const rowsHtml = _jbBuildEquipRowsHtml(rows, pre);
  container.style.display = 'block';
  container.innerHTML = `
    <label style="display:block;font-size:0.8rem;font-weight:600;color:var(--text-secondary);margin-bottom:6px;">Which equipment for the new job? <span style="font-weight:400;color:#64748b;">${pre.mode === 'none' ? '(tap to deselect)' : pre.mode === 'paired' ? '(checked = your assignment; tap to change)' : "(dispatch hasn't put you in a unit — tap the one you're running)"}</span></label>
    <div style="display:flex;flex-direction:column;gap:8px;">${rowsHtml}</div>
    <button type="button" id="jb-switch-labor-only" onclick="window.JobBoardModule.jbSwitchLaborOnly()"
      style="width:100%;min-height:48px;margin-top:10px;padding:12px;font-size:0.9rem;font-weight:600;border:2px dashed #cbd5e1;border-radius:12px;background:#f8fafc;color:#334155;cursor:pointer;">
      <span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:18px;">person</span>No Equipment (Labor Only)
    </button>`;
}

function jbSwitchLaborOnly() {
  const container = document.getElementById('jb-switch-equipment');
  if (!container) return;
  container.querySelectorAll('.jb-eqc-row').forEach(row => {
    if (row.dataset.selected === '1') jbToggleEquipConfirmRow(row);
  });
  // Equipment selection changed → re-confirm any prior take-anyway acknowledgement.
  _jbSwitchConfirmTake = false;
  _jbResetSwitchWarning();
}

function _jbReadSwitchAssetIds() {
  const container = document.getElementById('jb-switch-equipment');
  if (!container || container.style.display === 'none') return [];
  return Array.from(container.querySelectorAll('.jb-eqc-row'))
    .filter(r => r.dataset.selected === '1')
    .map(r => r.dataset.assetId)
    .filter(Boolean);
}

function _jbResetSwitchWarning() {
  const warnEl = document.getElementById('jb-switch-warning');
  if (warnEl) { warnEl.style.display = 'none'; warnEl.innerHTML = ''; }
  const btn = document.getElementById('jb-switch-save');
  if (btn) btn.innerHTML = '<span class="material-icons" style="font-size:16px;">swap_horiz</span> Switch job';
}

function jbCloseSwitchJob() {
  document.getElementById('jb-switch-modal')?.remove();
  _jbSwitchEntry = null;
}

// TASK-9556 — the ONE client entry point to the server switchJob op. Both
// Switch Job buttons go through here: the board's "Done here — switch" (below)
// and the driver Actions-sheet tile (dispatch.js submitSwitchJob). The tile used
// to re-point the SAME clock entry at the new job and mint an open labor line
// there, never closing the old job's line — so the old ticket kept an open,
// 0-hour line and the board close-out later put the WHOLE shift on the new job.
// Returns the server's `updated` payload (needsConfirm / newEntryId / totalHours…).
// Throws on failure; the caller owns the UI.
async function jbRunSwitchJob(entry, opts) {
  const o = opts || {};
  if (!entry || !entry.id) throw new Error('Clock entry not loaded.');
  const tieEnabled = equipmentTieEnabled();
  // A's equipment to close. ON → exactly what the operator tied to at clock-in
  // (entry.assetIds). OFF → the legacy day-fill (this job day's trucks+trailers),
  // so a switch finalizes the SAME equipment a normal OFF close-out would bill.
  // A caller that already resolved it from its own warm state (dispatch.js —
  // the job board's state is cold on the SPA route) passes equipmentAssetIds.
  const equipmentAssetIds = Array.isArray(o.equipmentAssetIds)
    ? o.equipmentAssetIds.slice()
    : (tieEnabled && Array.isArray(entry.assetIds))
      ? entry.assetIds.slice()
      : _jbLegacyEquipmentIdsForEntry(entry);
  const gwPayload = {
    entryId: entry.id,
    switchOutNotes: o.notes || null,
    ...(typeof o.draftBase === 'string' ? { switchDraftBase: o.draftBase } : {}), // TASK-9694 S4
    buildTickets: isTicketMode(),
    equipmentTieEnabled: tieEnabled,
    equipmentAssetIds,
    newCustomerId: o.newCustomerId,
    newJobId: o.newJobId || null,
    newAssetIds: tieEnabled && Array.isArray(o.newAssetIds) ? o.newAssetIds : [],
    confirmTakeAsset: o.confirmTakeAsset === true,
  };
  const gwScope = 'switchJob:' + entry.id;
  // TASK-1160 — key on the switch itself, not the GPS reading below: a retry
  // gets a slightly different fix and must still replay, not double-finalize.
  const opKey = _gwOpKey(gwScope, gwPayload);
  // TASK-9712 — a switch is A's clock-out and B's clock-in, so it stamps the same
  // GPS + geofence fields those do: one best-effort reading now (never blocks —
  // denied / unavailable / timeout → null → fields omitted, like a normal punch),
  // A's fence checked as 'out', B's as 'in'. The server range-checks and copies.
  const loc = await captureClockLocation();
  const jobList = Array.isArray(o.jobs) ? o.jobs : (JobBoardState.jobs || []);
  const findJob = (id) => (id ? (jobList.find(j => j && j.id === id) || null) : null);
  const aIsShop = !entry.customerId && entry.customerName === 'Shop';
  const bIsShop = !o.newCustomerId || o.newCustomerId === '__shop__';
  gwPayload.switchLocation = loc ? { lat: loc.lat, lng: loc.lng, accuracy: loc.accuracy } : null;
  gwPayload.clockOutGeofence = _jbComputeGeofenceFields(loc, findJob(entry.jobId), aIsShop, 'out');
  gwPayload.clockInGeofence = _jbComputeGeofenceFields(loc, bIsShop ? null : findJob(o.newJobId), bIsShop, 'in');
  const fn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: GW_CLIENT_TIMEOUT_MS });
  const result = await fn({
    op: 'switchJob',
    tenantId: o.tenantId || JobBoardState.tenantId,
    payload: gwPayload,
    opKey,
  });
  const updated = result?.data?.updated || {};
  if (!updated.needsConfirm) _gwOpDone(gwScope);
  return updated;
}

// Exposed for the dispatch.js tile so it can word a timeout the same way.
function jbIsGatewayTimeout(err) { return _gwIsDeadlineExceeded(err); }

async function jbSubmitSwitchJob() {
  // A bound sheet switches ONLY its own entry; a mismatch refuses (COD r1 —
  // never fall back to _btEntry). Only an unbound sheet uses _btEntry.
  const _bound = document.getElementById('jb-switch-modal')?.dataset?.entryId;
  const entry = _bound ? (_jbSwitchEntry?.id === _bound ? _jbSwitchEntry : null) : JobBoardState._btEntry;
  if (!entry) { window.showToast?.('Clock entry not loaded — reopen and try again.', 'error'); return; }
  const errEl = document.getElementById('jb-switch-error');
  const warnEl = document.getElementById('jb-switch-warning');
  const showErr = (msg) => { if (errEl) { errEl.textContent = msg; errEl.style.display = 'block'; } else window.showToast?.(msg, 'error'); };
  if (errEl) errEl.style.display = 'none';

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    showErr('You’re offline — reconnect to switch jobs.');
    return;
  }

  const custVal = document.getElementById('jb-switch-customer')?.value || '';
  if (!custVal) { showErr('Choose the customer for the new job.'); return; }
  const isShop = custVal === '__shop__';
  const newCustomerId = isShop ? '__shop__' : custVal;
  const newJobId = isShop ? '' : (document.getElementById('jb-switch-job')?.value || '');
  const notes = document.getElementById('jb-switch-notes')?.value?.trim() || '';

  // TASK-619 — a switch closes out the current segment (a clock-out), so honor
  // the require-shift-note setting if the tenant opted in.
  if (window.AppState?.settings?.requireShiftNotes === true && !notes) {
    showErr('Add a shift note before switching — what did you do on this job?');
    document.getElementById('jb-switch-notes')?.focus();
    return;
  }

  const newAssetIds = equipmentTieEnabled() ? _jbReadSwitchAssetIds() : [];

  const btn = document.getElementById('jb-switch-save');
  const origHtml = btn ? btn.innerHTML : '';
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">hourglass_empty</span> Switching…'; }

  try {
    const updated = await jbRunSwitchJob(entry, {
      newCustomerId, newJobId, notes, newAssetIds,
      confirmTakeAsset: _jbSwitchConfirmTake === true,
      draftBase: _jbSwitchDraftBase,
    });

    // Forgot-to-clock-out soft warning — a re-selected truck is still open with
    // another operator. Don't block: surface it, let the driver take it anyway.
    if (updated.needsConfirm) {
      const conflicts = Array.isArray(updated.conflicts) ? updated.conflicts : [];
      const lines = conflicts.map(c =>
        `<div style="margin-top:4px;"><b>${escapeHtml(c.assetName || c.assetId)}</b> is still clocked in with ${escapeHtml(c.holderName || 'another operator')} on ${escapeHtml(c.jobTitle || 'another job')}.</div>`
      ).join('');
      if (warnEl) {
        warnEl.innerHTML = `<div style="font-weight:700;display:flex;align-items:center;gap:6px;"><span class="material-icons" style="font-size:16px;">warning</span> Heads up</div>${lines}<div style="margin-top:6px;color:#78350f;">Take it anyway? Dispatch can clean up the other shift.</div>`;
        warnEl.style.display = 'block';
      }
      _jbSwitchConfirmTake = true;
      if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">swap_horiz</span> Take it anyway & switch'; }
      return;
    }

    const destLabel = (updated.newJobTitle || updated.newCustomerName || 'the new job');
    window.showToast?.(`Switched — ${updated.totalHours != null ? updated.totalHours + ' hrs closed out, ' : ''}now on ${destLabel}.`, 'success');

    // Close both the switch modal and the close-out modal — the personId-scoped
    // clockEntries subscription will surface B as the new active shift.
    const _fromJobScreen = document.getElementById('jobboard-detail-modal')?.dataset?.jobscreen === '1';
    jbCloseSwitchJob();
    closeClockOutModal();
    // TASK-9694 — started on a job screen: follow the driver to B's screen.
    if (_fromJobScreen) _jbJsFollowSwitch(updated.newEntryId);
  } catch (e) {
    console.error('[JobBoard] switchJob failed:', e);
    // TASK-1160 — on a timeout the server may STILL complete the switch. Say so
    // plainly (never a silent trap — the driver must be able to finish their
    // shift) and let them retry: the opKey dedups an identical retry, and the
    // server's in-txn "already closed" guard catches everything else.
    if (_gwIsDeadlineExceeded(e)) {
      showErr('That took too long — the switch may still have gone through. Wait a few seconds, then close and reopen this screen to check before retrying.');
    } else {
      showErr((e && e.message) ? e.message : 'Could not switch jobs. Try again.');
    }
    if (btn) { btn.disabled = false; btn.innerHTML = origHtml; }
  }
}

let _downtimeCounter = 0;
function addDowntimeRow() {
  // Legacy for startTicketFromBoard — new form uses jbAddDowntime()
  const list = document.getElementById('clockout-downtime-list');
  if (!list) return;
  const idx = _downtimeCounter++;
  const row = document.createElement('div');
  row.className = 'clockout-dt-row';
  row.style.cssText = 'display:flex;gap:6px;align-items:center;';
  row.innerHTML = `
    <input type="number" class="form-control dt-hours" placeholder="Hours" step="0.25" min="0" style="width:80px;">
    <input type="text" class="form-control dt-notes" placeholder="Reason for downtime..." style="flex:1;">
    <button type="button" class="btn-icon" onclick="this.parentElement.remove()" title="Remove">
      <span class="material-icons" style="font-size:16px;color:var(--status-danger);">close</span>
    </button>
  `;
  list.appendChild(row);
}

// ─── TASK-9190 — device-clock skew reconciliation on clock-out ───────────────
// Every clock-IN in this codebase is stamped `Timestamp.now()` — the DEVICE
// clock (jobboard :9230 + :13199, clock.js :17918, dispatch.js :26943). TASK-1180
// then made the un-edited clock-OUT `serverTimestamp()`. So a completed entry
// stores ONE device-clock endpoint and ONE server endpoint, while `totalHours`
// is measured device-to-device.
//
// The consequence is the OPPOSITE of "a fast phone inflates hours", and the
// direction is the whole reason this helper corrects what it corrects. With both
// ends of the subtraction read off the same (skewed) device, the DURATION is
// right; what is wrong by the skew is the stored clockIn, and therefore the
// stored window (clockOut − clockIn). Measured on 8 live TITS entries from one
// device running ~59 min fast: the 2026-09-04 punch stores clockIn 07:58 CDT
// against a job titled "Safety Meeting - 7AM" with totalHours 10.55, and
// 06:59 + 10.55 h lands exactly on the server-stamped clock-out. Re-deriving
// totalHours from the stored window would have recorded 9.56 h — an hour of
// worked time deleted, i.e. the FLSA underpayment exposure this task was told
// not to create. So: reconcile the TIMESTAMP, never the duration.
//
//   • measure the skew by reading back the resolved serverTimestamp,
//   • shift clockIn by exactly that, so the stored window agrees with the hours
//     already recorded (and with the TASK-9191 self-arm bounds — 0.02 h on
//     hours, clockInMoveOk on the window — for any later write to the entry),
//   • leave totalHours/hours untouched, so pay cannot move in either direction,
//   • keep the raw device value and the measured skew on the doc and write an
//     audit row, so the correction is visible, attributable and reversible.
//
// 🔴 THE SHIFT NEEDS SAME-DEVICE EVIDENCE, AND THE ARITHMETIC IS NOT IT (COD
// Code Reviewer). A dispatcher-stamped clock-in plus a driver's skewed clock-out
// closes exactly the same equation — and in THAT shape the clock-in is correct
// and the HOURS are long, so shifting the timestamp would make the record worse.
// The two cases are indistinguishable from the stored values, so the correction
// is applied only to an entry this browser's own clock-in created (a marker
// dropped at clock-in). No marker — another device, cleared storage, a private
// window, an office-created entry — and we record the measured skew and change
// nothing else. Fail-safe: the default is today's behaviour.
//
// Deliberately NOT flagged `needsReview`: every consumer of that flag renders it
// as "driver adjusted" (clock.js :3407, :4832, :5500, :6067). A device with a
// wrong clock is not a person adjusting their own time, and saying so on the
// payroll surfaces would be a false accusation. The explicit skew fields plus
// the audit row are the honest trail.
//
// Best-effort by construction — one transaction, every failure swallowed, and
// deliberately NOT routed through jbAckOrQueue, which flips the shared
// `_btWriteUnacked` (:8041) that the STEP-2 ticket phase reads. It runs after
// the entry is already completed, so it can never block or trap a driver.
const JB_CLOCK_SKEW_MIN_MS = 60 * 1000;            // under a minute: inside the TASK-9191 72 s tolerance, leave it alone
const JB_CLOCK_SKEW_MAX_MS = 24 * 60 * 60 * 1000;  // refuse an implausible measurement instead of trusting it
const JB_CLOCK_SKEW_LATENCY_MAX_MS = 30 * 1000;    // GPS (<=6s) + write ack; past this the reading is too stale to measure with
const JB_CLOCK_SKEW_KEY = 'dd_jb_devclockin_';
const JB_CLOCK_SKEW_MARK_TTL_MS = 30 * 24 * 60 * 60 * 1000;

// Marks an entry as "this browser's clock stamped the clockIn". Storage can be
// unavailable (private window, blocked site data) — every path swallows, and an
// absent marker only ever means "do not correct".
function _jbMarkDeviceStampedClockIn(entryId, clockInMs) {
  if (!entryId || !Number.isFinite(clockInMs)) return;
  try {
    const now = Date.now();
    localStorage.setItem(JB_CLOCK_SKEW_KEY + entryId, String(clockInMs));
    // Prune on write so the markers can't accumulate for the life of the device.
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k || k.indexOf(JB_CLOCK_SKEW_KEY) !== 0) continue;
      const at = parseInt(localStorage.getItem(k) || '0', 10);
      if (!at || now - at > JB_CLOCK_SKEW_MARK_TTL_MS) localStorage.removeItem(k);
    }
  } catch (_) { /* storage unavailable — reconciliation simply won't apply */ }
}

// True only when THIS browser stamped the clockIn the doc still carries. Binding
// to the value (not just the id) means a driver time-edit or an office correction
// voids the marker on its own — the reconciliation can never re-shift a time a
// human chose (COD Code Reviewer, round 2).
function _jbWasClockInDeviceStamped(entryId, storedInMs) {
  if (!entryId || !Number.isFinite(storedInMs)) return false;
  try {
    const marked = localStorage.getItem(JB_CLOCK_SKEW_KEY + entryId);
    return marked !== null && parseInt(marked, 10) === storedInMs;
  } catch (_) { return false; }
}

async function _jbReconcileClockSkew({ tenantId, entryId, entryRef, deviceClockOutMs }) {
  try {
    if (!tenantId || !entryId || !entryRef || !Number.isFinite(deviceClockOutMs)) return;
    // The reading that produced `hours` is taken at submit, BEFORE the GPS
    // capture (bounded 6s) and the write, so the measurement carries that elapsed
    // time as well as the device's real error (COD Code Reviewer, round 2). Both
    // are device-clock readings, so the elapsed is measurable WITHOUT the skew —
    // and past a point the measurement is too contaminated to move a timestamp on.
    if (Date.now() - deviceClockOutMs > JB_CLOCK_SKEW_LATENCY_MAX_MS) return;
    // A transaction, not read-then-write: an office correction landing between
    // the two would otherwise be clobbered by a clockIn derived from the stale
    // read (COD Code Reviewer). Fails closed if it can't reach the server.
    const result = await runTransaction(db, async (tx) => {
      // Every value this attempt produces is RETURNED, never closed over: a
      // transaction retry re-runs this body, and a leftover audit from an
      // abandoned attempt would log a correction that never committed.
      const snap = await tx.get(entryRef);
      if (!snap.exists()) return null;
      const e = snap.data() || {};
      // Already reconciled — never shift the same entry twice on a replay.
      if (typeof e.deviceClockSkewMs === 'number') return null;
      const srvOutMs = e.clockOut?.toMillis?.();
      const storedInMs = e.clockIn?.toMillis?.();
      // clockOut reads back null while the serverTimestamp() sentinel is still
      // pending. "Not resolved yet" is not "no skew" — stop rather than guess.
      if (!srvOutMs || !storedInMs) return null;
      const skewMs = deviceClockOutMs - srvOutMs;
      if (!Number.isFinite(skewMs)) return null;
      if (Math.abs(skewMs) < JB_CLOCK_SKEW_MIN_MS || Math.abs(skewMs) > JB_CLOCK_SKEW_MAX_MS) return null;
      const recorded = typeof e.totalHours === 'number' ? e.totalHours
        : (typeof e.hours === 'number' ? e.hours : null);
      const correctedInMs = storedInMs - skewMs;
      const correctedWindowH = (srvOutMs - correctedInMs) / 3600000;
      // The corrected window must reproduce the hours ALREADY recorded. That
      // proves `totalHours` was derived from this device's own pair — hand-edited
      // times or an office correction that landed first will not close, and we
      // leave those for a human instead of moving a timestamp on a guess.
      // TASK-9795 — a driver session may move a FINISHED shift's clockIn only
      // while the tenant's "Drivers can edit their clock times" switch is ON
      // (firestore.rules driverTimeEditAllowed denies it otherwise, and the
      // catch below would swallow the denial — COD Blast-Radius 2026-10-12).
      // With the switch OFF the skew is still RECORDED (clockSkewUnreconciled)
      // so the office can see the device was wrong; hours are unchanged either way.
      const _skewDriverScope = !!(window.AppState?.driverMode || window.AppState?.driverModePin || window.AppState?.isPublicAccess);
      const _skewMayMoveClockIn = !_skewDriverScope || window.AppState?.settings?.allowDriverTimeEdit === true;
      const canCorrect = _skewMayMoveClockIn && _jbWasClockInDeviceStamped(entryId, storedInMs) && recorded !== null
        && correctedWindowH > 0 && Math.abs(correctedWindowH - recorded) <= 0.02;
      const upd = {
        deviceClockSkewMs: Math.round(skewMs),
        // updatedAt is device-stamped everywhere else on this path; stamp the
        // skew-corrected device time so this write doesn't re-introduce exactly
        // what it just took out.
        updatedAt: Timestamp.fromMillis(Date.now() - skewMs)
      };
      let _audit = null;
      if (canCorrect) {
        const _corrected = Timestamp.fromMillis(correctedInMs);
        upd.clockIn = _corrected;
        upd.clockInBeforeSkewCorrection = e.clockIn;
        upd.clockInSkewCorrected = true;
        _audit = { before: e, after: { ...e, clockIn: _corrected } };
      } else {
        // Measured but not acted on: the office can see the device was wrong.
        upd.clockSkewUnreconciled = true;
      }
      tx.update(entryRef, upd);
      return { skewMs: Math.round(skewMs), audit: _audit };
    });
    if (!result) return;
    console.warn('[JobBoard] TASK-9190 device clock off by', Math.round(result.skewMs / 1000), 's —',
      result.audit ? 'clockIn reconciled to the server clock, hours unchanged' : 'recorded only (clock-in not stamped by this device, or the time was hand-edited)');
    // Fire-and-forget audit row (the helper strips undefined and swallows its
    // own failures) — reached through window.ClockModule, never an import.
    if (result.audit) {
      window.ClockModule?.logClockEntryChange?.(
        tenantId, entryId, result.audit.before, result.audit.after, 'edit', 'device-clock-skew'
      );
    }
  } catch (err) {
    console.warn('[JobBoard] TASK-9190 skew reconcile skipped:', err?.code || err?.message || err);
  }
}

async function submitClockOut() {
  const modal = document.getElementById('clockout-modal');
  if (!modal) return;

  // TASK-9402 — a ticket photo or stamp still converting must ride this
  // clock-out (jbSaveTicket runs nested below). Settle BEFORE the lock.
  _jbTrackStampPrep(); // COD r2 — stamp wait inside the one guarded settle
  const _editor = _jbEditorIdentity(); // COD r3 — the SAME modal + ticket after the wait
  if (!(await settlePhotoPrepBeforeSave(_jbPhotoPrep, 'btn-clockout-submit'))) return;
  if (document.getElementById('clockout-modal') !== modal || !_jbSameEditor(_editor)) return; // closed / reopened while we waited

  // Prevent double-click
  const coBtn = document.getElementById('btn-clockout-submit');
  if (coBtn?.disabled) return;
  const _coBtnLabel = coBtn ? coBtn.innerHTML : '';
  if (coBtn) {
    coBtn.disabled = true;
    coBtn.innerHTML = '<span class="btn-spinner"></span> Submitting...';
  }

  // clock-dst-all — the ticket rows / work times the nested jbSaveTicket will
  // write are resolved HERE, before the clock-out itself is written: a
  // repeated fall-back time is asked now (the choice is remembered for the
  // nested save), a gap / unreadable company zone stops the clock-out with
  // nothing written. Ticket-only (legacy) mode types no wall times.
  // COD 2026-10-03: a TICKET-row problem must never keep a person on the clock.
  // Only an explicit Cancel in the which-time picker stops here (the person
  // re-taps and picks); a gap / unreadable zone / unreadable row time lets the
  // clock-out proceed — the nested jbSaveTicket refuses those rows with the
  // reason and the existing "ticket not saved" path (_ticketLineFailed) runs.
  // COD 1.15.2009: NOTHING about a ticket row stops the punch — not a gap, not an
  // unreadable zone, not a Cancel in the picker. Unresolved rows are left
  // unsaved (the nested save refuses them without asking again) and the entry
  // is flagged needsReview for the office.
  let _jbTicketTimesProblem = null;
  if (modal.dataset.ticketOnly !== 'true') {
    const _coTimes = await _jbResolveTicketTimes();
    if (!_coTimes.ok) _jbTicketTimesProblem = _coTimes.message || 'A ticket time could not be read.';
  }

  jbResetNetOutcome(); // TASK-1642 — fresh network verdict for this clock-out.

  const _isTicketOnly = modal.dataset.ticketOnly === 'true';
  const entryId = modal.dataset.entryId;
  const jobId = modal.dataset.jobId;
  const jobDayId = modal.dataset.jobDayId;
  const tenantId = JobBoardState.tenantId;
  const entry = JobBoardState._btEntry;
  const _ticketMode = isTicketMode() || modal.dataset.cmTicket === '1';

  // For ticket-only mode (startTicketFromBoard), use legacy path
  if (_isTicketOnly) {
    return _submitClockOutLegacy(modal);
  }

  const clockInMs = parseInt(modal.dataset.clockInMs || '0');
  let clockInTime, clockOutTime, hours;

  // Use edited times if available
  if (JobBoardState._btEditedClockIn) {
    clockInTime = JobBoardState._btEditedClockIn;
  } else {
    clockInTime = new Date(clockInMs);
  }
  if (JobBoardState._btEditedClockOut) {
    clockOutTime = JobBoardState._btEditedClockOut;
  } else {
    clockOutTime = new Date();
  }
  hours = parseFloat(((clockOutTime - clockInTime) / 3600000).toFixed(2));

  const shiftNotes = document.getElementById('jb-shift-notes')?.value?.trim() || null;

  // TASK-619 (Lacey) — require a shift note before clock-out when the tenant opts
  // in. Read the setting inline (no clock.js import — cache-buster cross-import
  // pitfall). Gate runs BEFORE any Firestore write so a blank note never produces
  // a half-completed clock-out. The submit button was already disabled at the
  // double-click guard (~:4905), so on a blank-note bounce we MUST re-enable it —
  // otherwise the driver is stuck staring at a dead "Submitting…" button (COD
  // Code Reviewer catch). Never trap a driver: clear toast + focus the field.
  if (window.AppState?.settings?.requireShiftNotes === true && !shiftNotes) {
    window.showToast?.('Add a shift note to clock out — what did you do today?', 'error');
    if (coBtn) {
      coBtn.disabled = false;
      coBtn.innerHTML = '<span class="material-icons" style="font-size:20px;">logout</span> ' + (modal.dataset.btPrimaryLbl || 'Clock Out');
    }
    document.getElementById('jb-shift-notes')?.focus();
    return;
  }

  // TASK-1483 — personal-vehicle claim: validated BEFORE any write, so a bad
  // or missing miles value bounces here (re-enabled button, focused field) and
  // can never leave a half-completed clock-out.
  const _jbPv = window.ClockModule?.pvReadForm?.('jb') || null;
  if (_jbPv && !_jbPv.ok) {
    window.showToast?.(_jbPv.error, 'error');
    if (coBtn) {
      coBtn.disabled = false;
      coBtn.innerHTML = '<span class="material-icons" style="font-size:20px;">logout</span> ' + (modal.dataset.btPrimaryLbl || 'Clock Out');
    }
    document.getElementById('jb-pv-miles')?.focus();
    return;
  }
  const _jbPvFields = _jbPv ? _jbPv.fields : null;

  const btn = document.getElementById('btn-clockout-submit');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = `<span class="btn-spinner"></span> ${_ticketMode ? 'Building...' : 'Clocking out...'}`;
  }

  try {
    const job = JobBoardState.jobs.find(j => j.id === jobId);
    const customerId = job?.customerId || null;
    const customerName = getCustomerName(customerId) || null;

    // ── STEP 1: Complete the clock entry FIRST (never trap a driver) ──────
    // A driver must NEVER be left clocked-in by a downstream ticket-build
    // failure. (TITS 2026-06-05: TASK-428's `clockEntry.jobId == ticket.jobId`
    // gate denied shared-ticket line writes for PIN crews whose job-board jobId
    // differed from the dispatch ticket's jobId; the throw skipped the
    // completion that used to live below, stranding guys clocked-in 14+ hrs.
    // Completing here first removes the trap regardless of the rule — the ticket
    // build below is now best-effort.)
    if (entryId) {
      // Best-effort GPS capture for clock-out location — never blocks on failure
      const outLoc = await captureClockLocation();
      // TASK-191 Phase 3 — geofence distance flag on clock-OUT (audit only, NEVER
      // blocks). isShop derived from the entry (Shop punches carry no customerId
      // and customerName 'Shop'). {} when no coords/fence/error → field omitted.
      const _outIsShop = !entry?.customerId && (entry?.customerName === 'Shop');
      const outGeo = _jbComputeGeofenceFields(outLoc, job, _outIsShop, 'out');
      const entryRef = doc(db, 'tenants', tenantId, 'clockEntries', entryId);
      const clockOutUpdate = {
        // TASK-1180 — when the driver did NOT pick a time, stamp clockOut with
        // serverTimestamp() instead of the device clock. The clockEntries
        // self-arm now bounds clockOut <= server now + 10min; a device with a
        // fast clock stamping its own `new Date()` could be denial-trapped on
        // the NORMAL clock-out path (TASK-428 class) — serverTimestamp always
        // satisfies the bound and is the more honest value anyway.
        // TASK-9190 — the rest of this comment used to read "totalHours still
        // derives from the client clock (display parity); seconds-level drift
        // vs the stored clockOut is acceptable and pre-existing." The drift is
        // real, but it is not seconds and it is not in totalHours: clockIn is
        // device-stamped too (:9230), so hours — device minus device — is the
        // honest elapsed time, and what is wrong by the skew is the stored
        // WINDOW. Measured at 59 minutes on one live device. Display parity is
        // also exactly what the client value buys: the total shown at clock-out
        // matches the running timer the driver watched on his own phone all
        // day. So totalHours stays as it is and _jbReconcileClockSkew (called
        // after the write) pulls clockIn onto the server clock so the window
        // agrees with it.
        clockOut: JobBoardState._btEditedClockOut ? Timestamp.fromDate(clockOutTime) : serverTimestamp(),
        status: 'completed',
        totalHours: hours,
        hours: hours,
        notes: shiftNotes,
        // TASK-4621 — the ticket's shift-note block renders `clockOutNotes` ONLY
        // (billing.js filters on it and drops the entry when empty). The only
        // other writer of clockOutNotes on this path is jbSaveTicket, which runs
        // in the best-effort STEP-2 try/catch below (TASK-489) — when that phase
        // is skipped, the driver's own words were stranded in `notes`, invisible
        // on the ticket (64 of 114 stranded TITS entries). Mirror the note HERE,
        // in the same write that completes the entry, so it never depends on the
        // ticket phase surviving. Guarded truthy so an empty box can't null out
        // a draft the driver saved mid-shift.
        ...(shiftNotes ? { clockOutNotes: shiftNotes, draftShiftNotes: shiftNotes } : {}),
        updatedAt: Timestamp.now(),
        ...(outLoc ? { clockOutLat: outLoc.lat, clockOutLng: outLoc.lng, clockOutAccuracy: outLoc.accuracy } : {}),
        ...outGeo,
        // TASK-9264 — the driver's "materials used" claim rides the write that
        // completes his own entry (offline-safe). {} unless he changed it;
        // never throws (see _jbConsumablesUpdate).
        ..._jbConsumablesUpdate()
      };
      // clock-dst-all — ticket times left unsaved: the punch still saves, flagged,
      // with WHAT was not saved (typed text + reason) for the office.
      const _jbUnsaved = [];
      [['equipment', JobBoardState.btEquipment], ['downtime', JobBoardState.btDowntime], ['standby', JobBoardState.btStandby]].forEach(([type, rows]) => (rows || []).forEach((r) => {
        if (!r || !_jbRowUnresolved(r)) return;
        _jbUnsaved.push({ type, name: String(r.assetName || '').slice(0, 60) || null, start: String(r.startTime || '').slice(0, 16), end: String(r.endTime || '').slice(0, 16), notes: String(r.notes || '').slice(0, 120) || null });
      }));
      if (_jbTicketTimesProblem || _jbUnsaved.length) {
        clockOutUpdate.needsReview = true;
        clockOutUpdate.ticketTimesUnsaved = { rows: _jbUnsaved.slice(0, 20), reasons: [String(_jbTicketTimesProblem || 'a ticket time could not be read').slice(0, 200)], at: Timestamp.now() };
      }
      // Persist clock-in adjustment
      if (JobBoardState._btEditedClockIn) {
        clockOutUpdate.clockIn = Timestamp.fromDate(clockInTime);
        clockOutUpdate.adjustedClockIn = Timestamp.fromDate(clockInTime);
      }
      // TASK-1180 — forgotten-clock-out shrink (jbApplyForgotClockOut): stamp
      // the existing audit convention (adjustedClockOut mirrors adjustedClockIn)
      // plus the review flags the Workforce weekly table / reports / payroll
      // CSVs now surface — a shrunk-to-normal entry must NOT vanish from review.
      if (JobBoardState._btDriverAdjustedClockOut && JobBoardState._btEditedClockOut) {
        clockOutUpdate.adjustedClockOut = Timestamp.fromDate(clockOutTime);
        clockOutUpdate.driverAdjustedClockOut = true;
        clockOutUpdate.needsReview = true;
        if (JobBoardState._btDriverAdjustReason) {
          clockOutUpdate.driverAdjustReason = JobBoardState._btDriverAdjustReason;
        }
      }
      // TASK-1575 — snapshot the entry BEFORE the clock-out write so an adjustment
      // made on this path (clock-in edit and/or driver clock-out shrink) can be
      // audited. `entry` (_btEntry) still holds the pre-write clock values here.
      const _coBefore = { ...entry };
      const _coClockInEdited = !!JobBoardState._btEditedClockIn;
      const _coClockOutAdjusted = !!(JobBoardState._btDriverAdjustedClockOut && JobBoardState._btEditedClockOut);
      let _coPhoneClockOut = false; // TASK-10014 — set when the clock-out went in on the phone clock
      try {
        // TASK-1642 — the clock-out write hangs offline exactly like the ticket
        // writes do, and it runs BEFORE any of them: unbounded, this alone spun
        // the Clock Out button forever. Bounded, the entry is applied locally,
        // queued for the server, and the driver keeps moving.
        // TASK-1483 — the claim rides the SAME write that completes the entry.
        await jbWriteWithVehicleClaim(entryRef, clockOutUpdate, _jbPvFields, 'clockEntry:clockOut');
        // TASK-1575 — audit driver-side time ADJUSTMENTS on clock-out (a routine
        // clock-out with no edit is the normal lifecycle and is NOT logged; only a
        // clock-in or clock-out time adjustment writes a row). Same single helper,
        // reached via window.ClockModule (Pitfall #1). Builds the after-snapshot
        // from concrete Timestamps — never the serverTimestamp() sentinel in
        // clockOutUpdate — so the stored snapshot holds real values. Fire-and-forget:
        // the helper strips undefined + swallows failure, so it never traps a driver.
        if (_coClockInEdited || _coClockOutAdjusted) {
          const _coAfter = { ..._coBefore, status: 'completed', hours, totalHours: hours };
          if (_coClockInEdited) {
            _coAfter.clockIn = Timestamp.fromDate(clockInTime);
            _coAfter.adjustedClockIn = Timestamp.fromDate(clockInTime);
          }
          if (_coClockOutAdjusted) {
            // clockOut is a tracked field — record the actual clockOut change
            // (active/null -> edited timestamp), not just adjustedClockOut, so the
            // audit row shows WHAT the clock-out became. Safe here because
            // _coClockOutAdjusted implies _btEditedClockOut, so the persisted
            // clockOut is this concrete Timestamp, never the serverTimestamp()
            // sentinel (which we still omit in the clock-in-only branch above).
            _coAfter.clockOut = Timestamp.fromDate(clockOutTime);
            _coAfter.adjustedClockOut = Timestamp.fromDate(clockOutTime);
          }
          window.ClockModule?.logClockEntryChange?.(
            tenantId, entryId, _coBefore, _coAfter, 'edit', 'driver-clockout-adjust'
          );
        }
        // TASK-1575 — the clock-in adjustment is now PERSISTED (6631-6633) and
        // AUDITED above. submitClockOut calls jbSaveTicket() below (ticket mode),
        // whose step 8 re-checks _btEditedClockIn and would re-write + DOUBLE-LOG
        // the same clock-in change against a stale pre-write snapshot. Clear the
        // flag here so jbSaveTicket agrees — exactly what the skew fallback already
        // does at :6714-6715 ("clear the edit state so jbSaveTicket agrees"); the
        // main path simply never needed to before there was an audit row to dupe.
        JobBoardState._btEditedClockIn = null;
      } catch (updErr) {
        // TASK-1180 — skew fallback: an EDITED clockOut can trip the new rules
        // bound only when the device clock runs >10min fast AND the driver
        // picked a near-"now" time (a genuine shrink lands hours in the past
        // and can never trip it). Never trap the driver: retry ONCE with a
        // server-stamped clockOut, drop the time edits, and flag for review so
        // the office applies the intended correction manually.
        if (updErr?.code !== 'permission-denied') throw updErr;
        // TASK-10014 — the un-edited clock-out can be refused too: see
        // _jbPhoneClockOutRetry. It is also the last resort when the TASK-1180
        // server-time retry below is refused for the same reason.
        if (!JobBoardState._btEditedClockOut) {
          const _ph = await _jbPhoneClockOutRetry({ entryRef, clockInMs, pvFields: _jbPvFields, base: { notes: shiftNotes, shiftNotes, outLoc, outGeo }, droppedEdit: _coClockInEdited ? `clock-in ${clockInTime.toLocaleString('en-US')}` : null, carry: _jbReviewCarry(clockOutUpdate) });
          clockInTime = _ph.clockInTime; clockOutTime = _ph.clockOutTime; hours = _ph.hours;
          _coPhoneClockOut = true;
          JobBoardState._btEditedClockIn = null;
        } else {
        console.warn('[JobBoard] Edited clockOut rejected by rules bounds — retrying with serverTimestamp', updErr?.code);
        clockOutTime = new Date();
        hours = parseFloat(((clockOutTime - (new Date(clockInMs))) / 3600000).toFixed(2));
        clockInTime = new Date(clockInMs);
        const _reqOut = JobBoardState._btEditedClockOut;
        const _reqIn = _coClockInEdited ? JobBoardState._btEditedClockIn : null; // TASK-10014 COD r2
        try {
        // TASK-1483 — carry the vehicle claim through the retry too (once the
        // entry completes, the driver can no longer add it — rules freeze it).
        await jbWriteWithVehicleClaim(entryRef, {
          clockOut: serverTimestamp(),
          status: 'completed',
          totalHours: hours,
          hours: hours,
          notes: shiftNotes,
          // TASK-4621 — same clockOutNotes mirror as the main clock-out write
          // above; the skew retry completes the same entry and must not strand
          // the note either.
          ...(shiftNotes ? { clockOutNotes: shiftNotes, draftShiftNotes: shiftNotes } : {}),
          updatedAt: Timestamp.now(),
          needsReview: true,
          driverAdjustReason: `${JobBoardState._btDriverAdjustReason ? JobBoardState._btDriverAdjustReason + ' — ' : ''}Driver requested clock-out ${_reqOut.toLocaleString('en-US')} but the device time was rejected; server time recorded. Correct the entry in Workforce.`,
          ...(outLoc ? { clockOutLat: outLoc.lat, clockOutLng: outLoc.lng, clockOutAccuracy: outLoc.accuracy } : {}),
          ...outGeo,
          // TASK-10014 COD r2 — keep the STEP-1 ticket-time marks on this retry too.
          ..._jbReviewCarry(clockOutUpdate)
        }, _jbPvFields, 'clockEntry:clockOutSkewRetry');
        } catch (srvErr) {
          if (srvErr?.code !== 'permission-denied') throw srvErr;
          // TASK-10014 — server time is BEFORE this phone's clock-in (fast
          // phone, short shift): the server-time retry is refused for the
          // same reason. Fall back to the phone clock, edits dropped + flagged.
          const _ph = await _jbPhoneClockOutRetry({ entryRef, clockInMs, pvFields: _jbPvFields, base: { notes: shiftNotes, shiftNotes, outLoc, outGeo }, droppedEdit: `${_reqIn ? `clock-in ${_reqIn.toLocaleString('en-US')} and ` : ''}clock-out ${_reqOut.toLocaleString('en-US')}${JobBoardState._btDriverAdjustReason ? ' (' + JobBoardState._btDriverAdjustReason + ')' : ''}`, carry: _jbReviewCarry(clockOutUpdate) });
          clockInTime = _ph.clockInTime; clockOutTime = _ph.clockOutTime; hours = _ph.hours;
          _coPhoneClockOut = true;
        }
        // Ticket lines below follow the ACTUAL recorded window, not the
        // rejected edit — clear the edit state so jbSaveTicket agrees.
        JobBoardState._btEditedClockOut = null;
        JobBoardState._btEditedClockIn = null;
        }
      }

      // TASK-9190 — the entry is completed and the driver is free; only now do
      // we make the stored clock WINDOW agree with the hours it just recorded.
      // Gated to the two SERVER-stamped shapes: an edited clockOut is a concrete
      // device Timestamp the driver chose (nothing to reconcile against), and
      // the skew retry above clears that flag, so `!_btEditedClockOut` here
      // means clockOut went in as serverTimestamp(). `clockOutTime` is the exact
      // device reading `hours` was computed from (the retry reassigns it), and
      // using the same reading is what makes the reconciliation arithmetic
      // close. A clock-in the driver EDITED in this modal is excluded outright —
      // that time is a human choice, not a device stamp (the value-bound marker
      // would refuse it anyway; this says so where a reader will look).
      // Deliberately NOT awaited — a driver never waits on it.
      // TASK-10014 — a phone-clock clock-out has no server stamp to measure
      // against (it would read ~0 skew); the entry already carries the flag.
      if (!JobBoardState._btEditedClockOut && !JobBoardState._btWriteUnacked && !_coClockInEdited && !_coPhoneClockOut) {
        _jbReconcileClockSkew({
          tenantId, entryId, entryRef, deviceClockOutMs: clockOutTime.getTime()
        });
      }
    }

    // ── STEP 2: Build/attach the ticket line (BEST-EFFORT) ───────────────
    // Wrapped so a ticketLines rule denial (or any write failure) can no longer
    // block the already-completed clock-out above. On failure the driver is
    // clocked out cleanly and dispatch finalizes the line.
    let _ticketLineFailed = false;
    try {
      // Find or create ticket if in ticket mode.
      // Route through window.ClockModule.findOrCreateTicket so two concurrent
      // clock-outs (double-tap, multi-tab, retry) serialize on the same
      // customer+job+date lock and hit the 4-tier dedup against dispatcher-side
      // tickets — and so the new ticket gets originalJobId/originalCustomerId/
      // originalWorkDate stamped for future matching. The one jobboard-specific
      // field left, source='clock_out', flows into the single addDoc via
      // extraFields — no post-create update, no partial-failure window.
      // (TASK-1644 removed notes/workPerformed from that set — see the block at
      // the create call below. jbEnsureTicketId at :5346 already passed a bare
      // { source: 'clock_out' }, so all three clock-out create sites now agree.)
      let ticketId = JobBoardState._btTicketId;
      // TASK-1642 — same reason as jbEnsureTicketId: with no signal
      // findOrCreateTicket cannot run its server-side dedup, so minting a ticket
      // here risks a duplicate on reconnect. Leave it to dispatch; the clock-out
      // above is already recorded and queued.
      if (_ticketMode && customerId && !ticketId && jbNetLooksOffline()) {
        JobBoardState._btWriteUnacked = true;
        throw new Error('offline: ticket not started');
      }
      if (_ticketMode && customerId && !ticketId) {
        // TASK-1644 — do NOT seed the customer-facing invoice-notes field from
        // the shift note. `#jb-shift-notes` is a PER-OPERATOR, FIRST-PERSON
        // record by deliberate product standard (TASK-1327), while
        // `ticket.workPerformed` prints on the customer invoice — three billing
        // renderers plus the server-side PDF read it. Auto-copying put one
        // driver-voice paragraph onto the document the customer pays against.
        // Chris, 2026-08-18: "I wouldn't auto copy shift notes."
        //
        // Nothing is lost. The shift note is written to the clock entry by the
        // STEP-1 clockOutUpdate itself (`notes` + `clockOutNotes` +
        // `draftShiftNotes` — TASK-4621; before that fix only jbSaveTicket
        // wrote clockOutNotes, and this comment was false whenever the
        // best-effort ticket phase was skipped) and again to the entry by
        // jbSaveTicket, which submitClockOut calls further down this same
        // function (TASK-9693: never to the shared ticket doc). The customer-facing box
        // (`#jb-ticket-notes`) has its own write path —
        // window.ClockModule.saveInvoiceNotes (TASK-1397, reached through
        // `window` and never a ?v= import) — which is SHARED across the crew:
        // whoever types there is seen by the next person who opens Build
        // Ticket. That shared box is where the work description belongs, and
        // it stays empty until a human writes one.
        //
        // Empty is safe downstream: every workPerformed renderer is
        // truthy-guarded (billing.js :13391 / :26738 / :26744 / :27064,
        // app.js :4369, functions/src/services/pdf.js :765), so an unseeded
        // ticket OMITS the "Work Performed" section rather than printing an
        // empty one. The dispatcher-side create already behaves this way
        // (billing.js :23634 — "User enters notes — never auto-generate").
        const extra = { source: 'clock_out' };
        const ticketResult = await window.ClockModule.findOrCreateTicket(
          customerId,
          customerName,
          jobId || null,
          job?.title || job?.name || null,
          new Date(),
          tenantId,
          extra,
          // TASK-1221 — split ticket keyed on this entry's own assets + operator.
          { splitBy: job?.splitBy || 'combined', assetIds: Array.isArray(entry?.assetIds) ? entry.assetIds : [], personId: entry?.personId }
        );
        ticketId = ticketResult.ticketId;
        JobBoardState._btTicketId = ticketId;
      }

      // Save all form data to ticket (if ticket mode)
      if (_ticketMode && ticketId) {
        // Create employee line (only if not already created from a previous save).
        // TASK-355 2026-05-22: PIN -> SPA member identity resolution.
        const driverId = getActiveJobBoardPersonId();
        const driverName = getActiveJobBoardPersonName();
        const person = JobBoardState.people.find(p => p.id === driverId);
        const role = person?.crewType || person?.role || person?.roles?.[0] || 'NON_CDL';
        const linesRef = collection(db, 'tenants', tenantId, 'tickets', ticketId, 'ticketLines');

        // Check if employee line already exists for this clock entry
        // TASK-1642 — bounded. Without a definite answer here we cannot tell
        // create from update, and guessing risks a DUPLICATE EMPLOYEE line on
        // the ticket. Bail into the best-effort catch below instead: the
        // clock-out itself is already recorded and queued, and dispatch
        // finalizes the lines. Never a guess on a billable row.
        const empSnap = await jbBoundedRead(
          getDocs(query(linesRef, where('clockEntryId', '==', entryId), where('type', '==', 'EMPLOYEE'))),
          'ticketLines:existingEmployeeLine');
        if (!empSnap) {
          JobBoardState._btWriteUnacked = true;
          throw new Error('offline: could not read existing ticket lines');
        }
        let _empLineId;

        // TASK-490 — ATOMIC clock-out: write the EMPLOYEE line AND the clock
        // entry's pointer (ticketId/ticketLineId + billedTicketIds[]) in ONE
        // writeBatch. Previously the line was addDoc'd FIRST and the clock
        // entry's ticketId was updateDoc'd a step LATER (the "null-race"): the
        // onTicketLineWrite dedup/stray-guard trigger fired the instant the line
        // landed, saw clockEntry.ticketId still unset/stale, and could mis-decide.
        // Committing both in one batch closes that window — the guard never sees
        // a line without its pointer. (Per-line photos/equipment still flow
        // through jbSaveTicket below; only the pointer-establishing writes are
        // batched here.)
        const _batch = writeBatch(db);
        const _entryRef = doc(db, 'tenants', tenantId, 'clockEntries', entryId);
        const _ticketRef = doc(db, 'tenants', tenantId, 'tickets', ticketId);
        if (empSnap.empty) {
          const _empLineRef = doc(linesRef); // pre-allocate id so the batch can reference it
          _empLineId = _empLineRef.id;
          _batch.set(_empLineRef, {
            type: 'EMPLOYEE',
            personId: driverId, personName: driverName, role,
            hours, rate: 0, rateUnit: 'Hourly', lineAmount: 0,
            fromTime: Timestamp.fromDate(clockInTime),
            toTime: Timestamp.fromDate(clockOutTime),
            clockEntryId: entryId,
            createdAt: Timestamp.now()
          });
          _batch.update(_ticketRef, {
            employeeCount: increment(1), updatedAt: Timestamp.now()
          });
        } else {
          // Update existing employee line with final times
          _empLineId = empSnap.docs[0].id;
          _batch.update(doc(db, 'tenants', tenantId, 'tickets', ticketId, 'ticketLines', _empLineId), {
            hours, fromTime: Timestamp.fromDate(clockInTime), toTime: Timestamp.fromDate(clockOutTime),
            updatedAt: Timestamp.now()
          });
        }

        // Link ticket AND line back to the clock entry, IN THE SAME BATCH.
        // ticketLineId was never written here before (dispatch.js:16263 does) —
        // its absence left completed jobboard entries unlinked and forced manual
        // rebuilds. billedTicketIds[] (TASK-490) is the append-only history the
        // stray-guard reads for observability; ticketId stays the live pointer.
        _batch.update(_entryRef, {
          ticketId,
          billedTicketIds: arrayUnion(ticketId),
          ...(_empLineId ? { ticketLineId: _empLineId } : {})
        });
        await jbAckOrQueue(_batch.commit(), 'clockOut:employeeLineBatch');

        // TASK-572 — final authority: any equipment/downtime/standby entry
        // still tracking the clock window follows the FINAL clock times
        // (edited or submit-time now) before the lines are written, so the
        // EQUIPMENT line can never bill a different window than the EMPLOYEE
        // line written above from the same clockInTime/clockOutTime. Also
        // covers slow-modal drift when no edit was made (line endTime was
        // frozen at modal-open `now`, employee toTime is submit-time `now`).
        _jbSyncClockTrackedLines(clockInTime, clockOutTime);

        // Save equipment, downtime, standby, photos, notes via jbSaveTicket logic
        const _nestedSaveOk = await jbSaveTicket({ nested: true }); // TASK-4641 — the verdict is USED now, not discarded // TASK-1642 — keep this clock-out's network verdict
        // A refused nested save must never be papered over by the clock-out toast.
        if (_nestedSaveOk === false) _ticketLineFailed = true;
      }
    } catch (lineErr) {
      _ticketLineFailed = true;
      console.error('[JobBoard] Clocked out OK but ticket-line build failed:', lineErr?.code || lineErr?.message);
      try { if (typeof logClockAudit === 'function') logClockAudit(tenantId, 'CLOCK_OUT_LINE_FAIL', { source: 'jobboard', clockEntryId: entryId, reason: lineErr?.code || lineErr?.message }); } catch (_e) {}
      // TASK-4666 — a DEAD-signal clock-out throws HERE before jbSaveTicket ever
      // runs (the employee-line bounded read, or "ticket not started"), so the
      // TASK-4594 stash inside the merge-save never fires and the typed section
      // rows died with closeClockOutModal() below — no stash, no breadcrumb.
      // The modal DOM is still alive at this point: collect and stash now.
      // Harmless when the merge-save DID land before a later step threw — the
      // recovery pass is strictly additive, finds every row already on the
      // server, and clears this stash silently.
      try {
        const _coTicketId = JobBoardState._btTicketId;
        if (_ticketMode && _coTicketId && JobBoardState._btCsdStashed !== true) {
          const _coCollected = jbCollectCustomSectionData();
          if (_jbStashCsd(_coTicketId, _coCollected)) {
            JobBoardState._btCsdStashed = true;
            _jbQueueAbandonBreadcrumb({
              ticketId: _coTicketId,
              sections: Object.keys(_coCollected || {}).length,
              instances: Object.values(_coCollected || {})
                  .reduce((n, s) => n + ((s && s.instances) ? s.instances.length : 0), 0),
              stashedLocally: true,
              via: 'clockout-line-fail',
            });
          }
        }
      } catch (_e2) { /* stash is best-effort — never block the clock-out */ }
    }

    // TASK-1642 — say which of the three things actually happened. "No signal"
    // outranks everything: the driver IS clocked out on this device, the write is
    // queued, and the one thing they must not do is close the page or redo it.
    // TASK-4641 — refusals recorded by the nested jbSaveTicket (signatures,
    // stamp, total). NOT signal problems: retrying does not fix them, so they
    // never join _btNeedsSignal — they are said in full, in both arms below.
    const _coRefused = JobBoardState._btPartialProblems || [];
    if (JobBoardState._btWriteUnacked) {
      // Closing this modal drops anything Cloud Storage could not take (photos,
      // signature, stamp) — say it plainly and point at the only person who can
      // fix it, rather than implying it will catch up on its own like the times do.
      const _lost = JobBoardState._btNeedsSignal || [];
      window.showToast?.(
        `No signal — clocked out on this phone, ${hours} hours. Keep this page open and it sends when you get bars.`
        // Keep the pre-existing "dispatch will finish it" line — the no-signal
        // branch must not swallow the fact that no ticket lines got built.
        + (_ticketLineFailed ? ' Dispatch will finish the ticket.' : '')
        + (_lost.length ? ` Your ${_lost.join(' and ')} did NOT send — tell dispatch.` : '')
        + (_coRefused.length ? ' ' + _coRefused.join(' ') : ''),
        'warning', null, 14000
      );
    } else {
      // 🔴 TASK-4641 — a green "Ticket built — 8 hours" used to fire here even
      // when the nested save's signature write had just been REFUSED, because
      // jbPersistSignatures is non-fatal and said its piece in a toast of its
      // own. Same defect as the Save button's "Ticket data saved", same rule: a
      // refusal always owns the message, and it is never green.
      const _coBase = _ticketLineFailed
        ? `Clocked out — ${hours} hours. Ticket details will be finalized by dispatch.`
        : (_ticketMode ? `Ticket built — ${hours} hours` : `Clocked out — ${hours} hours`);
      window.showToast?.(
        _coRefused.length ? `${_coBase}. ${_coRefused.join(' ')}` : _coBase,
        (_ticketLineFailed || _coRefused.length) ? 'warning' : 'success',
        null, _coRefused.length ? 14000 : null
      );
    }
    // fix-1999-followups — flip the SPA Personal Actions tile back to CLOCK IN
    // (it re-reads this person's open shift; off Dispatch nothing else updates it).
    window._refreshPersonalClockLabel?.();
    // TASK-9694 S3 — inline job screen only: the clock-out above is recorded;
    // now mark this operator's ticket finished (server finalize, after the fact).
    if (modal.dataset.btInline === '1') {
      _jbJsAfterFinish({ entryId, jobId, hours, clockInMs: clockInTime.getTime(),
        ticketLineFailed: _ticketLineFailed, unacked: !!JobBoardState._btWriteUnacked });
    }
    closeClockOutModal();
  } catch (err) {
    console.error('[JobBoard] Clock out error:', err);
    window.logError?.(err, { module: 'jobboard', operation: 'CLOCK_OUT_SUBMIT' });
    // TASK-1642 — name the real failure. This modal is now THE clock-out surface
    // for drivers AND self-only staff (clock-out convergence), so a generic
    // "Error submitting" here strands the whole crew with nothing to act on.
    if (err?.code !== 'permission-denied') {
      window.showToast?.(`Clock-out did not go through — ${jbFriendlyErr(err)}. You are still clocked in; try again.`, 'error');
    } else {
      // TASK-10014 — a refusal here is the CLOCK-OUT itself (the ticket phase
      // is best-effort and never lands in this catch), after both retries.
      // "This login is not allowed" read as a lost permission in Guy's demo;
      // say what failed and what the driver can do.
      window.showToast?.('Clock-out didn\'t go through. You are still clocked in. Turn on "Set Automatically" in your phone\'s Date & Time settings and try again, or ask the office to clock you out.', 'error', null, 14000);
    }
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = `<span class="material-icons" style="font-size:20px;">logout</span> ${modal.dataset.btPrimaryLbl || 'Clock Out'}`;
    }
  }
}

// Legacy submit for ticket-only mode (startTicketFromBoard)
async function _submitClockOutLegacy(modal) {
  const jobId = modal.dataset.jobId;
  const jobDayId = modal.dataset.jobDayId;
  const entryId = modal.dataset.entryId || null; // TASK-427: per-session key for lines
  const tenantId = JobBoardState.tenantId;

  const notesInput = document.getElementById('clockout-notes');
  const notes = notesInput?.value?.trim() || null;
  // TASK-619 (Lacey) — require a shift note when the tenant opts in. This legacy
  // ticket-only path is currently dead (no live call site), but gate it anyway so
  // it's protected if ever rewired. Runs before any button-disable/write.
  if (window.AppState?.settings?.requireShiftNotes === true && !notes) {
    window.showToast?.('Add a shift note to clock out — what did you do today?', 'error');
    notesInput?.focus();
    return;
  }
  const hours = parseFloat(document.getElementById('ticketonly-hours')?.value) || 0;
  if (hours <= 0) {
    window.showToast?.('Please enter hours worked', 'error');
    return;
  }

  // TASK-428 slice 2 — defensive guard. This legacy ticket-only path
  // (startTicketFromBoard) currently has ZERO call sites (dead), but if it is
  // ever wired back up it builds ticketLines with clockEntryId = entryId. For a
  // driver-PIN session the firestore.rules driver-write gate requires a real
  // active clock entry; a null entryId would DENY every line. Surface a clear
  // error instead of silently failing the writes. (Members are unaffected — the
  // rule's member branch passes regardless.)
  const _isDriverScope = !!(window.AppState?.driverModePin || window.AppState?.driverMode || window.AppState?.isPublicAccess);
  if (_isDriverScope && !entryId) {
    window.showToast?.('Clock in to this job before building its ticket.', 'error');
    return;
  }

  const btn = document.getElementById('btn-clockout-submit');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Submitting...';
  }

  try {
    const job = JobBoardState.jobs.find(j => j.id === jobId);
    const customerId = job?.customerId || null;
    const customerName = getCustomerName(customerId) || null;

    if (customerId) {
      // Route through window.ClockModule.findOrCreateTicket for the serializing
      // lock + 4-tier dedup + original* fields. Jobboard-specific fields flow
      // into the single addDoc via extraFields — see clock.js findOrCreateTicket.
      // TASK-1644 — this seed is DELIBERATELY LEFT IN PLACE, unlike the
      // clock-out path above. Different source, different treatment:
      //   * submitClockOut seeds from `#jb-shift-notes`, a first-person
      //     per-operator record that sits ALONGSIDE a separate customer-facing
      //     box. Copying one into the other was pure leakage — removed.
      //   * this legacy ticket-only modal has exactly ONE textarea
      //     (`#clockout-notes`, :9081), labelled "Notes" with the placeholder
      //     "Work performed, conditions, notes..." — customer-facing framing,
      //     and the only sink for the text. Dropping the seed here would not
      //     protect the invoice; it would SILENTLY DISCARD whatever the user
      //     typed, and the requireShiftNotes gate at :7255 would force them to
      //     type into a black hole.
      // The path is also dead (startTicketFromBoard is exported but has zero
      // call sites), so this is a latent shape, not live behaviour. If it is
      // ever revived it needs the two-box split the live modal has: a
      // first-person shift-notes field that stays on the clock entry, and a
      // shared invoice-notes field routed through
      // window.ClockModule.saveInvoiceNotes. Do NOT rewire it by deleting
      // these two lines alone.
      const extra = { source: 'ticket_only' };
      if (notes) {
        extra.notes = notes;
        extra.workPerformed = notes;
      }
      // TASK-1221 — split ticket keyed on the entry's own assets + operator.
      const _legacyEntry = JobBoardState._btEntry;
      const ticketResult = await window.ClockModule.findOrCreateTicket(
        customerId,
        customerName,
        jobId || null,
        job?.title || job?.name || null,
        new Date(),
        tenantId,
        extra,
        { splitBy: job?.splitBy || 'combined', assetIds: Array.isArray(_legacyEntry?.assetIds) ? _legacyEntry.assetIds : [], personId: _legacyEntry?.personId }
      );
      const ticketId = ticketResult.ticketId;

      const linesRef = collection(db, 'tenants', tenantId, 'tickets', ticketId, 'ticketLines');
      // TASK-355 2026-05-22: PIN -> SPA member identity resolution.
      const driverId = getActiveJobBoardPersonId();
      const driverName = getActiveJobBoardPersonName();
      const person = JobBoardState.people.find(p => p.id === driverId);
      const role = person?.crewType || person?.role || person?.roles?.[0] || 'NON_CDL';

      await addDoc(linesRef, {
        type: 'EMPLOYEE', personId: driverId, personName: driverName, role,
        hours, rate: 0, rateUnit: 'Hourly', lineAmount: 0,
        clockEntryId: entryId, // TASK-427: per-session key
        createdAt: Timestamp.now()
      });
      await updateDoc(doc(db, 'tenants', tenantId, 'tickets', ticketId), {
        // TASK-4699 — the legacy clock-out submit: it has just written this
        // driver's EMPLOYEE line and is about to write their equipment lines.
        // Crew and hours both show on the customer's copy.
        employeeCount: increment(1),
        ticketEmailDirtyAt: Timestamp.now(), updatedAt: Timestamp.now()
      });

      // Equipment from checkboxes
      const equipCheckboxes = modal.querySelectorAll('.clockout-equip-cb:checked');
      for (const cb of equipCheckboxes) {
        const eqType = cb.dataset.type;
        const eqId = cb.dataset.id;
        const eqList = eqType === 'truck' ? JobBoardState.trucks : JobBoardState.trailers;
        const eq = eqList.find(e => e.id === eqId);
        if (!eq) continue;
        const eqName = window.getUnitDisplay ? window.getUnitDisplay(eq) : (eq.unit || eq.name || '');
        await addDoc(linesRef, {
          type: 'EQUIPMENT', assetType: eqType, assetId: eqId, assetName: eqName,
          fleetNumber: eq.unit || eq.fleetNumber || '', vehicleType: eq.type || '',
          hours, rate: 0, rateUnit: 'Hourly', lineAmount: 0, includeOnInvoice: true,
          ratePending: true, // TASK-9646 S5 — the server prices it from the price book
          clockEntryId: entryId, // TASK-427: per-session key
          createdAt: Timestamp.now()
        });
      }

      // Downtime
      const dtRows = modal.querySelectorAll('.clockout-dt-row');
      for (const row of dtRows) {
        const dtHours = parseFloat(row.querySelector('.dt-hours')?.value) || 0;
        const dtNotes = row.querySelector('.dt-notes')?.value?.trim() || null;
        if (dtHours <= 0 && !dtNotes) continue;
        await addDoc(linesRef, {
          type: 'DOWNTIME', hours: dtHours, rate: 0, lineAmount: 0, notes: dtNotes,
          clockEntryId: entryId, // TASK-428 slice 2: bind for driver-write rule gate
          createdAt: Timestamp.now()
        });
      }
    }

    window.showToast?.(`Ticket submitted — ${hours} hours`, 'success');
    closeClockOutModal();
  } catch (err) {
    console.error('[JobBoard] Ticket submit error:', err);
    window.showToast?.('Error submitting. Please try again.', 'error');
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">receipt_long</span> Submit Ticket';
    }
  }
}

function toLocalDatetimeString(date) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

// ============================================
// DRIVER EXPENSE CAPTURE
// ============================================
// Hardcoded defaults. Tenants can override / remove via
// `settings.driverExpenses.categories[]` — see `getActiveExpenseCategories()`
// below. Mirrored server-side as `DRIVER_EXPENSE_CATEGORIES` (functions/src/index.js).
// Lacey @ TITS asked to remove "tolls" 2026-05-14 — this enables that.
const EXPENSE_CATEGORIES = [
  { value: 'fuel', label: 'Fuel', icon: 'local_gas_station', hasFuelFields: true },
  { value: 'def', label: 'DEF / Fluids', icon: 'water_drop', hasVehicle: true },
  { value: 'parts', label: 'Parts / Supplies', icon: 'build', hasVehicle: true },
  { value: 'tolls', label: 'Tolls', icon: 'toll' },
  { value: 'meals', label: 'Meals', icon: 'restaurant' },
  { value: 'materials', label: 'Materials', icon: 'inventory_2' },
  { value: 'lodging', label: 'Lodging', icon: 'hotel' },
  { value: 'other', label: 'Other', icon: 'more_horiz' },
];

/**
 * Resolves the active category list for the current tenant.
 * Lookup order:
 *   1) `tenant.settings.driverExpenses.categories[]` if set + non-empty
 *      (rows are { value, label, icon? } — admin-curated)
 *   2) Hardcoded `EXPENSE_CATEGORIES` (default for tenants who have not
 *      customized yet)
 *
 * Empty arrays still fall back to the default — saves an admin from
 * accidentally locking themselves out by deleting all rows.
 *
 * Custom rows keep parity with the hardcoded fields: fuel = hasFuelFields,
 * def/parts = hasVehicle. We can't infer those from a custom row, so we
 * merge metadata from the hardcoded `EXPENSE_CATEGORIES` when the `value`
 * matches. New custom categories default to a plain (no-fuel-fields,
 * no-vehicle) row.
 */
function getActiveExpenseCategories() {
  const tenantCats = window.AppState?.settings?.driverExpenses?.categories;
  if (Array.isArray(tenantCats) && tenantCats.length > 0) {
    return tenantCats
      .filter(c => c && c.value && c.label)
      .map(c => {
        const baseline = EXPENSE_CATEGORIES.find(b => b.value === c.value) || {};
        return {
          value: String(c.value),
          label: String(c.label),
          icon: c.icon || baseline.icon || 'more_horiz',
          hasFuelFields: baseline.hasFuelFields || false,
          hasVehicle: baseline.hasVehicle || false,
        };
      });
  }
  return EXPENSE_CATEGORIES;
}

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

// Module-level expense context (set when modal opens, used by submitExpense)
let _expenseContext = null;

// TASK-9593 — the "Other / not listed" option value. Never a registry id, never
// sent as paymentCardId: submit turns it into paymentCardUnlistedLast4.
const EXPENSE_CARD_OTHER = '__other__';

// ─────────────────────────────────────────────────────────────────────────────
// TASK-10141 — "Paid with: Charge account at the vendor" (Chris 10/27).
// A purchase put on the company's account at a vendor: billed later on the
// vendor's statement, so it is neither a company-card charge nor money the
// driver is owed. The vendors offered are EXACTLY the CRM vendors flagged as a
// charge account (customers.chargeAccount, set only in CRM → vendor → "Charge
// account"; crew sessions read it off customerDirectory). The "Paid with" row
// stays hidden — and the form byte-for-byte what it was — until the company
// has at least one. A vendor typed under "Not listed" is recorded as "charge
// account, vendor not set up" for the office to link; nothing here creates a
// CRM vendor. The server (createDriverExpense / createMemberExpense /
// createExpenseAdmin via services/chargeAccounts.js) re-checks the vendor and
// refuses a charge account together with a card or a reimbursement.
// ─────────────────────────────────────────────────────────────────────────────
const EXPENSE_CHARGE_OTHER = '__charge_other__';
let _expenseChargeVendors = [];
let _expenseChargeGen = 0;
let _expensePendingChargeHint = null;

// Same normalisation as functions/src/services/billerQuestions.js vendorKey.
function _chargeVendorKey(name) {
  return String(name || '').normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

// The ONE charge-account vendor a receipt's vendor name points at, or null.
// Exact key first; else a single vendor whose name contains / is contained in
// the receipt's (3+ characters). Two candidates = null — never guess.
function _matchChargeVendor(name, vendors) {
  const k = _chargeVendorKey(name);
  if (!k || !Array.isArray(vendors) || !vendors.length) return null;
  const exact = vendors.filter(v => v && _chargeVendorKey(v.name) === k);
  if (exact.length) return exact.length === 1 ? exact[0] : null;
  const part = vendors.filter((v) => {
    const vk = v ? _chargeVendorKey(v.name) : '';
    return vk.length >= 3 && k.length >= 3 && (k.includes(vk) || vk.includes(k));
  });
  return part.length === 1 ? part[0] : null;
}

// [{ id, name }] of active charge-account vendors, sorted by name. Crew
// sessions read customerDirectory, the office reads customers; a refusal on
// one tries the other (same order as utils/customerSource.js). Any other
// failure = [] (the row just stays hidden).
async function _loadExpenseChargeVendors(ctx) {
  if (Array.isArray(ctx && ctx.chargeVendors)) return ctx.chargeVendors;
  const tid = (ctx && ctx.tenantId) || JobBoardState.tenantId || window.AppState?.tenant;
  if (!tid) return [];
  const core = window.CustomerSourceCore;
  const dirFirst = core && typeof core.sessionReadsDirectory === 'function' ? core.sessionReadsDirectory(window.AppState) : true;
  const order = dirFirst ? ['customerDirectory', 'customers'] : ['customers', 'customerDirectory'];
  const vendorLike = (c) => (window.BillableCustomer && typeof window.BillableCustomer.isVendorLikeCustomer === 'function')
    ? window.BillableCustomer.isVendorLikeCustomer(c)
    : ['vendor', 'subcontractor'].includes(String((c && c.type) || '').trim().toLowerCase());
  for (const col of order) {
    try {
      const snap = await getDocs(query(collection(db, 'tenants', tid, col), where('chargeAccount', '==', true)));
      return snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }))
        .filter(c => c.chargeAccount === true && vendorLike(c) && c.active !== false && String(c.status || '').trim().toLowerCase() !== 'inactive')
        .map(c => ({ id: c.id, name: String(c.name || c.company || '').trim() }))
        .filter(c => c.name)
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch (e) {
      if (!/permission/i.test(String((e && (e.code || e.message)) || ''))) {
        console.warn('[Expense] charge-account vendors did not load', e && e.message);
        return [];
      }
    }
  }
  return [];
}

// 'company' | 'charge' | 'pocket' while the Paid-with row is showing, else null.
function _expensePaidWith() {
  const field = document.getElementById('expense-paidwith-field');
  if (!field || field.style.display === 'none') return null;
  return document.querySelector('input[name="expense-paidwith"]:checked')?.value || 'company';
}

function _bindExpensePaidWith(ctx) {
  const field = document.getElementById('expense-paidwith-field');
  const chargeField = document.getElementById('expense-charge-field');
  const sel = document.getElementById('expense-charge-vendor');
  const otherWrap = document.getElementById('expense-charge-other-wrap');
  const reimb = document.getElementById('expense-reimbursement');
  if (!field || !chargeField || !sel || !otherWrap || !reimb) return;
  const gen = ++_expenseChargeGen;
  _expensePendingChargeHint = null;
  const syncOther = () => { otherWrap.style.display = sel.value === EXPENSE_CHARGE_OTHER ? '' : 'none'; };
  const apply = () => {
    const v = document.querySelector('input[name="expense-paidwith"]:checked')?.value || 'company';
    // Out of pocket drives the existing reimbursement box, so the card picker
    // and the reimbursement flow behave exactly as when it is ticked by hand.
    const wantReimb = v === 'pocket';
    if (reimb.checked !== wantReimb) {
      reimb.checked = wantReimb;
      reimb.dispatchEvent(new Event('change'));
    }
    const cardWrap = document.getElementById('expense-card-field');
    const cardSel = document.getElementById('expense-card-select');
    if (cardWrap && cardSel) {
      if (v === 'charge') {
        cardWrap.style.display = 'none';
        if (cardSel.value) { cardSel.value = ''; cardSel.dispatchEvent(new Event('change')); }
      } else if (v === 'company' && cardWrap.style.display === 'none') {
        cardWrap.style.display = '';
        cardSel.value = cardWrap.dataset.defaultCardId || '';
        cardSel.dispatchEvent(new Event('change'));
      }
    }
    chargeField.style.display = v === 'charge' ? '' : 'none';
    syncOther();
  };
  field._applyPaidWith = apply;
  sel.addEventListener('change', syncOther);
  field.querySelectorAll('input[name="expense-paidwith"]').forEach((r) => {
    r.addEventListener('change', () => { field.dataset.touched = '1'; apply(); });
  });
  _loadExpenseChargeVendors(ctx).then((list) => {
    if (gen !== _expenseChargeGen || !document.body.contains(field)) return;
    _expenseChargeVendors = Array.isArray(list) ? list : [];
    if (!_expenseChargeVendors.length) return; // no charge accounts: form unchanged
    sel.innerHTML = '<option value="">— Pick the vendor —</option>'
      + _expenseChargeVendors.map(v => `<option value="${escapeHtml(String(v.id))}">${escapeHtml(String(v.name))}</option>`).join('')
      + `<option value="${EXPENSE_CHARGE_OTHER}">Not listed — type the vendor</option>`;
    // A reimbursement box ticked before the list landed carries over.
    const start = reimb.checked ? 'pocket' : 'company';
    const radio = field.querySelector(`input[name="expense-paidwith"][value="${start}"]`);
    if (radio) radio.checked = true;
    field.style.display = '';
    const reimbField = document.getElementById('expense-reimb-field');
    if (reimbField) reimbField.style.display = 'none';
    apply();
    if (_expensePendingChargeHint) _applyExpenseChargeHint(_expensePendingChargeHint);
  }).catch(() => {});
}

// Smart Scan read the receipt: pre-pick the matching charge-account vendor, and
// pre-select "Charge account" when the reader says it was charged on account —
// only while the driver has not chosen Paid-with himself. A suggestion; he can
// change it. A charge at a vendor that is not set up pre-fills "Not listed".
function _applyExpenseChargeHint(data) {
  const field = document.getElementById('expense-paidwith-field');
  if (!field) return;
  if (field.style.display === 'none') { _expensePendingChargeHint = data || null; return; }
  _expensePendingChargeHint = null;
  if (!data) return;
  const sel = document.getElementById('expense-charge-vendor');
  const isCharge = data.paymentType === 'charge';
  const match = data.vendor ? _matchChargeVendor(data.vendor, _expenseChargeVendors) : null;
  if (sel && !sel.value) {
    if (match) sel.value = match.id;
    else if (isCharge && data.vendor) {
      sel.value = EXPENSE_CHARGE_OTHER;
      const other = document.getElementById('expense-charge-other');
      if (other && !other.value) other.value = String(data.vendor).slice(0, 100);
    }
  }
  // COD r1 (Edge): only from the untouched default — never over "Out of pocket"
  // (which may have been ticked on the reimbursement box before the list landed).
  const _cur = document.querySelector('input[name="expense-paidwith"]:checked')?.value || 'company';
  if (isCharge && field.dataset.touched !== '1' && _cur === 'company') {
    const radio = field.querySelector('input[name="expense-paidwith"][value="charge"]');
    if (radio) radio.checked = true;
  }
  if (typeof field._applyPaidWith === 'function') field._applyPaidWith();
}

// TASK-9593 — who gets the OWN-CARDS-ONLY picker. Mirrors the server exactly:
// a driver-scope session (createDriverExpense always checks assignment), or a
// signed-in member who routes to createMemberExpense WITHOUT the `billing`
// module (that callable checks assignment for them). Owner, legacy admin
// (admin with no modules list) and billing holders are office/AP and keep every
// company card. A member with no member doc (founder via ownerId) is office too.
function _expenseCardPickerOwnOnly() {
  const AS = window.AppState || {};
  if (AS.driverModePin || AS.isPublicAccess || AS.driverMode) return true;
  const ma = AS.memberAccess;
  if (!ma || ['revoked', 'inactive', 'pending'].includes(ma.status)) return false;
  if (ma.role === 'owner') return false;
  if (!Array.isArray(ma.modules)) return false;
  if (ma.role === 'admin' && ma.modules.length === 0) return false;
  return !ma.modules.includes('billing');
}

// TASK-9949b — [ Purchase | Return ] toggle. Toggle + Return form live in
// views/expenseReturn.js, loaded here (cold-load perf budget); it is offered
// only on the person's OWN expense (m.mount checks: submitExpenseReturn files
// on the SESSION's person), so office / AP (no driverId) / owner entry points
// and a kiosk filing for someone else get no toggle. Purchase stays the
// untouched default. jobboard.js hands in what it owns (no ?v= cross-import).
let _expRetMod = null;
function _expenseReturnMount(modal, driverId) {
  if (driverId) __ddChunkWrap(import('./expenseReturn.js?v=9949201')).then((m) => {
    _expRetMod = m;
    m.mount(modal, driverId, {
      mintId: _mintExpenseRequestId, retry: _callExpenseWithRetry, transport: _isExpenseTransportError,
      park: _expenseParkSingle, parkNote: _expenseParkNote, banner: _showExpenseResultBanner, close: closeExpenseModal,
      ctx: () => _expenseContext, JobBoardState, prepareUploadFile, httpsCallable, functions,
      auth, storage, ref, uploadBytes, getDownloadURL,
    });
  }).catch((e) => console.warn('[Expense] return form did not load', e));
}

function openExpenseModal(entryId, jobId, jobDayId, ctx) {
  // Lost-answer v2 (COD r8) — a modal still holding unanswered sends is never
  // silently replaced: those sends would be dropped unchecked.
  if (_expensePendingFor(document.getElementById('expense-modal'))) {
    _showExpenseResultBanner(false, 'Finish sending (or close) the expense that did not get an answer first.');
    return;
  }
  // Remove any existing expense modal
  document.getElementById('expense-modal')?.remove();
  _expensePending = null; // a fresh modal is a new submission

  // ctx = optional override context from public clock page
  // Falls back to JobBoardState for job board usage
  const clockEntries = ctx?.clockEntries || JobBoardState.clockEntries || [];
  const allTrucks = ctx?.trucks || JobBoardState.trucks || [];
  const allJobs = ctx?.jobs || JobBoardState.jobs || [];
  const allJobDays = ctx?.jobDays || JobBoardState.jobDays || [];
  // Driver-PIN landing renders dispatch.js; jobboard view may never have been
  // initialized, so JobBoardState.tenantId can be undefined. Fall back to
  // AppState.tenant (set by the auth layer in every context — admin, driver PIN,
  // and public clock).
  const tenantId = ctx?.tenantId || JobBoardState.tenantId || window.AppState?.tenant;
  // TASK-355 2026-05-22: PIN -> SPA member identity resolution.
  const driverId = ctx?.driverId || getActiveJobBoardPersonId();
  const driverName = ctx?.driverName || getActiveJobBoardPersonName();

  // TASK-362 Slice 2: Credit-card picker cached snapshot at modal open.
  // Source: tenant.linesOfCredit (cached via AppState.tenantData or ctx).
  // Admin-AP entry-points may pass ctx.linesOfCredit; else read from AppState.
  const allLinesOfCredit = ctx?.linesOfCredit
    || window.AppState?.tenantData?.linesOfCredit
    || [];
  const activeCreditCards = Array.isArray(allLinesOfCredit)
    ? allLinesOfCredit.filter(l => l && l.subtype === 'credit_card' && !l.archived)
    : [];

  // Store context for submitExpense
  // TASK-1338 — people + jobDays MUST ride along: submit re-derives job
  // visibility with the same helper, and dropping them let the picker and the
  // submit path disagree (Codex v5).
  _expenseContext = { tenantId, clockEntries, trucks: allTrucks, jobs: allJobs, jobDays: allJobDays, people: (ctx?.people || JobBoardState.people || []), driverId, driverName, creditCards: activeCreditCards,
    // TASK-4585 — submit must judge the requirement against the SAME list the
    // renderer drew. The AP "Record Expense" entry point ctx-supplies
    // allocations and may run with no JobBoard subscription at all, so reading
    // JobBoardState there would look permanently unloaded and refuse the save.
    costAllocations: (ctx?.costAllocations || JobBoardState.costAllocations || []),
    costAllocationsSettled: (ctx && 'costAllocationsSettled' in ctx)
      ? !!ctx.costAllocationsSettled
      : !!((ctx?.costAllocations && ctx.costAllocations.length) || JobBoardState._costAllocationsLoaded),
    driverExpenseSettings: (ctx?.driverExpenseSettings || window.AppState?.settings?.driverExpenses || {}) };

  // TASK-4639 — clear the previous modal's receipt + scan state. Nothing used
  // to reset these on OPEN, so a scan that was run and then cancelled rode into
  // the next expense: the fresh manual entry was stamped `parsedByAI:true` with
  // somebody else's `rawAIResponse`. That is "AI was used when it wasn't picked"
  // in its quietest form.
  _expenseReceiptFile = null;
  _expenseReceiptFiles = [];
  _expenseLastAIResponse = null;
  _expensePickGen++; // TASK-9402 — a conversion still running for the last modal must not land here

  // Get context from clock entry
  const entry = clockEntries.find(e => e.id === entryId);
  const job = allJobs.find(j => j.id === jobId);
  const jobDay = allJobDays.find(jd => jd.id === jobDayId);

  // Build truck options from assigned equipment
  const trucks = allTrucks.filter(t => t.status !== 'inactive');
  const assignedTruckIds = jobDay?.trucks || [];
  let truckOptions = '<option value="">— Select Vehicle —</option>';
  trucks.forEach(t => {
    const unit = t.unit || t.id;
    const name = t.nickname ? `${t.nickname} — #${unit}` : `#${unit}`;
    const selected = assignedTruckIds.includes(t.id) ? 'selected' : '';
    truckOptions += `<option value="${t.id}" data-display="${escapeHtml(name)}" ${selected}>${escapeHtml(name)}</option>`;
  });

  // Category options — sourced from tenant settings (fallback to hardcoded defaults).
  // All three attrs are escaped because tenant-curated categories can carry
  // attacker-controlled strings if a tenant admin is compromised. Server side
  // also sanitizes on save (ap.js _saveDriverExpenseSettings).
  const _activeCats = getActiveExpenseCategories();
  let catOptions = _activeCats.map(c =>
    `<option value="${escapeHtml(c.value)}" data-icon="${escapeHtml(c.icon)}">${escapeHtml(c.label)}</option>`
  ).join('');

  // State options for fuel
  let stateOptions = '<option value="">State</option>' + US_STATES.map(s => `<option value="${s}">${s}</option>`).join('');

  // Cost allocation options — sourced from tenant `costAllocations` collection.
  // Required if `settings.driverExpenses.requireCostAllocation === true`.
  // We auto-select the matching truck allocation if the driver has a clocked-in vehicle.
  // Admin entry-points (e.g. AP "Record Expense") pre-fetch allocations and
  // pass them via ctx since they may open before JobBoard's subscription has
  // populated JobBoardState.costAllocations.
  const allocs = ctx?.costAllocations || JobBoardState.costAllocations || [];
  const driverExpenseSettings = (ctx?.driverExpenseSettings) ||
    window.AppState?.settings?.driverExpenses || {};
  // TASK-4585 — a requirement nobody can satisfy is not a requirement. When the
  // tenant has ZERO allocations the old code drew this select disabled+blank and
  // then refused the save, telling the DRIVER to go open an admin screen he does
  // not have. The waiver only applies once the list is known to be loaded
  // (ctx-supplied lists come pre-fetched by the AP entry points, so they count).
  const allocsLoaded = (ctx && 'costAllocationsSettled' in ctx)
    ? !!ctx.costAllocationsSettled
    : !!((ctx?.costAllocations && ctx.costAllocations.length) || JobBoardState._costAllocationsLoaded);
  const costAllocConfigured = driverExpenseSettings.requireCostAllocation !== false;
  const costAllocUnsatisfiable = allocsLoaded && allocs.length === 0;
  const costAllocRequired = costAllocConfigured && !costAllocUnsatisfiable;
  // Auto-pick first allocation matching the assigned truck.
  let _autoAllocId = '';
  if (assignedTruckIds.length === 1) {
    const found = allocs.find(a => a.type === 'truck' && a.refId === assignedTruckIds[0]);
    if (found) _autoAllocId = found.id;
  }
  let costAllocOptions = `<option value="">${costAllocRequired ? '— Select Cost Allocation —' : '— No allocation —'}</option>`;
  allocs.forEach(a => {
    const sel = a.id === _autoAllocId ? 'selected' : '';
    const typeBadge = a.type ? ` (${a.type})` : '';
    costAllocOptions += `<option value="${escapeHtml(String(a.id))}" data-label="${escapeHtml(String(a.label || ''))}" data-alloc-type="${escapeHtml(String(a.type || ''))}" data-ref-id="${escapeHtml(String(a.refId == null ? '' : a.refId))}" ${sel}>${escapeHtml(String(a.label || a.id))}${typeBadge}</option>`;
  });

  // TASK-362 Slice 2: Credit-card picker options. Default selection via
  // last-4 match against the driver's `creditCardLast4` on the person doc
  // (HR Employment Info). If two or more cards share the same last-4,
  // DISABLE auto-select — leave on "Select card" so we don't guess wrong
  // (Codex + Independent catch). Picker stays optional; no save gate.
  const _people = ctx?.people || JobBoardState.people || [];
  const _driverDoc = driverId ? _people.find(p => p && p.id === driverId) : null;
  // TASK-4866 - prefer the explicit FK. `creditCardId` is written by HR when
  // somebody names the card on the Employment tab; it is an exact link, so it
  // beats guessing on a last-4 and it is immune to the duplicate-last-4 case
  // below (two cards ending 1234 auto-selected NOTHING). Falls straight back to
  // the last-4 match for every person saved before the field existed.
  const _fkCardId = _driverDoc?.creditCardId ? String(_driverDoc.creditCardId).trim() : '';
  const _fkCard = _fkCardId ? activeCreditCards.find(c => c && c.id === _fkCardId) : null;
  const _driverLast4 = _driverDoc?.creditCardLast4 ? String(_driverDoc.creditCardLast4).trim() : '';
  const _matchingCards = _driverLast4
    ? activeCreditCards.filter(c => String(c.lastFourDigits || '').trim() === _driverLast4)
    : [];
  // TASK-9291 — a person can now hold MORE THAN ONE card. The extra ones are
  // an assignment on the person doc, `additionalCardIds`: OPAQUE tenant
  // registry ids, resolved here against the SAME `linesOfCredit` array this
  // picker is already built from, so a driver-PIN session learns nothing it
  // could not already read. (The PINs are NOT here and never will be — they
  // live in the admin-gated `peopleCard` vault; see public/src/personCard.js.)
  //
  // Order: the legacy primary FK first, then the additional cards in the order
  // HR assigned them. De-duplicated, and any id with no live registry row is
  // dropped — an archived or deleted card must not be offerable on an expense.
  const _assignedCardIds = [];
  const _seenAssigned = new Set();
  [_fkCardId].concat(Array.isArray(_driverDoc?.additionalCardIds) ? _driverDoc.additionalCardIds : [])
    .forEach((raw) => {
      const id = String(raw || '').trim();
      if (!id || _seenAssigned.has(id)) return;
      if (!activeCreditCards.some(c => c && c.id === id)) return;
      _seenAssigned.add(id);
      _assignedCardIds.push(id);
    });
  // Default selection — today's rule, UNCHANGED, with one additive arm:
  //   explicit FK wins  ->  else exactly ONE assigned card  ->  else the
  //   single last-4 match  ->  else nothing (never guess between two).
  // Everyone live today has zero additionalCardIds, so the middle arm is inert
  // until someone is actually assigned a second card, and the selection this
  // picker makes for all 456 people is byte-identical to before.
  const _defaultCardId = _fkCard
    ? _fkCard.id
    : (_assignedCardIds.length === 1
      ? _assignedCardIds[0]
      : ((_matchingCards.length === 1) ? _matchingCards[0].id : ''));
  // TASK-9593 — OWN CARDS ONLY for a driver-PIN session or a self-service
  // (no `billing` module) member: the list is the cards ASSIGNED to this
  // person (credit slot FK, fuel slot FK, then the TASK-9291 tail), plus
  // "Other / not listed" which takes a last-4 only and is flagged for the
  // office. Office/AP keep every company card (the branch below, unchanged).
  // No last-4 matching here: a person with no FK is assigned nothing — the
  // office assigns the card on the person page (never a backfill from last-4).
  // createDriverExpense / createMemberExpense refuse an unassigned card id,
  // so this list is a convenience, not the boundary.
  const _cardOwnOnly = _expenseCardPickerOwnOnly();
  const _ownCardIds = [];
  if (_cardOwnOnly) {
    const _seenOwn = new Set();
    [_fkCardId, _driverDoc?.fuelCardId]
      .concat(Array.isArray(_driverDoc?.additionalCardIds) ? _driverDoc.additionalCardIds : [])
      .forEach((raw) => {
        const id = String(raw || '').trim();
        if (!id || _seenOwn.has(id)) return;
        if (!activeCreditCards.some(c => c && c.id === id)) return;
        _seenOwn.add(id);
        _ownCardIds.push(id);
      });
  }
  // Exactly one assigned card → auto-selected. Several → the credit-slot FK if
  // it is one of them, else nothing (never guess between two).
  const _cardDefault = !_cardOwnOnly
    ? _defaultCardId
    : (_ownCardIds.length === 1 ? _ownCardIds[0] : (_ownCardIds.includes(_fkCardId) ? _fkCardId : ''));
  // The person's own recorded last-4 pre-fills the "Other" box (their record,
  // no binding, still flagged) so a driver with no assigned card types nothing.
  const _otherCardPrefill = _cardOwnOnly && !_ownCardIds.length && /^\d{4}$/.test(_driverLast4) ? _driverLast4 : '';
  const _cardOptionHTML = (c) => {
    const sel = c.id === _cardDefault ? 'selected' : '';
    const last4 = c.lastFourDigits ? `····${escapeHtml(String(c.lastFourDigits))}` : '';
    // Append `· Exp MM/YY` if within 60 days so driver sees expiring-soon warning.
    let expSuffix = '';
    if (c.cardExpiryDate && /^\d{4}-\d{2}$/.test(c.cardExpiryDate)) {
      const [yr, mo] = c.cardExpiryDate.split('-').map(Number);
      const lastDay = new Date(yr, mo, 0); lastDay.setHours(23, 59, 59, 999);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const daysLeft = Math.ceil((lastDay - today) / (1000 * 60 * 60 * 24));
      if (daysLeft <= 60) {
        const yy = String(yr).slice(-2);
        const mm = String(mo).padStart(2, '0');
        expSuffix = daysLeft <= 0 ? ` · Exp ${mm}/${yy} (EXPIRED)` : ` · Exp ${mm}/${yy}`;
      }
    }
    return `<option value="${escapeHtml(String(c.id))}" data-last4="${escapeHtml(String(c.lastFourDigits || ''))}" data-name="${escapeHtml(String(c.name || ''))}" ${sel}>${escapeHtml(String(c.name || 'Card'))} ${last4}${expSuffix}</option>`;
  };
  let _cardPickerOptions = '<option value="">— Select card —</option>';
  if (_cardOwnOnly) {
    // TASK-9593 — only this person's cards, then the escape hatch.
    _cardPickerOptions += _ownCardIds
      .map(id => activeCreditCards.find(c => c && c.id === id))
      .filter(Boolean)
      .map(_cardOptionHTML).join('');
    _cardPickerOptions += `<option value="${EXPENSE_CARD_OTHER}">Other / not listed</option>`;
  } else if (_assignedCardIds.length > 1) {
    // 🔴 GROUPED ONLY WHEN THERE IS SOMETHING TO GROUP. With zero or one
    // assigned card the list is emitted FLAT, exactly as it always has been —
    // that is what keeps the 429 people with no cards, and every one of the 27
    // with a single slot, on byte-identical markup.
    const _mine = _assignedCardIds
      .map(id => activeCreditCards.find(c => c && c.id === id))
      .filter(Boolean);
    const _others = activeCreditCards.filter(c => c && !_seenAssigned.has(c.id));
    _cardPickerOptions += `<optgroup label="Your cards">${_mine.map(_cardOptionHTML).join('')}</optgroup>`;
    if (_others.length) {
      _cardPickerOptions += `<optgroup label="Other company cards">${_others.map(_cardOptionHTML).join('')}</optgroup>`;
    }
  } else {
    activeCreditCards.forEach(c => { _cardPickerOptions += _cardOptionHTML(c); });
  }
  const _showCardPicker = activeCreditCards.length > 0;

  // TASK-1338 — optional Job on an expense, so AP-recorded costs stop landing in
  // "Unassigned" and customer P&L stops understating direct costs (BI buckets
  // costEntries per customer at bi.js:1525).
  //
  // ONE dropdown, not a checkbox plus two selects. Chris, after COD pushed back
  // on three taps on a driver's phone: "I just thought adding 2 dropdowns makes
  // an already noisy modal even busier." The customer is never asked for — it
  // comes FROM the job, surfaced by the optgroup header while the list is open
  // and by rewriting the existing muted helper line on select. Net +1 row.
  //
  // Built from the in-memory jobs array on purpose: a driver-PIN session has no
  // read access to tenants/{tid}/customers, so reading it would break the
  // driver path entirely.
  // COD Code Reviewer ❌ — the first cut listed EVERY job in state, which on a
  // driver-PIN session ignores the per-person `showAllJobs` scoping the rest of
  // this module enforces via jobDayVisibleToCurrentDriver(). A restricted
  // driver (the 3rd-party/contractor case that setting exists for) would have
  // read every customer and job name in the fleet out of this dropdown.
  // Narrow to jobs reachable through a jobDay they can actually see. An
  // explicit ctx.jobs (AP / admin context) is trusted as already-scoped.
  //
  // Two COD rounds found this gate wrong twice, both times because I keyed off
  // HOW someone logged in instead of WHO they are:
  //   v2 🔴 — gating on `driverModePin` alone let a `?access=TOKEN` link
  //     (driverMode + isPublicAccess, no PIN flag) through unscoped.
  //   v3 🔴 — even the three-flag version missed a restricted SPA member, who
  //     sets none of them, and `!ctx?.jobs` was an unconditional trust escape:
  //     app.js openPersonalExpense and dispatch.js openDriverRecordExpenseForPerson
  //     both hand in DispatchState.jobs, the full unfiltered tenant list.
  //
  // Restriction is a property of the PERSON, exactly as jobDayVisibleToCurrentDriver
  // treats it — tenant `personalizedSchedule`, or that person's `showAllJobs:false`.
  // Someone with no resolved personId (office/admin) is never narrowed, which is
  // why this can apply to every caller and drop the ctx.jobs escape entirely.
  const _expJobs = _expenseVisibleJobs(ctx);
  // TASK-1513 — picker-only relevance window over the post-restriction list.
  // 739 jobs on a phone picker is unusable; default to the recent window and
  // put the rest behind the "Show older jobs" link below the select. Driver
  // contexts get the tight window; the AP/bookkeeper path (driverId null) gets
  // the wide one for monthly reconciliation. Both submit paths re-derive via
  // _expenseVisibleJobs, so this is provably presentation-only.
  const _expJobsAll = _expJobs.filter(j => j && j.status !== 'cancelled');
  const _expWin = driverId ? EXPENSE_JOB_WINDOW_DRIVER : EXPENSE_JOB_WINDOW_ADMIN;
  // 🔴 forceIncludeId is resolved inside _expJobs (post-restriction), NEVER
  // ctx.jobs/JobBoardState.jobs — a restricted driver opened with an
  // out-of-scope jobId must get NOTHING forced in, not that job's
  // title+customer. Same trap the _expenseVisibleJobs comment warns about;
  // don't "clean this up" to search a wider array.
  const _expJobsWindowed = filterExpenseJobWindow(_expJobs, {
    backDays: _expWin.backDays,
    fwdDays: _expWin.fwdDays,
    forceIncludeId: jobId || null,
  });
  const _showJobPicker = _expJobsAll.length > 0;
  const _jobDate = (j) => j.startDate?.toDate?.() || (j.startDate ? new Date(j.startDate) : null)
    || j.createdAt?.toDate?.() || null;
  const _short = (d) => d && !isNaN(d.getTime()) ? ` · ${d.getMonth() + 1}/${d.getDate()}` : '';
  // TASK-1513 — options builder, callable twice: once for the default
  // (windowed) list, again from the "Show older jobs" link with the FULL
  // post-restriction list. `value=`/`data-cust=`/optgroup `label=` are
  // ATTRIBUTE context — escAttr, not escapeHtml (escapeHtml is not
  // attribute-safe; see lesson_escapehtml_is_not_attribute_safe).
  const _escA = window.escAttr || escapeHtml;
  const _buildJobPickerOptions = (jobsArr, selectedId) => {
    let out = '<option value="">— No job —</option>';
    const _byCustomer = new Map();
    for (const j of jobsArr) {
      const cust = String(j.customer || j.customerName || '').trim() || 'No customer';
      if (!_byCustomer.has(cust)) _byCustomer.set(cust, []);
      _byCustomer.get(cust).push(j);
    }
    for (const cust of [..._byCustomer.keys()].sort((a, b) => a.localeCompare(b))) {
      const list = _byCustomer.get(cust).sort((a, b) => (_jobDate(b) || 0) - (_jobDate(a) || 0));
      out += `<optgroup label="${_escA(cust)}">`;
      for (const j of list) {
        const sel = (selectedId && j.id === selectedId) ? ' selected' : '';   // Change 2 — pre-fill from job context
        const title = String(j.title || j.name || 'Untitled Job');
        out += `<option value="${_escA(String(j.id))}" data-cust="${_escA(String(j.customer || j.customerName || ''))}"${sel}>${escapeHtml(title + _short(_jobDate(j)))}</option>`;
      }
      out += '</optgroup>';
    }
    return out;
  };
  const _jobPickerOptions = _showJobPicker ? _buildJobPickerOptions(_expJobsWindowed, jobId || null) : '<option value="">— No job —</option>';
  const _olderJobCount = Math.max(0, _expJobsAll.length - _expJobsWindowed.filter(j => j.status !== 'cancelled').length);
  // Change 2 — when the modal opens from a job (driver taps "+ Expense" inside
  // one), show that job's customer immediately. Today that context is captured
  // silently and the driver can neither see nor correct it.
  const _preJob = jobId ? _expJobs.find(j => j.id === jobId) : null;
  const _jobHelperInitial = _preJob
    ? `Customer: ${escapeHtml(String(_preJob.customer || _preJob.customerName || '— none on this job —'))}`
    : 'Optional — ties this cost to a job so it lands on the right customer.';

  // TASK-9493 — split-line option lists. Built from the SAME scoped lists the
  // single pickers above use (allocs, the post-restriction windowed jobs, the
  // active trucks), never a wider array, so a restricted driver sees nothing
  // on a split line he could not already pick above. Nothing pre-selected:
  // line 1 is seeded from the single pickers when the split opens.
  const _splitAllocOptions = `<option value="">${costAllocRequired ? '— Cost allocation * —' : '— Cost allocation —'}</option>`
    + allocs.map(a => `<option value="${escapeHtml(String(a.id))}">${escapeHtml(String(a.label || a.id))}${a.type ? ` (${escapeHtml(String(a.type))})` : ''}</option>`).join('');
  const _splitJobOptions = _showJobPicker ? _buildJobPickerOptions(_expJobsWindowed, null) : '';
  const _splitVehicleOptions = '<option value="">— Vehicle —</option>' + trucks.map(t => {
    const unit = t.unit || t.id;
    const name = t.nickname ? `${t.nickname} — #${unit}` : `#${unit}`;
    return `<option value="${escapeHtml(String(t.id))}">${escapeHtml(name)}</option>`;
  }).join('');
  const _splitAvailable = allocs.length > 0 || _showJobPicker || trucks.length > 0;
  _expenseContext.split = {
    available: _splitAvailable,
    required: costAllocRequired,
    allocOptions: allocs.length > 0 ? _splitAllocOptions : '',
    jobOptions: _splitJobOptions,
    vehicleOptions: trucks.length > 0 ? _splitVehicleOptions : '',
    // allocation id -> vehicle id, for truck/trailer allocations whose unit is
    // in the vehicle list: picking "Truck #12" on a line fills its vehicle.
    allocVehicle: new Map(allocs
      .filter(a => a && (a.type === 'truck' || a.type === 'trailer') && a.refId && trucks.some(t => t.id === a.refId))
      .map(a => [String(a.id), String(a.refId)])),
  };

  const modal = document.createElement('div');
  modal.id = 'expense-modal';
  modal.className = 'modal';
  modal.style.cssText = 'display:flex;align-items:center;justify-content:center;z-index:10000;';
  modal.dataset.entryId = entryId || '';
  modal.dataset.jobId = jobId || '';
  modal.dataset.jobDayId = jobDayId || '';

  modal.innerHTML = `
    <div class="modal-content modal-fullscreen" style="max-width:540px;display:flex;flex-direction:column;max-height:90dvh;">
      <div class="eticket-form-header">
        <div class="eticket-form-header-title">
          <span class="material-icons">receipt</span>
          Record Expense
        </div>
        <button class="modal-close" onclick="window.JobBoardModule.closeExpenseModal()" style="color:#fff;">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;overflow-y:auto;flex:1;">
        <!-- Mode toggle -->
        <div style="display:flex;gap:8px;margin-bottom:16px;">
          <button class="btn btn-sm" id="expense-mode-manual" style="flex:1;background:var(--brand-blue);color:#fff;border:none;padding:8px;border-radius:8px;font-weight:600;cursor:pointer;" onclick="window.JobBoardModule.setExpenseMode('manual')">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;">edit</span> Manual Entry
          </button>
          <button class="btn btn-sm" id="expense-mode-scan" style="flex:1;background:#fff;color:var(--text-primary);border:1px solid var(--border-light);padding:8px;border-radius:8px;font-weight:600;cursor:pointer;" onclick="window.JobBoardModule.setExpenseMode('scan')">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;">document_scanner</span> Smart Scan
          </button>
        </div>

        <!-- Smart Scan section (hidden by default) -->
        <div id="expense-scan-section" style="display:none;">
          <div style="text-align:center;padding:24px 16px;background:var(--bg-secondary);border-radius:12px;border:2px dashed var(--border-light);">
            <span class="material-icons" style="font-size:48px;color:var(--brand-blue);">photo_camera</span>
            <p style="margin:8px 0 16px;color:var(--text-secondary);">Snap a photo or attach a PDF — Smart Tools will fill in the details</p>
            <label style="display:inline-flex;align-items:center;gap:8px;padding:12px 24px;background:var(--brand-blue);color:#fff;border-radius:8px;font-weight:600;cursor:pointer;">
              <span class="material-icons">camera_alt</span> Scan Receipt
              <input type="file" id="expense-scan-input" accept="image/*,application/pdf" style="display:none;" onchange="window.JobBoardModule.scanExpenseReceipt(this)">
            </label>
          </div>
          <div id="expense-scan-status" style="display:none;text-align:center;padding:20px 16px;">
            <div id="expense-scan-thumb" style="display:none;margin:0 auto 14px;width:96px;height:96px;border-radius:10px;overflow:hidden;border:1px solid var(--border-light);box-shadow:0 2px 8px rgba(0,0,0,0.08);"></div>
            <span class="btn-spinner"></span>
            <p id="expense-scan-status-text" style="margin:12px 0 4px;color:var(--text-primary);font-weight:600;">Uploading receipt…</p>
            <p id="expense-scan-status-sub" style="margin:0 0 14px;color:var(--text-secondary);font-size:0.8rem;line-height:1.4;">Smart Tools is reading the details</p>
            <div class="expense-scan-bar" id="expense-scan-bar"><div class="expense-scan-bar-fill" id="expense-scan-bar-fill"></div></div>
          </div>
        </div>

        <!-- Manual entry form -->
        <div id="expense-form-section">
          ${(allocs.length > 0 || costAllocConfigured) ? `
          <div class="eticket-form-field" style="margin-top:0;">
            <label class="eticket-form-label">Cost Allocation${costAllocRequired ? ' *' : ''}</label>
            <select class="form-control" id="expense-cost-allocation" ${allocs.length === 0 ? 'disabled' : ''} onchange="window.JobBoardModule.onExpenseCategoryChange()">
              ${costAllocOptions}
            </select>
            ${allocs.length === 0 ? `
              <div style="margin-top:6px;padding:10px 12px;background:#FEF3C7;border:1px solid #FCD34D;border-radius:8px;font-size:0.78rem;color:#92400E;line-height:1.4;">
                <span class="material-icons" style="font-size:14px;vertical-align:middle;color:#B45309;">info</span>
                ${allocsLoaded
                  ? `No cost allocations are set up on this account yet, so this expense will save without one. (Admin: AP → Driver Expense Settings → <strong>Seed from fleet</strong> adds your trucks, trailers and assets.)`
                  : `Loading cost allocations…`}
              </div>
            ` : `
              <p style="font-size:0.72rem;color:var(--text-muted);margin:4px 0 0;">Pick the truck, trailer, asset, or shop/office this expense belongs to.</p>
            `}
          </div>
          ` : ''}

          <!-- TASK-1338 — optional Job. One select, grouped by customer; the
               customer is shown in the helper line below rather than in a
               second dropdown. Omitted entirely when there are no jobs so we
               never render a dead control. -->
          ${_showJobPicker ? `
          <div class="eticket-form-field" ${(allocs.length > 0 || costAllocConfigured) ? '' : 'style="margin-top:0;"'}>
            <label class="eticket-form-label">Job <span style="color:var(--text-muted);font-weight:400;">(optional)</span></label>
            <select class="form-control" id="expense-job">
              ${_jobPickerOptions}
            </select>
            <p id="expense-job-helper" style="font-size:0.72rem;color:var(--text-muted);margin:4px 0 0;">${_jobHelperInitial}</p>
            ${_olderJobCount > 0 ? `
            <!-- TASK-1513 — a TEXT LINK below the select, NOT an <option> inside
                 it (native mobile pickers render action options poorly, and a
                 sentinel option would need submit-side special-casing). -->
            <a href="#" id="expense-job-show-older" style="display:inline-block;font-size:0.75rem;font-weight:600;color:var(--brand-blue);margin-top:6px;text-decoration:underline;">Show older jobs (${_olderJobCount} more)</a>
            ` : ''}
          </div>
          ` : ''}

          <!-- TASK-9493 — split ONE receipt across trucks / jobs / allocations.
               A text link, not another control: the modal stays exactly as it
               was until someone asks for a split. The expense is still ONE
               entry at the full amount (it must match the card charge 1:1);
               the lines ride along as allocations. -->
          ${_splitAvailable ? `
          <div id="expense-split-link-wrap" style="margin:6px 0 2px;">
            <a href="#" id="expense-split-toggle" style="display:inline-flex;align-items:center;gap:4px;font-size:0.8rem;font-weight:600;color:var(--brand-blue);text-decoration:underline;">
              <span class="material-icons" style="font-size:16px;">call_split</span> Split this receipt across trucks / jobs
            </a>
          </div>
          ` : ''}

          <div class="eticket-form-field" ${(allocs.length > 0 || costAllocConfigured || _showJobPicker) ? '' : 'style="margin-top:0;"'}>
            <label class="eticket-form-label">Category</label>
            <select class="form-control" id="expense-category" onchange="window.JobBoardModule.onExpenseCategoryChange()">
              ${catOptions}
            </select>
          </div>

          <!-- TASK-10141 — Paid with: company card / vendor charge account / out
               of pocket. Hidden until the company has a vendor set up as a
               charge account (CRM → vendor → Charge account); until then the
               form is exactly as before. -->
          <div class="eticket-form-field" id="expense-paidwith-field" style="display:none;">
            <label class="eticket-form-label">Paid with</label>
            <div id="expense-paidwith" role="radiogroup" style="display:flex;flex-wrap:wrap;gap:6px;">
              <label style="display:flex;align-items:center;gap:6px;min-height:44px;padding:8px 12px;border:1px solid var(--border-light);border-radius:8px;cursor:pointer;background:var(--bg-secondary);color:var(--text-primary);font-size:0.9rem;"><input type="radio" name="expense-paidwith" value="company" checked> <span>${_showCardPicker ? 'Company card' : 'Company paid'}</span></label>
              <label style="display:flex;align-items:center;gap:6px;min-height:44px;padding:8px 12px;border:1px solid var(--border-light);border-radius:8px;cursor:pointer;background:var(--bg-secondary);color:var(--text-primary);font-size:0.9rem;"><input type="radio" name="expense-paidwith" value="charge"> <span>Charge account</span></label>
              <label style="display:flex;align-items:center;gap:6px;min-height:44px;padding:8px 12px;border:1px solid var(--border-light);border-radius:8px;cursor:pointer;background:var(--bg-secondary);color:var(--text-primary);font-size:0.9rem;"><input type="radio" name="expense-paidwith" value="pocket"> <span>Out of pocket</span></label>
            </div>
          </div>
          <div class="eticket-form-field" id="expense-charge-field" style="display:none;">
            <label class="eticket-form-label" for="expense-charge-vendor">Charged to our account at</label>
            <select class="form-control" id="expense-charge-vendor"><option value="">— Pick the vendor —</option></select>
            <div id="expense-charge-other-wrap" style="display:none;margin-top:8px;">
              <input type="text" class="form-control" id="expense-charge-other" maxlength="100" autocomplete="off" placeholder="Vendor name">
              <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">Not set up as a charge account yet. The office will link it.</div>
            </div>
            <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">The vendor bills the office later. Not a card, and not paid back to you.</div>
          </div>

          <!-- TASK-362 Slice 2: credit-card picker. Renders only when the tenant
               has 1+ active (non-archived) credit cards. Optional always; no
               save gate. Driver's card auto-selected via creditCardLast4 match
               unless two cards share that last-4 (then blank, no guess). Hidden
               when reimbursement is checked. -->
          ${_showCardPicker ? `
          <div class="eticket-form-field" id="expense-card-field" data-default-card-id="${escapeHtml(_cardDefault)}"${_cardOwnOnly ? ' data-own-cards="1"' : ''}>
            <label class="eticket-form-label">Credit Card <span style="color:var(--text-muted);font-weight:400;">(optional)</span></label>
            <select class="form-control" id="expense-card-select">
              ${_cardPickerOptions}
            </select>${_cardOwnOnly ? `
            <div id="expense-card-other-wrap" style="display:none;margin-top:8px;">
              <input type="text" class="form-control" id="expense-card-other-last4" inputmode="numeric" pattern="[0-9]*" maxlength="4" autocomplete="off" placeholder="Last 4 digits of the card" value="${escapeHtml(_otherCardPrefill)}" style="max-width:200px;">
              <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">Not one of your cards? Enter the last 4 digits of the card number (not the PIN). The office will check it.</div>
            </div>` : ''}
          </div>
          ` : ''}

          <div class="eticket-form-field">
            <label class="eticket-form-label">Amount</label>
            <div style="position:relative;">
              <span style="position:absolute;left:12px;top:50%;transform:translateY(-50%);color:var(--text-secondary);font-weight:600;pointer-events:none;font-size:0.875rem;line-height:1;z-index:1;">$</span>
              <input type="number" class="form-control" id="expense-amount" placeholder="0.00" step="0.01" min="0" inputmode="decimal" style="padding-left:30px !important;">
            </div>
          </div>

          <!-- TASK-9493 — split lines. Directly under the receipt total so the
               "Remaining to allocate" counter sits next to the number it is
               counting down from. Hidden until the Split link is tapped. -->
          ${_splitAvailable ? `
          <div class="eticket-form-field" id="expense-split-section" style="display:none;">
            <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;">
              <label class="eticket-form-label" style="margin:0;">Split across</label>
              <a href="#" id="expense-split-cancel" style="font-size:0.78rem;font-weight:600;color:var(--text-secondary);text-decoration:underline;">Don't split</a>
            </div>
            <div id="expense-split-lines"></div>
            <button type="button" id="expense-split-add" class="btn btn-sm btn-secondary" style="margin-top:8px;min-height:44px;display:inline-flex;align-items:center;gap:4px;">
              <span class="material-icons" style="font-size:16px;">add</span> Add line
            </button>
            <div id="expense-split-remaining" role="status" aria-live="polite" style="margin-top:10px;padding:10px 12px;border-radius:8px;font-size:0.85rem;font-weight:700;line-height:1.35;"></div>
          </div>
          ` : ''}

          <!-- Fuel-specific fields -->
          <div id="expense-fuel-fields" style="display:none;">
            <div style="display:flex;gap:8px;">
              <div class="eticket-form-field" style="flex:1;">
                <label class="eticket-form-label">Gallons</label>
                <input type="number" class="form-control" id="expense-gallons" placeholder="0.0" step="0.1" min="0">
              </div>
              <div class="eticket-form-field" style="flex:1;">
                <label class="eticket-form-label">$/Gallon</label>
                <input type="number" class="form-control" id="expense-ppg" placeholder="0.00" step="0.01" min="0">
              </div>
              <div class="eticket-form-field" style="width:90px;">
                <label class="eticket-form-label">State</label>
                <select class="form-control" id="expense-state" style="padding:8px 4px;">${stateOptions}</select>
              </div>
            </div>
            <!-- TASK-1200 — optional odometer at fill-up (John ask, IFTA audit
                 trail). Saved as fuelOdometer on the costEntry; when a vehicle
                 is also selected, a metersLog reading is appended after save so
                 PM intervals + IFTA miles-per-state reconstruction benefit. -->
            <div class="eticket-form-field">
              <label class="eticket-form-label">Odometer <span style="font-weight:400;color:var(--text-secondary);">(optional)</span></label>
              <input type="number" class="form-control" id="expense-odometer" placeholder="Miles on the dash at fill-up" step="1" min="0" inputmode="numeric">
            </div>
          </div>

          <!-- Vehicle field (shown for fuel, DEF, parts) -->
          <div id="expense-vehicle-field" class="eticket-form-field">
            <label class="eticket-form-label">Vehicle</label>
            <select class="form-control" id="expense-vehicle">${truckOptions}</select>
          </div>
          <!-- TASK-9592 — shown instead of the Vehicle field when a truck/trailer
               Cost Allocation already names the vehicle (filled via textContent). -->
          <p id="expense-alloc-vehicle-note" style="display:none;font-size:0.8rem;color:var(--text-secondary);margin:6px 0 0;">
            <span class="material-icons" style="font-size:15px;vertical-align:middle;color:var(--brand-blue);">local_shipping</span>
            Vehicle: <strong id="expense-alloc-vehicle-name" style="color:var(--text-primary);"></strong> (from Cost Allocation)
          </p>

          <div class="eticket-form-field">
            <label class="eticket-form-label">Description</label>
            <input type="text" class="form-control" id="expense-description" placeholder="Vendor name or details...">
          </div>

          <!-- Reimbursement-required toggle: driver paid out of pocket.
               TASK-10141: hidden (and driven by "Out of pocket") while the
               Paid-with row is showing. -->
          <div class="eticket-form-field" id="expense-reimb-field" style="margin-top:8px;">
            <label style="display:flex;align-items:flex-start;gap:8px;padding:10px 12px;border:1px solid var(--border-light);border-radius:8px;cursor:pointer;background:var(--bg-secondary);">
              <input type="checkbox" id="expense-reimbursement" style="margin-top:3px;accent-color:#DC2626;">
              <span>
                <strong style="display:block;color:var(--text-primary);font-size:0.92rem;">I paid this out of pocket — reimbursement needed</strong>
                <span style="font-size:0.78rem;color:var(--text-secondary);">Flags this expense for the office to pay you back. Leave unchecked if it was paid with a company card or fuel card.</span>
              </span>
            </label>
          </div>

          <!-- Notes (optional) - Chris-requested 2026-05-21. Free-form internal
               notes for the office: vendor follow-up, special handling, tax
               category hints, etc. Persisted as the existing 'notes' field on
               costEntries (server already declares notes empty default - now
               populated). Renders in AP Driver Expenses row when present. -->
          <div class="eticket-form-field" style="margin-top:8px;">
            <label class="eticket-form-label" for="expense-notes">Notes <span style="color:var(--text-muted);font-weight:400;">(optional)</span></label>
            <textarea class="form-control" id="expense-notes" rows="3" maxlength="1000" placeholder="Internal notes — vendor follow-up, special handling, tax category, etc." style="resize:vertical;min-height:64px;font-family:inherit;"></textarea>
          </div>

          <!-- Receipt photo (optional) -->
          <!-- TASK-244 v1.2: multi-file picker. When >1 file is selected, the
               submit flow switches to fire-and-forget background OCR — modal
               closes immediately, Smart Tools reads the amounts server-side,
               and each receipt becomes its own pending costEntries doc for the
               office to review. Single-file path is unchanged. -->
          <div class="eticket-form-field">
            <label class="eticket-form-label">Receipt</label>
            <div style="font-size:0.78rem;color:var(--text-secondary);margin:-2px 0 8px;line-height:1.35;">Photo or PDF. Optional. Pick several to upload them all at once.</div>
            <div id="expense-receipt-preview" style="display:none;margin-bottom:8px;position:relative;">
              <img id="expense-receipt-img" style="max-width:100%;max-height:200px;border-radius:8px;border:1px solid var(--border-light);display:none;">
              <div id="expense-receipt-pdf-badge" style="display:none;align-items:center;gap:8px;padding:12px 14px;background:var(--bg-secondary);border:1px solid var(--border-light);border-radius:8px;">
                <span class="material-icons" style="color:#DC2626;">picture_as_pdf</span>
                <span id="expense-receipt-pdf-name" style="font-size:0.875rem;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"></span>
              </div>
              <button type="button" style="position:absolute;top:4px;right:4px;background:rgba(0,0,0,0.6);color:#fff;border:none;border-radius:50%;width:28px;height:28px;cursor:pointer;display:flex;align-items:center;justify-content:center;" onclick="window.JobBoardModule.clearExpenseReceipt()">
                <span class="material-icons" style="font-size:16px;">close</span>
              </button>
            </div>
            <!-- Multi-receipt thumbnail grid (TASK-244 v1.2). Hidden when <=1 file. -->
            <div id="expense-receipts-multi" style="display:none;margin-bottom:8px;"></div>
            <label style="display:inline-flex;align-items:center;gap:6px;padding:8px 16px;background:var(--bg-secondary);border:1px solid var(--border-light);border-radius:8px;cursor:pointer;font-size:0.875rem;color:var(--text-secondary);">
              <span class="material-icons" style="font-size:18px;">attach_file</span> Attach Receipt(s)
              <input type="file" id="expense-receipt-input" accept="image/*,application/pdf" multiple style="display:none;" onchange="window.JobBoardModule.previewExpenseReceipt(this)">
            </label>
            <!-- Multi-mode helper banner (TASK-244 v1.2). Shown only when >1 file selected.
                 TASK-4639 — this used to ANNOUNCE that Smart Tools would read every
                 receipt. Announcing is not asking. Chris attached a screenshot of his
                 Chase statement to a batch, never picked Smart Scan, and the scan
                 filed a $579.28 card balance under the vendor name "United Quest".
                 His rule: "AI shouldn't be used unless it's picked on Record Expense."
                 So the promise became a CHOICE, and it starts OFF. -->
            <div id="expense-multi-hint" style="display:none;margin-top:8px;padding:10px 12px;background:#EFF6FF;border:1px solid #BFDBFE;border-radius:8px;font-size:0.78rem;color:#1E40AF;line-height:1.4;">
              <div>
                <span class="material-icons" style="font-size:14px;vertical-align:middle;">receipt_long</span>
                <strong>Each receipt becomes its own expense.</strong> The cost allocation, job, and reimbursement setting above apply to <strong>ALL</strong> receipts in this batch.
              </div>
              <label style="display:flex;align-items:flex-start;gap:8px;margin-top:10px;padding:10px 12px;background:#fff;border:1px solid #BFDBFE;border-radius:8px;cursor:pointer;">
                <input type="checkbox" id="expense-multi-smartscan" style="margin-top:2px;accent-color:var(--brand-blue);" onchange="window.JobBoardModule.onExpenseSmartScanToggle()">
                <span>
                  <strong style="display:block;color:var(--text-primary);font-size:0.85rem;">Let Smart Tools read these receipts</strong>
                  <span id="expense-multi-smartscan-sub" style="color:var(--text-secondary);font-size:0.76rem;">Off — each receipt is filed exactly as you entered it and the office keys in the amounts.</span>
                </span>
              </label>
              <!-- TASK-4639 — one amount field, several receipts. Say out loud what
                   that does instead of silently discarding the number, which is what
                   the old hardcoded zero-amount payload did. -->
              <div id="expense-multi-amount-warn" style="display:none;margin-top:8px;padding:8px 10px;background:#FEF3C7;border:1px solid #FCD34D;border-radius:8px;color:#92400E;font-weight:600;"></div>
            </div>
          </div>

          <!-- Fluid hint -->
          <div id="expense-fluid-hint" style="display:none;padding:10px 12px;background:#EFF6FF;border-radius:8px;margin-top:8px;">
            <span class="material-icons" style="font-size:16px;color:var(--brand-blue);vertical-align:middle;">info</span>
            <span style="font-size:0.8rem;color:var(--text-secondary);">Bought fluids? Track consumption in <a href="#" onclick="event.preventDefault();window.navigateToView?.('maintenance')" style="color:var(--brand-blue);text-decoration:underline;">Maintenance → Add Fluid</a></span>
          </div>
        </div>
      </div>
      <div class="eticket-form-actions">
        <button class="btn btn-secondary" onclick="window.JobBoardModule.closeExpenseModal()">Cancel</button>
        <button class="btn btn-primary" id="btn-expense-submit" onclick="window.JobBoardModule.submitExpense()">
          <span class="material-icons">save</span> Save Expense
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // Show vehicle field by default (fuel is first category)
  onExpenseCategoryChange();

  // TASK-4639 — keep the "$X × N receipts" warning live as they type. Bound
  // after append (not an inline oninput) so re-opening the modal can't stack
  // listeners — the element is destroyed with the modal each time.
  document.getElementById('expense-amount')
    ?.addEventListener('input', _syncExpenseMultiAmountWarning);

  // TASK-4639 — remember which category the form OPENED on. A <select> always
  // has a value, so "did the driver choose this?" is unanswerable without a
  // baseline; with one, Smart Scan can fill an untouched dropdown and must keep
  // its hands off a deliberate pick.
  const _catOpen = document.getElementById('expense-category');
  if (_catOpen) _catOpen.dataset.openedWith = _catOpen.value;

  // TASK-362 Slice 2d: Hide the credit-card picker (and clear its value) when
  // the driver checks "I paid this out of pocket — reimbursement needed". On
  // uncheck, re-show + re-apply the default card. Attached AFTER append (not
  // inline `oninput` in the template) so re-opens don't accumulate listeners
  // and the elements exist before bind (Codex catch).
  if (_showCardPicker) {
    const reimbEl = document.getElementById('expense-reimbursement');
    const cardWrap = document.getElementById('expense-card-field');
    const cardSelect = document.getElementById('expense-card-select');
    if (reimbEl && cardWrap && cardSelect) {
      // TASK-9593 — "Other / not listed" reveals the last-4 box (own-cards mode only).
      const otherWrap = document.getElementById('expense-card-other-wrap');
      const syncOtherCard = () => {
        if (otherWrap) otherWrap.style.display = cardSelect.value === EXPENSE_CARD_OTHER ? '' : 'none';
      };
      if (otherWrap) {
        cardSelect.addEventListener('change', syncOtherCard);
        const otherInput = document.getElementById('expense-card-other-last4');
        if (otherInput) otherInput.addEventListener('input', () => {
          const digits = otherInput.value.replace(/\D/g, '').slice(0, 4);
          if (digits !== otherInput.value) otherInput.value = digits;
        });
      }
      const applyCardPickerVisibility = () => {
        if (reimbEl.checked) {
          cardWrap.style.display = 'none';
          cardSelect.value = '';
        } else {
          cardWrap.style.display = '';
          cardSelect.value = _cardDefault;
        }
        syncOtherCard();
      };
      reimbEl.addEventListener('change', applyCardPickerVisibility);
      // Run once on open — if some other code path pre-checked the checkbox
      // (e.g. driver opens, dismisses, reopens with reimbursement still on),
      // the picker visibility should match the current state.
      applyCardPickerVisibility();
    }
  }

  // TASK-10141 — Paid with (bound AFTER the card picker so "Out of pocket"
  // can drive the reimbursement box through its existing listener).
  _bindExpensePaidWith(ctx);

  // TASK-1338 — surface the selected job's customer in the existing helper line
  // instead of adding a second dropdown. Bound here (not inline `onchange` in
  // the template) for the same reason as the card picker above: inline handlers
  // accumulate across re-opens.
  if (_showJobPicker) {
    const jobSel = document.getElementById('expense-job');
    const jobHelp = document.getElementById('expense-job-helper');
    if (jobSel && jobHelp) {
      jobSel.addEventListener('change', () => {
        const j = _expJobs.find(x => x.id === jobSel.value);
        if (!j) {
          jobHelp.textContent = 'Optional — ties this cost to a job so it lands on the right customer.';
          return;
        }
        const cust = String(j.customer || j.customerName || '').trim();
        jobHelp.textContent = cust ? `Customer: ${cust}` : 'This job has no customer on it.';
      });
    }
    // TASK-1513 — "Show older jobs": re-render the SAME <select>'s options from
    // the full post-restriction _expJobsAll list (never ctx.jobs — that array
    // bypasses _expenseVisibleJobs and is the weakest-check trap). Replacing
    // innerHTML keeps the select element itself, so the change listener bound
    // above stays live and the "Customer: …" helper keeps updating.
    const olderLink = document.getElementById('expense-job-show-older');
    if (olderLink && jobSel) {
      olderLink.addEventListener('click', (ev) => {
        ev.preventDefault();
        const keep = jobSel.value || null;
        jobSel.innerHTML = _buildJobPickerOptions(_expJobsAll, keep);
        if (keep) jobSel.value = keep;
        olderLink.remove();
      });
    }
  }

  // TASK-9493 — Split link + lines. Bound after append, like the pickers above.
  _wireExpenseSplit();
  _expenseReturnMount(modal, driverId); // TASK-9949b
}

// ─────────────────────────────────────────────────────────────────────────────
// TASK-9493 — split one receipt across trucks / jobs / cost allocations.
//
// The expense stays ONE entry at the FULL amount (the card statement matcher
// pairs one charge with one expense). The split is a list of lines, each with a
// cost allocation / job / vehicle and an amount; the lines must add up to the
// total to the cent. The rule (cents rounding, validation) is the generated
// window.CostSplit mirror of functions/src/shared/costAllocationSplit.js — the
// same file the callables validate with — never re-implemented here.
//
// UX: a live "Remaining to allocate" counter under the lines so a one-cent
// mismatch is visible BEFORE Save, never a mystery refusal on a phone. The LAST
// line auto-fills with whatever is left until someone types in it.
// ─────────────────────────────────────────────────────────────────────────────
function _isExpenseSplitActive() {
  return document.getElementById('expense-modal')?.dataset.split === '1';
}

function _expenseSplitFieldWraps() {
  const wrapOf = (id) => document.getElementById(id)?.closest('.eticket-form-field') || null;
  return {
    alloc: wrapOf('expense-cost-allocation'),
    job: wrapOf('expense-job'),
    vehicle: document.getElementById('expense-vehicle-field'),
    odometer: wrapOf('expense-odometer'),
  };
}

function _expenseSplitLineHTML() {
  const o = _expenseContext?.split || {};
  const sel = (cls, opts, label) => opts
    ? `<select class="form-control ${cls}" aria-label="${label}" style="flex:1 1 140px;min-width:0;">${opts}</select>`
    : '';
  return `
    <div class="expense-split-line" style="border:1px solid var(--border-light);border-radius:10px;padding:10px;margin-top:8px;background:var(--bg-secondary);">
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:nowrap;">
        ${sel('expense-split-alloc', o.allocOptions, 'Cost allocation for this line')}
        <div style="position:relative;flex:0 0 108px;">
          <span style="position:absolute;left:10px;top:50%;transform:translateY(-50%);color:var(--text-secondary);font-weight:600;pointer-events:none;font-size:0.875rem;z-index:1;">$</span>
          <input type="number" class="form-control expense-split-amount" aria-label="Amount for this line" placeholder="0.00" step="0.01" min="0" inputmode="decimal" style="padding-left:24px !important;">
        </div>
        <button type="button" class="expense-split-remove" aria-label="Remove this line" title="Remove line" style="flex:0 0 auto;min-width:44px;min-height:44px;background:none;border:none;color:var(--text-secondary);cursor:pointer;padding:6px;display:flex;align-items:center;justify-content:center;">
          <span class="material-icons" style="font-size:20px;">close</span>
        </button>
      </div>
      ${(o.jobOptions || o.vehicleOptions) ? `
      <div style="display:flex;gap:8px;margin-top:6px;flex-wrap:wrap;">
        ${sel('expense-split-job', o.jobOptions, 'Job for this line')}
        ${sel('expense-split-vehicle', o.vehicleOptions, 'Vehicle for this line')}
      </div>` : ''}
    </div>`;
}

function _addExpenseSplitLine(seed) {
  const host = document.getElementById('expense-split-lines');
  if (!host) return null;
  const max = window.CostSplit?.MAX_LINES || 20;
  if (host.querySelectorAll('.expense-split-line').length >= max) {
    window.showToast?.(`A split can have at most ${max} lines`, 'info');
    return null;
  }
  const tmp = document.createElement('div');
  tmp.innerHTML = _expenseSplitLineHTML().trim();
  const line = tmp.firstElementChild;
  host.appendChild(line);
  if (seed) {
    const setIf = (cls, v) => { const el = line.querySelector(cls); if (el && v && [...el.options].some(op => op.value === v)) el.value = v; };
    setIf('.expense-split-alloc', seed.costAllocationId);
    setIf('.expense-split-job', seed.jobId);
    setIf('.expense-split-vehicle', seed.vehicleId);
  }
  _syncExpenseSplitRemoveButtons();
  return line;
}

function _syncExpenseSplitRemoveButtons() {
  const lines = document.querySelectorAll('#expense-split-lines .expense-split-line');
  // A split is at least two lines — below that it is just the expense.
  // display (not visibility) so a hidden button takes no room on a narrow phone.
  lines.forEach(l => { const b = l.querySelector('.expense-split-remove'); if (b) b.style.display = lines.length > 2 ? 'flex' : 'none'; });
}

// Fill the LAST line with whatever is left, until someone types in it.
function _autoFillExpenseSplitLast(editedEl) {
  const lines = [...document.querySelectorAll('#expense-split-lines .expense-split-line')];
  if (lines.length < 2 || !window.CostSplit) return;
  const lastInput = lines[lines.length - 1].querySelector('.expense-split-amount');
  if (!lastInput || lastInput === editedEl || lastInput.dataset.touched === '1') return;
  const total = document.getElementById('expense-amount')?.value;
  const others = lines.slice(0, -1).map(l => l.querySelector('.expense-split-amount')?.value);
  const rem = window.CostSplit.remainingCents(total, others);
  lastInput.value = rem > 0 ? (rem / 100).toFixed(2) : '';
}

function _syncExpenseSplitRemaining() {
  const box = document.getElementById('expense-split-remaining');
  if (!box || !_isExpenseSplitActive() || !window.CostSplit) return;
  const totalRaw = document.getElementById('expense-amount')?.value;
  const totalCents = window.CostSplit.toCents(totalRaw);
  const amounts = [...document.querySelectorAll('#expense-split-lines .expense-split-amount')].map(i => i.value);
  const fmt = (c) => window.CostSplit.formatCents(c);
  let bg, fg, border, text;
  if (!Number.isFinite(totalCents) || totalCents <= 0) {
    bg = 'var(--bg-secondary)'; fg = 'var(--text-primary)'; border = 'var(--border-light)';
    text = 'Enter the receipt total above, then split it.';
  } else {
    const rem = window.CostSplit.remainingCents(totalRaw, amounts);
    if (rem === 0) {
      bg = '#DCFCE7'; fg = '#166534'; border = '#86EFAC';
      text = `Remaining to allocate: $0.00 — all ${fmt(totalCents)} is split across ${amounts.length} lines.`;
    } else if (rem > 0) {
      bg = '#FEF3C7'; fg = '#92400E'; border = '#FCD34D';
      text = `Remaining to allocate: ${fmt(rem)}`;
    } else {
      bg = '#FEE2E2'; fg = '#991B1B'; border = '#FCA5A5';
      text = `Over by ${fmt(-rem)} — the lines add up to more than the receipt.`;
    }
  }
  box.style.background = bg; box.style.color = fg; box.style.border = `1px solid ${border}`;
  box.textContent = text;
}

function _setExpenseSplitActive(on) {
  const modal = document.getElementById('expense-modal');
  const section = document.getElementById('expense-split-section');
  const linkWrap = document.getElementById('expense-split-link-wrap');
  const host = document.getElementById('expense-split-lines');
  if (!modal || !section || !host) return;
  const w = _expenseSplitFieldWraps();
  if (on) {
    // Seed line 1 from whatever the single pickers already hold, so nothing the
    // driver picked is thrown away by tapping Split.
    const seed = {
      costAllocationId: document.getElementById('expense-cost-allocation')?.value || null,
      jobId: document.getElementById('expense-job')?.value || null,
      // TASK-9592 — a truck/trailer allocation's vehicle, not the hidden select.
      vehicleId: _expenseAllocVehicle()?.id || document.getElementById('expense-vehicle')?.value || null,
    };
    modal.dataset.split = '1';
    host.innerHTML = '';
    _addExpenseSplitLine(seed);
    _addExpenseSplitLine(null);
    section.style.display = 'block';
    if (linkWrap) linkWrap.style.display = 'none';
    if (w.alloc) w.alloc.style.display = 'none';
    if (w.job) w.job.style.display = 'none';
    if (w.vehicle) w.vehicle.style.display = 'none';
    if (w.odometer) w.odometer.style.display = 'none';
    _syncExpenseSplitRemaining();
  } else {
    delete modal.dataset.split;
    host.innerHTML = '';
    section.style.display = 'none';
    if (linkWrap) linkWrap.style.display = '';
    if (w.alloc) w.alloc.style.display = '';
    if (w.job) w.job.style.display = '';
    if (w.odometer) w.odometer.style.display = '';
    onExpenseCategoryChange(); // restores the vehicle field per category
  }
}

function _wireExpenseSplit() {
  const toggle = document.getElementById('expense-split-toggle');
  if (!toggle) return;
  toggle.addEventListener('click', (ev) => {
    ev.preventDefault();
    if (_expenseReceiptFiles.length > 1) {
      window.showToast?.('Split works on one receipt at a time', 'info');
      return;
    }
    _setExpenseSplitActive(true);
  });
  document.getElementById('expense-split-cancel')?.addEventListener('click', (ev) => {
    ev.preventDefault();
    _setExpenseSplitActive(false);
  });
  document.getElementById('expense-split-add')?.addEventListener('click', () => {
    _addExpenseSplitLine(null);
    _autoFillExpenseSplitLast(null);
    _syncExpenseSplitRemaining();
  });
  const host = document.getElementById('expense-split-lines');
  host?.addEventListener('input', (ev) => {
    const t = ev.target;
    if (t && t.classList?.contains('expense-split-amount')) {
      t.dataset.touched = '1';
      _autoFillExpenseSplitLast(t);
      _syncExpenseSplitRemaining();
    }
  });
  host?.addEventListener('change', (ev) => {
    const t = ev.target;
    if (!t || !t.classList) return;
    if (t.classList.contains('expense-split-vehicle')) t.dataset.touched = '1';
    // A truck / trailer allocation names its unit — fill the line's vehicle
    // unless the driver already chose one on this line.
    if (t.classList.contains('expense-split-alloc')) {
      const line = t.closest('.expense-split-line');
      const veh = line?.querySelector('.expense-split-vehicle');
      const vid = _expenseContext?.split?.allocVehicle?.get(String(t.value));
      if (veh && veh.dataset.touched !== '1') veh.value = vid || '';
    }
  });
  host?.addEventListener('click', (ev) => {
    const btn = ev.target?.closest?.('.expense-split-remove');
    if (!btn) return;
    const lines = host.querySelectorAll('.expense-split-line');
    if (lines.length <= 2) return;
    btn.closest('.expense-split-line')?.remove();
    _syncExpenseSplitRemoveButtons();
    _autoFillExpenseSplitLast(null);
    _syncExpenseSplitRemaining();
  });
  document.getElementById('expense-amount')?.addEventListener('input', () => {
    if (!_isExpenseSplitActive()) return;
    _autoFillExpenseSplitLast(null);
    _syncExpenseSplitRemaining();
  });
}

// Split is one receipt at a time: hide the link while a multi-receipt batch is
// attached, and back out of an open split if a second receipt lands.
function _syncExpenseSplitAvailability() {
  const linkWrap = document.getElementById('expense-split-link-wrap');
  if (!linkWrap) return;
  const multi = _expenseReceiptFiles.length > 1;
  if (multi && _isExpenseSplitActive()) {
    _setExpenseSplitActive(false);
    window.showToast?.('Split turned off — it works on one receipt at a time', 'info');
  }
  if (!_isExpenseSplitActive()) linkWrap.style.display = multi ? 'none' : '';
}

/**
 * Read + validate the split lines at submit. Returns:
 *   { active:false }                         — no split, submit as before
 *   { active:true, ok:false, error, el }     — refuse, show `error` on `el`
 *   { active:true, ok:true, lines }          — lines to send as `allocations`
 * Every line's job is re-derived through _expenseVisibleJobs — never the
 * posted id — exactly like the single job picker.
 */
function _readExpenseSplitForSubmit(totalAmount, ec, costAllocRequired) {
  if (!_isExpenseSplitActive()) return { active: false };
  const rows = [...document.querySelectorAll('#expense-split-lines .expense-split-line')];
  const box = document.getElementById('expense-split-remaining');
  if (!window.CostSplit) return { active: true, ok: false, error: 'Split is still loading — try again in a second.', el: box };
  const visible = _expenseVisibleJobs(ec);
  const lines = [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const allocId = r.querySelector('.expense-split-alloc')?.value || null;
    const jobIdRaw = r.querySelector('.expense-split-job')?.value || null;
    const vehicleId = r.querySelector('.expense-split-vehicle')?.value || null;
    const amtEl = r.querySelector('.expense-split-amount');
    const job = jobIdRaw ? visible.find(j => j.id === jobIdRaw) : null;
    if (jobIdRaw && !job) return { active: true, ok: false, error: `Line ${i + 1}: that job is not available — pick another.`, el: r.querySelector('.expense-split-job') };
    if (costAllocRequired && !allocId) return { active: true, ok: false, error: `Line ${i + 1}: pick a cost allocation.`, el: r.querySelector('.expense-split-alloc') || amtEl };
    lines.push({ costAllocationId: allocId, jobId: job ? job.id : null, vehicleId, amount: amtEl?.value });
  }
  const v = window.CostSplit.validateCostAllocations(totalAmount, lines);
  if (!v.ok || !v.split) {
    return { active: true, ok: false, error: v.error || 'Check the split lines.', el: box };
  }
  return { active: true, ok: true, lines: v.lines.map(l => ({ costAllocationId: l.costAllocationId, jobId: l.jobId, vehicleId: l.vehicleId, amount: l.amount })) };
}

// Put a split error ON the line (or the counter) that caused it.
function _showExpenseSplitError(res) {
  const el = res.el;
  if (el && el.classList && el.classList.contains('form-control')) {
    el.style.borderColor = '#DC2626';
    const clear = () => { el.style.borderColor = ''; el.removeEventListener('change', clear); el.removeEventListener('input', clear); };
    el.addEventListener('change', clear); el.addEventListener('input', clear);
  }
  const box = document.getElementById('expense-split-remaining');
  if (box) {
    _syncExpenseSplitRemaining();
    box.style.background = '#FEE2E2'; box.style.color = '#991B1B'; box.style.border = '1px solid #FCA5A5';
    box.textContent = res.error;
  }
  try { (el || box)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) { /* old browsers */ }
  window.showToast?.(res.error, 'error');
}

function closeExpenseModal(opts) {
  // Lost-answer sends (2026-10-20) — closing drops them unchecked, so ask first.
  const _openModal = document.getElementById('expense-modal');
  if (!(opts && opts.force === true) && _expensePendingFor(_openModal)) {
    _expenseAsk(
      'Close without checking?',
      'Some of what you sent did not get an answer and may already be in. If you close now it will not be checked. If you are not sure it went in, tell the office instead of sending it again.',
      'Close anyway', 'Keep open'
    ).then((yes) => {
      // COD r9 — only the modal this question was about (Finish may have
      // closed it, and another opened, while the question was up).
      if (yes && document.getElementById('expense-modal') === _openModal) closeExpenseModal({ force: true });
    });
    return;
  }
  _expensePending = null;
  // Tear down any in-flight Smart Scan progress so a hung callable doesn't leave
  // a timer ticking or an object URL leaked after the modal is gone (COD 2026-05-29).
  if (typeof _expenseScanStop === 'function') { try { _expenseScanStop(); } catch (_) {} }
  document.getElementById('expense-modal')?.remove();
  _expensePickGen++; // TASK-9402 COD r5 — any scan/pick still running for this modal is now stale
}

function setExpenseMode(mode) {
  const manualBtn = document.getElementById('expense-mode-manual');
  const scanBtn = document.getElementById('expense-mode-scan');
  const scanSection = document.getElementById('expense-scan-section');
  const formSection = document.getElementById('expense-form-section');
  if (!manualBtn || !scanBtn) return;

  if (mode === 'scan') {
    scanBtn.style.background = 'var(--brand-blue)';
    scanBtn.style.color = '#fff';
    scanBtn.style.border = 'none';
    manualBtn.style.background = '#fff';
    manualBtn.style.color = 'var(--text-primary)';
    manualBtn.style.border = '1px solid var(--border-light)';
    scanSection.style.display = 'block';
    formSection.style.display = 'none';
  } else {
    manualBtn.style.background = 'var(--brand-blue)';
    manualBtn.style.color = '#fff';
    manualBtn.style.border = 'none';
    scanBtn.style.background = '#fff';
    scanBtn.style.color = 'var(--text-primary)';
    scanBtn.style.border = '1px solid var(--border-light)';
    scanSection.style.display = 'none';
    formSection.style.display = 'block';
  }
}

// TASK-9592 — a truck/trailer Cost Allocation already names the vehicle. The
// separate Vehicle field is hidden then and the allocation's vehicle is sent
// (the server derives it from its own costAllocations read either way and
// ignores a client refId). Shop / office / custom / asset keep the field:
// "parts for which truck?". null while split — each line has its own.
function _expenseAllocVehicle() {
  if (_isExpenseSplitActive()) return null;
  const opt = document.getElementById('expense-cost-allocation')?.selectedOptions?.[0];
  if (!opt || !opt.value) return null;
  const kind = opt.dataset.allocType || '';
  const refId = opt.dataset.refId || '';
  if ((kind !== 'truck' && kind !== 'trailer') || !refId) return null;
  return { id: refId, kind, display: opt.dataset.label || null };
}

function onExpenseCategoryChange() {
  const cat = document.getElementById('expense-category')?.value;
  const fuelFields = document.getElementById('expense-fuel-fields');
  const vehicleField = document.getElementById('expense-vehicle-field');
  const fluidHint = document.getElementById('expense-fluid-hint');

  const catConfig = getActiveExpenseCategories().find(c => c.value === cat);
  const showFuel = catConfig?.hasFuelFields || false;
  const showVehicle = showFuel || catConfig?.hasVehicle || false;
  const showFluidHint = cat === 'def';

  fuelFields.style.display = showFuel ? 'block' : 'none';
  // TASK-9493 — while split, each line carries its own vehicle; the single
  // Vehicle field stays hidden whatever the category.
  // TASK-9592 — and hidden when a truck/trailer allocation already names it.
  const allocVehicle = _expenseAllocVehicle();
  vehicleField.style.display = (showVehicle && !_isExpenseSplitActive() && !allocVehicle) ? 'block' : 'none';
  // COD (UX) — say which vehicle the allocation assigned, so a hidden field does
  // not read as "the Vehicle field failed to load".
  const allocNote = document.getElementById('expense-alloc-vehicle-note');
  if (allocNote) {
    const nameEl = document.getElementById('expense-alloc-vehicle-name');
    if (nameEl) nameEl.textContent = allocVehicle ? (allocVehicle.display || allocVehicle.id) : '';
    allocNote.style.display = allocVehicle ? 'block' : 'none';
  }
  fluidHint.style.display = showFluidHint ? 'block' : 'none';
}

let _expenseReceiptFile = null;
// Set when Smart Scan succeeds — passed to createDriverExpense so admins
// can audit Gemini's output if anything looks wrong.
let _expenseLastAIResponse = null;
// TASK-244 v1.2 multi-receipt state. When .length > 1, submitExpense routes
// through the pending_ocr fire-and-forget path (one upload + one callable
// per file, modal closes immediately). When .length <= 1, the legacy single-
// receipt singleton path is used — fully backward-compatible.
let _expenseReceiptFiles = [];
// TASK-9402 — receipt prep (iPhone HEIC → JPEG) runs at pick time; submitExpense
// settles it before its guards read state. _expensePickGen drops a conversion
// that a newer pick, a Clear, or a fresh modal superseded.
const _expensePhotoPrep = createPhotoPrepTracker();
let _expensePickGen = 0;

function previewExpenseReceipt(input) {
  const picked = Array.from(input.files || []);
  if (!picked.length) return;
  const gen = ++_expensePickGen;
  // TASK-9402 — a HEIC receipt was staged raw: no preview off Safari, stored as
  // HEIC bytes. Convert images (PDFs pass through). The expense has its own
  // data, so a receipt that won't convert is SKIPPED with a toast.
  return _expensePhotoPrep.track((async () => {
    const { prepared, skipped } = await prepareUploadFiles(picked);
    if (gen !== _expensePickGen || !document.getElementById('expense-modal')) return;
    if (skipped.length) window.showToast?.(skippedPhotosMessage(skipped), 'error');
    const files = prepared.map(p => p.file);
    // COD Code — a new pick REPLACES the batch. If none of it converted, the
    // previous receipts must not stay staged as if they were this pick.
    if (!files.length) { clearExpenseReceipt(); return; }
    _applyExpenseReceiptPick(files);
  })());
}

function _applyExpenseReceiptPick(files) {

  // TASK-4639 (COD Code Reviewer) — a NEW selection replaces the whole batch, so
  // it replaces the decision that went with it. Without this, picking 3 receipts
  // and ticking Smart Scan, then re-picking a single file, then re-picking
  // several again leaves the box silently still ticked — an opt-in carried over
  // from receipts that are no longer here. Reset before either branch runs.
  const _ssReset = document.getElementById('expense-multi-smartscan');
  if (_ssReset) _ssReset.checked = false;
  onExpenseSmartScanToggle();

  // Multi-file branch (TASK-244 v1.2): >1 file triggers the multi-upload UI.
  // Single-file path is unchanged.
  if (files.length > 1) {
    _expenseReceiptFiles = files;
    _expenseReceiptFile = null; // multi mode supersedes singleton
    _renderExpenseMultiReceiptThumbs();
    _updateExpenseSubmitButtonLabel();
    // Hide the single-receipt preview if it was visible.
    const singlePreview = document.getElementById('expense-receipt-preview');
    if (singlePreview) singlePreview.style.display = 'none';
    return;
  }

  // Single-file path — backward-compatible.
  _expenseReceiptFiles = [];
  _expenseReceiptFile = files[0];
  _showExpenseReceiptPreview(files[0]);
  // Clear any stale multi-thumb grid.
  const multiContainer = document.getElementById('expense-receipts-multi');
  if (multiContainer) {
    multiContainer.style.display = 'none';
    multiContainer.innerHTML = '';
  }
  const multiHint = document.getElementById('expense-multi-hint');
  if (multiHint) multiHint.style.display = 'none';
  _updateExpenseSubmitButtonLabel();
}

// TASK-244 v1.2 — render the multi-receipt thumbnail grid. Each thumb shows
// a tiny preview (image data URL) or PDF badge, with a delete button that
// removes that file from the batch. When the array drops to 1, switch back
// to the single-file UI path automatically.
function _renderExpenseMultiReceiptThumbs() {
  const container = document.getElementById('expense-receipts-multi');
  const hint = document.getElementById('expense-multi-hint');
  if (!container) return;

  if (_expenseReceiptFiles.length <= 1) {
    container.style.display = 'none';
    container.innerHTML = '';
    if (hint) hint.style.display = 'none';
    // TASK-4639 — dropping out of batch mode has to un-pick the scan too, or a
    // checkbox the driver can no longer see keeps deciding for them.
    const _ss = document.getElementById('expense-multi-smartscan');
    if (_ss) _ss.checked = false;
    onExpenseSmartScanToggle();
    if (_expenseReceiptFiles.length === 1) {
      _expenseReceiptFile = _expenseReceiptFiles[0];
      _expenseReceiptFiles = [];
      _showExpenseReceiptPreview(_expenseReceiptFile);
    }
    return;
  }

  container.style.display = 'grid';
  container.style.gridTemplateColumns = 'repeat(auto-fill, minmax(80px, 1fr))';
  container.style.gap = '6px';
  if (hint) hint.style.display = 'block';
  _syncExpenseMultiAmountWarning(); // TASK-4639 — batch size just changed

  container.innerHTML = '';
  _expenseReceiptFiles.forEach((file, idx) => {
    const cell = document.createElement('div');
    cell.style.cssText = 'position:relative;aspect-ratio:1;border:1px solid var(--border-light);border-radius:6px;overflow:hidden;background:var(--bg-secondary);display:flex;align-items:center;justify-content:center;';

    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
    if (isPdf) {
      cell.innerHTML = `
        <div style="text-align:center;padding:4px;">
          <span class="material-icons" style="font-size:24px;color:#DC2626;">picture_as_pdf</span>
          <div style="font-size:0.65rem;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:70px;">${escapeHtml(file.name || 'receipt.pdf')}</div>
        </div>
      `;
    } else {
      const img = document.createElement('img');
      img.style.cssText = 'width:100%;height:100%;object-fit:cover;';
      img.alt = 'Receipt thumbnail';
      // TASK-1383 — guarded shared reader; thumbnail is best-effort (a failed
      // read leaves an empty cell, the receipt file itself still uploads).
      window.FileReadUtil.readAsDataURL(file).then((dataUrl) => { img.src = dataUrl; }).catch(() => {});
      cell.appendChild(img);
    }

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.style.cssText = 'position:absolute;top:2px;right:2px;background:rgba(0,0,0,0.7);color:#fff;border:none;border-radius:50%;width:22px;height:22px;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;';
    removeBtn.innerHTML = '<span class="material-icons" style="font-size:14px;">close</span>';
    removeBtn.onclick = () => removeExpenseReceiptAt(idx);
    cell.appendChild(removeBtn);

    container.appendChild(cell);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// TASK-4639 — "AI shouldn't be used unless it's picked on 'record expense'."
//
// The multi-receipt batch is the ONLY expense path that reaches the server-side
// scanner (createDriverExpense pendingOcr:true → onCostEntryCreate). It lives
// inside MANUAL ENTRY mode, behind an "Attach Receipt(s)" button, and until now
// it scanned every batch without ever asking. Smart Scan mode is a separate,
// single-file, explicitly-picked flow.
//
// So the opt-in has to live on the batch itself. This reads the checkbox; the
// value goes on the payload as `smartScanRequested`, which the server already
// honours by skipping the Gemini call entirely.
// ─────────────────────────────────────────────────────────────────────────────
function _isExpenseSmartScanPicked() {
  return !!document.getElementById('expense-multi-smartscan')?.checked;
}

// Keep the sub-line honest about what each state actually does. A checkbox whose
// label doesn't change when you tick it teaches the driver nothing.
function onExpenseSmartScanToggle() {
  const sub = document.getElementById('expense-multi-smartscan-sub');
  if (sub) {
    sub.textContent = _isExpenseSmartScanPicked()
      ? 'On — Smart Tools reads each receipt and fills in the amounts. It never changes anything you typed here.'
      : 'Off — each receipt is filed exactly as you entered it and the office keys in the amounts.';
  }
  _syncExpenseMultiAmountWarning();
}

// One Amount field, several receipts. Whatever is typed goes on EVERY entry in
// the batch — that is a real multiplication of money, so it gets said plainly
// rather than being quietly zeroed the way the old payload did.
function _syncExpenseMultiAmountWarning() {
  const warn = document.getElementById('expense-multi-amount-warn');
  if (!warn) return;
  const n = _expenseReceiptFiles.length;
  const amt = parseFloat(document.getElementById('expense-amount')?.value);
  if (n > 1 && Number.isFinite(amt) && amt > 0) {
    warn.textContent = `Heads up — $${amt.toFixed(2)} will be recorded on all ${n} receipts (${n} × $${amt.toFixed(2)}). `
      + 'Leave Amount blank if each receipt is a different total.';
    warn.style.display = 'block';
  } else {
    warn.style.display = 'none';
    warn.textContent = '';
  }
}

// TASK-244 v1.2 — remove one file from the multi-receipt batch by index.
function removeExpenseReceiptAt(index) {
  if (index < 0 || index >= _expenseReceiptFiles.length) return;
  _expenseReceiptFiles.splice(index, 1);
  _renderExpenseMultiReceiptThumbs();
  _updateExpenseSubmitButtonLabel();
}

// 2026-08-23 — every save-blocked path used to be a bottom-right toast and
// nothing else. On a phone, inside a fullscreen modal, that reads as "the Save
// button is dead" — which is exactly how a driver reported it. Put the reason
// ON the field that caused it, scroll it into view, and leave it there until
// they fix it. The toast still fires; this is the signal they actually see.
function _expenseFieldError(fieldId, msg) {
  const el = document.getElementById(fieldId);
  if (!el) return;
  const wrap = el.closest('.eticket-form-field') || el.parentElement;
  if (!wrap) return;
  let note = wrap.querySelector('.expense-field-error');
  if (!note) {
    note = document.createElement('div');
    note.className = 'expense-field-error';
    note.style.cssText = 'margin-top:6px;padding:8px 10px;background:#FEE2E2;border:1px solid #FCA5A5;border-radius:8px;font-size:0.78rem;color:#991B1B;font-weight:600;line-height:1.35;';
    wrap.appendChild(note);
  }
  note.textContent = msg;
  el.style.borderColor = '#DC2626';
  try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) { el.scrollIntoView(); }
  // Clear the moment they act on it, so the modal never carries a stale red box.
  const clear = () => { note.remove(); el.style.borderColor = ''; el.removeEventListener('input', clear); el.removeEventListener('change', clear); };
  el.addEventListener('input', clear);
  el.addEventListener('change', clear);
}

// Wipe every inline error before re-validating — otherwise the first complaint
// stays on screen next to a field the driver already corrected.
function _clearExpenseFieldErrors() {
  const modal = document.getElementById('expense-modal');
  if (!modal) return;
  modal.querySelectorAll('.expense-field-error').forEach(n => n.remove());
  modal.querySelectorAll('.form-control').forEach(n => { n.style.borderColor = ''; });
}
// TASK-244 v1.2 — submit button label adapts to multi vs single mode.
function _updateExpenseSubmitButtonLabel() {
  _syncExpenseSplitAvailability(); // TASK-9493 — receipt count just changed
  const btn = document.getElementById('btn-expense-submit');
  if (!btn) return;
  if (_expensePendingFor(document.getElementById('expense-modal'))) {
    btn.innerHTML = '<span class="material-icons">sync</span> Finish sending';
    return;
  }
  const n = _expenseReceiptFiles.length;
  if (n > 1) {
    btn.innerHTML = `<span class="material-icons">cloud_upload</span> Upload ${n} Receipts`;
  } else {
    btn.innerHTML = '<span class="material-icons">save</span> Save Expense';
  }
}

function _showExpenseReceiptPreview(file) {
  const preview = document.getElementById('expense-receipt-preview');
  const img = document.getElementById('expense-receipt-img');
  const pdfBadge = document.getElementById('expense-receipt-pdf-badge');
  const pdfName = document.getElementById('expense-receipt-pdf-name');
  if (!preview) return;
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
  if (isPdf) {
    if (img) { img.style.display = 'none'; img.src = ''; }
    if (pdfBadge) pdfBadge.style.display = 'flex';
    if (pdfName) pdfName.textContent = file.name || 'receipt.pdf';
    preview.style.display = 'block';
  } else {
    if (pdfBadge) pdfBadge.style.display = 'none';
    // TASK-1383 — guarded shared reader; preview is best-effort (a failed
    // read skips the preview, the receipt file itself still uploads).
    window.FileReadUtil.readAsDataURL(file).then((dataUrl) => {
      if (img) { img.src = dataUrl; img.style.display = 'block'; }
      preview.style.display = 'block';
    }).catch(() => {});
  }
}

function clearExpenseReceipt() {
  _expensePickGen++; // TASK-9402 — a pick still converting must not reappear after Clear
  _expenseReceiptFile = null;
  // TASK-244 v1.2 — also clear the multi-receipt batch state.
  _expenseReceiptFiles = [];
  const multiContainer = document.getElementById('expense-receipts-multi');
  if (multiContainer) {
    multiContainer.style.display = 'none';
    multiContainer.innerHTML = '';
  }
  const multiHint = document.getElementById('expense-multi-hint');
  if (multiHint) multiHint.style.display = 'none';
  // TASK-4639 — the batch is gone, so the scan opt-in that belonged to it is too.
  const _ssBox = document.getElementById('expense-multi-smartscan');
  if (_ssBox) _ssBox.checked = false;
  onExpenseSmartScanToggle();
  _updateExpenseSubmitButtonLabel();
  const preview = document.getElementById('expense-receipt-preview');
  if (preview) preview.style.display = 'none';
  const img = document.getElementById('expense-receipt-img');
  if (img) { img.src = ''; img.style.display = 'none'; }
  const pdfBadge = document.getElementById('expense-receipt-pdf-badge');
  if (pdfBadge) pdfBadge.style.display = 'none';
  const input = document.getElementById('expense-receipt-input');
  if (input) input.value = '';
}

// Staged progress for the Smart Scan reader. A single Gemini call is opaque,
// so we cycle realistic step messages + show a thumbnail of what's being read
// so the user knows it's working (and roughly how far along). Returns a cleanup
// fn that stops the timers, revokes the object URL, and hides the panel — call
// it on EVERY exit path (success, no-data, race-guard, error).
function _startExpenseScanProgress(file) {
  // In-flight guard: if a previous scan is still cycling, tear it down first so
  // we never stack intervals or leak two object URLs (COD-flagged 2026-05-29).
  if (typeof _expenseScanStop === 'function') { try { _expenseScanStop(); } catch (_) {} }

  const scanStatus = document.getElementById('expense-scan-status');
  const textEl = document.getElementById('expense-scan-status-text');
  const subEl = document.getElementById('expense-scan-status-sub');
  const thumbEl = document.getElementById('expense-scan-thumb');
  const barFill = document.getElementById('expense-scan-bar-fill');
  if (scanStatus) scanStatus.style.display = 'block';

  // Thumbnail: image preview for photos, doc glyph for PDFs. Check both the
  // MIME type and the filename extension — some camera/scan apps hand us a
  // PDF with a blank or generic type (matches _showExpenseReceiptPreview).
  const isPdf = /pdf/i.test(file?.type || '') || /\.pdf$/i.test(file?.name || '');
  let objUrl = null;
  if (thumbEl) {
    if (file && file.type && file.type.startsWith('image/')) {
      objUrl = URL.createObjectURL(file);
      thumbEl.innerHTML = `<img src="${objUrl}" alt="" style="width:100%;height:100%;object-fit:cover;">`;
      thumbEl.style.display = 'block';
    } else if (isPdf) {
      thumbEl.innerHTML = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:var(--bg-secondary);"><span class="material-icons" style="font-size:40px;color:var(--brand-blue);">picture_as_pdf</span></div>`;
      thumbEl.style.display = 'block';
    } else {
      thumbEl.style.display = 'none';
    }
  }

  // A little personality — drivers stare at this on slow cellular, so give the
  // wait some character instead of a dead "Reading receipt…".
  const stages = [
    { t: '📸  Snapping it into focus…', s: 'Beaming your receipt up to Smart Tools' },
    { t: '🔍  Reading every line…', s: 'Making out the numbers and line items' },
    { t: '🧾  Hunting down the total…', s: 'Sniffing out the merchant and amount' },
    { t: '✨  Working a little magic…', s: 'Filling in the expense so you don’t have to' },
  ];
  // Honest progress mapping. The Gemini call is a single opaque request — there
  // is NO real %, so the bar eases through stage targets off the SAME stage
  // timer, parks at ~90% on the final stage (with an indeterminate sheen), and
  // only snaps to 100% when the callable actually returns (markDone below).
  // Do NOT walk this to 100% on a timer — that would fake completion before
  // the data lands.
  const stageTargets = [22, 50, 75, 90];
  let i = 0;
  const setStage = (n) => {
    if (textEl) textEl.textContent = stages[n].t;
    if (subEl) subEl.textContent = stages[n].s;
    if (barFill) {
      barFill.style.width = stageTargets[n] + '%';
      // On the final stage, hold at ~90% with a sheen instead of advancing.
      if (n === stages.length - 1) barFill.classList.add('is-holding');
    }
  };
  // Kick the bar off the 0% baseline on the next frame so the first transition
  // animates (a width set in the same paint as display:block won't ease).
  if (barFill) {
    barFill.classList.remove('is-holding', 'is-done');
    barFill.style.width = '0%';
  }
  setStage(0);
  // Advance through stages on a timer; hold on the last one until the call returns.
  const timer = setInterval(() => {
    if (i < stages.length - 1) setStage(++i);
    else clearInterval(timer);
  }, 1600);

  // Called on REAL success: stop cycling, snap the bar to 100% (green), and let
  // the user see the fill complete before the modal flips to the manual form.
  const markDone = () => {
    clearInterval(timer);
    if (barFill) {
      barFill.classList.remove('is-holding');
      barFill.classList.add('is-done');
    }
  };

  const cleanup = () => {
    clearInterval(timer);
    if (objUrl) { try { URL.revokeObjectURL(objUrl); } catch (_) {} objUrl = null; }
    if (thumbEl) { thumbEl.innerHTML = ''; thumbEl.style.display = 'none'; }
    if (scanStatus) scanStatus.style.display = 'none';
    // Reset the bar so a re-scan starts clean (no stuck/leftover fill).
    if (barFill) {
      barFill.classList.remove('is-holding', 'is-done');
      barFill.style.width = '0%';
    }
    // Reset to the first stage so a re-scan starts clean.
    if (textEl) textEl.textContent = stages[0].t;
    if (subEl) subEl.textContent = stages[0].s;
    if (_expenseScanStop === cleanup) _expenseScanStop = null;
  };
  // Expose markDone on the cleanup fn so the caller can snap-to-100 on success.
  cleanup.markDone = markDone;
  _expenseScanStop = cleanup;
  return cleanup;
}
let _expenseScanStop = null;

// ─────────────────────────────────────────────────────────────────────────────
// TASK-4639 — Smart Scan PROPOSES on the client too.
//
// The single-receipt scan path used to do this:
//     if (data.total)  document.getElementById('expense-amount').value = ...
//     if (data.vendor) document.getElementById('expense-description').value = ...
// — an unconditional overwrite. A driver who typed $96.04, then scanned to
// attach the photo, watched their own number silently become the model's. The
// value they were then asked to approve was not one they had ever entered.
//
// New rule, matching the server: fill an EMPTY field, never a full one. Where
// the scan and the driver disagree, show both and let the person choose.
// ─────────────────────────────────────────────────────────────────────────────

// Remove every scan caption/conflict row — called before each new scan so the
// modal never carries a proposal from the previous photo.
function _clearExpenseScanNotes() {
  document.getElementById('expense-modal')
    ?.querySelectorAll('.expense-scan-note').forEach(n => n.remove());
}

function _expenseFieldWrap(fieldId) {
  const el = document.getElementById(fieldId);
  if (!el) return null;
  return { el, wrap: el.closest('.eticket-form-field') || el.parentElement };
}

// Quiet caption under a field the scan actually filled in. It was empty, so
// nothing was taken away — but the driver still deserves to know the number in
// front of them came from the photo and not from their own thumbs.
function _noteExpenseFieldFilled(fieldId, whatItRead) {
  const f = _expenseFieldWrap(fieldId);
  if (!f || !f.wrap) return;
  const note = document.createElement('div');
  note.className = 'expense-scan-note';
  note.style.cssText = 'margin-top:6px;padding:6px 10px;background:#EFF6FF;border:1px solid #BFDBFE;border-radius:8px;font-size:0.74rem;color:#1E40AF;line-height:1.35;';
  note.textContent = `Filled in from the scan (${whatItRead}) — check it before saving.`;
  f.wrap.appendChild(note);
  // The moment they edit it, it's theirs again and the caption is a lie.
  const clear = () => { note.remove(); f.el.removeEventListener('input', clear); };
  f.el.addEventListener('input', clear);
}

// The disagreement case. Their value STAYS in the box; the scan sits beside it
// as a proposal with two buttons. Nothing is applied until they press one.
function _showExpenseScanConflict(fieldId, mineDisplay, scanDisplay, scanValue) {
  const f = _expenseFieldWrap(fieldId);
  if (!f || !f.wrap) return;
  const row = document.createElement('div');
  row.className = 'expense-scan-note expense-scan-conflict';
  row.style.cssText = 'margin-top:6px;padding:10px 12px;background:#FEF3C7;border:1px solid #FCD34D;border-radius:8px;font-size:0.78rem;color:#92400E;line-height:1.4;';

  const text = document.createElement('div');
  text.style.cssText = 'font-weight:600;margin-bottom:8px;';
  text.textContent = `Scan read ${scanDisplay} — you entered ${mineDisplay}.`;
  row.appendChild(text);

  const btns = document.createElement('div');
  btns.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;';

  const mkBtn = (label, primary) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    b.style.cssText = primary
      ? 'padding:7px 14px;border-radius:8px;border:none;background:var(--brand-blue);color:#fff;font-weight:600;font-size:0.78rem;cursor:pointer;'
      : 'padding:7px 14px;border-radius:8px;border:1px solid #D97706;background:#fff;color:#92400E;font-weight:600;font-size:0.78rem;cursor:pointer;';
    return b;
  };

  const useScan = mkBtn('Use scan', true);
  useScan.addEventListener('click', () => {
    f.el.value = scanValue;
    row.remove();
    _noteExpenseFieldFilled(fieldId, scanDisplay);
    if (fieldId === 'expense-amount') _syncExpenseMultiAmountWarning();
  });

  const keepMine = mkBtn('Keep mine', false);
  keepMine.addEventListener('click', () => { row.remove(); });

  btns.appendChild(useScan);
  btns.appendChild(keepMine);
  row.appendChild(btns);
  f.wrap.appendChild(row);
  try { row.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) { /* best effort */ }
}

/**
 * Put one scanned value in front of the driver.
 *   empty field    → fill it, captioned as coming from the scan
 *   same value     → say nothing; there is no decision to make
 *   different value→ leave theirs alone and offer the scan beside it
 *
 * `display` is what a human is shown ("$579.28", "“United Quest”"); `value` is
 * what actually goes in the input.
 */
function _proposeExpenseScanValue(fieldId, value, display) {
  const f = _expenseFieldWrap(fieldId);
  if (!f) return;
  if (value === null || value === undefined || value === '') return;
  const scanValue = String(value);
  const existing = String(f.el.value ?? '').trim();

  if (!existing) {
    f.el.value = scanValue;
    _noteExpenseFieldFilled(fieldId, display);
    if (fieldId === 'expense-amount') _syncExpenseMultiAmountWarning();
    return;
  }
  // Numeric fields agree on VALUE, not on string form — "96.0" and "96.00" are
  // the same number and must not be dressed up as a disagreement.
  if (f.el.type === 'number') {
    const a = parseFloat(existing);
    const b = parseFloat(scanValue);
    if (Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) < 0.005) return;
  } else if (existing.toLowerCase() === scanValue.trim().toLowerCase()) {
    return;
  }

  const mineDisplay = (fieldId === 'expense-amount')
    ? `$${(parseFloat(existing) || 0).toFixed(2)}`
    : `“${existing}”`;
  _showExpenseScanConflict(fieldId, mineDisplay, display, scanValue);
}

async function scanExpenseReceipt(input) {
  let file = input.files?.[0];
  if (!file) return;

  const stopProgress = _startExpenseScanProgress(file);

  // TASK-9402 — an iPhone HEIC went to Smart Scan AND into the stored receipt
  // raw. Convert first (PDFs pass through); the scan, the preview and the
  // upload all use the JPEG. A photo that won't convert stops here with the
  // converter's message — the driver can still type the expense by hand.
  let scanType = file.type;
  // COD r4 — this scan belongs to the expense modal it was started in. A
  // close/reopen (openExpenseModal bumps _expensePickGen) or a newer pick
  // must never receive it.
  const scanGen = ++_expensePickGen;
  try {
    // COD r3/r4 — tracked, and the converted receipt is STAGED inside the
    // tracked op, so a Submit pressed during conversion waits and then saves
    // WITH the receipt (OCR can still be running; it only prefills fields).
    const prepared = await _expensePhotoPrep.track((async () => {
      const p = await prepareUploadFile(file);
      if (scanGen === _expensePickGen && document.getElementById('expense-modal')) {
        _expenseReceiptFile = p.file;
        _showExpenseReceiptPreview(p.file);
      }
      return p;
    })());
    file = prepared.file;
    scanType = prepared.contentType;
  } catch (convErr) {
    stopProgress();
    if (scanGen !== _expensePickGen) return;
    setExpenseMode('manual');
    _showExpenseResultBanner(false, photoErrorMessage(convErr));
    return;
  }
  if (scanGen !== _expensePickGen) { stopProgress(); return; } // modal closed/reopened while converting

  try {
    // Convert to base64 — TASK-1383 guarded shared reader (settles on every
    // path; the existing Smart Scan catch below surfaces the failure).
    const base64 = await window.FileReadUtil.readAsBase64(file);

    // Call cloud function. In driver-PIN context the existing `scanReceipt`
    // would fail requireTenantMember; use the new `scanReceiptForDriver`
    // variant which accepts the driver's PIN instead. Admins (with a
    // tenant-member claim) keep using the original `scanReceipt`.
    // Use page-init `functions, httpsCallable` from firebase-config.js
    // (10.8.0 SDK — single instance shared with auth/firestore). A dynamic
    // 10.7.1 import here returns a separate SDK instance and callables
    // silently fail. See E2E findings 2026-05-12.
    const isDriverScope = !!(window.AppState?.driverModePin || window.AppState?.isPublicAccess || window.AppState?.driverMode);
    let result;
    if (isDriverScope) {
      const driverPersonId = window.AppState?.driverPersonId;
      // Token is the canonical credential — minted by validateDriverPin and
      // persisted on AppState (+ localStorage). driverPin is only populated
      // on Clock-screen entry (clock.js:10815); standard driver-PIN login
      // leaves it undefined, so the old `driverPin || driverModePin` fallback
      // was sending the literal string "true" → callable rejected.
      const driverToken = window.AppState?.driverAccessToken || '';
      const driverPin = typeof window.AppState?.driverPin === 'string' ? window.AppState.driverPin : '';
      if (!driverPersonId || (!driverToken && !driverPin)) {
        stopProgress();
        setExpenseMode('manual');
        _showExpenseResultBanner(false, "Couldn't verify driver session — please re-enter your PIN.");
        return;
      }
      const tenantId = _expenseContext?.tenantId || JobBoardState.tenantId || window.AppState?.tenant;
      if (!tenantId) {
        stopProgress();
        setExpenseMode('manual');
        _showExpenseResultBanner(false, "Couldn't resolve tenant for this session.");
        return;
      }
      const scanReceiptForDriver = httpsCallable(functions, 'scanReceiptForDriver');
      result = await scanReceiptForDriver({
        tenantId,
        personId: driverPersonId,
        driverToken,
        driverPin,
        imageBase64: base64,
        mimeType: scanType, // TASK-9402 — from the bytes
      });
    } else {
      const scanReceipt = httpsCallable(functions, 'scanReceipt');
      result = await scanReceipt({
        tenantId: _expenseContext?.tenantId || JobBoardState.tenantId || window.AppState?.tenant,
        imageBase64: base64,
        mimeType: scanType, // TASK-9402 — from the bytes
      });
    }

    const data = result.data;
    // Race guard: if the user typed an amount, hit Save, and the submit
    // closed the modal while this callable was in flight, the pre-fill
    // writes below would throw on null `#expense-amount`. Silently discard
    // the AI result — the save already happened with the user's manual
    // values, which is the intent. (COD-approved 2026-05-15.)
    // TASK-9402 COD r4 — also discard if the modal was closed and REOPENED
    // (a different expense) or a newer receipt pick superseded this scan.
    if (!document.getElementById('expense-modal') || scanGen !== _expensePickGen) { stopProgress(); return; }

    if (!data || (!data.total && !data.vendor)) {
      // No usable data — tear down immediately and drop the user into manual
      // mode so they have a clear path forward instead of staring at the scan
      // section with no visible signal.
      stopProgress();
      setExpenseMode('manual');
      _showExpenseResultBanner(false, "Couldn't read that receipt. Try a clearer photo or enter manually.");
      return;
    }

    // Real success: snap the bar to 100% (green) and let the driver see the
    // fill complete for a beat before the modal flips to the pre-filled form.
    // Honors HONEST-PROGRESS: 100% only happens here, after the data landed.
    if (typeof stopProgress.markDone === 'function') {
      stopProgress.markDone();
      await new Promise((r) => setTimeout(r, 450));
    }
    stopProgress();

    // Re-check after the success-beat delay: the modal could have been closed
    // (or a save fired) during those 450ms, which would null-deref the prefill
    // writes below. Same intent as the pre-call race guard — discard silently.
    // TASK-9402 COD r5 — and the SAME modal/pick (a close + reopen during the
    // beat bumps _expensePickGen via openExpenseModal).
    if (!document.getElementById('expense-modal') || scanGen !== _expensePickGen) return;

    // Switch to manual mode and pre-fill
    setExpenseMode('manual');

    // TASK-4639 — PROPOSE, DON'T APPLY. Empty fields get filled and captioned;
    // fields the driver already filled are left alone and the scan is offered
    // beside them. Wipe any proposal left over from a previous photo first.
    _clearExpenseScanNotes();
    if (Number.isFinite(data.total) && data.total > 0) {
      _proposeExpenseScanValue('expense-amount', data.total.toFixed(2), `$${data.total.toFixed(2)}`);
    }
    if (data.vendor) {
      _proposeExpenseScanValue('expense-description', String(data.vendor), `“${String(data.vendor)}”`);
    }
    // TASK-10141 — charge account vendor / Paid-with suggestion.
    _applyExpenseChargeHint(data);

    // Try to detect fuel from items or vendor. Category is a dropdown that always
    // has a value, so "is it empty?" can't be asked of it — instead only move it
    // off the default the form opened on. A driver who deliberately picked
    // "Permits" does not get overruled by the word "fuel" appearing in the JSON.
    const _catEl = document.getElementById('expense-category');
    const _catUntouched = !!_catEl && _catEl.value === (_catEl.dataset.openedWith || '');
    const _setCategory = (slug) => {
      if (!_catEl || !_catUntouched) return;
      if (!Array.from(_catEl.options).some(o => o.value === slug)) return; // tenant may not use it
      _catEl.value = slug;
      onExpenseCategoryChange();
    };
    const text = JSON.stringify(data).toLowerCase();
    if (text.includes('fuel') || text.includes('diesel') || text.includes('gallon') || text.includes('unleaded') || text.includes('gas station') || text.includes('pilot') || text.includes('love') || text.includes('flying j')) {
      _setCategory('fuel');
      // Try to extract gallons — same no-overwrite rule as amount/description.
      // Only when the fuel block is actually on screen — never write a proposal
      // into an input the driver cannot see to argue with.
      const _fuelBlock = document.getElementById('expense-fuel-fields');
      if (data.items?.length && _fuelBlock && _fuelBlock.style.display !== 'none') {
        for (const item of data.items) {
          const itemText = (item.description || '').toLowerCase();
          if (itemText.includes('gallon') || itemText.includes('diesel') || itemText.includes('fuel') || itemText.includes('unleaded')) {
            if (item.quantity) _proposeExpenseScanValue('expense-gallons', String(item.quantity), `${item.quantity} gal`);
            if (Number.isFinite(item.unitPrice)) _proposeExpenseScanValue('expense-ppg', item.unitPrice.toFixed(2), `$${item.unitPrice.toFixed(2)}/gal`);
          }
        }
      }
    } else if (text.includes('def') || text.includes('fluid')) {
      _setCategory('def');
    } else if (text.includes('hotel') || text.includes('inn') || text.includes('lodge') || text.includes('motel')) {
      _setCategory('lodging');
    }

    // Store the receipt file for upload (already staged at conversion time —
    // TASK-9402). COD r5: re-check the pick generation HERE, after the success
    // beat — a close + reopen during it must not receive this scan.
    if (scanGen !== _expensePickGen) return;
    _expenseReceiptFile = file;
    _showExpenseReceiptPreview(file);

    // Stash raw response for audit on submit
    _expenseLastAIResponse = data;

    // TASK-4639 — when the scan disagreed with something the driver typed, the
    // banner has to say so, or "Scanned: $579.28" reads as a done deal.
    const _conflictCount = document.getElementById('expense-modal')
      ?.querySelectorAll('.expense-scan-conflict').length || 0;
    _showExpenseResultBanner(true, _conflictCount
      ? `Scan read $${data.total?.toFixed(2) || '?'} — ${data.vendor || 'review details'}. What you already entered was kept — pick which to use below.`
      : `Scanned: $${data.total?.toFixed(2) || '?'} — ${data.vendor || 'review details'}`);
  } catch (err) {
    console.error('[Expense] Smart Scan error:', err);
    stopProgress();
    // Drop into manual mode so the user isn't stuck looking at the scan tile
    // with no preview and no visible signal that anything happened.
    setExpenseMode('manual');
    // Decode the callable error so the user sees the actual reason (PIN drift,
    // Gemini quota, malformed image) instead of a generic message.
    const raw = String(err?.message || err || '').trim();
    let msg;
    if (/Gemini API key not configured|GEMINI_API_KEY/i.test(raw)) {
      msg = "Smart Scan is temporarily offline. Please enter the expense manually.";
    } else if (/PIN|pin/.test(raw)) {
      msg = "PIN check failed — please re-enter your driver PIN and try again.";
    } else if (/imageBase64 required|invalid-argument/i.test(raw)) {
      msg = "Couldn't read that file. Try a JPEG, PNG, or PDF photo.";
    } else if (raw) {
      // Strip the "internal:" / "functions/internal:" prefix the Functions SDK
      // wraps around the server-side message, then cap length so the banner
      // stays readable on phones.
      const clean = raw.replace(/^.*?:\s*/, '').slice(0, 140);
      msg = `Smart Scan failed: ${clean}. Try a clearer photo or enter manually.`;
    } else {
      msg = "Couldn't read that receipt. Try a clearer photo or enter manually.";
    }
    _showExpenseResultBanner(false, msg);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// TASK-9211 — expense duplicate protection, client half.
//
// Chris, 2026-09-14: "I submited this once… but their are four there."
// He did submit once. The code created four. The cause is NOT a double-tap, a
// retry loop or a rebound handler — it is `submitExpenseMulti` below: it sends
// the ONE amount the driver typed with EVERY receipt in the batch. Four permit
// photos + one "$252" = four $252 rows, $1,008 on the books. Verified against
// the live docs (one shared batchId, four distinct receipt files, ~1.5s apart =
// the sequential upload loop) and reproduced end-to-end against the deployed
// callable on a throwaway tenant: one submit, four rows.
//
// So the guard is NOT a debounce and NOT a silent dedupe. Both would be theatre
// here: every one of those four writes was a distinct, intended, successful
// call, and the server cannot tell them from four real same-priced expenses.
// The only place the truth exists is in front of the human who typed the
// amount, so that is where we ask.
// ─────────────────────────────────────────────────────────────────────────────

// One id per INTENDED row. The server writes at `req_<id>`, so a replay of the
// same attempt (retry, resumed offline queue, second tab) lands on the same doc
// instead of minting another. A genuinely new submit mints a new id, so a real
// repeat expense is never blocked by this.
function _mintExpenseRequestId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().replace(/-/g, '');
    }
  } catch (_) { /* older webviews */ }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}${Math.random().toString(36).slice(2, 12)}`;
}

// Thousands-separated money for the warning copy. The module's own
// `formatMoney` renders "$1008.00"; a driver reading a dollar figure off a
// phone at a fuel island needs the comma, and this string IS the warning.
function _fmtExpenseMoney(n) {
  const v = Number(n) || 0;
  return `$${v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

// Prefer the app's own confirm modal; fall back to the native one (driver PIN
// mode renders the same index.html, but never assume shared DOM is there).
async function _expenseAsk(title, message, confirmText, cancelText) {
  if (typeof window.showConfirm === 'function') {
    return !!(await window.showConfirm(title, message, {
      confirmText, cancelText, icon: 'warning', iconColor: '#F59E0B',
    }));
  }
  return typeof window.confirm === 'function' ? window.confirm(`${title}\n\n${message}`) : true;
}

/**
 * _callExpenseWithDuplicateConfirm — one round-trip, plus a second one only if
 * the server asks a question.
 *
 * The server answers `{ ok:false, needsConfirm:true, message }` and writes
 * NOTHING when it spots a recent same-amount, same-description entry from the
 * same person. We put its sentence in front of the user; on "yes" we re-send
 * the IDENTICAL payload with confirmDuplicate:true — and the SAME
 * clientRequestId, so the answer cannot itself become a second row.
 *
 * Returns the callable result, or null when the user said no (caller treats
 * null as "cancelled", not as an error — nothing was written either way).
 */
async function _callExpenseWithDuplicateConfirm(callable, payload, st = {}) {
  const first = await _callExpenseWithRetry(callable, payload, st);
  if (!first?.data?.needsConfirm) return first;
  const ok = await _expenseAsk(
    'Looks like a repeat',
    first.data.message || 'There is already a submission for this total. Submit this one too?',
    'Yes, submit it', 'No, cancel'
  );
  if (!ok) return null;
  return _callExpenseWithRetry(callable, { ...payload, confirmDuplicate: true }, st);
}

// ─────────────────────────────────────────────────────────────────────────────
// Lost-answer saves (Kayden, TITS, 2026-10-20). The phone sent the expense, the
// server SAVED it, and the answer never made it back — the Firebase SDK calls
// that `internal`, and the driver saw "Could not save: internal". Every expense
// callable is idempotent on clientRequestId (TASK-9211: the same id replays the
// row already written, `deduped:true`, before any duplicate check), so:
//
//   1. A transport failure is re-sent ONCE automatically with the SAME payload
//      (fast failures only — a ~70s timeout is not doubled).
//   2. If there is still no answer, the EXACT payload that was sent (same id,
//      same values, same uploaded photo) is parked on the modal, the form is
//      LOCKED, and the button becomes "Finish sending". That tap re-sends the
//      parked payload — never the form — so nothing on screen can change what
//      gets filed, a landed row comes back as its replay, and no path can mint
//      a fresh id for it while it is parked. Offline or online, one receipt or
//      a batch, office or driver: one mechanism.
//   3. Closing the modal asks first; a new modal is a new submission.
//
// v2 (after COD r5 blocked v1): v1 tracked "same form -> same id" fingerprints
// and a resumable batch, and three holes came out of that — an offline resume
// fell through to the single path with a new id, "save as new" after an edit
// dropped the batch's ids, and a resumed batch filed whatever the form said
// now. Freezing the sent payload and locking the form removes all three.
// ─────────────────────────────────────────────────────────────────────────────
function _isExpenseTransportError(err) {
  const code = String(err?.code || '');
  const msg = String(err?.message || '').trim();
  if (code === 'functions/unavailable' || code === 'functions/deadline-exceeded') return true;
  // `internal` with no server sentence = the SDK got no usable response (status
  // 0 / network / CORS), or the function crashed (`INTERNAL`). Both re-send-safe.
  if (code === 'functions/internal' && /^internal$/i.test(msg)) return true;
  return /^(internal|load failed|failed to fetch|network ?error|networkerror)/i.test(msg);
}

// Per send chain `st` (one per submit / per batch receipt / per parked item,
// never shared — COD r7: a module-global could be overwritten by a second
// submit while the first is still in flight): st.transportSeen = did any call
// lose its answer; st.lastSent = the last payload actually sent (it may carry
// confirmDuplicate:true after a "yes").
const _expenseSleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function _callExpenseOnce(callable, payload, st) {
  st.lastSent = payload;
  try {
    return await callable(payload);
  } catch (err) {
    if (_isExpenseTransportError(err)) st.transportSeen = true;
    throw err;
  }
}

async function _callExpenseWithRetry(callable, payload, st = {}, delayMs = 1500) {
  try {
    return await _callExpenseOnce(callable, payload, st);
  } catch (err) {
    // deadline-exceeded already burned ~70s; the person's next tap re-sends.
    if (!_isExpenseTransportError(err) || String(err?.code || '') === 'functions/deadline-exceeded') throw err;
    console.warn('[Expense] no answer from the server — re-sending the same request once:', err?.code || err?.message);
    await _expenseSleep(delayMs);
    // The first call may still be RUNNING (not yet written), so the re-send can
    // miss the replay and get the repeat question about its own in-flight twin.
    // Give it time to land before treating that question as real.
    let res = await _callExpenseOnce(callable, payload, st);
    for (const wait of [4000, 8000]) {
      if (!res?.data?.needsConfirm) break;
      await _expenseSleep(wait);
      res = await _callExpenseOnce(callable, payload, st);
    }
    return res;
  }
}

// The parked sends: { modal, items: [{ callable, payload, confirmable, file }],
// onDone({ done }) }. Bound to ONE modal element.
let _expensePending = null;
function _expensePendingFor(modal) {
  return (_expensePending && modal && modal.isConnected !== false && _expensePending.modal === modal) ? _expensePending : null;
}

function _expenseLockForm(modal, locked, noteText) {
  if (!modal) return;
  const body = modal.querySelector('.modal-body');
  if (body) {
    body.querySelectorAll('input, select, textarea, button').forEach((el) => {
      if (locked) {
        if (!el.disabled) { el.dataset.pendingLock = '1'; el.disabled = true; }
      } else if (el.dataset.pendingLock === '1') {
        el.disabled = false;
        delete el.dataset.pendingLock;
      }
    });
    body.style.opacity = locked ? '0.6' : '';
  }
  let note = modal.querySelector('#expense-pending-note');
  if (locked) {
    if (!note) {
      note = document.createElement('div');
      note.id = 'expense-pending-note';
      note.setAttribute('role', 'status');
      note.style.cssText = 'margin:12px 16px 0;padding:10px 12px;border-radius:8px;background:#FEF3C7;color:#78350F;font-size:0.9rem;font-weight:600;line-height:1.35;';
      if (body && body.parentNode) body.parentNode.insertBefore(note, body);
      else modal.appendChild(note);
    }
    note.textContent = noteText || '';
  } else if (note) {
    note.remove();
  }
  const btn = modal.querySelector('#btn-expense-submit');
  if (btn) {
    btn.disabled = false;
    if (locked) btn.innerHTML = '<span class="material-icons">sync</span> Finish sending';
    else _updateExpenseSubmitButtonLabel();
  }
}

function _expenseParkNote(n) {
  return n > 1
    ? `${n} receipts did not get an answer — they may already be in. Tap Finish sending when you have signal. Nothing is filed twice. The details are locked: what you see is what was sent.`
    : 'Your save did not get an answer — it may already be in. Tap Finish sending when you have signal. It will not be filed twice. The details are locked: what you see is what was sent.';
}

function _expensePark(modal, items, onDone) {
  _expensePending = { modal, items, onDone };
  _expenseLockForm(modal, true, _expenseParkNote(items.length));
}

// One receipt (driver, staff or office). A "No" to a real repeat question on
// Finish sending leaves the form unlocked with nothing filed.
function _expenseParkSingle(modal, callable, payload, file, afterSave) {
  _expensePark(modal, [{ callable, payload, confirmable: true, file: payload?.receiptUrl ? null : (file || null) }],
    async ({ done }) => {
      if (!done.length) return;
      await afterSave(done[0].res, done[0].it.payload);
    });
}

// A parked driver payload keeps its data but rides the CURRENT session (the
// driver may have re-entered a PIN since). Auth only — never a data field.
function _expenseFreshAuth(payload) {
  if (!payload || !('driverToken' in payload || 'driverPin' in payload)) return payload;
  const tok = window.AppState?.driverAccessToken || '';
  const pin = typeof window.AppState?.driverPin === 'string' ? window.AppState.driverPin : '';
  return { ...payload, driverToken: tok || payload.driverToken || '', driverPin: pin || payload.driverPin || '' };
}

// Upload a receipt photo; null when it did not upload (the save goes on).
async function _uploadExpenseReceipt(tenant, file, nameHint) {
  try {
    const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase();
    const fileName = `${Date.now()}_${nameHint || 'receipt'}.${ext}`;
    const storageRef = ref(storage, `tenants/${tenant}/receipts/expenses/${fileName}`);
    // TASK-9402 — label from the bytes (the pick already converted any HEIC).
    await uploadBytes(storageRef, file, { contentType: await uploadContentType(file, 'image/jpeg') });
    return { receiptUrl: await getDownloadURL(storageRef), receiptFileName: file.name || fileName };
  } catch (uploadErr) {
    console.error('[Expense] Receipt upload failed:', uploadErr);
    return null;
  }
}

// The row went in but the photo is not on it — say so, never hide it.
function _expensePhotoMissing(res, payload, hadFile) {
  const d = res?.data || {};
  if (d.demo) return false;
  if (payload?.receiptUrl) return d.deduped === true && d.receiptOnRow === false;
  return !!hadFile;
}

// "Finish sending": re-send every parked payload exactly as it was sent.
async function _expenseFinishPending(p) {
  if (p.busy) return;
  p.busy = true;
  const btn = document.getElementById('btn-expense-submit');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Checking...'; }
  const done = [];
  const still = [];
  let lastErr = null;
  let declinedAny = false;
  const tenant = (_expenseContext || {}).tenantId || JobBoardState.tenantId || window.AppState?.tenant;
  for (const it of p.items) {
    // The first try went without its photo (upload failed): upload the SAME
    // photo now; the server puts it on the row it replays.
    if (!it.payload.receiptUrl && it.file && tenant) {
      const up = await _uploadExpenseReceipt(tenant, it.file, 'receipt');
      if (up) it.payload = { ...it.payload, receiptUrl: up.receiptUrl, receiptFileName: up.receiptFileName };
    }
    const st = {};
    try {
      const sendPayload = _expenseFreshAuth(it.payload);
      // A parked send is ALWAYS ambiguous — it was parked because an answer
      // was lost, and that first try may still be on its way (COD r10). So a
      // repeat question is first given time to turn into the replay, and a
      // "No" keeps it parked: it never unlocks the form into a fresh id.
      // (Every send here carries the SAME id, so even "Yes" cannot add a row
      // beside the first try — the atomic create collapses them.)
      let res = await _callExpenseWithRetry(it.callable, sendPayload, st);
      if (it.confirmable && res?.data?.needsConfirm) {
        await _expenseSleep(4000);
        res = await _callExpenseOnce(it.callable, sendPayload, st);
      }
      if (it.confirmable && res?.data?.needsConfirm) {
        const yes = await _expenseAsk(
          'Looks like a repeat',
          res.data.message || 'There is already a submission for this total. Submit this one too?',
          'Yes, submit it', 'No, not yet'
        );
        if (!yes) { declinedAny = true; still.push(it); continue; }
        res = await _callExpenseWithRetry(it.callable, { ...sendPayload, confirmDuplicate: true }, st);
      }
      done.push({ it, res });
    } catch (err) {
      lastErr = err;
      console.error('[Expense] finish sending failed:', err);
      // Keep what was last sent (a "yes" to a repeat stays a "yes").
      if (st.lastSent) it.payload = st.lastSent;
      still.push(it);
    }
  }
  p.busy = false;
  // COD r6 — answers collected across every Finish tap, not just this one.
  p.doneAll = (p.doneAll || []).concat(done);
  // Closed (or replaced by a new modal) while this was in flight: report the
  // outcome, but never touch whatever modal is open now.
  if (_expensePending !== p) {
    if (done.length) _showExpenseResultBanner(true, 'Your earlier expense went in.');
    return;
  }
  if (still.length) {
    p.items = still;
    _expenseLockForm(p.modal, true, _expenseParkNote(still.length));
    const msg = lastErr && !_isExpenseTransportError(lastErr)
      ? `${_expenseSaveErrorMessage(lastErr)} It may already be in — if this keeps happening, tell the office.`
      : (declinedAny && !lastErr)
        ? 'Nothing new was sent. Your first try may still be on its way — tap Finish sending in a minute to check, or Close if you are sure it is not needed.'
        : 'Still no answer. Nothing was lost — tap Finish sending again when you have signal. It will not be filed twice.';
    _showExpenseResultBanner(false, msg);
    window.showToast?.(msg, 'error');
    return;
  }
  _expensePending = null;
  _expenseLockForm(p.modal, false);
  await p.onDone({ done: p.doneAll });
}

// One place that turns a save failure into words a person can act on.
function _expenseSaveErrorMessage(err) {
  const rawMsg = String(err?.message || '');
  if (rawMsg.includes('Cost allocation is required')) return 'Pick a cost allocation';
  if (rawMsg.includes('Driver expense submission is not enabled')) return 'Your office has not enabled expense submission yet.';
  if (rawMsg.includes('Driver not found') || rawMsg.includes('driverPin') || rawMsg.includes('PIN')) {
    return 'PIN check failed — please re-enter your driver PIN and try again.';
  }
  if (rawMsg.includes('Invalid category')) return 'That category was removed by the office — pick another.';
  if (rawMsg.includes('Invalid amount') || rawMsg.includes('Amount must be')) return 'Enter a valid amount greater than $0.';
  if (_isExpenseTransportError(err)) return 'No answer from DispatchDeck (check your signal).';
  if (rawMsg) return `Could not save: ${rawMsg.replace(/^.*?:\s*/, '').slice(0, 140)}`;
  return 'Error saving expense — please try again or contact your dispatcher.';
}

async function submitExpense() {
  if (jbBlockIfReadOnlyLink('log an expense')) return;
  // TASK-9402 — a receipt still converting must join this save. Before any
  // guard reads the receipt state and before the button lock.
  // COD r4 — capture WHICH expense modal this Submit belongs to before the
  // wait; Cancel stays live, and a reopened modal must not be submitted.
  const _modalBefore = document.getElementById('expense-modal');
  const _ctxBefore = _expenseContext;
  // Lost-answer sends parked on this modal go first, exactly as they were sent.
  // Ahead of every other branch (online/offline, single/batch, the form) — no
  // path below can mint a new id for them while they are parked.
  const _parked = _expensePendingFor(_modalBefore);
  if (_parked) { await _expenseFinishPending(_parked); return; }
  // TASK-9949b — the Return toggle is on: the slip form sends (Purchase below is untouched).
  if (_modalBefore?.dataset.expKind === 'return' && _expRetMod) return _expRetMod.submit(_modalBefore);
  if (!(await settlePhotoPrepBeforeSave(_expensePhotoPrep, 'btn-expense-submit'))) return;
  const modal = document.getElementById('expense-modal');
  if (!modal) return;
  if (modal !== _modalBefore || _expenseContext !== _ctxBefore) return; // closed/reopened while converting
  _clearExpenseFieldErrors();

  const category = document.getElementById('expense-category')?.value;
  const amount = parseFloat(document.getElementById('expense-amount')?.value);
  const description = document.getElementById('expense-description')?.value?.trim() || '';
  // TASK-9493 — while split, the single pickers are hidden and IGNORED: each
  // line carries its own allocation / job / vehicle (never both).
  const _splitActive = _isExpenseSplitActive();
  const vehicleSelect = document.getElementById('expense-vehicle');
  // TASK-9592 — a truck/trailer allocation IS the vehicle (field hidden).
  const _allocVehicle = _expenseAllocVehicle();
  const vehicleId = _splitActive ? null : (_allocVehicle ? _allocVehicle.id : (vehicleSelect?.value || null));
  const vehicleDisplay = !vehicleId ? null : (_allocVehicle ? _allocVehicle.display : (vehicleSelect.selectedOptions[0]?.dataset?.display || null));
  const allocSelect = document.getElementById('expense-cost-allocation');
  const costAllocationId = _splitActive ? null : (allocSelect?.value || null);
  const costAllocationLabel = costAllocationId ? (allocSelect.selectedOptions[0]?.dataset?.label || null) : null;
  const reimbursementRequired = !!document.getElementById('expense-reimbursement')?.checked;
  // Optional notes — empty/whitespace coerces to null on server via safeStr.
  // Length-capped client-side too (maxlength=1000) but server still clamps.
  const notesRaw = document.getElementById('expense-notes')?.value || '';
  const notes = notesRaw.trim() ? notesRaw.trim().slice(0, 1000) : null;

  // TASK-10141 — "Charge account" picked: the vendor (a flagged CRM vendor, or
  // a typed name the office links later). Never a card, never a reimbursement.
  let _expenseCharge = null;
  if (_expensePaidWith() === 'charge') {
    const _cs = document.getElementById('expense-charge-vendor');
    const _cv = (_cs && _cs.value) || '';
    if (!_cv) {
      _expenseFieldError('expense-charge-vendor', 'Pick the vendor you charged it to.');
      window.showToast?.('Pick the charge-account vendor', 'error');
      return;
    }
    if (_cv === EXPENSE_CHARGE_OTHER) {
      const _nm = String(document.getElementById('expense-charge-other')?.value || '').trim().slice(0, 100);
      if (!_nm) {
        _expenseFieldError('expense-charge-other', "Type the vendor's name.");
        window.showToast?.("Type the vendor's name", 'error');
        return;
      }
      _expenseCharge = { paidWith: 'charge_account', chargeVendorId: null, chargeVendorName: _nm };
    } else {
      _expenseCharge = { paidWith: 'charge_account', chargeVendorId: _cv, chargeVendorName: _cs.selectedOptions[0]?.textContent || null };
    }
  }

  // TASK-362 Slice 2/3: optional credit-card attribution. Reimbursement-checked
  // hides the picker; if it's checked we explicitly null these out (defense-in-
  // depth — listener already clears, but belt-and-suspenders against odd race
  // where the checkbox flips between listener-fire and submit).
  const cardSelectEl = document.getElementById('expense-card-select');
  const selectedCardOpt = cardSelectEl?.selectedOptions?.[0] || null;
  // TASK-9593 — "Other / not listed" carries a last-4 ONLY, never a card id.
  const _cardIsOther = !reimbursementRequired && !_expenseCharge && cardSelectEl?.value === EXPENSE_CARD_OTHER;
  const paymentCardId = (!reimbursementRequired && !_expenseCharge && cardSelectEl?.value && !_cardIsOther) ? cardSelectEl.value : null;
  const paymentCardLast4 = paymentCardId ? (selectedCardOpt?.dataset?.last4 || null) : null;
  let paymentCardUnlistedLast4 = null;
  if (_cardIsOther) {
    const _l4 = String(document.getElementById('expense-card-other-last4')?.value || '').trim();
    if (!/^\d{4}$/.test(_l4)) {
      _expenseFieldError('expense-card-other-last4', 'Enter the last 4 digits of the card, or pick one of your cards.');
      window.showToast?.('Enter the card\'s last 4 digits', 'error');
      return;
    }
    paymentCardUnlistedLast4 = _l4;
  }

  // Validate cost allocation if tenant requires it.
  const tenantDriverExpenseSettings = _expenseContext?.driverExpenseSettings || window.AppState?.settings?.driverExpenses || {};
  // TASK-4585 — same waiver as the renderer, and the reason the 2026-08-23
  // client-only relaxation was correctly reverted: it keyed on "the select has no
  // options", which is ALSO true while the un-awaited costAllocations snapshot is
  // still in flight, so it would have silently skipped the guard on a tenant that
  // has allocations. `_costAllocationsLoaded` is what separates the two, and the
  // server now applies the identical waiver (functions/src/index.js, the
  // requireCostAllocation gate in createDriverExpense) off its own authoritative
  // read — so this is no longer a client-only relaxation that trades an inline
  // error for a round-trip failure.
  const _allocsForGuard = _expenseContext?.costAllocations || JobBoardState.costAllocations || [];
  const _allocsSettled = (_expenseContext && 'costAllocationsSettled' in _expenseContext)
    ? !!_expenseContext.costAllocationsSettled
    : !!JobBoardState._costAllocationsLoaded;
  const costAllocRequired = tenantDriverExpenseSettings.requireCostAllocation !== false
    && !(_allocsSettled && _allocsForGuard.length === 0);
  // Still loading with the requirement on: refuse rather than waive, or a slow
  // snapshot becomes a way to skip the requirement entirely.
  if (!_splitActive && tenantDriverExpenseSettings.requireCostAllocation !== false && !_allocsSettled && !costAllocationId) {
    _expenseFieldError('expense-cost-allocation', 'Still loading cost allocations — try again in a second.');
    window.showToast?.('Still loading cost allocations', 'error');
    return;
  }
  // 2026-08-23 — REVERTED a relaxation that would have skipped this guard when
  // the select had no options (the zero-allocations lock-out). COD killed it on
  // two counts: it does not actually fix the lock-out, because createDriverExpense
  // re-enforces the same requirement server-side (functions/src/index.js ~:75297),
  // so the driver would trade a clear inline error for a round-trip failure; and
  // costAllocations arrives on an un-awaited onSnapshot, so a modal opened before
  // the first snapshot looks empty and the guard would silently no-op on a tenant
  // that HAS allocations. The real fix is server-side and is boarded separately.
  if (!_splitActive && costAllocRequired && !costAllocationId) {
    _expenseFieldError('expense-cost-allocation', 'Pick a cost allocation to save this expense.');
    window.showToast?.('Pick a cost allocation', 'error');
    return;
  }

  // Detect online state — offline path saves photo + minimal context.
  const isOnline = (typeof navigator !== 'undefined') ? (navigator.onLine !== false) : true;
  const isDriverScope = !!(window.AppState?.driverModePin || window.AppState?.isPublicAccess || window.AppState?.driverMode);

  // TASK-244 v1.2 — multi-receipt fire-and-forget path. Only when driver scope
  // AND >1 receipt selected. Skips amount validation (OCR fills it in
  // server-side). Routes to the dedicated handler at the bottom of this fn.
  const isMultiReceipt = isDriverScope && _expenseReceiptFiles.length > 1 && isOnline;
  // TASK-9493 — one receipt, one split. A batch files N separate expenses and
  // has no single total to split; the link is hidden then, this is the backstop.
  if (_splitActive && _expenseReceiptFiles.length > 1) {
    _showExpenseSplitError({ error: 'Split works on one receipt at a time — remove the extra receipts or turn off the split.', el: null });
    return;
  }
  if (isMultiReceipt) {
    return submitExpenseMulti({
      // TASK-4639 — `amount` was never handed over at all, and the payload then
      // hardcoded `amount: 0` / `category: 'other'`. A driver could type $96.04,
      // press Save, and watch the number never reach Firestore.
      category, amount, description, vehicleId, vehicleDisplay,
      costAllocationId, costAllocationLabel, reimbursementRequired, notes,
      paymentCardId, paymentCardLast4,
      paymentCardUnlistedLast4, // TASK-9593
      charge: _expenseCharge, // TASK-10141
      // TASK-4639 — Chris's rule, literally: AI only runs when it's picked.
      smartScanRequested: _isExpenseSmartScanPicked(),
    });
  }

  // Offline guard — without amount AND no receipt photo, we have nothing to save.
  if (!isOnline && !_expenseReceiptFile && (!amount || amount <= 0)) {
    _expenseFieldError('expense-amount', 'You are offline — enter the amount, or attach a photo of the receipt, to save.');
    window.showToast?.('Offline — take a photo of the receipt or enter an amount to save', 'error');
    return;
  }
  // Online + amount missing — block as before.
  if (isOnline && (!amount || amount <= 0)) {
    _expenseFieldError('expense-amount', 'Enter the dollar amount to save this expense.');
    window.showToast?.('Enter an amount', 'error');
    return;
  }

  // TASK-9493 — validate the split BEFORE the button locks: lines must add up
  // to the total to the cent (the same helper the server re-checks with).
  // A still-loading allocation list with the requirement on refuses rather
  // than waives, exactly like the single picker above.
  let _splitLines = null;
  if (_splitActive) {
    if (tenantDriverExpenseSettings.requireCostAllocation !== false && !_allocsSettled) {
      _showExpenseSplitError({ error: 'Still loading cost allocations — try again in a second.', el: null });
      return;
    }
    if (!amount || amount <= 0) {
      _expenseFieldError('expense-amount', 'Enter the receipt total to split it.');
      window.showToast?.('Enter the receipt total', 'error');
      return;
    }
    const _sr = _readExpenseSplitForSubmit(amount, _expenseContext || {}, costAllocRequired);
    if (!_sr.ok) { _showExpenseSplitError(_sr); return; }
    _splitLines = _sr.lines;
  }

  const btn = document.getElementById('btn-expense-submit');
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner"></span> Saving...';

  // Lost-answer sends (2026-10-20) — set inside the try, read by the catch.
  let _sentCallable = null;
  let _afterSave = null;
  const _sentFile = _expenseReceiptFile;
  const _st = {}; // this submit's own send chain
  // COD r13 — the odometer reading is stamped with the SUBMIT time, even when
  // the row only lands on a later "Finish sending" (IFTA / PM trail).
  const _submitTs = Timestamp.now();
  try {
    const ec = _expenseContext || {};
    const tenant = ec.tenantId || JobBoardState.tenantId || window.AppState?.tenant;
    const entryId = modal.dataset.entryId;
    const jobId = modal.dataset.jobId;
    const clockEntries = ec.clockEntries || JobBoardState.clockEntries || [];
    const allTrucks = ec.trucks || JobBoardState.trucks || [];
    const entry = clockEntries.find(e => e.id === entryId);
    // TASK-1338 — the dropdown is the source of truth when it's on screen; fall
    // back to the modal's job context only when there is no picker (no jobs).
    // "— No job —" sends all four as null, so a stale dataset.jobId cannot ride
    // along on an expense the user deliberately left unassigned.
    const _jobSel = document.getElementById('expense-job');
    // TASK-9493 — a split carries its jobs per line; the parent names none.
    const _chosenJobId = _splitActive ? null : (_jobSel ? (_jobSel.value || null) : (jobId || null));
    // Re-derive from the SAME visibility helper the picker used — never trust
      // the posted id. DOM tampering or a stale context would otherwise stamp a
      // job this person cannot see (Codex v4, fifth vector).
    const job = _chosenJobId
      ? _expenseVisibleJobs(ec).find(j => j.id === _chosenJobId)
      : null;
    // Customer comes from the JOB, not the clock entry. The old behavior stamped
    // whichever customer the driver happened to be clocked into, which is wrong
    // the moment an expense is tied to a different job.
    const _expCustomerId = job ? (job.customerId || null) : null;
    const _expCustomerName = job ? (job.customer || job.customerName || null) : null;
    // TASK-355 2026-05-22: PIN -> SPA member identity resolution (ctx wins, helpers fallback).
    const driverId = ec.driverId || getActiveJobBoardPersonId();
    const driverName = ec.driverName || getActiveJobBoardPersonName();

    // Upload receipt photo before write so the costEntry doc holds the
    // download URL. Driver-PIN clients write under tenants/{tid}/... per
    // storage.rules catch-all (line 99); admin writes use the same path.
    let receiptUrl = null;
    let receiptFileName = null;
    if (_sentFile) {
      // Continue without receipt — don't block save when only the photo failed.
      // (The success banner then says the photo did not make it.)
      const up = await _uploadExpenseReceipt(tenant, _sentFile, 'receipt');
      if (up) { receiptUrl = up.receiptUrl; receiptFileName = up.receiptFileName; }
    }

    // Fuel-specific extras
    const fuelGallons = parseFloat(document.getElementById('expense-gallons')?.value) || null;
    const fuelPricePerGallon = parseFloat(document.getElementById('expense-ppg')?.value) || null;
    const fuelState = document.getElementById('expense-state')?.value || null;
    // TASK-1200 — optional odometer at fill-up (IFTA audit trail). Read ONLY
    // when the fuel block is actually visible: unlike gallons/ppg (which the
    // server drops for non-fuel categories), the odometer also drives a
    // client-side metersLog append below — a stale value left in the hidden
    // input after a category switch must not log a phantom meter reading.
    const _fuelFieldsEl = document.getElementById('expense-fuel-fields');
    const _fuelFieldsVisible = !!_fuelFieldsEl && _fuelFieldsEl.style.display !== 'none';
    const _odoRaw = parseFloat(document.getElementById('expense-odometer')?.value);
    const fuelOdometer = (_fuelFieldsVisible && Number.isFinite(_odoRaw) && _odoRaw > 0 && _odoRaw < 10000000)
      ? _odoRaw : null;

    // TASK-527 — routing for a signed-in NON-ADMIN member. A member who is NOT a
    // driver-scope session and NOT an owner/legacy-admin used to fall into the
    // admin branch below → createExpenseAdmin → requireTenantAdmin → "Admin
    // access required". They now route to createMemberExpense (server gates on
    // the `billing` module, stamps source:'staff'/status:'pending' for AP review).
    // Owner / legacy-admin (admin role, no modules) still go through the admin
    // path; PIN/public still go through the driver path above.
    const _ma = window.AppState?.memberAccess;
    const _maBlocked = _ma && ['revoked', 'inactive', 'pending'].includes(_ma.status);
    const _isScopedMember = !!_ma && !_maBlocked
      && _ma.role !== 'owner'
      && !(_ma.role === 'admin' && (!Array.isArray(_ma.modules) || _ma.modules.length === 0))
      && Array.isArray(_ma.modules);
    const isMemberScope = !isDriverScope && _isScopedMember;

    // TASK-1200 — capture the callable result (costEntryId + demo flag) so the
    // metersLog append below can stamp sourceId and skip demo no-op saves.
    let _saveResult = null;

    // TASK-9211 — one id for this one submit, shared by whichever branch runs
    // AND by the confirm re-send inside _callExpenseWithDuplicateConfirm, so
    // answering "yes, submit it" can never itself mint a second row.
    const _clientRequestId = _mintExpenseRequestId();

    // Everything that happens once the server has the row — shared by this
    // submit and by "Finish sending" after a lost answer (same values: the
    // closure is the submit that built the parked payload).
    let _sentPayload = null;
    _afterSave = async (_saveResult, _usedPayload) => {
      // TASK-1200 — IFTA/PM odometer trail: when the fuel expense named a
      // vehicle AND an odometer was typed, append a metersLog reading mirroring
      // the pretrip/DVIR write shape (pretrip.js:3453 — getLatestMeters and the
      // maintenance PM intervals match on truckId + read `miles`). Driver PINs
      // are allowed to create metersLog (firestore.rules metersLog block —
      // canWriteTenant), same as the DVIR path. BEST-EFFORT: the expense is
      // already saved — a metersLog denial or network blip must never surface
      // as a failed expense (COD Edge #2). No vehicle selected → costEntry
      // only, no metersLog (COD Edge #1). Demo tenants skip (the server no-op'd
      // the expense too — don't leave a real metersLog doc behind).
      if (vehicleId && fuelOdometer && !_saveResult?.data?.demo && _allocVehicle?.kind !== 'trailer') { // TASK-9592
        try {
          await addDoc(collection(db, 'tenants', tenant, 'metersLog'), {
            truckId: vehicleId,
            truckUnit: vehicleDisplay || '',
            type: 'reading',
            miles: fuelOdometer,
            hours: null,
            timestamp: _submitTs,
            source: 'fuel_expense',
            sourceId: _saveResult?.data?.costEntryId || null,
            createdAt: serverTimestamp() // TASK-9898 follow-up: the 7-day review clock (server time)
          });
        } catch (mlErr) {
          console.warn('[Expense] metersLog append failed (expense saved ok):', mlErr);
        }
      }

      // COD r8 — an answer that arrives after the form was closed / reopened
      // must not clear or close the NEW form; the banner below still reports it.
      if (document.getElementById('expense-modal') === modal) {
        _expenseReceiptFile = null;
        _expenseLastAIResponse = null;
        closeExpenseModal();
      }
      // TASK-527: member submissions are PENDING admin review (not saved/approved).
      // Use an explicit "submitted for admin review" message so a staff member
      // doesn't think it failed and duplicate-submit.
      const successMsg = reimbursementRequired
        ? `$${(amount || 0).toFixed(2)} reimbursement request submitted`
        : ((isDriverScope || isMemberScope)
            ? (isMemberScope
                ? `$${(amount || 0).toFixed(2)} expense submitted for admin review`
                : `$${(amount || 0).toFixed(2)} expense submitted for review`)
            : `$${(amount || 0).toFixed(2)} expense saved`)
        + (_splitLines ? ` — split ${_splitLines.length} ways` : '');
      // TASK-9493 — a server that predates the split ignores `allocations` and
      // files the receipt unsplit. Say so instead of claiming the split landed
      // (new servers always answer with `splitLines`; a replayed request does not).
      if (_splitLines && _saveResult?.data && !_saveResult.data.demo && _saveResult.data.deduped !== true
        && !('splitLines' in _saveResult.data)) {
        const warnMsg = `$${(amount || 0).toFixed(2)} expense saved, but the split did not save — tell the office which trucks / jobs it belongs to.`;
        _showExpenseResultBanner(false, warnMsg);
        window.showToast?.(warnMsg, 'error');
        return;
      }
      // Driver-side: surface a persistent success banner (lives 8s, dismissable)
      // because the default 1.8s toast was being missed on phones. Lacey reported
      // "nothing happens" — root cause was a silent PIN-mismatch error + a
      // success toast too short to see. See bugfix 2026-05-14.
      // Lost-answer v2 — the row went in but its photo did not (the upload
      // failed, or a replay could not attach it): say so, never hide it.
      if (_expensePhotoMissing(_saveResult, _usedPayload || _sentPayload, !!_sentFile)) {
        const photoMsg = `${successMsg} — but the receipt photo did not make it. Give the receipt to the office.`;
        _showExpenseResultBanner(false, photoMsg);
        window.showToast?.(photoMsg, 'error');
        return;
      }
      _showExpenseResultBanner(true, successMsg);
      if (isDriverScope) {
        window.showToast?.(successMsg, 'success');
      } else {
        window.showToast?.(successMsg, 'success');
      }
    };

    if (isDriverScope) {
      // Driver path → callable (rules block direct costEntries writes for drivers).
      // Prefer driverAccessToken (always present after PIN login); fall back
      // to AppState.driverPin only when Clock-screen entry stashed it.
      // The old fallback `driverPin || driverModePin` was sending the literal
      // boolean true → string "true" → server PIN-mismatch (Chris bug 2026-05-14).
      const driverToken = window.AppState?.driverAccessToken || '';
      const driverPin = typeof window.AppState?.driverPin === 'string' ? window.AppState.driverPin : '';
      if (!driverToken && !driverPin) {
        throw new Error('Missing driver session — please re-enter your PIN.');
      }
      // Use page-init `functions, httpsCallable` from firebase-config.js
      // (10.8.0). Dynamic 10.7.1 imports break SDK identity (E2E 2026-05-12).
      const createDriverExpense = httpsCallable(functions, 'createDriverExpense');
      const payload = {
        tenantId: tenant,
        personId: driverId,
        driverToken,
        driverPin,
        category,
        amount: amount || 0,
        description,
        vehicleId: vehicleId || null,
        vehicleDisplay: vehicleDisplay || null,
        vehicleType: vehicleId ? (allTrucks.find(t => t.id === vehicleId)?.type || null) : null,
        jobId: job?.id || null,
        jobName: job?.title || job?.name || null,
        customerId: _expCustomerId,
        customerName: _expCustomerName,
        costAllocationId: costAllocationId || null,
        costAllocationLabel: costAllocationLabel || null,
        reimbursementRequired,
        notes,
        // TASK-362 Slice 2/3: optional credit-card attribution.
        paymentCardId,
        paymentCardLast4,
        paymentCardUnlistedLast4, // TASK-9593 — "Other / not listed"
        ...(_expenseCharge || {}), // TASK-10141 — charge account at the vendor
        fuelGallons,
        fuelPricePerGallon,
        fuelState,
        fuelOdometer, // TASK-1200 — optional, server-validated
        receiptUrl,
        receiptFileName,
        parsedByAI: !!_expenseLastAIResponse,
        rawAIResponse: _expenseLastAIResponse || null,
        offline: !isOnline || (amount || 0) <= 0, // flag as offline if no amount
        clientRequestId: _clientRequestId, // TASK-9211 idempotency
        // TASK-9493 — split lines (null = unsplit, sent as before).
        ...(_splitLines ? { allocations: _splitLines } : {}),
      };
      _sentCallable = createDriverExpense;
      _sentPayload = payload;
      _saveResult = await _callExpenseWithDuplicateConfirm(createDriverExpense, payload, _st);
    } else if (isMemberScope) {
      // TASK-527 member path — signed-in non-admin staff (gated on `billing`
      // module server-side). Writes source:'staff'/status:'pending' → lands in
      // the AP review queue for an admin to approve. NO approval/source/status
      // sent from the client (server hardcodes them).
      const createMemberExpense = httpsCallable(functions, 'createMemberExpense');
      const payload = {
        tenantId: tenant,
        category,
        amount: amount || 0,
        description,
        vehicleId: vehicleId || null,
        vehicleDisplay: vehicleDisplay || null,
        vehicleType: vehicleId ? (allTrucks.find(t => t.id === vehicleId)?.type || null) : null,
        personId: driverId || null,
        personName: driverName || null,
        jobId: job?.id || null,
        jobName: job?.title || job?.name || null,
        customerId: _expCustomerId,
        customerName: _expCustomerName,
        costAllocationId: costAllocationId || null,
        costAllocationLabel: costAllocationLabel || null,
        reimbursementRequired,
        notes,
        paymentCardId,
        paymentCardLast4,
        paymentCardUnlistedLast4, // TASK-9593 — server checks it for self-service members
        ...(_expenseCharge || {}), // TASK-10141
        fuelGallons,
        fuelPricePerGallon,
        fuelState,
        fuelOdometer, // TASK-1200 — optional, server-validated
        receiptUrl,
        receiptFileName,
        parsedByAI: !!_expenseLastAIResponse,
        rawAIResponse: _expenseLastAIResponse || null,
        clientRequestId: _clientRequestId, // TASK-9211 idempotency
        // TASK-9493 — split lines (null = unsplit, sent as before).
        ...(_splitLines ? { allocations: _splitLines } : {}),
      };
      _sentCallable = createMemberExpense;
      _sentPayload = payload;
      _saveResult = await _callExpenseWithDuplicateConfirm(createMemberExpense, payload, _st);
    } else {
      // Admin path — call createExpenseAdmin (requireTenantAdmin server-side).
      // v1.1 refactor (2026-05-12): used to write directly to costEntries with
      // source:'driver', which polluted the AP "Driver Expenses" filter pill
      // (filters `where('source','==','driver')`). Now goes through a dedicated
      // callable that stamps `source:'admin'` + `status:'approved'` + the
      // actor's uid as approvedBy. Cleaner audit trail, semantically honest
      // (admins are NOT drivers), and keeps the driver-submitted review queue
      // free of admin-recorded receipts.
      const createExpenseAdmin = httpsCallable(functions, 'createExpenseAdmin');
      const payload = {
        tenantId: tenant,
        category,
        amount: amount || 0,
        description,
        vehicleId: vehicleId || null,
        vehicleDisplay: vehicleDisplay || null,
        vehicleType: vehicleId ? (allTrucks.find(t => t.id === vehicleId)?.type || null) : null,
        personId: driverId || null,
        personName: driverName || null,
        jobId: job?.id || null,
        jobName: job?.title || job?.name || null,
        customerId: _expCustomerId,
        customerName: _expCustomerName,
        costAllocationId: costAllocationId || null,
        costAllocationLabel: costAllocationLabel || null,
        reimbursementRequired,
        notes,
        // TASK-362 Slice 2/3: optional credit-card attribution.
        paymentCardId,
        paymentCardLast4,
        ...(_expenseCharge || {}), // TASK-10141
        fuelGallons,
        fuelPricePerGallon,
        fuelState,
        fuelOdometer, // TASK-1200 — optional, server-validated
        receiptUrl,
        receiptFileName,
        parsedByAI: !!_expenseLastAIResponse,
        rawAIResponse: _expenseLastAIResponse || null,
        clientRequestId: _clientRequestId, // TASK-9211 idempotency
        // TASK-9493 — split lines (null = unsplit, sent as before).
        ...(_splitLines ? { allocations: _splitLines } : {}),
      };
      _sentCallable = createExpenseAdmin;
      _sentPayload = payload;
      _saveResult = await _callExpenseWithDuplicateConfirm(createExpenseAdmin, payload, _st);
    }

    // TASK-9211 — the user answered "no" to the repeat warning. NOTHING was
    // written (the server returns the question instead of writing), so this is
    // a cancel, not an error: leave the modal open with their typed values
    // intact so they can correct the amount or back out, and do NOT fall
    // through to the success banner / metersLog append below.
    if (_saveResult === null) {
      // Lost-answer v2 — an answer went missing on the way, so the "repeat"
      // may be this very save: park it (Finish sending replays it) instead of
      // treating it as declined.
      if (_st.transportSeen && _sentCallable && _st.lastSent) {
        // COD r13 — closed / reopened meanwhile: never park on the new form,
        // and never claim nothing was sent.
        if (document.getElementById('expense-modal') !== modal) {
          _showExpenseResultBanner(false, 'Your expense did not get an answer and may already be in — check with the office before sending it again.');
          return;
        }
        _expenseParkSingle(modal, _sentCallable, _st.lastSent, _sentFile, _afterSave);
        _showExpenseResultBanner(false, _expenseParkNote(1));
        return;
      }
      _showExpenseResultBanner(false, 'Nothing was submitted — this looked like a repeat.');
      window.showToast?.('Nothing was submitted', 'info');
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">save</span> Save Expense';
      return;
    }

    await _afterSave(_saveResult);
  } catch (err) {
    console.error('[Expense] Save error:', err);
    // Lost-answer v2 — ANY lost answer in this submit (even if the automatic
    // re-send then got a refusal) leaves the first try ambiguous: park the
    // exact payload that was sent and lock the form. Finish sending replays it.
    // Only onto the modal this submit belongs to (closed / reopened meanwhile:
    // say it may be in, never park it on someone else's form).
    const _lost = (_isExpenseTransportError(err) || _st.transportSeen) && _sentCallable && _st.lastSent;
    if (_lost && document.getElementById('expense-modal') !== modal) {
      _showExpenseResultBanner(false, 'Your expense did not get an answer and may already be in — check with the office before sending it again.');
      return;
    }
    if (_lost) {
      _expenseParkSingle(modal, _sentCallable, _st.lastSent, _sentFile, _afterSave);
      _showExpenseResultBanner(false, _expenseParkNote(1));
      window.showToast?.('No answer yet — tap Finish sending when you have signal', 'error');
      return;
    }
    // Surface the callable's actual error message so the driver can self-correct
    // (PIN mismatch / not enabled / etc.). The previous generic "Error saving expense"
    // hid real failures (e.g. Lacey's 4 driver_pin_mismatch security events on TITS
    // 2026-05-13 → user saw nothing, AP saw 0 expenses).
    const msg = _expenseSaveErrorMessage(err);
    _showExpenseResultBanner(false, msg);
    window.showToast?.(msg, 'error');
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">save</span> Save Expense';
  }
}

/**
 * TASK-244 v1.2 — multi-receipt fire-and-forget submission.
 *
 * Called only from submitExpense when isDriverScope && >1 receipt selected.
 * For each file:
 *   1. Upload to tenants/{tid}/receipts/expenses/{batchId}_{n}.{ext}
 *   2. Call createDriverExpense with pendingOcr:true (writes a costEntries
 *      doc at status:'pending_ocr'; onCostEntryCreate trigger runs OCR
 *      server-side and flips status to 'pending' once parsed).
 *
 * Uploads run sequentially (not Promise.all) to keep the order predictable
 * and avoid hammering Storage with N parallel large-image uploads from a
 * mobile device. Each upload is independent — one failure doesn't abort
 * the rest. The summary toast reports any failed file names.
 *
 * UX: modal closes immediately after the loop completes, toast tells the
 * driver to check the office's review queue. No spinner-blocking. No need
 * to wait for OCR.
 */
async function submitExpenseMulti({
  category, amount, description, vehicleId, vehicleDisplay,
  costAllocationId, costAllocationLabel, reimbursementRequired, notes,
  paymentCardId, paymentCardLast4, // TASK-362 Slice 2/3
  paymentCardUnlistedLast4, // TASK-9593 — "Other / not listed"
  charge, // TASK-10141 — { paidWith, chargeVendorId, chargeVendorName } or null
  smartScanRequested, // TASK-4639 — did a human PICK Smart Scan for this batch?
}) {
  if (jbBlockIfReadOnlyLink('log an expense')) return;
  const modal = document.getElementById('expense-modal');
  if (!modal) return;
  const files = _expenseReceiptFiles.slice(); // snapshot — user may not edit during upload
  if (!files.length) return;

  // ── TASK-9211 — the fix for "I submitted this once but there are four there".
  //
  // Every payload below carries the SAME `amount`. With N receipts that mints N
  // rows at that amount: the typed $252 is copied verbatim onto all four, and
  // the office sees $1,008 of pending reimbursement from one $252 receipt.
  // Exactly what happened to Chris on 2026-09-14 (batch-1789400448922-x30tm3,
  // four rows, IMG_9925-9928).
  //
  // 🔴 THE CONDITION IS `amount > 0`, WITH NO SMART SCAN EXEMPTION, and the
  // first cut of this guard got that wrong. It assumed Smart Scan differentiates
  // the rows — that OCR reads each receipt and replaces the typed figure. It
  // does NOT. TASK-4639's rule #1 is "NEVER overwrite a value a human typed":
  // `_buildOcrPatchFromScan` (functions/src/index.js) sets ocrStatus:'suggested'
  // and DELIBERATELY OMITS `amount` whenever userEnteredAmount is present. It
  // only writes an amount on the 'applied' branch, which requires the driver to
  // have typed NOTHING. So a typed amount survives the scan on every receipt,
  // Smart Scan on or off, and the $1,008 books either way. (Caught by COD —
  // Edge Case Hunter, Code Reviewer and Independent Code Reviewer, all three
  // independently, then confirmed by reading the trigger.)
  //
  // When nothing was typed, `amount` is 0, this guard does not fire, and OCR is
  // free to give each receipt its own figure — which is the case it is for.
  //
  // We do NOT guess what the driver meant. Photographing four separate $252
  // permits is a real thing, and silently collapsing them would lose real money
  // — worse than a duplicate a human can reject. So we say the total out loud
  // and let them answer. One extra tap; a legitimate repeat stays possible.
  // ── TASK-9211 (COD Adversary) — the SAME photo attached twice.
  //
  // Distinct from the amount fan-out below and it fires even when nothing was
  // typed, because that is the case the server cannot see at all: with no typed
  // amount the entries are created at $0 and OCR fills each one in afterwards,
  // so `_findRecentDuplicateExpense` runs long before either amount exists —
  // and it is skipped inside a batch anyway. Adding a receipt, removing it, and
  // adding it back is an ordinary phone fumble that would otherwise bill twice.
  //
  // Name + size is the only identity available client-side without hashing
  // megabytes on a phone. It is a WARNING, never a block: two receipts really
  // can share a name (`image.jpg` from a camera roll is the live example —
  // `trippin-trucking` has two legitimate $30 "EVERYTHING SAFETY" entries,
  // 2.5 months and two different drivers apart, both named image.jpg).
  {
    const seen = new Map();
    const dupNames = [];
    for (const f of files) {
      const key = `${f.name || ''}|${f.size || 0}`;
      if (seen.has(key)) { if (!dupNames.includes(f.name)) dupNames.push(f.name || 'a receipt'); }
      else seen.set(key, true);
    }
    if (dupNames.length) {
      const go = await _expenseAsk(
        'The same photo is attached twice',
        `${dupNames.join(', ')} appears more than once in this batch. Each copy files its own `
        + `expense, so the office would see it twice. Remove the extra unless these really are `
        + `separate receipts.`,
        'Submit anyway', 'Go back'
      );
      if (!go) {
        window.showToast?.('Nothing was submitted.', 'info');
        return;
      }
    }
  }

  if (files.length > 1 && Number.isFinite(amount) && amount > 0) {
    const each = _fmtExpenseMoney(amount);
    const total = _fmtExpenseMoney(amount * files.length);
    const go = await _expenseAsk(
      'One amount, several receipts',
      `You entered ${each} and attached ${files.length} receipts. That files ${files.length} separate `
      + `${each} expenses — ${total} in total. If these ${files.length} photos are all one `
      + `${each} expense, go back and send them one at a time. To have each receipt read `
      + `separately, clear the amount first — Smart Tools never overwrites a figure you typed.`,
      `Yes — ${each} each (${total})`, 'Go back'
    );
    if (!go) {
      window.showToast?.('Nothing was submitted.', 'info');
      return;
    }
  }

  const btn = document.getElementById('btn-expense-submit');
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner"></span> Uploading...';

  try {
    const ec = _expenseContext || {};
    const tenant = ec.tenantId || JobBoardState.tenantId || window.AppState?.tenant;
    const entryId = modal.dataset.entryId;
    const jobId = modal.dataset.jobId;
    const clockEntries = ec.clockEntries || JobBoardState.clockEntries || [];
    const allTrucks = ec.trucks || JobBoardState.trucks || [];
    const entry = clockEntries.find(e => e.id === entryId);
    // TASK-1338 — the dropdown is the source of truth when it's on screen; fall
    // back to the modal's job context only when there is no picker (no jobs).
    // "— No job —" sends all four as null, so a stale dataset.jobId cannot ride
    // along on an expense the user deliberately left unassigned.
    const _jobSel = document.getElementById('expense-job');
    const _chosenJobId = _jobSel ? (_jobSel.value || null) : (jobId || null);
    // Re-derive from the SAME visibility helper the picker used — never trust
      // the posted id. DOM tampering or a stale context would otherwise stamp a
      // job this person cannot see (Codex v4, fifth vector).
    const job = _chosenJobId
      ? _expenseVisibleJobs(ec).find(j => j.id === _chosenJobId)
      : null;
    // Customer comes from the JOB, not the clock entry. The old behavior stamped
    // whichever customer the driver happened to be clocked into, which is wrong
    // the moment an expense is tied to a different job.
    const _expCustomerId = job ? (job.customerId || null) : null;
    const _expCustomerName = job ? (job.customer || job.customerName || null) : null;
    const driverId = ec.driverId || window.AppState?.driverPersonId;

    const driverToken = window.AppState?.driverAccessToken || '';
    const driverPin = typeof window.AppState?.driverPin === 'string' ? window.AppState.driverPin : '';
    if (!driverToken && !driverPin) {
      throw new Error('Missing driver session — please re-enter your PIN.');
    }

    const createDriverExpense = httpsCallable(functions, 'createDriverExpense');
    const batchId = `batch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    // TASK-9211 — ONE id per intended row, so a retried file re-lands on its own
    // doc instead of adding a fifth. Minted per-file inside the loop below off
    // this batch seed; a re-run of the whole batch mints a fresh seed, because a
    // driver deliberately re-uploading the same photos IS a new submission.
    const reqSeed = _mintExpenseRequestId();

    const failures = []; // {fileName, reason}
    let successCount = 0;
    // Lost-answer v2 — receipts whose save was SENT but got no answer: their
    // exact payloads (photo already uploaded, own id) park on the modal.
    const parked = [];
    const notSentFiles = [];
    let noPhotoFirst = 0;

    // Sequential loop — keeps Storage + Gemini fanout predictable from a phone.
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const idxLabel = `${i + 1}/${files.length}`;
      btn.innerHTML = `<span class="btn-spinner"></span> Uploading ${idxLabel}...`;
      const st = {}; // this receipt's own send chain; st.lastSent stays unset until the save is actually sent
      try {
        // 1. Upload to Storage.
        const ts = Date.now();
        const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase();
        const fileName = `${ts}_${i}_receipt.${ext}`;
        const storageRef = ref(storage, `tenants/${tenant}/receipts/expenses/${fileName}`);
        // TASK-9402 — label from the bytes (the pick already converted any HEIC).
        await uploadBytes(storageRef, file, { contentType: await uploadContentType(file, 'image/jpeg') });
        const receiptUrl = await getDownloadURL(storageRef);

        // 2. Create the pending_ocr costEntries doc via callable.
        const payload = {
          tenantId: tenant,
          personId: driverId,
          driverToken,
          driverPin,
          // pendingOcr branch: server skips amount + category validation.
          pendingOcr: true,
          batchId,
          // TASK-9211 — stable per file. The server writes at `req_<id>`, so a
          // retry of THIS file is a no-op instead of another row.
          clientRequestId: `${reqSeed}f${i}`,
          // TASK-4639 — the driver's OWN category, not a hardcoded 'other'. The
          // server-side fuel upgrade still only fires when this is 'other', so a
          // deliberate pick survives the scan.
          category: category || 'other',
          // TASK-4639 — and the driver's OWN amount. This was a hardcoded 0: the
          // typed figure was destroyed here, client-side, before the callable was
          // ever reached, and the trigger then wrote its guess into the hole.
          // 0 now means one thing only — nobody typed anything.
          amount: (Number.isFinite(amount) && amount > 0) ? amount : 0,
          description: description || '',
          // TASK-4639 — the opt-in. `false` makes onCostEntryCreate skip Gemini
          // outright; the entries land exactly as submitted for the office to key
          // in. Always sent explicitly so "not picked" can never be read as
          // "unspecified" (which the server treats as legacy auto-scan).
          smartScanRequested: smartScanRequested === true,
          vehicleId: vehicleId || null,
          vehicleDisplay: vehicleDisplay || null,
          vehicleType: vehicleId ? (allTrucks.find(t => t.id === vehicleId)?.type || null) : null,
          jobId: job?.id || null,
          jobName: job?.title || job?.name || null,
          customerId: _expCustomerId,
          customerName: _expCustomerName,
          costAllocationId: costAllocationId || null,
          costAllocationLabel: costAllocationLabel || null,
          reimbursementRequired,
          notes: notes || null,
          // TASK-362 Slice 2/3: card attribution applies to ALL receipts in
          // the batch (same as cost-allocation, vehicle, reimbursement flag).
          paymentCardId: paymentCardId || null,
          paymentCardLast4: paymentCardLast4 || null,
          paymentCardUnlistedLast4: paymentCardUnlistedLast4 || null, // TASK-9593
          ...(charge || {}), // TASK-10141 — same for every receipt in the batch
          receiptUrl,
          receiptFileName: file.name || fileName,
        };
        // A lost answer re-sends once with the same per-file id.
        const res = await _callExpenseWithRetry(createDriverExpense, payload, st);
        successCount += 1;
        // COD r7 — a replay that could not attach this photo is said out loud.
        if (_expensePhotoMissing(res, payload, false)) noPhotoFirst += 1;
      } catch (err) {
        console.error(`[Expense Multi] File ${i + 1} failed:`, err);
        // Sent, then no answer (even if the re-send got a refusal): it may be
        // in. Park it — never report it as failed, never let it be re-picked.
        // An upload failure never reaches the send, so it stays a plain
        // failure (nothing was filed; the driver still has the photo).
        if ((_isExpenseTransportError(err) || st.transportSeen) && st.lastSent) {
          parked.push({ callable: createDriverExpense, payload: st.lastSent, confirmable: false, file: null });
          continue;
        }
        const rawMsg = String(err?.message || '').replace(/^.*?:\s*/, '').slice(0, 80);
        failures.push({ fileName: file.name || `Receipt ${i + 1}`, reason: rawMsg || 'unknown error' });
        notSentFiles.push(file); // never sent, or the server said no: safe to send again as new
      }
    }

    // COD r8 — closed / reopened during the upload: report, never touch the
    // form that is open now (its staged photos and parked sends are its own).
    if (document.getElementById('expense-modal') !== modal) {
      const msg = parked.length
        ? `${successCount} of ${files.length} receipts went in. ${parked.length} did not get an answer and may already be in — check with the office before sending ${parked.length === 1 ? 'it' : 'them'} again.`
        : `${successCount} of ${files.length} receipts went in.${failures.length ? ` Not sent: ${failures.map(f => f.fileName).slice(0, 3).join(', ')}.` : ''}`;
      _showExpenseResultBanner(!parked.length && !failures.length && !noPhotoFirst, msg);
      window.showToast?.(msg, (parked.length || failures.length || noPhotoFirst) ? 'error' : 'success');
      return;
    }
    if (parked.length) {
      // Nothing staged survives: the parked payloads ARE these receipts, and a
      // re-pick would mint new ids. The form locks; Finish sending replays.
      _expenseReceiptFiles = [];
      _expenseReceiptFile = null;
      _renderExpenseMultiReceiptThumbs();
      const failedNames = failures.map(f => f.fileName).slice(0, 3).join(', ');
      const failedNote = failures.length
        ? ` Not sent: ${failedNames}${failures.length > 3 ? ` (+${failures.length - 3} more)` : ''} — ${failures.length === 1 ? 'it stays' : 'they stay'} here to send after.`
        : '';
      const sentBefore = successCount;
      const retryFiles = notSentFiles.slice();
      _expensePark(modal, parked, async ({ done }) => {
        _expenseLastAIResponse = null;
        const n = sentBefore + done.length;
        // COD r6 — a replay that could not attach its photo is said out loud.
        const noPhoto = noPhotoFirst + done.filter((x) => _expensePhotoMissing(x.res, x.it.payload, false)).length;
        const photoNote = noPhoto
          ? ` ${noPhoto} went in without ${noPhoto === 1 ? 'its' : 'their'} photo — give ${noPhoto === 1 ? 'that receipt' : 'those receipts'} to the office.`
          : '';
        // Receipts that never went in come back, staged, as a NEW send (they
        // were never sent, or the server refused them, so a new id is safe).
        if (retryFiles.length && document.getElementById('expense-modal') === modal) {
          _expenseReceiptFiles = retryFiles.length > 1 ? retryFiles.slice() : [];
          _expenseReceiptFile = retryFiles.length === 1 ? retryFiles[0] : null;
          if (retryFiles.length > 1) _renderExpenseMultiReceiptThumbs();
          else _showExpenseReceiptPreview(retryFiles[0]);
          _updateExpenseSubmitButtonLabel();
          // One leftover is an ordinary one-receipt expense (it needs its
          // amount, like any single receipt); two or more stay a batch.
          const msg = `${n} receipt${n === 1 ? '' : 's'} went in.${photoNote} `
            + (retryFiles.length === 1
              ? '1 did not send and is still here — enter its amount and tap Save Expense.'
              : `${retryFiles.length} did not send and are still here — tap the button to send them.`);
          _showExpenseResultBanner(false, msg);
          window.showToast?.(msg, 'error');
          return;
        }
        closeExpenseModal({ force: true });
        const msg = `${n} receipt${n === 1 ? '' : 's'} went in. The office will see them in the review queue shortly.${photoNote}${failures.length ? ` Not sent: ${failedNames} — send again.` : ''}`;
        _showExpenseResultBanner(!failures.length && !noPhoto, msg);
        window.showToast?.(msg, (failures.length || noPhoto) ? 'error' : 'success');
      });
      const msg = (successCount ? `${successCount} of ${files.length} went in. ` : '')
        + `${parked.length} receipt${parked.length === 1 ? '' : 's'} did not get an answer — ${parked.length === 1 ? 'it' : 'they'} may already be in. `
        + `Tap Finish sending when you have signal. Nothing is filed twice.${failedNote}`;
      _showExpenseResultBanner(false, msg);
      window.showToast?.(msg, 'error');
      return;
    }

    // Reset state + close modal.
    _expenseReceiptFiles = [];
    _expenseReceiptFile = null;
    _expenseLastAIResponse = null;
    closeExpenseModal();

    // Summary banner + toast.
    // COD r7 — a replayed receipt whose photo could not attach is said out loud.
    const _photoNote = noPhotoFirst
      ? ` ${noPhotoFirst} went in without ${noPhotoFirst === 1 ? 'its' : 'their'} photo — give ${noPhotoFirst === 1 ? 'that receipt' : 'those receipts'} to the office.`
      : '';
    if (failures.length === 0 && noPhotoFirst) {
      const msg = `${successCount} receipt${successCount === 1 ? '' : 's'} went in.${_photoNote}`;
      _showExpenseResultBanner(false, msg);
      window.showToast?.(msg, 'error');
    } else if (failures.length === 0) {
      // TASK-4639 — don't promise a scan that was never asked for.
      const msg = smartScanRequested === true
        ? `${successCount} receipt${successCount === 1 ? '' : 's'} uploaded — Smart Tools is reading them now. The office will see them in the review queue shortly.`
        : `${successCount} receipt${successCount === 1 ? '' : 's'} uploaded exactly as you entered them. The office will see them in the review queue shortly.`;
      _showExpenseResultBanner(true, msg);
      window.showToast?.(msg, 'success');
    } else if (successCount === 0) {
      const msg = `None of the ${files.length} receipts uploaded. ${failures[0].reason}. Try again.`;
      _showExpenseResultBanner(false, msg);
      window.showToast?.(msg, 'error');
    } else {
      const failedNames = failures.map(f => f.fileName).slice(0, 3).join(', ');
      const more = failures.length > 3 ? ` (+${failures.length - 3} more)` : '';
      const msg = `${successCount} of ${files.length} uploaded. Failed: ${failedNames}${more}. Try those again.${_photoNote}`;
      _showExpenseResultBanner(false, msg);
      window.showToast?.(msg, 'error');
    }
  } catch (err) {
    console.error('[Expense Multi] Fatal error:', err);
    const rawMsg = String(err?.message || '').replace(/^.*?:\s*/, '').slice(0, 140);
    const msg = rawMsg.includes('PIN')
      ? 'PIN check failed — please re-enter your driver PIN and try again.'
      : `Upload failed: ${rawMsg || 'unknown error'}`;
    _showExpenseResultBanner(false, msg);
    window.showToast?.(msg, 'error');
    if (btn) {
      btn.disabled = false;
      _updateExpenseSubmitButtonLabel();
    }
  }
}

// Persistent success/error banner — appended to <body> when toast container
// may be missing (driver-PIN public clock context) or the toast is too short.
// Auto-dismisses on success after 8s; error stays until tap. Reused across all
// expense entry points (driver-PIN, public clock, admin AP).
function _showExpenseResultBanner(isSuccess, message) {
  try {
    document.getElementById('expense-result-banner')?.remove();
    const bg = isSuccess ? '#166534' : '#991B1B';
    const fg = '#fff';
    const icon = isSuccess ? 'check_circle' : 'error_outline';
    const banner = document.createElement('div');
    banner.id = 'expense-result-banner';
    banner.setAttribute('role', 'alert');
    banner.style.cssText = `
      position:fixed;top:16px;left:50%;transform:translateX(-50%);
      z-index:11500;max-width:92vw;min-width:280px;
      background:${bg};color:${fg};
      padding:14px 18px;border-radius:12px;
      box-shadow:0 10px 28px rgba(0,0,0,0.22),0 2px 6px rgba(0,0,0,0.12);
      font-size:0.95rem;font-weight:600;line-height:1.35;
      display:flex;align-items:flex-start;gap:10px;
    `;
    banner.innerHTML = `
      <span class="material-icons" style="font-size:22px;flex-shrink:0;">${icon}</span>
      <span style="flex:1;">${String(message || '').replace(/[<>&]/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;'}[c]))}</span>
      <button type="button" aria-label="Dismiss"
        style="background:transparent;border:none;color:${fg};cursor:pointer;padding:0 4px;font-size:1.3rem;line-height:1;flex-shrink:0;">×</button>
    `;
    const dismiss = () => banner.remove();
    banner.querySelector('button').onclick = dismiss;
    document.body.appendChild(banner);
    if (isSuccess) {
      setTimeout(dismiss, 8000);
    }
    // Error stays put until user dismisses — they need to read it.
  } catch (e) {
    // Never let banner UI throw past the save flow.
    console.warn('[Expense] banner render failed:', e?.message);
  }
}

// ============================================
// TICKET-ONLY MODE — Skip clock, go straight to ticket builder
// ============================================
function startTicketFromBoard(jobId, jobDayId) {
  const job = JobBoardState.jobs.find(j => j.id === jobId);
  const jobDay = JobBoardState.jobDays.find(jd => jd.id === jobDayId);
  const equipment = getAssignedEquipment(jobDayId);

  // Build equipment checkboxes
  let equipmentHTML = '';
  if (equipment.trucks.length > 0 || equipment.trailers.length > 0) {
    equipmentHTML = `
      <div class="eticket-form-field" style="margin-top:8px;">
        <label class="eticket-form-label" style="display:flex;align-items:center;gap:4px;">
          <span class="material-icons" style="font-size:16px;">local_shipping</span> Equipment Used
        </label>
        <div id="clockout-equipment" style="display:flex;flex-direction:column;gap:6px;">
          ${equipment.trucks.map(t => `
            <label style="display:flex;align-items:center;gap:8px;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);cursor:pointer;background:var(--bg-secondary);">
              <input type="checkbox" class="clockout-equip-cb" data-type="truck" data-id="${t.id}" checked style="accent-color:var(--brand-blue);">
              <span class="material-icons" style="font-size:18px;color:var(--text-secondary);">local_shipping</span>
              <span style="font-weight:500;">${escapeHtml(t.name)}</span>
            </label>
          `).join('')}
          ${equipment.trailers.map(t => `
            <label style="display:flex;align-items:center;gap:8px;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);cursor:pointer;background:var(--bg-secondary);">
              <input type="checkbox" class="clockout-equip-cb" data-type="trailer" data-id="${t.id}" checked style="accent-color:var(--brand-blue);">
              <span class="material-icons" style="font-size:18px;color:var(--text-secondary);">rv_hookup</span>
              <span style="font-weight:500;">${escapeHtml(t.name)}</span>
            </label>
          `).join('')}
        </div>
      </div>`;
  }

  // Downtime section
  const downtimeHTML = `
    <div class="eticket-form-field" style="margin-top:8px;">
      <label class="eticket-form-label" style="display:flex;align-items:center;gap:4px;">
        <span class="material-icons" style="font-size:16px;">timer_off</span> Downtime
      </label>
      <div id="clockout-downtime-list" style="display:flex;flex-direction:column;gap:6px;"></div>
      <button type="button" class="btn btn-secondary btn-sm" style="margin-top:6px;" onclick="window.JobBoardModule.addDowntimeRow()">
        <span class="material-icons" style="font-size:14px;">add</span> Add Downtime
      </button>
    </div>`;

  // 🔴 The PWA install banner (#pwa-install-banner, app.js, z-index 10000,
  // fixed bottom) sits ABOVE this modal's sticky footer (z-index 100) and
  // swallows taps on the primary Clock Out button — caught by the convergence
  // E2E: click resolved "visible, enabled and stable" and then never landed.
  // A driver mid-clock-out does not need an install prompt; remove it for the
  // session rather than z-index-dueling (the banner deliberately tops content).
  document.getElementById('pwa-install-banner')?.remove();
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'clockout-modal';
  modal.dataset.jobId = jobId;
  modal.dataset.jobDayId = jobDayId;
  modal.dataset.ticketOnly = 'true';

  modal.innerHTML = `
    <div class="modal-content modal-fullscreen" style="max-width:540px;">
      <div class="eticket-form-header">
        <div class="eticket-form-header-title">
          <span class="material-icons">receipt_long</span>
          Build Ticket
        </div>
        <button class="modal-close" onclick="window.JobBoardModule.closeClockOutModal()" style="color:#fff;">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;overflow-y:auto;">
        <div class="eticket-form-context" style="margin-bottom:16px;">
          <div class="eticket-form-context-row">
            <span class="material-icons">business</span>
            <span>${escapeHtml(getCustomerName(job?.customerId) || 'No customer')}</span>
          </div>
          <div class="eticket-form-context-row">
            <span class="material-icons">work</span>
            <span>${escapeHtml(job?.title || job?.name || 'Job')}</span>
          </div>
        </div>

        <!-- Hours worked (manual entry since no clock) -->
        <div class="eticket-form-field">
          <label class="eticket-form-label">Hours Worked</label>
          <input type="number" class="form-control" id="ticketonly-hours" step="0.25" min="0" max="24" placeholder="e.g. 8" value="8" style="font-size:1.1rem;font-weight:600;">
        </div>

        ${equipmentHTML}
        ${downtimeHTML}

        <div class="eticket-form-field">
          <label class="eticket-form-label">Notes</label>
          <textarea rows="3" class="form-control" id="clockout-notes" placeholder="Work performed, conditions, notes..."></textarea>
        </div>
      </div>
      <div class="eticket-form-actions">
        <button class="btn btn-secondary" onclick="window.JobBoardModule.closeClockOutModal()">
          Cancel
        </button>
        <button class="btn btn-primary" id="btn-clockout-submit" onclick="window.JobBoardModule.submitClockOut()">
          <span class="material-icons">receipt_long</span> Submit Ticket
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);
}

// ============================================
// TIME OFF REQUEST — Driver PIN View
// ============================================
function driverHasTimeOffAccess() {
  if (!window.AppState?.driverModePin) return false;
  // TASK-865 — settings flag generalized from the trippin-trucking hardcode
  // ("build for everybody"). Absent/false = no access via this leg — the
  // pre-existing non-TITS behavior (VIP + hr addon legs unchanged below).
  // TITS backfilled true via scripts/backfill-task865-tits-flags.cjs.
  if (window.AppState?.settings?.driverTimeOff?.enabled === true) return true;
  if (typeof window.isVipAccount === 'function' && window.isVipAccount()) return true;
  const addons = window.AppState.driverAddons || {};
  return addons.hr === true;
}

function openTimeOffRequest() {
  // TASK-1121 — DELEGATE to the rich dispatch.js personal-action modal
  // (TASK-320/598: balance card + "Your Requests" with pending/approved/denied
  // status + edit/cancel-while-pending). jobboard.js used to carry a lagging
  // duplicate modal (form + balance only) — a portal driver could submit but
  // never see status, so a DENIED request looked like silence. dispatch.js
  // assigns window.DispatchModule at module top level and app.js imports it
  // statically before jobboard.js, so the delegate is always present in the
  // driver-PIN portal. personId/personName come from session state
  // (getActiveJobBoardPersonId — PIN → SPA member resolution), never a URL.
  const dm = window.DispatchModule;
  if (dm && typeof dm.openDriverTimeOffForPerson === 'function') {
    dm.openDriverTimeOffForPerson(getActiveJobBoardPersonId(), getActiveJobBoardPersonName());
    return;
  }
  // Defensive fallback — should be unreachable (static import order).
  window.showToast?.('Time off is unavailable right now — please refresh the page', 'error');
}

// ============================================
// STANDALONE CLOCK IN (no job assignment needed)
// ============================================
// v1.15.924 — mirror of dispatch.js updateDriverBottomNavClock but sourced
// from JobBoardState.clockEntries. Fires from the clockEntries onSnapshot
// handler so the bottom-nav Clock button stays in lockstep with the actual
// clock state. Without this, dispatch.js's updateDriverBottomNavClock
// reads empty DispatchState._clockEntries (PIN drivers don't load
// dispatch.js subs) and the button is permanently stuck on "Clock In"
// even after the driver clocks in. Sean reported 2026-05-28.
function syncBottomNavClockFromJobBoard(myEntry) {
  const navItem = document.getElementById('driver-btmnav-clock');
  if (!navItem) return;
  const labelEl = document.getElementById('driver-btmnav-clock-label');
  const iconEl = document.getElementById('driver-btmnav-clock-icon');
  const mode = _jbClockMode(); // TASK-9553
  const isTicket = mode === 'ticket' || mode === 'ticketonly';
  const isTicketOnly = mode === 'ticketonly';
  if (myEntry) {
    if (labelEl) labelEl.textContent = isTicket ? 'Build Ticket' : 'Clock Out';
    if (iconEl) iconEl.textContent = isTicket ? 'receipt_long' : 'logout';
    navItem.classList.add('active');
  } else {
    if (labelEl) labelEl.textContent = isTicketOnly ? 'Build Ticket' : 'Clock In';
    if (iconEl) iconEl.textContent = isTicketOnly ? 'receipt_long' : 'login';
    navItem.classList.remove('active');
  }
}

// v1.15.923 — invoked by the driver bottom-nav Clock button (injected as
// `clockOnClick` config into dispatch.js getDriverBottomNavHTML). Replaces
// the prior route through dispatch.js handleDriverClockButton → openDriverClockIn
// which read DispatchState.customers (empty when PIN drivers are on /jobboard,
// since dispatch.js subscribeToData never ran for them). This uses
// JobBoardState directly so the customer dropdown actually populates.
function handleBottomNavClock() { // both branches wait for an unconfirmed own mode (confirmSelf)
  const driverId = getActiveJobBoardPersonId();
  const myEntry = driverId
    ? JobBoardState.clockEntries.find(e => e.personId === driverId)
    : null;
  if (myEntry) {
    clockOutStandalone(myEntry.id, myEntry.jobId || '', myEntry.jobDayId || '');
  } else {
    openStandaloneClockIn();
  }
}

async function openStandaloneClockIn() {
  if (await window.ClockMode?.confirmSelf?.() === null) return; // session changed while waiting
  // TASK-355 2026-05-22: allow SPA members (memberAccess.personId) into this
  // flow too. Before, `driverModePin`-only gate left SPA members with no way
  // to clock in from Job Board.
  const _viewerPid = getActiveJobBoardPersonId();
  if (!_viewerPid || !window.driverHasClockAccess?.()) return;

  // Check if already clocked in
  const driverId = _viewerPid;
  const existing = JobBoardState.clockEntries.find(e => e.personId === driverId);
  if (existing) {
    window.showToast?.('Already clocked in. Clock out first.', 'error');
    return;
  }

  const _ticketOnly = isTicketOnlyMode();
  const title = _ticketOnly ? 'Start Ticket' : 'Clock In';

  // Build customer options
  // TASK-1175 — internalTimeOnly (Shop/Office) pinned first; TASK-9548 — grouped
  // under a labeled optgroup by the shared builder (_jbCustomerOptionsHtml).
  const customers = JobBoardState.customers
    .filter(c => c.name && c.status !== 'inactive' && c.status !== 'Inactive');

  console.log('[JobBoard] openStandaloneClockIn: customers=' + customers.length + ', jobs=' + JobBoardState.jobs.length);

  const customerOptions = _jbCustomerOptionsHtml(customers);

  // Explicit inline styles ensure select/label visibility on all devices
  const labelStyle = 'display:block;font-size:14px;font-weight:600;color:#334155;margin-bottom:6px;';
  const selectStyle = 'display:block;width:100%;padding:12px;font-size:16px;border:1px solid #CBD5E1;border-radius:10px;background:#F8FAFC;color:#0F172A;-webkit-appearance:menulist;appearance:menulist;min-height:48px;';
  const inputStyle = 'display:block;width:100%;padding:12px;font-size:16px;border:1px solid #CBD5E1;border-radius:10px;background:#F8FAFC;color:#0F172A;min-height:48px;';

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'standalone-clockin-modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:440px;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">${_ticketOnly ? 'receipt_long' : 'login'}</span>${title}</h3>
        <button class="btn-icon" onclick="window.JobBoardModule.closeStandaloneClockIn()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;">
        <div style="margin-bottom:14px;">
          <label style="${labelStyle}">Customer <span style="color:#ef4444;">*</span></label>
          <select id="sa-clockin-customer" style="${selectStyle}" onchange="window.JobBoardModule.onStandaloneCustomerChange()">
            <option value="">Select customer...</option>
            ${customerOptions}
          </select>
          ${_jbCustomerHintHtml(customers, '#475569')}
        </div>
        <div style="margin-bottom:14px;">
          <label style="${labelStyle}">Job (optional)</label>
          <select id="sa-clockin-job" style="${selectStyle}" onchange="window.JobBoardModule.onStandaloneJobChange()">
            <option value="">No specific job</option>
          </select>
          <!-- TASK-9865 — advisory "Not on today's crew for J-1247 — you're on J-1248" + one-tap switch -->
          <div id="sa-clockin-crew-warn" style="display:none;margin-top:8px;"></div>
        </div>
        <!-- TASK-558 — per-operator equipment rows rendered INLINE here when
             equipmentTie is ON and the selected job's day has equipment. The
             single Clock In button reads this selection and writes assetIds[]
             atomically — no second sheet, no abort-gap. -->
        <div id="sa-clockin-equipment" style="margin-bottom:14px;display:none;"></div>
        <div style="margin-bottom:18px;">
          <label style="${labelStyle}">Notes (optional)</label>
          <input type="text" id="sa-clockin-notes" style="${inputStyle}" placeholder="e.g. shop work, yard cleanup..." />
        </div>
        <button id="sa-clockin-submit" class="btn btn-primary" style="width:100%;padding:14px;font-size:1rem;font-weight:600;background:#166534;border:none;border-radius:10px;color:#fff;" onclick="window.JobBoardModule.submitStandaloneClockIn()">
          <span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:20px;">${_ticketOnly ? 'receipt_long' : 'login'}</span>${title}
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

function onStandaloneCustomerChange() {
  const customerId = document.getElementById('sa-clockin-customer')?.value;
  const jobSelect = document.getElementById('sa-clockin-job');
  if (!jobSelect) return;

  // Filter jobs for this customer
  if (!customerId || customerId === '__shop__') {
    jobSelect.innerHTML = '<option value="">No specific job</option>';
    return;
  }

  const todayMs = new Date().setHours(0, 0, 0, 0);
  const driverId = getActiveJobBoardPersonId(); // TASK-355: SPA member support
  const customerJobs = _jbSortJobsForPicker(JobBoardState.jobs
    .filter(j => {
      if (j.customerId !== customerId || !(j.title || j.name)) return false;
      if (j.status === 'finished') return false;
      const endRaw = j.endDate || j.startDate;
      if (!endRaw) return true;
      const endMs = endRaw?.toDate ? endRaw.toDate().getTime() : new Date(endRaw).getTime();
      if (isNaN(endMs)) return true;
      const isAssigned = driverId && (j.assignedDrivers || []).includes(driverId);
      return endMs >= todayMs || isAssigned;
    }));

  jobSelect.innerHTML = '<option value="">No specific job</option>' +
    customerJobs.map(j =>
      `<option value="${j.id}">${escapeHtml(_jbJobPickLabel(j))}</option>`
    ).join('');

  // TASK-9865 — preselect the job this driver is crewed on today (nearest
  // start when several; nothing when two+ jobs today and none has them).
  const pick = _jbPickJobForViewer(customerJobs);
  if (pick && pick.jobId) jobSelect.value = pick.jobId;

  // TASK-558 — customer change resets the job list → re-sync the inline
  // equipment rows to whatever was just (pre)selected.
  _jbRenderStandaloneEquipment(jobSelect.value || '');
  _jbRenderCrewWarn('sa-clockin-job', 'sa-clockin-crew-warn', customerJobs);
}

// ============================================
// TASK-9865 — WHICH JOB TO PRESELECT (board twin of the clock.js table)
// ============================================
// Same-customer, same-day jobs whose titles differ only at the end ("…(8AM)"
// / "…(5PM)") listed alphabetically with NO preselect — and the ZL crew
// clocked onto the 5 PM run after working the 8 AM one. The decision table,
// the "8:00 AM · J-1248 · title" label and the advisory crew line all live in
// clock.js (clockPickJobForPerson & co.) and are read here through
// window.ClockModule at CALL time — never a ?v= import (Pitfall #1). If the
// clock module is somehow absent the pickers fall back to exactly what they
// rendered before (title-only, no preselect) — never a thrown picker.
function _jbTodaysJobDays() {
  const todayStr = formatDateISO(new Date());
  return (JobBoardState.jobDays || []).filter(jd => {
    if (!jd || !jd.date) return false;
    const d = jd.date?.toDate ? jd.date.toDate() : new Date(jd.date);
    return !isNaN(d.getTime()) && formatDateISO(d) === todayStr;
  });
}
function _jbViewerPerson() {
  const id = getActiveJobBoardPersonId();
  if (!id) return null;
  return { id, name: getActiveJobBoardPersonName() || null };
}
function _jbNowMinutes() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}
function _jbJobPickLabel(job) {
  const CM = window.ClockModule;
  if (!CM || typeof CM.clockJobPickLabel !== 'function') return job.title || job.name || 'Untitled Job';
  try { return CM.clockJobPickLabel(job, CM.clockTodaysDayFor(job.id, _jbTodaysJobDays(), _jbViewerPerson())); }
  catch (_) { return job.title || job.name || 'Untitled Job'; }
}
// Today's jobs first in start-time order (no start → after the timed ones),
// then everything else by title — the order the labels read in.
function _jbSortJobsForPicker(jobs) {
  const CM = window.ClockModule;
  const byTitle = (a, b) => (a.title || a.name || '').localeCompare(b.title || b.name || '');
  if (!CM || typeof CM.clockWallMinutes !== 'function' || typeof CM.clockTodaysDayFor !== 'function' || typeof CM.clockJobDayStart !== 'function') return (jobs || []).slice().sort(byTitle);
  try {
    const days = _jbTodaysJobDays();
    const person = _jbViewerPerson();
    const key = j => {
      const day = CM.clockTodaysDayFor(j.id, days, person);
      if (!day) return 2e9; // not today
      const m = CM.clockWallMinutes(CM.clockJobDayStart(j, day));
      return m == null ? 1e9 : m;
    };
    return (jobs || []).slice().sort((a, b) => (key(a) - key(b)) || byTitle(a, b));
  } catch (_) { return (jobs || []).slice().sort(byTitle); } // never a thrown picker
}
function _jbPickJobForViewer(jobs) {
  const CM = window.ClockModule;
  if (!CM || typeof CM.clockPickJobForPerson !== 'function') return null;
  try { return CM.clockPickJobForPerson(jobs, _jbTodaysJobDays(), _jbViewerPerson(), _jbNowMinutes()); }
  catch (_) { return null; }
}
function _jbCrewWarningFor(jobId, jobs) {
  const CM = window.ClockModule;
  if (!CM || typeof CM.clockCrewWarning !== 'function') return null;
  try { return CM.clockCrewWarning(jobId, jobs, _jbTodaysJobDays(), _jbViewerPerson(), _jbNowMinutes()); }
  catch (_) { return null; }
}
// Paint / clear the advisory line under a board-side job <select>.
function _jbRenderCrewWarn(selectId, warnId, jobs) {
  const el = document.getElementById(warnId);
  if (!el) return;
  const jobId = document.getElementById(selectId)?.value || '';
  const CM = window.ClockModule;
  const w = (CM && typeof CM.clockCrewWarningHtml === 'function') ? _jbCrewWarningFor(jobId, jobs) : null;
  if (!w) { el.style.display = 'none'; el.innerHTML = ''; return; }
  const esc = window.escJsAttr || ((s) => String(s == null ? '' : s).replace(/[^A-Za-z0-9_-]/g, ''));
  el.innerHTML = CM.clockCrewWarningHtml(w, `window.JobBoardModule._jbPickCrewJob('${selectId}','${esc(w.switchToJobId)}')`);
  el.style.display = '';
}
// One-tap "Switch to J-1248": set the picker and run the same follow-ups a
// manual change runs (equipment rows + the warning re-judges itself).
function _jbPickCrewJob(selectId, jobId) {
  const sel = document.getElementById(selectId);
  if (!sel) return;
  sel.value = jobId;
  if (sel.value !== jobId) return; // not an option any more — leave it
  if (selectId === 'jb-switch-job') jbSwitchJobChange();
  else onStandaloneJobChange();
}
// The jobs a board-side picker currently lists (for re-judging on change).
function _jbPickerJobs(selectId) {
  const sel = document.getElementById(selectId);
  if (!sel) return [];
  const ids = new Set(Array.from(sel.options).map(o => o.value).filter(Boolean));
  return (JobBoardState.jobs || []).filter(j => j && ids.has(j.id));
}
// Job modal / job screen: the viewer opened a job whose TODAY day does not
// carry them while another job today does → { html } for the one line +
// "Open J-1248" (opens that job's own modal, same path as tapping its card).
// null = nothing to say (on the crew, on no crew anywhere, no person).
function _jbCrewWarnForJobModal(job, jobDay) {
  if (!job) return null;
  const CM = window.ClockModule;
  if (!CM || typeof CM.clockCrewWarning !== 'function') return null;
  try {
    const days = _jbTodaysJobDays();
    const person = _jbViewerPerson();
    if (!person) return null;
    // Judge the day the modal was opened from when it is today's; a day from
    // another date (tomorrow's card) is not a clock-in-today question.
    if (jobDay && !days.some(d => d.id === jobDay.id)) return null;
    const w = CM.clockCrewWarning(job.id, JobBoardState.jobs || [], days, person, _jbNowMinutes());
    if (!w) return null;
    const esc = window.escJsAttr || ((s) => String(s == null ? '' : s).replace(/[^A-Za-z0-9_-]/g, ''));
    const html = CM.clockCrewWarningHtml(w, `window.JobBoardModule._jbOpenCrewJob('${esc(w.switchToJobId)}','${esc(w.switchToJobDayId || '')}')`, { buttonLabel: 'Open' });
    return { html, w };
  } catch (_) { return null; }
}
function _jbOpenCrewJob(jobId, jobDayId) {
  try { closeJobDetails(); } catch (_) { /* nothing open */ }
  showJobDetails(jobId, jobDayId || null);
}

// TASK-558 — render the per-operator equipment toggle rows INLINE in the
// standalone clock-in modal when equipmentTie is ON and the selected job's day
// has equipment. All assets default selected (mirrors the old confirm sheet).
// When OFF / no job / job has no equipment → the section is hidden and empty,
// so the single Clock In tap is unchanged one-tap.
function onStandaloneJobChange() {
  const jobId = document.getElementById('sa-clockin-job')?.value || '';
  _jbRenderStandaloneEquipment(jobId);
  _jbRenderCrewWarn('sa-clockin-job', 'sa-clockin-crew-warn', _jbPickerJobs('sa-clockin-job')); // TASK-9865
}

// ============================================
// TASK-1572 Slice 2 — STOP HIDING THE QUESTION AT CLOCK-IN
// ============================================
// Every clock-in picker returned early when the job's day had no equipment on
// it: `if (dayAssetIds.size === 0) { hide; return; }`. Dispatch often does not
// know the truck the night before, so the one person who does — the driver,
// standing next to it — was never asked, and the ticket was born silent about
// equipment. That is the CAUSE of category C; Slice 1 (the Build Ticket prompt)
// is the catch. Now, on an equipmentTie tenant, an empty day asks instead.
//
// 🔴 THE ANSWER IS A CLAIM, NOT assetIds. functions onClockEntryAssetValidate
// strips any assetId that is not on some jobDay of the job — that is its whole
// job (a crafted write must not bill a rig the job never had). Writing the
// driver's pick into assetIds would be silently stripped to [] a second later,
// AND the provenance would then read "a real menu was shown, nothing picked"
// (TASK-1539 rule 5), which switches OFF both the TASK-1539 legacy fill and the
// Slice 1 prompt at clock-out: his answer thrown away, and the catch disabled.
// So the pick rides on the provenance map as `claimedAssetIds` (the validator
// only runs when assetIds changes, and [] is left alone), assetIds stays [],
// renderedAssetIds stays [] ("nothing from DISPATCH to pick" — rule 6, exactly
// as before), and the claim materializes at clock-out as an equipment line
// flagged for billing review (TASK-1635's `_offJobDay`). The server validator is
// NOT loosened: a claim can put a line on the driver's OWN ticket for a biller to
// confirm — the same power the Build Ticket "Add Equipment" button already gives
// him — and nothing more.
//
// 🔴 NEVER PRE-SELECTED. First option is the empty placeholder. Ordered by this
// driver's own recent equipment (Slice 1's `_jbRecentAssetRank`): top-1
// concentration is 32%, so a default would be wrong two times in three.
//
// 🔴 NEVER BLOCKS A CLOCK-IN. Ignoring the question clocks in exactly as before
// (rule 6, fallback-eligible, Slice 1 asks again at clock-out). Every exit —
// pick, "No Equipment (Labor Only)", Clock In untouched, the sheet's X — ends
// clocked in.
//
// Gated on equipmentTie (OFF by default — the other tenants are byte-identical)
// AND on the driver being allowed to add equipment at all (asking someone who
// cannot answer is noise, and the claim must not become a way around
// allowDriverEquipmentEdit).
function _jbFleetAskEnabled() {
  return equipmentTieEnabled() && jbCanDriverEditEquipment();
}

// The <select>'s options: trucks then trailers, each ordered by this driver's
// recent use, placeholder first. `count` = selectable units (0 → do not ask).
function _jbBuildFleetOptions(personId) {
  let rank = new Map();
  try { rank = _jbRecentAssetRank(personId); } catch (_) { /* ordering is a nicety */ }
  const trucks = _jbSortByRecentUse((JobBoardState.trucks || []).filter(t => t && t.id != null), rank);
  const trailers = _jbSortByRecentUse((JobBoardState.trailers || []).filter(t => t && t.id != null), rank);
  const escA = window.escAttr || escapeHtml;
  const nameOf = t => (window.getUnitDisplay ? window.getUnitDisplay(t) : '') || t.nickname || t.unit || t.name || 'Unit';
  const opt = t => `<option value="${escA(String(t.id))}">${escapeHtml(nameOf(t))}</option>`;
  let html = '<option value="">Select what you\'re in…</option>';
  if (trucks.length) html += `<optgroup label="Trucks">${trucks.map(opt).join('')}</optgroup>`;
  if (trailers.length) html += `<optgroup label="Trailers">${trailers.map(opt).join('')}</optgroup>`;
  return { html, count: trucks.length + trailers.length };
}

// Markup for the ask. `hostId` is the container that carries the provenance
// dataset flags (eqTouched / eqLaborOnly), exactly like the day-rows twin.
function _jbFleetAskHtml(hostId, optionsHtml) {
  const escA = window.escAttr || escapeHtml;
  return `
    <label style="display:block;font-size:14px;font-weight:600;color:#334155;margin-bottom:4px;">Which truck are you in?</label>
    <div style="font-size:0.78rem;color:#64748b;margin-bottom:6px;line-height:1.35;">Dispatch hasn't assigned equipment to this job yet. Pick what you're running so it lands on your ticket.</div>
    <select class="jb-fleet-ask" data-host="${escA(hostId)}" onchange="window.JobBoardModule.jbFleetAskChanged(this)"
      style="width:100%;min-height:48px;padding:10px 12px;font-size:16px;border:2px solid #e2e8f0;border-radius:12px;background:#fff;color:#0f172a;">${optionsHtml}</select>
    <button type="button" class="jb-fleet-labor-only" onclick="window.JobBoardModule.jbFleetAskLaborOnly('${escA(hostId)}')"
      style="width:100%;min-height:48px;margin-top:10px;padding:12px;font-size:0.9rem;font-weight:600;border:2px dashed #cbd5e1;border-radius:12px;background:#f8fafc;color:#334155;cursor:pointer;">
      <span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:18px;">person</span>No Equipment (Labor Only)
    </button>`;
}

// A pick marks the choice touched and cancels an earlier labor-only tap (the
// later answer wins, same as the day-rows twin).
function jbFleetAskChanged(sel) {
  const host = sel ? document.getElementById(sel.dataset.host || '') : null;
  if (!host) return;
  host.dataset.eqTouched = '1';
  if (sel.value) delete host.dataset.eqLaborOnly;
}

function jbFleetAskLaborOnly(hostId) {
  const host = document.getElementById(hostId);
  if (!host) return;
  host.dataset.eqLaborOnly = '1';
  host.dataset.eqTouched = '1';
  const sel = host.querySelector('.jb-fleet-ask');
  if (sel) sel.value = '';
  const btn = host.querySelector('.jb-fleet-labor-only');
  if (btn) { btn.style.borderColor = '#64748b'; btn.style.background = '#e2e8f0'; }
  // On the one-tap sheet "No Equipment" is itself the answer AND the clock-in —
  // one tap, exactly like the day-rows sheet's labor-only button.
  if (hostId === 'jb-fleet-ask-host') jbFleetAskSheetFinish();
}

// Read the ask back: { claim: [id] | [], laborTap, touched }. null when the
// host is not showing a fleet ask (caller keeps its old path).
function _jbReadFleetAsk(host) {
  const sel = host ? host.querySelector('.jb-fleet-ask') : null;
  if (!sel) return null;
  const v = String(sel.value || '');
  return {
    claim: v ? [v] : [],
    laborTap: host.dataset.eqLaborOnly === '1',
    touched: host.dataset.eqTouched === '1'
  };
}

// Provenance for an empty-day clock-in. Built by the SAME normalizer as every
// other clock-in (so every existing field keeps its meaning), plus two fields:
// `fleetOffered` (the driver WAS asked) and `claimedAssetIds` (his answer).
// renderedAssetIds stays [] on purpose — see the Slice 2 header: rule 6 must
// still read "nothing from dispatch to pick" so the TASK-1539 fill and the
// Slice 1 prompt keep working. An explicit labor-only tap IS an answer (rule 4).
function _jbFleetAskProvenance(source, ask, dayAssetIds) {
  const a = ask || { claim: [], laborTap: false, touched: false };
  const prov = _jbBuildAssetProvenance(source, {
    renderedAssetIds: [],
    selectedAssetIds: [],
    explicitLaborOnly: a.laborTap === true && a.claim.length === 0,
    defaulted: a.touched !== true
  }, dayAssetIds);
  prov.fleetOffered = true;
  prov.claimedAssetIds = (a.claim || []).map(String);
  return prov;
}

// The driver's clock-in answer, if it still needs materializing. A real
// assetIds pick always wins (never both).
function _jbDriverClaimIds(entry) {
  const p = entry && entry.assetProvenance;
  if (!p || p.fleetOffered !== true || !Array.isArray(p.claimedAssetIds)) return [];
  if (!Array.isArray(entry.assetIds) || entry.assetIds.length > 0) return [];
  return p.claimedAssetIds.map(String).filter(Boolean);
}

// Clock-out: put the driver's clock-in answer on his ticket. Off-day (dispatch
// still has not listed it) → the TASK-1635 flagged fill, which tells him in
// plain words and routes the line to billing review. On-day (dispatch has since
// listed it) → a clean line, unless the day fill already put it there.
// Wrapped: this runs while building the clock-out modal, and a throw must never
// cost a driver his way off the clock.
function _jbFillDriverClaimedAssets(entry) {
  try {
    const ids = _jbDriverClaimIds(entry);
    if (ids.length === 0) return;
    const jobDay = _jbResolveJobDayForEntry(entry);
    const expanded = jobDay ? _jbExpandedDayAssetIds(jobDay) : new Set();
    const offIds = ids.filter(id => !_jbTiedIdIsOnDay(id, expanded));
    const onIds = ids.filter(id => _jbTiedIdIsOnDay(id, expanded));
    _jbFillTiedOffDayAssets(entry, offIds);
    if (onIds.length === 0) return;
    const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    const now = new Date();
    const sessionId = entry.id || null;
    const present = new Set((JobBoardState.btEquipment || [])
      .filter(e => (e._clockEntryId || null) === sessionId)
      .map(e => e.assetId).filter(Boolean));
    onIds.forEach(id => {
      const hit = _jbResolveAnyAsset(id);
      if (!hit) return;
      const assetId = hit.asset.id || id;
      if (present.has(assetId) || present.has(id)) return;
      present.add(assetId);
      JobBoardState.btEquipment.push({
        assetId,
        assetName: (window.getUnitDisplay ? window.getUnitDisplay(hit.asset) : '') || hit.asset.name || hit.asset.unit || 'Equipment',
        assetType: hit.assetType, fleetNumber: hit.asset.unit || '',
        rate: 0, rateUnit: 'Hourly',
        startTime: jbToLocalDT(clockIn), endTime: jbToLocalDT(now),
        notes: '', _dirty: false, _saved: false, _clockEntryId: sessionId, _followsClock: true
      });
    });
  } catch (err) {
    console.warn('[JobBoard] TASK-1572 claimed-equipment fill failed (non-fatal):', err?.message);
  }
}

// One-tap job-card path: the empty-day ask as a small sheet. Every button ends
// clocked in — X included (TASK-558: no tap toward clock-in may leave the driver
// un-clocked). `onDone(ask)` receives the read-back.
function _jbShowFleetAskSheet(personId, onDone) {
  const { html, count } = _jbBuildFleetOptions(personId);
  if (count === 0) { onDone(null); return; }
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'jb-fleet-ask-modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:440px;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">local_shipping</span>Clock in</h3>
        <button class="btn-icon" onclick="window.JobBoardModule.jbFleetAskSheetFinish(true)" aria-label="Clock in without equipment">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;">
        <div id="jb-fleet-ask-host">${_jbFleetAskHtml('jb-fleet-ask-host', html)}</div>
        <button type="button" id="jb-fleet-ask-submit" onclick="window.JobBoardModule.jbFleetAskSheetFinish()"
          style="width:100%;min-height:52px;margin-top:12px;padding:14px;font-size:1rem;font-weight:700;border:none;border-radius:12px;background:#166534;color:#fff;cursor:pointer;">
          <span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:20px;">login</span>Clock In
        </button>
      </div>
    </div>`;
  modal._jbOnDone = onDone;
  document.body.appendChild(modal);
}

// dismissed === true is the X: clock in WITHOUT an answer — the question stays
// open (rule 6), so Slice 1 asks again at clock-out. Same contract as the
// day-rows sheet's X (TASK-558).
function jbFleetAskSheetFinish(dismissed) {
  const modal = document.getElementById('jb-fleet-ask-modal');
  if (!modal) return;
  const cb = modal._jbOnDone;
  // Read BEFORE removing; a double tap finds no modal and does nothing.
  const ask = dismissed === true
    ? { claim: [], laborTap: false, touched: false }
    : _jbReadFleetAsk(document.getElementById('jb-fleet-ask-host'));
  modal.remove();
  if (typeof cb === 'function') cb(ask);
}

function _jbRenderStandaloneEquipment(jobId) {
  const container = document.getElementById('sa-clockin-equipment');
  if (!container) return;

  // TASK-1547 — every (re)render is a fresh menu (job/customer change swaps
  // the rows): reset the provenance flags so a stale touch/labor-only from a
  // previous selection can't leak onto the new one.
  delete container.dataset.eqTouched;
  delete container.dataset.eqLaborOnly;

  if (!equipmentTieEnabled() || !jobId) {
    container.style.display = 'none';
    container.innerHTML = '';
    return;
  }

  const jobDay = _jbResolveJobDayForEntry({ jobId, clockIn: new Date() });
  const dayAssetIds = _jbJobDayAssetIds(jobDay);
  if (dayAssetIds.size === 0) {
    // TASK-1572 Slice 2 — ON but dispatch left this day empty: ASK (fleet,
    // ordered, never pre-selected) instead of hiding the question. Falls back to
    // the old plain clock-in when the driver may not add equipment or there is
    // no fleet to offer.
    const _fleet = _jbFleetAskEnabled() ? _jbBuildFleetOptions(getActiveJobBoardPersonId()) : null;
    if (_fleet && _fleet.count > 0) {
      container.style.display = 'block';
      container.innerHTML = _jbFleetAskHtml('sa-clockin-equipment', _fleet.html);
      return;
    }
    // ON but this job day has no equipment → plain clock-in, no rows.
    container.style.display = 'none';
    container.innerHTML = '';
    return;
  }

  const rows = _jbBuildEquipRows(jobDay);
  // TASK-9763 — only THIS person's paired units start checked (rule 5).
  const pre = _jbCrewPairPreselect(jobDay, rows, getActiveJobBoardPersonId(), getActiveJobBoardPersonName());
  const rowsHtml = _jbBuildEquipRowsHtml(rows, pre);
  const labelStyle = 'display:block;font-size:14px;font-weight:600;color:#334155;margin-bottom:6px;';
  container.style.display = 'block';
  container.innerHTML = `
    <label style="${labelStyle}">Which equipment will you be in? <span style="font-weight:400;color:#64748b;">${pre.mode === 'none' ? '(tap to deselect)' : pre.mode === 'paired' ? '(checked = your assignment; tap to change)' : "(dispatch hasn't put you in a unit — tap the one you're running)"}</span></label>
    <div style="display:flex;flex-direction:column;gap:8px;">
      ${rowsHtml}
    </div>
    ${_jbTruckSwapHtml(rows, getActiveJobBoardPersonId())}
    <button type="button" id="sa-clockin-labor-only" onclick="window.JobBoardModule.jbStandaloneLaborOnly()"
      style="width:100%;min-height:48px;margin-top:10px;padding:12px;font-size:0.9rem;font-weight:600;border:2px dashed #cbd5e1;border-radius:12px;background:#f8fafc;color:#334155;cursor:pointer;">
      <span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:18px;">person</span>No Equipment (Labor Only)
    </button>`;
}

// TASK-558 — "No Equipment (Labor Only)" inline affordance: deselect every row
// so the single Clock In tap writes an empty assetIds[].
function jbStandaloneLaborOnly() {
  const container = document.getElementById('sa-clockin-equipment');
  if (!container) return;
  // TASK-1547 — remember the labor-only tap; explicitLaborOnly is only
  // stamped at submit if the selection is STILL empty then (a re-selected row
  // after this tap wins).
  container.dataset.eqLaborOnly = '1';
  container.querySelectorAll('.jb-eqc-row').forEach(row => {
    if (row.dataset.selected === '1') jbToggleEquipConfirmRow(row);
  });
}

// TASK-558 — read the inline equipment selection (selected rows only).
function _jbReadStandaloneAssetIds() {
  const container = document.getElementById('sa-clockin-equipment');
  if (!container) return [];
  return Array.from(container.querySelectorAll('.jb-eqc-row'))
    .filter(r => r.dataset.selected === '1')
    .map(r => r.dataset.assetId)
    .filter(Boolean);
}

async function submitStandaloneClockIn() {
  // TASK-9694 polish (switch ON only) — the TAP time, before any read or GPS
  // wait, so the nav Clock In records the same instant the job screen would.
  const tapMs = isTicketOnJobScreen() ? Date.now() : null;
  const tenantId = JobBoardState.tenantId;
  // TASK-355 2026-05-22: PIN -> SPA member personId/name resolution
  const driverId = getActiveJobBoardPersonId();
  const driverName = getActiveJobBoardPersonName();
  const customerId = document.getElementById('sa-clockin-customer')?.value;
  const jobId = document.getElementById('sa-clockin-job')?.value;
  const notes = document.getElementById('sa-clockin-notes')?.value?.trim() || '';

  console.log('[JobBoard] submitStandaloneClockIn:', { tenantId, driverId, driverName, customerId, jobId, notes });

  if (!customerId) {
    window.showToast?.('Select a customer', 'error');
    return;
  }

  const btn = document.getElementById('sa-clockin-submit');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="loading-spinner-small"></span> Clocking In...'; }

  const isShop = customerId === '__shop__';
  const customer = isShop ? null : JobBoardState.customers.find(c => c.id === customerId);
  const job = jobId ? JobBoardState.jobs.find(j => j.id === jobId) : null;
  const source = window.AppState?.driverModePin ? 'jobboard_pin' : 'jobboard_spa';
  const where = isShop ? 'Shop' : (customer?.name || '');

  // TASK-558 — per-operator equipment is now selected INLINE in this modal (no
  // second sheet, no abort-gap). When equipmentTie is ON and the selected job's
  // day has equipment, the inline rows are shown; the single Clock In tap reads
  // that selection and writes assetIds[] ATOMICALLY. When OFF (default) or no
  // job / no equipment, the section is hidden — one-tap, exactly as today.
  if (equipmentTieEnabled() && jobId) {
    const jobDay = _jbResolveJobDayForEntry({ jobId, clockIn: new Date() });
    const dayAssetIds = _jbJobDayAssetIds(jobDay);
    if (dayAssetIds.size > 0) {
      // Inline rows were rendered by onStandaloneJobChange — read the selection.
      const chosenAssetIds = _jbReadStandaloneAssetIds();
      // TASK-1547 — provenance off the inline rows: what was rendered, whether
      // the driver touched it, whether "No Equipment" led to an empty pick.
      const _eqEl = document.getElementById('sa-clockin-equipment');
      const _rendered = _eqEl ? Array.from(_eqEl.querySelectorAll('.jb-eqc-row')).map(r => r.dataset.assetId).filter(Boolean) : [];
      const _laborTap = _eqEl?.dataset?.eqLaborOnly === '1';
      const _touched = _laborTap || _eqEl?.dataset?.eqTouched === '1';
      return _jbFinalizeStandaloneClockIn({ tenantId, driverId, driverName, source, jobId, job, isShop, customerId, customer, notes, where, tapMs, assetIds: chosenAssetIds,
        assetProvenance: _jbBuildAssetProvenance('jobboard', {
          renderedAssetIds: _rendered,
          selectedAssetIds: chosenAssetIds,
          explicitLaborOnly: _laborTap && chosenAssetIds.length === 0,
          defaulted: !_touched
        }, dayAssetIds) });
    }
    // ON but the job has no equipment this day → explicit empty assetIds[].
    // TASK-1547 — renderedAssetIds:[] = "nothing to pick" (TASK-1539 rule 6:
    // fallback-eligible if the day gets equipment later).
    // TASK-1572 Slice 2 — if the fleet ask was shown, record the answer as a
    // CLAIM on the provenance (assetIds stays [] — see the Slice 2 header).
    const _fleetAsk = _jbReadFleetAsk(document.getElementById('sa-clockin-equipment'));
    return _jbFinalizeStandaloneClockIn({ tenantId, driverId, driverName, source, jobId, job, isShop, customerId, customer, notes, where, tapMs, assetIds: [],
      assetProvenance: _fleetAsk
        ? _jbFleetAskProvenance('jobboard', _fleetAsk, dayAssetIds)
        : _jbBuildAssetProvenance('jobboard', {
          renderedAssetIds: [], selectedAssetIds: [], explicitLaborOnly: false, defaulted: true
        }, dayAssetIds) });
  }

  // OFF path (default) / no job — UNCHANGED: no assetIds field.
  return _jbFinalizeStandaloneClockIn({ tenantId, driverId, driverName, source, jobId, job, isShop, customerId, customer, notes, where, tapMs, assetIds: null });
}

// TASK-491 — standalone clock-in write. assetIds === null → OFF (field omitted,
// identical to pre-TASK-491). Array (incl. []) → per-operator, written
// ATOMICALLY in the create.
// TASK-1547 — assetProvenance rides the same create, only alongside an
// assetIds array; OFF path writes neither field.
async function _jbFinalizeStandaloneClockIn({ tenantId, driverId, driverName, source, jobId, job, isShop, customerId, customer, notes, where, assetIds, assetProvenance, tapMs }) {
  // clock-double-tap — one open shift per person; before the GPS wait. A
  // refusal closes the sheet: the person is already on the clock.
  const _gs = await _jbGuardClockInStart(tenantId, driverId, driverName, 'board-standalone');
  if (!_gs.ok) {
    const _b = document.getElementById('sa-clockin-submit');
    if (_b) { _b.disabled = false; _b.innerHTML = '<span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:20px;">login</span>Clock In'; }
    return;
  }
  // Best-effort GPS capture — never blocks clock-in on failure
  const loc = await captureClockLocation();
  // TASK-191 Phase 3 — geofence distance flag (audit only, NEVER blocks).
  const geo = _jbComputeGeofenceFields(loc, job, isShop, 'in');

  try {
    // TASK-9190 — one Timestamp for the payload AND the same-device marker, so
    // the marker is bound to this exact clockIn value and any later edit voids it.
    // TASK-9694 polish — switch ON: the tap time is the clockIn, server time
    // rides along as audit, skew > 2 min flagged after the ack (same as the
    // job screen, so both Clock In paths write the same shape).
    const _tapMs = Number.isFinite(tapMs) ? _jbTapMsFor({ tapMs, onPhase: () => {} }) : null;
    const _cin = _tapMs != null ? Timestamp.fromMillis(_tapMs) : Timestamp.now();
    const _newEntryRef = await _jbWriteGuardedEntry(_gs, tenantId, driverId, {
      personId: driverId,
      personName: driverName,
      clockIn: _cin,
      clockOut: null,
      status: 'active',
      jobId: jobId || null,
      jobTitle: job?.title || job?.name || '',
      customerId: isShop ? null : (customerId || null),
      customerName: isShop ? 'Shop' : (customer?.name || ''),
      // TASK-355 2026-05-22: distinguish PIN vs SPA member origin for audit/reporting.
      source,
      notes: notes || null,
      ...(Array.isArray(assetIds) ? { assetIds } : {}),
      ...(Array.isArray(assetIds) && assetProvenance ? { assetProvenance } : {}),
      ...(loc ? { clockInLat: loc.lat, clockInLng: loc.lng, clockInAccuracy: loc.accuracy } : {}),
      ...geo,
      ...(_tapMs != null ? { clockInServerAt: serverTimestamp() } : {}),
      createdAt: Timestamp.now()
    });
    // TASK-9190 — remember that THIS device stamped this entry's clockIn.
    _jbMarkDeviceStampedClockIn(_newEntryRef?.id, _cin.toMillis());
    if (_tapMs != null) _jbFlagClockInTapSkew(_newEntryRef, _tapMs);

    const now = _tapMs != null ? new Date(_tapMs) : new Date();
    const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    window.showToast?.(`Clocked in at ${timeStr} — ${where}`, 'success');
    closeStandaloneClockIn();
  } catch (err) {
    if (err && err._userMessageShown) {
      closeStandaloneClockIn();
    } else {
      console.error('[JobBoard] Standalone clock in error:', err);
      window.showToast?.('Error clocking in. Please try again.', 'error');
      const btn = document.getElementById('sa-clockin-submit');
      if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:20px;">login</span>Clock In'; }
    }
  } finally {
    _gs.end();
  }
}

function closeStandaloneClockIn() {
  const modal = document.getElementById('standalone-clockin-modal');
  if (modal) modal.remove();
}

// TASK-491 — every-time equipment confirm shown at clock-in when per-operator
// equipment tracking is ON and the job day has equipment. The driver confirms
// which trucks/trailers/equipment THEY ran (all preselected — confirm is a
// 1-tap accept that doubles as a wrong-job tripwire: wrong job → your truck
// isn't listed). Explicit big "No Equipment (Labor Only)" button. ≥44px targets.
// onConfirm(chosenAssetIds[]) is invoked with the selected ids (or [] for labor
// only). Cancel closes without writing the clock entry.
// TASK-558 — shared builder: resolve a jobDay's asset ids to display rows
// {id, name, type, icon}. Used by both the inline clock-in modal selector
// (TASK-558) and the one-tap-path confirm sheet (TASK-491).
function _jbBuildEquipRows(jobDay) {
  const ids = Array.from(_jbJobDayAssetIds(jobDay));
  return ids.map(id => {
    let asset = _resolveFleetRef(id, JobBoardState.trucks);
    let type = 'Truck', icon = 'local_shipping';
    if (!asset) { asset = _resolveFleetRef(id, JobBoardState.trailers); type = 'Trailer'; icon = 'rv_hookup'; }
    if (!asset) { asset = _resolveFleetRef(id, JobBoardState.assets); type = 'Equipment'; icon = 'construction'; }
    const realId = asset?.id || id;
    const name = (window.getUnitDisplay && asset ? window.getUnitDisplay(asset) : '') || asset?.name || asset?.unit || id;
    return { id: realId, name, type, icon, slot: _jbSlot(jobDay, realId) };
  });
}

// TASK-9797 item 1 S2 — the unit's named slot ("H2-Night"), '' when none / setting off.
function _jbSlot(jobDay, id) {
  const CP = window.CrewPairing;
  try { return CP && CP.slot ? CP.slot(jobDay, id, window.AppState?.settings) : ''; } catch (_) { return ''; }
}

// TASK-558 — shared row markup. All rows default selected (data-selected="1"),
// matching the sheet's all-preselected default. Tapping a row toggles it via
// jbToggleEquipConfirmRow (same handler used inline + in the sheet).
// TASK-9763 — `pre` (optional) = _jbCrewPairPreselect's result. Absent or
// mode 'none' → every row checked, byte-identical to before. Otherwise only
// pre.checked rows start checked, they sort first, and a row dispatch paired to
// THIS person says so. Every row is still listed: a yard swap is one tap.
function _jbBuildEquipRowsHtml(rows, pre) {
  const rowStyle = 'display:flex;align-items:center;gap:12px;width:100%;min-height:56px;padding:12px 14px;border:2px solid #e2e8f0;border-radius:12px;background:#fff;cursor:pointer;text-align:left;-webkit-tap-highlight-color:rgba(0,0,0,0.05);';
  const paired = !!pre && pre.mode && pre.mode !== 'none';
  const isOn = (r) => !paired || pre.checked.has(String(r.id));
  const list = paired ? rows.filter(isOn).concat(rows.filter(r => !isOn(r))) : rows;
  return list.map(r => {
    const on = isOn(r);
    const mine = on && paired && pre.mode === 'paired';
    return `
    <button type="button" class="jb-eqc-row" data-asset-id="${escapeHtml(r.id)}" data-selected="${on ? '1' : '0'}"${mine ? ' data-paired="1"' : ''}
      onclick="window.JobBoardModule.jbToggleEquipConfirmRow(this)"
      style="${rowStyle}${on ? 'border-color:#3b82f6;background:#eff6ff;' : ''}">
      <span class="material-icons jb-eqc-check" style="font-size:24px;color:${on ? '#3b82f6' : '#94a3b8'};">${on ? 'check_box' : 'check_box_outline_blank'}</span>
      <span class="material-icons" style="font-size:22px;color:#64748b;">${r.icon}</span>
      <span style="flex:1;min-width:0;">
        <span style="display:block;font-weight:600;font-size:0.95rem;color:#0f172a;">${escapeHtml(r.name)}</span>
        <span style="display:block;font-size:0.78rem;color:#64748b;">${r.type}${mine ? ' · <strong style="color:#1e40af;">Your assignment</strong>' : ''}${r.slot ? ' · Slot ' + escapeHtml(r.slot) : ''}</span>
      </span>
    </button>`;
  }).join('');
}

// TASK-9763 — "who's in what" (dispatch pairs crew to units per day). The rule
// lives ONCE in window.CrewPairing (functions/src/shared/crewPairing.js); these
// adapters give it this module's dual-key identity: a day array can hold a doc
// id or a unit/name string, so both resolve to the doc id when they can.
// mirrors clock.js _clockUnitKey/_clockPersonKey/_clockCrewPairPreselect — keep in sync.
function _jbUnitKey(ref) {
  const s = String(ref);
  const a = _resolveFleetRef(s, JobBoardState.trucks)
    || _resolveFleetRef(s, JobBoardState.trailers)
    || _resolveFleetRef(s, JobBoardState.assets);
  return a && a.id != null ? String(a.id) : s;
}

function _jbPersonKey(ref) {
  const s = String(ref);
  const p = (JobBoardState.people || []).find(x => x && (String(x.id) === s || x.name === s));
  return p && p.id != null ? String(p.id) : s;
}

// The day's live pairing (read-time filtered). null = no CrewPairing module or
// no day → callers behave exactly as before.
function _jbCrewPairView(jobDay) {
  const CP = window.CrewPairing;
  if (!CP || !jobDay) return null;
  try { return CP.view(jobDay, { unitKey: _jbUnitKey, personKey: _jbPersonKey }); } catch (_) { return null; }
}

// The id wins when we have one: a NAME resolves to the FIRST roster person with
// that name, so two "Steve Smith"s would otherwise share each other's truck
// (COD Edge Case). The name is only the fallback for an id-less session.
function _jbViewerKeys(personId, personName) {
  if (personId) return [String(personId)];
  return personName ? [_jbPersonKey(personName)] : [];
}

// Rule 5 for this module's rows → { mode, checked: Set<row id> }. Any failure
// falls back to mode 'none' (all checked) — a pairing can never trap a driver.
function _jbCrewPairPreselect(jobDay, rows, personId, personName) {
  const all = new Set((rows || []).map(r => String(r.id)));
  const CP = window.CrewPairing;
  const view = _jbCrewPairView(jobDay);
  if (!CP || !view || !view.active) return { mode: 'none', checked: all };
  try {
    const keyOf = new Map(rows.map(r => [String(r.id), _jbUnitKey(r.id)]));
    const res = CP.preselect(view, Array.from(new Set(keyOf.values())), _jbViewerKeys(personId, personName));
    const on = new Set(res.checked || []);
    return { mode: res.mode, checked: new Set(rows.filter(r => on.has(keyOf.get(String(r.id)))).map(r => String(r.id))) };
  } catch (_) {
    return { mode: 'none', checked: all };
  }
}

// Driver card: display names of the units dispatch paired to THIS person on the
// day ([] when unpaired / no pairing). Same read-time filter as the clock-in.
function _jbMyPairedUnitNames(jobDay, personId, personName) {
  const CP = window.CrewPairing;
  const view = _jbCrewPairView(jobDay);
  if (!CP || !view || !view.active || !personId) return [];
  try {
    return CP.mine(view, _jbViewerKeys(personId, personName)).map(id => {
      const n = _jbResolveAssetDisplay(id).name;
      const sl = n && _jbSlot(jobDay, id); // TASK-9797 S2
      return sl ? n + ' · your slot ' + sl : n;
    }).filter(Boolean);
  } catch (_) { return []; }
}

// TASK-9763 S2 — "Different truck" at clock-in. settings.driverTruckSwap
// (Dispatch Settings, default OFF) lets a driver pick ANY active roster truck,
// not only the day's units, when the yard put him in another rig. The pick is a
// real row (checked) and rides assetIds like any other: the server
// (onClockEntryAssetValidate) keeps an off-job id only when it is a real,
// active truck of this tenant, and clock-out bills it as a flagged off-day line
// (TASK-1635). Picking swaps trucks: any checked TRUCK row comes off; trailers
// and equipment stay as they were. Setting off (or tie off) → '' → today's
// markup exactly.
// mirrors clock.js _clockTruckSwap* — keep in sync.
function jbDriverTruckSwapEnabled() {
  return equipmentTieEnabled() && window.AppState?.settings?.driverTruckSwap === true;
}

function _jbIsTruckRef(ref) {
  return !!_resolveFleetRef(String(ref), JobBoardState.trucks);
}

// Active trucks NOT already listed as rows, this driver's recent ones first.
function _jbTruckSwapOptions(rowIds, personId) {
  const listed = new Set();
  (rowIds || []).forEach(r => {
    listed.add(String(r));
    const t = _resolveFleetRef(String(r), JobBoardState.trucks);
    if (t && t.id != null) listed.add(String(t.id));
  });
  let rank = new Map();
  try { rank = _jbRecentAssetRank(personId); } catch (_) { /* ordering is a nicety */ }
  const trucks = _jbSortByRecentUse((JobBoardState.trucks || [])
    .filter(t => t && t.id != null && t.active !== false && t.deleted !== true && t.archived !== true && !listed.has(String(t.id))), rank);
  const escA = window.escAttr || escapeHtml;
  const nameOf = t => (window.getUnitDisplay ? window.getUnitDisplay(t) : '') || t.nickname || t.unit || t.name || 'Truck';
  return {
    count: trucks.length,
    html: '<option value="">Pick from all trucks…</option>'
      + trucks.map(t => `<option value="${escA(String(t.id))}">${escapeHtml(nameOf(t))}</option>`).join('')
  };
}

function _jbTruckSwapHtml(rows, personId) {
  if (!jbDriverTruckSwapEnabled()) return '';
  const opts = _jbTruckSwapOptions((rows || []).map(r => r.id), personId);
  if (opts.count === 0) return '';
  return `
    <label style="display:block;font-size:0.85rem;font-weight:600;color:#334155;margin:12px 0 4px;">In a different truck?</label>
    <select class="jb-eqc-swap" aria-label="Pick a different truck" onchange="window.JobBoardModule.jbEquipSwapPicked(this)"
      style="width:100%;min-height:48px;padding:10px 12px;font-size:16px;border:2px solid #e2e8f0;border-radius:12px;background:#fff;color:#0f172a;">${opts.html}</select>`;
}

function jbEquipSwapPicked(sel) {
  if (!sel || !sel.value) return;
  const id = String(sel.value);
  const host = sel.closest('#jb-equip-confirm-modal, #sa-clockin-equipment');
  const first = host ? host.querySelector('.jb-eqc-row') : null;
  const list = first ? first.parentNode : null;
  if (!host || !list) return;
  const rows = () => Array.from(list.querySelectorAll('.jb-eqc-row'));
  rows().forEach(row => {
    if (row.dataset.selected === '1' && row.dataset.assetId !== id && _jbIsTruckRef(row.dataset.assetId)) jbToggleEquipConfirmRow(row);
  });
  let row = rows().find(r => r.dataset.assetId === id);
  if (!row) {
    const t = _resolveFleetRef(id, JobBoardState.trucks);
    const name = (t && window.getUnitDisplay ? window.getUnitDisplay(t) : '') || t?.nickname || t?.unit || t?.name || 'Truck';
    list.insertAdjacentHTML('afterbegin', _jbBuildEquipRowsHtml(
      [{ id, name, type: 'Truck · not on this job', icon: 'local_shipping' }],
      { mode: 'unpaired', checked: new Set([id]) }));
    row = rows().find(r => r.dataset.assetId === id);
    if (row) row.dataset.swap = '1';
  } else if (row.dataset.selected !== '1') {
    jbToggleEquipConfirmRow(row);
  }
  host.dataset.eqTouched = '1';
  delete host.dataset.eqLaborOnly;
  const opt = Array.from(sel.options).find(o => o.value === id);
  if (opt) opt.remove();
  sel.value = '';
}

function _jbYourTruckLineHtml(jobDay) {
  const names = _jbMyPairedUnitNames(jobDay, getActiveJobBoardPersonId(), getActiveJobBoardPersonName());
  if (!names.length) return '';
  return `<div class="jb-your-truck" style="display:flex;align-items:center;gap:6px;margin:6px 0 2px;font-size:0.85rem;font-weight:700;color:var(--text-primary);"><span class="material-icons" style="font-size:18px;color:#1e40af;">local_shipping</span>Your truck: ${names.map(escapeHtml).join(' + ')}</div>`;
}

function _jbShowEquipmentConfirm(jobDay, onConfirm, onCancel, who) {
  const rows = _jbBuildEquipRows(jobDay);
  // TASK-9763 — only THIS person's paired units start checked (rule 5).
  const pre = _jbCrewPairPreselect(jobDay, rows, who?.personId || null, who?.personName || '');
  const rowsHtml = _jbBuildEquipRowsHtml(rows, pre);

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'jb-equip-confirm-modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:440px;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">build</span>Which equipment will you be in?</h3>
        <button class="btn-icon" onclick="window.JobBoardModule.jbCancelEquipConfirm()" aria-label="Cancel">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;">
        <p style="font-size:0.85rem;color:#64748b;margin:0 0 14px;">${pre.mode === 'none' ? "Tap to confirm the trucks, trailers, and equipment you'll be in this shift. Tap again to deselect." : pre.mode === 'paired' ? "Checked = what dispatch has you in. In something else today? Tap it instead." : "Dispatch hasn't put you in a unit today. Tap the one you're running."}</p>
        <div style="display:flex;flex-direction:column;gap:10px;">
          ${rowsHtml}
        </div>
        ${_jbTruckSwapHtml(rows, who?.personId || null)}
        <button type="button" onclick="window.JobBoardModule.jbConfirmEquipLaborOnly()"
          style="width:100%;min-height:52px;margin-top:16px;padding:14px;font-size:0.95rem;font-weight:600;border:2px dashed #cbd5e1;border-radius:12px;background:#f8fafc;color:#334155;cursor:pointer;">
          <span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:20px;">person</span>No Equipment (Labor Only)
        </button>
        <button type="button" id="jb-equip-confirm-submit" onclick="window.JobBoardModule.jbConfirmEquipSelection()"
          style="width:100%;min-height:52px;margin-top:12px;padding:14px;font-size:1rem;font-weight:700;border:none;border-radius:12px;background:#166534;color:#fff;cursor:pointer;">
          <span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:20px;">login</span>Confirm & Clock In
        </button>
      </div>
    </div>`;
  // Stash the callbacks so the button handlers can reach them.
  modal._jbOnConfirm = onConfirm;
  // TASK-558 — onCancel: instead of silently aborting (the old tap-then-dismiss
  // "ghost" where the driver thought they clocked in but no entry was written),
  // the dismiss/X falls back to clocking in WITHOUT equipment. No path can tap
  // toward clock-in and end up NOT clocked in.
  modal._jbOnCancel = onCancel;
  document.body.appendChild(modal);
}

function jbToggleEquipConfirmRow(btn) {
  const selected = btn.dataset.selected === '1';
  const next = !selected;
  btn.dataset.selected = next ? '1' : '0';
  btn.style.borderColor = next ? '#3b82f6' : '#e2e8f0';
  btn.style.background = next ? '#eff6ff' : '#fff';
  const chk = btn.querySelector('.jb-eqc-check');
  if (chk) { chk.textContent = next ? 'check_box' : 'check_box_outline_blank'; chk.style.color = next ? '#3b82f6' : '#94a3b8'; }
  // TASK-1547 — any row toggle marks the selection as user-touched (provenance
  // defaulted:false). Host = the confirm sheet OR the standalone inline block
  // (this handler is shared by both — TASK-558).
  const host = btn.closest('#jb-equip-confirm-modal, #sa-clockin-equipment');
  if (host) host.dataset.eqTouched = '1';
}

// TASK-1547 — laborOnlyTap: true when the driver reached this finish via the
// "No Equipment (Labor Only)" button (vs Confirm / dismiss). Captured BEFORE
// the modal is removed, alongside what the sheet actually rendered.
function _jbEquipConfirmFinish(chosenAssetIds, laborOnlyTap) {
  const modal = document.getElementById('jb-equip-confirm-modal');
  const cb = modal?._jbOnConfirm;
  const rendered = modal
    ? Array.from(modal.querySelectorAll('.jb-eqc-row')).map(r => r.dataset.assetId).filter(Boolean)
    : [];
  const touched = laborOnlyTap === true || modal?.dataset?.eqTouched === '1';
  const prov = {
    renderedAssetIds: rendered,
    selectedAssetIds: (chosenAssetIds || []).slice(),
    explicitLaborOnly: laborOnlyTap === true && (chosenAssetIds || []).length === 0,
    defaulted: !touched
  };
  if (modal) modal.remove();
  if (typeof cb === 'function') cb(chosenAssetIds, prov);
}

function jbConfirmEquipSelection() {
  const modal = document.getElementById('jb-equip-confirm-modal');
  if (!modal) return;
  const chosen = Array.from(modal.querySelectorAll('.jb-eqc-row'))
    .filter(r => r.dataset.selected === '1')
    .map(r => r.dataset.assetId)
    .filter(Boolean);
  _jbEquipConfirmFinish(chosen, false);
}

function jbConfirmEquipLaborOnly() {
  _jbEquipConfirmFinish([], true);
}

// TASK-558 — Cancel/X on the confirm sheet (only the one-tap-from-a-job-card
// path still uses this sheet; the standalone modal now selects inline). Instead
// of silently aborting (the old ghost: driver thinks they clocked in, no entry
// written), fall back to clocking in WITHOUT equipment via the stashed
// _jbOnCancel callback. No tap toward clock-in can leave the driver un-clocked.
function jbCancelEquipConfirm() {
  const modal = document.getElementById('jb-equip-confirm-modal');
  const onCancel = modal?._jbOnCancel;
  if (modal) modal.remove();
  const btn = document.getElementById('sa-clockin-submit');
  if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:20px;">login</span>Clock In'; }
  if (typeof onCancel === 'function') onCancel();
}

function clockOutStandalone(entryId, jobId, jobDayId) {
  console.log('[JobBoard] clockOutStandalone called:', { entryId, jobId, jobDayId, entriesCount: JobBoardState.clockEntries.length });
  // TASK-9694 — switch ON: the ticket lives on the job's screen. Every board
  // entry point (header button, bottom-nav Clock tile, job card) lands here, so
  // a nav clock-in and a job-screen clock-in reach the SAME inline ticket. Falls
  // through to the classic modal when the job is not on the live board.
  if (isTicketOnJobScreen() && _jbOpenJobScreenForEntryId(entryId)) return;
  // Use the full clock-out/ticket builder from the jobboard
  clockOutFromBoard(entryId, jobId, jobDayId);
}

// ============================================
// MY RECENT JSAS — TASK-361 (2026-05-26) — REMOVED.
// Driver-side "My Recent JSAs" was relocated to Inspections > JSA tab as a
// collapsible. See public/src/views/jsa.js:
//   - renderMyJsaSection / renderJSAActionCard
//   - openMyJsaEmailOverlay / sendMyJsaEmail / downloadMyJsaPdf / getMyJsaSignatureLink
// driverToken threading was added to the JSA module's emailJsaReport +
// downloadJsaPdf callables as part of the move (Independent's COD catch).
// ============================================

// ============================================
// TASK-342 Slice B — Safety Meeting Sign-In (driver portal)
//
// Driver-PIN context auto-fills name. Modal shows greeting + signature pad,
// driver submits, append goes to the same safetySignIns/{date} doc the
// forever URL writes to (server gate on settings.safetyMeetingSignInEnabled
// enforced in submitSafetySignIn, slice A). Source field on the signature
// distinguishes driver_portal vs forever_url for the admin.
// ============================================

function openSafetyMeetingModal() {
  const tenantId = window.AppState?.tenant;
  if (!tenantId) {
    window.showToast?.('Not signed in', 'error');
    return;
  }
  if (window.AppState?.settings?.safetyMeetingSignInEnabled !== true) {
    window.showToast?.('Safety meeting sign-in is not enabled for this carrier', 'info');
    return;
  }
  // Resolve identity across PIN-flow drivers AND admin/member auth context.
  // Server gate at submitSafetySignIn validates personId against roster when non-null,
  // so falling back to memberAccess.personId or null is safe.
  const authUser = window.AppState?.user;
  const personId = window.AppState?.driverPersonId
    || window.AppState?.memberAccess?.personId
    || null;
  const driverName = window.AppState?.driverPersonName
    || window.AppState?.driverFirstName
    || authUser?.displayName
    || (authUser?.email ? authUser.email.split('@')[0] : '')
    || '';
  if (!driverName) {
    window.showToast?.('Could not resolve your name — try refreshing the page', 'error');
    return;
  }

  // Remove any prior modal
  document.getElementById('safety-meeting-modal')?.remove();

  const modal = document.createElement('div');
  modal.id = 'safety-meeting-modal';
  modal.className = 'modal';
  modal.style.zIndex = '1100';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:520px;width:100%;">
      <div class="modal-header" style="background:linear-gradient(135deg,#059669,#10B981);color:#fff;">
        <h3 style="margin:0;color:#fff;display:flex;align-items:center;gap:8px;">
          <span class="material-icons">how_to_reg</span> Safety Meeting Sign-In
        </h3>
        <button class="modal-close" onclick="window.JobBoardModule.closeSafetyMeetingModal()" style="color:#0F172A;font-size:24px;line-height:1;" aria-label="Close">×</button>
      </div>
      <div class="modal-body" style="padding:20px;">
        <p style="margin:0 0 8px;font-size:1.05rem;color:var(--text-primary);">Hi <strong>${escapeHtml(driverName.split(/\s+/)[0])}</strong> — sign below to mark yourself in for today's safety meeting.</p>
        <p style="margin:0 0 14px;font-size:0.78rem;color:var(--text-muted);">Your signature is timestamped and saved to today's roster with the meeting's topics.</p>
        <!-- TASK-4882 — shown INSTEAD of the pad when today's sheet has no outline
             (server probe on open, or a 409 on submit). -->
        <div id="safety-meeting-not-ready" style="display:none;text-align:center;padding:8px 0 4px;">
          <span class="material-icons" style="font-size:40px;color:#d97706;">report_problem</span>
          <div style="font-size:1.05rem;font-weight:600;color:var(--text-primary);margin:8px 0 6px;">Today's meeting isn't set up yet</div>
          <div id="safety-meeting-not-ready-msg" style="font-size:0.9rem;color:var(--text-secondary);line-height:1.5;margin-bottom:16px;"></div>
          <div style="display:flex;gap:8px;">
            <button type="button" class="btn btn-secondary" onclick="window.JobBoardModule.closeSafetyMeetingModal()" style="flex:1;">Close</button>
            <button type="button" class="btn btn-primary" id="btn-safety-meeting-retry" onclick="window.JobBoardModule.probeSafetyMeetingReady()" style="flex:1;">Check again</button>
          </div>
        </div>
        <!-- TASK-9742 — the company's documents for today's meeting, read before signing. -->
        <div id="safety-meeting-docs" style="display:none;background:var(--bg-secondary);border-radius:8px;padding:10px 12px;margin-bottom:12px;">
          <div style="font-weight:600;color:var(--text-primary);font-size:0.95rem;margin-bottom:2px;">Review before you sign</div>
          <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:4px;">Each opens in a new tab. Come back here to sign.</div>
          <div id="safety-meeting-docs-list"></div>
        </div>
        <div id="safety-meeting-pad" class="signature-canvas-container" style="border:1px solid var(--border-light);border-radius:8px;background:#fff;margin-bottom:8px;position:relative;">
          <canvas id="safety-meeting-sig-canvas" width="400" height="160" style="width:100%;touch-action:none;"></canvas>
          <button type="button" class="btn btn-ghost btn-sm" onclick="window.JobBoardModule.clearSafetyMeetingSignature()" style="position:absolute;top:4px;right:4px;">
            <span class="material-icons" style="font-size:16px;">refresh</span>
          </button>
        </div>
        <div id="safety-meeting-actions" style="display:flex;gap:8px;margin-top:12px;">
          <button type="button" class="btn btn-secondary" onclick="window.JobBoardModule.closeSafetyMeetingModal()" style="flex:1;">Cancel</button>
          <button type="button" class="btn btn-primary" id="btn-safety-meeting-submit"
            onclick="window.JobBoardModule.submitSafetyMeetingSignIn()" style="flex:2;background:#059669;">
            <span class="material-icons">draw</span> Sign In
          </button>
        </div>
        <div id="safety-meeting-status" style="font-size:0.85rem;color:var(--text-muted);text-align:center;margin-top:10px;"></div>
        ${window.AppState?.driverPersonId ? `<!-- TASK-4883 — the driver's own past sheets live in My Documents. -->
        <button type="button" id="btn-safety-meeting-history" class="btn btn-ghost" onclick="window.JobBoardModule.openSafetyMeetingHistory()" style="width:100%;margin-top:10px;min-height:44px;color:var(--text-primary);">
          <span class="material-icons" style="font-size:18px;vertical-align:-4px;">history</span> My past sign-ins
        </button>` : ''}
      </div>
    </div>`;
  document.body.appendChild(modal);
  document.body.classList.add('modal-open');

  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeSafetyMeetingModal();
  });

  // Init signature canvas. Mirrors driver-sig-canvas pattern (jobboard.js:1765+)
  // but with its own canvas id so the two modals don't fight over the ref.
  setTimeout(() => _initSafetyMeetingSigCanvas(), 50);
  // TASK-4882 — ask the server whether today's sheet is outlined BEFORE the
  // driver draws anything. A failed probe leaves the pad up: the server is the
  // gate, this is the courtesy.
  probeSafetyMeetingReady();
}

// TASK-4882 — swap the pad for the "not set up yet" panel (or back).
function _setSafetyMeetingNotReady(message) {
  const panel = document.getElementById('safety-meeting-not-ready');
  const pad = document.getElementById('safety-meeting-pad');
  const actions = document.getElementById('safety-meeting-actions');
  const status = document.getElementById('safety-meeting-status');
  if (!panel) return;
  const notReady = typeof message === 'string';
  panel.style.display = notReady ? '' : 'none';
  if (notReady) { const docsBox = document.getElementById('safety-meeting-docs'); if (docsBox) docsBox.style.display = 'none'; }
  if (pad) pad.style.display = notReady ? 'none' : '';
  if (actions) actions.style.display = notReady ? 'none' : '';
  if (status && notReady) status.textContent = '';
  const msg = document.getElementById('safety-meeting-not-ready-msg');
  if (msg && notReady) msg.textContent = message || "The office hasn't filled in today's sign-in sheet yet. Ask them to add the meeting type and topics, then check again.";
}

// TASK-4882 — same endpoint as the real sign-in, `probe:true`, no name or
// signature. Carries the session token like the submit does so the server can
// tie the body tenantId to this driver (TASK-4879 token-less path).
async function probeSafetyMeetingReady() {
  const tenantId = window.AppState?.tenant;
  if (!tenantId) return;
  const retry = document.getElementById('btn-safety-meeting-retry');
  if (retry) { retry.disabled = true; retry.textContent = 'Checking…'; }
  try {
    const FUNCTIONS_BASE = 'https://us-central1-dispatch-deck-be88c.cloudfunctions.net';
    const headers = { 'Content-Type': 'application/json' };
    try {
      const idToken = await auth?.currentUser?.getIdToken?.();
      if (idToken) headers.Authorization = `Bearer ${idToken}`;
    } catch (_) { /* best effort, same as submit */ }
    const r = await fetch(`${FUNCTIONS_BASE}/submitSafetySignIn`, {
      method: 'POST', headers, body: JSON.stringify({ probe: true, tenantId }),
    });
    const d = await r.json().catch(() => ({}));
    if (r.ok && d.ok === true && d.ready === false) {
      _setSafetyMeetingNotReady(d.message || '');
      if (retry) { // a RE-check that is still not ready must say it was checked
        const msg = document.getElementById('safety-meeting-not-ready-msg');
        if (msg && retry.dataset.checked) msg.textContent += ' (Checked just now — still not set up.)';
        retry.dataset.checked = '1';
      }
    } else if (r.ok && d.ok === true && d.ready === true) {
      _setSafetyMeetingNotReady(null);
      _renderSafetyMeetingDocs(d.documents); // TASK-9742
    }
    // Any other answer (carrier off, unverifiable session): leave the pad; submit reports it.
  } catch (err) {
    console.warn('[JobBoard] safety readiness probe failed — leaving the pad up:', err);
    // COD Code Reviewer: when the panel is ALREADY up, a failed re-check must
    // not look like "the office still hasn't done it".
    const panel = document.getElementById('safety-meeting-not-ready');
    const msg = document.getElementById('safety-meeting-not-ready-msg');
    if (panel && panel.style.display !== 'none' && msg) {
      msg.textContent = "Couldn't reach DispatchDeck to check — look at your signal and try again.";
    }
  } finally {
    if (retry) { retry.disabled = false; retry.textContent = 'Check again'; }
  }
}

// TASK-9742 — list the documents the office attached to today's sheet. Titles
// are escaped; a link is used only when it is a Firebase Storage download URL
// (the server already vouched it is the file's own).
function _renderSafetyMeetingDocs(docs) {
  const box = document.getElementById('safety-meeting-docs');
  const list = document.getElementById('safety-meeting-docs-list');
  if (!box || !list) return;
  const items = Array.isArray(docs) ? docs.slice(0, 10) : [];
  list.innerHTML = items.map((d) => {
    const title = escapeHtml(String((d && d.title) || 'Document').slice(0, 120));
    const url = d && typeof d.url === 'string' && d.url.startsWith('https://firebasestorage.googleapis.com/') ? d.url : '';
    return `<div style="padding:6px 0;border-top:1px solid var(--border-light);font-size:0.95rem;color:var(--text-primary);word-break:break-word;">
      <span class="material-icons" style="font-size:18px;vertical-align:-4px;color:var(--text-secondary);">description</span>
      ${url ? `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer" style="color:var(--brand-blue);font-weight:500;">${title}</a>` : `${title} <span style="color:var(--text-secondary);font-size:0.8rem;">(ask the office for a copy)</span>`}
    </div>`;
  }).join('');
  box.style.display = items.length ? '' : 'none';
}

// TASK-4883 — past sign-ins are listed (own sheets only, server-scoped) in the
// driver's My Documents panel, which lives in hr.js.
function openSafetyMeetingHistory() {
  if (typeof window.HRModule?.openDriverMyDocuments !== 'function') {
    window.showToast?.('My Documents is loading — try again in a moment', 'info');
    return;
  }
  closeSafetyMeetingModal();
  window.HRModule.openDriverMyDocuments();
}

function closeSafetyMeetingModal() {
  const modal = document.getElementById('safety-meeting-modal');
  if (modal) modal.remove();
  document.body.classList.remove('modal-open');
  JobBoardState.safetySigCanvas = null;
  JobBoardState.safetySigCtx = null;
  JobBoardState.safetySigHasDrawn = false;
}

function _initSafetyMeetingSigCanvas() {
  const canvas = document.getElementById('safety-meeting-sig-canvas');
  if (!canvas) return;
  // TASK-4534 — back the canvas at devicePixelRatio: the fixed attr size
  // CSS-stretched across a high-DPI phone was the classic soft/blurry legal
  // signature. Lock the displayed size first (style), then upscale the backing
  // store; getPos below already normalizes by canvas.width/rect.width so
  // coordinates stay exact at any backing size. lineWidth scales with dpr so
  // the stroke keeps its visual weight.
  const _dpr = window.devicePixelRatio || 1;
  const _rect = canvas.getBoundingClientRect();
  if (_rect.width > 2 && _rect.height > 2) {
    canvas.style.width = _rect.width + 'px';
    canvas.style.height = _rect.height + 'px';
    canvas.width = Math.round(_rect.width * _dpr);
    canvas.height = Math.round(_rect.height * _dpr);
  }
  JobBoardState.safetySigCanvas = canvas;
  const ctx = canvas.getContext('2d');
  JobBoardState.safetySigCtx = ctx;
  ctx.strokeStyle = '#1E293B';
  ctx.lineWidth = 2 * _dpr;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  JobBoardState.safetySigHasDrawn = false;

  let drawing = false;
  let lastX = 0, lastY = 0;
  const getPos = (e) => {
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const ev = e.touches ? e.touches[0] : e;
    return { x: (ev.clientX - rect.left) * sx, y: (ev.clientY - rect.top) * sy };
  };
  const start = (e) => {
    e.preventDefault?.();
    drawing = true;
    const p = getPos(e);
    lastX = p.x; lastY = p.y;
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault?.();
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastX = p.x; lastY = p.y;
    JobBoardState.safetySigHasDrawn = true;
  };
  const end = () => { drawing = false; };
  canvas.addEventListener('mousedown', start);
  canvas.addEventListener('mousemove', move);
  canvas.addEventListener('mouseup', end);
  canvas.addEventListener('mouseout', end);
  canvas.addEventListener('touchstart', start, { passive: false });
  canvas.addEventListener('touchmove', move, { passive: false });
  canvas.addEventListener('touchend', end);
}

function clearSafetyMeetingSignature() {
  const canvas = JobBoardState.safetySigCanvas;
  const ctx = JobBoardState.safetySigCtx;
  if (!canvas || !ctx) return;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  JobBoardState.safetySigHasDrawn = false;
}

async function submitSafetyMeetingSignIn() {
  const status = document.getElementById('safety-meeting-status');
  const btn = document.getElementById('btn-safety-meeting-submit');
  if (!JobBoardState.safetySigHasDrawn) {
    if (status) { status.textContent = 'Please draw your signature first.'; status.style.color = '#dc2626'; }
    return;
  }
  const tenantId = window.AppState?.tenant;
  // TASK-348 — fall back to memberAccess.personId for SPA-signed-in members.
  const personId = window.AppState?.driverPersonId
    || window.AppState?.memberAccess?.personId
    || null;
  const driverName = window.AppState?.driverPersonName
    || window.AppState?.driverFirstName
    || window.AppState?._personalActionsName
    || window.AppState?.user?.displayName
    || '';
  if (!tenantId || !driverName) {
    if (status) { status.textContent = 'Missing identity — try refreshing.'; status.style.color = '#dc2626'; }
    return;
  }

  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Signing In…'; }
  if (status) { status.textContent = 'Saving your signature…'; status.style.color = 'var(--text-muted)'; }

  try {
    const signature = JobBoardState.safetySigCanvas.toDataURL('image/png');
    const FUNCTIONS_BASE = 'https://us-central1-dispatch-deck-be88c.cloudfunctions.net';
    // TASK-4879 — this path legitimately has no link token: the tenant comes
    // from the driver's own session. So it PROVES that session instead. The
    // server accepts a body tenantId only from a caller it can tie to that
    // tenant (an office member, or a live driverSessions doc — a PIN driver has
    // no members doc, so both are accepted). Best-effort by design: if the token
    // can't be fetched we still POST, and Trippin Trucking's grandfathered
    // forever-URL path keeps working regardless, so nobody who can sign today
    // stops being able to.
    const headers = { 'Content-Type': 'application/json' };
    try {
      const idToken = await auth?.currentUser?.getIdToken?.();
      if (idToken) headers.Authorization = `Bearer ${idToken}`;
    } catch (tokErr) {
      console.warn('[JobBoard] could not attach session token to safety sign-in:', tokErr);
    }
    const response = await fetch(`${FUNCTIONS_BASE}/submitSafetySignIn`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        tenantId,
        personId,
        name: driverName,
        signature,
        userAgent: navigator.userAgent,
      }),
    });
    const data = await response.json().catch(() => ({}));
    // TASK-4882 — the office hasn't outlined today's meeting (or cleared it
    // after this modal opened). Not a retry case: show what's missing.
    if (response.status === 409 && data.code === 'outline_missing') {
      if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">draw</span> Sign In'; }
      _setSafetyMeetingNotReady(data.error || '');
      return;
    }
    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Sign-in failed');
    }
    window.showToast?.('Signed in for today\'s safety meeting!', 'success');
    closeSafetyMeetingModal();
  } catch (err) {
    console.error('[JobBoard] Safety meeting sign-in failed:', err);
    if (status) { status.textContent = err.message || 'Failed to sign in. Please try again.'; status.style.color = '#dc2626'; }
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">draw</span> Sign In'; }
  }
}

// ============================================
// TASK-437 — DRIVER PPE SIGN-OFF (attestation)
// ============================================
// Identity-locked PPE attestation for the PIN-driver portal. Three flows, ONE
// `safetyEquipment` collection (discriminated by `source`):
//   • admin issues   → source:'admin', no acknowledgment (handled in hr.js)
//   • driver ACKs an admin-issued item → UPDATE the existing doc (set
//     acknowledgment + acknowledged:true). NO new row, NO inventory decrement.
//   • driver SELF-LOGS gear → CREATE source:'driver' + acknowledgment populated.
//     NO client-side decrementSafetyInventory (driver session has no inventory
//     state; self-logs are reconciled admin-side — see TASK-437 landmine note).
// Identity comes from getActiveJobBoardPersonId/Name (single source of truth).
// Signature pad mirrors _initSafetyMeetingSigCanvas verbatim (own canvas id).

// Local copy of the size list (hr.js EQUIPMENT_SIZES). Not imported to avoid the
// cross-module ?v= cache-buster duplicate-instance trap (CLAUDE.md pitfall #1).
const PPE_EQUIPMENT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

async function openPpeModal() {
  const tenantId = window.AppState?.tenant;
  if (!tenantId) { window.showToast?.('Not signed in', 'error'); return; }
  if (window.AppState?.settings?.safetyEquipmentDriverEnabled !== true) {
    window.showToast?.('PPE sign-off is not enabled for this carrier', 'info');
    return;
  }
  const personId = getActiveJobBoardPersonId();
  const personName = getActiveJobBoardPersonName();
  if (!personId) {
    window.showToast?.('Could not resolve your identity — try refreshing the page', 'error');
    return;
  }

  // Reset modal state for a fresh open.
  JobBoardState.ppeMode = 'list';
  JobBoardState.ppeAckTargetId = null;
  JobBoardState.ppeSigHasDrawn = false;

  document.getElementById('ppe-modal')?.remove();
  const modal = document.createElement('div');
  modal.id = 'ppe-modal';
  modal.className = 'modal';
  modal.style.zIndex = '1100';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:540px;width:100%;max-height:90vh;max-height:90dvh;overflow-y:auto;">
      <div class="eticket-form-header">
        <div class="eticket-form-header-title">
          <span class="material-icons">shield</span>
          PPE Sign-Off
        </div>
        <button class="modal-close" onclick="window.JobBoardModule.closePpeModal()" style="color:#fff;" aria-label="Close">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:18px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:14px;padding:8px 12px;background:var(--bg-secondary);border-radius:8px;">
          <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">person</span>
          <span style="font-weight:600;color:var(--text-primary);">You — ${escapeHtml(personName || 'Driver')}</span>
        </div>
        <div id="ppe-modal-content">
          <!-- TASK-697: loading state — the gear list + self-log form load async
               (equipment types + pending items); without this the modal shows a blank
               sliver under the name until the read resolves (mrscdd 2026-06-19). -->
          <div style="display:flex;flex-direction:column;align-items:center;gap:10px;padding:34px 16px;color:var(--text-secondary);">
            <span class="material-icons spin" style="font-size:30px;">autorenew</span>
            <span style="font-size:0.85rem;">Loading your PPE…</span>
          </div>
        </div>
      </div>
    </div>`;
  document.body.appendChild(modal);
  document.body.classList.add('modal-open');
  modal.addEventListener('click', (e) => { if (e.target === modal) closePpeModal(); });

  // Load equipment types + this driver's records, then render the list view.
  // 🔴 Wrapped (mrscdd 2026-06-19 "stuck on Loading your PPE"): if the load or the
  // render throws/hangs, the modal must NOT sit on an eternal spinner — show a clear
  // error + Retry, and SURFACE the actual error so it's diagnosable (the read path
  // resolves/rejects-fast in repro, so a stuck spinner = a render throw or a stalled
  // network; either way the driver gets a recoverable state, not a frozen modal).
  try {
    await Promise.race([
      _ppeLoadData(tenantId, personId),
      new Promise((_, rej) => setTimeout(() => rej(new Error('Timed out loading PPE — check your connection and tap Retry.')), 12000)),
    ]);
    _ppeRenderList();
  } catch (e) {
    console.error('[PPE] open failed:', e?.message || e, e);
    const c = document.getElementById('ppe-modal-content');
    if (c) {
      c.innerHTML = `
        <div style="padding:24px 16px;text-align:center;color:var(--text-secondary);">
          <span class="material-icons" style="font-size:30px;color:var(--status-danger,#dc2626);display:block;margin-bottom:8px;">error_outline</span>
          <div style="font-size:0.9rem;color:var(--text-primary);margin-bottom:4px;">Couldn't load your PPE</div>
          <div style="font-size:0.78rem;margin-bottom:14px;">${escapeHtml(e?.message || 'Please try again.')}</div>
          <button class="btn btn-secondary" onclick="window.JobBoardModule.openPpeModal()"><span class="material-icons">refresh</span> Retry</button>
        </div>`;
    }
  }
}

function closePpeModal() {
  document.getElementById('ppe-modal')?.remove();
  document.body.classList.remove('modal-open');
  JobBoardState.ppeSigCanvas = null;
  JobBoardState.ppeSigCtx = null;
  JobBoardState.ppeSigHasDrawn = false;
  JobBoardState.ppeMode = 'list';
  JobBoardState.ppeAckTargetId = null;
  JobBoardState.ppeJobCtx = null;
}

async function _ppeLoadData(tenantId, personId) {
  JobBoardState.ppeTypes = [];
  JobBoardState.ppePending = [];
  try {
    // Active equipment types (for the self-log form). One-shot read — the driver
    // session has no HRState subscription.
    const typesSnap = await getDocs(query(
      collection(db, 'tenants', tenantId, 'safetyEquipmentTypes'),
      orderBy('name', 'asc')
    ));
    JobBoardState.ppeTypes = typesSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(t => t.active !== false);
  } catch (e) {
    console.warn('[PPE] could not load equipment types:', e?.message || e);
  }
  try {
    // Admin-issued items for THIS driver that have NOT yet been acknowledged.
    // Read scoped to own personId (rules allow tenant read; we filter to self).
    const recSnap = await getDocs(query(
      collection(db, 'tenants', tenantId, 'safetyEquipment'),
      where('personId', '==', personId)
    ));
    JobBoardState.ppePending = recSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      // Pending = admin-issued (or legacy/no-source) AND not yet acknowledged
      // AND not already handed back.
      //
      // TASK-1586 — the `returned` clause is the DRIVER-side twin of the count in
      // hr.js (`_ppeStatusLabel` / the Safety "By Person" pending badge). Without
      // it a returned item sat in this queue forever, asking the driver to sign
      // for gear they already gave back. The two predicates must move together.
      //
      // Read through the shared helper so the three surfaces can't disagree about
      // what a row's status IS — but tolerate its absence rather than throw: this
      // whole block is inside a catch that only console.warns, so a throw here
      // would silently empty the driver's sign-off queue instead of failing loud.
      // The fallback reads `status` directly, which is identical for 'returned'.
      .filter(r => (r.source !== 'driver') && !r.acknowledgment && r.acknowledged !== true
        && (window.PersonSections?.equipmentStatus?.(r) || String(r.status || '').trim()) !== 'returned'
        // TASK-9719 — a VOIDED issue (logged by mistake, never handed out) is not
        // something to sign for; firestore.rules also refuses an ack on it.
        && !r.voidedAt)
      .sort((a, b) => String(b.dateReceived || '').localeCompare(String(a.dateReceived || '')));
  } catch (e) {
    console.warn('[PPE] could not load equipment records:', e?.message || e);
  }
}

function _ppeRenderList() {
  const c = document.getElementById('ppe-modal-content');
  if (!c) return;
  JobBoardState.ppeMode = 'list';
  const pending = JobBoardState.ppePending || [];

  let pendingHtml = '';
  if (pending.length) {
    pendingHtml = `
      <h4 style="margin:0 0 8px;font-size:0.95rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:18px;color:#d97706;">pending_actions</span>
        Waiting for your sign-off
      </h4>
      <div style="display:grid;gap:8px;margin-bottom:18px;">
        ${pending.map(r => `
          <div style="display:flex;align-items:center;gap:10px;padding:10px 12px;background:var(--bg-secondary);border:1px solid var(--border-light);border-radius:8px;">
            <span class="material-icons" style="font-size:18px;color:var(--text-tertiary);">shield</span>
            <span style="flex:1;font-size:0.88rem;">
              <strong>${escapeHtml(r.equipmentType || 'Equipment')}</strong>${r.size ? ' (' + escapeHtml(r.size) + ')' : ''} ×${escapeHtml(String(r.quantity || 1))}
              ${r.dateReceived ? `<span style="display:block;font-size:0.72rem;color:var(--text-secondary);">Issued ${escapeHtml(r.dateReceived)}</span>` : ''}
            </span>
            <button class="btn btn-primary btn-sm" onclick="window.JobBoardModule.ppeStartAck('${r.id}')">
              <span class="material-icons" style="font-size:16px;">draw</span> Sign off
            </button>
          </div>
        `).join('')}
      </div>`;
  } else {
    pendingHtml = `
      <div style="padding:14px;text-align:center;color:var(--text-secondary);font-size:0.85rem;background:var(--bg-secondary);border-radius:8px;margin-bottom:18px;">
        <span class="material-icons" style="font-size:24px;display:block;margin-bottom:4px;color:var(--status-success,#16a34a);">check_circle</span>
        Nothing waiting for sign-off.
      </div>`;
  }

  c.innerHTML = `
    ${pendingHtml}
    <div style="border-top:1px solid var(--border-light);padding-top:14px;">
      <h4 style="margin:0 0 8px;font-size:0.95rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">add_circle</span>
        Log gear you grabbed
      </h4>
      <p style="margin:0 0 10px;font-size:0.78rem;color:var(--text-secondary);">Picked PPE off the shelf yourself? Log it here so it's on your record.</p>
      <button class="btn btn-secondary" onclick="window.JobBoardModule.ppeStartSelfLog()" style="width:100%;">
        <span class="material-icons">inventory_2</span> Log PPE I received
      </button>
    </div>`;
}

// Shared attestation form (condition + fits + signature) used by BOTH the ack
// flow and the self-log flow. `mode` decides the write performed on submit.
function _ppeRenderAttestation({ mode, headingHtml }) {
  const c = document.getElementById('ppe-modal-content');
  if (!c) return;
  JobBoardState.ppeSigHasDrawn = false;

  // Self-log mode needs the type/size/qty picker; ack mode targets a known doc.
  let pickerHtml = '';
  if (mode === 'selflog') {
    const types = JobBoardState.ppeTypes || [];
    pickerHtml = `
      <div class="form-group">
        <label for="ppe-type-select" style="font-size:0.82rem;">Equipment</label>
        <select id="ppe-type-select" class="form-control" onchange="window.JobBoardModule.ppeOnTypeChange()">
          <option value="">Select type…</option>
          ${types.map(t => `<option value="${escapeHtml(t.id)}" data-name="${escapeHtml(t.name)}" data-has-size="${t.hasSize ? '1' : ''}">${escapeHtml(t.name)}</option>`).join('')}
        </select>
      </div>
      <div id="ppe-size-wrap" style="display:none;" class="form-group">
        <label for="ppe-size-select" style="font-size:0.82rem;">Size</label>
        <select id="ppe-size-select" class="form-control" style="max-width:140px;">
          ${PPE_EQUIPMENT_SIZES.map(s => `<option value="${s}">${s}</option>`).join('')}
        </select>
      </div>
      <div class="form-group">
        <label for="ppe-qty" style="font-size:0.82rem;">Qty</label>
        <input type="number" id="ppe-qty" class="form-control" value="1" min="1" max="99" style="max-width:100px;">
      </div>
      <div id="ppe-job-group" style="display:none;" class="form-group">
        <label for="ppe-job-select" style="font-size:0.82rem;">Job (optional)</label>
        <select id="ppe-job-select" class="form-control"><option value="">No job</option></select>
        <div id="ppe-job-hint" style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;"></div>
      </div>`;
  }

  c.innerHTML = `
    <button class="btn btn-ghost btn-sm" onclick="window.JobBoardModule.ppeBackToList()" style="margin-bottom:8px;padding-left:0;">
      <span class="material-icons" style="font-size:18px;">arrow_back</span> Back
    </button>
    ${headingHtml}
    ${pickerHtml}
    <div class="form-group">
      <label style="font-size:0.82rem;">Condition</label>
      <div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:4px;">
        <label style="display:flex;align-items:center;gap:5px;font-size:0.85rem;font-weight:normal;cursor:pointer;">
          <input type="radio" name="ppe-condition" value="good" checked> Good
        </label>
        <label style="display:flex;align-items:center;gap:5px;font-size:0.85rem;font-weight:normal;cursor:pointer;">
          <input type="radio" name="ppe-condition" value="damaged"> Damaged
        </label>
        <label style="display:flex;align-items:center;gap:5px;font-size:0.85rem;font-weight:normal;cursor:pointer;">
          <input type="radio" name="ppe-condition" value="wrong_size"> Wrong size
        </label>
      </div>
    </div>
    <div class="form-group">
      <label style="display:flex;align-items:center;gap:6px;font-size:0.85rem;font-weight:normal;cursor:pointer;">
        <input type="checkbox" id="ppe-fits" checked> It fits / is the right gear
      </label>
    </div>
    <div class="form-group">
      <label for="ppe-notes" style="font-size:0.82rem;">Notes / Serial Number (Optional)</label>
      <textarea id="ppe-notes" class="form-control" rows="2" maxlength="500"
        placeholder="e.g. serial #, calibration date, condition note" style="resize:vertical;"></textarea>
    </div>
    <div class="form-group">
      <label style="font-size:0.82rem;">Your signature</label>
      <div class="signature-canvas-container" style="border:1px solid var(--border-light);border-radius:8px;background:#fff;position:relative;">
        <canvas id="ppe-sig-canvas" width="400" height="150" style="width:100%;touch-action:none;"></canvas>
        <button type="button" class="btn btn-ghost btn-sm" onclick="window.JobBoardModule.ppeClearSignature()" style="position:absolute;top:4px;right:4px;">
          <span class="material-icons" style="font-size:16px;">refresh</span>
        </button>
      </div>
    </div>
    <div style="display:flex;gap:8px;margin-top:12px;">
      <button type="button" class="btn btn-secondary" onclick="window.JobBoardModule.ppeBackToList()" style="flex:1;">Cancel</button>
      <button type="button" class="btn btn-primary" id="ppe-submit-btn"
        onclick="window.JobBoardModule.ppeSubmit('${mode}')" style="flex:2;">
        <span class="material-icons">check</span> ${mode === 'ack' ? 'Confirm Receipt' : 'Save & Sign'}
      </button>
    </div>
    <div id="ppe-status" style="font-size:0.82rem;color:var(--text-muted);text-align:center;margin-top:10px;"></div>`;

  setTimeout(() => _ppeInitSigCanvas(), 50);
}

function ppeBackToList() { _ppeRenderList(); }

function ppeStartAck(docId) {
  const rec = (JobBoardState.ppePending || []).find(r => r.id === docId);
  if (!rec) { window.showToast?.('Item not found — refresh', 'error'); return; }
  JobBoardState.ppeMode = 'ack';
  JobBoardState.ppeAckTargetId = docId;
  _ppeRenderAttestation({
    mode: 'ack',
    headingHtml: `
      <div style="padding:10px 12px;background:var(--bg-secondary);border-radius:8px;margin-bottom:12px;font-size:0.88rem;">
        Confirming receipt of <strong>${escapeHtml(rec.equipmentType || 'Equipment')}</strong>${rec.size ? ' (' + escapeHtml(rec.size) + ')' : ''} ×${escapeHtml(String(rec.quantity || 1))}
      </div>`,
  });
}

function ppeStartSelfLog() {
  JobBoardState.ppeMode = 'selflog';
  JobBoardState.ppeAckTargetId = null;
  JobBoardState.ppeJobCtx = null;
  JobBoardState.ppeJobGen = (JobBoardState.ppeJobGen || 0) + 1;
  _ppeRenderAttestation({
    mode: 'selflog',
    headingHtml: `<p style="margin:0 0 10px;font-size:0.85rem;color:var(--text-primary);">Log PPE you picked up and sign for it.</p>`,
  });
  _ppeLoadSelfLogJob();
}

// TASK-9721 — fill the optional Job picker for the driver's own self-log. Their
// own jobs only (open clock entry + their assignments today), never the rest of
// the board. The field stays hidden until there is something to pick, and a
// failed or slow lookup leaves it hidden: the self-log saves exactly as before.
async function _ppeLoadSelfLogJob() {
  const tenantId = window.AppState?.tenant;
  const personId = getActiveJobBoardPersonId();
  if (!tenantId || !personId) return;
  const gen = JobBoardState.ppeJobGen;
  const M = await _ppeJobModLoad().catch(() => null);
  if (!M) return;
  const ctx = await M.loadPpeIssueJobContext({
    fs: { collection, query, where, getDocs, getDoc, doc, Timestamp },
    db, tenantId, personId, personName: getActiveJobBoardPersonName() || '',
    dateStr: M.ppeLocalDateStr(new Date()), isToday: true, withOthers: false,
  });
  // The driver may have backed out, reopened the form, or switched identity meanwhile.
  if (JobBoardState.ppeMode !== 'selflog' || gen !== JobBoardState.ppeJobGen
    || window.AppState?.tenant !== tenantId || getActiveJobBoardPersonId() !== personId) return;
  ctx._personId = personId;
  ctx._day = M.ppeLocalDateStr(new Date());
  const group = document.getElementById('ppe-job-group');
  const sel = document.getElementById('ppe-job-select');
  if (!group || !sel) return;
  JobBoardState.ppeJobCtx = ctx;
  if (!ctx.candidates.length) return;
  sel.innerHTML = M.ppeIssueJobOptionsHtml(ctx, ctx.jobId || '', escapeHtml);
  sel.value = ctx.jobId || '';
  const hint = document.getElementById('ppe-job-hint');
  if (hint) hint.textContent = M.ppeIssueJobHint(ctx, { self: true });
  group.style.display = '';
}

function ppeOnTypeChange() {
  const sel = document.getElementById('ppe-type-select');
  const hasSize = sel?.selectedOptions?.[0]?.dataset?.hasSize === '1';
  const wrap = document.getElementById('ppe-size-wrap');
  if (wrap) wrap.style.display = hasSize ? '' : 'none';
}

function _ppeInitSigCanvas() {
  const canvas = document.getElementById('ppe-sig-canvas');
  if (!canvas) return;
  // TASK-4534 — back the canvas at devicePixelRatio: the fixed attr size
  // CSS-stretched across a high-DPI phone was the classic soft/blurry legal
  // signature. Lock the displayed size first (style), then upscale the backing
  // store; getPos below already normalizes by canvas.width/rect.width so
  // coordinates stay exact at any backing size. lineWidth scales with dpr so
  // the stroke keeps its visual weight.
  const _dpr = window.devicePixelRatio || 1;
  const _rect = canvas.getBoundingClientRect();
  if (_rect.width > 2 && _rect.height > 2) {
    canvas.style.width = _rect.width + 'px';
    canvas.style.height = _rect.height + 'px';
    canvas.width = Math.round(_rect.width * _dpr);
    canvas.height = Math.round(_rect.height * _dpr);
  }
  JobBoardState.ppeSigCanvas = canvas;
  const ctx = canvas.getContext('2d');
  JobBoardState.ppeSigCtx = ctx;
  ctx.strokeStyle = '#1E293B';
  ctx.lineWidth = 2 * _dpr;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  JobBoardState.ppeSigHasDrawn = false;

  let drawing = false;
  let lastX = 0, lastY = 0;
  const getPos = (e) => {
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const ev = e.touches ? e.touches[0] : e;
    return { x: (ev.clientX - rect.left) * sx, y: (ev.clientY - rect.top) * sy };
  };
  const start = (e) => { e.preventDefault?.(); drawing = true; const p = getPos(e); lastX = p.x; lastY = p.y; };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault?.();
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastX = p.x; lastY = p.y;
    JobBoardState.ppeSigHasDrawn = true;
  };
  const end = () => { drawing = false; };
  canvas.addEventListener('mousedown', start);
  canvas.addEventListener('mousemove', move);
  canvas.addEventListener('mouseup', end);
  canvas.addEventListener('mouseout', end);
  canvas.addEventListener('touchstart', start, { passive: false });
  canvas.addEventListener('touchmove', move, { passive: false });
  canvas.addEventListener('touchend', end);
}

function ppeClearSignature() {
  const canvas = JobBoardState.ppeSigCanvas;
  const ctx = JobBoardState.ppeSigCtx;
  if (!canvas || !ctx) return;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  JobBoardState.ppeSigHasDrawn = false;
}

async function ppeSubmit(mode) {
  const status = document.getElementById('ppe-status');
  const btn = document.getElementById('ppe-submit-btn');
  const setErr = (m) => { if (status) { status.textContent = m; status.style.color = '#dc2626'; } };

  if (!JobBoardState.ppeSigHasDrawn) { setErr('Please draw your signature first.'); return; }

  const tenantId = window.AppState?.tenant;
  const personId = getActiveJobBoardPersonId();
  const personName = getActiveJobBoardPersonName();
  if (!tenantId || !personId) { setErr('Missing identity — try refreshing.'); return; }

  const condition = document.querySelector('input[name="ppe-condition"]:checked')?.value || 'good';
  const fits = !!document.getElementById('ppe-fits')?.checked;
  // TASK-697 — optional free-text note (serial #, calibration date, etc.).
  // Clamp client-side to 500 chars; the firestore.rules size guard is the real
  // server-side enforcement (the .slice here is bypassable via a raw SDK write).
  const notes = (document.getElementById('ppe-notes')?.value || '').trim().slice(0, 500);
  const signature = JobBoardState.ppeSigCanvas.toDataURL('image/png');
  const acknowledgment = {
    by: personId,
    name: personName || '',
    at: Timestamp.now(),
    signature,
    condition,
    fits,
    notes,
  };

  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving…'; }
  if (status) { status.textContent = 'Saving…'; status.style.color = 'var(--text-muted)'; }

  try {
    if (mode === 'ack') {
      const docId = JobBoardState.ppeAckTargetId;
      if (!docId) throw new Error('No item selected');
      // COD mitigation (Code Reviewer 2026-06-04) — re-validate the ack target is
      // still in our own pending list before writing (defense in depth; the rule
      // already clamps to own-personId + ack-only, this just rejects stale/forged
      // ids client-side with a clean error instead of a permission-denied).
      const target = (JobBoardState.ppePending || []).find(r => r.id === docId);
      if (!target) throw new Error('That item is no longer pending — refresh.');
      // UPDATE the existing admin-issued doc. Rules (Slice 5) allow a driver
      // session to write ONLY acknowledgment/acknowledged on an own-personId doc.
      // NO inventory decrement — the admin already decremented at issue time.
      await updateDoc(doc(db, 'tenants', tenantId, 'safetyEquipment', docId), {
        acknowledgment,
        acknowledged: true,
      });
    } else {
      // SELF-LOG → CREATE a new source:'driver' doc. NO decrementSafetyInventory
      // (driver session has no inventory state; reconciled admin-side).
      const typeSel = document.getElementById('ppe-type-select');
      const equipmentType = typeSel?.selectedOptions?.[0]?.dataset?.name || '';
      if (!equipmentType) { throw new Error('Please pick an equipment type'); }
      const hasSize = typeSel?.selectedOptions?.[0]?.dataset?.hasSize === '1';
      const size = hasSize ? (document.getElementById('ppe-size-select')?.value || '') : '';
      // COD mitigation — clamp qty to a sane 1..99 range so a self-log can't
      // poison inventory reconciliation with a garbage quantity.
      const quantity = Math.min(99, Math.max(1, parseInt(document.getElementById('ppe-qty')?.value, 10) || 1));
      // TASK-9721 — optional job ({} when none picked or the lookup never landed).
      // Bound to the person AND the local day it was looked up for: a form left
      // open past midnight does not stamp yesterday's job (COD r2).
      const jobFields = (JobBoardState.ppeJobCtx && _ppeJobMod && JobBoardState.ppeJobCtx._personId === personId
        && JobBoardState.ppeJobCtx._day === _ppeJobMod.ppeLocalDateStr(new Date()))
        ? _ppeJobMod.ppeIssueJobFields(JobBoardState.ppeJobCtx, document.getElementById('ppe-job-select')?.value || '')
        : {};
      await addDoc(collection(db, 'tenants', tenantId, 'safetyEquipment'), {
        personId,
        personName: personName || '',
        equipmentType,
        quantity,
        size,
        notes: '',
        ...jobFields,
        // TASK-9721 — the LOCAL day (UTC read as tomorrow after ~7 PM Central,
        // which would date tonight's take on the day after its job).
        dateReceived: (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; })(),
        source: 'driver',
        acknowledged: true,
        acknowledgment,
        createdAt: Timestamp.now(),
      });
    }
    window.showToast?.(mode === 'ack' ? 'Receipt confirmed — thanks!' : 'PPE logged & signed', 'success');
    // Reload pending list so the just-signed item drops off, then back to list.
    await _ppeLoadData(tenantId, personId);
    _ppeRenderList();
  } catch (err) {
    console.error('[PPE] submit failed:', err);
    setErr(err?.message || 'Could not save — try again.');
    if (btn) { btn.disabled = false; btn.innerHTML = `<span class="material-icons">check</span> ${mode === 'ack' ? 'Confirm Receipt' : 'Save & Sign'}`; }
  }
}

// ============================================
// EXPORTS
// ============================================
window.JobBoardModule = {
  openJbProjectDoc, // TASK-9650 — project library doc on the job view
  initJobBoard, // TASK-979 — pretrip.js calls this via window (Pitfall #1: no ?v= cross-import)
  // TASK-9865 — one-tap "Switch to J-1248" under a board-side job picker, and
  // "Open J-1248" from a job modal / job screen the viewer is not crewed on.
  _jbPickCrewJob,
  _jbOpenCrewJob,
  // TASK-9740 — driver-signed Bill of Lading / Disposal Ticket on the job modal
  openJobDocumentForm,
  closeJobDocumentForm,
  submitJobDocument,
  openJobDocumentPdf,
  refreshOpenJobDocumentsSection,
  // TASK-9694 — driver job screen = the ticket (switch settings.ticketOnJobScreen).
  renderTicketEditor,
  isTicketOnJobScreen,
  _jbJsConfirmFinish,
  _jbJsFinishYes,
  _jbJsCloseSheet,
  _jbJsStartTap,
  _jbJsCinRetry,
  _jbJsSwitchHere,
  // TASK-1513/1515 — ONE implementation of the expense job-picker relevance
  // window; reconciliation.js reuses it via window (Pitfall #1: no ?v=
  // cross-import). Pass an ALREADY-SCOPED jobs array only.
  filterExpenseJobWindow,
  EXPENSE_JOB_WINDOW_ADMIN,
  EXPENSE_JOB_WINDOW_DRIVER,
  navigate,
  setViewMode,
  // TASK-424 #5 (C3): widen the windowed admin jobs/jobDays live stream.
  loadOlderJobBoard,
  jumpToDayFromCalendar,
  // TASK-563 — driver calendar: day-detail bottom sheet + customer filter
  openDayDetailSheet,
  closeDayDetailSheet,
  openJobFromDaySheet,
  toggleCalendarCustomerFilter,
  onlyCalendarCustomerFilter, // TASK-9399
  // TASK-570 — driver calendar: customer-filter multi-select bottom sheet
  openCalendarFilterSheet,
  closeCalendarFilterSheet,
  showJobDetails,
  reopenJobDetails, // TASK-9511
  closeJobDetails,
  // TASK-9370 — driver Add Job strip + job-details Edit (gated on settings.driverJobEdit)
  driverAddJob,
  driverEditJob,
  showVehicleInfo,
  startJSA,
  getJsaSignatures, // TASK-794
  openJsaToSign,    // TASK-794
  openJsaToEdit,    // TASK-9371
  // TASK-9372 — job modal equipment inspections
  inspectAssetFromJob,
  openJobInspection,
  toggleJobInspectionsShowAll,
  // TASK-342 Slice B — safety meeting sign-in
  openSafetyMeetingModal,
  probeSafetyMeetingReady, // TASK-4882
  openSafetyMeetingHistory, // TASK-4883
  closeSafetyMeetingModal,
  clearSafetyMeetingSignature,
  submitSafetyMeetingSignIn,
  // TASK-437 — driver PPE sign-off
  openPpeModal,
  closePpeModal,
  ppeBackToList,
  ppeStartAck,
  ppeStartSelfLog,
  ppeOnTypeChange,
  ppeClearSignature,
  ppeSubmit,
  showDriverTicket,
  closeTicketModal,
  // Clock In/Out / Ticket Only
  clockInFromBoard,
  clockOutFromBoard,
  _jbDetailClockInTap, // TASK-9364 — job details modal Clock In
  // TASK-348 — SPA personal-action wrappers (explicit personId arg, no AppState.driverPersonId mutation)
  clockInFromBoardForPerson,
  clockOutFromBoardForPerson,
  _csGrabGPS,
  _jbCsRefreshFileChips,   // TASK-4607 — file-field chips
  _jbRetryCsdStash,   // TASK-4594 — kept: still the rows-only retry path
  _jbRetryUnsaved,    // TASK-4608 — inline onclick on the merged unsaved-work banner
  _jbTsfDismissFailed, // TASK-4635 — inline onclick on the "did NOT make it" banner
  _jbRetryStashFromBoard,   // TASK-4666 — inline onclick on the board-level Send button
  _jbDismissStashRecovery,  // TASK-4666 — inline onclick on the terminal (locked/gone) banner
  _csAddInstance: _jbCsAddInstance,
  _csRemoveInstance: _jbCsRemoveInstance,
  jbToggleWorkRecap,  // TASK-9169 — "What's on this ticket" recap above Shift Notes
  startTicketFromBoard,
  submitClockOut,
  closeClockOutModal,
  addDowntimeRow,
  // TASK-771 — "wrong customer/job" correction (re-point active entry)
  jbOpenRepoint,
  jbRepointCustomerChange,
  jbCloseRepoint,
  jbSubmitRepoint,
  // TASK-837 — Switch Job (finalize current shift + start a new one)
  jbOpenSwitchJob,
  jbSwitchCustomerChange,
  jbSwitchJobChange,
  jbSwitchLaborOnly,
  jbCloseSwitchJob,
  jbSubmitSwitchJob,
  // TASK-9556 — shared switch op for the dispatch.js Actions-sheet tile
  jbRunSwitchJob,
  jbIsGatewayTimeout,
  // Build Ticket form (rich clock-out form)
  jbToggle,
  jbResignSignature, // TASK-395
  jbShowTimeEdit,
  jbCancelTimeEdit,
  jbApplyTimeEdit,
  jbApplyForgotClockOut, // TASK-1180 — out-only forgotten-clock-out correction
  jbUpdateEquipTime,
  jbUpdateEquipNotes,
  jbRemoveEquip,
  jbShowEquipPicker,
  jbSelectEquip,
  jbDismissTruckAsk, // TASK-1572 Slice 1 — "No Equipment (Labor Only)" out on the truck ask
  jbAddDowntime,
  jbRemoveDowntime,
  jbAddStandby,
  jbRemoveStandby,
  jbUpdateTimeEntry,
  jbHandlePhotos,
  jbRemovePhoto,
  jbShowPhoto, // TASK-1220 — thumbnail → lightbox
  jbSetPhotoType, // TASK-9868 — BOL / scale ticket / permit / other
  jbSaveTicket,
  // Time Off
  openTimeOffRequest,
  // Standalone Clock In/Out
  openStandaloneClockIn,
  closeStandaloneClockIn,
  onStandaloneCustomerChange,
  onStandaloneJobChange, // TASK-558 — inline equipment render hook
  submitStandaloneClockIn,
  clockOutStandalone,
  handleBottomNavClock,
  // TASK-491 — per-operator equipment confirm at clock-in
  jbToggleEquipConfirmRow,
  jbConfirmEquipSelection,
  jbConfirmEquipLaborOnly,
  jbCancelEquipConfirm,
  jbStandaloneLaborOnly, // TASK-558 — inline "Labor Only" affordance
  jbFleetAskChanged, // TASK-1572 Slice 2 — empty-day "which truck?" select
  jbEquipSwapPicked, // TASK-9763 S2 — "In a different truck?" at clock-in
  jbFleetAskLaborOnly, // TASK-1572 Slice 2 — its "No Equipment (Labor Only)" out
  jbFleetAskSheetFinish, // TASK-1572 Slice 2 — one-tap job-card sheet (every exit clocks in)
  // Expense capture
  openExpenseModal,
  closeExpenseModal,
  setExpenseMode,
  onExpenseCategoryChange,
  previewExpenseReceipt,
  clearExpenseReceipt,
  removeExpenseReceiptAt, // TASK-244 v1.2 multi-receipt
  onExpenseSmartScanToggle, // TASK-4639 — batch Smart Scan opt-in
  scanExpenseReceipt,
  submitExpense,
  // Load counter
  recordLoadPickup,
  undoLoadPickup,
  // TASK-361 — Driver JSA email/PDF/signing handlers moved to window.JSAModule.
  // Driver-PIN inventory (TASK-197 Phase 3)
  showDriverInventory,
  switchDriverInventoryTab,
  updateDriverPurchaseTotal,
  handleDriverPurchaseReceipt,
  submitDriverInventoryCount,
  submitDriverInventoryPurchase,
  // Hydrate state from dispatch (for opening Build Ticket in-place)
  _hydrateState: (data) => {
    JobBoardState.tenantId = data.tenantId;
    JobBoardState.trucks = data.trucks || [];
    JobBoardState.trailers = data.trailers || [];
    JobBoardState.people = data.people || [];
    window.ClockMode?.notePeople?.(JobBoardState.people); // TASK-9553
    JobBoardState.customers = data.customers || [];
    JobBoardState.clockEntries = data.clockEntries || [];
    JobBoardState.jobs = data.jobs || [];
    JobBoardState.jobDays = data.jobDays || [];
    if (Array.isArray(data.costAllocations)) JobBoardState.costAllocations = data.costAllocations;
  }
};

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
